import React__default, { useLayoutEffect, useEffect, useRef, useState, useCallback, useMemo, forwardRef, useImperativeHandle, useContext, createContext } from 'react';
import { c as classNames, E as ErrorBoundary, F as FixedSizeList, N as Ne, d as de$1, Y as Ye, P as Pe, u as useSortable, C as CSS, K as KeyboardCode, a as PointerSensor$1, b as KeyboardSensor$1, e as useSensors, f as useSensor, D as DndContext, g as closestCenter, S as SortableContext, v as verticalListSortingStrategy, h as DragOverlay, i as arrayMove, j as getCountryCallingCode, k as PhoneCountryInput } from './vendor-Dcb4hqow.js';
import { createUseStyles, useTheme, ThemeProvider } from 'react-jss';
import { sanitizeString, getClientEnvironmentDetails } from '@sm/utils';
import { uniqueId, visuallyHidden as visuallyHidden$1 } from '@wds/utils';
import ReactDOM from 'react-dom';
import { defineMessages, t, L10NContext, T, L10nProvider } from '@sm/intl';
import getFontWeights from '@sm/webassets/SurveyTheme/getFontWeights';
import isDark from '@sm/webassets/SurveyTheme/isDark';
import { c as contrastColor } from './theme-Du7FL5TG.js';
import { WrenchTheme } from '@wds/styles';
import { Button as Button$1 } from '@wds/button';

const useStyles$16 = createUseStyles({
    rowStyles: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
    },
});

function Row({ id, className, children, ...props }) {
    const { rowStyles } = useStyles$16();
    return (React__default.createElement("div", { id: id, className: classNames(rowStyles, className), ...props }, children));
}

const useStyles$15 = createUseStyles((theme) => ({
    tooltip: {
        'span[data-tooltip] &': {
            display: 'none',
        },
        [`@media (min-width: ${theme.breakpoints?.md.min ?? '769px'})`]: {
            'span[data-tooltip]:hover &, span[data-tooltip]:focus &': {
                display: 'inline-block',
            },
        },
        position: 'absolute',
        left: ({ center }) => center,
        bottom: 20,
        width: 275,
        transform: 'translate(-50%, -5px)',
        textAlign: 'center',
        zIndex: 12,
        fontSize: 13,
        '&:after': {
            position: 'absolute',
            bottom: -4,
            left: '50%',
            width: 10,
            height: 10,
            backgroundColor: 'white',
            content: '""',
            marginLeft: -5,
            transform: 'rotate(45deg)',
            boxShadow: [[3, 5, 5, 'rgb(0 0 0 / 0.07)']],
            zIndex: 13,
        },
    },
    tooltipBody: {
        display: 'inline-block',
        maxWidth: 275,
        color: 'black',
        fontWeight: 300,
        fontFamily: theme.fontFamily,
        fontStyle: 'normal',
        boxShadow: [[0, 2, 10, 0, 'rgb(0 0 0 / 0.14)']],
        background: 'white',
        padding: [[12, 16]],
        lineHeight: 1.4,
        borderRadius: 2,
        textAlign: 'left',
    },
}));

const defaultSelectors = [
    'a[href]',
    'button',
    'textarea',
    'input:not([type="hidden"])',
    'select',
    'details',
    '[tabindex]:not([tabindex="-1"])',
];
const useFocusTrap = ({ selectors = defaultSelectors, } = {}) => {
    const focusRef = React__default.useRef(null);
    const [focusableEls, setEls] = React__default.useState([]);
    React__default.useEffect(() => {
        if (focusRef.current) {
            const els = Array.from(focusRef.current.querySelectorAll(selectors.join(',')));
            setEls(els);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusRef]);
    function handleFocus(e) {
        if (focusRef.current) {
            const focusableElements = focusableEls;
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    lastElement.focus();
                    e.preventDefault();
                }
            }
            else if (document.activeElement === lastElement) {
                firstElement.focus();
                e.preventDefault();
            }
        }
    }
    React__default.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Tab') {
                handleFocus(e);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    });
    return focusRef;
};

const useStyles$14 = createUseStyles((theme) => ({
    modalOverlay: {
        zIndex: 999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
    modal: {
        position: 'relative',
        width: '85vw',
        minHeight: '20vw',
        marginBottom: 20,
        background: 'white',
        borderRadius: 2,
    },
    closeBtn: {
        position: 'absolute',
        cursor: 'pointer',
        background: 'transparent',
        right: 0,
        border: 0,
        padding: 8,
        marginTop: 0,
        color: 'black',
        zIndex: 105,
    },
    tooltipText: {
        color: theme.questionBody?.color || '#333E48',
        fontFamily: theme.fontFamily,
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '1.4',
        padding: [28, 12, 16],
    },
}));

const COPY$l = defineMessages({
    CLOSE_MODAL: {
        id: 'Modal.CloseModal',
        defaultMessage: 'Close tooltip',
        description: '[Type: label][Vis: Med] - close button text',
    },
});
function Modal({ tooltip, closeModal }) {
    const { modalOverlay, closeBtn, modal, tooltipText } = useStyles$14();
    const closeRef = React__default.useRef(null);
    const tooltipId = uniqueId('tooltip-modal');
    const focusRef = useFocusTrap();
    const handleClick = (e) => {
        // click on button or overlay should close modal
        e.stopPropagation();
        if (e.currentTarget === e.target) {
            closeModal();
        }
    };
    React__default.useEffect(() => {
        // listen for ESC to close modal
        const handleKeydown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                closeModal();
            }
        };
        document.addEventListener('keydown', handleKeydown);
        return () => {
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [closeModal]);
    React__default.useEffect(() => {
        closeRef.current?.focus();
    }, []);
    return (React__default.createElement("div", { className: modalOverlay, onClick: handleClick, "aria-hidden": "true" },
        React__default.createElement("div", { ref: focusRef, className: modal, role: "dialog", "aria-modal": "true", "aria-describedby": tooltipId },
            React__default.createElement("button", { type: "button", className: closeBtn, ref: closeRef, onClick: handleClick, "aria-label": t(COPY$l.CLOSE_MODAL) }, "\u00D7"),
            React__default.createElement("p", { id: tooltipId, className: tooltipText }, tooltip))));
}

/** Displays a tooltip on hover for large screens and a modal on click for small screens */
const Tooltip = ({ container, className, id = '' }) => {
    /** container center position to display tooltip */
    const center = container.offsetWidth / 2;
    const text = container.getAttribute('data-tooltip') ?? '';
    const theme = useTheme();
    const { tooltip, tooltipBody } = useStyles$15({ center });
    const [showModal, setShowModal] = React__default.useState(false);
    const [mode, setMode] = React__default.useState('TOOLTIP');
    const closeModal = () => {
        setShowModal(false);
        container.focus();
    };
    React__default.useEffect(() => {
        container.setAttribute('role', 'tooltip');
        // make tooltip anchor focusable
        if (!container.hasAttribute('tabIndex')) {
            container.setAttribute('tabIndex', '0');
        }
        // opens modal if matches small screen breakpoint
        const openModal = () => {
            const mql = window.matchMedia(`(max-width: ${theme?.breakpoints?.md.min ?? '769px'})`);
            if (mql.matches) {
                setMode('MODAL');
                setShowModal(true);
            }
            else {
                setMode('TOOLTIP');
            }
        };
        const handleClick = () => {
            openModal();
        };
        const handleKeydown = (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                e.stopPropagation();
                openModal();
            }
        };
        // listen for click, ENTER or SPACE to open modal
        container.addEventListener('click', handleClick);
        container.addEventListener('keydown', handleKeydown);
        return () => {
            container.removeEventListener('click', handleClick);
            container.removeEventListener('keydown', handleKeydown);
        };
    }, [container, theme]);
    const modalEl = showModal ? (React__default.createElement(Modal, { tooltip: text, closeModal: closeModal })) : null;
    const tooltipEl = (React__default.createElement("div", { className: classNames(tooltip, className) },
        React__default.createElement("span", { id: id, className: tooltipBody, "aria-hidden": "true" }, text)));
    const child = mode === 'MODAL' ? modalEl : tooltipEl;
    const containerEl = mode === 'MODAL' ? document.body : container;
    return ReactDOM.createPortal(child, containerEl);
};

const listSpacing = {
    margin: [16, 0],
    padding: [0, 0, 0, 40],
};
const mediaReset = {
    border: 0,
    maxWidth: '100%',
};
const useStyles$13 = createUseStyles({
    richTextContent: {
        // Typography
        '& a': {
            // Links are not themed by default. Users must select a link color in the editor
            color: 'blue',
            textDecoration: 'underline',
            '&:hover': {
                cursor: 'pointer',
            },
            '&:focus': {
                outline: 'currentColor auto 1px',
            },
        },
        '& ol': {
            ...listSpacing,
            listStyleType: 'decimal',
        },
        '& ul': {
            ...listSpacing,
            listStyle: 'disc outside none',
            '& ul li': {
                listStyle: 'circle',
            },
        },
        '& p': {
            margin: [13, 0],
        },
        '& b': {
            fontWeight: 'bold',
        },
        '& strong': {
            fontWeight: 'bold',
        },
        '& em': {
            fontStyle: 'italic',
        },
        '& i': {
            fontStyle: 'italic',
        },
        '& big': {
            fontSize: 'larger',
        },
        '& small': {
            fontSize: 'smaller',
        },
        '& sup': {
            fontSize: '75%',
            lineHeight: 0,
            position: 'relative',
            top: '-0.5em',
            verticalAlign: 'baseline',
        },
        // Media embeds
        '& iframe': mediaReset,
        '& img': {
            ...mediaReset,
            height: 'auto',
            '-ms-interpolation-mode': 'bicubic',
        },
        '& span[data-tooltip]': {
            position: 'relative',
            display: 'inline',
            borderBottom: [[1, 'dashed']],
            textDecoration: 'inherit',
            cursor: 'pointer',
        },
    },
});

// Support iframe video embeds and their matching attributes
const sanitizeOptions = {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
        'target',
        'allow',
        'allowfullscreen',
        'frameborder',
        'sandbox',
        'scrolling',
    ],
};
function RichText({ id: elementId, element: Element = 'div', text, className, ...containerProps }) {
    const { richTextContent } = useStyles$13();
    const [tooltipNodes, setTooltipNodes] = React__default.useState([]);
    const tooltipRef = React__default.useRef(null);
    React__default.useEffect(() => {
        if (tooltipRef?.current) {
            const el = tooltipRef?.current;
            setTooltipNodes(Array.from(el.querySelectorAll('[data-tooltip]')));
        }
    }, []);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Element, { id: elementId, className: classNames(richTextContent, className), dangerouslySetInnerHTML: {
                __html: sanitizeString(text, sanitizeOptions),
            }, ref: tooltipRef, translate: "no", ...containerProps }),
        tooltipNodes.map((container) => {
            const id = uniqueId('rich-text-tooltip');
            container.setAttribute('aria-describedby', id);
            return React__default.createElement(Tooltip, { key: id, container: container, id: id });
        })));
}

const useStyles$12 = createUseStyles((theme) => ({
    wdsIcons: ({ color, size }) => {
        const fontSize = size
            ? { fontSize: theme.iconSize[size] }
            : { fontSize: 'inherit' };
        const chosenColor = 
        // @ts-expect-error Fallback styles are acceptable
        theme.palette?.text[color] ||
            theme.palette?.[color]?.main ||
            'currentColor';
        return {
            ...fontSize,
            width: '1em',
            height: '1em',
            display: 'inline-block',
            verticalAlign: 'middle',
            textAlign: 'center',
            fill: chosenColor,
        };
    },
}));

// https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging
const getDisplayName = (Component) => Component.displayName || Component.name || 'Component';
function withIcon(Component) {
    function WithIcon({ color, title, size, ...rest }) {
        const classes = useStyles$12({ color, size });
        return (React__default.createElement("svg", { className: classes.wdsIcons, viewBox: "0 0 16 16", preserveAspectRatio: "xMidYMid", "aria-label": title, role: title ? 'img' : 'presentation', ...rest },
            React__default.createElement(Component, null)));
    }
    WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;
    WithIcon.getOriginalComponent = () => Component;
    return WithIcon;
}

const useSurveyIconStyles = createUseStyles({
    svgContainer: {
        display: 'inline-block',
        width: '1em',
        height: '1em',
    },
});

let instance = 0;
function withSvgIcon(Component, displayName = 'SvgElement') {
    function SvgIcon({ id, 'aria-labelledby': ariaLabelledBy, title, viewBox = '0 0 16 16', className, ...props }) {
        instance += 1;
        const styles = useSurveyIconStyles();
        const hasTitle = !!title && !ariaLabelledBy;
        const iconTitleId = id ? `${id}-icon-title` : `icon-title-${instance}`;
        return (React__default.createElement("svg", { className: classNames(styles.svgContainer, className), viewBox: viewBox, preserveAspectRatio: "xMidYMid", "aria-hidden": hasTitle ? undefined : true, "aria-labelledby": hasTitle ? iconTitleId : ariaLabelledBy, role: hasTitle ? 'img' : undefined, focusable: "false", ...props, "data-testid": displayName },
            hasTitle && React__default.createElement("title", { id: iconTitleId }, title),
            React__default.createElement(Component, null)));
    }
    SvgIcon.displayName = displayName;
    return SvgIcon;
}

function DocumentXIconPath() {
    return (React__default.createElement("path", { d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z" }));
}
var IconDocumentX = withIcon(DocumentXIconPath);

const useStyles$11 = createUseStyles((theme) => {
    return {
        fallbackComponent: {
            fontFamily: theme.questionBody?.fontFamily ?? {},
            color: theme.questionColor,
            border: `1px solid ${theme.questionColor}`,
            borderRadius: '5px',
            width: '100%',
            height: '100%',
            minHeight: '220px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& svg': {
                fill: theme.questionColor,
            },
        },
        errorMessage: {
            marginLeft: `8px`, // equivalent to wrench theme.spacing[2]
        },
    };
});

const COPY$k = defineMessages({
    ERROR: {
        id: 'FallbackComponent.ERROR',
        defaultMessage: 'Error loading question',
        description: '[Type: label][Vis: high] - error message indicating a question could not be loaded',
    },
});
function FallbackComponent() {
    const { fallbackComponent, errorMessage } = useStyles$11();
    return (React__default.createElement("div", { className: fallbackComponent },
        React__default.createElement(IconDocumentX, null),
        React__default.createElement("p", { className: errorMessage }, t(COPY$k.ERROR))));
}

const withErrorBoundary = (Component) => {
    const displayName = Component.displayName || Component.name || 'Component';
    function ComponentWithErrorBoundary(props) {
        return (React__default.createElement(ErrorBoundary, { FallbackComponent: FallbackComponent },
            React__default.createElement(Component, { ...props })));
    }
    ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
    return ComponentWithErrorBoundary;
};

const createFieldId = (questionId) => `question-field-${questionId}`;
const createLegendId = (questionId) => `question-title-legend-${questionId}`;
const createErrorRowId = (questionId) => `error-row-${questionId}`;
const createErrorId = (questionId) => `${questionId}-validation-message`;

/**
 * Hook to split question-choices by type and filter for visibility
 */
function useQuestionChoices(choices = [], columns = []) {
    const options = choices.filter((c) => !!c && c.visible);
    const columnOptions = columns.filter((c) => !!c && c.visible);
    return {
        options,
        /** Standard column options (Matrix) */
        columnOptions,
        /** Standard answer options */
        sortableOptions: options.filter((c) => !c.type),
        /** None-Of-The-Above (NOTA) option  */
        notaOption: options.find((c) => c.type === 'NOTA'),
        /** Other answer option */
        otherOption: options.find((c) => c.type === 'ANSWER'),
        /** Other comment option - One comment for the question */
        commentOption: options.find((c) => c.type === 'COMMENT'),
        /** Other comment option - One comment per row (e.g. for Matrix) */
        commentColumnOption: options.find((c) => c.type === 'COMMENT_COLUMN'),
    };
}

const useStyles$10 = createUseStyles((theme) => {
    return {
        button: {
            color: theme.nextButton.color,
            backgroundColor: theme.nextButton.backgroundColor,
            fontFamily: theme.questionBody.fontFamily ?? {},
            fontSize: 15,
            border: [1, 'solid', 'transparent'],
            borderRadius: 2,
            padding: [1, 6],
            maxWidth: '100%',
            minWidth: 60,
            minHeight: 40,
            cursor: 'pointer',
            overflowWrap: 'break-word',
            transition: `all .2s linear`,
            '&:focus, &:hover': {
                outlineOffset: 5,
                outline: `1px solid ${theme.answerColor}`,
                // Safari seems to have an outline bug, may not render without boxshadow
                boxShadow: '0 0 4px transparent',
            },
        },
    };
});

const COPY$j = defineMessages({
    OK_LABEL: {
        id: 'Button.OK_LABEL',
        defaultMessage: 'OK',
        description: '[Type: button][Vis: high] - question ok label',
    },
});
// This might need to be a prop later once confirmed
const MAX_LENGTH = 20;
function Button({ text = t(COPY$j.OK_LABEL), type = 'button', className, ...props }) {
    const { button } = useStyles$10();
    const slicedText = text.length <= MAX_LENGTH ? text : text.slice(0, MAX_LENGTH);
    return (
    // eslint-disable-next-line react/button-has-type
    React__default.createElement("button", { className: classNames(button, className), type: type, ...props }, slicedText));
}

/*eslint spaced-comment: ["error", "never", { "exceptions": ["NOSONAR"] }] */
/**
 * SSR Safe version of useLayoutEffect
 * @see https://github.com/mntv-webplatform/smweb/blob/main/apps/contentweb/src/app/pages/MRX/helpers/useIsomorphicLayoutEffect.js
 *
 * Note: `NOSONAR` is used to bypass a `minor:code-smell` error that states `createElement` is deprecated.
 * The eslint rule above disables the spaced comments to use NOSONAR as stated in the docs.
 */
const useSSRSafeLayoutEffect = typeof window !== 'undefined' &&
    typeof window?.document?.createElement !== 'undefined' //NOSONAR
    ? useLayoutEffect
    : useEffect;

/** Referentially stable empty function
 *
 * e.g. as default or placeholder */
// eslint-disable-next-line @typescript-eslint/no-empty-function
const EmptyFn = () => { };

/* eslint-disable no-param-reassign, no-plusplus */
/**
 * @name EasingFunctions- Useful easing equations
 * @comment only considering the t value for the range [0, 1] => [0, 1]
 */
const easingFunctions = {
    /** @comment linear - no easing, no acceleration */
    linear: (t) => t,
    /** @comment easeInQuad - accelerating from zero velocity */
    easeInQuad: (t) => t * t,
    /** @comment easeOutQuad - decelerating to zero velocity */
    easeOutQuad: (t) => t * (2 - t),
    /** @comment easeInOutQuad - acceleration until halfway, then deceleration */
    easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
    /** @comment easeInCubic - accelerating from zero velocity */
    easeInCubic: (t) => t * t * t,
    /** @comment easeOutCubic - decelerating to zero velocity */
    easeOutCubic: (t) => --t * t * t + 1,
    /** @comment easeInOutCubic - acceleration until halfway, then deceleration */
    easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
    /** @comment easeInQuart - accelerating from zero velocity */
    easeInQuart: (t) => t * t * t * t,
    /** @comment easeOutQuart - decelerating to zero velocity */
    easeOutQuart: (t) => 1 - --t * t * t * t,
    /** @comment easeInOutQuart - acceleration until halfway, then deceleration */
    easeInOutQuart: (t) => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,
    /** @comment easeInQuint - accelerating from zero velocity */
    easeInQuint: (t) => t * t * t * t * t,
    /** @comment easeOutQuint - decelerating to zero velocity */
    easeOutQuint: (t) => 1 + --t * t * t * t * t,
    /** @comment easeInOutQuint - acceleration until halfway, then deceleration */
    easeInOutQuint: (t) => t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t,
};
/**
 * Given a start/end point of a scroll and time elapsed, calculate the scroll position we should be at
 */
function getValue(start, end, elapsed, duration, easingType = 'linear') {
    if (elapsed > duration) {
        return end;
    }
    const easing = typeof easingType === 'string' ? easingFunctions[easingType] : easingType;
    return start + (end - start) * easing(elapsed / duration);
}
/**
 * Smoothly animate between two values
 */
function animate({ fromValue, toValue, onStart, onUpdate, onComplete, duration = 600, delay = 0, easingType = 'easeOutQuart', }) {
    const startTime = performance.now() + delay;
    const tick = () => {
        const elapsed = performance.now() - startTime;
        const time = getValue(fromValue, toValue, elapsed, duration, easingType);
        const callback = elapsed <= duration ? tick : onComplete ?? EmptyFn;
        const updateAnimation = () => {
            onUpdate(time, callback);
        };
        window.requestAnimationFrame(updateAnimation);
    };
    onStart?.();
    if (delay > 0) {
        setTimeout(() => {
            tick();
        }, delay);
    }
    else {
        tick();
    }
}

const useStyles$$ = createUseStyles({
    paddingBox: ({ padding, width }) => {
        return {
            paddingTop: padding?.top || 0,
            paddingBottom: padding?.bottom || 0,
            paddingLeft: padding?.left || 0,
            paddingRight: padding?.right || 0,
            width: width
                ? `${width.width}${width.format === 'PERCENT' ? '%' : 'px'}`
                : '100%',
            maxWidth: '100%',
        };
    },
});

function QuestionSpacing({ padding, width, children, ...props }) {
    const { paddingBox } = useStyles$$({ padding, width });
    return (React__default.createElement("div", { className: paddingBox, "data-testid": "QuestionSpacing__paddingBox", ...props }, children));
}

/**
 * Hook that calls the provided callback function when the ref element is blurred,
 * only if the next focus is outside the ref element
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useOnFocusLeave(ref, ()=> onLeaveQuestion());
 * <div rf={ref}><input /></div>
 */
function useOnFocusLeave(ref, focusCallback) {
    useEffect(() => {
        const ele = ref?.current;
        function handleFocus(e) {
            if (!ele?.contains(e.relatedTarget)) {
                focusCallback(ref);
            }
        }
        ele?.addEventListener('focusout', handleFocus);
        return () => {
            ele?.removeEventListener('focusout', handleFocus);
        };
    }, [ref, focusCallback]);
}

const useStyles$_ = createUseStyles({
    footerRow: ({ hasFooterPadding }) => ({
        paddingTop: hasFooterPadding ? 20 : 0,
    }),
    containerStyles: {
        position: 'relative',
        transition: 'opacity 500ms ease-out',
    },
    errorRow: {
        overflow: 'hidden',
    },
    formReset: {
        border: 0,
        minWidth: 0,
    },
    headerRow: {
        marginBottom: 24,
        width: '100%',
    },
});

function QuestionFieldLayoutTemplate({ footer, hasFooterPadding, children, clickShield, error, id, padding, width = { width: 100, format: 'PERCENT' }, title, onSubmit, 
// eslint-disable-next-line @typescript-eslint/no-empty-function
onLeave = () => { }, ...props }) {
    const { footerRow, containerStyles, formReset, errorRow, headerRow, } = useStyles$_({
        hasFooterPadding,
        clickShield,
    });
    const containerRef = useRef(null);
    const errorRef = useRef(null);
    /** Keep track of last error to enable error-hiding animation */
    const [lastShownError, setLastShownError] = useState(error);
    const handleKeydown = useCallback((event) => {
        if (event.key === 'Enter') {
            onSubmit?.();
        }
    }, [onSubmit]);
    /**
     * resolves double height animate issue with RAWR-1240
     */
    const isAnimating = useRef(false);
    useSSRSafeLayoutEffect(() => {
        const el = errorRef.current;
        if (!el || isAnimating.current) {
            return;
        }
        const hasError = !!error;
        const initialHeight = el.offsetHeight;
        el.style.height = 'auto'; // remove height to measure full size
        const { offsetHeight } = el;
        const fromValue = hasError ? 0 : offsetHeight;
        const toValue = hasError ? offsetHeight : 0;
        // don't animate if value is already correct (e.g. at initial load)
        if (initialHeight === toValue) {
            el.style.height = `${initialHeight}px`;
            return;
        }
        el.style.height = `${initialHeight}px`;
        animate({
            fromValue,
            toValue,
            duration: 300,
            delay: 200,
            onUpdate: (height, next) => {
                el.style.height = `${height}px`;
                next();
            },
            onStart: () => {
                isAnimating.current = true;
            },
            onComplete: () => {
                setLastShownError(error);
                isAnimating.current = false;
            },
        });
    }, [error]);
    const fieldsetRef = useRef(null);
    useOnFocusLeave(fieldsetRef, onLeave);
    useEffect(() => {
        const { current: container } = containerRef;
        container?.addEventListener('keydown', handleKeydown);
        return () => {
            return container?.removeEventListener('keydown', handleKeydown);
        };
    }, [containerRef, handleKeydown]);
    /**
     * This id is queried in RespWeb to calculate the offset for scroll-to-error animations in classic mode
     * ref: https://github.com/mntv-webplatform/smweb/pull/10725
     */
    let errorRowId;
    /** used in RespWeb for OQAATView */
    let fieldId;
    /** used in RespWeb for OQAATView */
    let questionTitleId;
    if (id) {
        errorRowId = createErrorRowId(id);
        fieldId = createFieldId(id);
        questionTitleId = createLegendId(id);
    }
    return (React__default.createElement("div", { ref: containerRef, id: fieldId, ...props, className: containerStyles },
        React__default.createElement(QuestionSpacing, { padding: padding, width: width },
            React__default.createElement("div", { ref: errorRef, id: errorRowId, className: errorRow }, error || lastShownError),
            React__default.createElement("fieldset", { ref: fieldsetRef, className: formReset },
                React__default.createElement("legend", { id: questionTitleId, className: headerRow, tabIndex: -1 }, title),
                children),
            footer && React__default.createElement("div", { className: footerRow }, footer))));
}

/**
 * Calculate the max number of children each column can be filled in the layout
 * @param columns number of columns
 * @param total total child count
 * @description Based on the current implementation in production (as of Jan 20, 2021),
 * - if columns = 1, the max number of children per column = total (render all children vertically)
 * - if columns > 1, the max number of children per column = (total / columns) + 1
 */
const calculateMaxNumOfChildrenPerColumn = (columns, total) => {
    if (columns === 1) {
        return total;
    }
    const childrenPerColumn = total / columns;
    if (childrenPerColumn % 1 === 0) {
        return childrenPerColumn;
    }
    return Math.floor(childrenPerColumn) + 1;
};
/**
 * Divide the children array into slices for rendering
 * @param childrenArr - children array
 * @param maxCellsPerCol - maximum number of children per slice
 * @param totalColumns - number of columns
 * @example
 * childrenArr = [option1, option2, option3, option4, option5, option6, option7]
 * childrenPerSlice = 2
 * totalColumns = 3
 * output: [[option1, option2, option3], [option4, option5], [option6, option7]]
 */
const sliceChildren = (childrenArr, maxCellsPerCol, totalColumns) => {
    let index = 0;
    const result = [];
    const partialColumnsCount = totalColumns * maxCellsPerCol - childrenArr.length;
    // Calculate the number of fullColumns, to conditionally push slice with the correct # of cells
    let fullColumnsCount = totalColumns - partialColumnsCount;
    while (index < childrenArr.length) {
        if (fullColumnsCount > 0) {
            result.push(childrenArr.slice(index, index + maxCellsPerCol));
            index += maxCellsPerCol;
        }
        else {
            result.push(childrenArr.slice(index, index + maxCellsPerCol - 1));
            index += maxCellsPerCol - 1;
        }
        fullColumnsCount -= 1;
    }
    return result;
};

const useStyles$Z = createUseStyles((theme) => ({
    containerVertical: {
        display: 'initial',
    },
    containerHorizontal: {
        display: 'block',
    },
    /** shared between horizontal and vertical columns */
    column: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
    },
    columnHorizontal: {},
    /** Horizontal Column that adjust width to content width */
    columnHorizontalAutoAdjust: {
        width: 'auto',
    },
    [`@media (min-width: ${theme.breakpoints.xs.min})`]: {
        containerVertical: {
            display: 'flex',
        },
        containerHorizontal: {
            display: 'flex',
            flexWrap: 'wrap',
        },
    },
    [`@media only screen and (min-width: ${theme.breakpoints.lg.min})`]: {
        columnHorizontal: {
            width: '19%',
        },
    },
    [`@media (min-width: ${theme.breakpoints.sm.min}) and (max-width: ${theme.breakpoints.md.max})`]: {
        columnHorizontal: {
            width: '24%',
        },
    },
    [`@media only screen and (max-width: ${theme.breakpoints.xxs.max})`]: {
        columnHorizontal: {
            width: '100%',
            display: 'block',
        },
        columnHorizontalAutoAdjust: {
            width: '100%',
            display: 'block',
        },
    },
    answerLayoutCell: ({ gridCellMargin = '0' }) => ({
        margin: gridCellMargin,
        flex: [0, 0, 'auto'],
        wordBreak: 'normal',
        overflowWrap: 'anywhere',
    }),
    otherLayoutCell: ({ otherCellMargin = '0' }) => ({
        margin: otherCellMargin,
    }),
}));

function QuestionAnswerLayoutTemplate({ children, columns = 1, other, noneOfTheAbove, adjustToContent = false, gridCellMargin, otherCellMargin, }) {
    const { containerVertical, containerHorizontal, column, columnHorizontal, columnHorizontalAutoAdjust, answerLayoutCell, otherLayoutCell, } = useStyles$Z({
        columns,
        adjustToContent,
        gridCellMargin,
        otherCellMargin,
    });
    const isHorizontal = columns === 'horizontal';
    /* Based on the columns config, divide the question answers
      (children of this component) into column groups for rendering.
  
      The reason we need to do this is when there are multiple columns,
      the answers are listed vertically instead of horizontally.
      For example:
      If there are 3 answers and 2 columns,
      Then the ordering will be:
      Answer 1    Answer 3
      Answer 2
  
      NOT:
      Answer 1    Answer 2
      Answer 3
      * though `horizontal` uses this
      
    */
    const columnGroups = useMemo(() => {
        const totalChildren = React__default.Children.count(children);
        const totalColumns = columns === 'horizontal' ? totalChildren : columns;
        const childrenArray = React__default.Children.toArray(children);
        if (!other && !!noneOfTheAbove && columns !== 'horizontal') {
            childrenArray.push(noneOfTheAbove);
        }
        const answersPerColumn = columns === 'horizontal'
            ? 1
            : calculateMaxNumOfChildrenPerColumn(totalColumns, totalChildren + (!other && !!noneOfTheAbove ? 1 : 0));
        return sliceChildren(childrenArray, answersPerColumn, totalColumns);
    }, [children, columns, noneOfTheAbove, other]);
    const columnClassNames = isHorizontal
        ? [column, adjustToContent ? columnHorizontalAutoAdjust : columnHorizontal]
        : column;
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: isHorizontal ? containerHorizontal : containerVertical }, columnGroups.map((col, columnIndex) => (React__default.createElement("div", { key: `col-${columnIndex + 1}`, className: classNames(columnClassNames), "data-testid": "answer-layout-column" }, col.map((cell, cellIndex) => (React__default.createElement("div", { key: `cell-${cellIndex + 1}`, "data-testid": "answer-layout-cell", className: answerLayoutCell }, cell))))))),
        other && React__default.createElement("div", { className: otherLayoutCell }, other),
        ((other && noneOfTheAbove) || columns === 'horizontal') && (React__default.createElement("div", { className: otherLayoutCell, "data-testid": "Other-NOTA" }, noneOfTheAbove))));
}

function WarningIcon() {
    return (React__default.createElement("g", { className: "warning-icon" },
        React__default.createElement("path", { className: "background", transform: "translate(0 .49999)", d: "M8.49642,0.635797c.1494.086885.27371.210979.36079.360173L15.8621,12.9978c.2782.4766.1169,1.0883-.3602,1.3662-.1529.0891-.3267.136-.5037.136L1,14.5c-.552285,0-1-.4473-1-.999c0-.1766.046846-.35.135766-.5026L7.1291,0.996589c.27785-.476838.89002-.63837,1.36732-.360792Z", clipRule: "evenodd", fill: "currentColor", fillRule: "evenodd", stroke: "none" }),
        React__default.createElement("path", { className: "foreground", transform: "translate(.000815 .49999)", d: "M8,11.2546c-.3797,0-.69349.2794-.74315.6419L7.25,11.9973v.26l.00685.1008C7.30651,12.7206,7.6203,13,8,13s.69349-.2794.74315-.6419L8.75,12.2573v-.26l-.00685-.1008C8.69349,11.534,8.3797,11.2546,8,11.2546ZM8,4c-.3797,0-.69349.2794-.74315.6419L7.25,4.74268v4.54393l.00685.10078c.04966.3625.36345.64191.74315.64191s.69349-.27941.74315-.64191L8.75,9.28661v-4.54393L8.74315,4.6419C8.69349,4.2794,8.3797,4,8,4Z", clipRule: "evenodd", fill: "#fff", fillRule: "evenodd", stroke: "none" })));
}
var WarningIcon$1 = withSvgIcon(WarningIcon, 'WarningIcon');

const useStyles$Y = createUseStyles((theme) => ({
    icon: {
        color: '#F05B24',
        fontSize: theme.fontSize.body,
        alignSelf: 'flex-start',
        marginTop: 2,
        flexShrink: 0,
    },
    validationMessage: {
        fontFamily: theme.fontFamily,
        lineHeight: 1.5,
        color: '#333e48',
        fontWeight: 400,
        fontSize: 16,
        padding: theme.isDark ? 8 : [4, 0],
        backgroundColor: theme.isDark ? 'rgba(255, 255, 255, 0.9)' : '',
        borderRadius: 2,
        display: 'inline-flex',
        gap: 5,
        alignItems: 'center',
        marginBottom: 8,
        [`@media only screen and (max-width: ${theme.breakpoints?.xxs.max})`]: {
            width: '100%',
            padding: theme.isDark ? 4 : [4, 0],
        },
    },
}));

function ValidationMessage({ children, id, tag: Wrapper = 'span', ...props }) {
    const { icon, validationMessage } = useStyles$Y();
    return (React__default.createElement(Wrapper, { className: validationMessage, "aria-live": "polite", id: id, ...props },
        React__default.createElement(WarningIcon$1, { className: icon }),
        React__default.createElement("span", { translate: "no" }, children)));
}

const useStyles$X = createUseStyles((theme) => {
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    const fontWeight = theme.name.includes('Pencils')
        ? fontWeightOptions.bold
        : fontWeightOptions.medium;
    return {
        inputArea: {
            fontFamily: theme.questionBody.fontFamily ?? {},
            fontWeight,
            fontSize: '18px',
            [breakpointMedium]: {
                fontSize: '16px',
            },
            lineHeight: '1.15em',
            padding: '6px 60px 6px 6px',
            maxWidth: '100%',
            [breakpointXsMin]: {
                // extra 66px to account for the padding
                width: ({ cols }) => `calc(${cols}ch + 66px)`,
            },
            width: '100%',
            border: `1px solid ${theme.answerColor}`,
            borderRadius: '0px',
            backgroundColor: '#fff',
            color: '#000',
            transition: 'all 0.1s linear',
            verticalAlign: 'top',
            textSizeAdjust: 'auto',
            '&:focus, &:hover': {
                outline: `2px solid ${theme.primaryAccentColor}`,
            },
            '&:read-only:not(:disabled)': {
                borderColor: 'transparent',
                backgroundColor: theme.input.bgColor,
                color: theme.isDark ? '#fff' : '#000',
                opacity: 0.5,
            },
            '&:disabled': {
                opacity: 0.4,
            },
        },
    };
});

function TextArea({ className, disabled = false, required = false, readOnly = false, cols = 50, ...props }, forwardedRef) {
    const { inputArea } = useStyles$X({ cols });
    return (React__default.createElement("textarea", { className: classNames(inputArea, className), disabled: disabled, "aria-disabled": disabled, required: required, "aria-required": required, readOnly: readOnly, "aria-readonly": readOnly, spellCheck: true, ref: forwardedRef, ...props }));
}
var TextArea$1 = forwardRef(TextArea);

const useStyles$W = createUseStyles((theme) => {
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    const fontWeight = theme.name.includes('Pencils')
        ? fontWeightOptions.bold
        : fontWeightOptions.medium;
    return {
        inputField: {
            fontFamily: theme.questionBody.fontFamily ?? {},
            fontSize: '18px',
            [breakpointMedium]: {
                fontSize: '16px',
            },
            fontWeight,
            lineHeight: '1.5em',
            height: ({ autoHeight }) => (autoHeight ? {} : '50px'),
            maxWidth: '100%',
            width: '100%',
            [breakpointXsMin]: {
                // extra 12px to account for the padding
                width: ({ size }) => `calc(${size}ch + 12px)`,
            },
            padding: '6px',
            border: `1px solid ${theme.answerColor}`,
            borderRadius: '0px',
            backgroundColor: '#fff',
            color: isDark(theme.answerColor) ? theme.answerColor : '#000',
            transition: 'all 0.1s linear',
            '&:focus, &:hover': {
                outline: `2px solid ${theme.primaryAccentColor}`,
                outlineOffset: '0',
            },
            '&:read-only:not(:disabled)': {
                borderColor: 'transparent',
                backgroundColor: theme.input.bgColor,
                color: theme.isDark ? '#fff' : '#000',
                opacity: 0.5,
            },
            '&:disabled': {
                opacity: 0.4,
            },
        },
    };
});

function TextInput({ className, autoHeight = false, size = 50, required = false, disabled = false, readOnly = false, type = 'text', onChange: handleChange, labelledby, ...props }, forwardedRef) {
    const { inputField } = useStyles$W({
        autoHeight,
        size,
    });
    return (React__default.createElement("input", { type: type, className: classNames(inputField, className), disabled: disabled, "aria-disabled": disabled, required: required, "aria-required": required, readOnly: readOnly, "aria-readonly": readOnly, "aria-labelledby": labelledby, spellCheck: true, onChange: handleChange, ...props, ref: forwardedRef }));
}
var TextInput$1 = forwardRef(TextInput);

/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps, no-console */
function throwControlChangeError(isControlled, controlled, name, state) {
    const conState = ['controlled', 'uncontrolled'];
    const stateStr = isControlled ? conState : [...conState].reverse();
    if (isControlled !== (controlled !== undefined)) {
        console.error([
            `@sm/question-ui: A component is changing the ${stateStr[0]} ${state} state of ${name} to be ${stateStr[1]}.`,
            'Elements should not switch from uncontrolled to controlled (or vice versa).',
            `Decide between using a controlled or uncontrolled ${name} element for the lifetime of the component.`,
            "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.",
            'More info: https://fb.me/react-controlled-components',
        ].join('\n'));
    }
}
function throwDefaultStateError(isControlled, defaultValue, defaultProp, name, state) {
    if (!isControlled && defaultValue !== defaultProp) {
        console.error([
            `@sm/question-ui: A component is changing the default ${state} state of an uncontrolled ${name} after being initialized. `,
            `To suppress this warning opt to use a controlled ${name}.`,
        ].join('\n'));
    }
}
function useControlledState({ controlled, default: defaultProp, name, state = 'value', }) {
    // isControlled is ignored in the hook dependency lists as it should never change.
    const { current: isControlled } = React__default.useRef(controlled !== undefined);
    const [valueState, setValue] = React__default.useState(defaultProp);
    const value = isControlled ? controlled : valueState;
    if (process.env.NODE_ENV !== 'production') {
        const { current: defaultValue } = React__default.useRef(defaultProp);
        React__default.useEffect(() => {
            throwControlChangeError(isControlled, controlled, name, state);
            throwDefaultStateError(isControlled, defaultValue, defaultProp, name, state);
        }, [state, name, controlled, defaultProp]);
    }
    const setValueIfUncontrolled = React__default.useCallback((newValue) => {
        if (!isControlled) {
            setValue(newValue);
        }
    }, [setValue]);
    return [value, setValueIfUncontrolled];
}

function setRef(ref, value) {
    if (typeof ref === 'function') {
        ref(value);
    }
    else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = value;
    }
}
/**
 * useForkRef
 * Joins refs together and returns a combination of the two as a new ref
 *
 * @param refA
 * @param refB
 */
function useForkRef(refA, refB) {
    /**
     * This will create a new function if the ref props change and are defined.
     * This means react will call the old forkRef with `null` and the new forkRef
     * with the ref. Cleanup naturally emerges from this behavior
     */
    return useMemo(() => {
        if (refA == null && refB == null) {
            return null;
        }
        return (refValue) => {
            setRef(refA, refValue);
            setRef(refB, refValue);
        };
    }, [refA, refB]);
}

function useInputStyles({ onChange, ...props }) {
    /** extract props without affecting passthrough */
    const { checked: isChecked, defaultChecked: isDefaultChecked, disabled: isDisabled, 'aria-disabled': isAriaDisabled, readOnly: isReadOnly, 'aria-readonly': isAriaReadOnly, } = props;
    /** configure style-props */
    const disabled = isDisabled || isAriaDisabled === 'true';
    const readOnly = isReadOnly || isAriaReadOnly === 'true';
    const [checked, setIsChecked] = useState(isDefaultChecked ?? isChecked ?? false);
    React__default.useEffect(() => {
        setIsChecked(isChecked ?? false);
    }, [isChecked]);
    /** handle change for checked style (controlled/uncontrolled) */
    const handleChange = (e) => {
        setIsChecked(e.target.checked);
        onChange?.(e);
    };
    const styleProps = {
        checked,
        disabled,
        readOnly,
    };
    const inputProps = { ...props, onChange: handleChange };
    return {
        styleProps,
        inputProps,
    };
}

const useStyles$V = createUseStyles({
    inputContainer: {
        display: 'inline-flex',
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        width: '1em',
        minWidth: '1em',
        cursor: 'pointer',
    },
    input: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        opacity: 0.00001,
        zIndex: 1,
        margin: 0,
        cursor: ({ disabled, readOnly }) => disabled || readOnly ? 'default' : 'pointer',
        pointerEvents: ({ disabled, readOnly }) => disabled || readOnly ? 'none' : undefined,
    },
    controlIcon: {
        width: '100%',
        height: '100%',
        opacity: ({ checked }) => (checked ? 1 : 0),
        transition: 'opacity .2s linear',
    },
});

function BaseInput({ element: Element = 'span', className, icon, checked: checkedProp, defaultChecked, onClick, onChange, ...inputProps }, forwardedRef) {
    /** extract props without affecting passthrough */
    const { disabled: disabledProp, 'aria-disabled': ariaDisabledProp, readOnly: readOnlyProp, 'aria-readonly': ariaReadOnlyProp, } = inputProps;
    const disabled = disabledProp || ariaDisabledProp === 'true';
    const readOnly = readOnlyProp || ariaReadOnlyProp === 'true';
    const [checked, setChecked] = useControlledState({
        controlled: checkedProp,
        default: defaultChecked,
        state: 'checked',
        name: 'BaseInput',
    });
    const handleChange = (e) => {
        if (e.nativeEvent.defaultPrevented) {
            return;
        }
        if (disabled || readOnly) {
            e.preventDefault();
            return;
        }
        const newChecked = e.target.checked;
        setChecked(newChecked);
        onChange?.(e);
    };
    const handleClick = (e) => {
        if (disabled || readOnly) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        onClick?.(e);
    };
    const { inputContainer, input, controlIcon } = useStyles$V({
        disabled,
        readOnly,
        checked,
    });
    return (React__default.createElement(Element, { className: classNames(inputContainer, className) },
        React__default.createElement("input", { className: input, checked: checkedProp, defaultChecked: defaultChecked, onChange: handleChange, onClick: handleClick, ...inputProps, ref: forwardedRef }),
        React__default.cloneElement(icon, {
            className: controlIcon,
        })));
}
var BaseInput$1 = forwardRef(BaseInput);

function CheckboxIcon() {
    return (React__default.createElement("g", { className: "checkbox-icon" },
        React__default.createElement("rect", { fill: "currentColor", className: "background", width: "16", height: "16", rx: "2", ry: "2" }),
        React__default.createElement("path", { fill: "#fff", className: "checkmark", transform: "matrix(.91924 .91924 -.91924 .91924 -.72152 -9.5796)", d: "m14.521 0h1.4788v8h-5.0216l-0.002914-1.4404h3.5456l-1e-6 -6.5596z" })));
}
var CheckboxIcon$1 = withSvgIcon(CheckboxIcon, 'CheckboxIcon');

function RadioIcon() {
    return (React__default.createElement("g", { className: "radio-icon" },
        React__default.createElement("ellipse", { fill: "currentColor", className: "background", transform: "translate(8 8)", rx: "8", ry: "8" }),
        React__default.createElement("path", { fill: "#fff", className: "checkmark", transform: "matrix(.91924 .91924 -.91924 .91924 -.72152 -9.5796)", d: "m14.521 0h1.4788v8h-5.0216l-0.002914-1.4404h3.5456l-1e-6 -6.5596z" })));
}
var RadioIcon$1 = withSvgIcon(RadioIcon, 'RadioIcon');

function SelectIcon() {
    return (React__default.createElement("g", { className: "select-icon" },
        React__default.createElement("polygon", { className: "select-arrow select-arrow-top", transform: "matrix(1.4619 0 0 1.4619 5.8783 5.4791)", points: "1.4514 -3.0633 5.5556 1.0409 -2.6529 1.0409", fill: "currentColor", stroke: "transparent", strokeLinejoin: "round", strokeWidth: ".5" }),
        React__default.createElement("polygon", { className: "select-arrow select-arrow-bottom", transform: "matrix(-1.4619 0 0 -1.4619 10.122 10.522)", points: "1.4514 -3.0633 5.5556 1.0409 -2.6529 1.0409", fill: "currentColor", stroke: "transparent", strokeLinejoin: "round", strokeWidth: ".5" })));
}
var SelectIcon$1 = withSvgIcon(SelectIcon, 'SelectIcon');

function CaretDown() {
    return (React__default.createElement("g", { className: "caret-down-icon" },
        React__default.createElement("path", { d: "M13.254 4c.19.001.38.088.526.26.267.316.29.81.073 1.158l-.073.1L8.53 11.74l-.084.086a.66.66 0 0 1-.901-.007l-.076-.08L2.22 5.523l-.073-.1a1.024 1.024 0 0 1 0-1.058l.073-.1.084-.085a.674.674 0 0 1 .446-.175V4h10.504z", fill: "currentColor" })));
}
var CaretDownIcon = withSvgIcon(CaretDown, 'CaretDownIcon');

function CaretDownOutline() {
    return (React__default.createElement("g", { className: "caret-down-icon" },
        React__default.createElement("path", { d: "m922.74 642.16l-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z", transform: "matrix(-.016568 0 0 -.016568 15.842 16.026)", fill: "currentcolor" })));
}
var CaretDownOutlineIcon = withSvgIcon(CaretDownOutline, 'CaretDownOutlineIcon');

function CaretUpOutline() {
    return (React__default.createElement("g", { className: "caret-up-icon" },
        React__default.createElement("path", { d: "m922.74 642.16-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z", transform: "matrix(.016568 0 0 .016568 .15803 -.025552)", fill: "currentcolor" })));
}
var CaretUpOutlineIcon = withSvgIcon(CaretUpOutline, 'CaretUpOutlineIcon');

function CaretLeftOutline() {
    return (React__default.createElement("g", { className: "caret-left-icon" },
        React__default.createElement("path", { transform: "matrix(0 -.016568 .016568 0 -.025552 15.842)", d: "m922.74 642.16-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z", fill: "currentcolor" })));
}
var CaretLeftOutlineIcon = withSvgIcon(CaretLeftOutline, 'CaretLeftOutlineIcon');

function CaretRightOutline() {
    return (React__default.createElement("g", { className: "caret-right-icon" },
        React__default.createElement("path", { transform: "matrix(0 .016568 -.016568 0 16.026 .15803)", d: "m922.74 642.16-415.95-416.06c-18.533-18.538-48.507-18.538-67.04 0l-415.85 416.06c-18.533 18.538-18.533 48.519 0 67.057l33.473 33.481c18.533 18.538 48.507 18.538 67.04 0l348.91-349 348.91 349c18.533 18.538 48.507 18.538 67.04 0l33.473-33.481c18.533-18.538 18.533-48.519 0-67.057z", fill: "currentcolor" })));
}
var CaretRightOutlineIcon = withSvgIcon(CaretRightOutline, 'CaretRightOutlineIcon');

function DragHandleIcon() {
    return (React__default.createElement("g", { className: "drag-handle-icon" },
        React__default.createElement("rect", { transform: "matrix(1 0 0 1.4 1 2)", width: "14", height: "1.4286", rx: ".71", ry: ".71", strokeWidth: "0", fill: "currentColor" }),
        React__default.createElement("rect", { transform: "matrix(1 0 0 1.4 1 5.3333)", width: "14", height: "1.4286", rx: ".71", ry: ".71", strokeWidth: "0", fill: "currentColor" }),
        React__default.createElement("rect", { transform: "matrix(1 0 0 1.4 1 8.6667)", width: "14", height: "1.4286", rx: ".71", ry: ".71", strokeWidth: "0", fill: "currentColor" }),
        React__default.createElement("rect", { transform: "matrix(1 0 0 1.4 1 12)", width: "14", height: "1.4286", rx: ".71", ry: ".71", strokeWidth: "0", fill: "currentColor" })));
}
var DragHandleIcon$1 = withSvgIcon(DragHandleIcon, 'DragHandleIcon');

function Calendar() {
    return (React__default.createElement("g", { fill: "currentcolor", transform: "matrix(.031614 0 0 .031614 .33438 .33438)" },
        React__default.createElement("path", { d: "m405.17 363.15h-55.727c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4h55.727c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4z" }),
        React__default.createElement("path", { d: "m314.23 363.15h-55.727c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4h55.727c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4z" }),
        React__default.createElement("path", { d: "m223.28 363.15h-55.726c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4h55.726c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4z" }),
        React__default.createElement("path", { d: "m132.34 363.15h-55.725c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4h55.726c2.209 0 4-1.791 4-4v-48.256c-1e-3 -2.209-1.792-4-4.001-4z" }),
        React__default.createElement("path", { d: "m349.44 340.41h55.727c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4h-55.727c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4z" }),
        React__default.createElement("path", { d: "m258.5 340.41h55.726c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4h-55.726c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4z" }),
        React__default.createElement("path", { d: "m167.56 340.41h55.726c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4h-55.726c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4z" }),
        React__default.createElement("path", { d: "m76.611 340.41h55.726c2.209 0 4-1.791 4-4v-48.256c0-2.209-1.791-4-4-4h-55.726c-2.209 0-4 1.791-4 4v48.256c0 2.209 1.791 4 4 4z" }),
        React__default.createElement("path", { d: "m349.44 261.41h55.727c2.209 0 4-1.791 4-4v-48.255c0-2.209-1.791-4-4-4h-55.727c-2.209 0-4 1.791-4 4v48.255c0 2.209 1.791 4 4 4z" }),
        React__default.createElement("path", { d: "m258.5 261.41h55.727c2.209 0 4-1.791 4-4v-48.255c0-2.209-1.791-4-4-4h-55.727c-2.209 0-4 1.791-4 4v48.255c0 2.209 1.791 4 4 4z" }),
        React__default.createElement("path", { d: "m363.37 114.86h6.938c10.543 0 19.09-8.549 19.09-19.091v-72.39c0-10.54-8.547-19.089-19.09-19.089h-6.938c-10.545 0-19.092 8.549-19.092 19.089v72.389c2e-3 10.543 8.549 19.092 19.092 19.092z" }),
        React__default.createElement("path", { d: "m118.5 115.33h6.938c10.544 0 19.091-8.55 19.091-19.091v-72.389c0-10.541-8.547-19.09-19.091-19.09h-6.938c-10.543 0-19.09 8.549-19.09 19.09v72.389c0 10.541 8.547 19.091 19.09 19.091z" }),
        React__default.createElement("path", { d: "M453.916,43.558h-48.996v57.209c0,19.084-15.525,29.61-34.607,29.61h-6.938c-19.084,0-34.609-15.526-34.609-34.61v-52.209h-168.713v52.682c0,19.084-15.525,34.61-34.609,34.61h-6.938c-19.083,0-34.608-15.526-34.608-34.61v-52.682h-52.862C13.923,43.558,0,57.481,0,74.595v375.03c0,17.114,13.923,31.037,31.036,31.037h422.88c17.113,0,31.035-13.923,31.035-31.037v-375.03c.002-17.113-13.922-31.037-31.035-31.037Zm0,406.067h-422.879l-.001-283.213h422.886l.016,283.212c-.002,0-.008.001-.022.001Z" })));
}
var CalendarIcon = withSvgIcon(Calendar, 'CalendarIcon');

function CheckOutlineIcon() {
    return (React__default.createElement("g", { className: "checkoutline-icon" },
        React__default.createElement("path", { fill: "currentcolor", transform: "matrix(.8 0 0 0.8-1.6-1.6)", d: "M10.6,13.8L8.45,11.65q-.275-.275-.7-.275t-.7.275-.275.7.275.7L9.9,15.9q.3.3.7.3t.7-.3l5.65-5.65q.275-.275.275-.7t-.275-.7-.7-.275-.7.275L10.6,13.8ZM12,22q-2.075,0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175t-.788-3.9q0-2.075.788-3.9t2.137-3.175Q6.275,3.575,8.1,2.788t3.9-.788q2.075,0,3.9.788t3.175,2.137q1.35,1.35,2.138,3.175t.787,3.9q0,2.075-.788,3.9t-2.137,3.175q-1.35,1.35-3.175,2.138t-3.9.787Zm0-2q3.35,0,5.675-2.325t2.325-5.675-2.325-5.675-5.675-2.325-5.675,2.325Q4,8.65,4,12t2.325,5.675t5.675,2.325Zm0-8" })));
}
var CheckOutlineIcon$1 = withSvgIcon(CheckOutlineIcon, 'CheckOutlineIcon');

function CloseOutlineIcon() {
    return (React__default.createElement("g", { className: "closeoutline-icon", fill: "currentColor" },
        React__default.createElement("path", { d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm6.5 8a6.5 6.5 0 1 0-13 0 6.5 6.5 0 0 0 13 0zm-3.318-3.182a.75.75 0 0 1 0 1.06L9.061 8l2.121 2.121a.75.75 0 0 1-1.06 1.061L8 9.061l-2.121 2.121a.75.75 0 0 1-1.061-1.06L6.939 8 4.818 5.879a.75.75 0 0 1 1.06-1.061L8 6.939l2.121-2.121a.75.75 0 0 1 1.061 0z" })));
}
var CloseOutlineIcon$1 = withSvgIcon(CloseOutlineIcon, 'CloseOutlineIcon');

function CloudArrowUpIcon() {
    return (React__default.createElement("g", { className: "cloudarrowup-icon", fill: "currentColor" },
        React__default.createElement("path", { d: "m8.446 6.397.084.073 2.5 2.5a.75.75 0 0 1-.976 1.133l-.084-.073-1.22-1.219v5.439a.75.75 0 0 1-1.493.102l-.007-.102V8.811l-1.22 1.22a.75.75 0 0 1-.976.072l-.084-.073a.75.75 0 0 1-.073-.976l.073-.084 2.5-2.5a.75.75 0 0 1 .976-.073zM9.5 1a4.75 4.75 0 0 1 4.677 5.586l-.057.271-.013.046.113.06a3.499 3.499 0 0 1 1.757 2.64l.018.213.005.193a3.491 3.491 0 0 1-3.3 3.486l-.191.005H10.75a.75.75 0 1 1 0-1.5h1.759c1.05 0 1.91-.812 1.986-1.843l.005-.148c0-.922-.63-1.717-1.511-1.936a.75.75 0 0 1-.507-1.027 3.25 3.25 0 1 0-6.1-2.213.75.75 0 0 1-1.07.453l-.217-.136a1.25 1.25 0 0 0-1.716 1.654l.12.212a.75.75 0 0 1-.494 1.048 1.998 1.998 0 0 0 .346 3.93L3.5 12h1.75a.75.75 0 1 1 0 1.5H3.5a3.5 3.5 0 0 1-3.495-3.308L0 10c0-1.217.626-2.308 1.597-2.936l.175-.107.063-.034-.02-.08-.036-.195-.022-.198-.007-.2a2.75 2.75 0 0 1 3.18-2.717l.209.042.12.033.094-.177a4.753 4.753 0 0 1 3.692-2.41l.236-.016L9.5 1z" })));
}
var CloudArrowUpIcon$1 = withSvgIcon(CloudArrowUpIcon, 'CloudArrowUpIcon');

function HeartIcon() {
    return (React__default.createElement("g", { className: "heart-icon" },
        React__default.createElement("path", { d: "m14.78 2.259c-0.77098-0.8019-1.8346-1.2563-2.947-1.259-1.1121 0.002982-2.1753 0.45735-2.946 1.259l-0.887 0.915-0.889-0.915c-0.76906-0.80332-1.8329-1.2576-2.945-1.2576s-2.1759 0.4543-2.945 1.2576c-1.628 1.677-1.628 4.396 0 6.074l6.243 6.438c0.13979 0.14616 0.33325 0.22883 0.5355 0.22883s0.39571-0.082668 0.5355-0.22883l6.244-6.438c0.7858-0.81546 1.2237-1.9046 1.221-3.037 0-1.14-0.44-2.232-1.22-3.037z", fill: "currentColor" })));
}
var HeartIcon$1 = withSvgIcon(HeartIcon, 'HeartIcon');

function StarIcon() {
    return (React__default.createElement("g", { className: "star-icon" },
        React__default.createElement("path", { d: "M5.738,5.528l-4.999.008-.103.007c-.634.085-.878.933-.34,1.345l4.06,3.105-1.548,5.028-.023.094c-.12.65.623,1.149,1.17.732L8,12.772l4.044,3.075.08.053c.564.332,1.272-.215,1.068-.879l-1.55-5.028l4.062-3.105.078-.069c.457-.454.152-1.282-.521-1.283l-5-.008L8.704,0.525c-.093271-.310889-.379421-.523792-.704-.523792s-.610729.212903-.704.523792L5.738,5.528Z", fill: "currentColor" })));
}
var StarIcon$1 = withSvgIcon(StarIcon, 'StarIcon');

function ThumbsUpIcon() {
    return (React__default.createElement("g", { className: "thumbs-up-icon" },
        React__default.createElement("path", { d: "M10,2.509c.125.598.06,1.217-.048,1.737l-.068.298-.073.269-.166.567c-.02.08-.022.122,0,.122h5.105c.462363-.026197.901269.205584,1.140346.602205s.239077.892969,0,1.28959-.677983.628402-1.140346.602205h-.5c.462363-.026197.901269.205584,1.140346.602205s.239077.892969,0,1.28959-.677983.628402-1.140346.602205l-.5.001c.462363-.026197.901269.205584,1.140346.602205s.239077.892969,0,1.28959-.677983.628402-1.140346.602205h-.75c.364265-.011529.705904.176222.891446.489903s.185543.703512,0,1.017194-.527181.501432-.891446.489903L8.078,15l-1.526-.013-.13-.005c-.378-.023-.8-.155-1.263-.397-.097921-.05214-.159074-.154063-.159-.265v-8.148c-.000163-.095485.045138-.185345.122-.242c1.113-.812,1.834-1.54,2.162-2.182.15-.303.245-.653.328-1l.12-.51c.148-.606.32-1.117.769-1.226.33-.08,1.23.205,1.5,1.497ZM2.8,6.001c.386209,0,.699448.312791.7.699v7.084c-.000552.386209-.313791.699-.7.699h-2.1c-.386209,0-.699448-.312791-.7-.699L0,6.7c0-.185652.07375-.363699.205025-.494975s.309323-.205025.494975-.205025h2.1Z", fill: "currentColor" })));
}
var ThumbsUpIcon$1 = withSvgIcon(ThumbsUpIcon, 'ThumbsUpIcon');

function SmileyIcon() {
    return (React__default.createElement("g", { className: "smiley-icon" },
        React__default.createElement("path", { d: "m8 0c4.4183 0 8 3.5817 8 8s-3.5817 8-8 8-8-3.5817-8-8 3.5817-8 8-8zm3.738 8.815c-0.1822-0.079843-0.38866-0.08403-0.57395-0.01164s-0.33423 0.21543-0.41405 0.39764c-0.4769 1.0938-1.5567 1.801-2.75 1.801s-2.2731-0.7072-2.75-1.801c-0.16624-0.37942-0.60858-0.55224-0.988-0.386s-0.55224 0.60858-0.386 0.988c0.71589 1.6395 2.335 2.6993 4.124 2.6993s3.4081-1.0597 4.124-2.6993c0.079843-0.1822 0.08403-0.38866 0.01164-0.57395s-0.21543-0.33423-0.39764-0.41405zm-7.238-3.315c-0.55228 0-1 0.44772-1 1s0.44772 1 1 1 1-0.44772 1-1-0.44772-1-1-1zm7 0c-0.55228 0-1 0.44772-1 1s0.44772 1 1 1 1-0.44772 1-1-0.44772-1-1-1z", fill: "currentColor" })));
}
var SmileyIcon$1 = withSvgIcon(SmileyIcon, 'SmileyIcon');

function InfoIcon() {
    return (React__default.createElement("g", { className: "info-icon" },
        React__default.createElement("path", { fill: "currentcolor", d: "M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm.775 6.632c-.081-.613-.868-.877-1.302-.39L5.94 7.963l-.067.088a.746.746 0 0 0 .13.967l.088.067c.305.2.72.15.969-.13l.224-.252.001 4.498.007.106c.082.613.868.877 1.302.39l1.567-1.756.068-.088a.746.746 0 0 0-.129-.967l-.088-.067a.75.75 0 0 0-.97.128l-.26.29.001-4.5zm-.742-4.181a.748.748 0 0 0-.749.747v1.05l.007.102a.749.749 0 0 0 1.491-.101V3.198l-.007-.101a.749.749 0 0 0-.742-.646z" })));
}
var InfoIcon$1 = withSvgIcon(InfoIcon, 'InfoIcon');

function TrashIcon() {
    return (React__default.createElement("g", { className: "trash-icon" },
        React__default.createElement("path", { fill: "currentColor", d: "M9.583 0a1.25 1.25 0 0 1 1.2.902l.03.125L11.171 3 15.25 3a.75.75 0 1 1 0 1.5h-1.298l-.673 10.335a1.25 1.25 0 0 1-1.117 1.162l-.13.006H3.968a1.25 1.25 0 0 1-1.232-1.039l-.015-.13L2.048 4.5.75 4.5a.75.75 0 0 1 0-1.5h4.079l.359-1.973A1.25 1.25 0 0 1 6.289.007L6.417 0h3.166zm2.867 4.5H3.551l.651 10.002h7.595L12.45 4.5zm-6.226 2c.4 0 .724.324.724.724v4.552a.724.724 0 0 1-1.448 0V7.224c0-.4.324-.724.724-.724zm3.526 0a.75.75 0 0 1 .75.75v4.5a.75.75 0 1 1-1.5 0v-4.5a.75.75 0 0 1 .75-.75zm-.376-5H6.626L6.352 3h3.294l-.272-1.5z" })));
}
var TrashIcon$1 = withSvgIcon(TrashIcon, 'TrashIcon');

const useStyles$U = createUseStyles((theme) => ({
    checkboxInput: ({ checked, disabled, readOnly }) => {
        const checkBgColor = checked ? theme.questionColor : '#fff';
        const backgroundColor = disabled || readOnly ? '#aaa' : checkBgColor;
        return {
            color: disabled || readOnly ? '#aaa' : theme.questionColor,
            borderRadius: 2,
            backgroundColor,
            border: `1px solid ${disabled || readOnly ? '#aaa' : theme.questionColor}`,
            '& .checkmark': {
                fill: theme.isDark ? '#000' : '#fff',
            },
            '&:focus-within': {
                boxShadow: `0 0 0 1px ${theme.primaryAccentColor}`,
                borderColor: '#fff',
            },
            '& input': {
                cursor: 'pointer',
            },
        };
    },
}));

function CheckboxInput({ className, ...props }, ref) {
    const { inputProps, styleProps } = useInputStyles(props);
    const { checkboxInput } = useStyles$U(styleProps);
    return (React__default.createElement(BaseInput$1, { ref: ref, type: "checkbox", className: classNames(checkboxInput, className), icon: React__default.createElement(CheckboxIcon$1, null), ...inputProps }));
}
var CheckboxInput$1 = React__default.forwardRef(CheckboxInput);

// get the margin top by taking the font size and the line height and divide by 2 to get the margin top
const marginTopHelper$1 = (height = 0, lineHeight = 1) => {
    const heightNumber = parseFloat(height.toString());
    const lineHeightNumber = parseFloat(lineHeight.toString());
    return Math.max(0, (heightNumber * lineHeightNumber - heightNumber) / 2); // no negative margins
};
const useStyles$T = createUseStyles((theme) => ({
    checkboxContainer: ({ checked, disabled }) => {
        const borderStyle = checked ? 'solid' : 'dashed';
        const borderColor = disabled && !checked ? 'transparent' : theme.questionColor;
        const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
        return {
            display: 'flex',
            alignItems: 'start',
            cursor: 'pointer',
            padding: [5, 8, 7, 8],
            gap: 8,
            borderRadius: 4,
            border: [1, 'solid', checked ? theme.questionColor : 'transparent'],
            backgroundColor: checked ? theme.input.bgColor : 'inherit',
            ...theme.questionBody,
            lineHeight: 1.5,
            fontWeight: checked
                ? fontWeightOptions.medium
                : theme.questionBody.fontWeight ?? fontWeightOptions.regular,
            '&:hover': {
                border: [1, borderStyle, borderColor],
            },
            '&:focus-within': {
                background: theme.input.bgColor,
            },
        };
    },
    icon: {
        flexShrink: 0,
        fontSize: ({ inputSize }) => inputSize ?? '1em',
        marginTop: marginTopHelper$1(theme.questionBody?.fontSize, 1.5),
        // The following selector singles out Safari and Chrome on iOS14 to
        // implement a workaround for the lack of flex gap.
        '@supports (-webkit-touch-callout: none) and (not (translate: none))': {
            marginRight: 8,
        },
    },
}));

function Checkbox$2({ children: label = '', inputSize, onChange, ...props }, ref) {
    const handleChange = (e) => {
        onChange?.({
            id: e.target.id,
            checked: e.target.checked,
            value: e.target.value,
        });
    };
    const { inputProps, styleProps } = useInputStyles({
        ...props,
        onChange: handleChange,
    });
    const { checkboxContainer, icon } = useStyles$T({ ...styleProps, inputSize });
    return (React__default.createElement("label", { htmlFor: inputProps.id, className: checkboxContainer, tabIndex: -1 },
        React__default.createElement(CheckboxInput$1, { ref: ref, ...inputProps, className: icon }),
        React__default.createElement(RichText, { element: "span", text: label })));
}
var Checkbox$3 = forwardRef(Checkbox$2);

const useStyles$S = createUseStyles({
    imageStyles: {
        maxWidth: '100%',
        display: 'block',
    },
});

function Image({ alt = '', className, ...props }) {
    const { imageStyles } = useStyles$S();
    return (React__default.createElement("img", { className: classNames(imageStyles, className), "data-testid": "image", alt: alt, ...props }));
}

const useStyles$R = createUseStyles((theme) => ({
    imageChoice: {
        display: 'flex',
        position: 'relative',
    },
    labelContainer: ({ checked, disabled }) => {
        const borderColor = disabled && !checked ? 'transparent' : theme.primaryAccentColor;
        return {
            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
            cursor: 'pointer',
            width: '100%',
            border: [
                1,
                'solid',
                checked
                    ? theme.questionColor
                    : theme.questionBody.highlightColor ?? 'rgb(208, 210, 211)',
            ],
            borderRadius: 2,
            backgroundColor: checked ? theme.input.bgColor : 'inherit',
            ...theme.questionBody,
            '&:hover': {
                border: [1, 'solid', borderColor],
            },
            '&:focus-within': {
                border: [1, 'solid', borderColor],
            },
        };
    },
    controlImage: {
        position: 'absolute',
        right: '-1',
        top: '-1',
        width: '26px',
        height: '26px',
        transitionDuration: '0.3s',
        border: 'none',
        borderRadius: 2,
        zIndex: 10,
        transform: ({ checked }) => (checked ? 'scale(1)' : 'scale(0)'),
    },
    optionLabel: ({ checked }) => {
        return {
            padding: '9px 7px',
            width: '100%',
            wordWrap: 'break-word',
            fontWeight: checked ? 'bold' : 'normal',
            '&:focus-within': {
                background: theme.questionBody.highlightColor ?? 'rgb(208, 210, 211)',
            },
        };
    },
    imageContainer: {
        paddingTop: '75%',
        width: '100%',
        position: 'relative',
        '& > img': {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        },
    },
    inputContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: '#EFEFEF',
    },
}));

const useStyles$Q = createUseStyles((theme) => ({
    imageInput: ({ checked, disabled, readOnly }) => {
        const checkBgColor = checked ? theme.questionColor : 'transparent';
        const backgroundColor = disabled || readOnly ? '#aaa' : checkBgColor;
        return {
            color: disabled || readOnly ? '#aaa' : theme.input.activeColor,
            border: 'none',
            borderRadius: 4,
            backgroundColor,
            '& .checkmark': {
                fill: contrastColor(theme.input.activeColor),
            },
            '& input': {
                cursor: 'pointer',
            },
        };
    },
}));

function ImageInput({ className, type, ...props }, ref) {
    const { inputProps, styleProps } = useInputStyles(props);
    const { imageInput } = useStyles$Q(styleProps);
    return (React__default.createElement(BaseInput$1, { ref: ref, type: type, className: classNames(imageInput, className), icon: React__default.createElement(CheckboxIcon$1, null), ...inputProps }));
}
var ImageInput$1 = React__default.forwardRef(ImageInput);

function ImageChoice$2({ id, type, alt = '', label, src: imageSrc, className, checked, name, onChange, onKeyDown, onClick, disabled: disabledProp, 'aria-disabled': ariaDisabledProp, readOnly: readOnlyProp, 'aria-readonly': ariaReadOnlyProp, ...inputProps }, ref) {
    const handleChange = (e) => {
        onChange?.({
            id: e.target.id,
            checked: e.target.checked,
            value: e.target.value,
        });
    };
    const handleClick = (e) => {
        onClick?.(e);
        if (e.isDefaultPrevented()) {
            return;
        }
        const target = e.target;
        let checkVal = true;
        if (!inputProps.required && checked) {
            checkVal = false;
        }
        onChange?.({
            id: target.id,
            checked: checkVal,
            value: target.value,
        });
    };
    const handleKeyboard = (e) => {
        onKeyDown?.(e);
        if (e.isDefaultPrevented()) {
            return;
        }
        const target = e.target;
        if (!inputProps.required && checked && e.code === 'Space') {
            e.preventDefault();
            onChange?.({
                id: target.id,
                checked: false,
                value: target.value,
            });
        }
    };
    const disabled = disabledProp || ariaDisabledProp === 'true';
    const readOnly = readOnlyProp || ariaReadOnlyProp === 'true';
    const { imageChoice, controlImage, inputContainer, labelContainer, optionLabel, imageContainer, } = useStyles$R({
        disabled,
        readOnly,
        checked,
    });
    const imageId = `${id}-image`;
    const labelId = `${id}-label`;
    const ariaDescribeId = alt ? imageId : undefined;
    const imageInputEvents = type === 'radio'
        ? {
            onClick: handleClick,
            onKeyDown: handleKeyboard,
            onChange: handleChange,
        }
        : { onChange: handleChange };
    return (React__default.createElement("div", { className: imageChoice },
        React__default.createElement("label", { htmlFor: id, className: classNames(labelContainer, className), tabIndex: -1 },
            React__default.createElement("div", { className: inputContainer },
                React__default.createElement(ImageInput$1, { "aria-labelledby": labelId, "aria-describedby": ariaDescribeId, ref: ref, checked: checked, className: controlImage, id: id, type: type, value: id, name: name, ...inputProps, ...imageInputEvents }),
                React__default.createElement("div", { className: imageContainer },
                    React__default.createElement("img", { id: imageId, src: imageSrc, alt: alt }))),
            label && (React__default.createElement("span", { id: labelId, className: optionLabel }, label)))));
}
var ImageChoiceOption = forwardRef(ImageChoice$2);

const useStyles$P = createUseStyles({
    listBox: {
        listStyle: 'none',
        width: 'fit-content',
        overflowY: 'auto',
        overflowX: 'hidden',
        '&:focus-visible': {
            outline: '#005fcc auto 1px',
            outlineOffset: 1,
        },
    },
});

/**
 * Scroll to the item at the specified index if it is fully or partially obscured
 * */
const scrollToItem = (itemRect, containerRef) => {
    const ulRect = containerRef.current?.getBoundingClientRect();
    if (!ulRect || !containerRef.current) {
        return;
    }
    if (itemRect.top < ulRect.top) {
        // eslint-disable-next-line no-param-reassign
        containerRef.current.scrollTop -= ulRect.top - itemRect.top;
    }
    else if (itemRect.bottom > ulRect.bottom) {
        // eslint-disable-next-line no-param-reassign
        containerRef.current.scrollTop += itemRect.bottom - ulRect.bottom;
    }
};
/**
 * Returns the number of items visible inside the listbox given its current dimensions
 * note: if height is 0, it will return 10 as a default to satisfy tests
 * */
const numItemsVisible = (containerHeight, itemHeight = 48) => {
    return !containerHeight ? 10 : Math.round(containerHeight / itemHeight);
};

/**
 * Focus another item in the listBox depending on which key is pressed
 * @param key The key that was pressed
 * @param focusedIndex The index of the currently focused item
 * @param itemCount The number of items in the listBox
 * @param visibleItemCount The number of items visible in the listBox
 * @param focusItem Function which updates the the currently focused item
 */
const focusNext = (key, focusedIndex, itemCount, visibleItemCount, focusItem) => {
    const lastIndex = itemCount - 1;
    if (key === 'End') {
        focusItem(lastIndex);
        return;
    }
    if (focusedIndex === null) {
        focusItem(0);
        return;
    }
    switch (key) {
        case 'Down':
        case 'ArrowDown':
            if (focusedIndex < lastIndex) {
                focusItem(focusedIndex + 1);
            }
            else {
                focusItem(0);
            }
            break;
        case 'Up':
        case 'ArrowUp':
            if (focusedIndex > 0) {
                focusItem(focusedIndex - 1);
            }
            else {
                focusItem(lastIndex);
            }
            break;
        case 'PageDown':
            if (focusedIndex < lastIndex - visibleItemCount) {
                focusItem(focusedIndex + visibleItemCount);
            }
            else {
                focusItem(lastIndex);
            }
            break;
        case 'PageUp':
            if (focusedIndex > visibleItemCount) {
                focusItem(focusedIndex - visibleItemCount);
            }
            else {
                focusItem(0);
            }
            break;
        case 'Home':
            focusItem(0);
            break;
    }
};

React__default.forwardRef(({ children, focusedIndex: externalFocusedIndex, tabIndex, onKeyDown, onClick, onChange, className, ...rest }, ref) => {
    const { listBox } = useStyles$P();
    const [focusedIndex, setFocusedIndex] = useState(null);
    // override internal focusedIndex with external state if provided
    useEffect(() => {
        if (externalFocusedIndex !== undefined) {
            setFocusedIndex(externalFocusedIndex);
        }
    }, [externalFocusedIndex]);
    const itemsRef = useRef([]);
    const ulRef = useRef(null);
    const focusItem = (index) => {
        setFocusedIndex(index);
    };
    useImperativeHandle(ref, () => ulRef.current);
    useEffect(() => {
        if (focusedIndex === null) {
            return;
        }
        // Scroll to the currently focused item anytime the focused index changes
        scrollToItem(itemsRef.current?.[focusedIndex].getBoundingClientRect(), ulRef);
        // call the onChange callback if the focused index changes
        onChange?.({
            target: itemsRef.current[focusedIndex],
            value: itemsRef.current[focusedIndex].dataset.value ?? '',
        });
    }, [focusedIndex]);
    const handleKeyDown = (e) => {
        onKeyDown?.(e);
        if (!(e.target instanceof HTMLUListElement)) {
            return;
        }
        if ([
            'Down',
            'Up',
            'ArrowDown',
            'ArrowUp',
            'PageDown',
            'PageUp',
            'End',
            'Home',
        ].includes(e.key)) {
            e.preventDefault();
            // Focus another item in the listBox depending on which key is pressed
            focusNext(e.key, focusedIndex, itemsRef.current.length, numItemsVisible(ulRef.current?.getBoundingClientRect().height), focusItem);
        }
    };
    const handleClick = (e) => {
        onClick?.(e);
        if (e.target instanceof HTMLLIElement) {
            setFocusedIndex(itemsRef.current.indexOf(e.target));
        }
    };
    const interactiveProps = {
        tabIndex,
        'aria-activedescendant': tabIndex !== undefined && focusedIndex !== null
            ? `ListBoxItem-${focusedIndex}`
            : undefined,
    };
    return (React__default.createElement("ul", { ref: ulRef, className: classNames(listBox, className), role: "listbox", onKeyDown: handleKeyDown, onClick: handleClick, ...interactiveProps, ...rest }, React__default.Children.map(children, (child, index) => React__default.isValidElement(child) &&
        React__default.cloneElement(child, {
            key: child.props.value,
            id: child.props.id ?? `ListBoxItem-${index}`,
            selected: index === focusedIndex,
            ref: (el) => {
                itemsRef.current[index] = el;
            },
        }))));
});

const useStyles$O = createUseStyles({
    listBoxItem: ({ selected, disabled }) => ({
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        backgroundColor: '#FFF',
        cursor: 'pointer',
        ...(selected && {
            backgroundColor: 'rgb(237, 238, 238)',
        }),
        '&:hover, &:focus': {
            backgroundColor: 'rgb(237, 238, 238)',
        },
        '&:active': {
            backgroundColor: 'rgb(224, 226, 226)',
        },
        ...(disabled && {
            opacity: 0.5,
            pointerEvents: 'none',
        }),
    }),
});

const ListBoxItem = React__default.forwardRef(({ children, disabled, selected, value, className, ...rest }, ref) => {
    const { listBoxItem } = useStyles$O({
        disabled,
        selected,
    });
    return (React__default.createElement("li", { ref: ref, "data-value": value, className: classNames(listBoxItem, className), role: "option", "aria-selected": selected ? true : undefined, ...rest }, children));
});

const useStyles$N = createUseStyles((theme) => {
    const { color, fontSize, fontFamily } = theme.questionBody;
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    const fontWeight = theme.name.includes('Pencils')
        ? fontWeightOptions.bold
        : fontWeightOptions.medium;
    return {
        selectContainerStyles: {
            maxWidth: '100%',
            position: 'relative',
            display: 'inline-flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            overflow: 'hidden',
            transition: 'all 0.1s linear',
            borderRadius: `4px`,
            border: `2px solid transparent`,
            outline: `1px solid transparent`,
            color: isDark(theme.answerColor) ? theme.answerColor : '#000',
            '&:focus-within, &:hover': {
                border: `2px solid ${theme.primaryAccentColor}`,
                outline: `1px solid #fff`,
            },
        },
        selectStyles: {
            position: 'relative',
            display: 'inline-flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            overflow: 'hidden',
            color: isDark(theme.answerColor) ? theme.answerColor : '#000',
            fontSize,
            [breakpointMedium]: {
                fontSize: '16px',
            },
            fontFamily,
            fontWeight,
            lineHeight: 'normal',
            outline: 'none',
            backgroundColor: '#fff',
            maxWidth: '100%',
            padding: ({ isRTL = false }) => isRTL ? '6px 6px 6px 1.75em' : '6px 1.75em 6px 6px',
            verticalAlign: 'bottom',
            textSizeAdjust: 'auto',
            // Prevent de fault chevron from showing
            mozAppearance: 'none',
            webkitAppearance: 'none',
            appearance: 'none',
            transition: 'border 0.1s linear',
            border: `1px solid ${color}`,
            '&:focus-within, &:hover': {
                border: `1px solid transparent`,
            },
        },
        selectIconStyles: {
            position: 'absolute',
            width: '1em',
            height: '1em',
            display: 'block',
            margin: '0 .5em',
            right: ({ isRTL = false }) => (isRTL ? 'auto' : 0),
            left: ({ isRTL = false }) => (isRTL ? 0 : 'auto'),
            zIndex: 2,
            pointerEvents: 'none',
        },
        disabledStyles: {
            opacity: 0.4,
        },
    };
});

function NativeSelect({ required, children, disabled, className, icon = React__default.createElement(SelectIcon$1, null), ...props }) {
    const { isRTL } = useContext(L10NContext);
    const { selectStyles, selectContainerStyles, selectIconStyles, disabledStyles, } = useStyles$N({ isRTL });
    const { className: iconClasses, ...iconProps } = icon.props;
    return (React__default.createElement("div", { className: classNames(selectContainerStyles, disabled ? disabledStyles : {}, className) },
        React__default.cloneElement(icon, {
            // this will apply our placement styles, along with any other supplied classes
            className: classNames(selectIconStyles, iconClasses),
            // apply all other props.
            ...iconProps,
        }),
        React__default.createElement("select", { className: selectStyles, required: required, "aria-required": required, "data-testid": "select", disabled: disabled, ...props }, children)));
}

const useStyles$M = createUseStyles(({ questionBody: { fontFamily, fontSize, fontWeight, color }, }) => ({
    selectOption: {
        color,
        fontFamily,
        fontSize,
        fontWeight,
    },
}));
function SelectAnswerOption({ id, label, ...props }) {
    const { selectOption } = useStyles$M();
    return (React__default.createElement("option", { id: id, "data-testid": "select-answer-option", className: selectOption, dangerouslySetInnerHTML: {
            __html: sanitizeString(label ?? ''),
        }, ...props }));
}

const radioGroupContext = createContext(undefined);

function useRadioGroup() {
    return useContext(radioGroupContext);
}

/**
 * SMQ Tier 3
 */
function RadioGroup({ children, name: nameProp, value: valueProp, defaultValue, onChange, disabled = false, readOnly = false, ...divProps }) {
    const [value, setValueState] = useControlledState({
        controlled: valueProp,
        default: defaultValue,
        name: 'RadioGroup',
    });
    const handleChange = (e) => {
        setValueState(e.value);
        onChange?.(e);
    };
    const name = nameProp;
    return (React__default.createElement(radioGroupContext.Provider
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    , { 
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value: { name, onChange: handleChange, value, disabled, readOnly } },
        React__default.createElement("div", { role: "radiogroup", ...divProps }, children)));
}

const useStyles$L = createUseStyles((theme) => ({
    radioInput: {
        color: theme.isDark ? '#fff' : '#000',
        backgroundColor: ({ checked }) => {
            if (checked) {
                return theme.isDark ? '#fff' : '#000';
            }
            return '#fff';
        },
        border: `1px solid ${theme.answerColor}`,
        borderRadius: '100%',
        '& .checkmark': {
            fill: theme.isDark ? '#000' : '#fff',
        },
        '&:focus-within': {
            boxShadow: `0 0 0 1px ${theme.primaryAccentColor}`,
            borderColor: '#fff',
        },
        '& input': {
            cursor: 'pointer',
        },
    },
}));

function RadioInput({ className, ...props }, forwardedRef) {
    const { inputProps, styleProps } = useInputStyles(props);
    const styles = useStyles$L(styleProps);
    return (React__default.createElement(BaseInput$1, { type: "radio", className: classNames(styles.radioInput, className), icon: React__default.createElement(RadioIcon$1, null), ref: forwardedRef, ...inputProps }));
}
var RadioInput$1 = forwardRef(RadioInput);

// get the margin top by taking the font size and the line height and divide by 2 to get the margin top
const marginTopHelper = (height = 0, lineHeight = 1) => {
    return Math.max(0, (height * lineHeight - height) / 2); // no negative margins
};
const useStyles$K = createUseStyles((theme) => ({
    controlLabel: ({ checked, disabled }) => {
        const borderStyle = checked ? 'solid' : 'dashed';
        const borderColor = disabled && !checked ? 'transparent' : theme.questionColor;
        const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
        const isPencilsTheme = theme.name?.toLowerCase().indexOf('pencils') > -1;
        let fontWeight = checked
            ? fontWeightOptions.medium
            : theme.questionBody.fontWeight ?? fontWeightOptions.light;
        if (isPencilsTheme) {
            fontWeight = fontWeightOptions.bold;
        }
        return {
            display: 'flex',
            alignItems: 'start',
            cursor: 'pointer',
            padding: [5, 8, 7, 8],
            gap: 8,
            borderRadius: 4,
            border: [1, 'solid', checked ? theme.questionColor : 'transparent'],
            backgroundColor: checked
                ? theme.correctAnswerHighlightColor
                : 'transparent',
            ...theme.questionBody,
            lineHeight: 1.5,
            fontWeight,
            '&:hover': {
                border: [1, borderStyle, borderColor],
            },
            '&:focus-within': {
                background: theme.correctAnswerHighlightColor,
            },
        };
    },
    icon: {
        flexShrink: 0,
        fontSize: ({ inputSize }) => inputSize ?? '1em',
        marginTop: marginTopHelper(theme.questionBody?.fontSize, 1.5),
        // The following selector singles out Safari and Chrome on iOS14 to
        // implement a workaround for the lack of flex gap.
        '@supports (-webkit-touch-callout: none) and (not (translate: none))': {
            marginRight: 8,
        },
    },
}));

function Radio({ children: label = '', className, onChange, checked: checkedProp, name: nameProp, disabled: disabledProp, readOnly: readOnlyProp, inputSize, ...props }, forwardedRef) {
    const radioGroup = useRadioGroup();
    let checked = checkedProp;
    let name = nameProp;
    if (radioGroup) {
        if (typeof checked === 'undefined') {
            checked = radioGroup.value === props.value;
        }
        if (typeof name === 'undefined') {
            name = radioGroup.name;
        }
    }
    const disabled = radioGroup?.disabled ?? disabledProp;
    const readOnly = radioGroup?.readOnly ?? readOnlyProp;
    const handleChange = (e) => {
        const { id, checked: isChecked, value } = e.target;
        radioGroup?.onChange({ id, checked: isChecked, value });
        onChange?.({ id, checked: isChecked, value });
    };
    const { inputProps, styleProps } = useInputStyles({
        ...props,
        name,
        checked,
        disabled,
        readOnly,
        onChange: handleChange,
    });
    const { controlLabel, icon } = useStyles$K({ ...styleProps, inputSize });
    return (React__default.createElement("label", { tabIndex: -1, htmlFor: props.id, className: classNames(controlLabel, className) },
        React__default.createElement(RadioInput$1, { ...inputProps, ref: forwardedRef, className: icon, "aria-labelledby": `${props.id}-label` }),
        React__default.createElement(RichText, { element: "span", text: label, id: `${props.id}-label` })));
}
var Radio$1 = forwardRef(Radio);

const useStyles$J = createUseStyles({
    listBoxContainer: {
        // TODO: Possible to utilize focus-visible to show only on keyboard interaction? (with child)
        '&:focus-within': {
            outline: '#005fcc auto 1px',
            outlineOffset: 1,
        },
    },
    listBox: {
        listStyle: 'none',
        width: 'fit-content',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: 0,
        // added background color to make the issue in https://surveymonkey.atlassian.net/browse/SPAGE-10825
        // a bit less jarring. This is a temporary fix until we can address the root cause.
        backgroundColor: '#fff',
    },
});

const ARIA_ACTIVEDESCENDANT = 'aria-activedescendant';
/**
 * A virtualized version of the List component, using `react-window` to render large datasets. This component should only be used when the number of items would cause performance issues with the standard List component (most likely >500 items).
 */
const VirtualizedListBox = React__default.forwardRef(({ height = 325, width = 175, itemSize, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, [ARIA_ACTIVEDESCENDANT]: ariaActiveDescendant, tabIndex, itemCount, focusedIndex: externalFocusedIndex, onChange, onKeyDown, onClick, className, children, ...rest }, ref) => {
    const [focusedIndex, setFocusedIndex] = useState(null);
    const listRef = useRef(null);
    const innerRef = useRef(null);
    const outerRef = useRef(null);
    const itemsRef = useRef([]);
    // override internal focusedIndex with external state if provided
    useEffect(() => {
        if (externalFocusedIndex !== undefined) {
            if (externalFocusedIndex !== null) {
                listRef.current?.scrollToItem(externalFocusedIndex);
            }
            setFocusedIndex(externalFocusedIndex);
        }
    }, [externalFocusedIndex]);
    const { listBoxContainer, listBox } = useStyles$J();
    const handleChange = (item) => {
        onChange?.({
            target: item,
            value: item.dataset.value ?? '',
        });
    };
    /**
     * Scroll to and focus the item at the specified index
     */
    const focusItem = (index) => {
        listRef.current?.scrollToItem(index);
        setFocusedIndex(index);
        handleChange(itemsRef.current[index]);
    };
    useImperativeHandle(ref, () => ({
        listRef,
        innerRef,
        outerRef,
        itemsRef,
    }));
    const handleKeyDown = useCallback((e) => {
        onKeyDown?.(e);
        if (!(e.target instanceof HTMLUListElement)) {
            return;
        }
        if ([
            'Down',
            'Up',
            'ArrowDown',
            'ArrowUp',
            'PageDown',
            'PageUp',
            'End',
            'Home',
        ].includes(e.key)) {
            e.preventDefault();
            // Focus another item in the listBox depending on which key is pressed
            focusNext(e.key, focusedIndex, itemCount, numItemsVisible(outerRef.current?.getBoundingClientRect().height), focusItem);
        }
    }, [focusedIndex, itemCount, onKeyDown]);
    const handleClick = useCallback((e) => {
        onClick?.(e);
        if (e.target instanceof HTMLLIElement) {
            setFocusedIndex(itemsRef.current.indexOf(e.target));
            handleChange(e.target);
        }
    }, [onClick]);
    useSSRSafeLayoutEffect(() => {
        const listElement = innerRef.current;
        outerRef.current?.removeAttribute('tabindex');
        if (listElement) {
            listElement.className = listBox;
            listElement.setAttribute('role', 'listbox');
            listElement.addEventListener('keydown', handleKeyDown);
            listElement.addEventListener('click', handleClick);
            if (tabIndex === undefined) {
                listElement.removeAttribute('tabindex');
            }
            else {
                listElement.setAttribute('tabindex', tabIndex?.toString());
            }
            if (ariaLabel) {
                listElement.setAttribute('aria-label', ariaLabel);
            }
            if (ariaLabelledBy) {
                listElement.setAttribute('aria-labelledby', ariaLabelledBy);
            }
        }
        return () => {
            if (listElement) {
                listElement.removeEventListener('keydown', handleKeyDown);
                listElement.removeEventListener('click', handleClick);
            }
        };
    }, [
        ariaLabel,
        ariaLabelledBy,
        handleClick,
        handleKeyDown,
        listBox,
        tabIndex,
    ]);
    useSSRSafeLayoutEffect(() => {
        const listElement = innerRef.current;
        if (ariaActiveDescendant ||
            (tabIndex !== undefined && focusedIndex !== null)) {
            listElement?.setAttribute(ARIA_ACTIVEDESCENDANT, ariaActiveDescendant ?? `ListBoxItem-${focusedIndex}`);
        }
        else {
            listElement?.removeAttribute(ARIA_ACTIVEDESCENDANT);
        }
    }, [ariaActiveDescendant, focusedIndex, tabIndex]);
    return (React__default.createElement(FixedSizeList, { className: classNames(listBoxContainer, className), itemSize: itemSize, height: Math.min(height, itemCount * itemSize), width: width, innerElementType: "ul", innerRef: innerRef, outerRef: outerRef, overscanCount: 10, itemCount: itemCount, ref: listRef, ...rest }, (props) => React__default.cloneElement(children(props), {
        id: children(props).props.id ?? `ListBoxItem-${props.index}`,
        key: props.index,
        selected: props.index === focusedIndex,
        ref: (el) => {
            itemsRef.current[props.index] = el;
        },
    })));
});

const useStyles$I = createUseStyles((theme) => {
    return {
        commentLabelText: {
            ...theme.questionBody,
            marginBottom: 5,
            lineHeight: 1.25,
        },
        commentLabel: {
            display: 'block',
        },
    };
});

function CommentChoice({ label, lineCount = 3, charCount = 50, onChange, className, ...props }) {
    const handleChange = (e) => {
        onChange?.(e);
    };
    const { commentLabel, commentLabelText } = useStyles$I();
    const multipleTextLines = lineCount > 1;
    return (React__default.createElement("div", { className: className },
        React__default.createElement("label", { id: `${props.id}-label`, htmlFor: props.id, className: commentLabel, tabIndex: -1 },
            React__default.createElement(RichText, { element: "div", text: label ?? '', className: commentLabelText }),
            multipleTextLines ? (React__default.createElement(TextArea$1, { onChange: handleChange, rows: lineCount, cols: charCount, ...props })) : (React__default.createElement(TextInput$1, { autoHeight: true, onChange: handleChange, labelledby: props.id, size: charCount, ...props })))));
}

/**
 * Visually Hidden CSS properties
 * @description common css-properties to visually hide text and available for screen-readers.
 * There may be times where the css-specificity may not be high enough for the class to be invoked,
 * Ergo, the use of `!important` which will help ensure the properties are applied at any specificity.
 * @example
 * // usage with `react-jss`
 * import visuallyHiddenProperties from './utils/visuallyHidden';
 *
 * const useStyles = createUseStyles({
 *   srOnly: {...visuallyHiddenProperties}
 * })
 */
var visuallyHidden = {
    border: [[0], '!important'],
    /**
     * @desc for backwards compatibility
     * @deprecated
     * @see https://developer.mozilla.org/en-US/docs/Web/CSS/clip
     * @see https://caniuse.com/mdn-css_properties_clip
     */
    clip: ['rect(1px, 1px, 1px, 1px)', '!important'],
    /**
     * @desc future-proof version
     * @see https://developer.mozilla.org/en-US/docs/Web/CSS/clip-path
     * @see https://caniuse.com/css-clip-path
     */
    clipPath: ['inset(50%)', '!important'],
    height: ['1px', '!important'],
    margin: ['-1px', '!important'],
    overflow: ['hidden', '!important'],
    padding: [[0], '!important'],
    position: ['absolute', '!important'],
    width: ['1px', '!important'],
    /**
     * @desc preventing text to be condensed
     * @see https://medium.com/@jessebeach/beware-smushed-off-screen-accessible-text-5952a4c2cbfe
     */
    whiteSpace: ['nowrap', '!important'],
};

const textReset = {
    margin: 0,
};
const useStyles$H = createUseStyles((theme) => {
    const { fontSize, ...questionTitleFormatting } = theme.questionTitle;
    const questionTitleFontSize = fontSize ?? {};
    return {
        srOnly: {
            ...visuallyHidden,
        },
        addonContainer: {
            display: 'inline-block',
            margin: [0, 5],
            verticalAlign: 'text-bottom',
        },
        container: {
            position: 'relative',
            display: 'flex',
        },
        questionNumber: ({ isRTL }) => ({
            margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0',
            flex: [0, 0],
        }),
        questionTitle: ({ useDefaultFrontSize }) => ({
            ...textReset,
            ...questionTitleFormatting,
            color: theme.questionTitle?.color || theme.questionColor,
            fontSize: useDefaultFrontSize && !theme.isAccessible
                ? theme.fontSize.body
                : questionTitleFontSize,
            lineHeight: 1.25,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
        }),
        requiredAsterisk: ({ isRTL }) => ({
            display: 'inline-block',
            margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0',
        }),
        richText: {
            flex: [1, 0],
            wordBreak: 'break-word',
        },
    };
});

const COPY$i = defineMessages({
    REQUIRED: {
        id: 'RespondentQuestionTitle.REQUIRED',
        defaultMessage: '(Required.)',
        description: '[Type: label][Vis: medium] - visualy hidden required text',
    },
});
function RespondentQuestionTitle({ element: Element = 'div', addon, heading, id, number, numbered, required, requiredAsterisk: showAsterisk, useDefaultFrontSize = false, ...props }) {
    const { isRTL } = useContext(L10NContext);
    const { addonContainer, container, questionNumber, questionTitle, requiredAsterisk, richText, srOnly, } = useStyles$H({ useDefaultFrontSize, isRTL });
    return (React__default.createElement(Element, { id: id, className: classNames(container, questionTitle), translate: "no", ...props },
        required && showAsterisk && (React__default.createElement("span", { "aria-hidden": true, className: requiredAsterisk }, "*")),
        numbered && React__default.createElement("span", { className: questionNumber },
            number,
            "."),
        React__default.createElement(RichText, { element: "span", text: heading, className: richText }),
        required && (React__default.createElement("span", { className: srOnly },
            React__default.createElement(T, { desc: COPY$i.REQUIRED }))),
        addon && React__default.createElement("span", { className: addonContainer }, addon)));
}

// default text to be added as a translation to a specific version of Button directly.
const defaultOKConfig = { text: 'OK', visible: false };
function QuestionField({ id, title: titleProps, okButton: { visible: showButton, ...okButtonProps } = defaultOKConfig, error, errorId: errorIdProp, onLeave, padding = { top: 0, bottom: 0, left: 0, right: 0 }, width, children, ...props }) {
    const errorId = errorIdProp ?? createErrorId(id);
    return (React__default.createElement(QuestionFieldLayoutTemplate, { footer: showButton && React__default.createElement(Button, { type: "button", ...okButtonProps }), hasFooterPadding: true, clickShield: false, error: error && React__default.createElement(ValidationMessage, { id: errorId }, error), onLeave: onLeave, id: id, padding: padding, width: width, title: React__default.createElement(RespondentQuestionTitle, { ...titleProps }), ...props }, children));
}

const useStyles$G = createUseStyles((theme) => {
    const breakpointSmMax = `@media screen and (max-width: ${theme.breakpoints.sm.max})`;
    return {
        rowStyles: {
            borderRadius: '3px',
            textAlign: 'center',
            cursor: 'pointer',
            alignItems: 'center',
            padding: '3px',
            ...theme.questionBody,
            fontSize: '20px',
        },
        rowLabel: {
            margin: [0, 2, 0, 2],
            fontSize: '18px',
            padding: '3px',
            width: ({ width = 60 }) => `${width}%`,
            wordWrap: 'wrap',
            flexWrap: 'wrap',
            /**
             * There is a known jss issue that results in media query
             * checks not occurring for all mapped elements, hence the use of !important
             * https://github.com/cssinjs/jss/issues/1320
             * https://github.com/cssinjs/jss/issues/1327#issuecomment-707967909
             * Because function rules/values have higher source order specificity (rendered after static rules),
             * we're using !important to override that.
             * */
            [breakpointSmMax]: {
                width: ['50%', '!important'],
            },
        },
        rowRadio: {
            justifyContent: 'center',
            gap: 0,
            padding: '3px',
            fontSize: 'inherit',
            width: ({ radioWidth = 20 }) => `${radioWidth}%`,
            [breakpointSmMax]: {
                width: ['25%', '!important'],
            },
        },
    };
});

function BestWorstRow({ id: rowId, label, value = null, columnIds = [], className, onChange, onClick, onKeyDown, width, ...props }) {
    const DEFAULT_RADIO_WIDTH = 20;
    const [bestColId, worstColId] = columnIds;
    const bestRadioId = `${rowId}_${bestColId}`;
    const worstRadioId = `${rowId}_${worstColId}`;
    const labelId = `${rowId}_label`;
    const radioEvents = { onClick, onKeyDown };
    const radioWidth = width !== undefined ? (100 - width) / 2 : DEFAULT_RADIO_WIDTH;
    const handleChange = (e) => {
        onChange?.({ id: rowId, value: e.value, checked: e.checked });
    };
    const { rowStyles, rowLabel, rowRadio } = useStyles$G({ width, radioWidth });
    return (React__default.createElement(Row, { className: classNames(rowStyles, className), ...props },
        React__default.createElement(Radio$1, { id: bestRadioId, className: rowRadio, value: bestColId, name: rowId, checked: value === bestColId, "aria-labelledby": `${bestColId} ${labelId}`, onChange: handleChange, ...radioEvents }),
        React__default.createElement("span", { className: rowLabel },
            React__default.createElement(RichText, { id: labelId, element: "span", text: label })),
        React__default.createElement(Radio$1, { id: worstRadioId, className: rowRadio, value: worstColId, name: rowId, checked: value === worstColId, "aria-labelledby": `${worstColId} ${labelId}`, onChange: handleChange, ...radioEvents })));
}

const useStyles$F = createUseStyles((theme) => {
    const breakpointSmMax = `@media screen and (max-width: ${theme.breakpoints.sm.max})`;
    const breakpointXxsMax = `@media screen and (max-width: ${theme.breakpoints.xxs.max})`;
    return {
        rowList: {
            '& > :nth-child(even)': {
                backgroundColor: theme.input.bgColor,
            },
        },
        headerColumn: ({ radioWidth = 20 }) => ({
            display: 'flex',
            width: `${radioWidth}%`,
            padding: '5px 8px',
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'center',
            ...theme.questionBody,
            fontSize: '20px',
            fontWeight: 500,
            wordBreak: 'break-word',
            [breakpointSmMax]: {
                width: '25%',
            },
            [breakpointXxsMax]: {
                width: '35%',
            },
        }),
        labelColumn: ({ labelWidth = 60 }) => ({
            display: 'flex',
            width: `${labelWidth}%`,
            margin: [0, 2, 0, 2],
            padding: '3px',
            ...theme.questionBody,
            [breakpointSmMax]: {
                width: '50%',
            },
            [breakpointXxsMax]: {
                width: '30%',
            },
        }),
    };
});

function BestWorst({ id: questionId, disabled: _dis, readOnly: _ro, choices: fields = [], responses = [], columnChoices = [], onChange, labelWidth, ...questionFieldProps }) {
    const DEFAULT_RADIO_WIDTH = 20;
    const [selectedChoices, setSelectedChoices] = useState(responses);
    const [bestAnswerColumn, worstAnswerColumn] = columnChoices;
    const columnIds = columnChoices?.map((c) => c.id);
    const errorId = createErrorId(questionId);
    const radioWidth = labelWidth !== undefined ? (100 - labelWidth) / 2 : DEFAULT_RADIO_WIDTH;
    /** Filter choices that exist and are visible  */
    const { options: rowFields } = useQuestionChoices(fields);
    /** determines whether a radio should be rendered as checked or unchecked */
    const getRadioSelection = (id) => {
        return selectedChoices.find((c) => c.id === id)?.value[0].id ?? '';
    };
    /** unChecks selected choice  */
    const unCheckOption = (radioId) => {
        const newChoices = selectedChoices.filter((c) => !radioId.match(c.id));
        setSelectedChoices(newChoices);
        onChange?.(newChoices);
    };
    /** handles adding new checked selection to responses */
    const handleChange = (e) => {
        let newChoices = [];
        const rowId = e.id;
        const rowValue = e.value;
        /** remove any other checked radio in the same row or column */
        newChoices = selectedChoices.filter((row) => !(row.id === rowId || row.value.find((col) => col.value === rowValue)));
        newChoices.push({
            id: rowId,
            value: [{ id: rowValue, value: rowValue }],
        });
        setSelectedChoices(newChoices);
        onChange?.(newChoices);
    };
    const handleClick = (e) => {
        const input = e.target;
        const isSelected = selectedChoices.find((c) => input.id.match(c.id));
        if (isSelected && input.checked) {
            unCheckOption(input.id);
        }
    };
    const handleKeyboard = (e) => {
        const input = e.target;
        const isSelected = selectedChoices.find((c) => input.id.match(c.id));
        if (e.code === 'Space' && isSelected) {
            e.preventDefault();
            unCheckOption(input.id);
        }
    };
    const { headerColumn, rowList, labelColumn } = useStyles$F({
        labelWidth,
        radioWidth,
    });
    return (
    /*
      NOTE: The id has been modified to work in hybrid mode. The id needs to
      be different from the question-field id created in the responseweb.
    */
    React__default.createElement(QuestionField, { id: `hybrid-${questionId}`, "data-testid": "BestWorstQuestionType", ...questionFieldProps },
        React__default.createElement("div", { className: rowList },
            React__default.createElement(Row, null,
                React__default.createElement(RichText, { id: bestAnswerColumn?.id, element: "span", text: bestAnswerColumn?.label, className: classNames(headerColumn) }),
                React__default.createElement("span", { className: classNames(labelColumn) }),
                React__default.createElement(RichText, { id: worstAnswerColumn?.id, element: "span", text: worstAnswerColumn?.label, className: classNames(headerColumn) })),
            rowFields?.map((field) => {
                const rowId = `${questionId}_${field.id}`;
                return (React__default.createElement(BestWorstRow, { key: rowId, id: rowId, label: field.label, width: labelWidth, value: getRadioSelection(rowId), columnIds: columnIds, onChange: handleChange, onClick: handleClick, onKeyDown: handleKeyboard, "aria-describedby": (questionFieldProps.error && errorId) || undefined }));
            }))));
}
var BestWorst$1 = withErrorBoundary(BestWorst);

const useStyles$E = createUseStyles((theme) => ({
    checkbox: {
        '& label': {
            alignItems: 'baseline',
        },
    },
    labelContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    textInput: {
        opacity: ({ isChecked }) => (isChecked ? 1 : 0.5),
        marginTop: 5,
        marginLeft: 32,
        maxWidth: 'calc(100% - 32px)',
        [`@media (max-width: ${theme.breakpoints.xxs.max})`]: {
            marginLeft: 0,
            maxWidth: '100%',
        },
        // overwrite TextInput styles
        fontSize: theme.questionBody.fontSize,
        lineHeight: 'normal',
    },
    checkHover: {
        '&:hover': {
            outline: 'none',
            cursor: 'pointer',
        },
    },
}));

function CheckboxTextfield(props) {
    const { id, children: label, value: inputValue = '', defaultValue, maxLength, onClick, onKeyDown, onChange, checked: checkedProp = false, ...checkboxProps } = props;
    const [isChecked, setIsChecked] = useState(checkedProp);
    const [value, setValue] = React__default.useState(defaultValue || inputValue);
    const { checkbox, textInput, checkHover } = useStyles$E({
        isChecked,
    });
    const handleValueChange = (e) => {
        setValue(e.value);
        onChange?.({ id, value: e.value, checked: e.checked });
    };
    const handleInputChange = (e) => {
        if (!isChecked) {
            setIsChecked(true);
        }
        handleValueChange({ value: e.target.value, checked: true });
    };
    const inputRef = React__default.useRef(null);
    const handleCheckboxClick = (e) => {
        if (!isChecked && e.clientX !== 0 && e.clientY !== 0) {
            inputRef.current?.focus();
        }
        setIsChecked(!isChecked);
        onClick?.(e);
    };
    const handleTextboxClick = () => {
        if (!isChecked) {
            setIsChecked(true);
        }
        handleValueChange({ value, checked: true });
    };
    const handleKeyDown = (e) => {
        if (e.code === 'Space') {
            e.preventDefault();
            setIsChecked(!isChecked);
            handleValueChange({ value, checked: !isChecked });
            return;
        }
        onKeyDown?.(e);
    };
    React__default.useEffect(() => {
        setIsChecked(checkedProp);
    }, [checkedProp]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Checkbox$3, { id: `checkbox-input-${id}`, checked: isChecked, onKeyDown: handleKeyDown, onClick: handleCheckboxClick, onChange: handleValueChange, className: checkbox, value: value, maxLength: maxLength, ...checkboxProps }, label),
        React__default.createElement(TextInput$1, { id: `checkbox-field-${id}`, onChange: handleInputChange, onClick: handleTextboxClick, className: classNames(textInput, { [checkHover]: !isChecked }), defaultValue: defaultValue, autoHeight: true, labelledby: `checkbox-input-${id}`, maxLength: maxLength, tabIndex: isChecked ? 0 : -1, ref: inputRef })));
}

/**
 * Maximum input characters is 20,000
 * Resolves: RAWR-1186
 */
const TEXT_INPUT_MAX_CHARS = 20000;
const US_STATES = [
    'Alabama',
    'Alaska',
    'American Samoa',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Guam',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Northern Mariana Islands',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Puerto Rico',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'U.S. Virgin Islands',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
];
// COUNTRY CODES
const COUNTRY_CODES = [
    'AF',
    'AX',
    'AL',
    'DZ',
    'AS',
    'AD',
    'AO',
    'AI',
    'AG',
    'AR',
    'AM',
    'AW',
    'AC',
    'AU',
    'AT',
    'AZ',
    'BS',
    'BH',
    'BD',
    'BB',
    'BE',
    'BZ',
    'BJ',
    'BM',
    'BT',
    'BO',
    'BQ',
    'BA',
    'BW',
    'BR',
    'IO',
    'BN',
    'BG',
    'BF',
    'BI',
    'KH',
    'CM',
    'CA',
    'CV',
    'KY',
    'CF',
    'TD',
    'CL',
    'CN',
    'CX',
    'CC',
    'CO',
    'KM',
    'CG',
    'CD',
    'CK',
    'CR',
    'CI',
    'HR',
    'CW',
    'CY',
    'CZ',
    'DK',
    'DJ',
    'DM',
    'DO',
    'EC',
    'EG',
    'SV',
    'GQ',
    'ER',
    'EE',
    'ET',
    'FK',
    'FO',
    'FM',
    'FJ',
    'FI',
    'FR',
    'GF',
    'PF',
    'GA',
    'GM',
    'GE',
    'DE',
    'GH',
    'GI',
    'GR',
    'GL',
    'GD',
    'GP',
    'GU',
    'GT',
    'GG',
    'GN',
    'GW',
    'GY',
    'HT',
    'VA',
    'HN',
    'HK',
    'HU',
    'IS',
    'IN',
    'ID',
    'IQ',
    'IE',
    'IM',
    'IL',
    'IT',
    'JM',
    'JP',
    'JE',
    'JO',
    'KZ',
    'KE',
    'KI',
    'XK',
    'KW',
    'KG',
    'LA',
    'LV',
    'LB',
    'LS',
    'LR',
    'LY',
    'LI',
    'LT',
    'LU',
    'MO',
    'MG',
    'MW',
    'MY',
    'MV',
    'ML',
    'MT',
    'MH',
    'MQ',
    'MR',
    'MU',
    'YT',
    'MX',
    'MD',
    'MC',
    'MN',
    'ME',
    'MS',
    'MA',
    'MZ',
    'MM',
    'NA',
    'NR',
    'NP',
    'NL',
    'NC',
    'NZ',
    'NI',
    'NE',
    'NG',
    'NU',
    'NF',
    'MK',
    'MP',
    'NO',
    'OM',
    'PK',
    'PW',
    'PS',
    'PA',
    'PG',
    'PY',
    'PE',
    'PH',
    'PL',
    'PT',
    'PR',
    'QA',
    'RE',
    'RO',
    'RS',
    'RW',
    'BL',
    'SH',
    'KN',
    'LC',
    'MF',
    'PM',
    'VC',
    'WS',
    'SM',
    'ST',
    'SA',
    'SN',
    'SC',
    'SL',
    'SG',
    'SX',
    'SK',
    'SI',
    'SB',
    'SO',
    'ZA',
    'KR',
    'SS',
    'ES',
    'LK',
    'SR',
    'SJ',
    'SZ',
    'SE',
    'CH',
    'TW',
    'TJ',
    'TZ',
    'TH',
    'TL',
    'TG',
    'TK',
    'TO',
    'TT',
    'TA',
    'TN',
    'TR',
    'TM',
    'TC',
    'TV',
    'UG',
    'UA',
    'AE',
    'GB',
    'US',
    'UY',
    'UZ',
    'VU',
    'VE',
    'VN',
    'VG',
    'VI',
    'WF',
    'EH',
    'YE',
    'ZM',
    'ZW',
];

const useStyles$D = createUseStyles({
    commentChoiceContainer: {
        marginTop: '10px',
    },
});

const INPUT_SIZE$2 = 20;
function Checkbox({ id: questionId, required: _req, readOnly: _ro, disabled, columnLayout, choices = [], responses = [], choiceNoneOfTheAbove, choiceOther, choiceComment, onChange, ...questionFieldProps }) {
    const [selectedChoices, setSelectedChoices] = useState(responses);
    const errorId = createErrorId(questionId);
    /**
     * Default value by id
     * used for text-input based choices
     */
    const getDefaultValueById = (id) => responses.find((c) => c.id === id)?.value;
    const handleDefault = (id) => responses.find((c) => c.id === id)?.value;
    const isChecked = (id) => !!selectedChoices.find((item) => item.id === id);
    const handleChange = (e) => {
        // 1. remove NOTA from array
        const newState = selectedChoices.filter((c) => c.id !== e.id && c.type !== 'NOTA');
        // 2. if e.target.checked remove item from array
        if (e.checked) {
            newState.push({ id: e.id, value: e.value });
        }
        setSelectedChoices(newState);
        onChange?.(newState);
    };
    const handleNAChange = (e) => {
        const newState = selectedChoices.filter((c) => c.type === 'COMMENT');
        // 2. if e.target.checked remove item from array
        if (e.checked) {
            newState.push({ id: e.id, value: e.value, type: 'NOTA' });
        }
        setSelectedChoices(newState);
        onChange?.(newState);
    };
    const handleOtherChange = (e) => {
        const newState = selectedChoices.filter((c) => c.id !== e.id && c.type !== 'NOTA');
        // 2. if e.target.checked remove item from array
        if (e.checked) {
            newState.push({ id: e.id, value: e.value, type: 'ANSWER' });
        }
        setSelectedChoices(newState);
        onChange?.(newState);
    };
    const handleCommentChange = (e) => {
        const newState = selectedChoices.filter((c) => c.type !== 'COMMENT');
        if (newState && !!e.target.value) {
            newState.push({
                id: e.target.id,
                value: e.target.value,
                type: 'COMMENT',
            });
        }
        setSelectedChoices(newState);
        onChange?.(newState);
    };
    const { commentChoiceContainer } = useStyles$D();
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "CheckboxQuestionType", ...questionFieldProps },
        React__default.createElement(QuestionAnswerLayoutTemplate, { gridCellMargin: [0, 2, 5, 2], otherCellMargin: [0, 2, 5, 2], noneOfTheAbove: choiceNoneOfTheAbove &&
                choiceNoneOfTheAbove?.visible && (React__default.createElement(Checkbox$3, { id: choiceNoneOfTheAbove.id, value: choiceNoneOfTheAbove.id, onChange: handleNAChange, checked: isChecked(choiceNoneOfTheAbove.id), disabled: disabled, readOnly: _ro, "aria-describedby": (questionFieldProps.error && errorId) || undefined, inputSize: INPUT_SIZE$2 }, choiceNoneOfTheAbove.label)), other: choiceOther?.type === 'ANSWER' &&
                choiceOther?.visible && (React__default.createElement(CheckboxTextfield, { id: choiceOther.id, defaultValue: handleDefault(choiceOther.id), onChange: handleOtherChange, checked: isChecked(choiceOther.id), maxLength: TEXT_INPUT_MAX_CHARS, disabled: disabled, readOnly: _ro, "aria-describedby": (questionFieldProps.error && errorId) || undefined, inputSize: INPUT_SIZE$2 }, choiceOther.label)), columns: columnLayout }, choices
            ?.filter((choice) => choice.visible)
            .map((choice) => {
            return (React__default.createElement(Checkbox$3, { key: choice.id, id: choice.id, onChange: handleChange, checked: isChecked(choice.id), value: choice.id, disabled: disabled, readOnly: _ro, "aria-describedby": (questionFieldProps.error && errorId) || undefined, inputSize: INPUT_SIZE$2 }, choice.label));
        })),
        choiceComment && (React__default.createElement(CommentChoice, { id: choiceComment.id, label: choiceComment.label, onChange: handleCommentChange, defaultValue: getDefaultValueById(choiceComment.id), lineCount: choiceComment.linesCount, charCount: choiceComment.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, "aria-describedby": (questionFieldProps.error && errorId) || undefined, className: commentChoiceContainer }))));
}
var Checkbox$1 = withErrorBoundary(Checkbox);

function CommentBox({ id: questionId, required: _req, disabled, readOnly, responses: defaultResponses = [], onChange, cols = 50, rows = 3, ...fieldProps }) {
    const { error, errorId = createErrorId(questionId), title: { id: titleId }, } = fieldProps;
    const defaultValue = useMemo(() => defaultResponses.find((r) => !!r.value)?.value ?? '', 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const handleChange = (e) => {
        const responses = e.target.value
            ? [{ id: questionId, value: e.target.value }]
            : [];
        onChange?.(responses);
    };
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "CommentBoxQuestionType", ...fieldProps },
        React__default.createElement(TextArea$1, { name: questionId, defaultValue: defaultValue, disabled: disabled, readOnly: readOnly, cols: cols, rows: rows, onChange: handleChange, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS })));
}
var CommentBox$1 = withErrorBoundary(CommentBox);

const useStyles$C = createUseStyles((theme) => {
    const { color, fontSize, fontFamily, fontWeight } = theme.questionBody;
    return {
        inputGroup: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: ({ rtl }) => (rtl ? 'row-reverse' : 'row'),
            transition: 'all 0.1s linear',
            border: `1px solid ${color}`,
            '&:focus-within, &:hover': {
                border: `1px solid transparent`,
            },
        },
        input: {
            border: 'none',
            textAlign: ({ rtl }) => (rtl ? 'right' : 'left'),
            height: ({ height }) => height || 'auto',
            width: 'calc(100% - 30px)',
            outline: 'currentColor none medium',
            backgroundColor: '#FFF',
            padding: '6px 0 6px 10px',
            fontSize,
            fontFamily,
            fontWeight,
        },
        button: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            height: ({ height }) => height || 'auto',
            width: 30,
            backgroundColor: '#FFF',
            border: 'none',
        },
        buttonIcon: {
            transform: ({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0)'),
        },
    };
});

const ComboBoxInputGroup = React__default.forwardRef(({ isOpen, value, height, rtl = false, onChange, open, close, onKeyDown, 'aria-activedescendant': ariaActiveDescendant, 'aria-autocomplete': ariaAutocomplete, 'aria-controls': ariaControls, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, className, ...rest }, ref) => {
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const { inputGroup, input, button, buttonIcon } = useStyles$C({
        isOpen,
        height,
        rtl,
    });
    return (React__default.createElement("div", { className: classNames(inputGroup, className), ...rest },
        React__default.createElement("input", { className: input, type: "text", role: "combobox", "aria-autocomplete": ariaAutocomplete, "aria-expanded": isOpen, "aria-controls": ariaControls, "aria-activedescendant": ariaActiveDescendant, value: value, onChange: onChange, onClick: open, onKeyDown: onKeyDown, ref: inputRef }),
        React__default.createElement("button", { className: button, type: "button", tabIndex: -1, "aria-expanded": isOpen, "aria-controls": ariaControls, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, onClick: () => {
                if (isOpen) {
                    close();
                }
                else {
                    open();
                }
                inputRef.current?.focus();
            } },
            React__default.createElement(CaretDownIcon, { className: buttonIcon }))));
});

const useStyles$B = createUseStyles((theme) => {
    return {
        comboBox: {
            display: 'inline-block',
            width: ({ width }) => width,
            borderRadius: `4px`,
            border: `2px solid transparent`,
            outline: `1px solid transparent`,
            '&:focus-within, &:hover': {
                border: `2px solid ${theme.primaryAccentColor}`,
                outline: `1px solid #fff`,
            },
        },
        listBox: {
            display: ({ isOpen }) => (isOpen ? 'block' : 'none'),
            '&:focus-within': {
                outline: 'none',
            },
            border: '1px solid rgb(204, 204, 204)',
        },
        listBoxItem: {
            direction: ({ rtl }) => (rtl ? 'rtl' : 'ltr'),
            paddingLeft: '10px',
        },
    };
});

function ComboBox({ id, options, height, width = '100%', itemSize = 48, className, classes, filterOnChange = false, value: outsideValue = '', optionAlign = 'center', filter, onChange, rtl = false, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...rest }) {
    const comboBoxRef = useRef(null);
    const inputRef = useRef(null);
    const listBoxRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [value, setValue] = useState(outsideValue);
    const [currentOptions, setCurrentOptions] = useState(options);
    const [focusedIndex, setFocusedIndex] = useState(null);
    const [filterActive, setFilterActive] = useState(!filterOnChange);
    // the initial value of the input field when the listbox is opened (used to control whether the filter is active or not)
    const [valueOnOpen, setValueOnOpen] = useState(outsideValue);
    /** For preventing onChange being called on first render */
    const firstRender = useRef(true);
    const { comboBox, listBox, listBoxItem } = useStyles$B({
        isOpen,
        width,
        rtl,
    });
    const closeListBox = () => {
        if (isOpen) {
            setIsOpen(false);
        }
    };
    const openListBox = () => {
        if (!isOpen) {
            setValueOnOpen(value);
            setIsOpen(true);
        }
    };
    // close the listbox when the user clicks outside of it
    useOnFocusLeave(comboBoxRef, () => {
        closeListBox();
        setFocusedIndex(null);
    });
    const focusItem = (index) => {
        setFocusedIndex(index);
    };
    // call onChange when value changes
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
        }
        else {
            onChange?.({
                value,
                target: inputRef.current,
            });
        }
    }, [value]);
    // filter the current options if a filter is provided and filtering is active. If filtering is inactive, reset the current options to the original options
    useEffect(() => {
        if (filter && filterActive) {
            setCurrentOptions(options.filter((option) => filter(option, value)));
        }
        else if (!filterActive) {
            setCurrentOptions(options);
        }
    }, [options, value, filter, filterActive]);
    // if filterOnChange is true, activate filtering if the value has changed since the listbox was opened, otherwise deactivate filtering
    useEffect(() => {
        if (filterOnChange) {
            setFilterActive(value !== valueOnOpen);
        }
    }, [filterOnChange, value, valueOnOpen]);
    // if the current value matches an option when the list opens, focus it and center it in the list
    useEffect(() => {
        if (isOpen) {
            const matchingOption = currentOptions.find((option) => option.label === value);
            if (matchingOption) {
                listBoxRef.current?.listRef.current?.scrollToItem(currentOptions.indexOf(matchingOption), optionAlign // position the option in the list according to the alignment (default is `center`)
                );
                setFocusedIndex(currentOptions.indexOf(matchingOption));
            }
        }
    }, [isOpen, currentOptions, value, optionAlign]);
    useEffect(() => {
        setValue(outsideValue);
    }, [outsideValue]);
    const handleKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown':
            case 'Down':
            case 'ArrowUp':
            case 'Up':
            case 'PageDown':
            case 'PageUp':
                e.preventDefault();
                openListBox();
                if (!e.altKey) {
                    focusNext(e.key, focusedIndex, currentOptions.length, numItemsVisible(listBoxRef.current?.outerRef.current?.getBoundingClientRect()
                        .height, itemSize), focusItem);
                }
                break;
            case 'Escape':
                if (isOpen) {
                    closeListBox();
                    setFocusedIndex(null);
                }
                else {
                    setValue('');
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (isOpen) {
                    if (focusedIndex !== null) {
                        setValue(listBoxRef.current?.itemsRef.current?.[focusedIndex]
                            .textContent ?? '');
                    }
                    closeListBox();
                    setFocusedIndex(null);
                }
                break;
        }
    };
    const handleListBoxClick = () => {
        inputRef.current?.focus();
        closeListBox();
    };
    const activeDescendant = focusedIndex !== null && currentOptions[focusedIndex]
        ? `${id}-${currentOptions[focusedIndex].value}`
        : undefined;
    return (React__default.createElement("div", { className: classNames(comboBox, classes?.comboBox, className), ref: comboBoxRef, ...rest },
        React__default.createElement(ComboBoxInputGroup, { ref: inputRef, className: classes?.inputGroup, height: height, rtl: rtl, value: value, isOpen: isOpen, "aria-controls": `${id}-listbox`, "aria-autocomplete": filter ? 'list' : 'none', "aria-activedescendant": activeDescendant, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, open: openListBox, close: closeListBox, onChange: (e) => setValue(e.target.value), onKeyDown: handleKeyDown }),
        React__default.createElement(VirtualizedListBox, { ref: listBoxRef, itemSize: itemSize, itemCount: currentOptions.length, className: classNames(listBox, classes?.listBox), focusedIndex: focusedIndex, width: comboBoxRef.current?.clientWidth, onChange: (e) => setValue(e.target?.textContent ?? ''), onClick: handleListBoxClick, tabIndex: -1, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-activedescendant": activeDescendant, 
            /**
             * adding inline style to overwrite react-window inline styles as per their docs
             * https://github.com/bvaughn/react-window
             */
            style: { position: 'absolute' } }, ({ index, style }) => (React__default.createElement(ListBoxItem, { className: classNames(listBoxItem, classes?.listBoxItem), style: style, value: currentOptions[index].value, id: `${id}-${currentOptions[index].value}`, key: `${id}-${currentOptions[index].value}` }, currentOptions[index].label)))));
}

const useStyles$A = createUseStyles({
    srOnly: visuallyHidden,
});

/** default element for the visually hidden component */
const DEFAULT_ELEMENT = 'span';
/**
 * A component to have visually hidden text still available to Assistive
 * Technologies, such as screen-readers.
 * All HTML properties are available on the selected `element` tag used.
 */
function VisuallyHidden({ element: Tag = DEFAULT_ELEMENT, className, ...props }) {
    const { srOnly } = useStyles$A();
    return React__default.createElement(Tag, { className: classNames(srOnly, className), ...props });
}

const useCalendarStyles = createUseStyles({
    calendarPickerWrapper: {
        width: ({ width }) => width,
        height: ({ height }) => height,
        border: '1px solid #CCCCCC',
        padding: '10px',
        background: '#FFFFFF',
        color: '#333333',
    },
    calendarControls: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '5px',
    },
    button: {
        border: 'none',
        borderRadius: 0,
        background: 'transparent',
        width: '50px',
        backgroundColor: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#05467E',
            color: '#FFFFFF',
        },
    },
    comboboxWrapper: {
        display: 'flex',
        flexGrow: 1,
    },
    monthSelectWrapper: {
        display: ({ calendarVisibility }) => {
            return calendarVisibility ? 'block' : 'none';
        },
        '& select': {
            fontSize: 16,
        },
    },
    yearSelectWrapper: {
        display: ({ calendarVisibility }) => {
            return calendarVisibility ? 'block' : 'none';
        },
        '& input': {
            fontSize: 16,
        },
    },
    monthWrapper: {
        display: 'grid',
        margin: '32px 0 0',
        gridTemplateColumns: `repeat(1, 100%)`,
        gridGap: '0 64px',
    },
});
const useMonthStyles = createUseStyles({
    weekdayLabel: {
        textAlign: 'center',
        marginBottom: '10px',
        fontSize: '10px',
    },
    weekGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        justifyContent: 'center',
    },
});
const useDayStyles = createUseStyles({
    dayDefault: {
        padding: '8px',
        border: '1px solid #CCCCCC',
        borderRadius: '0',
        color: '#001217',
        backgroundColor: '#FFFFFF',
        '&:hover': {
            color: '#FFFFFF',
            backgroundColor: '#05467E',
            borderColor: '#05467E',
        },
    },
    daySelected: {
        color: '#FFFFFF',
        backgroundColor: '#71c9ed',
    },
    daySelectedFirstOrLast: {
        color: '#FFFFFF',
        backgroundColor: '#000000',
        borderColor: '#000000',
    },
    dayDisabled: {
        color: '#808285',
        backgroundColor: '#FFFFFF',
    },
});

const COPY$h = defineMessages({
    DAY_OF_MONTH: {
        id: 'Day.dayOfMonth',
        defaultMessage: '{dayInCalendar, date, full}',
        description: '[Type: label][Vis: high] - label for the full date',
    },
});
function Day({ dayLabel, date, focusedDate, isDateSelected, isDateFocused, isDateHovered, onDateHover, onDateSelect, onDateFocus, isFirstOrLastSelectedDate, }) {
    const dayRef = useRef(null);
    const fn = () => false;
    const { isSelected, isSelectedStartOrEnd, isWithinHoverRange, disabledDate, onClick, onKeyDown, onMouseEnter, tabIndex, } = Ne({
        date,
        focusedDate,
        isDateFocused,
        isDateSelected,
        isDateHovered,
        isDateBlocked: fn,
        isFirstOrLastSelectedDate,
        onDateFocus,
        onDateSelect,
        onDateHover});
    useEffect(() => {
        if (dayRef && dayRef.current && isDateFocused(date)) {
            dayRef.current.focus();
        }
    }, [dayRef, date, isDateFocused]);
    const { dayDefault, daySelected, daySelectedFirstOrLast, dayDisabled, } = useDayStyles();
    if (!dayLabel) {
        return React__default.createElement("div", null);
    }
    return (React__default.createElement("button", { onClick: onClick, onKeyDown: onKeyDown, onMouseEnter: onMouseEnter, tabIndex: tabIndex, type: "button", ref: dayRef, "aria-label": t(COPY$h.DAY_OF_MONTH, { dayInCalendar: date }), className: classNames(dayDefault, {
            [daySelected]: isSelected || isWithinHoverRange,
            [daySelectedFirstOrLast]: isSelectedStartOrEnd,
            [dayDisabled]: disabledDate,
        }) }, dayLabel));
}

const COPY$g = defineMessages({
    DAY_OF_WEEK_LEGEND_SHORT: {
        id: 'Month.dayOfWeekLegendShort',
        defaultMessage: '{dayOfTheWeek, select, Mo {Mo} Tu {Tu} We {We} Th {Th} Fr {Fr} Sa {Sa} Su {Su} other {other}}',
        description: '[Type: label][Vis: high] - label for the day of the week in shorthand',
    },
    DAY_OF_WEEK_LEGEND_FULL: {
        id: 'Month.dayOfWeekLegendLong',
        defaultMessage: '{dayOfTheWeek, select, Mo {Monday} Tu {Tuesday} We {Wednesday} Th {Thursday} Fr {Friday} Sa {Saturday} Su {Sunday} other {other}}',
        description: '[Type: label][Vis: high] - label for the day of the week',
    },
});
function Month({ year, month, firstDayOfWeek, ...dayProps }) {
    const { days, weekdayLabels } = de$1({
        year,
        month,
        firstDayOfWeek,
    });
    const { weekdayLabel, weekGrid } = useMonthStyles();
    return (React__default.createElement("div", null,
        React__default.createElement("div", { className: weekGrid, role: "grid" },
            weekdayLabels.map((dayLabel) => (React__default.createElement("div", { className: weekdayLabel, key: dayLabel, role: "columnheader" },
                React__default.createElement("span", { role: "presentation" },
                    React__default.createElement(T, { desc: COPY$g.DAY_OF_WEEK_LEGEND_SHORT, values: { dayOfTheWeek: dayLabel } })),
                React__default.createElement(VisuallyHidden, null,
                    React__default.createElement(T, { desc: COPY$g.DAY_OF_WEEK_LEGEND_FULL, values: { dayOfTheWeek: dayLabel } }))))),
            days.map((day, index) => {
                if (typeof day === 'object') {
                    return (React__default.createElement(Day, { date: day.date, key: day.dayLabel, dayLabel: day.dayLabel, ...dayProps }));
                }
                const emptyDayKey = `empty-day-${index}`;
                return React__default.createElement("div", { key: emptyDayKey });
            }))));
}

const COPY$f = defineMessages({
    PREVIOUS_MONTH: {
        id: 'CalendarPicker.previousMonth',
        defaultMessage: 'Previous Month',
        description: '[Type: button][Vis: high] - button label to go to previous month',
    },
    NEXT_MONTH: {
        id: 'CalendarPicker.nextMonth',
        defaultMessage: 'Next Month',
        description: '[Type: button][Vis: high] - button label to go to next month',
    },
    MONTH_SELECT: {
        id: 'CalendarPicker.month',
        defaultMessage: 'Select a month',
        description: '[Type: label][Vis: high] - label for month selection combo box',
    },
    YEAR_SELECT: {
        id: 'CalendarPicker.year',
        defaultMessage: 'Select a year',
        description: '[Type: label][Vis: high] - label for year selection combo box',
    },
    MONTHS: {
        id: 'CalendarPicker.months',
        defaultMessage: '{months, select, January {January} February {February} March {March} April {April} May {May} June {June} July {July} August {August} September {September} October {October} November {November} December {December} other {other}}',
        description: '[Type: label][Vis: high] - label for the months',
    },
});
const startingYear = new Date().getFullYear() - 200;
const yearOptions = [...Array(251)].map((_, year) => ({
    label: `${startingYear + year}`,
    value: `${startingYear + year}`,
}));
function getPreviousMonth(currentMonth) {
    let newDate;
    if (currentMonth.getMonth() - 1 < 0) {
        newDate = new Date(currentMonth.getFullYear() - 1, 11, currentMonth.getDate());
    }
    else {
        const prevMonthLastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDate();
        if (currentMonth.getDate() > prevMonthLastDay) {
            newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, prevMonthLastDay);
        }
        else {
            newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, currentMonth.getDate());
        }
    }
    return newDate;
}
function getNextMonth(currentMonth) {
    let newDate;
    if (currentMonth.getMonth() + 1 > 11) {
        newDate = new Date(currentMonth.getFullYear() + 1, 0, currentMonth.getDate());
    }
    else {
        const nextMonthLastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0).getDate();
        if (currentMonth.getDate() > nextMonthLastDay) {
            newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, nextMonthLastDay);
        }
        else {
            newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, currentMonth.getDate());
        }
    }
    return newDate;
}
const comboBoxSeedId = 'calendar-picker-combobox';
function CalendarPicker({ onDaySelect, onDateChange, selectedDate, width = '300px', height = 'auto', startingDayOfWeek = 0, visible = true, }) {
    const focusTrapRef = useFocusTrap({
        selectors: ['button:not([tabindex="-1"])', 'input'],
    });
    const comboboxId = useMemo(() => uniqueId(comboBoxSeedId), []);
    const selectId = useMemo(() => uniqueId('calendar-picker-select'), []);
    const [state, setState] = useState(selectedDate);
    const handleDateChange = ({ startDate }) => {
        const d = startDate ?? new Date();
        setState(d);
        onDaySelect?.(d);
    };
    const { firstDayOfWeek, activeMonths, isDateSelected, isDateHovered, isFirstOrLastSelectedDate, isDateFocused, focusedDate, onDateHover, onDateSelect, onDateFocus, goToDate, } = Ye({
        startDate: selectedDate,
        endDate: null,
        focusedInput: Pe,
        onDatesChange: handleDateChange,
        numberOfMonths: 1,
        /*
         * minBookingDays and exactBookingDays are used to remove the date range feature
         * see: https://github.com/tomgreenwood1/react-datepicker/tree/f2b1969cb4808c68d578428c4c5e6ed98c96461e/packages/hooks#exactminbookingdays-boolean--undefined-default-false
         */
        minBookingDays: 1,
        exactMinBookingDays: true,
        /*
         * firstDayofWeek is used to set the first day of the week. 0 = Sunday, 1 = Monday, etc.
         */
        firstDayOfWeek: startingDayOfWeek,
    });
    useEffect(() => {
        goToDate(selectedDate);
    }, [selectedDate, goToDate]);
    const currentMonth = activeMonths[0];
    const { comboboxWrapper, calendarControls, calendarPickerWrapper, button, monthWrapper, monthSelectWrapper, yearSelectWrapper, } = useCalendarStyles({
        height,
        width,
        calendarVisibility: visible,
    });
    // populate monthOptions array
    const monthNames = [
        t(COPY$f.MONTHS, { months: 'January' }),
        t(COPY$f.MONTHS, { months: 'February' }),
        t(COPY$f.MONTHS, { months: 'March' }),
        t(COPY$f.MONTHS, { months: 'April' }),
        t(COPY$f.MONTHS, { months: 'May' }),
        t(COPY$f.MONTHS, { months: 'June' }),
        t(COPY$f.MONTHS, { months: 'July' }),
        t(COPY$f.MONTHS, { months: 'August' }),
        t(COPY$f.MONTHS, { months: 'September' }),
        t(COPY$f.MONTHS, { months: 'October' }),
        t(COPY$f.MONTHS, { months: 'November' }),
        t(COPY$f.MONTHS, { months: 'December' }),
    ];
    const monthOptions = monthNames.map((name) => ({
        label: name,
        value: name,
    }));
    const getMonthLabel = (monthNamesIndex) => {
        return monthNames[monthNamesIndex];
    };
    const handleMonthChange = (e) => {
        const monthIndex = monthOptions.findIndex((i) => i.value === e.target.value);
        const newDate = new Date(currentMonth.year, monthIndex, state.getDate());
        if (monthIndex !== -1) {
            goToDate(newDate);
            setState(newDate);
            onDateChange?.(newDate);
        }
    };
    const handleYearChange = (e) => {
        if (e.value.length >= 4) {
            const newDate = new Date(parseInt(e.value, 10), currentMonth.month, state.getDate());
            goToDate(newDate);
            setState(newDate);
            onDateChange?.(newDate);
        }
    };
    const handleGoToPreviousMonths = () => {
        const newDate = getPreviousMonth(state);
        setState(newDate);
        goToDate(newDate);
        onDateChange?.(newDate);
    };
    const handleGoToNextMonths = () => {
        const newDate = getNextMonth(state);
        setState(newDate);
        goToDate(newDate);
        onDateChange?.(newDate);
    };
    return (React__default.createElement("div", { className: calendarPickerWrapper, "data-testid": "CalendarPicker", ref: focusTrapRef },
        React__default.createElement("div", { className: calendarControls },
            React__default.createElement("button", { type: "button", onClick: handleGoToPreviousMonths, className: button, "aria-label": t(COPY$f.PREVIOUS_MONTH), "data-testid": "previous-month-button" },
                React__default.createElement(CaretLeftOutlineIcon, null)),
            React__default.createElement("div", { className: comboboxWrapper },
                React__default.createElement("div", { className: monthSelectWrapper },
                    React__default.createElement(NativeSelect, { id: selectId, value: getMonthLabel(currentMonth.month), onChange: handleMonthChange }, monthOptions.map((option) => (React__default.createElement(SelectAnswerOption, { value: option.value, label: option.value, key: option.value }))))),
                React__default.createElement("div", { className: yearSelectWrapper },
                    React__default.createElement(ComboBox, { id: comboboxId, "aria-label": t(COPY$f.YEAR_SELECT), options: yearOptions, value: currentMonth.year.toString(), onChange: handleYearChange }))),
            React__default.createElement("button", { type: "button", onClick: handleGoToNextMonths, className: button, "aria-label": t(COPY$f.NEXT_MONTH), "data-testid": "next-month-button" },
                React__default.createElement(CaretRightOutlineIcon, null))),
        React__default.createElement("div", { className: monthWrapper },
            React__default.createElement(Month, { year: currentMonth.year, month: currentMonth.month, firstDayOfWeek: firstDayOfWeek, onChange: goToDate, focusedDate: focusedDate, isDateSelected: isDateSelected, isDateFocused: isDateFocused, isDateHovered: isDateHovered, onDateHover: onDateHover, onDateSelect: onDateSelect, onDateFocus: onDateFocus, isFirstOrLastSelectedDate: isFirstOrLastSelectedDate }))));
}

const useStyles$z = createUseStyles({
    container: {
        visibility: ({ calendarVisibility }) => {
            return calendarVisibility ? 'visible' : 'hidden';
        },
    },
});

const DisclosureContainer = forwardRef(function DisclosureContainer({ onClose, visible = false, className, ...props }, ref) {
    const focusTrapRef = useFocusTrap({
        selectors: ['button:not([tabindex="-1"])', 'input'],
    });
    const disclosureRef = useForkRef(focusTrapRef, ref);
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose?.();
            }
        };
        const handleClick = (e) => {
            const target = e.target;
            const clickedOutside = !focusTrapRef.current?.contains(target);
            const comboBoxOptionClicked = !target.id.startsWith(comboBoxSeedId);
            if (visible && clickedOutside && comboBoxOptionClicked) {
                onClose?.();
            }
        };
        if (visible) {
            /*
             * RAWR-1437
             * Delay setting up the EventListener to after the current render, otherwise the handleClick triggers
             * when the calendar first opens and closes the calendar right away.
             */
            setTimeout(() => {
                document.addEventListener('keydown', handleKeyDown);
                document.addEventListener('click', handleClick);
            }, 0);
        }
        else {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClick);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);
    const { container } = useStyles$z({
        calendarVisibility: visible,
    });
    return (React__default.createElement("div", { ref: disclosureRef, className: classNames(container, className), ...props }));
});

// constants.ts
const US_DATE_FORMAT = 'MM/DD/YYYY';
const EURO_DATE_FORMAT = 'DD/MM/YYYY';

const DATE_DELIMITER = /[/.-]/g;
const validDateStringCheck = (dateString) => {
    const badValues = ['0', ' '];
    const isValid = dateString.every((value) => {
        // eslint-disable-next-line no-restricted-globals
        return badValues.indexOf(value) === -1 && !isNaN(parseInt(value, 10));
    });
    return dateString.length === 3 && isValid;
};
const toDateString = (date, format = US_DATE_FORMAT) => {
    if (!date) {
        return '';
    }
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    if (format === US_DATE_FORMAT) {
        return `${m}/${d}/${y}`;
    }
    return `${d}/${m}/${y}`;
};
const isValidDate = (year, month, day) => {
    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    return (date.getFullYear() === Number(year) &&
        date.getMonth() === Number(month) - 1 &&
        date.getDate() === Number(day));
};
const stringToDate = (dateString, format = US_DATE_FORMAT) => {
    if (!dateString) {
        return new Date();
    }
    if (!validDateStringCheck(dateString.split(DATE_DELIMITER))) {
        return new Date();
    }
    if (format === EURO_DATE_FORMAT) {
        const [d, m, y] = dateString.split(DATE_DELIMITER);
        if (isValidDate(y, m, d)) {
            return new Date(`${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T00:00:00`);
        }
    }
    const [m, d, y] = dateString.split(DATE_DELIMITER);
    if (isValidDate(y, m, d)) {
        return new Date(`${y.padStart(4, '0')}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T00:00:00`);
    }
    return new Date();
};
const getCalendarPosition = (inputBoundTop, disclosureBoundHeight) => {
    if (inputBoundTop < disclosureBoundHeight + 30) {
        // not enough space
        return 'auto';
    }
    return '100%';
};

const useStyles$y = createUseStyles((theme) => {
    return {
        container: {
            position: 'relative',
        },
        dateInputWrapper: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        dateInput: {
            maxWidth: '175px',
            maxHeight: '36px',
            padding: ({ isRTL = false }) => isRTL ? '6px 6px 6px 36px' : '6px 36px 6px 6px',
        },
        calendarButton: {
            borderRadius: 'none',
            border: 'none',
            margin: ({ isRTL = false }) => (isRTL ? '0 -37px 0 0' : '0 0 0 -37px'),
            height: '34px',
            width: '36px',
            fontSize: '15px',
            color: '#404040',
            backgroundColor: '#EFEFEF',
            '&:focus, &:hover': {
                cursor: 'pointer',
                outline: `2px solid ${theme.primaryAccentColor}`,
                background: 'linear-gradient(0deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.2) 30%, #EFEFEF 100%)',
            },
            '&:focus': {
                borderRadius: '3px',
            },
            '&:active': {
                background: 'linear-gradient(0deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.4) 30%, #EFEFEF 100%)',
            },
            '&:disabled': {
                opacity: '.5',
            },
        },
        calendarWrapper: {
            position: 'absolute',
            bottom: ({ calendarPosition }) => calendarPosition,
            zIndex: 100,
        },
        commentLabel: {
            fontWeight: theme.questionBody.fontWeight,
            fontFamily: theme.questionBody.fontFamily,
            marginBottom: 5,
            display: 'block',
            fontSize: '14px',
        },
    };
});

const defaultLabels$1 = {
    dateInputLabel: 'Date',
    iconButton: 'Open Calendar',
    usDateFormat: 'MM/DD/YYYY',
    intlDateFormat: 'DD/MM/YYYY',
};
function DateInput({ onChange, dateFormat = US_DATE_FORMAT, defaultValue = '', id, className, labels = defaultLabels$1, ...props }, ref) {
    const [calendarVisibility, setCalendarVisibility] = useState(false);
    const dateInputID = useMemo(() => uniqueId('date-input-'), []);
    const internalInputRef = useRef(null);
    const inputRef = useForkRef(ref, internalInputRef);
    const calendarButtonRef = useRef(null);
    const disclosureRef = useRef(null);
    const dateValueRef = useRef(defaultValue);
    const calendarPosition = useRef('100%');
    const calculateCalendarPosition = () => {
        if (internalInputRef.current && disclosureRef.current) {
            const inputBound = internalInputRef.current.getBoundingClientRect();
            const disclosureBound = disclosureRef.current.getBoundingClientRect();
            calendarPosition.current = getCalendarPosition(inputBound.top, disclosureBound.height);
        }
    };
    useSSRSafeLayoutEffect(() => {
        calculateCalendarPosition();
    }, [calendarVisibility]);
    const { dateInputLabel, iconButton, usDateFormat, intlDateFormat } = labels;
    const handleCalendarVisibility = () => {
        calculateCalendarPosition();
        setCalendarVisibility((s) => {
            return !s;
        });
    };
    const handleDisclosureClose = () => {
        setCalendarVisibility(false);
    };
    const handleDaySelect = (date) => {
        const dateString = toDateString(date, dateFormat);
        dateValueRef.current = dateString;
        internalInputRef.current?.setAttribute('value', dateString);
        onChange?.(dateString);
        calendarButtonRef.current?.focus();
        handleDisclosureClose();
    };
    const handleInputChange = (e) => {
        dateValueRef.current = e.target.value;
        onChange?.(e.target.value);
    };
    const { isRTL } = useContext(L10NContext);
    const { dateInput, calendarButton, calendarWrapper, commentLabel, dateInputWrapper, container, } = useStyles$y({
        isRTL,
        calendarPosition: calendarPosition.current,
    });
    return (React__default.createElement("div", { className: classNames(container, className), id: id },
        React__default.createElement("label", { htmlFor: dateInputID, className: commentLabel }, dateInputLabel),
        React__default.createElement("div", { className: dateInputWrapper },
            React__default.createElement(TextInput$1, { "data-testid": "DateInput", className: dateInput, maxLength: 10, onChange: handleInputChange, value: dateValueRef.current, ref: inputRef, placeholder: dateFormat === 'DD/MM/YYYY' ? intlDateFormat : usDateFormat, id: dateInputID, ...props }),
            React__default.createElement("button", { type: "button", ref: calendarButtonRef, className: calendarButton, onClick: handleCalendarVisibility, "aria-expanded": calendarVisibility, "aria-label": iconButton },
                React__default.createElement(CalendarIcon, null))),
        React__default.createElement(DisclosureContainer, { className: calendarWrapper, onClose: handleDisclosureClose, ref: disclosureRef, visible: calendarVisibility, "data-testid": "DisclosureContainer" },
            React__default.createElement(CalendarPicker
            /** date coming out */
            , { 
                /** date coming out */
                onDaySelect: handleDaySelect, 
                /** date going in */
                selectedDate: stringToDate(dateValueRef.current, dateFormat), visible: calendarVisibility }))));
}
var DateInput$1 = forwardRef(DateInput);

const COPY$e = defineMessages({
    DATE_LABEL: {
        id: 'DateInputWrapper.dateLabel',
        defaultMessage: 'Date',
        description: '[Type: label][Vis: high] - label for date input',
    },
    DATE_BUTTON_LABEL: {
        id: 'DateInputWrapper.dateButton',
        defaultMessage: 'Open calendar',
        description: '[Type: button][Vis: high] - aria text for opening the calendar picker',
    },
    US_DATE_FORMAT: {
        id: 'DateInputWrapper.dateFormatUs',
        defaultMessage: 'MM/DD/YYYY',
        description: '[Type: label][Vis: high] - ISO 8601 US date character format notation. No localized delimiter translation. Must be uppercase.',
    },
    INTL_DATE_FORMAT: {
        id: 'DateInputWrapper.dateFormatIntl',
        defaultMessage: 'DD/MM/YYYY',
        description: '[Type: label][Vis: high] - ISO 8601 International date character format notation. Must be uppercase.',
    },
});
function DateInputWrapper(props, ref) {
    // translates and defines the label dictionary
    const l10n = {
        dateInputLabel: t(COPY$e.DATE_LABEL),
        iconButton: t(COPY$e.DATE_BUTTON_LABEL),
        usDateFormat: t(COPY$e.US_DATE_FORMAT),
        intlDateFormat: t(COPY$e.INTL_DATE_FORMAT),
    };
    return React__default.createElement(DateInput$1, { labels: l10n, ...props, ref: ref });
}
var DateInputWrapper$1 = forwardRef(DateInputWrapper);

const useStyles$x = createUseStyles((theme) => ({
    container: {
        display: 'flex',
    },
    verticalContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    horizontalContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        /**
         * including specific margin value to match production
         */
        marginRight: 35,
    },
    label: {
        fontSize: '14px',
        fontWeight: theme.questionBody.fontWeight,
        fontFamily: theme.questionBody.fontFamily,
        width: '100%',
        display: 'block',
        marginBottom: '5px',
    },
    input: {
        /**
         * including specific height and width values to match production
         */
        maxWidth: '50px',
        maxHeight: '36px',
        textAlign: 'center',
    },
    separator: {
        width: '20px',
        height: '36px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    srOnly: {
        ...visuallyHidden,
    },
}));

const defaultLabels = {
    timeLabel: 'Time',
    hour: 'Hour',
    minutes: 'Minutes',
    daytimeLabel: 'AM/PM',
    am: 'AM',
    pm: 'PM',
    hourPlaceholder: 'hh',
    minutesPlaceholder: 'mm',
};
function TimeInput({ onChange, defaultValue = { hour: '', minute: '', period: '-' }, id = uniqueId('TimeInput-'), className, required, 'aria-describedby': ariaDescribedBy, labels = defaultLabels, }) {
    const [state, setState] = useState(defaultValue);
    const timeLabelID = `${id}-time-label`;
    const hourLabelID = `${id}-hour-label`;
    const hourInputID = `${id}-hour-input`;
    const minuteLabelID = `${id}-minute-label`;
    const minuteInputID = `${id}-minute-input`;
    const periodLabelID = `${id}-period-label`;
    const periodSelectID = `${id}-period-input`;
    const handleHourChange = (e) => {
        const newState = { ...state, hour: e.target.value };
        // if hour is > 12, set daytime period to PM
        if (!Number.isNaN(parseInt(newState.hour, 10)) &&
            parseInt(newState.hour, 10) > 12) {
            newState.period = '-';
        }
        if (!newState.period) {
            newState.period = '-';
        }
        setState(newState);
        onChange?.(newState);
    };
    const handleHourOnBlur = (e) => {
        const newState = { ...state };
        const hour = parseInt(e.target.value, 10);
        if (!Number.isNaN(hour)) {
            if (hour > 12) {
                newState.period = '-';
            }
            newState.hour = String(hour);
            setState(newState);
            onChange?.(newState);
        }
    };
    const handleMinuteChange = (e) => {
        const newState = {
            ...state,
            minute: e.target.value,
        };
        setState(newState);
        onChange?.(newState);
    };
    const handleSelectChange = (e) => {
        const period = e.target.value;
        const hour = state.hour ? parseInt(state.hour, 10) : 0;
        const newState = { ...state, period };
        if ((period === 'AM' || period === 'PM') && hour > 12) {
            newState.hour = `${hour - 12}`;
        }
        setState(newState);
        onChange?.(newState);
    };
    const { container, verticalContainer, horizontalContainer, input, label, separator, srOnly, } = useStyles$x();
    return (React__default.createElement("div", { className: classNames(container, className), id: id, "data-testid": "TimeInput" },
        React__default.createElement("div", { className: verticalContainer },
            React__default.createElement("label", { htmlFor: hourInputID, id: hourLabelID, "aria-labelledby": `${timeLabelID} ${hourLabelID}`, className: srOnly }, labels.hour),
            React__default.createElement("div", { className: horizontalContainer },
                React__default.createElement("div", { className: verticalContainer },
                    React__default.createElement("div", { className: label, id: timeLabelID }, labels.timeLabel),
                    React__default.createElement(TextInput$1, { className: input, maxLength: 2, onChange: handleHourChange, onBlur: handleHourOnBlur, value: state.hour, placeholder: labels.hourPlaceholder, id: hourInputID, required: required, "aria-describedby": ariaDescribedBy })),
                React__default.createElement("span", { className: separator }, ":"),
                React__default.createElement("div", { className: verticalContainer },
                    React__default.createElement("label", { htmlFor: minuteInputID, id: minuteLabelID, "aria-labelledby": `${timeLabelID} ${minuteLabelID}`, className: srOnly }, labels.minutes),
                    React__default.createElement(TextInput$1, { className: input, maxLength: 2, onChange: handleMinuteChange, value: state.minute, placeholder: labels.minutesPlaceholder, id: minuteInputID, "aria-labelledby": `${timeLabelID} ${minuteLabelID}`, required: required, "aria-describedby": ariaDescribedBy })))),
        React__default.createElement("div", { className: verticalContainer },
            React__default.createElement("label", { id: periodLabelID, htmlFor: periodSelectID, className: label }, labels.daytimeLabel),
            React__default.createElement(NativeSelect, { id: periodSelectID, value: state.period, onChange: handleSelectChange, required: required },
                React__default.createElement(SelectAnswerOption, { value: "-", label: "-" }),
                React__default.createElement(SelectAnswerOption, { value: "AM", label: labels.am }),
                React__default.createElement(SelectAnswerOption, { value: "PM", label: labels.pm })))));
}

const COPY$d = defineMessages({
    TIME_LABEL: {
        id: 'TimeInputWrapper.timeLabel',
        defaultMessage: 'Time',
        description: '[Type: label][Vis: high] - label for time input',
    },
    HOUR_LABEL: {
        id: 'TimeInputWrapper.hour',
        defaultMessage: 'Hour',
        description: '[Type: label][Vis: high] - label for hour input',
    },
    MINUTES_LABEL: {
        id: 'TimeInputWrapper.minutes',
        defaultMessage: 'Minutes',
        description: '[Type: label][Vis: high] - label for minutes input',
    },
    DAYTIME_LABEL: {
        id: 'TimeInputWrapper.daytimeLabel',
        defaultMessage: 'AM/PM',
        description: '[Type: label][Vis: high] - label for daytime AM/PM select',
    },
    AM: {
        id: 'TimeInputWrapper.am',
        defaultMessage: 'AM',
        description: '[Type: label][Vis: high] - label for ante meridiem',
    },
    PM: {
        id: 'TimeInputWrapper.pm',
        defaultMessage: 'PM',
        description: '[Type: label][Vis: high] - label for post meridiem',
    },
    HOUR_PLACEHOLDER: {
        id: 'TimeInputWrapper.hhPlaceholder',
        defaultMessage: 'hh',
        description: '[Type: placeholder][Vis: high] - placeholder text for hour input',
    },
    MINUTE_PLACEHOLDER: {
        id: 'TimeInputWrapper.mmPlaceholder',
        defaultMessage: 'mm',
        description: '[Type: placeholder][Vis: high] - placeholder text for minutes input',
    },
});
function TimeInputWrapper(props) {
    // translates and defines the label dictionary
    const l10n = {
        timeLabel: t(COPY$d.TIME_LABEL),
        hour: t(COPY$d.HOUR_LABEL),
        minutes: t(COPY$d.MINUTES_LABEL),
        daytimeLabel: t(COPY$d.DAYTIME_LABEL),
        am: t(COPY$d.AM),
        pm: t(COPY$d.PM),
        hourPlaceholder: t(COPY$d.HOUR_PLACEHOLDER),
        minutesPlaceholder: t(COPY$d.MINUTE_PLACEHOLDER),
    };
    return React__default.createElement(TimeInput, { labels: l10n, ...props });
}

const useStyles$w = createUseStyles((theme) => {
    const { fontSize, fontFamily, fontWeight } = theme.questionBody;
    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '7px',
            color: theme.answerColor,
        },
        label: {
            fontSize,
            fontFamily,
            fontWeight,
            color: theme.questionColor,
            marginBottom: '14px',
        },
        inputContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '35px',
            [`@media (max-width: ${theme.breakpoints.sm.min})`]: {
                flexDirection: 'column',
                gap: '5px',
            },
        },
        inlineErrorContainer: {
            padding: '10px 0',
            fontSize: '16px',
            fontWeight: theme.questionBody.fontWeight,
            fontFamily: theme.questionBody.fontFamily,
            '& ul': {
                padding: 0,
                margin: 0,
            },
            '& ul li': {
                paddingBottom: '0.2rem',
                listStyle: 'none',
                '&:before': {
                    content: '"*"',
                    marginRight: '5px',
                },
            },
        },
    };
});

const COPY$c = defineMessages({
    DATE_INTL_FORMAT_ERROR: {
        id: 'DateTime.formatErrorDateIntl',
        defaultMessage: 'Please provide date in DD/MM/YYYY format.',
        description: '[Type: label][Vis: high] - label for incorrect or missing date error (using ISO 8601 US date character format notation)',
    },
    DATE_US_FORMAT_ERROR: {
        id: 'DateTime.formatErrorDateUs',
        defaultMessage: 'Please provide date in MM/DD/YYYY format.',
        description: '[Type: label][Vis: high] - label for incorrect or missing date error (using ISO 8601 international date character format notation)',
    },
    ERROR_ENTER_HOURS: {
        id: 'DateTime.errorEnterHours',
        defaultMessage: 'Please enter hours from 0 to 23.',
        description: '[Type: label][Vis: high] - label for incorrect or missing hour input',
    },
    ERROR_ENTER_MINUTES: {
        id: 'DateTime.errorEnterMinutes',
        defaultMessage: 'Please enter minutes from 0 to 59.',
        description: '[Type: label][Vis: high] - label for incorrect or missing minute input',
    },
    ERROR_SELECT_PERIOD: {
        id: 'DateTime.errorSelectPeriod',
        defaultMessage: 'Please select AM/PM.',
        description: '[Type: label][Vis: high] - label for incorrect or missing period selection',
    },
    ERROR_YEAR_OUT_OF_RANGE: {
        id: 'DateTime.errorYearOutOfRange',
        defaultMessage: 'Please provide year from 1900 to 9999.',
        description: '[Type: label][Vis: high] - label for year out of range',
    },
});
function DateTime({ id: questionId, required, choices = [], responses = [], showDate = true, showTime = true, dateFormat, onChange, inlineErrors = [], ...questionFieldProps }) {
    const [responseValues, setResponseValues] = useState(responses);
    const errorId = createErrorId(questionId);
    const dateInputRef = useRef(null);
    /** Translation keys may change so this separates the key from the message */
    const errorMessage = useMemo(() => {
        return {
            ERROR_PROVIDE_DATE_INTL_FORMAT: t(COPY$c.DATE_INTL_FORMAT_ERROR),
            ERROR_PROVIDE_DATE_US_FORMAT: t(COPY$c.DATE_US_FORMAT_ERROR),
            ERROR_ENTER_HOURS: t(COPY$c.ERROR_ENTER_HOURS),
            ERROR_ENTER_MINUTES: t(COPY$c.ERROR_ENTER_MINUTES),
            ERROR_SELECT_PERIOD: t(COPY$c.ERROR_SELECT_PERIOD),
            ERROR_YEAR_OUT_OF_RANGE: t(COPY$c.ERROR_YEAR_OUT_OF_RANGE),
        };
    }, []);
    const getDateValueById = (id) => {
        const choice = responseValues.find((item) => item.id === id);
        return choice?.value.date;
    };
    const getTimeValueById = (id) => {
        const choice = responseValues.find((item) => item.id === id);
        return choice?.value
            ? {
                hour: choice?.value.hour,
                minute: choice?.value.minute,
                period: choice?.value.period,
            }
            : undefined;
    };
    const handleDateOrTimeChange = ({ id, date, time, }) => {
        const newState = responseValues.filter((r) => r.id !== id);
        const prevState = responseValues.find((r) => r.id === id) ?? {
            value: {
                date: '',
                hour: '',
                minute: '',
                period: 'AM',
            },
        };
        const newDateOrTime = time ?? { date };
        const newResponse = {
            id,
            value: { ...prevState.value, ...newDateOrTime },
        };
        if (newResponse.value?.period === '-') {
            newResponse.value.period =
                newResponse.value?.hour && parseInt(newResponse.value.hour, 10) >= 12
                    ? 'PM'
                    : 'AM';
        }
        if (newResponse.value?.date ||
            newResponse.value?.hour ||
            newResponse.value?.minute) {
            newState.push(newResponse);
        }
        setResponseValues(newState);
        onChange?.(newState);
    };
    const { container, label, inputContainer, inlineErrorContainer, } = useStyles$w();
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "DateTimeQuestionType", ...questionFieldProps }, choices?.map((choice) => {
        return (React__default.createElement("div", { className: container, key: choice.id, id: choice.id },
            React__default.createElement(RichText, { element: "div", text: choice.label, className: label }),
            React__default.createElement("div", { className: inputContainer },
                showDate && (React__default.createElement(DateInputWrapper$1, { ref: dateInputRef, onChange: (date) => handleDateOrTimeChange({ id: choice.id, date }), defaultValue: getDateValueById(choice.id), dateFormat: dateFormat, required: required, "aria-describedby": (questionFieldProps.error && errorId) || undefined })),
                showTime && (React__default.createElement(TimeInputWrapper, { onChange: (time) => handleDateOrTimeChange({ id: choice.id, time }), defaultValue: getTimeValueById(choice.id), required: required }))),
            inlineErrors.length > 0 && (React__default.createElement("div", { className: inlineErrorContainer },
                React__default.createElement("ul", null, inlineErrors
                    .filter((e) => e.fieldId === choice.id)
                    .map((error) => (React__default.createElement("li", { key: `${error.fieldId}-${error.detail}` }, errorMessage[error.detail]))))))));
    })));
}
var DateTime$1 = withErrorBoundary(DateTime);

const useStyles$v = createUseStyles((theme) => {
    const defaultFontSize = theme.fontSize.body;
    const { fontFamily = 'inherit', fontWeight = 'inherit', fontStyle = 'inherit', textDecoration = 'inherit', highlightColor = 'inherit', color = 'inherit', } = theme.questionBody ?? {};
    return {
        otherStyles: {
            marginTop: '5px',
            display: 'block',
        },
        hiddenInput: {
            display: 'none',
        },
        commentChoice: {
            marginTop: 10,
        },
        label: {
            display: 'block',
            marginTop: '10px',
            position: 'relative',
            fontFamily,
            fontWeight,
            fontStyle,
            textDecoration,
            highlightColor,
            color,
            fontSize: defaultFontSize,
        },
    };
});

/**
 * MultipleChoice Question Type (Tier 2)
 */
function Dropdown({ id: questionId, disabled, required: _required, readOnly: _readOnly, choices = [], choiceOther, choiceComment, responses: defaultResponses = [], onChange, ...fieldProps }) {
    const errorId = createErrorId(questionId);
    /** Filter choices that exist and are visible  */
    const { options, sortableOptions, otherOption, commentOption, } = useQuestionChoices([...choices, choiceOther, choiceComment]);
    const [responseValue, setResponseValue] = useState(defaultResponses);
    const { otherStyles, hiddenInput, commentChoice } = useStyles$v();
    /**
     * Default value by id
     * used for text-input based choices
     */
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value;
    };
    /**
     * Default value for the choices
     */
    const defaultValue = useMemo(() => {
        return defaultResponses.find((c) => c.type !== 'COMMENT')?.id;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * Other Answer Textfield state
     */
    const [otherText, setOtherText] = useState((otherOption && getDefaultValueById(otherOption.id)) || '');
    const handleSelectChange = (e) => {
        const choiceData = options.find((c) => c.id === e.target.value);
        const responses = responseValue.filter((c) => c.type === 'COMMENT');
        if (choiceData) {
            const { id, type } = choiceData;
            if (type === 'ANSWER') {
                /** Want to update the value with the value of the TextField texts, when otherAnswer is selected */
                responses.push({ id, type, value: otherText });
            }
            else {
                responses.push({ id, type, value: e.target.value });
            }
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    /**
     * Other Answer Textfield Change
     */
    const handleTextChange = (e) => {
        const choiceData = options.find((c) => c.type === 'ANSWER');
        const responses = responseValue.filter((c) => c.type === 'COMMENT');
        if (choiceData) {
            const { id, type } = choiceData;
            responses.push({ id, type, value: e.target.value });
            setOtherText(e.target.value);
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    const handleCommentChange = (e) => {
        const choiceData = options.find((c) => c.id === e.target.id);
        const responses = responseValue.filter((c) => c.type !== 'COMMENT');
        if (choiceData && !!e.target.value) {
            const { id, type } = choiceData;
            responses.push({ id, type, value: e.target.value });
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    const isOtherAnswerSelected = !!responseValue.find((c) => c.type === 'ANSWER');
    const multipleTextLines = otherOption && otherOption?.linesCount > 1;
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "DropdownQuestionType", ...fieldProps },
        React__default.createElement(NativeSelect, { defaultValue: defaultValue, disabled: disabled, onChange: handleSelectChange },
            React__default.createElement(SelectAnswerOption, { value: undefined }),
            " ",
            sortableOptions?.map((choice) => {
                return (React__default.createElement(SelectAnswerOption, { key: choice.id, id: choice.id, label: choice.label, value: choice.id }));
            }),
            otherOption && (React__default.createElement(SelectAnswerOption, { key: otherOption.id, id: otherOption.id, label: otherOption.label, value: otherOption.id }))),
        otherOption &&
            (multipleTextLines ? (React__default.createElement(TextArea$1, { defaultValue: getDefaultValueById(otherOption.id), autoFocus: false, rows: otherOption.linesCount, cols: otherOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, onChange: handleTextChange, className: classNames(otherStyles, {
                    [hiddenInput]: !isOtherAnswerSelected,
                }), tabIndex: isOtherAnswerSelected ? 0 : -1 })) : (React__default.createElement(TextInput$1, { defaultValue: getDefaultValueById(otherOption.id), autoFocus: false, autoHeight: true, size: otherOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, onChange: handleTextChange, className: classNames(otherStyles, {
                    [hiddenInput]: !isOtherAnswerSelected,
                }), tabIndex: isOtherAnswerSelected ? 0 : -1 }))),
        commentOption && (React__default.createElement(CommentChoice, { id: commentOption.id, label: commentOption.label, onChange: handleCommentChange, defaultValue: getDefaultValueById(commentOption.id), lineCount: commentOption.linesCount, charCount: commentOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, "aria-describedby": fieldProps.error && errorId, className: commentChoice }))));
}
var Dropdown$1 = withErrorBoundary(Dropdown);

const useStyles$u = createUseStyles((theme) => {
    const isFull = theme.layout.includes('CENTER') || theme.layout.includes('BANNER');
    const isThird = theme.layout.includes('THIRD');
    const isHalf = theme.layout.includes('HALF') ||
        (theme.layout.includes('FULL') && !theme.layout.includes('CENTER'));
    const cols3 = 'repeat(3, minmax(0, 1fr))';
    const cols2 = 'repeat(2, minmax(0, 1fr))';
    const cols1 = 'minmax(0, 1fr)';
    return {
        imageChoiceContainer: {
            display: 'grid',
            flexWrap: 'wrap',
            gap: '25px',
            width: '100%',
            gridTemplateColumns: 'minmax(0, 1fr)',
            [`@media (min-width: ${theme.breakpoints?.md.min})`]: {
                gridTemplateColumns: isFull || isThird ? cols2 : cols1,
            },
            [`@media (min-width: ${theme.breakpoints?.lg.min})`]: {
                gridTemplateColumns: () => {
                    if (isFull || isThird) {
                        return cols3;
                    }
                    if (isHalf) {
                        return cols2;
                    }
                    return cols1;
                },
            },
            [`@media (min-width: ${theme.breakpoints?.xl.min})`]: {
                gridTemplateColumns: isHalf ? cols2 : cols3,
            },
        },
        imageChoice: {
            width: '100%',
        },
        noneOfTheAboveContainer: {
            marginTop: '15px',
        },
    };
});

const INPUT_SIZE$1 = 20;
function ImageChoice({ id: questionId, readOnly, required, disabled, multiple = false, choices = [], choiceNoneOfTheAbove, onChange, responses = [], ...questionFieldProps }) {
    const imageChoiceType = multiple ? 'checkbox' : 'radio';
    const groupName = !multiple ? `${questionId}-imageChoice` : undefined;
    const errorId = createErrorId(questionId);
    const [selectedChoices, setSelectedChoices] = useState(responses);
    const handleChange = (e) => {
        const newState = !multiple
            ? []
            : selectedChoices.filter((c) => c.id !== e.id && c.type !== 'NOTA');
        if (e.checked) {
            newState.push({ id: e.id, value: e.value });
        }
        setSelectedChoices(newState);
        onChange?.(newState);
    };
    const handleNAChange = (e) => {
        const newState = [];
        if (e.checked) {
            newState.push({ id: e.id, value: e.value, type: 'NOTA' });
        }
        setSelectedChoices(newState);
        onChange?.(newState);
    };
    const isChecked = (id) => !!selectedChoices.find((item) => item.id === id);
    const { imageChoiceContainer, imageChoice, noneOfTheAboveContainer, } = useStyles$u();
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "ImageChoiceQuestionType", errorId: errorId, ...questionFieldProps },
        React__default.createElement("div", { className: imageChoiceContainer }, choices
            ?.filter((choice) => choice.visible)
            .map((choice) => {
            return (React__default.createElement(ImageChoiceOption, { key: choice.id, id: choice.id, disabled: disabled, required: required, readOnly: readOnly, label: choice.label, onChange: handleChange, className: imageChoice, value: choice.id, src: choice.image.url, alt: choice.image.altText || '', name: groupName, type: imageChoiceType, "aria-describedby": (questionFieldProps.error && errorId) || undefined, checked: isChecked(choice.id) }));
        })),
        choiceNoneOfTheAbove && choiceNoneOfTheAbove?.visible && (React__default.createElement("div", { className: noneOfTheAboveContainer },
            React__default.createElement(Checkbox$3, { id: choiceNoneOfTheAbove.id, value: choiceNoneOfTheAbove.id, onChange: handleNAChange, checked: isChecked(choiceNoneOfTheAbove.id), disabled: disabled, readOnly: readOnly, "aria-describedby": (questionFieldProps.error && errorId) || undefined, inputSize: INPUT_SIZE$1 }, choiceNoneOfTheAbove.label)))));
}
var ImageChoice$1 = withErrorBoundary(ImageChoice);

const useStyles$t = createUseStyles((theme) => {
    return {
        presentationalTitleStyles: {
            fontFamily: theme.questionTitle.fontFamily ?? 'inherit',
            fontSize: theme.questionBody.fontSize ?? '16px',
            fontWeight: theme.questionTitle.fontWeight ?? (theme.isAccessible ? 500 : 300),
            marginBottom: '24px',
            textDecoration: theme.questionTitle.textDecoration ?? 'inherit',
            color: theme.answerColor ?? 'inherit',
        },
        buttonStyles: {
            marginTop: '20px',
        },
    };
});

function ImagePresentational({ text, image, padding = { top: 0, bottom: 0, left: 0, right: 0 }, id, okButton = { visible: false }, }) {
    const richTextId = `text-presentational-header-${id}`;
    const { presentationalTitleStyles, buttonStyles } = useStyles$t();
    const { visible: showButton, ...buttonProps } = okButton;
    return (React__default.createElement(QuestionSpacing, { padding: padding, "data-testid": "ImagePresentational" },
        text && (React__default.createElement(RichText, { id: richTextId, element: "div", text: text, className: presentationalTitleStyles })),
        React__default.createElement(Image, { ...image }),
        showButton && React__default.createElement(Button, { ...buttonProps, className: buttonStyles })));
}
var ImagePresentational$1 = withErrorBoundary(ImagePresentational);

const useStyles$s = createUseStyles((theme) => ({
    radioInput: {
        '& label': {
            alignItems: 'baseline',
        },
    },
    labelContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    textInput: {
        opacity: ({ checked }) => (checked ? 1 : 0.5),
        cursor: ({ checked }) => (checked ? 'inherit' : 'pointer'),
        marginTop: 5,
        marginLeft: 32,
        maxWidth: 'calc(100% - 32px)',
        [`@media (max-width: ${theme.breakpoints.xxs.max})`]: {
            marginLeft: 0,
            maxWidth: '100%',
        },
        // overwrite TextInput styles
        fontSize: theme.questionBody.fontSize,
        lineHeight: 'normal',
    },
    checkHover: {
        '&:hover': {
            outline: 'none',
        },
    },
}));

function RadioTextfield({ refs, ...props }) {
    const { id, children: label, checked: checkedProp = false, value: valueProp, defaultValue, lineCount = 1, charCount = 50, maxLength, onClick, onChange, ...radioProps } = props;
    const radioGroup = useRadioGroup();
    const [checked, setChecked] = useState(checkedProp);
    const [value, setValue] = useState(valueProp || defaultValue);
    const radioInputRef = useRef(null);
    const radioRef = useForkRef(radioInputRef, refs?.radio);
    const textInputRef = useRef(null);
    const inputRef = useForkRef(textInputRef, refs?.text);
    const multipleTextLines = lineCount > 1;
    const handleValueChange = (e) => {
        radioGroup?.onChange(e);
        setValue(e.value);
        onChange?.(e);
    };
    const handleInputChange = (e) => {
        handleValueChange({ id, value: e.target.value, checked });
    };
    const handleFocusChange = (e) => {
        handleValueChange({ id, value: e.target.value, checked });
    };
    const handleClick = (e) => {
        /**
         * Selection via keyboard calls onClick event - check for
         * mouse-position to ensure we were called by a pointing device.
         */
        if (!checked && e.clientX !== 0 && e.clientY !== 0) {
            textInputRef?.current?.focus();
        }
        setChecked(true);
        onClick?.(e);
    };
    useEffect(() => {
        setChecked(checkedProp);
    }, [checkedProp]);
    useEffect(() => {
        if (radioGroup?.value !== value) {
            setChecked(false);
        }
    }, [radioGroup, value]);
    const { radioInput, textInput, checkHover } = useStyles$s({
        checked,
    });
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(Radio$1, { id: `radio-input-${id}`, checked: checked, value: value, onClick: handleClick, onChange: handleValueChange, className: radioInput, ref: radioRef, ...radioProps }, label),
        multipleTextLines ? (React__default.createElement(TextArea$1, { id: `radio-field-${id}`, defaultValue: defaultValue, autoFocus: false, rows: lineCount, cols: charCount, maxLength: maxLength, onClick: handleClick, onChange: handleInputChange, onBlur: handleFocusChange, onFocus: handleFocusChange, className: classNames(textInput, {
                [checkHover]: !checked,
            }), ref: inputRef, tabIndex: checked ? 0 : -1 })) : (React__default.createElement(TextInput$1, { id: `radio-field-${id}`, defaultValue: defaultValue, autoFocus: false, autoHeight: true, size: charCount, maxLength: maxLength, onClick: handleClick, onChange: handleInputChange, onBlur: handleFocusChange, onFocus: handleFocusChange, labelledby: `radio-input-${id}`, className: classNames(textInput, {
                [checkHover]: !checked,
            }), ref: inputRef, tabIndex: checked ? 0 : -1 }))));
}

const useStyles$r = createUseStyles({
    commentChoiceContainer: {
        marginTop: '10px',
    },
});

const INPUT_SIZE = 20;
/**
 * MultipleChoice Question Type (Tier 2)
 */
function MultipleChoice({ id: questionId, required, disabled, readOnly, columnLayout = 1, choices = [], choiceNoneOfTheAbove, choiceOther, choiceComment, responses: defaultResponses = [], onChange, ...fieldProps }) {
    const groupId = createFieldId(questionId);
    const errorId = createErrorId(questionId);
    /** Filter choices that exist and are visible  */
    const { options, sortableOptions, notaOption, otherOption, commentOption, } = useQuestionChoices([
        ...choices,
        choiceNoneOfTheAbove,
        choiceOther,
        choiceComment,
    ]);
    const [responseValue, setResponseValue] = useState(defaultResponses);
    /**
     * Default value by id
     * used for text-input based choices
     */
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value;
    };
    /**
     * Default value for the group
     */
    const defaultValue = useMemo(() => defaultResponses.find((c) => c.type !== 'COMMENT')?.value, 
    // defaultValues should not be changed post render (breaks rules of controlled components)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    /**
     * Determine if choice is selected
     * @return TRUE if the choice.id exists in responses
     */
    const isChecked = (id) => {
        return !!responseValue.find((c) => c.id === id);
    };
    const handleRadioChange = (e) => {
        const choiceData = options.find((c) => e.id.match(c.id));
        const responses = responseValue.filter((c) => c.type === 'COMMENT');
        if (choiceData) {
            const { id, type } = choiceData;
            responses.push({ id, type, value: e.value });
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    const handleCommentChange = (e) => {
        const choiceData = options.find((c) => c.id === e.target.id);
        const responses = responseValue.filter((c) => c.type !== 'COMMENT');
        if (choiceData && !!e.target.value) {
            const { id, type } = choiceData;
            responses.push({ id, type, value: e.target.value });
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    /**
     * deselect choice if the question is not required
     */
    const unCheckOption = () => {
        if (required) {
            return;
        }
        const responses = responseValue.filter((c) => c.type === 'COMMENT');
        setResponseValue(responses);
        onChange?.(responses);
    };
    const handleClick = (e) => {
        // match is used since RadioTextfield applies a prefix
        // to maintain unique ID's'
        const input = e.target;
        const isSelected = responseValue.find((c) => input.id.match(c.id));
        if (isSelected && input.checked) {
            e.preventDefault();
            unCheckOption();
        }
    };
    const handleKeyboard = (e) => {
        const isSelected = responseValue.find((c) => e.target.id.match(c.id));
        if (isSelected && e.code === 'Space') {
            e.preventDefault();
            unCheckOption();
        }
    };
    const radioEvents = { onClick: handleClick, onKeyDown: handleKeyboard };
    const radioGroupProps = {
        disabled,
        readOnly,
        name: groupId,
        defaultValue,
        onChange: handleRadioChange,
    };
    const { commentChoiceContainer } = useStyles$r();
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "MultipleChoiceQuestionType", ...fieldProps },
        React__default.createElement(RadioGroup, { ...radioGroupProps },
            React__default.createElement(QuestionAnswerLayoutTemplate, { gridCellMargin: [0, 2, 5, 2], otherCellMargin: [0, 2, 5, 2], columns: columnLayout, other: otherOption && (React__default.createElement(RadioTextfield, { id: otherOption.id, checked: isChecked(otherOption.id), defaultValue: getDefaultValueById(otherOption.id), lineCount: otherOption.linesCount, charCount: otherOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, "aria-describedby": fieldProps.error && errorId, inputSize: INPUT_SIZE, ...radioEvents }, otherOption.label)), noneOfTheAbove: notaOption && (React__default.createElement(Radio$1, { id: notaOption.id, value: notaOption.id, checked: isChecked(notaOption.id), "aria-describedby": fieldProps.error && errorId, inputSize: INPUT_SIZE, ...radioEvents }, notaOption.label)) }, sortableOptions.map((choice) => (React__default.createElement(Radio$1, { id: choice.id, key: choice.id, value: choice.id, checked: isChecked(choice.id), "aria-describedby": fieldProps.error && errorId, inputSize: INPUT_SIZE, ...radioEvents }, choice.label)))),
            commentOption && (React__default.createElement(CommentChoice, { id: commentOption.id, label: commentOption.label, onChange: handleCommentChange, defaultValue: getDefaultValueById(commentOption.id), lineCount: commentOption.linesCount, charCount: commentOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, "aria-describedby": fieldProps.error && errorId, className: commentChoiceContainer })))));
}

const useStyles$q = createUseStyles((theme) => {
    const { fontSize, fontFamily, fontWeight } = theme.questionBody;
    return {
        container: {
            display: 'flex',
            flexDirection: 'row',
            wordBreak: 'break-word',
            marginBottom: '7px',
            [`@media (max-width: ${theme.breakpoints.sm.min})`]: {
                flexWrap: 'wrap',
            },
        },
        label: {
            fontSize,
            fontFamily,
            fontWeight,
            color: theme.questionColor,
            flex: '0 0 20%',
            marginRight: '7px',
            [`@media (max-width: ${theme.breakpoints.sm.min})`]: {
                flex: '0 0 100%',
                margin: '0 2px 2px 0',
            },
        },
        input: {
            marginTop: 'auto',
            flex: 'auto',
        },
    };
});

function MultipleTextbox({ id: questionId, required: _req, disabled, readOnly, choices = [], responses = [], onChange, size, ...fieldProps }) {
    const [responseValues, setResponseValues] = useState(responses);
    const { error, errorId = createErrorId(questionId) } = fieldProps;
    const getDefaultValueById = (id) => {
        const defaultChoice = responseValues.find((item) => item.id === id);
        return defaultChoice?.value;
    };
    const handleChange = (e) => {
        const newState = responseValues.filter((response) => response.id !== e.target.id);
        if (e.target.value) {
            const newResponse = { id: e.target.id, value: e.target.value };
            newState.push(newResponse);
        }
        setResponseValues(newState);
        onChange?.(newState);
    };
    const { container, label, input } = useStyles$q();
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "MultipleTextbox", ...fieldProps }, choices?.map((choice) => {
        const labelId = `${choice.id}-label`;
        return (React__default.createElement("div", { className: container, key: choice.id },
            React__default.createElement("label", { className: label, id: labelId, htmlFor: choice.id },
                React__default.createElement(RichText, { element: "span", text: choice.label })),
            React__default.createElement(TextInput$1, { id: choice.id, defaultValue: getDefaultValueById(choice.id), disabled: disabled, readOnly: readOnly, size: size, onChange: handleChange, "aria-invalid": !!error, "aria-describedby": error && errorId, maxLength: TEXT_INPUT_MAX_CHARS, className: input })));
    })));
}

const useStyles$p = createUseStyles((theme) => {
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    return {
        npsContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            minWidth: 230,
        },
        npsLabel: {
            color: theme.questionColor,
            fontFamily: theme.questionBody.fontFamily,
            fontSize: 14,
            fontWeight: fontWeightOptions.regular,
            lineHeight: 1.25,
        },
        npsRatingButtonList: {
            margin: 0,
            padding: 0,
            display: 'inline-flex',
            listStyleType: 'none',
            border: [1, 'solid', theme.primaryAccentColor],
            borderRadius: 4,
            overflow: 'hidden',
            outline: [2, 'solid', 'transparent'],
            outlineOffset: 2,
            width: '100%',
        },
        npsRatingButton: ({ isRTL }) => ({
            padding: 0,
            minWidth: 20,
            flex: '1 1 9.09%',
            '& + $npsRatingButton': {
                borderLeft: isRTL ? 0 : [2, 'solid', theme.primaryAccentColor],
                borderRight: isRTL ? [2, 'solid', theme.primaryAccentColor] : 0,
            },
        }),
        minLabel: ({ isRTL }) => ({
            margin: isRTL ? [0, 4, 2, 0] : [0, 0, 2, 4],
        }),
        maxLabel: ({ isRTL }) => ({
            margin: isRTL ? [0, 0, 2, 4] : [0, 4, 2, 0],
        }),
        srOnly: visuallyHidden$1,
    };
});

const useStyles$o = createUseStyles((theme) => {
    // theme.correctAnswerHighlightColor is the correct color, but not an accurate token.
    const focusBackgroundColor = theme.correctAnswerHighlightColor;
    return {
        container: {
            position: 'relative',
            display: 'flex',
            cursor: 'pointer',
        },
        radio: {
            appearance: 'none',
            outline: 'none',
            position: 'absolute',
            width: '100%',
            height: '100%',
            '&:focus + $label, &:hover + $label': {
                border: [2, 'solid', theme.primaryAccentColor],
                backgroundColor: focusBackgroundColor,
            },
            '&:checked + $label': {
                backgroundColor: theme.questionColor,
                color: theme.primaryBackgroundColor,
                fontWeight: 'bold',
                transition: 'background 200ms ease',
            },
        },
        label: {
            position: 'relative',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            border: [2, 'solid', 'transparent'],
            backgroundColor: theme.primaryBackgroundColor,
            color: theme.questionColor,
            fontFamily: theme.fontFamily,
            fontSize: 15,
            fontWeight: 700,
            padding: [10, 0],
            width: '100%',
        },
    };
});
function NpsRatingButton({ id, className, children: label, component: Tag = 'div', ...props }) {
    const styles = useStyles$o();
    return (React__default.createElement(Tag, { className: classNames(styles.container, className) },
        React__default.createElement("input", { id: id, type: "radio", className: styles.radio, ...props }),
        React__default.createElement("label", { className: classNames(styles.label), htmlFor: id }, label)));
}

const COPY$b = defineMessages({
    NPS_QUESTION_TITLE: {
        id: 'Nps.QUESTION_TITLE',
        defaultMessage: '<div class="{srOnlyClass}">On a scale of {minValue} to {maxValue}, </div>{heading}<div id="{instructionsId}" class="{srOnlyClass}"> {minValue} for {minLabelText}, {maxValue} for {maxLabelText}</div>',
        description: '[Type: header][Vis: high] - question title',
    },
});
/** Unicode dash characters, in addition to standard delimiters (forward/back-slash, pipe, hyphen) */
const LABEL_SEPERATOR = /\s(?:[\u2010-\u2015\u2043/\-|])\s/gi;
function Nps({ id: questionId, choices = [], className, title, rowId, responses: defaultResponses = [], required, onChange, ...fieldProps }) {
    const [responseValue, setResponseValue] = useState(defaultResponses);
    const defaultValue = useMemo(() => defaultResponses.find((r) => !!r.value)?.value ?? '', 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const ratingChanged = (e) => {
        const { value } = e.target;
        const response = value ? [{ id: rowId, value }] : [];
        /**
         * FIXES: RAWR-1628
         * Safari doesn't apply focus by default; an inelegant solution for an inelegant browser.
         * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
         */
        e.target.focus();
        setResponseValue(response);
        onChange?.(response);
    };
    const unCheckOption = (input) => {
        if (required) {
            return;
        }
        // eslint-disable-next-line no-param-reassign
        input.checked = false;
        setResponseValue([]);
        onChange?.([]);
    };
    const buttonClicked = (e) => {
        const input = e.target;
        const isSelected = responseValue[0]?.value === input.value;
        if (isSelected && input.checked) {
            unCheckOption(input);
        }
    };
    const handleKeyboard = (e) => {
        const input = e.target;
        const isSelected = responseValue[0]?.value === input.value;
        if (isSelected && e.code === 'Space') {
            e.preventDefault();
            unCheckOption(input);
        }
    };
    const getLabel = (str) => {
        let parts = str.split(LABEL_SEPERATOR);
        // the direction of the label is not always written reverse,
        // and is not based on RTL. (i.e. Russian)
        if (Number.isNaN(Number(parts[0]))) {
            parts = parts.reverse();
        }
        const [numLabel, textLabel] = parts;
        return [numLabel, textLabel ?? numLabel];
    };
    const { isRTL } = useContext(L10NContext);
    const errorId = createErrorId(questionId);
    const { npsContainer, npsLabel, minLabel, maxLabel, srOnly, npsRatingButtonList, npsRatingButton, } = useStyles$p({
        isRTL,
    });
    const { heading } = title;
    const minFullLabel = choices[0]?.label ?? '';
    const maxFullLabel = choices.slice(-1)[0]?.label ?? '';
    const [minValue, minLabelText] = getLabel(minFullLabel);
    const [maxValue, maxLabelText] = getLabel(maxFullLabel);
    const instructionsId = `${questionId}-instructions`;
    const accessibleHeading = t(COPY$b.NPS_QUESTION_TITLE, {
        minValue,
        maxValue,
        minLabelText,
        maxLabelText,
        heading,
        instructionsId,
        srOnlyClass: classNames(srOnly),
    });
    const accessibleTitle = {
        ...title,
        heading: accessibleHeading,
    };
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "NpsQuestionType", title: accessibleTitle, ...fieldProps },
        React__default.createElement("div", { className: classNames(npsContainer, className) },
            React__default.createElement("span", { className: classNames(npsLabel, minLabel), role: "presentation" }, minLabelText),
            React__default.createElement("span", { className: classNames(npsLabel, maxLabel), role: "presentation" }, maxLabelText),
            React__default.createElement("div", { className: classNames(npsRatingButtonList) }, choices.map((c) => (React__default.createElement(NpsRatingButton, { id: `${questionId}-${c.id}`, key: `${questionId}-${c.id}`, name: `${questionId}-rating`, value: c.id, className: classNames(npsRatingButton), onChange: ratingChanged, onClick: buttonClicked, onKeyDown: handleKeyboard, defaultChecked: defaultValue === c.id }, getLabel(c.label)[0])))))));
}

const getBackgroundColor = (themeIsDark) => {
    if (themeIsDark) {
        return 'rgba(0, 0, 0, 0.15)';
    }
    return 'rgba(0, 0, 0, 0.05)';
};
const useStyles$n = createUseStyles((theme) => {
    const layouts = [
        'BACKGROUND_FULL_TEXT_RIGHT',
        'BACKGROUND_FULL_TEXT_LEFT',
        'BACKGROUND_PARTIAL_LEFT_HALF',
        'BACKGROUND_PARTIAL_RIGHT_HALF',
    ];
    const isHalf = layouts.some((str) => theme.layout.includes(str));
    return {
        gridCell: {
            display: 'flex',
            flex: '1 auto',
            position: 'relative',
            padding: 3,
            justifyContent: 'center',
        },
        gridCellMobile: {
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                justifyContent: 'flex-start',
                background: theme.isDark
                    ? 'rgba(0, 0, 0, 0.15)'
                    : 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
            },
        },
        [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
            gridCellMobile: ({ isGridWhenMobile }) => ({
                background: !isGridWhenMobile && isHalf ? getBackgroundColor(theme.isDark) : '',
                marginTop: !isGridWhenMobile && isHalf ? '5px' : '',
                marginBottom: !isGridWhenMobile && isHalf ? '5px' : '',
            }),
        },
    };
});

function GridCell({ isGridWhenMobile = false, children, className, ...props }) {
    const { gridCell, gridCellMobile } = useStyles$n({ isGridWhenMobile });
    return (React__default.createElement("div", { className: classNames(gridCell, {
            [gridCellMobile]: !isGridWhenMobile,
        }, className), role: "gridcell", ...props }, children));
}

const useStyles$m = createUseStyles((theme) => {
    const layouts = [
        'BACKGROUND_FULL_TEXT_RIGHT',
        'BACKGROUND_FULL_TEXT_LEFT',
        'BACKGROUND_PARTIAL_LEFT_HALF',
        'BACKGROUND_PARTIAL_RIGHT_HALF',
    ];
    const isHalf = layouts.some((str) => theme.layout.includes(str));
    return {
        labelVisibleBase: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'left',
            padding: [3, 3, 3, 7],
            color: theme.answerColor,
            fontWeight: theme.questionBody.fontWeight ?? null,
            fontFamily: theme.questionBody.fontFamily ?? null,
            fontSize: theme.questionBody.fontSize ?? null,
        },
        labelVisibleMobile: {
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                width: '100%',
            },
        },
        labelHidden: { display: 'none' },
        rowBaseStyles: {
            color: theme.answerColor,
            border: 'none',
            background: theme.isDark
                ? 'rgba(0, 0, 0, 0.15)'
                : 'rgba(0, 0, 0, 0.05)',
            [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
                background: ({ isGridWhenMobile }) => !isGridWhenMobile && isHalf ? 'transparent' : '',
            },
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                marginTop: '5px',
                marginBottom: '5px',
            },
        },
        rowAsColumnStyles: {
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                flexDirection: 'column',
            },
        },
    };
});

function GridRow({ id: rowId, row: rowIndex, label, columnDataArray, hideLabel, className, gridComponent, isGridWhenMobile = false, }) {
    const { rowBaseStyles, rowAsColumnStyles, labelHidden, labelVisibleBase, labelVisibleMobile, } = useStyles$m({ isGridWhenMobile });
    const labelVisibleClass = isGridWhenMobile
        ? labelVisibleBase
        : classNames(labelVisibleBase, labelVisibleMobile);
    const labelClass = hideLabel ? labelHidden : labelVisibleClass;
    return (React__default.createElement("div", { id: rowId, role: "row", className: classNames(rowBaseStyles, className, {
            [rowAsColumnStyles]: !isGridWhenMobile,
        }) },
        React__default.createElement("div", { id: `${rowId}-header`, role: "rowheader", className: labelClass },
            React__default.createElement(RichText, { element: "span", text: label })),
        columnDataArray.map(({ id: colId }, colIndex) => {
            const cellId = `${rowId}-${colId}-cell`;
            const props = {
                rowId,
                rowIndex,
                colId,
                colIndex,
                label,
                'aria-labelledby': `${rowId}-header ${colId}-header`,
            };
            return (React__default.createElement(GridCell, { id: cellId, key: cellId, "data-row": rowIndex, "data-col": colIndex, isGridWhenMobile: isGridWhenMobile }, gridComponent?.({ ...props })));
        })));
}

const useStyles$l = createUseStyles((theme) => {
    const layouts = [
        'BACKGROUND_FULL_TEXT_RIGHT',
        'BACKGROUND_FULL_TEXT_LEFT',
        'BACKGROUND_PARTIAL_LEFT_HALF',
        'BACKGROUND_PARTIAL_RIGHT_HALF',
    ];
    const isHalf = layouts.some((str) => theme.layout.includes(str));
    return {
        gridContainer: {
            width: '100%',
            // grid keeps the grid rows all the same length, and therefore the grid columns in alignment
            display: 'grid',
            gridTemplateColumns: ({ labelWidth, columnCount, hideLabel }) => hideLabel
                ? `repeat(${columnCount}, minmax(auto, 1fr))`
                : `fit-content(auto, ${labelWidth}%) repeat(${columnCount}, minmax(auto, 1fr))`,
        },
        gridRow: {
            gridColumn: ({ columnCount, hideLabel }) => `span ${columnCount + (hideLabel ? 0 : 1)}`,
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
                gridTemplateColumns: ({ isGridWhenMobile }) => !isGridWhenMobile && isHalf ? '100%' : '',
            },
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                gridTemplateColumns: ({ isGridWhenMobile }) => !isGridWhenMobile ? '100%' : '',
                background: ({ isGridWhenMobile }) => !isGridWhenMobile ? 'transparent' : '',
            },
            // if comment per row is enabled
            '&:nth-child(4n)': {
                background: ({ hasOtherColumn }) => hasOtherColumn ? 'transparent' : '',
            },
            // if comment per row is disabled
            '&:nth-child(2n+3)': {
                background: ({ hasOtherColumn }) => hasOtherColumn ? '' : 'transparent',
            },
        },
        gridOtherRow: ({ columnCount, hideLabel, isGridWhenMobile }) => ({
            gridColumn: `span ${columnCount + (hideLabel ? 0 : 1)}`,
            background: theme.isDark
                ? 'rgba(0, 0, 0, 0.15)'
                : 'rgba(0, 0, 0, 0.05)',
            '&:nth-child(4n+1)': {
                background: 'transparent',
            },
            '& > div': {
                display: 'block',
            },
            [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
                background: !isGridWhenMobile && isHalf ? 'transparent' : '',
                margin: !isGridWhenMobile && isHalf ? '0' : '',
                '& > div > div': {
                    margin: !isGridWhenMobile && isHalf ? '5px 0 10px' : '',
                },
            },
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                background: 'transparent',
                '& div': {
                    margin: '0',
                },
                '& > div': {
                    background: 'transparent',
                    margin: '5px 0 10px',
                },
            },
        }),
        gridOtherCell: {
            [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
                background: ({ isGridWhenMobile }) => !isGridWhenMobile && isHalf ? 'transparent' : '',
            },
        },
        gridStyles: {
            display: 'flex',
            flexWrap: 'wrap',
            color: theme.answerColor,
            border: 'none',
        },
        columnContainerMobile: {
            [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
                display: ({ isGridWhenMobile }) => !isGridWhenMobile && isHalf ? 'none' : '',
            },
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                display: 'none',
            },
        },
        columnHeader: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            wordBreak: 'keep-all',
            flex: 1,
            padding: 3,
            color: theme.answerColor,
            fontWeight: theme.questionBody.fontWeight ?? null,
            fontFamily: theme.questionBody.fontFamily ?? null,
            fontSize: 14,
            textAlign: 'center',
        },
        bufferVisible: {
            padding: [3, 3, 3, 7],
        },
        bufferHidden: { display: 'none' },
        scrollContainer: {
            overflowX: 'auto',
        },
    };
});

function MatrixGrid({ id, rowDataArray, columnDataArray, commentColumnOtherOption, hideLabel, labelWidth = 10, gridComponent, commentColumnComponent, isGridWhenMobile, }) {
    const gridRef = useRef(null);
    const visibleColumns = columnDataArray.filter((col) => {
        if (col.visible) {
            return col;
        }
        return null;
    });
    /**
     * @note This is specific to MatrixRating, logic should be moved there and favour
     * composition over configuration
     */
    /** If true "other" inputs are rendered per-row  */
    const hasCommentColumnOther = commentColumnOtherOption?.type === 'COMMENT_COLUMN' &&
        commentColumnComponent;
    let computedRows;
    if (hasCommentColumnOther) {
        // fill rows with artificial rows for the other column options
        computedRows = rowDataArray.reduce((acc, row) => {
            return [
                ...acc,
                row,
                {
                    ...row,
                    id: `${row.id}-other`,
                    associatedId: row.id,
                    isOtherRow: true,
                },
            ];
        }, []);
    }
    else {
        computedRows = rowDataArray;
    }
    const columnCount = visibleColumns.length;
    const hasOtherColumn = !!hasCommentColumnOther;
    const { columnContainerMobile, columnHeader, bufferHidden, bufferVisible, gridContainer, scrollContainer, gridRow, gridOtherRow, gridOtherCell, } = useStyles$l({
        labelWidth,
        columnCount,
        hideLabel,
        hasOtherColumn,
        isGridWhenMobile,
    });
    const bufferClass = hideLabel ? bufferHidden : bufferVisible;
    return (React__default.createElement("div", { className: scrollContainer },
        React__default.createElement("div", { id: id, role: "grid", ref: gridRef, className: gridContainer },
            React__default.createElement("div", { role: "row", className: classNames(gridRow, {
                    [columnContainerMobile]: !isGridWhenMobile,
                }) },
                React__default.createElement("div", { role: "gridcell", className: bufferClass }),
                visibleColumns.map((column) => {
                    return (React__default.createElement("div", { id: `${column.id}-header`, role: "columnheader", key: column.id, className: columnHeader },
                        React__default.createElement(RichText, { element: "span", text: column.label })));
                })),
            computedRows.map((row, rowIndex) => !row.isOtherRow ? (React__default.createElement(GridRow, { id: row.id, hideLabel: hideLabel, key: row.id, row: rowIndex, label: row.label, columnDataArray: visibleColumns, gridComponent: gridComponent, isGridWhenMobile: isGridWhenMobile, className: gridRow })) : (React__default.createElement("div", { role: "row", className: gridOtherRow, "data-other": true, key: row.id },
                React__default.createElement(GridCell, { id: `${rowIndex}-${columnCount + 1}-cell`, "data-row": rowIndex, "data-col": columnCount + 1, colSpan: columnCount + 1, isGridWhenMobile: isGridWhenMobile, className: gridOtherCell }, commentColumnComponent?.({
                    commentColumnOtherOption,
                    row,
                    columnCount,
                }))))))));
}

const useStyles$k = createUseStyles({
    cellInputWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    scrollContainer: {
        overflowX: 'auto',
        paddingBottom: '1em',
    },
    commentChoiceContainer: {
        width: '100%',
        marginTop: '10px',
    },
});

const UNSELECTED_OPTION = 'unselectedOption';
const COPY$a = defineMessages({
    SELECT_AN_OPTION: {
        id: 'MatrixDropdown.EmptyTopNativeSelect',
        defaultMessage: 'Select an option',
        description: 'Label for screen readers when the top/default selection is empty',
    },
});
function MatrixDropdown({ id: questionId, choices: rows = [], columnChoices: columns = [], choiceComment, onChange, responses: defaultResponses = [], disabled: _disabled, readOnly: _ro, required: _req, labelWidth, ...questionFieldProps }) {
    const errorId = createErrorId(questionId);
    /** Filter choices that exist and are visible  */
    const { options, sortableOptions: sortableRows, commentOption, } = useQuestionChoices([...rows, choiceComment]);
    const [responseValue, setResponseValue] = useState(defaultResponses);
    /**
     * Default value by id
     * used for text-input based choices (i.e. choiceComment)
     * Matrix provides a 2D Array construct
     */
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value[0]?.value;
    };
    const handleChange = (e, gridCell) => {
        const { rowId, colId } = gridCell;
        const storedValuesForGrid = responseValue.filter((r) => !(r.id === rowId));
        const rowDataContainingChange = responseValue.find((r) => r.id === rowId) || {
            id: rowId,
            value: [],
        };
        const prevCellsForChangedRow = rowDataContainingChange.value.filter((cell) => cell.id !== colId);
        const newCellData = { id: colId, value: e.target.value };
        // remove old data for the changed cell
        const newCellsForChangedRow = prevCellsForChangedRow.filter((cell) => cell.id !== colId);
        if (newCellData.value !== UNSELECTED_OPTION) {
            newCellsForChangedRow.push(newCellData);
        }
        // add the new row if at least one cell has a value other than UNSELECTED_OPTION
        const newRowForGrid = newCellsForChangedRow.length > 0
            ? [{ ...rowDataContainingChange, value: newCellsForChangedRow }]
            : [];
        const responses = [...storedValuesForGrid, ...newRowForGrid];
        setResponseValue(responses);
        onChange?.(responses);
    };
    const handleCommentChange = (e) => {
        const choiceData = options.find((c) => c.id === e.target.id);
        const responses = responseValue.filter((c) => c.type !== 'COMMENT');
        if (choiceData && !!e.target.value) {
            const { id, type } = choiceData;
            const response = { id, type, value: e.target.value };
            // @note: ensure to loop the values when transforming in RespWeb
            responses.push({
                id,
                type,
                value: [response],
            });
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    /**
     * Default value for the choices
     */
    const getDefaultValue = (rowId, colId) => {
        return defaultResponses
            .find((c) => c.id === rowId)
            ?.value?.find((c) => c.id === colId)?.value;
    };
    const { cellInputWrapper, scrollContainer, commentChoiceContainer, } = useStyles$k();
    const selectAnOptionWithDashes = `-- ${t(COPY$a.SELECT_AN_OPTION)} --`;
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "MatrixDropdownQuestionType", errorId: errorId, ...questionFieldProps },
        React__default.createElement("div", { className: scrollContainer },
            React__default.createElement(MatrixGrid, { id: questionId, columnDataArray: columns, rowDataArray: sortableRows, labelWidth: labelWidth, isGridWhenMobile: true, 
                // eslint-disable-next-line react/no-unstable-nested-components
                gridComponent: (gridCell) => {
                    const { colId } = gridCell;
                    return (React__default.createElement("div", { className: cellInputWrapper },
                        React__default.createElement(NativeSelect, { onChange: (e) => handleChange(e, gridCell), defaultValue: getDefaultValue(gridCell.rowId, gridCell.colId), "aria-labelledby": gridCell['aria-labelledby'] },
                            React__default.createElement(SelectAnswerOption, { value: UNSELECTED_OPTION, label: selectAnOptionWithDashes }),
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            columns
                                .find((col) => col.id === colId)
                                .choices.map((choice) => {
                                if (choice.visible) {
                                    return (React__default.createElement(SelectAnswerOption, { key: choice.id, id: choice.id, label: choice.label, value: choice.id }));
                                }
                                return null; // Add this line to return null for non-visible choices
                            }))));
                } }),
            commentOption && (React__default.createElement(CommentChoice, { id: commentOption.id, label: commentOption.label, onChange: handleCommentChange, defaultValue: getDefaultValueById(commentOption.id), lineCount: commentOption.linesCount, charCount: commentOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, className: commentChoiceContainer })))));
}

const useStyles$j = createUseStyles((theme) => {
    const layouts = [
        'BACKGROUND_FULL_TEXT_RIGHT',
        'BACKGROUND_FULL_TEXT_LEFT',
        'BACKGROUND_PARTIAL_LEFT_HALF',
        'BACKGROUND_PARTIAL_RIGHT_HALF',
    ];
    const isHalf = layouts.some((str) => theme.layout.includes(str));
    return {
        cellInputWrapperBase: ({ checked }) => ({
            display: 'flex',
            width: '100%',
            border: [1, 'solid', checked ? theme.questionColor : 'transparent'],
            '&:hover': {
                border: [1, checked ? 'solid' : 'dashed', theme.questionColor],
            },
            backgroundColor: checked ? theme.input.bgColor : 'transparent',
            '&:focus-within': {
                backgroundColor: theme.input.bgColor,
            },
            color: theme.answerColor,
            padding: 5,
            borderRadius: 4,
            cursor: 'pointer',
        }),
        cellInputCenterOnly: {
            justifyContent: 'center',
            alignItems: 'center',
        },
        cellInputLeftWhenMobile: {
            justifyContent: 'center',
            alignItems: 'center',
            background: 'lightblue',
            [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
                justifyContent: isHalf ? 'flex-start' : '',
                alignItems: isHalf ? 'flex-start' : '',
            },
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
            },
        },
        labelForMobileView: ({ checked, isRTL }) => ({
            margin: isRTL ? '0 10px 2px 0' : '0 0 2px 10px',
            fontWeight: checked ? 'bold' : theme.questionBody.fontWeight ?? null,
            fontFamily: theme.questionBody.fontFamily ?? null,
            fontSize: theme.questionBody.fontSize ?? null,
        }),
        [`@media (min-width: ${theme.breakpoints?.sm.min})`]: {
            labelForMobileView: { display: 'none' },
        },
        [`@media (max-width: ${theme.breakpoints?.lg.max})`]: {
            labelForMobileView: ({ isGridWhenMobile }) => ({
                display: !isGridWhenMobile && isHalf ? 'block' : '',
            }),
        },
    };
});

function RatingInput({ qid: questionId, gridCell, checked = false, isMultipleSelectionsPerRow = false, className, onChange, columnChoices, isGridWhenMobile = false, ...props }) {
    const { isRTL } = useContext(L10NContext);
    const { rowId = '', colId = '', 'aria-labelledby': ariaLabelledBy } = gridCell || {};
    const CheckboxOrRadio = isMultipleSelectionsPerRow
        ? CheckboxInput$1
        : RadioInput$1;
    const inputId = `${questionId}-${rowId}-${colId}`;
    const handleInputChange = (e) => {
        onChange?.({
            id: rowId,
            value: e.target.value,
            checked: e.target.checked,
        });
    };
    const { cellInputWrapperBase, cellInputLeftWhenMobile, cellInputCenterOnly, labelForMobileView, } = useStyles$j({ checked, isGridWhenMobile, isRTL });
    const columnLabel = columnChoices.find((column) => column.id === colId)?.label || '';
    return (React__default.createElement("label", { htmlFor: inputId, "aria-labelledby": ariaLabelledBy, className: classNames(cellInputWrapperBase, { [cellInputCenterOnly]: isGridWhenMobile }, { [cellInputLeftWhenMobile]: !isGridWhenMobile }) },
        React__default.createElement(CheckboxOrRadio, { id: inputId, value: colId, "data-component": true, checked: checked, onChange: handleInputChange, className: className, ...props }),
        !isGridWhenMobile && (React__default.createElement(RichText, { element: "span", className: labelForMobileView, text: columnLabel }))));
}

const useStyles$i = createUseStyles({
    gridComponentStyle: {
        display: 'flex',
        flexShrink: 0,
        width: 20,
        height: 20,
    },
    commentChoiceContainer: {
        width: '100%',
        marginTop: '10px',
    },
    commentColumnContainer: {
        margin: '1em',
    },
    scrollContainer: {
        overflowX: 'auto',
        paddingBottom: '1em',
    },
});

function MatrixRating({ id: questionId, isMultipleSelectionsPerRow = false, choices = [], columnChoices = [], choiceOther, hideLabel, choiceNoneOfTheAbove, required, requiredType, choiceComment, isForcedRanking, onChange, responses: defaultResponses = [], disabled: _disabled, readOnly: _ro, title: titleProps, labelWidth, ...questionFieldProps }) {
    const errorId = createErrorId(questionId);
    /** Filter choices that exist and are visible  */
    const { options, sortableOptions, commentOption, commentColumnOption, columnOptions, } = useQuestionChoices([
        ...choices,
        choiceNoneOfTheAbove
            ? { ...choiceNoneOfTheAbove, type: 'NOTA' }
            : undefined,
        choiceOther,
        choiceComment,
    ], [
        ...columnChoices,
        choiceNoneOfTheAbove
            ? { ...choiceNoneOfTheAbove, type: 'NOTA' }
            : undefined,
    ]);
    const [responseValue, setResponseValue] = useState(defaultResponses);
    /**
     * Default value by id
     * used for text-input based choices (i.e. choiceComment)
     * Matrix provides a 2D Array construct
     */
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value[0]?.value;
    };
    const getDefaultCommentRowValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value?.find((c) => c.id === commentColumnOption?.id)
            ?.value;
    };
    const isChecked = (rowId, colId) => {
        return !!responseValue.find((r) => r.id === rowId &&
            Array.isArray(r.value) &&
            r.value.find((c) => c.id === colId));
    };
    const unCheckOption = (gridCell) => {
        if (required && requiredType === 'ALL') {
            return;
        }
        const result = responseValue
            .map((option) => {
            if (option.id === gridCell.rowId) {
                return {
                    ...option,
                    value: option.value.filter((value) => value.id !== gridCell.colId),
                };
            }
            return option;
        })
            .filter((option) => option.value.length > 0);
        setResponseValue(result);
        onChange?.(result);
    };
    const handleClick = (e, gridCell) => {
        const input = e.target;
        input.focus();
        if (isChecked(gridCell.rowId, gridCell.colId)) {
            unCheckOption(gridCell);
        }
    };
    const handleKeyboard = (e, gridCell) => {
        if (e.code === 'Space') {
            if (isChecked(gridCell.rowId, gridCell.colId)) {
                e.preventDefault();
                unCheckOption(gridCell);
            }
        }
    };
    const handleChange = (e, gridCell) => {
        const { id: rowId, value: colId, checked } = e;
        const isNa = gridCell.colIndex > columnChoices.length;
        const type = isNa ? 'NOTA' : undefined;
        const prevRows = responseValue.filter((r) => r.id !== rowId);
        const currentRow = responseValue.find((r) => r.id === rowId && r.type !== 'NOTA') || {
            id: rowId,
            value: [],
            type,
        };
        let currentCols = [];
        if (Array.isArray(currentRow?.value) && currentRow.value.length > 0) {
            const values = currentRow.value;
            if (isMultipleSelectionsPerRow) {
                currentCols = values.filter((c) => c.value !== colId);
            }
            else {
                currentCols = values.filter((c) => c.type === 'COMMENT_COLUMN');
            }
        }
        if (checked) {
            currentCols.push({ id: colId, value: colId });
        }
        const newRows = currentCols.length
            ? [{ ...currentRow, value: currentCols }]
            : [];
        if (isForcedRanking) {
            // current column finders start
            const currentColsComment = currentCols.length
                ? currentCols.find((v) => v.type === 'COMMENT_COLUMN')
                : null;
            const foundCurrentComment = currentColsComment
                ? currentCols.indexOf(currentColsComment)
                : null;
            const colInputIndex = foundCurrentComment === 0 ? 1 : 0;
            // current column finders end
            // mapping through the rows
            const filteredPreviousRows = prevRows.map(({ id, value }) => {
                const hasComment = value.some((v) => v.type === 'COMMENT_COLUMN');
                if (hasComment) {
                    if (value.length > 1) {
                        const commentCol = value.length
                            ? value.find((v) => v.type === 'COMMENT_COLUMN')
                            : null;
                        const foundPreviousComment = commentCol
                            ? value.indexOf(commentCol)
                            : 0;
                        const inputIndex = foundPreviousComment === 0 ? 1 : 0;
                        const prevRow = currentCols[colInputIndex].value === value[inputIndex].value
                            ? { id, value: [value[foundPreviousComment]] }
                            : { id, value };
                        return prevRow;
                    }
                    return { id, value };
                }
                const filteredRow = currentCols[colInputIndex].value === value[0].value
                    ? { id, value: [] }
                    : { id, value };
                return filteredRow;
            });
            const rowsWithNoValue = filteredPreviousRows.filter((c) => c.value.length === 0);
            if (rowsWithNoValue.length) {
                const emptyRowIndex = filteredPreviousRows.indexOf(rowsWithNoValue[0]);
                filteredPreviousRows.splice(emptyRowIndex, 1);
            }
            const rows = [...filteredPreviousRows, ...newRows];
            const responses = rows;
            setResponseValue(responses);
            onChange?.(responses);
        }
        else {
            const rows = [...prevRows, ...newRows];
            const responses = rows;
            setResponseValue(responses);
            onChange?.(responses);
        }
    };
    const handleCommentChange = (e) => {
        const choiceData = options.find((c) => c.id === e.target.id);
        const responses = responseValue.filter((c) => c.type !== 'COMMENT');
        if (choiceData && !!e.target.value) {
            const { id, type } = choiceData;
            const response = { id, type, value: e.target.value };
            // @note: ensure to loop the values when transforming in RespWeb
            responses.push({
                id,
                type,
                value: [response],
            });
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    const handleCommentColumnChange = (e, associatedRowId) => {
        if (!commentColumnOption) {
            return; // mainly here for type-safety
        }
        const currentRowData = responseValue.find(({ id }) => id === associatedRowId);
        const allButNewValue = currentRowData?.value.filter(({ id }) => id !== commentColumnOption.id) ??
            [];
        const isDelete = !e.target.value &&
            !!currentRowData &&
            allButNewValue.length < currentRowData?.value.length;
        const newValue = isDelete
            ? allButNewValue
            : [
                ...allButNewValue,
                {
                    id: commentColumnOption.id,
                    value: e.target.value,
                    type: 'COMMENT_COLUMN',
                },
            ];
        const responses = responseValue.filter(({ id }) => id !== associatedRowId);
        if (newValue.length > 0) {
            responses.push({
                id: associatedRowId,
                value: newValue,
            });
        }
        setResponseValue(responses);
        onChange?.(responses);
    };
    const { gridComponentStyle, commentChoiceContainer, commentColumnContainer, scrollContainer, } = useStyles$i();
    const questionTitleProps = {
        requiredGroup: !!titleProps.required,
        ...titleProps,
    };
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "MatrixRatingQuestionType", errorId: errorId, title: questionTitleProps, ...questionFieldProps },
        React__default.createElement("div", { className: scrollContainer },
            React__default.createElement(MatrixGrid, { id: questionId, hideLabel: hideLabel, labelWidth: labelWidth, columnDataArray: columnOptions, rowDataArray: sortableOptions, isGridWhenMobile: isForcedRanking, 
                // eslint-disable-next-line react/no-unstable-nested-components
                gridComponent: (gridCell) => (React__default.createElement(RatingInput, { qid: questionId, gridCell: gridCell, isMultipleSelectionsPerRow: isMultipleSelectionsPerRow, checked: isChecked(gridCell.rowId, gridCell.colId), onChange: (e) => handleChange(e, gridCell), name: isMultipleSelectionsPerRow
                        ? undefined
                        : `${gridCell.rowId}-group`, onClick: (e) => {
                        e.stopPropagation();
                        handleClick(e, gridCell);
                    }, onKeyDown: (e) => handleKeyboard(e, gridCell), className: gridComponentStyle, isGridWhenMobile: isForcedRanking, columnChoices: columnOptions })), commentColumnOtherOption: commentColumnOption, commentColumnComponent: commentColumnOption
                    ? // eslint-disable-next-line react/no-unstable-nested-components
                        (commentProps) => (React__default.createElement(CommentChoice, { id: `${commentProps.row.id}-comment`, label: commentColumnOption?.label, onChange: (e) => commentProps.row.associatedId &&
                                handleCommentColumnChange(e, commentProps.row.associatedId), defaultValue: getDefaultCommentRowValueById(commentProps.row.associatedId ?? ''), lineCount: commentColumnOption.linesCount, charCount: commentColumnOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, className: commentColumnContainer, "aria-labelledby": `${commentProps.row.associatedId}-header ${commentProps.row.id}-comment-label` }))
                    : undefined }),
            commentOption && (React__default.createElement(CommentChoice, { id: commentOption.id, label: commentOption.label, onChange: handleCommentChange, defaultValue: getDefaultValueById(commentOption.id), lineCount: commentOption.linesCount, charCount: commentOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, className: commentChoiceContainer })))));
}

const useStyles$h = createUseStyles((theme) => {
    const breakpointSmMin = `@media screen and (max-width: ${theme.breakpoints.sm.min})`;
    const { bgColor: listItemBackground, activeColor: listItemBorderActive, } = theme.input;
    const flexStart = 'flex-start';
    const lineHeight = 1.25;
    const controlsPadding = 6;
    const controlsFontSize = 'inherit';
    // helps align label-first-line to controls center
    const labelOffset = Math.floor(controlsPadding / lineHeight);
    return {
        listItem: {
            position: 'relative',
            userSelect: 'none',
            lineHeight: 0,
            outline: 'none',
            zIndex: ({ ghost }) => (ghost ? 5 : 'auto'),
            '&:active, &:focus, &:focus-within': {
                zIndex: 5,
            },
            '&:active $content, &:focus-within $content': {
                outlineWidth: 1,
                outlineStyle: ({ ghost }) => (ghost ? 'dashed' : 'solid'),
                outlineColor: listItemBorderActive,
            },
            '&:active $content, &:focus $content': {
                outlineWidth: 3,
                outlineStyle: ({ ghost }) => (ghost ? 'dashed' : 'solid'),
                outlineColor: listItemBorderActive,
            },
        },
        rankItemContainer: {
            display: 'flex',
            fontSize: theme.questionBody.fontSize,
            lineHeight,
            color: theme.questionBody.color,
        },
        orderIndex: {
            padding: '16px 5px 10px 5px',
            visibility: ({ ranksVisible }) => (ranksVisible ? 'visible' : 'hidden'),
            minWidth: '45px',
            display: 'flex',
        },
        content: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: flexStart,
            alignItems: flexStart,
            gap: 10,
            flex: '1 auto',
            borderWidth: 1,
            borderRadius: 4,
            borderStyle: 'solid',
            borderColor: 'transparent',
            background: isDark(theme.questionBody.color || '#fff')
                ? listItemBackground
                : 'rgba(255, 255, 255, 0.15)',
            padding: 10,
            cursor: 'move',
            [breakpointSmMin]: {
                flexWrap: 'wrap',
            },
        },
        dragHandle: {
            outline: 'none',
            visibility: ({ ghost }) => (ghost ? 'hidden' : 'visible'),
            fontSize: controlsFontSize,
            lineHeight: 0,
            padding: controlsPadding,
            touchAction: 'none',
            '&::after': {
                content: "''",
                position: 'absolute',
                display: 'block',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },
        },
        label: {
            visibility: ({ ghost }) => (ghost ? 'hidden' : 'visible'),
            display: 'flex',
            justifyContent: flexStart,
            alignItems: flexStart,
            flex: '1 auto',
            paddingTop: labelOffset,
            '& iframe': {
                position: 'relative',
            },
            overflowWrap: 'anywhere',
            [breakpointSmMin]: {
                order: 3,
                paddingLeft: '6px',
                width: '100%',
            },
        },
        controls: {
            visibility: ({ ghost }) => (ghost ? 'hidden' : 'visible'),
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-around',
            gap: 10,
            fontSize: controlsFontSize,
            [breakpointSmMin]: {
                order: 2,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
            },
        },
    };
});

function RankingRow({ className, dragListeners, dragRef, ghost, id, position, label, controls, onFocus, ranksVisible = true, ...props }, ref) {
    const { listItem, rankItemContainer, orderIndex, content: contentContainer, dragHandle: dragHandleContainer, label: labelContainer, controls: controlsContainer, } = useStyles$h({ ghost, ranksVisible });
    const labelId = `${id}-label`;
    const index = position ?? -1;
    const indexLabel = index >= 0 ? index + 1 : '';
    return (React__default.createElement("li", { id: id, className: classNames('rankItem', listItem, className), ref: ref, ...props },
        React__default.createElement("div", { className: rankItemContainer },
            React__default.createElement("span", { className: classNames('OrderIndex', orderIndex), "data-testid": "RankingRow__OrderIndex", "aria-hidden": true }, indexLabel),
            React__default.createElement("div", { className: classNames('rankItem__content', contentContainer) },
                React__default.createElement("div", { "data-testid": "RankingRow__DragHandle", className: classNames('rankItem__dragHandle', dragHandleContainer), onFocus: onFocus, "data-pos": index, ref: dragRef, ...dragListeners },
                    React__default.createElement(DragHandleIcon$1, { className: "rankItem__dragHandleIcon" })),
                React__default.createElement("div", { "data-testid": "RankingRow__Label", className: classNames('rankItem__label', labelContainer) },
                    React__default.createElement(RichText, { id: labelId, element: "div", className: "rankItem__richText", translate: "no", text: label })),
                React__default.createElement("div", { "data-no-dnd": "true", "data-testid": "RankingRow__Controls", className: classNames('rankItem__controls', controlsContainer) }, controls)))));
}
var RankingRow$1 = forwardRef(RankingRow);

/**
 * OrderButton
 *
 * @todo: replace instances with reusable `Button` component that satisfies
 * all scenarios, variants and themes.
 */
const useStyles$g = createUseStyles((theme) => ({
    orderButton: {
        color: '#000',
        backgroundColor: '#fff',
        fontSize: 'inherit',
        border: [1, 'solid', '#000'],
        borderRadius: 4,
        padding: [6, 6],
        maxWidth: '100%',
        cursor: 'pointer',
        lineHeight: 0,
        outlineColor: theme.primaryAccentColor,
        '&[aria-disabled]': {
            backgroundColor: 'rgba(255,255,255,.5)',
            borderColor: 'rgba(0,0,0,.5)',
            pointerEvents: 'none',
            cursor: 'default',
        },
        '&[aria-disabled] > svg': {
            opacity: 0.5,
            cursor: 'default',
        },
        '&:focus, &:focus-visible': {
            borderColor: theme.primaryAccentColor,
            outline: `2px solid ${theme.primaryAccentColor}`,
        },
    },
    '@media (hover: hover) and (pointer: fine)': {
        orderButton: {
            '&:hover': {
                borderColor: theme.primaryAccentColor,
                outline: `2px solid ${theme.primaryAccentColor}`,
            },
        },
    },
    srOnly: visuallyHidden,
}));
function OrderButton({ icon, children: label, className, disabled, ...props }) {
    const { orderButton, srOnly } = useStyles$g();
    return (React__default.createElement("button", { type: "button", "aria-disabled": disabled || undefined, className: classNames('order-button', orderButton, className), ...props },
        icon,
        React__default.createElement("span", { className: srOnly }, label)));
}

const useStyles$f = createUseStyles({
    controlsContainer: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-around',
        gap: 10,
    },
    applicableControl: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
});

const COPY$9 = defineMessages({
    RANK_NA: {
        id: 'OrderControl.NA',
        defaultMessage: 'N/A',
        description: '[Type: label][Vis: high] - not applicable label',
    },
    ARIA_RANK_NA: {
        id: 'OrderControl.AriaNA',
        defaultMessage: 'Not Applicable',
        description: '[Type: label][Vis: high] - accessible not applicable label',
    },
});
const RANK_DIRECTION_BACKWARD = -1;
const RANK_DIRECTION_FORWARD = 1;
function OrderControl({ id, labels, position, showApplicability = true, applicable: defaultApplicable = true, className, onApplicableChange, onOrderUp, onOrderDown, disableOrderUp = false, disableOrderDown = false, ...props }) {
    const [applicable, setApplicable] = useState(defaultApplicable);
    const currentIndex = position ?? -1;
    const handleOrderUp = (_e) => {
        onOrderUp?.({
            id,
            value: currentIndex,
            direction: RANK_DIRECTION_BACKWARD,
        });
    };
    const handleOrderDown = (_e) => {
        onOrderDown?.({
            id,
            value: currentIndex,
            direction: RANK_DIRECTION_FORWARD,
        });
    };
    const handleApplicableChange = (e) => {
        const isApplicable = !e.target.checked;
        onApplicableChange?.({
            id,
            applicable: isApplicable,
        });
        setApplicable(isApplicable);
    };
    const applicableId = `${id}-applicable`;
    const { controlsContainer, applicableControl } = useStyles$f();
    return (React__default.createElement("div", { id: id, className: classNames(controlsContainer, className), ...props },
        React__default.createElement(OrderButton, { disabled: disableOrderUp || !applicable, onClick: handleOrderUp, icon: React__default.createElement(CaretUpOutlineIcon, null) }, labels.orderUpLabel),
        React__default.createElement(OrderButton, { disabled: disableOrderDown || !applicable, onClick: handleOrderDown, icon: React__default.createElement(CaretDownOutlineIcon, null) }, labels.orderDownLabel),
        showApplicability && (React__default.createElement("label", { className: classNames('order-na', applicableControl), htmlFor: applicableId, "aria-label": t(COPY$9.ARIA_RANK_NA) },
            React__default.createElement(CheckboxInput$1, { id: applicableId, checked: !applicable, onChange: handleApplicableChange }),
            React__default.createElement(T, { desc: COPY$9.RANK_NA })))));
}

const useStyles$e = createUseStyles({
    item: {},
    itemDisabled: {
        '& .rankItem__dragHandle::after': {
            cursor: 'default',
        },
        '& .rankItem__dragHandle, & .rankItem__label': {
            opacity: 0.5,
        },
    },
});

const COPY$8 = defineMessages({
    ARIA_RANK_MOVE_UP: {
        id: 'RankingItem.AriaRankMoveUp',
        defaultMessage: 'Move up {label}',
        description: '[Type: button][Vis: high] - accessible rank move up label',
    },
    ARIA_RANK_MOVE_DOWN: {
        id: 'RankingItem.AriaRankMoveDown',
        defaultMessage: 'Move down {label}',
        description: '[Type: button][Vis: high] - accessible rank move down label',
    },
});
function RankingItem({ id, className, label, position: index, listLength, applicable, showApplicability, onApplicableChange, onOrderUp, onOrderDown, ranksVisible, ...props }) {
    const textOnlyLabel = sanitizeString(label);
    const controlLabels = {
        orderUpLabel: t(COPY$8.ARIA_RANK_MOVE_UP, { label: textOnlyLabel }),
        orderDownLabel: t(COPY$8.ARIA_RANK_MOVE_DOWN, { label: textOnlyLabel }),
    };
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, setActivatorNodeRef, } = useSortable({
        id,
        /*
         * role=listitem is forced to override DnD-kits use of role=button.
         * This re-instates proper a11y announcements of the role
         */
        attributes: { role: 'listitem', tabIndex: -1 },
        disabled: !applicable,
        data: {
            id,
            position: index,
            label,
            applicable,
            showApplicability,
        },
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const handleOrderUp = (e) => {
        onOrderUp?.({ ...e, id });
    };
    const handleOrderDown = (e) => {
        onOrderDown?.({ ...e, id });
    };
    const handleApplicableChange = (e) => {
        onApplicableChange?.({ ...e, id });
    };
    const { item, itemDisabled } = useStyles$e({ applicable });
    return (React__default.createElement(RankingRow$1, { id: id, label: label, position: index, className: classNames(item, { [itemDisabled]: !applicable }, className), ref: setNodeRef, style: style, ghost: isDragging, ...attributes, dragListeners: listeners, dragRef: setActivatorNodeRef, ranksVisible: ranksVisible, controls: React__default.createElement(OrderControl, { id: `${id}-control`, position: index, applicable: applicable, showApplicability: !!showApplicability, onApplicableChange: handleApplicableChange, onOrderUp: handleOrderUp, onOrderDown: handleOrderDown, labels: controlLabels, disableOrderUp: index === 0, disableOrderDown: index === listLength - 1 }), ...props }));
}

const useRankingIds = ({ rankings, choiceNotApplicable, }) => {
    return useMemo(() => {
        const order = rankings
            .filter((r) => !!r && r.visible)
            .sort((a, b) => Number(a.label) - Number(b.label))
            .map((r) => r.id);
        if (choiceNotApplicable) {
            order.push(choiceNotApplicable.id);
        }
        return order;
    }, [rankings, choiceNotApplicable]);
};
const useRankingState = ({ choices, rankingIds, defaultResponses = [], choiceNotApplicable, }) => {
    const findResponseByChoiceId = (id) => defaultResponses.find((r) => r.id === id);
    const findPositionByRankId = (id) => rankingIds.findIndex((rid) => rid === id);
    return useState(() => {
        // Filter choices that exist and are visible, add rankable meta data
        return choices
            .filter((c) => !!c && c.visible)
            .map(({ id, label }, defaultIndex) => {
            const { value: rankId = rankingIds[defaultIndex], type } = findResponseByChoiceId(id) || {};
            const item = {
                id,
                label,
                type,
                value: rankId,
                applicable: rankId !== choiceNotApplicable?.id,
                index: findPositionByRankId(rankId),
            };
            return item;
        })
            .sort((a, b) => a.index - b.index);
    });
};

/* istanbul ignore file */
function shouldHandleEvent(element) {
    let cur = element;
    while (cur) {
        if (cur.dataset && cur.dataset.noDnd) {
            return false;
        }
        cur = cur.parentElement;
    }
    return true;
}
const defaultKeyboardCodes = {
    start: [KeyboardCode.Space, KeyboardCode.Enter],
    cancel: [KeyboardCode.Esc],
    end: [KeyboardCode.Space, KeyboardCode.Enter],
};
class PointerSensor extends PointerSensor$1 {
}
PointerSensor.activators = [
    {
        eventName: 'onPointerDown',
        handler: ({ nativeEvent: event }, { onActivation }) => {
            if (!shouldHandleEvent(event.target)) {
                return false;
            }
            onActivation?.({ event });
            return true;
        },
    },
];
class KeyboardSensor extends KeyboardSensor$1 {
}
KeyboardSensor.activators = [
    {
        eventName: 'onKeyDown',
        handler: ({ nativeEvent: event }, { keyboardCodes = defaultKeyboardCodes, onActivation }, { active }) => {
            const { code } = event;
            if (keyboardCodes.start.includes(code)) {
                const activator = active.activatorNode.current;
                if ((activator && event.target !== activator) ||
                    !shouldHandleEvent(event.target)) {
                    return false;
                }
                event.preventDefault();
                onActivation?.({ event });
                return true;
            }
            return false;
        },
    },
];

const useStyles$d = createUseStyles({
    list: {
        '& > *': {
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
        },
    },
    listItem: {
        marginBottom: 10,
    },
    dragOverlay: {
        opacity: 0.5,
        '& .rankItem__orderIndex, & .rankItem__controls': {
            opacity: 0,
        },
    },
    srOnly: { ...visuallyHidden },
});

const COPY$7 = defineMessages({
    ARIA_RANK_MOVED_UP: {
        id: 'Ranking.AriaRankMovedUp',
        defaultMessage: '{label} moved up to number {position}',
        description: '[Type: button][Vis: high] - accessible ranking moved up label',
    },
    ARIA_RANK_MOVED_DOWN: {
        id: 'Ranking.AriaRankMovedDown',
        defaultMessage: '{label} moved down to number {position}',
        description: '[Type: button][Vis: high] - accessible ranking moved down label',
    },
    ARIA_RANK_MOVED_TO_TOP: {
        id: 'Ranking.AriaRankAtTop',
        defaultMessage: '{label} moved to the top of the list',
        description: '[Type: button][Vis: high] - accessible ranking at top of list',
    },
    ARIA_RANK_MOVED_TO_BOTTOM: {
        id: 'Ranking.AriaRankAtBottom',
        defaultMessage: '{label} moved to the bottom of the list',
        description: '[Type: button][Vis: high] - accessible ranking at bottom of list',
    },
});
/** Turn off a11y announcements for Drag and Drop in the DnDContext - this is handled by the order buttons */
const emptyStringNoop = () => '';
const dndAnnouncements = {
    onDragStart: emptyStringNoop,
    onDragMove: emptyStringNoop,
    onDragOver: emptyStringNoop,
    onDragEnd: emptyStringNoop,
    onDragCancel: emptyStringNoop,
};
const dndAccessibility = {
    announcements: dndAnnouncements,
    screenReaderInstructions: {
        draggable: '',
    },
};
function Ranking({ id: questionId, disabled: _dis, readOnly: _ro, choices = /* istanbul ignore next: cannot reach default branch */ [], rankings = /* istanbul ignore next: cannot reach default branch */ [], choiceNotApplicable, responses: defaultResponses = [], onChange, hideRanks = false, ...fieldProps }) {
    /**
     * @author <Keisha Mukasa>[kmukasa@surveymonkey.com]
     * @description The id has been modified to work in __hybrid__ mode. The `id` needs to
     * be different from the question-field `id` normally created/used.
     * @see https://github.com/mntv-authoring-transformation/smquestions/pull/1204
     * @todo remove prefix 'hybrid-' when no longer required
     */
    const fieldId = `hybrid-${questionId}`;
    /**
     * @description `createLegendId` is used internally by [QuestionFieldLayoutTemplate](https://github.com/mntv-authoring-transformation/smquestions/blob/main/packages/question-ui/src/capabilities/respondent-survey/components/QuestionFieldLayoutTemplate/QuestionFieldLayoutTemplate.tsx#L157)
     * to create an id.  This `id` cannot be passed down via props.  It is recreated here for `ARIA` referencing.
     */
    const questionTitleId = createLegendId(fieldId);
    const errorId = createErrorId(questionId);
    const sensors = useSensors(useSensor(PointerSensor));
    // toggles display of N/A choice
    const showApplicability = choiceNotApplicable !== undefined;
    // create ranking order (N/A choice is last)
    const rankingIds = useRankingIds({ rankings, choiceNotApplicable });
    const [, setActiveId] = useState(null);
    const [activeItem, setActiveItem] = useState(null);
    // for Aria-Live
    const [statusMessage, announceStatus] = useState('');
    const setAnnoucement = (str) => {
        setTimeout(() => announceStatus(''), 5000);
        announceStatus(str);
    };
    const [ranksVisible, setRanksVisible] = useState(hideRanks);
    const [listItems, setListItems] = useRankingState({
        choices,
        defaultResponses,
        choiceNotApplicable,
        rankingIds,
    });
    /* istanbul ignore next */
    const getItemIndices = (items, fromId, toId) => {
        const fromIndex = items.findIndex((item) => item.id === fromId);
        const toIndex = items.findIndex((item) => item.id === toId);
        return { fromIndex, toIndex };
    };
    /* istanbul ignore next */
    const moveById = (items, fromId, toId) => {
        const { fromIndex, toIndex } = getItemIndices(items, fromId, toId);
        return arrayMove(items, fromIndex, toIndex);
    };
    /* istanbul ignore next: cannot test drag-and-drop logic */
    const moveItem = (fromId, toId) => {
        setListItems((items) => moveById(items, fromId, toId));
    };
    const { list, listItem, dragOverlay, srOnly } = useStyles$d();
    const orderedListProps = {
        /**
         * role is forced for a11y, so SR's will announce properly,
         * this is somehow lost or manipulated, possibly, by DnD-Kit
         */
        role: 'list',
        className: list,
        'aria-labelledby': questionTitleId,
    };
    const updateRankingOrder = (current, next) => {
        const nextValue = Math.max(0, Math.min(next, listItems.length - 1));
        const responses = arrayMove(listItems, current, nextValue).map((response, index) => ({
            ...response,
            index,
            value: rankingIds[index],
        }));
        setRanksVisible(true);
        setListItems(responses);
        onChange?.(responses);
    };
    const announcePosition = (current, next, label) => {
        const nextPos = Math.max(0, Math.min(next, listItems.length - 1));
        if (current === nextPos)
            return; // no change in position
        const position = nextPos + 1; // announce non-zero base number
        let message = COPY$7.ARIA_RANK_MOVED_DOWN;
        if (nextPos <= 0) {
            // moved to top
            message = COPY$7.ARIA_RANK_MOVED_TO_TOP;
        }
        else if (nextPos < current) {
            // moved up
            message = COPY$7.ARIA_RANK_MOVED_UP;
        }
        else if (nextPos >= listItems.length - 1) {
            // moved to bottom
            message = COPY$7.ARIA_RANK_MOVED_TO_BOTTOM;
        }
        setAnnoucement(t(message, { label: sanitizeString(label), position }));
    };
    const handleOrderUp = (e) => {
        const { id: rowId, value: currentPosition, direction = /* istanbul ignore next: cannot reach default branch */ 0, } = e;
        const item = listItems.find((li) => li.id === rowId);
        const nextPosition = currentPosition + direction;
        updateRankingOrder(currentPosition, nextPosition);
        announcePosition(currentPosition, nextPosition, item.label);
    };
    const handleOrderDown = (e) => {
        const { id: rowId, value: currentPosition, direction = /* istanbul ignore next: cannot reach default branch */ 0, } = e;
        const item = listItems.find((li) => li.id === rowId);
        const nextPosition = currentPosition + direction;
        updateRankingOrder(currentPosition, nextPosition);
        announcePosition(currentPosition, nextPosition, item.label);
    };
    const handleApplicableChange = (e) => {
        /* istanbul ignore else: should never branch */
        if (showApplicability) {
            const { id, applicable } = e;
            const responses = listItems.map((item) => ({
                ...item,
                ...(item.id === id ? { applicable } : {}),
                ...(item.id === id && !applicable
                    ? { value: choiceNotApplicable.id }
                    : {}),
            }));
            setListItems(responses);
            onChange?.(responses);
        }
    };
    /* istanbul ignore next: cannot test drag-and-drop logic */
    const handleDragStart = ({ active }) => {
        setActiveId(active.id);
        setActiveItem(active);
    };
    /* istanbul ignore next: cannot test drag-and-drop logic */
    const handleDragEnd = ({ active, over }) => {
        if (active.id !== over?.id) {
            moveItem(active.id, over?.id);
        }
        setRanksVisible(true);
        setActiveId(null);
        setActiveItem(null);
    };
    return (React__default.createElement(QuestionField, { id: `hybrid-${questionId}`, errorId: errorId, "data-testid": "RankingQuestionType", ...fieldProps },
        React__default.createElement("div", { role: "status", "aria-live": "assertive", "aria-atomic": "true", className: srOnly }, statusMessage),
        React__default.createElement(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragEnd: handleDragEnd, accessibility: dndAccessibility },
            React__default.createElement(SortableContext, { items: listItems, strategy: verticalListSortingStrategy },
                React__default.createElement("ol", { ...orderedListProps }, listItems.map(({ id: itemId, label, applicable }, index) => {
                    return (React__default.createElement(RankingItem, { id: itemId, key: itemId, label: label, position: index, listLength: listItems.length, applicable: applicable, showApplicability: !!showApplicability, onApplicableChange: handleApplicableChange, onOrderUp: handleOrderUp, onOrderDown: handleOrderDown, className: listItem, ranksVisible: ranksVisible }));
                })),
                React__default.createElement(DragOverlay, null, 
                /* istanbul ignore next: cannot test drag-and-drop logic */ activeItem ? (React__default.createElement(RankingRow$1, { id: activeItem.id, className: dragOverlay, label: activeItem.data.current?.label, position: activeItem.data.current?.index, ranksVisible: ranksVisible })) : null)))));
}

function SingleTextbox({ id: questionId, required: _req, disabled, readOnly, responses: defaultResponses = [], onChange, size, ...fieldProps }) {
    const { error, errorId = createErrorId(questionId), title: { id: titleId }, } = fieldProps;
    const defaultValue = useMemo(() => defaultResponses.find((r) => !!r.value)?.value ?? '', 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const handleChange = (e) => {
        const responses = e.target.value
            ? [{ id: questionId, value: e.target.value }]
            : [];
        onChange?.(responses);
    };
    return (React__default.createElement("div", null,
        React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "SingleTextboxQuestionType", ...fieldProps },
            React__default.createElement(TextInput$1, { name: questionId, defaultValue: defaultValue, disabled: disabled, readOnly: readOnly, size: size, onChange: handleChange, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS }))));
}
var SingleTextbox$1 = withErrorBoundary(SingleTextbox);

const useStyles$c = createUseStyles((theme) => {
    return {
        textPresentationalStyles: () => ({
            fontFamily: theme.questionTitle.fontFamily ?? 'inherit',
            fontSize: theme.questionBody.fontSize ?? '16px',
            fontWeight: theme.questionTitle.fontWeight ?? (theme.isAccessible ? 500 : 300),
            textDecoration: theme.questionTitle.textDecoration ?? 'inherit',
            fontStyle: theme.questionTitle.fontStyle ?? 'inherit',
            color: theme.answerColor ?? 'inherit',
        }),
        buttonStyles: {
            marginTop: '20px',
        },
    };
});

function TextPresentational({ text, padding = { top: 0, bottom: 0, left: 0, right: 0 }, id, okButton = { visible: false }, }) {
    const richTextId = `text-presentational-${id}`;
    const { textPresentationalStyles, buttonStyles } = useStyles$c();
    const { visible: showButton, ...buttonProps } = okButton;
    return (React__default.createElement(QuestionSpacing, { padding: padding, "data-testid": "TextPresentational" },
        React__default.createElement(RichText, { id: richTextId, element: "div", text: text, className: textPresentationalStyles }),
        showButton && React__default.createElement(Button, { ...buttonProps, className: buttonStyles })));
}
var TextPresentational$1 = withErrorBoundary(TextPresentational);

const useStyles$b = createUseStyles((theme) => {
    const { fontFamily = 'inherit', color = 'inherit', fontSize = 'inherit' } = theme.questionBody ?? {};
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    // for partial theme layout the breakpoint to switch the layout is larger than full theme
    const layoutBreakpoint = theme.layout.includes('PARTIAL')
        ? theme.breakpoints.sm.max
        : theme.breakpoints.md.max;
    return {
        rowBlock: {
            display: 'flex',
            width: '100%',
            [`@media (min-width: ${layoutBreakpoint})`]: {
                width: '33.3% !important',
            },
            flexDirection: 'column',
            float: 'left',
            padding: '0px 14px 8px 0px',
        },
        nameBlock: {
            width: '100%',
            [`@media (min-width: ${layoutBreakpoint})`]: {
                width: '33.3% !important',
                marginRight: '66.6%',
            },
            display: 'flex',
            flexDirection: 'column',
            padding: '0px 14px 8px 0px',
        },
        nameLabel: {
            paddingBottom: '8px',
            fontFamily,
            fontSize,
            color,
        },
        nameInput: {
            border: `1px solid ${theme.answerColor}`,
            height: '40px',
            marginBottom: '8px',
            width: '100%',
            fontSize,
            [breakpointMedium]: {
                fontSize: '16px',
            },
            fontFamily,
            '&::placeholder': {
                fontWeight: fontWeightOptions.regular,
                fontFamily,
                fontSize,
                color: '#999',
            },
        },
        container: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
        },
        requiredAsterisk: ({ isRTL }) => ({
            display: 'inline-block',
            margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0',
        }),
    };
});

function NameField({ id: questionId, required, disabled, readOnly, responses: defaultResponses = [], onChange, size = 20, answerOptions = [], ...fieldProps }) {
    const { error, errorId = createErrorId(questionId), title: { id: titleId }, } = fieldProps;
    const { isRTL } = useContext(L10NContext);
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value;
    };
    const handleChange = (e) => {
        const responses = e.target.value
            ? [{ id: questionId, value: e.target.value }]
            : [];
        onChange?.(responses);
    };
    const { nameLabel, nameInput, nameBlock, rowBlock, container, requiredAsterisk, } = useStyles$b({ isRTL });
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, ...fieldProps },
        React__default.createElement("div", { className: container }, answerOptions.map((option) => option?.visible && (React__default.createElement("div", { key: option.id, className: classNames(option.subtype.includes('name') ? rowBlock : nameBlock) },
            React__default.createElement("label", { className: nameLabel, htmlFor: `${questionId}_${option.id}` },
                option.required && (React__default.createElement("span", { "aria-hidden": true, className: requiredAsterisk }, "*")),
                React__default.createElement(RichText, { element: "span", text: option.label })),
            React__default.createElement(TextInput$1, { id: `${questionId}_${option.id}`, className: nameInput, name: `${questionId}_${option.id}`, defaultValue: getDefaultValueById(option.id), required: option?.required && required, disabled: option?.disabled && disabled, readOnly: option?.readOnly && readOnly, placeholder: option.placeholder ?? '', size: size, onChange: handleChange, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS })))))));
}
var NameField$1 = withErrorBoundary(NameField);

const useStyles$a = createUseStyles((theme) => {
    return {
        passwordFieldContainer: {
            gap: '8px',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
        },
        passwordLabel: {
            fontFamily: theme.questionBody?.fontFamily ?? 'inherit',
            fontSize: theme.questionBody?.fontSize ?? 'inherit',
            color: theme.questionBody?.color ?? 'inherit',
        },
    };
});

function PasswordField({ label, onChange }) {
    const inputId = 'password-input';
    const { passwordFieldContainer, passwordLabel } = useStyles$a({
        size: 20,
    });
    const handleChange = (e) => {
        onChange?.(e.target.value);
    };
    return (React__default.createElement("div", { className: passwordFieldContainer },
        React__default.createElement("label", { className: passwordLabel, id: inputId }, label),
        React__default.createElement(TextInput$1, { size: 20, type: "password", autoHeight: true, onChange: handleChange, "aria-labelledby": inputId })));
}

function getSelectedOption(options, value) {
    let selectedOption = { divider: false, label: '', value: '' };
    options.forEach((option) => {
        if (!option.divider && option.value === value) {
            selectedOption = option;
        }
    });
    return selectedOption;
}
const DIVIDER_STYLE = {
    fontSize: '1px',
    backgroundColor: 'currentColor',
    color: 'inherit',
};
function CountrySelect({ value, onChange, options, ...rest }) {
    const onChangeSelect = useCallback((event) => {
        const v = event.target.value;
        onChange(v === 'ZZ' ? undefined : v);
    }, [onChange]);
    // "ZZ" means "International".
    // (HTML requires each `<option/>` have some string `value`).
    return (React__default.createElement("select", { value: value || 'ZZ', onChange: onChangeSelect, ...rest }, options.map(({ value: val, label, divider }) => (React__default.createElement("option", { key: divider ? '|' : val || 'ZZ', value: divider ? '|' : val || 'ZZ', style: divider ? DIVIDER_STYLE : undefined, "calling-code": getCountryCallingCode(val) }, label)))));
}
function CountrySelectWithIcon({ value, options, className, iconComponent: Icon, ...rest }) {
    const selectedOption = useMemo(() => {
        return getSelectedOption(options, value);
    }, [options, value]);
    return (React__default.createElement("div", { className: "PhoneInputCountry" },
        React__default.createElement(CountrySelect, { ...rest, value: value, options: options, className: classNames('PhoneInputCountrySelect', className) }),
        React__default.createElement(Icon, { "aria-hidden": true, country: value, label: selectedOption && selectedOption.label }),
        React__default.createElement("div", { className: "PhoneInputCountrySelectArrow" })));
}

var ar = {
	"AddressField.selectCountry": "حدد دولة",
	"PhoneField.countryCode": "رمز الدولة",
	"countriesL10nMessages.AC": "جزيرة أسينشين",
	"countriesL10nMessages.AD": "أندورا",
	"countriesL10nMessages.AE": "الإمارات العربية المتحدة",
	"countriesL10nMessages.AF": "أفغانستان",
	"countriesL10nMessages.AG": "أنتيغوا وباربودا",
	"countriesL10nMessages.AI": "أنجويلا",
	"countriesL10nMessages.AL": "ألبانيا",
	"countriesL10nMessages.AM": "أرمينيا",
	"countriesL10nMessages.AO": "أنجولا",
	"countriesL10nMessages.AR": "الأرجنتين",
	"countriesL10nMessages.AS": "ساموا\nالأمريكية",
	"countriesL10nMessages.AT": "النمسا",
	"countriesL10nMessages.AU": "أستراليا",
	"countriesL10nMessages.AW": "أروبا",
	"countriesL10nMessages.AX": "جزر أولاند",
	"countriesL10nMessages.AZ": "أذربيجان",
	"countriesL10nMessages.BA": "البوسنة والهرسك",
	"countriesL10nMessages.BB": "بربادوس",
	"countriesL10nMessages.BD": "بنجلاديش",
	"countriesL10nMessages.BE": "بلجيكا",
	"countriesL10nMessages.BF": "بوركينا فاسو",
	"countriesL10nMessages.BG": "بلغاريا",
	"countriesL10nMessages.BH": "البحرين",
	"countriesL10nMessages.BI": "بوروندي",
	"countriesL10nMessages.BJ": "بنين",
	"countriesL10nMessages.BL": "سان بارتليمي",
	"countriesL10nMessages.BM": "برمودا",
	"countriesL10nMessages.BN": "بروناي دار السلام",
	"countriesL10nMessages.BO": "بوليفيا",
	"countriesL10nMessages.BQ": "جزر بونير و سينت أوستاتيوس و سابا",
	"countriesL10nMessages.BR": "البرازيل",
	"countriesL10nMessages.BS": "جزر البهاما",
	"countriesL10nMessages.BT": "بوتان",
	"countriesL10nMessages.BW": "بوتسوانا",
	"countriesL10nMessages.BZ": "بليز",
	"countriesL10nMessages.CA": "كندا",
	"countriesL10nMessages.CC": "جزر كوكوس (كيلينغ)",
	"countriesL10nMessages.CD": "الكونغو، جمهورية الكونغو الديمقراطية",
	"countriesL10nMessages.CF": "جمهورية افريقيا الوسطى",
	"countriesL10nMessages.CG": "الكونغو",
	"countriesL10nMessages.CH": "سويسرا",
	"countriesL10nMessages.CI": "كوت ديفوار",
	"countriesL10nMessages.CK": "جزر كوك",
	"countriesL10nMessages.CL": "شيلي",
	"countriesL10nMessages.CM": "الكاميرون",
	"countriesL10nMessages.CN": "الصين",
	"countriesL10nMessages.CO": "كولومبيا",
	"countriesL10nMessages.CR": "كوستاريكا",
	"countriesL10nMessages.CV": "الرأس الأخضر",
	"countriesL10nMessages.CW": "كوراساو",
	"countriesL10nMessages.CX": "جزيرة كريسماس",
	"countriesL10nMessages.CY": "قبرص",
	"countriesL10nMessages.CZ": "جمهورية التشيك",
	"countriesL10nMessages.DE": "ألمانيا",
	"countriesL10nMessages.DJ": "جيبوتي",
	"countriesL10nMessages.DK": "الدنمارك",
	"countriesL10nMessages.DM": "دومينيكا",
	"countriesL10nMessages.DO": "جمهورية الدومينيكان",
	"countriesL10nMessages.DZ": "الجزائر",
	"countriesL10nMessages.EC": "الاكوادور",
	"countriesL10nMessages.EE": "إستونيا",
	"countriesL10nMessages.EG": "مصر",
	"countriesL10nMessages.EH": "الصحراء الغربية",
	"countriesL10nMessages.ER": "إريتريا",
	"countriesL10nMessages.ES": "إسبانيا",
	"countriesL10nMessages.ET": "أثيوبيا",
	"countriesL10nMessages.FI": "فنلندا",
	"countriesL10nMessages.FJ": "فيجي",
	"countriesL10nMessages.FK": "جزر فوكلاند",
	"countriesL10nMessages.FM": "ولايات مايكرونزيا المتحدة",
	"countriesL10nMessages.FO": "جزر فارو",
	"countriesL10nMessages.FR": "فرنسا",
	"countriesL10nMessages.GA": "الجابون",
	"countriesL10nMessages.GB": "المملكة المتحدة",
	"countriesL10nMessages.GD": "غرينادا",
	"countriesL10nMessages.GE": "جورجيا",
	"countriesL10nMessages.GF": "غيانا الفرنسية",
	"countriesL10nMessages.GG": "غيرنزي",
	"countriesL10nMessages.GH": "غانا",
	"countriesL10nMessages.GI": "جبل طارق",
	"countriesL10nMessages.GL": "جرينلاند",
	"countriesL10nMessages.GM": "جامبيا",
	"countriesL10nMessages.GN": "غينيا",
	"countriesL10nMessages.GP": "غوادلوب",
	"countriesL10nMessages.GQ": "غينيا\nالإستوائية",
	"countriesL10nMessages.GR": "اليونان",
	"countriesL10nMessages.GT": "غواتيمالا",
	"countriesL10nMessages.GU": "غوام",
	"countriesL10nMessages.GW": "غينيا بيساو",
	"countriesL10nMessages.GY": "غيانا",
	"countriesL10nMessages.HK": "هونج كونج",
	"countriesL10nMessages.HN": "هندوراس",
	"countriesL10nMessages.HR": "كرواتيا",
	"countriesL10nMessages.HT": "هايتي",
	"countriesL10nMessages.HU": "هنغاريا",
	"countriesL10nMessages.ID": "إندونيسيا",
	"countriesL10nMessages.IE": "أيرلندا",
	"countriesL10nMessages.IL": "إسرائيل",
	"countriesL10nMessages.IM": "جزيرة مان",
	"countriesL10nMessages.IN": "الهند",
	"countriesL10nMessages.IO": "إقليم المحيط البريطاني الهندي",
	"countriesL10nMessages.IQ": "العراق",
	"countriesL10nMessages.IS": "أيسلندا",
	"countriesL10nMessages.IT": "إيطاليا",
	"countriesL10nMessages.JE": "جيرسي",
	"countriesL10nMessages.JM": "جامايكا",
	"countriesL10nMessages.JO": "الأردن",
	"countriesL10nMessages.JP": "اليابان",
	"countriesL10nMessages.KE": "كينيا",
	"countriesL10nMessages.KG": "قيرغيزستان",
	"countriesL10nMessages.KH": "كمبوديا",
	"countriesL10nMessages.KI": "كيريباتي",
	"countriesL10nMessages.KM": "جزر القمر",
	"countriesL10nMessages.KN": "سانت كيتس ونيفس",
	"countriesL10nMessages.KR": "كوريا الجنوبية",
	"countriesL10nMessages.KW": "الكويت",
	"countriesL10nMessages.KY": "جزر كايمان",
	"countriesL10nMessages.KZ": "كازاخستان",
	"countriesL10nMessages.LA": "لاوس",
	"countriesL10nMessages.LB": "لبنان",
	"countriesL10nMessages.LC": "سانت لوسيا",
	"countriesL10nMessages.LI": "ليختنشتاين",
	"countriesL10nMessages.LK": "سيريلانكا",
	"countriesL10nMessages.LR": "ليبيريا",
	"countriesL10nMessages.LS": "ليسوتو",
	"countriesL10nMessages.LT": "ليتوانيا",
	"countriesL10nMessages.LU": "لوكسمبورغ",
	"countriesL10nMessages.LV": "لاتفيا",
	"countriesL10nMessages.LY": "ليبيا",
	"countriesL10nMessages.MA": "المغرب",
	"countriesL10nMessages.MC": "موناكو",
	"countriesL10nMessages.MD": "مولدوفا",
	"countriesL10nMessages.ME": "الجبل الأسود",
	"countriesL10nMessages.MF": "سانت مارتن (الجزء الفرنسي)",
	"countriesL10nMessages.MG": "مدغشقر",
	"countriesL10nMessages.MH": "جزر مارشال",
	"countriesL10nMessages.MK": "مقدونيا",
	"countriesL10nMessages.ML": "مالي",
	"countriesL10nMessages.MM": "ميانمار",
	"countriesL10nMessages.MN": "منغوليا",
	"countriesL10nMessages.MO": "ماكاو",
	"countriesL10nMessages.MP": "جزيرة ماريانا الشمالية",
	"countriesL10nMessages.MQ": "مارتينيك",
	"countriesL10nMessages.MR": "موريتانيا",
	"countriesL10nMessages.MS": "مونتسرات",
	"countriesL10nMessages.MT": "مالطا",
	"countriesL10nMessages.MU": "موريشيوس",
	"countriesL10nMessages.MV": "جزر المالديف",
	"countriesL10nMessages.MW": "ملاوي",
	"countriesL10nMessages.MX": "المكسيك",
	"countriesL10nMessages.MY": "ماليزيا",
	"countriesL10nMessages.MZ": "موزمبيق",
	"countriesL10nMessages.NA": "ناميبيا",
	"countriesL10nMessages.NC": "كاليدونيا الجديدة",
	"countriesL10nMessages.NE": "النيجر",
	"countriesL10nMessages.NF": "جزيرة نورفولك",
	"countriesL10nMessages.NG": "نيجيريا",
	"countriesL10nMessages.NI": "نيكاراغوا",
	"countriesL10nMessages.NL": "هولندا",
	"countriesL10nMessages.NO": "النرويج",
	"countriesL10nMessages.NP": "نيبال",
	"countriesL10nMessages.NR": "ناورو",
	"countriesL10nMessages.NU": "نيوي",
	"countriesL10nMessages.NZ": "نيوزيلندا",
	"countriesL10nMessages.OM": "سلطنة عمان",
	"countriesL10nMessages.PA": "بنما",
	"countriesL10nMessages.PE": "بيرو",
	"countriesL10nMessages.PF": "بولينيزيا الفرنسية",
	"countriesL10nMessages.PG": "بابوا غينيا الجديدة",
	"countriesL10nMessages.PH": "الفلبين",
	"countriesL10nMessages.PK": "باكستان",
	"countriesL10nMessages.PL": "بولندا",
	"countriesL10nMessages.PM": "سانت بيير وميكلون",
	"countriesL10nMessages.PR": "بورتوريكو",
	"countriesL10nMessages.PS": "فلسطين",
	"countriesL10nMessages.PT": "البرتغال",
	"countriesL10nMessages.PW": "بالاو",
	"countriesL10nMessages.PY": "باراجواي",
	"countriesL10nMessages.QA": "قطر",
	"countriesL10nMessages.RE": "ريونيون",
	"countriesL10nMessages.RO": "رومانيا",
	"countriesL10nMessages.RS": "صربيا",
	"countriesL10nMessages.RW": "رواندا",
	"countriesL10nMessages.SA": "المملكة العربية السعودية",
	"countriesL10nMessages.SB": "جزر سليمان",
	"countriesL10nMessages.SC": "سيشيل",
	"countriesL10nMessages.SE": "السويد",
	"countriesL10nMessages.SG": "سنغافورة",
	"countriesL10nMessages.SH": "سانت هيلينا",
	"countriesL10nMessages.SI": "سلوفينيا",
	"countriesL10nMessages.SJ": "سفالبارد ويان ماين",
	"countriesL10nMessages.SK": "سلوفاكيا",
	"countriesL10nMessages.SL": "سيراليون",
	"countriesL10nMessages.SM": "سان مارينو",
	"countriesL10nMessages.SN": "السنغال",
	"countriesL10nMessages.SO": "الصومال",
	"countriesL10nMessages.SR": "سورينام",
	"countriesL10nMessages.SS": "جنوب السودان",
	"countriesL10nMessages.ST": "ساو تومي وبرينسيبي",
	"countriesL10nMessages.SV": "السلفادور",
	"countriesL10nMessages.SX": "سينت مارتن",
	"countriesL10nMessages.SZ": "سوازيلاند",
	"countriesL10nMessages.TA": "تريستان دا كونا",
	"countriesL10nMessages.TC": "جزر تركس وكايكوس",
	"countriesL10nMessages.TD": "تشاد",
	"countriesL10nMessages.TG": "توجو",
	"countriesL10nMessages.TH": "تايلاند",
	"countriesL10nMessages.TJ": "طاجيكستان",
	"countriesL10nMessages.TK": "توكيلاو",
	"countriesL10nMessages.TL": "تيمور الشرقية",
	"countriesL10nMessages.TM": "تركمانستان",
	"countriesL10nMessages.TN": "تونس",
	"countriesL10nMessages.TO": "تونغا",
	"countriesL10nMessages.TR": "تركيا",
	"countriesL10nMessages.TT": "ترينداد وتوباغو",
	"countriesL10nMessages.TV": "توفالو",
	"countriesL10nMessages.TW": "تايوان",
	"countriesL10nMessages.TZ": "تنزانيا",
	"countriesL10nMessages.UA": "أوكرانيا",
	"countriesL10nMessages.UG": "أوغندا",
	"countriesL10nMessages.US": "الولايات المتحدة",
	"countriesL10nMessages.UY": "أوروغواي",
	"countriesL10nMessages.UZ": "أوزبكستان",
	"countriesL10nMessages.VA": "الكرسي البابوي (دولة مدينة الفاتيكان)",
	"countriesL10nMessages.VC": "سانت فنسنت والجرينادينز",
	"countriesL10nMessages.VE": "فنزويلا",
	"countriesL10nMessages.VG": "جزر فيرجن البريطانية",
	"countriesL10nMessages.VI": "جزر العذراء الامريكية",
	"countriesL10nMessages.VN": "فيتنام",
	"countriesL10nMessages.VU": "فانواتو",
	"countriesL10nMessages.WF": "واليس وفوتونا",
	"countriesL10nMessages.WS": "ساموا",
	"countriesL10nMessages.XK": "كوسوفو",
	"countriesL10nMessages.YE": "اليمن",
	"countriesL10nMessages.YT": "مايوت",
	"countriesL10nMessages.ZA": "جنوب أفريقيا",
	"countriesL10nMessages.ZM": "زامبيا",
	"countriesL10nMessages.ZW": "زيمبابوي",
	"ToggleableEmailField.checkboxLabel": "إرسال نسخة من ردودي عبر البريد الإلكتروني",
	"ToggleableEmailField.emailLabel": "إدخال عنوان بريدك الإلكتروني"
};

var bg = {
	"AddressField.selectCountry": "Изберете държава",
	"PhoneField.countryCode": "Код на държавата",
	"countriesL10nMessages.AC": "Остров Възнесение",
	"countriesL10nMessages.AD": "Андора",
	"countriesL10nMessages.AE": "Обединени арабски емирства",
	"countriesL10nMessages.AF": "Афганистан",
	"countriesL10nMessages.AG": "Антигуа и Барбуда",
	"countriesL10nMessages.AI": "Ангуила",
	"countriesL10nMessages.AL": "Албания",
	"countriesL10nMessages.AM": "Армения",
	"countriesL10nMessages.AO": "Ангола",
	"countriesL10nMessages.AR": "Аржентина",
	"countriesL10nMessages.AS": "Американска Самоа",
	"countriesL10nMessages.AT": "Австрия",
	"countriesL10nMessages.AU": "Австралия",
	"countriesL10nMessages.AW": "Аруба",
	"countriesL10nMessages.AX": "Аландски острови",
	"countriesL10nMessages.AZ": "Азербайджан",
	"countriesL10nMessages.BA": "Босна и Херцеговина",
	"countriesL10nMessages.BB": "Барбадос",
	"countriesL10nMessages.BD": "Бангладеш",
	"countriesL10nMessages.BE": "Белгия",
	"countriesL10nMessages.BF": "Буркина Фасо",
	"countriesL10nMessages.BG": "България",
	"countriesL10nMessages.BH": "Бахрейн",
	"countriesL10nMessages.BI": "Бурунди",
	"countriesL10nMessages.BJ": "Бенин",
	"countriesL10nMessages.BL": "Сен Бартелеми",
	"countriesL10nMessages.BM": "Бермудите",
	"countriesL10nMessages.BN": "Бруней Даруссалам",
	"countriesL10nMessages.BO": "Боливия",
	"countriesL10nMessages.BQ": "Бонер, Синт Естатиус и Саба",
	"countriesL10nMessages.BR": "Бразилия",
	"countriesL10nMessages.BS": "Бахамите",
	"countriesL10nMessages.BT": "Бутан",
	"countriesL10nMessages.BW": "Ботсвана",
	"countriesL10nMessages.BZ": "Белиз",
	"countriesL10nMessages.CA": "Канада",
	"countriesL10nMessages.CC": "Кокосови (Кийлинг) острови",
	"countriesL10nMessages.CD": "Конго, Демократична република",
	"countriesL10nMessages.CF": "Централноафриканска република",
	"countriesL10nMessages.CG": "Конго",
	"countriesL10nMessages.CH": "Швейцария",
	"countriesL10nMessages.CI": "Кот д'Ивоар",
	"countriesL10nMessages.CK": "Острови Кук",
	"countriesL10nMessages.CL": "Чили",
	"countriesL10nMessages.CM": "Камерун",
	"countriesL10nMessages.CN": "Китай",
	"countriesL10nMessages.CO": "Колумбия",
	"countriesL10nMessages.CR": "Коста Рика",
	"countriesL10nMessages.CV": "Кабо Верде",
	"countriesL10nMessages.CW": "Кюрасао",
	"countriesL10nMessages.CX": "Остров Рождество",
	"countriesL10nMessages.CY": "Кипър",
	"countriesL10nMessages.CZ": "Чехия",
	"countriesL10nMessages.DE": "Германия",
	"countriesL10nMessages.DJ": "Джибути",
	"countriesL10nMessages.DK": "Дания",
	"countriesL10nMessages.DM": "Доминика",
	"countriesL10nMessages.DO": "Доминиканска република",
	"countriesL10nMessages.DZ": "Алжир",
	"countriesL10nMessages.EC": "Еквадор",
	"countriesL10nMessages.EE": "Естония",
	"countriesL10nMessages.EG": "Египет",
	"countriesL10nMessages.EH": "Западна Сахара",
	"countriesL10nMessages.ER": "Еритрея",
	"countriesL10nMessages.ES": "Испания",
	"countriesL10nMessages.ET": "Етиопия",
	"countriesL10nMessages.FI": "Финландия",
	"countriesL10nMessages.FJ": "Фиджи",
	"countriesL10nMessages.FK": "Фолкландски острови",
	"countriesL10nMessages.FM": "Федерални щати на Микронезия",
	"countriesL10nMessages.FO": "Фарьорски острови",
	"countriesL10nMessages.FR": "Франция",
	"countriesL10nMessages.GA": "Габон",
	"countriesL10nMessages.GB": "Великобритания",
	"countriesL10nMessages.GD": "Гренада",
	"countriesL10nMessages.GE": "Грузия",
	"countriesL10nMessages.GF": "Френска Гвиана",
	"countriesL10nMessages.GG": "Гърнси",
	"countriesL10nMessages.GH": "Гана",
	"countriesL10nMessages.GI": "Гибралтар",
	"countriesL10nMessages.GL": "Гренландия",
	"countriesL10nMessages.GM": "Гамбия",
	"countriesL10nMessages.GN": "Гвинея",
	"countriesL10nMessages.GP": "Гваделупа",
	"countriesL10nMessages.GQ": "Екваториална Гвинея",
	"countriesL10nMessages.GR": "Гърция",
	"countriesL10nMessages.GT": "Гватемала",
	"countriesL10nMessages.GU": "Гуам",
	"countriesL10nMessages.GW": "Гвинея-Бисау",
	"countriesL10nMessages.GY": "Гвиана",
	"countriesL10nMessages.HK": "Хонг Конг",
	"countriesL10nMessages.HN": "Хондурас",
	"countriesL10nMessages.HR": "Хърватия",
	"countriesL10nMessages.HT": "Хаити",
	"countriesL10nMessages.HU": "Унгария",
	"countriesL10nMessages.ID": "Индонезия",
	"countriesL10nMessages.IE": "Ирландия",
	"countriesL10nMessages.IL": "Израел",
	"countriesL10nMessages.IM": "Остров Ман",
	"countriesL10nMessages.IN": "Индия",
	"countriesL10nMessages.IO": "Британска територия в Индийския океан",
	"countriesL10nMessages.IQ": "Ирак",
	"countriesL10nMessages.IS": "Исландия",
	"countriesL10nMessages.IT": "Италия",
	"countriesL10nMessages.JE": "Джърси",
	"countriesL10nMessages.JM": "Ямайка",
	"countriesL10nMessages.JO": "Йордания",
	"countriesL10nMessages.JP": "Япония",
	"countriesL10nMessages.KE": "Кения",
	"countriesL10nMessages.KG": "Киргизстан",
	"countriesL10nMessages.KH": "Камбоджа",
	"countriesL10nMessages.KI": "Кирибати",
	"countriesL10nMessages.KM": "Коморски острови",
	"countriesL10nMessages.KN": "Сейнт Китс и Невис",
	"countriesL10nMessages.KR": "Южна Корея",
	"countriesL10nMessages.KW": "Кувейт",
	"countriesL10nMessages.KY": "Кайманови острови",
	"countriesL10nMessages.KZ": "Казахстан",
	"countriesL10nMessages.LA": "Лаос",
	"countriesL10nMessages.LB": "Ливан",
	"countriesL10nMessages.LC": "Сейнт Лусия",
	"countriesL10nMessages.LI": "Лихтенщайн",
	"countriesL10nMessages.LK": "Шри Ланка",
	"countriesL10nMessages.LR": "Либерия",
	"countriesL10nMessages.LS": "Лесото",
	"countriesL10nMessages.LT": "Литва",
	"countriesL10nMessages.LU": "Люксембург",
	"countriesL10nMessages.LV": "Латвия",
	"countriesL10nMessages.LY": "Либия",
	"countriesL10nMessages.MA": "Мароко",
	"countriesL10nMessages.MC": "Монако",
	"countriesL10nMessages.MD": "Молдова",
	"countriesL10nMessages.ME": "Черна гора",
	"countriesL10nMessages.MF": "Сен Мартен (френска част)",
	"countriesL10nMessages.MG": "Мадагаскар",
	"countriesL10nMessages.MH": "Маршалови острови",
	"countriesL10nMessages.MK": "Македония",
	"countriesL10nMessages.ML": "Мали",
	"countriesL10nMessages.MM": "Мианмар",
	"countriesL10nMessages.MN": "Монголия",
	"countriesL10nMessages.MO": "Макао",
	"countriesL10nMessages.MP": "Северни Мариански острови",
	"countriesL10nMessages.MQ": "Мартиника",
	"countriesL10nMessages.MR": "Мавритания",
	"countriesL10nMessages.MS": "Монсерат",
	"countriesL10nMessages.MT": "Малта",
	"countriesL10nMessages.MU": "Мавриций",
	"countriesL10nMessages.MV": "Малдиви",
	"countriesL10nMessages.MW": "Малави",
	"countriesL10nMessages.MX": "Мексико",
	"countriesL10nMessages.MY": "Малайзия",
	"countriesL10nMessages.MZ": "Мозамбик",
	"countriesL10nMessages.NA": "Намибия",
	"countriesL10nMessages.NC": "Нова Каледония",
	"countriesL10nMessages.NE": "Нигер",
	"countriesL10nMessages.NF": "Остров Норфолк",
	"countriesL10nMessages.NG": "Нигерия",
	"countriesL10nMessages.NI": "Никарагуа",
	"countriesL10nMessages.NL": "Холандия",
	"countriesL10nMessages.NO": "Норвегия",
	"countriesL10nMessages.NP": "Непал",
	"countriesL10nMessages.NR": "Науру",
	"countriesL10nMessages.NU": "Ниуе",
	"countriesL10nMessages.NZ": "Нова Зеландия",
	"countriesL10nMessages.OM": "Оман",
	"countriesL10nMessages.PA": "Панама",
	"countriesL10nMessages.PE": "Перу",
	"countriesL10nMessages.PF": "Френска Полинезия",
	"countriesL10nMessages.PG": "Папуа-Нова Гвинея",
	"countriesL10nMessages.PH": "Филипини",
	"countriesL10nMessages.PK": "Пакистан",
	"countriesL10nMessages.PL": "Полша",
	"countriesL10nMessages.PM": "Сен Пиер и Микелон",
	"countriesL10nMessages.PR": "Пуерто Рико",
	"countriesL10nMessages.PS": "Палестина",
	"countriesL10nMessages.PT": "Португалия",
	"countriesL10nMessages.PW": "Палау",
	"countriesL10nMessages.PY": "Парагвай",
	"countriesL10nMessages.QA": "Катар",
	"countriesL10nMessages.RE": "Реюнион",
	"countriesL10nMessages.RO": "Румъния",
	"countriesL10nMessages.RS": "Сърбия",
	"countriesL10nMessages.RW": "Руанда",
	"countriesL10nMessages.SA": "Саудитска Арабия",
	"countriesL10nMessages.SB": "Соломонови острови",
	"countriesL10nMessages.SC": "Сейшели",
	"countriesL10nMessages.SE": "Швеция",
	"countriesL10nMessages.SG": "Сингапур",
	"countriesL10nMessages.SH": "Света Елена",
	"countriesL10nMessages.SI": "Словения",
	"countriesL10nMessages.SJ": "Свалбард и Ян Майен",
	"countriesL10nMessages.SK": "Словакия",
	"countriesL10nMessages.SL": "Сиера Леоне",
	"countriesL10nMessages.SM": "Сан Марино",
	"countriesL10nMessages.SN": "Сенегал",
	"countriesL10nMessages.SO": "Сомалия",
	"countriesL10nMessages.SR": "Суринам",
	"countriesL10nMessages.SS": "Южен Судан",
	"countriesL10nMessages.ST": "Сао Томе и Принсипи",
	"countriesL10nMessages.SV": "Ел Салвадор",
	"countriesL10nMessages.SX": "Синт Мартен",
	"countriesL10nMessages.SZ": "Свазиленд",
	"countriesL10nMessages.TA": "Тристан да Куня",
	"countriesL10nMessages.TC": "Острови Търкс и Кайкос",
	"countriesL10nMessages.TD": "Чад",
	"countriesL10nMessages.TG": "Того",
	"countriesL10nMessages.TH": "Тайланд",
	"countriesL10nMessages.TJ": "Таджикистан",
	"countriesL10nMessages.TK": "Токелау",
	"countriesL10nMessages.TL": "Източен Тимор",
	"countriesL10nMessages.TM": "Туркменистан",
	"countriesL10nMessages.TN": "Тунис",
	"countriesL10nMessages.TO": "Тонга",
	"countriesL10nMessages.TR": "Турция",
	"countriesL10nMessages.TT": "Тринидад и Тобаго",
	"countriesL10nMessages.TV": "Тувалу",
	"countriesL10nMessages.TW": "Тайван",
	"countriesL10nMessages.TZ": "Танзания",
	"countriesL10nMessages.UA": "Украйна",
	"countriesL10nMessages.UG": "Уганда",
	"countriesL10nMessages.US": "САЩ",
	"countriesL10nMessages.UY": "Уругвай",
	"countriesL10nMessages.UZ": "Узбекистан",
	"countriesL10nMessages.VA": "Светият престол (град-държава Ватикана)",
	"countriesL10nMessages.VC": "Сейнт Винсент и Гренадини",
	"countriesL10nMessages.VE": "Венецуела",
	"countriesL10nMessages.VG": "Британски Вирджински острови",
	"countriesL10nMessages.VI": "Вирджински острови, САЩ",
	"countriesL10nMessages.VN": "Виетнам",
	"countriesL10nMessages.VU": "Вануату",
	"countriesL10nMessages.WF": "Уолис и Футуна",
	"countriesL10nMessages.WS": "Самоа",
	"countriesL10nMessages.XK": "Косово",
	"countriesL10nMessages.YE": "Йемен",
	"countriesL10nMessages.YT": "Майот",
	"countriesL10nMessages.ZA": "Южна Африка",
	"countriesL10nMessages.ZM": "Замбия",
	"countriesL10nMessages.ZW": "Зимбабве",
	"ToggleableEmailField.checkboxLabel": "Изпратете ми копие на моите отговори с имейл",
	"ToggleableEmailField.emailLabel": "Въведете имейл адреса си"
};

var bn = {
	"AddressField.selectCountry": "দেশ বেছে নিন",
	"PhoneField.countryCode": "দেশের কোড",
	"countriesL10nMessages.AC": "অ্যাসেনশন দ্বীপ",
	"countriesL10nMessages.AD": "অ্যান্ডোরা",
	"countriesL10nMessages.AE": "সংযুক্ত আরব আমিরাত",
	"countriesL10nMessages.AF": "আফগানিস্তান",
	"countriesL10nMessages.AG": "অ্যান্টিগুয়া এবং বার্বুডা",
	"countriesL10nMessages.AI": "অ্যাঙ্গুইলা",
	"countriesL10nMessages.AL": "আল্বেনিয়া",
	"countriesL10nMessages.AM": "আর্মেনিয়া",
	"countriesL10nMessages.AO": "অ্যাঙ্গোলা",
	"countriesL10nMessages.AR": "আর্জেন্টিনা",
	"countriesL10nMessages.AS": "আমেরিকান সামোয়া",
	"countriesL10nMessages.AT": "অস্ট্রিয়া",
	"countriesL10nMessages.AU": "অস্ট্রেলিয়া",
	"countriesL10nMessages.AW": "আরুবা",
	"countriesL10nMessages.AX": "অল্যান্ড দ্বীপপুঞ্জ",
	"countriesL10nMessages.AZ": "আজারবাইজান",
	"countriesL10nMessages.BA": "বসনিয়া এবং হার্জেগোভিনা",
	"countriesL10nMessages.BB": "বার্বাডোস",
	"countriesL10nMessages.BD": "বাংলাদেশ",
	"countriesL10nMessages.BE": "বেলজিয়াম",
	"countriesL10nMessages.BF": "বুরকিনা ফাসো",
	"countriesL10nMessages.BG": "বুলগেরিয়া",
	"countriesL10nMessages.BH": "বাহরাইন",
	"countriesL10nMessages.BI": "বুরুন্ডি",
	"countriesL10nMessages.BJ": "বেনিন",
	"countriesL10nMessages.BL": "সেন্ট বারথেলেমি",
	"countriesL10nMessages.BM": "বারমুডা",
	"countriesL10nMessages.BN": "ব্রুনেই দারুসসালাম",
	"countriesL10nMessages.BO": "বলিভিয়া",
	"countriesL10nMessages.BQ": "বোনায়র, সিন্ট ইউস্টেশিয়াস এবং সাবা",
	"countriesL10nMessages.BR": "ব্রাজিল",
	"countriesL10nMessages.BS": "বাহামাস",
	"countriesL10nMessages.BT": "ভুটান",
	"countriesL10nMessages.BW": "বতসোয়ানা",
	"countriesL10nMessages.BZ": "বেলিজ",
	"countriesL10nMessages.CA": "কানাডা",
	"countriesL10nMessages.CC": "কোকোস (কীলিং) দ্বীপপুঞ্জ",
	"countriesL10nMessages.CD": "কঙ্গো, গণতান্ত্রিক প্রজাতন্ত্র",
	"countriesL10nMessages.CF": "মধ্য আফ্রিকান প্রজাতন্ত্র",
	"countriesL10nMessages.CG": "কঙ্গো",
	"countriesL10nMessages.CH": "সুইজারল্যান্ড",
	"countriesL10nMessages.CI": "কোত দিভোয়ার",
	"countriesL10nMessages.CK": "কুক দ্বীপপুঞ্জ",
	"countriesL10nMessages.CL": "চিলি",
	"countriesL10nMessages.CM": "ক্যামেরুন",
	"countriesL10nMessages.CN": "চীন",
	"countriesL10nMessages.CO": "কলম্বিয়া",
	"countriesL10nMessages.CR": "কোস্টা রিকা",
	"countriesL10nMessages.CV": "কেপ ভার্দে",
	"countriesL10nMessages.CW": "কুরাসাও",
	"countriesL10nMessages.CX": "ক্রিসমাস দ্বীপ",
	"countriesL10nMessages.CY": "সাইপ্রাস",
	"countriesL10nMessages.CZ": "চেক প্রজাতন্ত্র",
	"countriesL10nMessages.DE": "জার্মানি",
	"countriesL10nMessages.DJ": "জিবুতি",
	"countriesL10nMessages.DK": "ডেনমার্ক",
	"countriesL10nMessages.DM": "ডোমিনিকা",
	"countriesL10nMessages.DO": "ডোমিনিকান প্রজাতন্ত্র",
	"countriesL10nMessages.DZ": "আলজেরিয়া",
	"countriesL10nMessages.EC": "ইকুয়েডর",
	"countriesL10nMessages.EE": "এস্তোনিয়া",
	"countriesL10nMessages.EG": "মিশর",
	"countriesL10nMessages.EH": "পশ্চিম সাহারা",
	"countriesL10nMessages.ER": "ইরিত্রিয়া",
	"countriesL10nMessages.ES": "স্পেন",
	"countriesL10nMessages.ET": "ইথিওপিয়া",
	"countriesL10nMessages.FI": "ফিনল্যান্ড",
	"countriesL10nMessages.FJ": "ফিজি",
	"countriesL10nMessages.FK": "ফকল্যান্ড দ্বীপপুঞ্জ",
	"countriesL10nMessages.FM": "মাইক্রোনেশিয়ার যুক্তরাষ্ট্র",
	"countriesL10nMessages.FO": "ফ্যারো দ্বীপপুঞ্জ",
	"countriesL10nMessages.FR": "ফ্রান্স",
	"countriesL10nMessages.GA": "গাবন",
	"countriesL10nMessages.GB": "যুক্তরাজ্য",
	"countriesL10nMessages.GD": "গ্রেনাডা",
	"countriesL10nMessages.GE": "জর্জিয়া",
	"countriesL10nMessages.GF": "ফরাসি গায়ানা",
	"countriesL10nMessages.GG": "গুয়ার্নসি",
	"countriesL10nMessages.GH": "ঘানা",
	"countriesL10nMessages.GI": "জিব্রাল্টার",
	"countriesL10nMessages.GL": "গ্রীনল্যান্ড",
	"countriesL10nMessages.GM": "গাম্বিয়া",
	"countriesL10nMessages.GN": "গিনি",
	"countriesL10nMessages.GP": "গুয়াদেলৌপ",
	"countriesL10nMessages.GQ": "ইকুয়েটরিয়াল গিনি",
	"countriesL10nMessages.GR": "গ্রীস",
	"countriesL10nMessages.GT": "গুয়াতেমালা",
	"countriesL10nMessages.GU": "গুয়াম",
	"countriesL10nMessages.GW": "গিনি-বিসাউ",
	"countriesL10nMessages.GY": "গায়ানা",
	"countriesL10nMessages.HK": "হংকং",
	"countriesL10nMessages.HN": "হন্ডুরাস",
	"countriesL10nMessages.HR": "ক্রোয়েশিয়া",
	"countriesL10nMessages.HT": "হাইতি",
	"countriesL10nMessages.HU": "হাঙ্গেরি",
	"countriesL10nMessages.ID": "ইন্দোনেশিয়া",
	"countriesL10nMessages.IE": "আয়ারল্যান্ড",
	"countriesL10nMessages.IL": "ইস্রায়েল",
	"countriesL10nMessages.IM": "আইল অফ ম্যান",
	"countriesL10nMessages.IN": "ভারত",
	"countriesL10nMessages.IO": "ব্রিটিশ ভারত মহাসাগরীয় এলাকা",
	"countriesL10nMessages.IQ": "ইরাক",
	"countriesL10nMessages.IS": "আইসল্যান্ড",
	"countriesL10nMessages.IT": "ইতালি",
	"countriesL10nMessages.JE": "জার্সি",
	"countriesL10nMessages.JM": "জামাইকা",
	"countriesL10nMessages.JO": "জর্ডান",
	"countriesL10nMessages.JP": "জাপান",
	"countriesL10nMessages.KE": "কেনিয়া",
	"countriesL10nMessages.KG": "কিরগিজস্তান",
	"countriesL10nMessages.KH": "কম্বোডিয়া",
	"countriesL10nMessages.KI": "কিরিবাতি",
	"countriesL10nMessages.KM": "কোমোরোস",
	"countriesL10nMessages.KN": "সেন্ট কিটস এবং নেভিস",
	"countriesL10nMessages.KR": "দক্ষিণ কোরিয়া",
	"countriesL10nMessages.KW": "কুয়েত",
	"countriesL10nMessages.KY": "কেম্যান দ্বীপপুঞ্জ",
	"countriesL10nMessages.KZ": "কাজাখস্তান",
	"countriesL10nMessages.LA": "লাওস",
	"countriesL10nMessages.LB": "লেবানন",
	"countriesL10nMessages.LC": "সেন্ট লুসিয়া",
	"countriesL10nMessages.LI": "লিচেনস্টাইন",
	"countriesL10nMessages.LK": "শ্রীলংকা",
	"countriesL10nMessages.LR": "লাইবেরিয়া",
	"countriesL10nMessages.LS": "লেসোথো",
	"countriesL10nMessages.LT": "লিথুয়ানিয়া",
	"countriesL10nMessages.LU": "লাক্সেমবার্গ",
	"countriesL10nMessages.LV": "লাটভিয়া",
	"countriesL10nMessages.LY": "লিবিয়া",
	"countriesL10nMessages.MA": "মরক্কো",
	"countriesL10nMessages.MC": "মোনাকো",
	"countriesL10nMessages.MD": "মলদোভা",
	"countriesL10nMessages.ME": "মন্টিনিগ্রো",
	"countriesL10nMessages.MF": "সেন্ট মার্টিন (ফরাসি অংশ)",
	"countriesL10nMessages.MG": "মাদাগাস্কার",
	"countriesL10nMessages.MH": "মার্শাল দ্বীপপুঞ্জ",
	"countriesL10nMessages.MK": "মেসিডোনিয়া",
	"countriesL10nMessages.ML": "মালি",
	"countriesL10nMessages.MM": "মায়ানমার",
	"countriesL10nMessages.MN": "মঙ্গোলিয়া",
	"countriesL10nMessages.MO": "ম্যাকাও",
	"countriesL10nMessages.MP": "উত্তর মারিয়ানা দ্বীপপুঞ্জ",
	"countriesL10nMessages.MQ": "মার্টিনিক",
	"countriesL10nMessages.MR": "মৌরিতানিয়া",
	"countriesL10nMessages.MS": "মন্টসেরাট",
	"countriesL10nMessages.MT": "মাল্টা",
	"countriesL10nMessages.MU": "মরিশাস",
	"countriesL10nMessages.MV": "মালদ্বীপ",
	"countriesL10nMessages.MW": "মালাউই",
	"countriesL10nMessages.MX": "মেক্সিকো",
	"countriesL10nMessages.MY": "মালয়েশিয়া",
	"countriesL10nMessages.MZ": "মোজাম্বিক",
	"countriesL10nMessages.NA": "নামিবিয়া",
	"countriesL10nMessages.NC": "নিউ ক্যালেডোনিয়া",
	"countriesL10nMessages.NE": "নাইজার",
	"countriesL10nMessages.NF": "নরফোক দ্বীপ",
	"countriesL10nMessages.NG": "নাইজেরিয়া",
	"countriesL10nMessages.NI": "নিকারাগুয়া",
	"countriesL10nMessages.NL": "নেদারল্যান্ডস",
	"countriesL10nMessages.NO": "নরওয়ে",
	"countriesL10nMessages.NP": "নেপাল",
	"countriesL10nMessages.NR": "নাউরু",
	"countriesL10nMessages.NU": "নিউয়ে",
	"countriesL10nMessages.NZ": "নিউজিল্যান্ড",
	"countriesL10nMessages.OM": "ওমান",
	"countriesL10nMessages.PA": "পানামা",
	"countriesL10nMessages.PE": "পেরু",
	"countriesL10nMessages.PF": "ফরাসি পলিনেশিয়া",
	"countriesL10nMessages.PG": "পাপুয়া নিউ গিনি",
	"countriesL10nMessages.PH": "ফিলিপাইন্স",
	"countriesL10nMessages.PK": "পাকিস্তান",
	"countriesL10nMessages.PL": "পোল্যান্ড",
	"countriesL10nMessages.PM": "সেন্ট পিয়ের এবং মিক্যুয়েলন",
	"countriesL10nMessages.PR": "পুয়ের্তো রিকো",
	"countriesL10nMessages.PS": "প্যালেস্টাইন",
	"countriesL10nMessages.PT": "পর্তুগাল",
	"countriesL10nMessages.PW": "পালাউ",
	"countriesL10nMessages.PY": "প্যারাগুয়ে",
	"countriesL10nMessages.QA": "কাতার",
	"countriesL10nMessages.RE": "রেইউনিয়ন",
	"countriesL10nMessages.RO": "রোমানিয়া",
	"countriesL10nMessages.RS": "সার্বিয়া",
	"countriesL10nMessages.RW": "রুয়ান্ডা",
	"countriesL10nMessages.SA": "সৌদি আরব",
	"countriesL10nMessages.SB": "সলোমান দ্বীপপুঞ্জ",
	"countriesL10nMessages.SC": "সিশেলস",
	"countriesL10nMessages.SE": "সুইডেন",
	"countriesL10nMessages.SG": "সিঙ্গাপুর",
	"countriesL10nMessages.SH": "সেন্ট হেলেনা",
	"countriesL10nMessages.SI": "স্লোভেনিয়া",
	"countriesL10nMessages.SJ": "স্ভালবার্ড এবং জান মায়েন",
	"countriesL10nMessages.SK": "স্লোভাকিয়া",
	"countriesL10nMessages.SL": "সিয়েরা লিওন",
	"countriesL10nMessages.SM": "সান মারিনো",
	"countriesL10nMessages.SN": "সেনেগাল",
	"countriesL10nMessages.SO": "সোমালিয়া",
	"countriesL10nMessages.SR": "সুরিনাম",
	"countriesL10nMessages.SS": "দক্ষিণ সুদান",
	"countriesL10nMessages.ST": "সাও টোমে এবং প্রিন্সিপি",
	"countriesL10nMessages.SV": "এল সালভাদর",
	"countriesL10nMessages.SX": "সিন্ট মার্টেন",
	"countriesL10nMessages.SZ": "সোয়াজিল্যান্ড",
	"countriesL10nMessages.TA": "ট্রিস্টান দা কুনিয়া",
	"countriesL10nMessages.TC": "টার্কস এবং কাইকোস দ্বীপপুঞ্জ",
	"countriesL10nMessages.TD": "চাদ",
	"countriesL10nMessages.TG": "টোগো",
	"countriesL10nMessages.TH": "থাইল্যান্ড",
	"countriesL10nMessages.TJ": "তাজিকিস্তান",
	"countriesL10nMessages.TK": "টোকেলাউ",
	"countriesL10nMessages.TL": "তিমোর-লেস্তে",
	"countriesL10nMessages.TM": "তুর্কমেনিস্তান",
	"countriesL10nMessages.TN": "তিউনিসিয়া",
	"countriesL10nMessages.TO": "টোঙ্গা",
	"countriesL10nMessages.TR": "তুরস্ক",
	"countriesL10nMessages.TT": "ট্রিনিডাদ ও টোব্যাগো",
	"countriesL10nMessages.TV": "টুভালু",
	"countriesL10nMessages.TW": "তাইওয়ান",
	"countriesL10nMessages.TZ": "তানজানিয়া",
	"countriesL10nMessages.UA": "ইউক্রেন",
	"countriesL10nMessages.UG": "উগান্ডা",
	"countriesL10nMessages.US": "মার্কিন যুক্তরাষ্ট্র",
	"countriesL10nMessages.UY": "উরুগুয়ে",
	"countriesL10nMessages.UZ": "উজবেকিস্তান",
	"countriesL10nMessages.VA": "হলি সি (ভ্যাটিকান সিটি রাষ্ট্র)",
	"countriesL10nMessages.VC": "সেন্ট ভিনসেন্ট এবং গ্রেনাডাইনস",
	"countriesL10nMessages.VE": "ভেনেজুয়েলা",
	"countriesL10nMessages.VG": "ভার্জিন দ্বীপপুঞ্জ, ব্রিটিশ",
	"countriesL10nMessages.VI": "ভার্জিন দ্বীপপুঞ্জ, ইউ.এস",
	"countriesL10nMessages.VN": "ভিয়েতনাম",
	"countriesL10nMessages.VU": "ভানুয়াটু",
	"countriesL10nMessages.WF": "ওয়ালিস এবং ফুটুনা",
	"countriesL10nMessages.WS": "সামোয়া",
	"countriesL10nMessages.XK": "কসোভো",
	"countriesL10nMessages.YE": "ইয়েমেন",
	"countriesL10nMessages.YT": "মায়োট",
	"countriesL10nMessages.ZA": "দক্ষিণ আফ্রিকা",
	"countriesL10nMessages.ZM": "জাম্বিয়া",
	"countriesL10nMessages.ZW": "জিম্বাবুয়ে",
	"ToggleableEmailField.checkboxLabel": "আমার প্রতিক্রিয়াগুলির একটি অনুলিপি আমাকে ইমেইলের মাধ্যমে পাঠান",
	"ToggleableEmailField.emailLabel": "আপনার ইমেইল ঠিকানা লিখুন"
};

var bs = {
	"AddressField.selectCountry": "Odaberi državu",
	"PhoneField.countryCode": "Pozivni broj države",
	"countriesL10nMessages.AC": "Ostrvo Ascension",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Ujedinjeni Arapski Emirati",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua i Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanija",
	"countriesL10nMessages.AM": "Armenija",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Američka Samoa",
	"countriesL10nMessages.AT": "Austrija",
	"countriesL10nMessages.AU": "Australija",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Olandska ostrva",
	"countriesL10nMessages.AZ": "Azerbedžajn",
	"countriesL10nMessages.BA": "Bosna i Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladeš",
	"countriesL10nMessages.BE": "Belgija",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bugarska",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermudi",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivija",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius i Saba",
	"countriesL10nMessages.BR": "Brazil",
	"countriesL10nMessages.BS": "Bahami",
	"countriesL10nMessages.BT": "Butan",
	"countriesL10nMessages.BW": "Bocvana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Ostrvima Kokos (Keeling)",
	"countriesL10nMessages.CD": "Kongo, Demokratska Republika",
	"countriesL10nMessages.CF": "Centralnoafrička Republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Švicarska",
	"countriesL10nMessages.CI": "Côte d'Ivoire",
	"countriesL10nMessages.CK": "Kukova ostrva",
	"countriesL10nMessages.CL": "Čile",
	"countriesL10nMessages.CM": "Kameron",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Kolumbija",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Zelenortska Ostrva",
	"countriesL10nMessages.CW": "Kurasao",
	"countriesL10nMessages.CX": "Božično Ostrvo",
	"countriesL10nMessages.CY": "Kipar",
	"countriesL10nMessages.CZ": "Češka Republika",
	"countriesL10nMessages.DE": "Njemačka",
	"countriesL10nMessages.DJ": "Džibuti",
	"countriesL10nMessages.DK": "Danska",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikanska Republika",
	"countriesL10nMessages.DZ": "Alžir",
	"countriesL10nMessages.EC": "Ekvador",
	"countriesL10nMessages.EE": "Estonija",
	"countriesL10nMessages.EG": "Egipat",
	"countriesL10nMessages.EH": "Zapadna Sahara",
	"countriesL10nMessages.ER": "Eritreja",
	"countriesL10nMessages.ES": "Španija",
	"countriesL10nMessages.ET": "Etiopija",
	"countriesL10nMessages.FI": "Finska",
	"countriesL10nMessages.FJ": "Fiđi",
	"countriesL10nMessages.FK": "Foklandska ostrva",
	"countriesL10nMessages.FM": "Federalne Države Mikronezije",
	"countriesL10nMessages.FO": "Faroe Ostrva",
	"countriesL10nMessages.FR": "Francuska",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Ujedinjeno Kraljevstvo",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzija",
	"countriesL10nMessages.GF": "Francuska Gvineja",
	"countriesL10nMessages.GG": "Gernzi",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grenland",
	"countriesL10nMessages.GM": "Gambija",
	"countriesL10nMessages.GN": "Gvineja",
	"countriesL10nMessages.GP": "Gvadalupe",
	"countriesL10nMessages.GQ": "Ekvatorijalna Gvineja",
	"countriesL10nMessages.GR": "Grčka",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Gvineja-Bisao",
	"countriesL10nMessages.GY": "Gvajana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Hrvatska",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Mađarska",
	"countriesL10nMessages.ID": "Indonezija",
	"countriesL10nMessages.IE": "Irska",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Ostrvo Man",
	"countriesL10nMessages.IN": "Indija",
	"countriesL10nMessages.IO": "Britanska Teritorija u Indijskom Okeanu",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italija",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamajca",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenija",
	"countriesL10nMessages.KG": "Kirgistan",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoros",
	"countriesL10nMessages.KN": "Saint Kitts i Nevis",
	"countriesL10nMessages.KR": "Južna Koreja",
	"countriesL10nMessages.KW": "Kuvajt",
	"countriesL10nMessages.KY": "Kajmanska Ostrva",
	"countriesL10nMessages.KZ": "Kazahstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Lebanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Lihtenštajn",
	"countriesL10nMessages.LK": "Šri Lanka",
	"countriesL10nMessages.LR": "Liberija",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Litvania",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libija",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavija",
	"countriesL10nMessages.ME": "Crna Gora",
	"countriesL10nMessages.MF": "Saint Martin (Franscuski dio)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Maršalova Ostrva",
	"countriesL10nMessages.MK": "Makedonija",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mijanmar",
	"countriesL10nMessages.MN": "Mongolija",
	"countriesL10nMessages.MO": "Mackao",
	"countriesL10nMessages.MP": "Sjeverna Marijanska ostrva",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritanija",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauricijus",
	"countriesL10nMessages.MV": "Maldivi",
	"countriesL10nMessages.MW": "Malavi",
	"countriesL10nMessages.MX": "Meksiko",
	"countriesL10nMessages.MY": "Malezija",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibija",
	"countriesL10nMessages.NC": "Nova Kaledonija",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Ostrvo Norfolk",
	"countriesL10nMessages.NG": "Nigerija",
	"countriesL10nMessages.NI": "Nikaragva",
	"countriesL10nMessages.NL": "Holandija",
	"countriesL10nMessages.NO": "Norveška",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Novi Zeland",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francuska Polinezija",
	"countriesL10nMessages.PG": "Papua Nova Gvineja",
	"countriesL10nMessages.PH": "Filipini",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poljska",
	"countriesL10nMessages.PM": "Saint Pierre i Miquelon",
	"countriesL10nMessages.PR": "Porto Riko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragvaj",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumunija",
	"countriesL10nMessages.RS": "Srbija",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudijska Arabija",
	"countriesL10nMessages.SB": "Solomonova Ostrva",
	"countriesL10nMessages.SC": "Sejšeli",
	"countriesL10nMessages.SE": "Švedska",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenija",
	"countriesL10nMessages.SJ": "Svalbard i Jan Mayen",
	"countriesL10nMessages.SK": "Slovačka",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalija",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Južni Sudan",
	"countriesL10nMessages.ST": "Sao Tome i Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Svazilend",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks i Caicos Ostrva",
	"countriesL10nMessages.TD": "Čad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tajland",
	"countriesL10nMessages.TJ": "Tađikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunis",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turska",
	"countriesL10nMessages.TT": "Trinidad i Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajvan",
	"countriesL10nMessages.TZ": "Tanzanija",
	"countriesL10nMessages.UA": "Ukrajina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Sjedinjene Države",
	"countriesL10nMessages.UY": "Urugvaj",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Holy See (Grad - država Vatikan)",
	"countriesL10nMessages.VC": "Saint Vincent i Grenadini",
	"countriesL10nMessages.VE": "Venecuela",
	"countriesL10nMessages.VG": "Djevičanska Ostrva, Britanska",
	"countriesL10nMessages.VI": "Djevičanska Ostrva, SAD",
	"countriesL10nMessages.VN": "Vijetnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis i Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Južna Afrika",
	"countriesL10nMessages.ZM": "Zambija",
	"countriesL10nMessages.ZW": "Zimbabve",
	"ToggleableEmailField.checkboxLabel": "Pošaljite mi kopiju mojih odgovora putem e-pošte",
	"ToggleableEmailField.emailLabel": "Unesite email adresu"
};

var ca = {
	"AddressField.selectCountry": "Seleccioneu un país",
	"PhoneField.countryCode": "Codi de país",
	"countriesL10nMessages.AC": "Illa de l'Ascensió",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emirats Àrabs Units",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua i Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albània",
	"countriesL10nMessages.AM": "Armènia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Nord-americana",
	"countriesL10nMessages.AT": "Àustria",
	"countriesL10nMessages.AU": "Austràlia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Illes Åland",
	"countriesL10nMessages.AZ": "Azerbaidjan",
	"countriesL10nMessages.BA": "Bòsnia i Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Bèlgica",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgària",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolívia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius i Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Bahames",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canadà",
	"countriesL10nMessages.CC": "Illes Cocos (Keeling).",
	"countriesL10nMessages.CD": "Congo, República Democràtica del",
	"countriesL10nMessages.CF": "República Centreafricana",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Suïssa",
	"countriesL10nMessages.CI": "Costa d'Ivori",
	"countriesL10nMessages.CK": "Illes Cook",
	"countriesL10nMessages.CL": "Xile",
	"countriesL10nMessages.CM": "Camerun",
	"countriesL10nMessages.CN": "Xina",
	"countriesL10nMessages.CO": "Colòmbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cap Verd",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Illa Christmas",
	"countriesL10nMessages.CY": "Xipre",
	"countriesL10nMessages.CZ": "República Txeca",
	"countriesL10nMessages.DE": "Alemanya",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Dinamarca",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "República Dominicana",
	"countriesL10nMessages.DZ": "Algèria",
	"countriesL10nMessages.EC": "Equador",
	"countriesL10nMessages.EE": "Estònia",
	"countriesL10nMessages.EG": "Egipte",
	"countriesL10nMessages.EH": "Sàhara Occidental",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Espanya",
	"countriesL10nMessages.ET": "Etiòpia",
	"countriesL10nMessages.FI": "Finlàndia",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Illes Falkland",
	"countriesL10nMessages.FM": "Estats Federats de Micronèsia",
	"countriesL10nMessages.FO": "Illes Fèroe",
	"countriesL10nMessages.FR": "França",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Regne Unit",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Geòrgia",
	"countriesL10nMessages.GF": "Guaiana Francesa",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenlàndia",
	"countriesL10nMessages.GM": "Gàmbia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadalupe",
	"countriesL10nMessages.GQ": "Guinea Equatorial",
	"countriesL10nMessages.GR": "Grècia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Hondures",
	"countriesL10nMessages.HR": "Croàcia",
	"countriesL10nMessages.HT": "Haití",
	"countriesL10nMessages.HU": "Hongria",
	"countriesL10nMessages.ID": "Indonèsia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Illa de Man",
	"countriesL10nMessages.IN": "Índia",
	"countriesL10nMessages.IO": "Territori Britànic de l'Oceà Índic",
	"countriesL10nMessages.IQ": "Iraq",
	"countriesL10nMessages.IS": "Islàndia",
	"countriesL10nMessages.IT": "Itàlia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordània",
	"countriesL10nMessages.JP": "Japó",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirguizistan",
	"countriesL10nMessages.KH": "Cambodja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comores",
	"countriesL10nMessages.KN": "Saint Kitts i Nevis",
	"countriesL10nMessages.KR": "Corea del Sud",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Illes Caiman",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Líban",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Libèria",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lituània",
	"countriesL10nMessages.LU": "Luxembourg",
	"countriesL10nMessages.LV": "Letònia",
	"countriesL10nMessages.LY": "Líbia",
	"countriesL10nMessages.MA": "Marroc",
	"countriesL10nMessages.MC": "Mònaco",
	"countriesL10nMessages.MD": "Moldàvia",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (part francesa)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Illes Marshall",
	"countriesL10nMessages.MK": "Macedònia",
	"countriesL10nMessages.ML": "Malí",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongòlia",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Illa Mariana del Nord",
	"countriesL10nMessages.MQ": "Martinica",
	"countriesL10nMessages.MR": "Mauritània",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurici",
	"countriesL10nMessages.MV": "Maldives",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mèxic",
	"countriesL10nMessages.MY": "Malàisia",
	"countriesL10nMessages.MZ": "Moçambic",
	"countriesL10nMessages.NA": "Namíbia",
	"countriesL10nMessages.NC": "Nova Caledònia",
	"countriesL10nMessages.NE": "Níger",
	"countriesL10nMessages.NF": "Illa Norfolk",
	"countriesL10nMessages.NG": "Nigèria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Països Baixos",
	"countriesL10nMessages.NO": "Noruega",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nova Zelanda",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panamà",
	"countriesL10nMessages.PE": "Perú",
	"countriesL10nMessages.PF": "Polinèsia Francesa",
	"countriesL10nMessages.PG": "Papua Nova Guinea",
	"countriesL10nMessages.PH": "Filipines",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polònia",
	"countriesL10nMessages.PM": "Saint-Pierre i Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguai",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Reunió",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Sèrbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Aràbia Saudita",
	"countriesL10nMessages.SB": "Illes Salomó",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Suècia",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Santa Helena",
	"countriesL10nMessages.SI": "Eslovènia",
	"countriesL10nMessages.SJ": "Svalbard i Jan Mayen",
	"countriesL10nMessages.SK": "Eslovàquia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somàlia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sudan del Sud",
	"countriesL10nMessages.ST": "Santo Tomé i Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swazilàndia",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Illes Turks i Caicos",
	"countriesL10nMessages.TD": "Txad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tailàndia",
	"countriesL10nMessages.TJ": "Tadjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor Oriental",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunísia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turquia",
	"countriesL10nMessages.TT": "Trinitat i Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzània",
	"countriesL10nMessages.UA": "Ucraïna",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Estats Units",
	"countriesL10nMessages.UY": "Uruguai",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Santa Seu (Estat de la Ciutat del Vaticà)",
	"countriesL10nMessages.VC": "Saint Vincent i les Grenadines",
	"countriesL10nMessages.VE": "Veneçuela",
	"countriesL10nMessages.VG": "Illes Verges Britàniques",
	"countriesL10nMessages.VI": "Illes Verges Nord-americanes",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis i Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Iemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Sud-àfrica",
	"countriesL10nMessages.ZM": "Zàmbia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Envieu-me una còpia de les meves respostes per correu electrònic",
	"ToggleableEmailField.emailLabel": "Introduïu l'adreça de correu electrònic"
};

var cs = {
	"AddressField.selectCountry": "Vybrat zemi",
	"PhoneField.countryCode": "Kód země",
	"countriesL10nMessages.AC": "Ostrov Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Spojené arabské emiráty",
	"countriesL10nMessages.AF": "Afghánistán",
	"countriesL10nMessages.AG": "Antigua a Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albánie",
	"countriesL10nMessages.AM": "Arménie",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Americká Samoa",
	"countriesL10nMessages.AT": "Rakousko",
	"countriesL10nMessages.AU": "Austrálie",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ålandské ostrovy",
	"countriesL10nMessages.AZ": "Ázerbájdžán",
	"countriesL10nMessages.BA": "Bosna a Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladéš",
	"countriesL10nMessages.BE": "Belgie",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulharsko",
	"countriesL10nMessages.BH": "Bahrajn",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Svatý Bartoloměj",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolívie",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius a Saba",
	"countriesL10nMessages.BR": "Brazílie",
	"countriesL10nMessages.BS": "Bahamy",
	"countriesL10nMessages.BT": "Bhútán",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosové (Keelingovy) ostrovy",
	"countriesL10nMessages.CD": "Kongo, Demokratická republika",
	"countriesL10nMessages.CF": "Středoafrická republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Švýcarsko",
	"countriesL10nMessages.CI": "Pobřeží slonoviny",
	"countriesL10nMessages.CK": "Cookovy ostrovy",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Čína",
	"countriesL10nMessages.CO": "Kolumbie",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Kapverdy",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Vánoční ostrov",
	"countriesL10nMessages.CY": "Kypr",
	"countriesL10nMessages.CZ": "Česká republika",
	"countriesL10nMessages.DE": "Německo",
	"countriesL10nMessages.DJ": "Džibutsko",
	"countriesL10nMessages.DK": "Dánsko",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikánská republika",
	"countriesL10nMessages.DZ": "Alžírsko",
	"countriesL10nMessages.EC": "Ekvádor",
	"countriesL10nMessages.EE": "Estonsko",
	"countriesL10nMessages.EG": "Egypt",
	"countriesL10nMessages.EH": "Západní Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Španělsko",
	"countriesL10nMessages.ET": "Etiopie",
	"countriesL10nMessages.FI": "Finsko",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falklandy",
	"countriesL10nMessages.FM": "Federativní státy Mikronésie",
	"countriesL10nMessages.FO": "Faerské ostrovy",
	"countriesL10nMessages.FR": "Francie",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Spojené království",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzie",
	"countriesL10nMessages.GF": "Francouzská Guyana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grónsko",
	"countriesL10nMessages.GM": "Gambie",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Rovníková Guinea",
	"countriesL10nMessages.GR": "Řecko",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Chorvatsko",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Maďarsko",
	"countriesL10nMessages.ID": "Indonésie",
	"countriesL10nMessages.IE": "Irsko",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Ostrov Man",
	"countriesL10nMessages.IN": "Indie",
	"countriesL10nMessages.IO": "Britská území v Indickém oceánu",
	"countriesL10nMessages.IQ": "Irák",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Itálie",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamajka",
	"countriesL10nMessages.JO": "Jordánsko",
	"countriesL10nMessages.JP": "Japonsko",
	"countriesL10nMessages.KE": "Keňa",
	"countriesL10nMessages.KG": "Kyrgyzstán",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komory",
	"countriesL10nMessages.KN": "Svatý Kryštof a Nevis",
	"countriesL10nMessages.KR": "Jižní Korea",
	"countriesL10nMessages.KW": "Kuvajt",
	"countriesL10nMessages.KY": "Kajmanské ostrovy",
	"countriesL10nMessages.KZ": "Kazachstán",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Svatá Lucie",
	"countriesL10nMessages.LI": "Lichtenštejnsko",
	"countriesL10nMessages.LK": "Srí Lanka",
	"countriesL10nMessages.LR": "Libérie",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litva",
	"countriesL10nMessages.LU": "Lucembursko",
	"countriesL10nMessages.LV": "Lotyšsko",
	"countriesL10nMessages.LY": "Libye",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldávie",
	"countriesL10nMessages.ME": "Černá Hora",
	"countriesL10nMessages.MF": "Svatý Martin (francouzská část)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshallovy ostrovy",
	"countriesL10nMessages.MK": "Makedonie",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolsko",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Ostrov Severní Mariany",
	"countriesL10nMessages.MQ": "Martinik",
	"countriesL10nMessages.MR": "Mauretánie",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauricius",
	"countriesL10nMessages.MV": "Maledivy",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexiko",
	"countriesL10nMessages.MY": "Malajsie",
	"countriesL10nMessages.MZ": "Mosambik",
	"countriesL10nMessages.NA": "Namibie",
	"countriesL10nMessages.NC": "Nová Kaledonie",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Ostrov Norfolk",
	"countriesL10nMessages.NG": "Nigérie",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Nizozemí",
	"countriesL10nMessages.NO": "Norsko",
	"countriesL10nMessages.NP": "Nepál",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nový Zéland",
	"countriesL10nMessages.OM": "Omán",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francouzská Polynésie",
	"countriesL10nMessages.PG": "Papua-Nová Guinea",
	"countriesL10nMessages.PH": "Filipíny",
	"countriesL10nMessages.PK": "Pákistán",
	"countriesL10nMessages.PL": "Polsko",
	"countriesL10nMessages.PM": "Saint Pierre a Miquelon",
	"countriesL10nMessages.PR": "Portoriko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugalsko",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumunsko",
	"countriesL10nMessages.RS": "Srbsko",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saúdská Arábie",
	"countriesL10nMessages.SB": "Šalamounovy ostrovy",
	"countriesL10nMessages.SC": "Seychely",
	"countriesL10nMessages.SE": "Švédsko",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Svatá Helena",
	"countriesL10nMessages.SI": "Slovinsko",
	"countriesL10nMessages.SJ": "Svalbard a Jan Mayen",
	"countriesL10nMessages.SK": "Slovensko",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somálsko",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Jižní Súdán",
	"countriesL10nMessages.ST": "Svatý Tomáš a Princův ostrov",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Svatý Martin",
	"countriesL10nMessages.SZ": "Svazijsko",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Ostrovy Turks a Caicos",
	"countriesL10nMessages.TD": "Čad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thajsko",
	"countriesL10nMessages.TJ": "Tadžikistán",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Východní Timor",
	"countriesL10nMessages.TM": "Turkmenistán",
	"countriesL10nMessages.TN": "Tunisko",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turecko",
	"countriesL10nMessages.TT": "Trinidad a Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tchaj-wan",
	"countriesL10nMessages.TZ": "Tanzanie",
	"countriesL10nMessages.UA": "Ukrajina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Spojené státy americké",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistán",
	"countriesL10nMessages.VA": "Svatý stolec (Vatikánský městský stát)",
	"countriesL10nMessages.VC": "Svatý Vincent a Grenadiny",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Britské Panenské ostrovy",
	"countriesL10nMessages.VI": "Americké Panenské ostrovy",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis a Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Jižní Afrika",
	"countriesL10nMessages.ZM": "Zambie",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Odešlete mi kopii mých odpovědí e-mailem",
	"ToggleableEmailField.emailLabel": "Zadejte svou e-mailovou adresu"
};

var cy = {
	"AddressField.selectCountry": "Dewiswch wlad",
	"PhoneField.countryCode": "Cod Gwlad",
	"countriesL10nMessages.AC": "Ynys Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Yr Emiradau Arabaidd Unedig",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua a Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Yr Ariannin",
	"countriesL10nMessages.AS": "Samoa Americanaidd",
	"countriesL10nMessages.AT": "Awstria",
	"countriesL10nMessages.AU": "Awstralia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ynysoedd Åland",
	"countriesL10nMessages.AZ": "Azerbaijan",
	"countriesL10nMessages.BA": "Bosnia a Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Gwlad Belg",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bwlgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Sant Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolifia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius a Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Y Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Ynysoedd Cocos (Keeling).",
	"countriesL10nMessages.CD": "Congo, Gweriniaeth Ddemocrataidd y",
	"countriesL10nMessages.CF": "Gweriniaeth Canolbarth Affrica",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Y Swistir",
	"countriesL10nMessages.CI": "Côte d'Ivoire",
	"countriesL10nMessages.CK": "Ynysoedd Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Cameroon",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cape Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Ynys y Nadolig",
	"countriesL10nMessages.CY": "Cyprus",
	"countriesL10nMessages.CZ": "Y Weriniaeth Tsiec",
	"countriesL10nMessages.DE": "Almaen",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Denmarc",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Gweriniaeth Dominica",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Yr Aifft",
	"countriesL10nMessages.EH": "Gorllewin y Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Sbaen",
	"countriesL10nMessages.ET": "Ethiopia",
	"countriesL10nMessages.FI": "Y Ffindir",
	"countriesL10nMessages.FJ": "Ffiji",
	"countriesL10nMessages.FK": "Ynysoedd Falkland",
	"countriesL10nMessages.FM": "Taleithiau Ffederal Micronesia",
	"countriesL10nMessages.FO": "Ynysoedd Faroe",
	"countriesL10nMessages.FR": "Ffrainc",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Y Deyrnas Unedig",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guiana Ffrengig",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Yr Ynys Las",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Guinea Gyhydeddol",
	"countriesL10nMessages.GR": "Groeg",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hwngari",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Iwerddon",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Ynys Manaw",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Tiriogaeth Cefnfor India Prydain",
	"countriesL10nMessages.IQ": "Irac",
	"countriesL10nMessages.IS": "Gwlad yr Iâ",
	"countriesL10nMessages.IT": "Yr Eidal",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Yr Iorddonen",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kyrgyzstan",
	"countriesL10nMessages.KH": "Cambodia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoros",
	"countriesL10nMessages.KN": "Sant Kitts a Nevis",
	"countriesL10nMessages.KR": "De Corea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Ynysoedd Cayman",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanus",
	"countriesL10nMessages.LC": "Sant Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lithwania",
	"countriesL10nMessages.LU": "Lwcsembwrg",
	"countriesL10nMessages.LV": "Latfia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Morocco",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldofa",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Sant Martin (rhan Ffrangeg)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Ynysoedd Marshall",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Ynys Gogledd Mariana",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldives",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mecsico",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Caledonia Newydd",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Ynys Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Yr Iseldiroedd",
	"countriesL10nMessages.NO": "Norwy",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Seland Newydd",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Periw",
	"countriesL10nMessages.PF": "Polynesia Ffrainc",
	"countriesL10nMessages.PG": "Papua Guinea Newydd",
	"countriesL10nMessages.PH": "Ynysoedd Philippines",
	"countriesL10nMessages.PK": "Pacistan",
	"countriesL10nMessages.PL": "Gwlad Pwyl",
	"countriesL10nMessages.PM": "Sant Pierre a Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portiwgal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rwmania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudi Arabia",
	"countriesL10nMessages.SB": "Ynysoedd Solomon",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Sweden",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Sant Helena",
	"countriesL10nMessages.SI": "Slofenia",
	"countriesL10nMessages.SJ": "Svalbard a Jan Mayen",
	"countriesL10nMessages.SK": "Slofacia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "De Swdan",
	"countriesL10nMessages.ST": "Sao Tome a Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Gwlad Swazi",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Ynysoedd Turks a Caicos",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Gwlad Thai",
	"countriesL10nMessages.TJ": "Tajikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tiwnisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Twrci",
	"countriesL10nMessages.TT": "Trinidad a Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Wcráin",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Yr Unol Daleithiau",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Yr Esgobaeth Sanctaidd (Dinas Wladwriaeth y Fatican)",
	"countriesL10nMessages.VC": "Saint Vincent a'r Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Ynysoedd y Wyryf, Prydeinig",
	"countriesL10nMessages.VI": "Ynysoedd y Wyryf, UDA",
	"countriesL10nMessages.VN": "Fietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis a Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "De Affrica",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Anfon copi o fy ymatebion drwy e-bost",
	"ToggleableEmailField.emailLabel": "Rhowch eich cyfeiriad e-bost"
};

var da = {
	"ActionMenuTagPin.PIN": "Fastgør",
	"ActionMenuTagPin.TAG": "Tag",
	"AddressField.selectCountry": "Vælg land",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Besvaret: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "Sp.{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Sprunget over: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Det synes folk:",
	"AsyncJobLoadingState.LOADING": "Indlæser besvarelser",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Vi informerer dig, når den er færdig",
	"AsyncJobLoadingState.TAGGING_TITLE": "Mærkning af besvarelser",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Tags er gældende for eksisterende og fremtidige besvarelser. Indsæt et mellemrum eller tast <strong>OR</strong> mellem ordene for at bruge mere end et nøgleord.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Mærk automatisk besvarelser",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Vælg en måned",
	"CalendarPicker.months": "{months, select, January {Januar} February {Februar} March {Marts} April {April} May {Maj} June {Juni} July {Juli} August {August} September {September} October {Oktober} November {November} December {December} other {andet}}",
	"CalendarPicker.nextMonth": "Næste måned",
	"CalendarPicker.previousMonth": "Forrige måned",
	"CalendarPicker.year": "Vælg et år",
	"ColorSwatches.TAG": "Tag {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Flerfarvede ordskyer bruger både farve og størrelse til at tilkendegive det antal gange, et ord eller vendinger blev nævnt.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Sådan bruger ordskyer farve",
	"ColorTab.DOWN_ARROW": "Vælg rullepil",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Flerfarvet",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "En farve",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Annuller",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Opret et nyt ord",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Det nye ord erstatter hver af de valgte ord nedenfor.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Rediger kombinerede ord",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Kombiner ord",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Opret et nyt ord, som erstatter et eller flere valgte ord. Du kan redigere dette senere for at få de oprindelige ord tilbage.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Indtast et hvilket som helst nyt ord",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Sammensæt",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Vælg ord, som skal sammensættes",
	"CreateTagFromSearch.CLOSE_MENU": "Annuller",
	"CreateTagFromSearch.DUPLICATED_NAME": "Du har allerede et tag med det pågældende navn.",
	"CreateTagFromSearch.NEW_TAG": "Nyt tag",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Ingen besvarelse \n\t\ttagges} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} besvarelse \n\t\t\t\n\t\ttagges} other {\n\t\t\t\t{responseTaggedFormatted} besvarelser \n\t\t\t\n\t\ttagges}}}}",
	"CreateTagFromSearch.SAVE": "Gem",
	"CreateTagFromSearch.TAG": "Tag",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Tagnavn må ikke være tomt.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Tagnavn må ikke kun være mellemrum.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Anvend tags på besvarelser, der matcher disse nøgleord. Vælg <strong>Opret tag</strong>. Vælg en farve og gem. Tagget anvendes på eksisterende og kommende besvarelser.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Besvarelser med matchende tag",
	"DashboardMenuContent.CREATE": "Opret",
	"DashboardMenuContent.NAME": "Navn",
	"DashboardMenuContent.NEW_DASHBOARD": "Nyt dashboard",
	"DateInputWrapper.dateButton": "Åbn kalender",
	"DateInputWrapper.dateFormatIntl": "DD/MM/ÅÅÅÅ",
	"DateInputWrapper.dateFormatUs": "MM/DD/ÅÅÅÅ",
	"DateInputWrapper.dateLabel": "Dato",
	"DateTime.errorEnterHours": "Angiv timer fra 0 til 23.",
	"DateTime.errorEnterMinutes": "Angiv minutter fra 0 til 59.",
	"DateTime.errorSelectPeriod": "Vælg AM/PM.",
	"DateTime.errorYearOutOfRange": "Angiv et årstal mellem 1900 og 9999.",
	"DateTime.formatErrorDateIntl": "Angiv dato i DD/MM/ÅÅÅÅ-format.",
	"DateTime.formatErrorDateUs": "Angiv dato i MM/DD/ÅÅÅÅ-format.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "Annuller",
	"EditorButtons.COPY_BUTTON": "KOPIÉR SPØRGSMÅL",
	"EditorButtons.MOVE_BUTTON": "Flyt spørgsmål",
	"EditorButtons.NEXT_QUESTION_BUTTON": "NÆSTE SPØRGSMÅL",
	"EditorButtons.SAVE_BUTTON": "Gem",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Datatendenser gælder ikke for dette spørgsmål",
	"FallbackComponent.ERROR": "Fejl under indlæsning af spørgsmål",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Vælg en fil</div> <div class={dragClass}>eller træk den her</div>",
	"FileUpload.fileUploaded": "Filen er uploadet",
	"FileUpload.fileUploading": "Uploader fil",
	"FileUpload.removeFile": "Fjern fil",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Erstat filen</div> <div class={dragClass}>eller træk den her</div>",
	"FileUpload.supportedFiles": "Understøttede filer",
	"FilterMenuContent.APPLY_ACTIONS": "Anvend",
	"FilterMenuContent.CLEAR_ACTIONS": "Ryd",
	"FilterMenuContent.CLOSE_MENU": "Annuller",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Annuller",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Skjul",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Gem",
	"HideWordsModal.HIDE_WORDS_HEADER": "Skjul ord",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Vælg ord for at skjule dem fra ordskyen.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Vælg ord, som skal skjules",
	"IconHelpStyled.HELP": "Hjælp",
	"IndividualResponseCard.NO_RESPONSE": "Respondent sprang dette spørgsmål over",
	"IndividualResponseCard.QUESTION_NUMBER": "Sp.{number}",
	"InfoBannerTagManagement.CLOSE": "Luk",
	"InfoBannerTagManagement.NEW": "Nyt",
	"InfoBannerTagManagement.TAG_RESPONSES": "Tag besvarelser",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Tilføj nøgleord for at oprette automatisk anvendelse af tags og organisere besvarelser.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Anvend tags på besvarelser for at gøre administrationen enkel",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positiv: {percentage,number,percent}} negative {Negativ: {percentage,number,percent}} neutral {Neutral: {percentage,number,percent}} unknown {Uopdaget: {percentage,number,percent}} other {andet}}",
	"LoadingState.LOADING": "Indlæser besvarelser",
	"MatrixDropdown.EmptyTopNativeSelect": "Foretag et valg",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Konfidensinterval",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Nytteværdiresultat",
	"Modal.CloseModal": "Luk værktøjstip",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {mandag} Tu {tirsdag} We {onsdag} Th {torsdag} Fr {fredag} Sa {lørdag} Su {søndag} other {andet}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {man.} Tu {tirs.} We {ons.} Th {tors.} Fr {fre.} Sa {lør.} Su {søn.} other {andet}}",
	"MultiColorSelectorMenu.APPLY": "Anvend",
	"MultiColorSelectorMenu.CANCEL": "Annuller",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Du kan organisere dine data i et brugerdefineret layout, forklare dine konklusioner og dele dit dashboard med hvem som helst. Du kan organisere dine data i et brugerdefineret layout, forklare dine konklusioner og dele dit dashboard med hvem som helst.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Dashboards",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Få mere at vide",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">På en skala fra {minValue} til {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} for {minLabelText}, {maxValue} for {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "målzone for resultater mellem {min} og {max}",
	"NpsGaugeChart.NPS_CHART": "NPS-målerdiagram",
	"NpsGaugeChart.NPS_SCORE": "NPS-score på {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "placering af diagrammets aksemærke for {score}-resultatet",
	"OrderControl.AriaNA": "Ikke relevant",
	"OrderControl.NA": "Ikke relevant",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "\"{title}\" besvarelser ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "skjul liste med andet svar",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "vis liste med andet svar",
	"PhoneField.countryCode": "Landekode",
	"QuestionCardItemFooter.FILTER_APPLIED": "Anvendte regler: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtrer",
	"QuestionCardTitle.ANSWERED": "Besvaret: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Valgt: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Sprunget over: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} point} other {{score}/{total} point}}",
	"QuizResultScore.SKIPPED": "Sprunget over",
	"RangeTab.RANGE_TAB_LABEL": "Omtaler: {value}",
	"RangeTab.TOTAL_WORDS": "ord i alt",
	"Ranking.AriaRankAtBottom": "{label} er rykket nederst på listen",
	"Ranking.AriaRankAtTop": "{label} er rykket øverst på listen",
	"Ranking.AriaRankMovedDown": "{label} er rykket ned til nr. {position}",
	"Ranking.AriaRankMovedUp": "{label} er rykket op til nr. {position}",
	"RankingItem.AriaRankMoveDown": "Ryk ned {label}",
	"RankingItem.AriaRankMoveUp": "Ryk op {label}",
	"RatingChart.AVERAGE_RATING": "gennemsnitlig vurdering",
	"RatingChartLegend.ANSWERED": "Besvaret: {number}",
	"RatingChartLegend.RATING": "{rating} stjerne",
	"RatingChartLegend.SKIPPED": "Sprunget over: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Anmod om funktion",
	"RequestAccessButton.UPGRADE": "Opgrader",
	"RespondentQuestionTitle.REQUIRED": "(påkrævet)",
	"ResponseItem.EXPAND_MENU_LABEL": "Udvid menu",
	"ResponseItem.MENU_LABEL": "Tilføj tags",
	"ResponseItem.RESPONDENT_ID": "Respondent-id",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Vis respondentens svar",
	"ResponseStatusBar.CLEAR": "Ryd",
	"ResponseStatusBar.FAILED_TO_LOAD": "Besvarelser blev ikke indlæst",
	"ResponseStatusBar.NO_RESPONSES_YET": "Ingen besvarelser endnu",
	"ResponseStatusBar.SEARCHING": "Søger ...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {positiv mening} negative {negativ mening} neutral {neutral mening} other {ukendt mening}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgt besvarelse for {searchTextFormatted} med {filters}   } other {Viser {resultsCountFormatted} besvarelse for {searchTextFormatted} med {filters}   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte besvarelser for {searchTextFormatted} med {filters}   } other {Viser {resultsCountFormatted} besvarelser for {searchTextFormatted} med {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgt besvarelse med {filters}   } other {Viser {resultsCountFormatted} besvarelse med {filters}   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte besvarelser med {filters}   } other {Viser {resultsCountFormatted} besvarelser med {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte besvarelse for {searchTextFormatted}   } other {Viser {resultsCountFormatted} besvarelse for {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte besvarelser for {searchTextFormatted}   } other {Viser {resultsCountFormatted} besvarelser for {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte besvarelse   } other {Viser {resultsCountFormatted} besvarelse   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte besvarelser   } other {Viser {resultsCountFormatted} besvarelser   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Ryd",
	"ResponseToolbar.CREATE_TAG": "Opret tag",
	"ResponseToolbar.SELECT_ALL": "Vælg alle besvarelser",
	"ResponseToolbar.UNSELECT_ALL": "Fravælg alle besvarelser",
	"SearchResponsesInput.CLEAR": "Ryd søgning",
	"SearchResponsesInput.SEARCH": "Søg",
	"SearchResponsesInput.SEARCH_RESPONSES": "Søg i besvarelser",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Brug anførselstegn <strong>“”</strong> til at søge efter en nøjagtig vending.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Indtast <strong>ELLER</strong> for at finde et af to eller flere søgeudtryk.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Indtast <b>AND</b> for at finde mere end et udtryk.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Brug <strong>IKKE</strong> for at udelukke et udtryk fra din søgning.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Indtast <strong>*</strong> efter et udtryk for at søge efter præfiks.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Brug parenteser for at kombinere AND/OR/NOT-baserede søgninger.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Indsæt <strong>~2</strong> efter et udtryk for at se resultater, som matcher udtrykket plus op til to andre tegn.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Søgeindstillinger",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Få mere at vide",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Nej tak",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Aktivér",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "<span id=\"link\">Automatisk indsigt</span> kan nu bruges i din konto. \n      Denne indstilling inkluderer enhver funktion, som bruger maskinel indlæring til at behandle dine spørgeundersøgelsesresultater og give dig viden tilbage.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Du kan altid ændre dine præferencer i <span id=\"link\">Min konto</span>",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Automatisk indsigt",
	"SentimentBanner.GENERATE_SENTIMENT": "Analysér mening",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Meningsanalyse",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Opdag meningen bag skriftlige besvarelser for at finde ud af, hvad respondenter synes.",
	"SentimentBanner.UPGRADE": "Opgrader",
	"SentimentBanner.WATCH_A_DEMO": "Se en demo",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Det synes folk:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtrer efter positiv mening} negative {Filtrer efter negativ mening} neutral {Filtrer efter neutral mening} other {Filtrer efter ukendt mening}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Udvid menu",
	"SentimentFilterMenu.MENU_LABEL": "efter mening",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Anmod om funktion",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Opdag følelsen og meningen bag skriftlige besvarelser.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Nyhed!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Vi præsenterer meningsanalyse",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Opgrader",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Se en demo",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Nej tak",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Anmod om funktion",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Opgrader",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Billede til opgradering til meningsanalyse",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Slå meningsanalyse til med et enkelt klik. Vi inddeler dine tekstbesvarelser som positive, neutrale eller negative for at vise den følelsesbetonede betydning, der ligger bag folks mening.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Find ud af med det samme hvad folk synes",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Meningsanalyse",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Meningsanalyse: TIL} false {Meningsanalyse: FRA} other {Meningsanalyse: andet}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Meningsanalyse: Indlæser ...",
	"SlideBoxNavigation.NEXT_BUTTON": "Næste",
	"SlideBoxNavigation.NEXT_SLIDE": "Næste dias",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "Forrige",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Forrige dias",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} af {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Ryd",
	"Slider.STEP_MESSAGE": "Vi har justeret det indtastede tal baseret på skydeknappens skala.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Besvaret: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "Sp.{questionNumber}{questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Sprunget over: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Skæringspunktet mellem billig og dyr giver en overordnet prisretning for dit produkt.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Prisfølsomhed (Van Westendorp)",
	"SrText.CORRECT": "Rigtigt",
	"SrText.INCORRECT": "Forkert",
	"StarRating.Heart": "{count, plural, one {{count} hjerte} other {{count} hjerter}}",
	"StarRating.STAR": "{count, plural, one {{count} stjerne} other {{count} stjerner}}",
	"StarRating.Smiley": "{count, plural, one {{count} smiley} other {{count} smileys}}",
	"StarRating.Thumb": "{count, plural, one {{count} tommelfinger} other {{count} tommelfingre}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Ikon for låsning af fanebladet Tags",
	"TabList.RESPONSES": "BESVARELSER ({responseCount})",
	"TabList.TAGS": "TAGS ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "TEMATISK ANALYSE",
	"TabList.WORD_CLOUD": "ORDSKY",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Udvid menu",
	"TagFilterMenu.MENU_LABEL": "Filter: efter tag",
	"TagList.TAGS": "Tags",
	"TagList.TAGS_KEYWORDS": "Nøgleord",
	"TagList.TAG_CONTROLS": "Tag-kontrolfunktioner",
	"TagList.TAG_NUMBER_PERCENT": "Procent af tags og antal tags",
	"TagList.TAG_PERCENTAGE_BAR": "Tag-procentdellinje",
	"TagListItem.DELETE": "Slet",
	"TagListItem.EDIT": "Rediger",
	"TagListItem.VIEW_ALL": "Vis alle",
	"TagListPanel.LOADING": "Indlæser tags",
	"TagListPanel.NEW_TAG": "+ Nyt tag",
	"TagListPanel.TAGS": "Tags",
	"TagMenuContent.APPLY_ACTIONS": "Anvend",
	"TagMenuContent.CLEAR_ACTIONS": "Ryd",
	"TagMenuContent.CLOSE_MENU": "Annuller",
	"TagMenuContent.DUPLICATED_NAME": "Du har allerede et tag med det pågældende navn.",
	"TagMenuContent.NEW_TAG": "Nyt tag",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Læs mere >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Anmod om funktion",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Opgrader",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Luk",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Søg, mærk og filtrer skriftlige besvarelser, og se ordskyer med hyppige ord og udtryk.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Tekstanalyse",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "tt",
	"TimeInputWrapper.hour": "time",
	"TimeInputWrapper.minutes": "Minutter",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Tid",
	"ToggleableEmailField.checkboxLabel": "Send en kopi af mine besvarelser via e-mail til mig",
	"ToggleableEmailField.emailLabel": "Indtast din e-mailadresse",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Slet",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {Tag af {responseCountFormatted} besvarelse bliver fjernet.} other {Tags af {responseCountFormatted} besvarelser bliver fjernet.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Når et tag slettes, bliver det slettet fra alle besvarelser, som er mærket, og det kan ikke fortrydes.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Vil du slette tag?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Gem",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Skal ændringerne gemmes?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Dette fjerner tagget fra alle besvarelser. Tagget anvendes igen på besvarelser, som \n      matcher de redigerede nøgleord.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Annuller",
	"UpgradeBanner.SUBTITLE": "Opgrader for at tilpasse din egen ordsky.",
	"UpgradeBanner.TITLE": "Synes du om det, du ser?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": " % af besvarelser",
	"VanWestendorpChartDataTable.PRICE": "Pris",
	"VanWestendorpChartDataTable.RESPONSES": "Besvarelser",
	"WordCloudCustomizationButton.CUSTOMIZE": "Tilpas",
	"WordCloudCustomizationFooter.APPLY": "Anvend",
	"WordCloudCustomizationFooter.CANCEL": "Annuller",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Tildel til alle skyer i spørgeundersøgelsen (nogen ændres måske ikke)",
	"WordCloudCustomizationFooter.NO_THANKS": "Nej tak",
	"WordCloudCustomizationFooter.REQUEST": "Anmod om funktion",
	"WordCloudCustomizationFooter.SAVE": "Gem",
	"WordCloudCustomizationFooter.UPGRADE": "Opgrader",
	"WordCloudCustomizationTabs.COLOR": "Farve",
	"WordCloudCustomizationTabs.FONT": "Skrifttype",
	"WordCloudCustomizationTabs.RANGE": "OMRÅDE",
	"WordCloudLegend.FEWER": "færre",
	"WordCloudLegend.MORE": "flere",
	"WordCloudLegend.TITLE": "Antal forekomster",
	"WordCloudListFooter.COMBINED_WORDS": "Kombinerede ord",
	"WordCloudListFooter.HIDDEN_WORDS": "Skjulte ord",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Udvid menu",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Sammensæt med",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Rediger kombinerede ord",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Udvid menu",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Skjul fra ordsky",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Del kombinerede ord",
	"WordCloudLoadingState.LOADING": "Indlæser ordsky ...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Luk-knap",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Få en forhåndvisning af tilpasning af ordskyer.",
	"WordCloudPreviewOverlay.TITLE": "Prøv det!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Skyvisning",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Listevisning",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Forekomster: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "Vigtigste ord nr. {number}",
	"WordCount.IN_PROGRESS": "Tekstanalyse undervejs ...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Der blev ikke fundet nogen vigtige ord.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Viser {count} ord } other {Viser {count} ord }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtrer efter positiv mening} negative {Filtrer efter negativ mening} neutral {Filtrer efter neutral mening} other {Filtrer efter ikke-registreret mening}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positiv} negative {Negativ} neutral {Neutral} other {Ukendt}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positiv: {percentage,number,percent}} negative {Negativ: {percentage,number,percent}} neutral {Neutral: {percentage,number,percent}} other {Ukendt: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "Annuller",
	"copyTagEditor.DUPLICATED_NAME": "Du har allerede et tag med det pågældende navn.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Indtast et navn på dette tag",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Angiv et eller flere nøgleord",
	"copyTagEditor.KEYWORDS": "<strong>Nøgleord</strong> (valgfrit)",
	"copyTagEditor.SAVE": "Gem",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Tag er gældende for alle besvarelser med nøgleord, der matcher",
	"copyTagEditor.TAG_NAME": "<strong>Tagnavn</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Tagnavn må ikke være tomt.",
	"copyTagEditor.TAG_NAME_SPACES": "Tagnavn må ikke kun være mellemrum.",
	"copyTagEditor.TAG_TOO_LONG": "Tag må højst være på 128 tegn.",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Forenede Arabiske Emirater",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua og Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanien",
	"countriesL10nMessages.AM": "Armenien",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Amerikansk Samoa",
	"countriesL10nMessages.AT": "Østrig",
	"countriesL10nMessages.AU": "Australien",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland",
	"countriesL10nMessages.AZ": "Aserbajdsjan",
	"countriesL10nMessages.BA": "Bosnien-Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgien",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgarien",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius og Saba",
	"countriesL10nMessages.BR": "Brasilien",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Cocosøerne (Keelingøerne)",
	"countriesL10nMessages.CD": "Demokratiske Republik Congo",
	"countriesL10nMessages.CF": "Den Centralafrikanske Republik",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Schweiz",
	"countriesL10nMessages.CI": "Elfenbenskysten",
	"countriesL10nMessages.CK": "Cookøerne",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Cameroun",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cap Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Christmas Island",
	"countriesL10nMessages.CY": "Cypern",
	"countriesL10nMessages.CZ": "Tjekkiet",
	"countriesL10nMessages.DE": "Tyskland",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Danmark",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Den Dominikanske Republik",
	"countriesL10nMessages.DZ": "Algeriet",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estland",
	"countriesL10nMessages.EG": "Egypten",
	"countriesL10nMessages.EH": "Vestsahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanien",
	"countriesL10nMessages.ET": "Etiopien",
	"countriesL10nMessages.FI": "Finland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falklandsøerne",
	"countriesL10nMessages.FM": "Mikronesien",
	"countriesL10nMessages.FO": "Færøerne",
	"countriesL10nMessages.FR": "Frankrig",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Storbritannien",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgien",
	"countriesL10nMessages.GF": "Fransk Guyana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grønland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Ækvatorialguinea",
	"countriesL10nMessages.GR": "Grækenland",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroatien",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungarn",
	"countriesL10nMessages.ID": "Indonesien",
	"countriesL10nMessages.IE": "Irland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Isle of Man",
	"countriesL10nMessages.IN": "Indien",
	"countriesL10nMessages.IO": "Det Britiske Territorium i Det Indiske Ocean",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italien",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirgisistan",
	"countriesL10nMessages.KH": "Cambodja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comorerne",
	"countriesL10nMessages.KN": "Saint Kitts og Nevis",
	"countriesL10nMessages.KR": "Sydkorea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Caymanøerne",
	"countriesL10nMessages.KZ": "Kasakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litauen",
	"countriesL10nMessages.LU": "Luxembourg",
	"countriesL10nMessages.LV": "Letland",
	"countriesL10nMessages.LY": "Libyen",
	"countriesL10nMessages.MA": "Marokko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint-Martin (den franske del)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshalløerne",
	"countriesL10nMessages.MK": "Makedonien",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongoliet",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Nordmarianerne",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauretanien",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldiverne",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexico",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Ny Kaledonien",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolkøerne",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Nederlandene",
	"countriesL10nMessages.NO": "Norge",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "New Zealand",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Fransk Polynesien",
	"countriesL10nMessages.PG": "Papua Ny Guinea",
	"countriesL10nMessages.PH": "Filippinerne",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polen",
	"countriesL10nMessages.PM": "Saint Pierre og Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palæstina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumænien",
	"countriesL10nMessages.RS": "Serbien",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudi-Arabien",
	"countriesL10nMessages.SB": "Salomonøerne",
	"countriesL10nMessages.SC": "Seychellerne",
	"countriesL10nMessages.SE": "Sverige",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Sankt Helena",
	"countriesL10nMessages.SI": "Slovenien",
	"countriesL10nMessages.SJ": "Svalbard og Jan Mayen",
	"countriesL10nMessages.SK": "Slovakiet",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sydsudan",
	"countriesL10nMessages.ST": "São Tomé og Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Saint Martin",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- og Caicosøerne",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tadsjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Østtimor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunesien",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Tyrkiet",
	"countriesL10nMessages.TT": "Trinidad og Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraine",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "USA",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Usbekistan",
	"countriesL10nMessages.VA": "Vatikanstaten",
	"countriesL10nMessages.VC": "Saint Vincent og Grenadinerne",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "De Britiske Jomfruøer",
	"countriesL10nMessages.VI": "Jomfruøer, De amerikanske",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis og Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Sydafrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Indifferent prispunkt",
	"helper.IPP": "Indifferent prispunkt (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Optimalt prispunkt (OPP)",
	"helper.OPP_DESCRIPTION": "Optimalt prispunkt",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Punkt for marginalt billigt (Point of marginal cheapness – PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Punkt for marginalt dyrt (Point of marginal expensiveness – PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Punkt for marginalt billigt (Point of marginal cheapness – PMC)",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Punkt for marginalt dyrt (Point of marginal expensiveness – PME)"
};

var de = {
	"ActionMenuTagPin.PIN": "Stecknadel",
	"ActionMenuTagPin.TAG": "Tag",
	"AddressField.selectCountry": "Land auswählen",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Beantwortet: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "F{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Übersprungen: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "So fühlen sich die Befragten:",
	"AsyncJobLoadingState.LOADING": "Beantwortungen werden geladen",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Wir sagen Ihnen Bescheid, wenn der Vorgang abgeschlossen ist.",
	"AsyncJobLoadingState.TAGGING_TITLE": "Tagging von Beantwortungen",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Tags gelten für vorhandene und künftige Beantwortungen. Lassen Sie zwischen den Wörtern ein Leerzeichen oder geben Sie <strong>OR</strong> ein, wenn Sie mehr als ein Schlüsselwort verwenden möchten.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Beantwortungen automatisch taggen",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Monat auswählen",
	"CalendarPicker.months": "{months, select, January {Januar} February {Februar} March {März} April {April} May {Mai} June {Juni} July {Juli} August {August} September {September} October {Oktober} November {November} December {Dezember} other {Sonstiges}}",
	"CalendarPicker.nextMonth": "Nächster Monat",
	"CalendarPicker.previousMonth": "Vorheriger Monat",
	"CalendarPicker.year": "Jahr auswählen",
	"ColorSwatches.TAG": "Mit {color} taggen",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Mehrfarbige Wortwolken verwenden sowohl Farben als auch Schriftgrößen, um die Anzahl der Nennungen eines Wortes oder Satzes anzuzeigen.",
	"ColorTab.COLOR_SELECTOR_TITLE": "So nutzen Wortwolken Farben",
	"ColorTab.DOWN_ARROW": "Dropdown-Pfeil auswählen",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Mehrfarbig",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Einfarbig",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Abbrechen",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Erstellen Sie ein neues Wort",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Das neue Wort ersetzt jedes der unten ausgewählten Wörter.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Kombinierte Wörter bearbeiten",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Wörter kombinieren",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Erstellen Sie ein neues Wort, das ein oder mehrere ausgewählte Wörter ersetzt. Sie können es später bearbeiten, um Ihre ursprünglichen Worte wiederherzustellen.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Geben Sie ein beliebiges neues Wort ein",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Kombinieren",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Wählen Sie Wörter zum Kombinieren aus",
	"CreateTagFromSearch.CLOSE_MENU": "Abbrechen",
	"CreateTagFromSearch.DUPLICATED_NAME": "Es ist bereits ein Tag mit diesem Namen vorhanden.",
	"CreateTagFromSearch.NEW_TAG": "Neuer Tag",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Keine Beantwortung \n\t\twird getaggt} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted}  Beantwortung \n\t\t\t\n\t\twird getaggt} other {\n\t\t\t\t{responseTaggedFormatted}  Beantwortungen \n\t\t\t\n\t\twerden getaggt}}}}",
	"CreateTagFromSearch.SAVE": "Speichern",
	"CreateTagFromSearch.TAG": "Tag",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Name des Tags darf nicht leer sein.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Name des Tags darf nicht nur aus Leerzeichen bestehen.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Taggen Sie Beantwortungen, die diesen Schlüsselwörtern entsprechen. Wählen Sie <strong>Tag erstellen</strong> aus. Wählen Sie eine Farbe und speichern Sie. Der Tag gilt für vorhandene und künftige Beantwortungen.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Taggen Sie übereinstimmende Beantwortungen",
	"DashboardMenuContent.CREATE": "Erstellen",
	"DashboardMenuContent.NAME": "Name",
	"DashboardMenuContent.NEW_DASHBOARD": "Neues Dashboard",
	"DateInputWrapper.dateButton": "Kalender öffnen",
	"DateInputWrapper.dateFormatIntl": "TT.MM.JJJJ",
	"DateInputWrapper.dateFormatUs": "MM/TT/JJJJ",
	"DateInputWrapper.dateLabel": "Datum",
	"DateTime.errorEnterHours": "Machen Sie eine Stundenangabe von 0 bis 23.",
	"DateTime.errorEnterMinutes": "Machen Sie eine Minutenangabe von 0 bis 59.",
	"DateTime.errorSelectPeriod": "Wählen Sie AM/PM aus.",
	"DateTime.errorYearOutOfRange": "Bitte geben Sie ein Jahr zwischen 1900 und 9999 an.",
	"DateTime.formatErrorDateIntl": "Geben Sie ein Datum im Format TT/MM/JJJJ ein.",
	"DateTime.formatErrorDateUs": "Geben Sie ein Datum im Format MM/TT/JJJJ ein.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "Abbrechen",
	"EditorButtons.COPY_BUTTON": "FRAGE KOPIEREN",
	"EditorButtons.MOVE_BUTTON": "FRAGE VERSCHIEBEN",
	"EditorButtons.NEXT_QUESTION_BUTTON": "Nächste Frage",
	"EditorButtons.SAVE_BUTTON": "Speichern",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Datentrends gelten nicht für diese Frage.",
	"FallbackComponent.ERROR": "Fehler beim Laden einer Frage",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Wählen Sie eine Datei aus</div> <div class={dragClass}>oder ziehen Sie sie hierher</div>",
	"FileUpload.fileUploaded": "Datei wurde erfolgreich hochgeladen",
	"FileUpload.fileUploading": "Datei wird hochgeladen",
	"FileUpload.removeFile": "Datei entfernen",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Ersetzen Sie die Datei</div> <div class={dragClass}>oder ziehen Sie sie hierher</div>",
	"FileUpload.supportedFiles": "Unterstützte Dateien",
	"FilterMenuContent.APPLY_ACTIONS": "Anwenden",
	"FilterMenuContent.CLEAR_ACTIONS": "Löschen",
	"FilterMenuContent.CLOSE_MENU": "Abbrechen",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Abbrechen",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Ausblenden",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Speichern",
	"HideWordsModal.HIDE_WORDS_HEADER": "Wörter ausblenden",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Wählen Sie Wörter aus, um sie aus der Wortwolke auszublenden.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Wählen Sie Wörter zum Ausblenden aus",
	"IconHelpStyled.HELP": "Hilfe",
	"IndividualResponseCard.NO_RESPONSE": "Vom Befragten übersprungene Frage",
	"IndividualResponseCard.QUESTION_NUMBER": "F{number}",
	"InfoBannerTagManagement.CLOSE": "Schließen",
	"InfoBannerTagManagement.NEW": "Neu",
	"InfoBannerTagManagement.TAG_RESPONSES": "Beantwortungen taggen",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Fügen Sie Schlüsselwörter hinzu, um automatische Tags einzurichten und die Beantwortungen zu organisieren.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Beantwortungen zur einfachen Verwaltung taggen",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positiv: {percentage,number,percent}} negative {Negativ: {percentage,number,percent}} neutral {Neutral: {percentage,number,percent}} unknown {Unerkannt: {percentage,number,percent}} other {Sonstiges}}",
	"LoadingState.LOADING": "Beantwortungen werden geladen",
	"MatrixDropdown.EmptyTopNativeSelect": "Wählen Sie eine Option aus",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Konfidenzintervall",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Nützlichkeitswert",
	"Modal.CloseModal": "QuickInfo schließen",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Montag} Tu {Dienstag} We {Mittwoch} Th {Donnerstag} Fr {Freitag} Sa {Samstag} Su {Sonntag} other {Sonstiges}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Mo} Tu {Di} We {Mi} Th {Do} Fr {Fr} Sa {Sa} Su {So} other {Sonstiges}}",
	"MultiColorSelectorMenu.APPLY": "Anwenden",
	"MultiColorSelectorMenu.CANCEL": "Abbrechen",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Ordnen Sie Ihre Daten in einem benutzerdefinierten Layout an und geben Sie Ihr Dashboard für beliebige Benutzer frei. Ordnen Sie Ihre Daten in einem benutzerdefinierten Layout an und geben Sie Ihr Dashboard für beliebige Benutzer frei.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Dashboards",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Weitere Infos",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">Auf einer Skala von {minValue} bis {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} für {minLabelText}, {maxValue} für {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "Tacho-Zone für Bewertungen zwischen {min} und {max}",
	"NpsGaugeChart.NPS_CHART": "NPS-Tacho-Diagramm",
	"NpsGaugeChart.NPS_SCORE": "NPS-Ergebnis {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "Position des Diagrammpunkts für das Ergebnis {score}",
	"OrderControl.AriaNA": "Nicht zutreffend",
	"OrderControl.NA": "k. A.",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "Beantwortungen für „{title}“ ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "Liste sonstiger Antworten ausblenden",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "Liste sonstiger Antworten anzeigen",
	"PhoneField.countryCode": "Land",
	"QuestionCardItemFooter.FILTER_APPLIED": "Angewendete Regeln: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filter",
	"QuestionCardTitle.ANSWERED": "Beantwortet: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Ausgewählt: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Übersprungen: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} Punkt} other {{score}/{total} Punkte}}",
	"QuizResultScore.SKIPPED": "Übersprungen",
	"RangeTab.RANGE_TAB_LABEL": "Erwähnungen: {value}",
	"RangeTab.TOTAL_WORDS": "Gesamtwortzahl",
	"Ranking.AriaRankAtBottom": "{label} wurde an das Ende der Liste verschoben",
	"Ranking.AriaRankAtTop": "{label} wurde an den Anfang der Liste verschoben",
	"Ranking.AriaRankMovedDown": "{label} wurde auf Nummer {position} nach unten verschoben",
	"Ranking.AriaRankMovedUp": "{label} wurde auf Nummer {position} nach oben verschoben",
	"RankingItem.AriaRankMoveDown": "{label} nach unten verschieben",
	"RankingItem.AriaRankMoveUp": "{label} nach oben verschieben",
	"RatingChart.AVERAGE_RATING": "Durchschnittliche Bewertung",
	"RatingChartLegend.ANSWERED": "Beantwortet: {number}",
	"RatingChartLegend.RATING": "{rating} Stern",
	"RatingChartLegend.SKIPPED": "Übersprungen: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Feature anfragen",
	"RequestAccessButton.UPGRADE": "Upgrade",
	"RespondentQuestionTitle.REQUIRED": "(Erforderlich.)",
	"ResponseItem.EXPAND_MENU_LABEL": "Menü ausklappen",
	"ResponseItem.MENU_LABEL": "Tags hinzufügen",
	"ResponseItem.RESPONDENT_ID": "Befragten-ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Antworten des oder der Befragten anzeigen",
	"ResponseStatusBar.CLEAR": "Löschen",
	"ResponseStatusBar.FAILED_TO_LOAD": "Beantwortungen konnten nicht geladen werden",
	"ResponseStatusBar.NO_RESPONSES_YET": "Noch keine Beantwortungen",
	"ResponseStatusBar.SEARCHING": "Wird gesucht ...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {positive Stimmung} negative {negative Stimmung} neutral {neutrale Stimmung} other {unbekannte Stimmung}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Es wird {resultsCountFormatted}  ausgewählte Beantwortung für {searchTextFormatted}   mit {filters} angezeigt   } other {Es wird {resultsCountFormatted}   Beantwortung   für {searchTextFormatted}   mit {filters} angezeigt   }}} other {{hasSelectedResponse, select, true {Es werden {resultsCountFormatted}  ausgewählte   Beantwortungen   für {searchTextFormatted}   mit {filters} angezeigt   } other {Es werden {resultsCountFormatted}   Beantwortungen   für {searchTextFormatted}   mit {filters} angezeigt   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Es wird {resultsCountFormatted}  ausgewählte   Beantwortung    mit {filters} angezeigt   } other {Es wird {resultsCountFormatted}   Beantwortung    mit {filters} angezeigt   }}} other {{hasSelectedResponse, select, true {Es werden {resultsCountFormatted}  ausgewählte   Beantwortungen    mit {filters} angezeigt   } other {Es werden {resultsCountFormatted}   Beantwortungen    mit {filters} angezeigt   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Es wird {resultsCountFormatted}  ausgewählte   Beantwortung   für {searchTextFormatted} angezeigt   } other {Es wird {resultsCountFormatted}   Beantwortung    für {searchTextFormatted} angezeigt   }}} other {{hasSelectedResponse, select, true {Es werden {resultsCountFormatted}  ausgewählte   Beantwortungen   für {searchTextFormatted} angezeigt   } other {Es werden {resultsCountFormatted}   Beantwortungen    für {searchTextFormatted} angezeigt   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Es wird {resultsCountFormatted}  ausgewählte   Beantwortung angezeigt   } other {Es wird {resultsCountFormatted}   Beantwortung angezeigt   }}} other {{hasSelectedResponse, select, true {Es werden {resultsCountFormatted}  ausgewählte   Beantwortungen angezeigt   } other {Es werden {resultsCountFormatted}   Beantwortungen angezeigt   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Löschen",
	"ResponseToolbar.CREATE_TAG": "Tag erstellen",
	"ResponseToolbar.SELECT_ALL": "Alle Beantwortungen auswählen",
	"ResponseToolbar.UNSELECT_ALL": "Auswahl aller Beantwortungen aufheben",
	"SearchResponsesInput.CLEAR": "Suche löschen",
	"SearchResponsesInput.SEARCH": "Suchen",
	"SearchResponsesInput.SEARCH_RESPONSES": "Beantwortungen durchsuchen",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Verwenden Sie Anführungszeichen <strong>„“</strong>, um nach einem genauen Ausdruck zu suchen.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Geben Sie <strong>OR</strong> ein, um einen von zwei oder mehr Suchbegriffen zu finden.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Geben Sie <b>AND</b> ein, um nach mehr als einem Begriff zu suchen.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Verwenden Sie <strong>NOT</strong>, um einen Begriff von Ihrer Suche auszuschließen.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Geben Sie <strong>*</strong> nach einem Begriff ein, um nach dem Präfix zu suchen.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Verwenden Sie Klammern, um Suchen, die auf „AND“, „OR“ oder „NOT“ basieren, zu kombinieren.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Fügen Sie <strong>~2</strong> nach einem Begriff ein, um Ergebnisse zu erhalten, die mit dem Begriff und bis zu zwei weiteren Zeichen übereinstimmen.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Suchoptionen",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Weitere Infos",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Nein danke",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Aktivieren",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "<span id=\"link\">Automatische Erkenntnisse</span> sind jetzt in Ihrem Konto eingeschaltet. \n      Diese Einstellung beinhaltet alle Features, die Machine Learning verwenden, um Ihre Umfrage zu verarbeiten und Ihnen Erkenntnisse zu liefern.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Sie können Ihre Einstellungen in <span id=\"link\">Mein Konto</span> ändern.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Automatische Erkenntnisse",
	"SentimentBanner.GENERATE_SENTIMENT": "Stimmung analysieren",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Stimmungsanalyse",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Analysieren Sie die Stimmung hinter den geschriebenen Beantwortungen, um zu erfahren, wie sich die Befragten fühlen.",
	"SentimentBanner.UPGRADE": "Upgrade",
	"SentimentBanner.WATCH_A_DEMO": "Demo ansehen",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "So fühlen sich die Befragten:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtern nach positiver Stimmung} negative {Filtern nach negativer Stimmung} neutral {Filtern nach neutraler Stimmung} other {Filtern nach unbekannter Stimmung}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Menü ausklappen",
	"SentimentFilterMenu.MENU_LABEL": "nach Stimmung",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Feature anfragen",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Erkennen Sie die Gefühle und Stimmungen hinter den geschriebenen Beantwortungen.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "NEU!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Einführung in die Stimmungsanalyse",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Upgrade",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Demo ansehen",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Nein danke",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Feature anfragen",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Upgrade",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Bild für Stimmungsaufhellung",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Schalten Sie die Stimmungsanalyse mit nur einem Klick ein. Wir klassifizieren Ihre schriftlichen Beantwortungen als positiv, neutral oder negativ, um die Stimmung der Befragten hinter ihren Antworten aufzudecken.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Sofort wissen, wie sich die Befragten fühlen",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Stimmungsanalyse",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Stimmungsanalyse: Ein} false {Stimmungsanalyse: Aus} other {Stimmungsanalyse: Sonstiges}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Stimmungsanalyse: Laden ...",
	"SlideBoxNavigation.NEXT_BUTTON": "Weiter",
	"SlideBoxNavigation.NEXT_SLIDE": "Nächste Folie",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "Zurück",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Vorherige Folie",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} von {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Löschen",
	"Slider.STEP_MESSAGE": "Wir haben die anhand der Skala des Schiebereglers eingegebene Zahl angepasst.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Beantwortet: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "F{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Übersprungen: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Die Schnittmenge von günstig und teuer gibt eine allgemeine Preisrichtung für Ihr Produkt vor.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Van-Westendorp-Preisanalyse",
	"SrText.CORRECT": "Richtig",
	"SrText.INCORRECT": "Falsch",
	"StarRating.Heart": "{count, plural, one {{count} Herz} other {{count} Herzen}}",
	"StarRating.STAR": "{count, plural, one {{count} Stern} other {{count} Sterne}}",
	"StarRating.Smiley": "{count, plural, one {{count} Smiley} other {{count} Smileys}}",
	"StarRating.Thumb": "{count, plural, one {{count} Daumen} other {{count} Daumen}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Sperrsymbol für die Registerkarte „Tags“",
	"TabList.RESPONSES": "Beantwortungen ({responseCount})",
	"TabList.TAGS": "Tags ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "Thematische Analyse",
	"TabList.WORD_CLOUD": "Wortwolke",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Menü ausklappen",
	"TagFilterMenu.MENU_LABEL": "Filter: nach Tag",
	"TagList.TAGS": "Tags",
	"TagList.TAGS_KEYWORDS": "Schlüsselwörter",
	"TagList.TAG_CONTROLS": "Tag-Kontrollen",
	"TagList.TAG_NUMBER_PERCENT": "Prozentsatz der Tags und Anzahl der Tags",
	"TagList.TAG_PERCENTAGE_BAR": "Prozentleiste für die Tags",
	"TagListItem.DELETE": "Löschen",
	"TagListItem.EDIT": "Bearbeiten",
	"TagListItem.VIEW_ALL": "Alle anzeigen",
	"TagListPanel.LOADING": "Tags werden geladen",
	"TagListPanel.NEW_TAG": "+ Neuer Tag",
	"TagListPanel.TAGS": "Tags",
	"TagMenuContent.APPLY_ACTIONS": "Anwenden",
	"TagMenuContent.CLEAR_ACTIONS": "Löschen",
	"TagMenuContent.CLOSE_MENU": "Abbrechen",
	"TagMenuContent.DUPLICATED_NAME": "Es ist bereits ein Tag mit diesem Namen vorhanden.",
	"TagMenuContent.NEW_TAG": "Neuer Tag",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Weitere Informationen >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Feature anfragen",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Upgrade",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Schließen",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Durchsuchen und filtern Sie geschriebene Beantwortungen, fügen Sie Tags hinzu und erhalten Sie Wortwolken mit häufig verwendeten Wörtern und Ausdrücken.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Textanalyse",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "HH",
	"TimeInputWrapper.hour": "Stunde",
	"TimeInputWrapper.minutes": "Minuten",
	"TimeInputWrapper.mmPlaceholder": "MM",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Zeit",
	"ToggleableEmailField.checkboxLabel": "Ich möchte eine Kopie meiner Antworten per E-Mail erhalten",
	"ToggleableEmailField.emailLabel": "Geben Sie Ihre E-Mail-Adresse ein",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Löschen",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted}  Beantwortung  wird nicht mehr getaggt.} other {{responseCountFormatted}  Beantwortungen  werden nicht mehr getaggt.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Durch das Löschen eines Tags wird es aus allen getaggten Beantwortungen entfernt. Dies kann nicht rückgängig gemacht werden.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Tag löschen?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Speichern",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Änderungen speichern?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Dadurch wird das Tag aus allen Beantwortungen entfernt. Das Tag wird wieder auf Beantwortungen angewendet, die \n      den bearbeiteten Schlüsselwörtern entsprechen.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Abbrechen",
	"UpgradeBanner.SUBTITLE": "Führen Sie ein Upgrade durch, um Ihre eigene Wortwolke anzupassen.",
	"UpgradeBanner.TITLE": "Gefällt Ihnen, was Sie sehen?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "Beantwortungen in %",
	"VanWestendorpChartDataTable.PRICE": "Preis",
	"VanWestendorpChartDataTable.RESPONSES": "Beantwortungen",
	"WordCloudCustomizationButton.CUSTOMIZE": "Anpassen",
	"WordCloudCustomizationFooter.APPLY": "Anwenden",
	"WordCloudCustomizationFooter.CANCEL": "Abbrechen",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Auf alle Wortwolken in der Umfrage anwenden (einige werden möglicherweise nicht geändert)",
	"WordCloudCustomizationFooter.NO_THANKS": "Nein danke",
	"WordCloudCustomizationFooter.REQUEST": "Feature anfragen",
	"WordCloudCustomizationFooter.SAVE": "Speichern",
	"WordCloudCustomizationFooter.UPGRADE": "Upgrade",
	"WordCloudCustomizationTabs.COLOR": "FARBE",
	"WordCloudCustomizationTabs.FONT": "SCHRIFTART",
	"WordCloudCustomizationTabs.RANGE": "Bereich",
	"WordCloudLegend.FEWER": "weniger",
	"WordCloudLegend.MORE": "mehr",
	"WordCloudLegend.TITLE": "Anzahl der Erwähnungen",
	"WordCloudListFooter.COMBINED_WORDS": "Kombinierte Wörter",
	"WordCloudListFooter.HIDDEN_WORDS": "Ausgeblendete Wörter",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Menü ausklappen",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Kombinieren mit",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Kombinierte Wörter bearbeiten",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Menü ausklappen",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "In der Wortwolke ausblenden",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Kombinierte Wörter trennen",
	"WordCloudLoadingState.LOADING": "Wortwolke wird geladen ...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Schaltfläche „Schließen“",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Sehen Sie sich die Vorschau der Features für die Anpassung von Wortwolken an.",
	"WordCloudPreviewOverlay.TITLE": "Jetzt testen!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Ansicht Wortwolke",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Listenansicht",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Instanzen: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}. wichtigstes Wort",
	"WordCount.IN_PROGRESS": "Textanalyse in Bearbeitung ...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Keine wichtigen Wörter gefunden.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Es wird {count}  Wort angezeigt } other {Es werden {count}  Wörter angezeigt }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtern nach positiver Stimmung} negative {Filtern nach negativer Stimmung} neutral {Filtern nach neutraler Stimmung} other {Filtern nach unerkannter Stimmung}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positiv} negative {Negativ} neutral {Neutral} other {Unbekannt}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positiv: {percentage,number,percent}} negative {Negativ: {percentage,number,percent}} neutral {Neutral: {percentage,number,percent}} other {Unbekannt: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "Abbrechen",
	"copyTagEditor.DUPLICATED_NAME": "Es ist bereits ein Tag mit diesem Namen vorhanden.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Geben Sie einen Namen für diesen Tag ein",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Geben Sie ein oder mehrere Schlüsselwörter ein",
	"copyTagEditor.KEYWORDS": "<strong>Schlüsselwörter</strong> (optional)",
	"copyTagEditor.SAVE": "Speichern",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Der Tag gilt für alle Beantwortungen mit passenden Schlüsselwörtern",
	"copyTagEditor.TAG_NAME": "<strong>Name des Tags</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Name des Tags darf nicht leer sein.",
	"copyTagEditor.TAG_NAME_SPACES": "Name des Tags darf nicht nur aus Leerzeichen bestehen.",
	"copyTagEditor.TAG_TOO_LONG": "Tag muss weniger als 128 Zeichen enthalten.",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Vereinigte Arabische Emirate",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua und Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanien",
	"countriesL10nMessages.AM": "Armenien",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentinien",
	"countriesL10nMessages.AS": "Amerikanisch-Samoa",
	"countriesL10nMessages.AT": "Österreich",
	"countriesL10nMessages.AU": "Australien",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland-Inseln",
	"countriesL10nMessages.AZ": "Aserbaidschan",
	"countriesL10nMessages.BA": "Bosnien-Herzegowina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesch",
	"countriesL10nMessages.BE": "Belgien",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgarien",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Sankt Bartholomäus",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivien",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius und Saba",
	"countriesL10nMessages.BR": "Brasilien",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botsuana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosinseln (Keelinginseln)",
	"countriesL10nMessages.CD": "Kongo, Demokratische Republik",
	"countriesL10nMessages.CF": "Zentralafrikanische Republik",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Schweiz",
	"countriesL10nMessages.CI": "Elfenbeinküste",
	"countriesL10nMessages.CK": "Cookinseln",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Kolumbien",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Kap Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Weihnachtsinsel",
	"countriesL10nMessages.CY": "Zypern",
	"countriesL10nMessages.CZ": "Tschechische Republik",
	"countriesL10nMessages.DE": "Deutschland",
	"countriesL10nMessages.DJ": "Dschibuti",
	"countriesL10nMessages.DK": "Dänemark",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Dominikanische Republik",
	"countriesL10nMessages.DZ": "Algerien",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estland",
	"countriesL10nMessages.EG": "Ägypten",
	"countriesL10nMessages.EH": "Westsahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanien",
	"countriesL10nMessages.ET": "Äthiopien",
	"countriesL10nMessages.FI": "Finnland",
	"countriesL10nMessages.FJ": "Fidschi",
	"countriesL10nMessages.FK": "Falklandinseln",
	"countriesL10nMessages.FM": "FÖDERIERTE STAATEN VON MIKRONESIEN",
	"countriesL10nMessages.FO": "Färöerinseln",
	"countriesL10nMessages.FR": "Frankreich",
	"countriesL10nMessages.GA": "Gabun",
	"countriesL10nMessages.GB": "Großbritannien",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgien",
	"countriesL10nMessages.GF": "Französisch-Guayana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grönland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Äquatorialguinea",
	"countriesL10nMessages.GR": "Griechenland",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroatien",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungarn",
	"countriesL10nMessages.ID": "Indonesien",
	"countriesL10nMessages.IE": "Irland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Insel Man",
	"countriesL10nMessages.IN": "Indien",
	"countriesL10nMessages.IO": "Britisches Territorium im Indischen Ozean",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italien",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Jordanien",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenia",
	"countriesL10nMessages.KG": "Kirgisistan",
	"countriesL10nMessages.KH": "Kambodscha",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoren",
	"countriesL10nMessages.KN": "St. Kitts und Nevis",
	"countriesL10nMessages.KR": "Südkorea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Kaimaninseln",
	"countriesL10nMessages.KZ": "Kasachstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "St. Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litauen",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Lettland",
	"countriesL10nMessages.LY": "Libyen",
	"countriesL10nMessages.MA": "Marokko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldawien",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint-Martin (französischer Teil)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshallinseln",
	"countriesL10nMessages.MK": "Mazedonien",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolei",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Nördliche Marianen",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauretanien",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Malediven",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexiko",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mosambik",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Neukaledonien",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolkinsel",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Niederlande",
	"countriesL10nMessages.NO": "Norwegen",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Neuseeland",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Französisch-Polynesien",
	"countriesL10nMessages.PG": "Papua-Neuguinea",
	"countriesL10nMessages.PH": "Philippinen",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polen",
	"countriesL10nMessages.PM": "St. Pierre und Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palästina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumänien",
	"countriesL10nMessages.RS": "Serbien",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudi-Arabien",
	"countriesL10nMessages.SB": "Salomonen",
	"countriesL10nMessages.SC": "Seychellen",
	"countriesL10nMessages.SE": "Schweden",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "St. Helena",
	"countriesL10nMessages.SI": "Slowenien",
	"countriesL10nMessages.SJ": "Svalbard und Jan Mayen",
	"countriesL10nMessages.SK": "Slowakei",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Südsudan",
	"countriesL10nMessages.ST": "Sao Tome und Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swasiland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- und Caicosinseln",
	"countriesL10nMessages.TD": "Tschad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tadschikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunesien",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Türkei",
	"countriesL10nMessages.TT": "Trinidad und Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tansania",
	"countriesL10nMessages.UA": "Ukraine",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "USA",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Usbekistan",
	"countriesL10nMessages.VA": "Staat Vatikanstadt",
	"countriesL10nMessages.VC": "Saint Vincent und die Grenadinen",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Britische Jungferninseln",
	"countriesL10nMessages.VI": "Amerikanische Jungferninseln",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis und Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Südafrika",
	"countriesL10nMessages.ZM": "Sambia",
	"countriesL10nMessages.ZW": "Simbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Indifferenzpreispunkt (IPP)",
	"helper.IPP": "Indifferenzpreispunkt (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Optimaler Preispunkt (OPP)",
	"helper.OPP_DESCRIPTION": "Optimaler Preispunkt",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Schwelle relativer Preiswürdigkeit (PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Schwelle relativer Teure (PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Schwelle relativer Preiswürdigkeit (PMC)",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Schwelle relativer Teure (PME)"
};

var el = {
	"AddressField.selectCountry": "Επιλογή χώρας",
	"PhoneField.countryCode": "Κωδικός χώρας",
	"countriesL10nMessages.AC": "Νήσος Αναλήψεως",
	"countriesL10nMessages.AD": "Ανδόρα",
	"countriesL10nMessages.AE": "Ηνωμένα Αραβικά Εμιράτα",
	"countriesL10nMessages.AF": "Αφγανιστάν",
	"countriesL10nMessages.AG": "Αντίγκουα και Μπαρμπούντα",
	"countriesL10nMessages.AI": "Ανγκουίλα",
	"countriesL10nMessages.AL": "Αλβανία",
	"countriesL10nMessages.AM": "Αρμενία",
	"countriesL10nMessages.AO": "Αγκόλα",
	"countriesL10nMessages.AR": "Αργεντινή",
	"countriesL10nMessages.AS": "Αμερικάνικη Σαμόα",
	"countriesL10nMessages.AT": "Αυστρία",
	"countriesL10nMessages.AU": "Αυστραλία",
	"countriesL10nMessages.AW": "Αρούμπα",
	"countriesL10nMessages.AX": "Νησιά Åland",
	"countriesL10nMessages.AZ": "Αζερμπαϊτζάν",
	"countriesL10nMessages.BA": "Βοσνία και Ερζεγοβίνη",
	"countriesL10nMessages.BB": "Μπαρμπάντος",
	"countriesL10nMessages.BD": "Μπαγκλαντές",
	"countriesL10nMessages.BE": "Βέλγιο",
	"countriesL10nMessages.BF": "Μπουρκίνα Φάσο",
	"countriesL10nMessages.BG": "Βουλγαρία",
	"countriesL10nMessages.BH": "Μπαχρέιν",
	"countriesL10nMessages.BI": "Μπουρούντι",
	"countriesL10nMessages.BJ": "Μπενίν",
	"countriesL10nMessages.BL": "Άγιος Βαρθολομαίος",
	"countriesL10nMessages.BM": "Βερμούδα",
	"countriesL10nMessages.BN": "Μπρουνάι Νταρουσαλάμ",
	"countriesL10nMessages.BO": "Βολιβία",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius και Saba",
	"countriesL10nMessages.BR": "Βραζιλία",
	"countriesL10nMessages.BS": "Μπαχάμες",
	"countriesL10nMessages.BT": "Μπουτάν",
	"countriesL10nMessages.BW": "Μποτσουάνα",
	"countriesL10nMessages.BZ": "Μπελίζ",
	"countriesL10nMessages.CA": "Καναδάς",
	"countriesL10nMessages.CC": "Νησιά Cocos (Keeling)",
	"countriesL10nMessages.CD": "Κονγκό, Λαϊκή Δημοκρατία του",
	"countriesL10nMessages.CF": "Δημοκρατία Κεντρικής Αφρικής",
	"countriesL10nMessages.CG": "Κογκό",
	"countriesL10nMessages.CH": "Ελβετία",
	"countriesL10nMessages.CI": "Ακτή Ελεφαντοστού",
	"countriesL10nMessages.CK": "Νήσοι Κουκ",
	"countriesL10nMessages.CL": "Χιλή",
	"countriesL10nMessages.CM": "Καμερούν",
	"countriesL10nMessages.CN": "Κίνα",
	"countriesL10nMessages.CO": "Κολομβία",
	"countriesL10nMessages.CR": "Κόστα Ρίκα",
	"countriesL10nMessages.CV": "Πράσινο Ακρωτήριο",
	"countriesL10nMessages.CW": "Κουρασάο",
	"countriesL10nMessages.CX": "Νησί των Χριστουγέννων",
	"countriesL10nMessages.CY": "Κύπρος",
	"countriesL10nMessages.CZ": "Δημοκρατία της Τσεχίας",
	"countriesL10nMessages.DE": "Γερμανία",
	"countriesL10nMessages.DJ": "Τζιμπουτί",
	"countriesL10nMessages.DK": "Δανία",
	"countriesL10nMessages.DM": "Δομίνικα",
	"countriesL10nMessages.DO": "Δομινικανική Δημοκρατία",
	"countriesL10nMessages.DZ": "Αλγερία",
	"countriesL10nMessages.EC": "Εκουαδόρ",
	"countriesL10nMessages.EE": "Εσθονία",
	"countriesL10nMessages.EG": "Αίγυπτος",
	"countriesL10nMessages.EH": "Δυτική Σαχάρα",
	"countriesL10nMessages.ER": "Ερυθραία",
	"countriesL10nMessages.ES": "Ισπανία",
	"countriesL10nMessages.ET": "Αιθιοπία",
	"countriesL10nMessages.FI": "Φινλανδία",
	"countriesL10nMessages.FJ": "Φίτζι",
	"countriesL10nMessages.FK": "Νησιά Φόκλαντ",
	"countriesL10nMessages.FM": "Ομόσπονδες Πολιτείες της Μικρονησίας",
	"countriesL10nMessages.FO": "Νήσοι Φαρόε",
	"countriesL10nMessages.FR": "Γαλλία",
	"countriesL10nMessages.GA": "Γκαμπόν",
	"countriesL10nMessages.GB": "Ηνωμένο Βασίλειο",
	"countriesL10nMessages.GD": "Γρενάδα",
	"countriesL10nMessages.GE": "Γεωργία",
	"countriesL10nMessages.GF": "Γαλλική Γουιάνα",
	"countriesL10nMessages.GG": "Γκέρνσεϊ",
	"countriesL10nMessages.GH": "Γκάνα",
	"countriesL10nMessages.GI": "Γιβραλτάρ",
	"countriesL10nMessages.GL": "Γροιλανδία",
	"countriesL10nMessages.GM": "Γκάμπια",
	"countriesL10nMessages.GN": "Γουινέα",
	"countriesL10nMessages.GP": "Γουαδελούπη",
	"countriesL10nMessages.GQ": "Ισημερινή Γουινέα",
	"countriesL10nMessages.GR": "Ελλάδα",
	"countriesL10nMessages.GT": "Γουατεμάλα",
	"countriesL10nMessages.GU": "Γκουάμ",
	"countriesL10nMessages.GW": "Γουινέα-Μπισάου",
	"countriesL10nMessages.GY": "Γουιάνα",
	"countriesL10nMessages.HK": "Χονγκ Κονγκ",
	"countriesL10nMessages.HN": "Ονδούρα",
	"countriesL10nMessages.HR": "Κροατία",
	"countriesL10nMessages.HT": "Αϊτή",
	"countriesL10nMessages.HU": "Ουγγαρία",
	"countriesL10nMessages.ID": "Ινδονησία",
	"countriesL10nMessages.IE": "Ιρλανδία",
	"countriesL10nMessages.IL": "Ισραήλ",
	"countriesL10nMessages.IM": "Νήσος Μαν",
	"countriesL10nMessages.IN": "Ινδία",
	"countriesL10nMessages.IO": "Βρετανική επικράτεια Ινδικού Ωκεανού",
	"countriesL10nMessages.IQ": "Ιράκ",
	"countriesL10nMessages.IS": "Ισλανδία",
	"countriesL10nMessages.IT": "Ιταλία",
	"countriesL10nMessages.JE": "Τζέρσεϊ",
	"countriesL10nMessages.JM": "Τζαμάικα",
	"countriesL10nMessages.JO": "Ιορδανία",
	"countriesL10nMessages.JP": "Ιαπωνία",
	"countriesL10nMessages.KE": "Κένυα",
	"countriesL10nMessages.KG": "Κιργιστάν",
	"countriesL10nMessages.KH": "Καμπότζη",
	"countriesL10nMessages.KI": "Κιριμπάτι",
	"countriesL10nMessages.KM": "Κομόρες",
	"countriesL10nMessages.KN": "Άγιος Χριστόφορος και Νέβις",
	"countriesL10nMessages.KR": "Νότια Κορέα",
	"countriesL10nMessages.KW": "Κουβέιτ",
	"countriesL10nMessages.KY": "Νήσοι Κέιμαν",
	"countriesL10nMessages.KZ": "Καζακστάν",
	"countriesL10nMessages.LA": "Λάος",
	"countriesL10nMessages.LB": "Λίβανος",
	"countriesL10nMessages.LC": "Αγία Λουκία",
	"countriesL10nMessages.LI": "Λιχτενστάιν",
	"countriesL10nMessages.LK": "Σρι Λάνκα",
	"countriesL10nMessages.LR": "Λιβερία",
	"countriesL10nMessages.LS": "Λεσότο",
	"countriesL10nMessages.LT": "Λιθουανία",
	"countriesL10nMessages.LU": "Λουξεμβούργο",
	"countriesL10nMessages.LV": "Λετονία",
	"countriesL10nMessages.LY": "Λιβύη",
	"countriesL10nMessages.MA": "Μαρόκο",
	"countriesL10nMessages.MC": "Μονακό",
	"countriesL10nMessages.MD": "Μολδαβία",
	"countriesL10nMessages.ME": "Μαυροβούνιο",
	"countriesL10nMessages.MF": "Άγιος Μαρτίνος (γαλλικό τμήμα)",
	"countriesL10nMessages.MG": "Μαδαγασκάρη",
	"countriesL10nMessages.MH": "Νήσοι Μάρσαλ",
	"countriesL10nMessages.MK": "Βόρεια Μακεδονία",
	"countriesL10nMessages.ML": "Μάλι",
	"countriesL10nMessages.MM": "Μιανμάρ",
	"countriesL10nMessages.MN": "Μογγολία",
	"countriesL10nMessages.MO": "Μακάο",
	"countriesL10nMessages.MP": "Νησί Βόρεια Μαριάνα",
	"countriesL10nMessages.MQ": "Μαρτινίκα",
	"countriesL10nMessages.MR": "Μαυριτανία",
	"countriesL10nMessages.MS": "Μονσεράτ",
	"countriesL10nMessages.MT": "Μάλτα",
	"countriesL10nMessages.MU": "Μαυρίκιος",
	"countriesL10nMessages.MV": "Μαλδίβες",
	"countriesL10nMessages.MW": "Μαλάουι",
	"countriesL10nMessages.MX": "Μεξικό",
	"countriesL10nMessages.MY": "Μαλαισία",
	"countriesL10nMessages.MZ": "Μοζαμβίκη",
	"countriesL10nMessages.NA": "Ναμίμπια",
	"countriesL10nMessages.NC": "Νέα Καληδονία",
	"countriesL10nMessages.NE": "Νίγηρας",
	"countriesL10nMessages.NF": "Νήσοι Κουκ",
	"countriesL10nMessages.NG": "Νιγηρία",
	"countriesL10nMessages.NI": "Νικαράγουα",
	"countriesL10nMessages.NL": "Ολλανδία",
	"countriesL10nMessages.NO": "Νορβηγία",
	"countriesL10nMessages.NP": "Νεπάλ",
	"countriesL10nMessages.NR": "Ναούρου",
	"countriesL10nMessages.NU": "Νίουε",
	"countriesL10nMessages.NZ": "Νέα Ζηλανδία",
	"countriesL10nMessages.OM": "Ομάν",
	"countriesL10nMessages.PA": "Παναμάς",
	"countriesL10nMessages.PE": "Περού",
	"countriesL10nMessages.PF": "Γαλλική Πολυνησία",
	"countriesL10nMessages.PG": "Παπούα Νέα Γουινέα",
	"countriesL10nMessages.PH": "Φιλιππίνες",
	"countriesL10nMessages.PK": "Πακιστάν",
	"countriesL10nMessages.PL": "Πολωνία",
	"countriesL10nMessages.PM": "Σεν Πιερ και Μικελόν",
	"countriesL10nMessages.PR": "Πουέρτο Ρίκο",
	"countriesL10nMessages.PS": "Παλαιστίνη",
	"countriesL10nMessages.PT": "Πορτογαλία",
	"countriesL10nMessages.PW": "Παλάου",
	"countriesL10nMessages.PY": "Παραγουάη",
	"countriesL10nMessages.QA": "Κατάρ",
	"countriesL10nMessages.RE": "Ρεουνιόν",
	"countriesL10nMessages.RO": "Ρουμανία",
	"countriesL10nMessages.RS": "Σερβία",
	"countriesL10nMessages.RW": "Ρουάντα",
	"countriesL10nMessages.SA": "Σαουδική Αραβία",
	"countriesL10nMessages.SB": "Νήσοι Σολωμόντος",
	"countriesL10nMessages.SC": "Σεϋχέλλες",
	"countriesL10nMessages.SE": "Σουηδία",
	"countriesL10nMessages.SG": "Σιγκαπούρη",
	"countriesL10nMessages.SH": "Αγία Ελένη",
	"countriesL10nMessages.SI": "Σλοβενία",
	"countriesL10nMessages.SJ": "Σβάλμπαρντ και Σαν Μαγιέν",
	"countriesL10nMessages.SK": "Σλοβακία",
	"countriesL10nMessages.SL": "Σιέρρα Λεόνε",
	"countriesL10nMessages.SM": "Άγιος Μαρίνος",
	"countriesL10nMessages.SN": "Σενεγάλη",
	"countriesL10nMessages.SO": "Σομαλία",
	"countriesL10nMessages.SR": "Σουρινάμ",
	"countriesL10nMessages.SS": "Νότιο Σουδάν",
	"countriesL10nMessages.ST": "Σάο Τομέ και Πρίνσιπε",
	"countriesL10nMessages.SV": "Ελ Σαλβαδόρ",
	"countriesL10nMessages.SX": "Άγιος Μαρτίνος",
	"countriesL10nMessages.SZ": "Σουαζιλάνδη",
	"countriesL10nMessages.TA": "Τριστάν ντα Κούνια",
	"countriesL10nMessages.TC": "Νήσοι Τερκς και Κάικος",
	"countriesL10nMessages.TD": "Τσαντ",
	"countriesL10nMessages.TG": "Τόγκο",
	"countriesL10nMessages.TH": "Ταϊλάνδη",
	"countriesL10nMessages.TJ": "Τατζικιστάν",
	"countriesL10nMessages.TK": "Τοκελάου",
	"countriesL10nMessages.TL": "Τιμόρ Λέστε",
	"countriesL10nMessages.TM": "Τουρκμενιστάν",
	"countriesL10nMessages.TN": "Τυνησία",
	"countriesL10nMessages.TO": "Τόνγκα",
	"countriesL10nMessages.TR": "Τουρκία",
	"countriesL10nMessages.TT": "Τρινιντάντ και Τομπάγκο",
	"countriesL10nMessages.TV": "Τουβαλού",
	"countriesL10nMessages.TW": "Ταϊβάν",
	"countriesL10nMessages.TZ": "Τανζανία",
	"countriesL10nMessages.UA": "Ουκρανία",
	"countriesL10nMessages.UG": "Ουγκάντα",
	"countriesL10nMessages.US": "Η.Π.Α.",
	"countriesL10nMessages.UY": "Ουρουγουάη",
	"countriesL10nMessages.UZ": "Ουζμπεκιστάν",
	"countriesL10nMessages.VA": "Αγία Έδρα (Κράτος της Πόλης του Βατικανού)",
	"countriesL10nMessages.VC": "Άγιος Βικέντιος και Γρεναδίνες",
	"countriesL10nMessages.VE": "Βενεζουέλα",
	"countriesL10nMessages.VG": "Παρθένες Νήσοι, Βρετανικές",
	"countriesL10nMessages.VI": "Παρθένοι Νήσοι, Η.Π.Α.",
	"countriesL10nMessages.VN": "Βιετνάμ",
	"countriesL10nMessages.VU": "Βανουάτου",
	"countriesL10nMessages.WF": "Γουόλις και Φουτούνα",
	"countriesL10nMessages.WS": "Σαμόα",
	"countriesL10nMessages.XK": "Κοσσυφοπέδιο",
	"countriesL10nMessages.YE": "Υεμένη",
	"countriesL10nMessages.YT": "Μαγιότ",
	"countriesL10nMessages.ZA": "Νότια Αφρική",
	"countriesL10nMessages.ZM": "Ζάμπια",
	"countriesL10nMessages.ZW": "Ζιμπάμπουε",
	"ToggleableEmailField.checkboxLabel": "Θέλω να λάβω αντίγραφο των απαντήσεών μου μέσω email",
	"ToggleableEmailField.emailLabel": "Καταχωρήστε τη διεύθυνση email σας"
};

var es = {
	"AddressField.selectCountry": "Seleccionar país",
	"PhoneField.countryCode": "Código de país",
	"countriesL10nMessages.AC": "Isla Ascensión",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emiratos Árabes Unidos",
	"countriesL10nMessages.AF": "Afganistán",
	"countriesL10nMessages.AG": "Antigua y Barbuda",
	"countriesL10nMessages.AI": "Anguila",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Americana",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Islas de Aland",
	"countriesL10nMessages.AZ": "Azerbaiyán",
	"countriesL10nMessages.BA": "Bosnia-Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladés",
	"countriesL10nMessages.BE": "Bélgica",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahréin",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benín",
	"countriesL10nMessages.BL": "San Bartolomé",
	"countriesL10nMessages.BM": "Bermudas",
	"countriesL10nMessages.BN": "Brunéi Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, San Eustaquio y Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bután",
	"countriesL10nMessages.BW": "Botsuana",
	"countriesL10nMessages.BZ": "Belice",
	"countriesL10nMessages.CA": "Canadá",
	"countriesL10nMessages.CC": "Islas Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, República Democrática del",
	"countriesL10nMessages.CF": "República Centroafricana",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Suiza",
	"countriesL10nMessages.CI": "Costa de Marfil",
	"countriesL10nMessages.CK": "Islas Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Camerún",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cabo Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Isla de Navidad",
	"countriesL10nMessages.CY": "Chipre",
	"countriesL10nMessages.CZ": "República Checa",
	"countriesL10nMessages.DE": "Alemania",
	"countriesL10nMessages.DJ": "Yibuti",
	"countriesL10nMessages.DK": "Dinamarca",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "República Dominicana",
	"countriesL10nMessages.DZ": "Argelia",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egipto",
	"countriesL10nMessages.EH": "Sahara Occidental",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "España",
	"countriesL10nMessages.ET": "Etiopía",
	"countriesL10nMessages.FI": "Finlandia",
	"countriesL10nMessages.FJ": "Fiyi",
	"countriesL10nMessages.FK": "Islas Malvinas",
	"countriesL10nMessages.FM": "Estados Federados de Micronesia",
	"countriesL10nMessages.FO": "Islas Feroe",
	"countriesL10nMessages.FR": "Francia",
	"countriesL10nMessages.GA": "Gabón",
	"countriesL10nMessages.GB": "Reino Unido",
	"countriesL10nMessages.GD": "Granada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guayana Francesa",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenlandia",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadalupe",
	"countriesL10nMessages.GQ": "Guinea Ecuatorial",
	"countriesL10nMessages.GR": "Grecia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croacia",
	"countriesL10nMessages.HT": "Haití",
	"countriesL10nMessages.HU": "Hungría",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Isla de Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Territorio británico del océano Índico",
	"countriesL10nMessages.IQ": "Iraq",
	"countriesL10nMessages.IS": "Islandia",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordania",
	"countriesL10nMessages.JP": "Japón",
	"countriesL10nMessages.KE": "Kenia",
	"countriesL10nMessages.KG": "Kirguistán",
	"countriesL10nMessages.KH": "Camboya",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoras",
	"countriesL10nMessages.KN": "San Cristóbal y Nieves",
	"countriesL10nMessages.KR": "Corea del Sur",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Islas Caimán",
	"countriesL10nMessages.KZ": "Kazajistán",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Líbano",
	"countriesL10nMessages.LC": "Santa Lucía",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Lituania",
	"countriesL10nMessages.LU": "Luxemburgo",
	"countriesL10nMessages.LV": "Letonia",
	"countriesL10nMessages.LY": "Libia",
	"countriesL10nMessages.MA": "Marruecos",
	"countriesL10nMessages.MC": "Mónaco",
	"countriesL10nMessages.MD": "Moldavia",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "San Martín (parte francesa)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Islas Marshall",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Birmania",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Islas Marianas del Norte",
	"countriesL10nMessages.MQ": "Martinica",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauricio",
	"countriesL10nMessages.MV": "Maldivas",
	"countriesL10nMessages.MW": "Malaui",
	"countriesL10nMessages.MX": "México",
	"countriesL10nMessages.MY": "Malasia",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Nueva Caledonia",
	"countriesL10nMessages.NE": "Níger",
	"countriesL10nMessages.NF": "Isla Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Países Bajos",
	"countriesL10nMessages.NO": "Noruega",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nueva Zelanda",
	"countriesL10nMessages.OM": "Omán",
	"countriesL10nMessages.PA": "Panamá",
	"countriesL10nMessages.PE": "Perú",
	"countriesL10nMessages.PF": "Polinesia Francesa",
	"countriesL10nMessages.PG": "Papúa Nueva Guinea",
	"countriesL10nMessages.PH": "Filipinas",
	"countriesL10nMessages.PK": "Pakistán",
	"countriesL10nMessages.PL": "Polonia",
	"countriesL10nMessages.PM": "San Pedro y Miguelón",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palaos",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Reunión",
	"countriesL10nMessages.RO": "Rumania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Arabia Saudita",
	"countriesL10nMessages.SB": "Islas Salomón",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Suecia",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Santa Helena",
	"countriesL10nMessages.SI": "Eslovenia",
	"countriesL10nMessages.SJ": "Svalbard y Jan Mayen",
	"countriesL10nMessages.SK": "Eslovaquia",
	"countriesL10nMessages.SL": "Sierra Leona",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sudán del Sur",
	"countriesL10nMessages.ST": "Santo Tomé y Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Suazilandia",
	"countriesL10nMessages.TA": "Tristán de Acuña",
	"countriesL10nMessages.TC": "Islas Turcas y Caicos",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tailandia",
	"countriesL10nMessages.TJ": "Tayikistán",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor Leste",
	"countriesL10nMessages.TM": "Turkmenistán",
	"countriesL10nMessages.TN": "Túnez",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turquía",
	"countriesL10nMessages.TT": "Trinidad y Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwán",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ucrania",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Estados Unidos",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistán",
	"countriesL10nMessages.VA": "Santa Sede (Ciudad del Vaticano)",
	"countriesL10nMessages.VC": "San Vicente y las Granadinas",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Islas Vírgenes Británicas",
	"countriesL10nMessages.VI": "Islas Vírgenes de los EE. UU.",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Islas Wallis y Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Sudáfrica",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabue",
	"ToggleableEmailField.checkboxLabel": "Quiero recibir una copia de mis respuestas por correo electrónico",
	"ToggleableEmailField.emailLabel": "Introduce tu dirección de correo electrónico"
};

var et = {
	"AddressField.selectCountry": "Valige riik",
	"PhoneField.countryCode": "Riigikood",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Araabia Ühendemiraadid",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua ja Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albaania",
	"countriesL10nMessages.AM": "Armeenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Ameerika Samoa",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Austraalia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ahvenamaa",
	"countriesL10nMessages.AZ": "Aserbaidžaan",
	"countriesL10nMessages.BA": "Bosnia ja Hertsegoviina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgia",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaaria",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalami Riik",
	"countriesL10nMessages.BO": "Boliivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius ja Saba",
	"countriesL10nMessages.BR": "Brasiilia",
	"countriesL10nMessages.BS": "Bahama",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kookossaared",
	"countriesL10nMessages.CD": "Kongo Demokraatlik Vabariik",
	"countriesL10nMessages.CF": "Kesk-Aafrika Vabariik",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Šveits",
	"countriesL10nMessages.CI": "Elevandiluurannik",
	"countriesL10nMessages.CK": "Cooki saared",
	"countriesL10nMessages.CL": "Tšiili",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Hiina",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Roheneemesaared",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Jõulusaar",
	"countriesL10nMessages.CY": "Küpros",
	"countriesL10nMessages.CZ": "Tšehhi Vabariik",
	"countriesL10nMessages.DE": "Saksamaa",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Taani",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Dominikaani Vabariik",
	"countriesL10nMessages.DZ": "Alžeeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Eesti",
	"countriesL10nMessages.EG": "Egiptus",
	"countriesL10nMessages.EH": "Lääne-Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Hispaania",
	"countriesL10nMessages.ET": "Etioopia",
	"countriesL10nMessages.FI": "Soome",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Falklandi saared",
	"countriesL10nMessages.FM": "Mikroneesia Liiduriigid",
	"countriesL10nMessages.FO": "Fääri saared",
	"countriesL10nMessages.FR": "Prantsusmaa",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Ühendkuningriik",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruusia",
	"countriesL10nMessages.GF": "Prantsuse Guajaana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Gröönimaa",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Ekvatoriaal-Guinea",
	"countriesL10nMessages.GR": "Kreeka",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Horvaatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungari",
	"countriesL10nMessages.ID": "Indoneesia",
	"countriesL10nMessages.IE": "Iirimaa",
	"countriesL10nMessages.IL": "Iisrael",
	"countriesL10nMessages.IM": "Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Briti India ookeani ala",
	"countriesL10nMessages.IQ": "Iraak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Itaalia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordaania",
	"countriesL10nMessages.JP": "Jaapan",
	"countriesL10nMessages.KE": "Keenia",
	"countriesL10nMessages.KG": "Kõrgõzstan",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoorid",
	"countriesL10nMessages.KN": "Saint Kitts ja Nevis",
	"countriesL10nMessages.KR": "Lõuna-Korea",
	"countriesL10nMessages.KW": "Kuveit",
	"countriesL10nMessages.KY": "Kaimanisaared",
	"countriesL10nMessages.KZ": "Kasahstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Liibanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Libeeria",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Leedu",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Läti",
	"countriesL10nMessages.LY": "Liibüa",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint-Martini territoriaalühendus (Prantsusmaa)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshalli saared",
	"countriesL10nMessages.MK": "Põhja-Makedoonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongoolia",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Põhja-Mariaanid",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritaania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldiivid",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mehhiko",
	"countriesL10nMessages.MY": "Malaisia",
	"countriesL10nMessages.MZ": "Mosambiik",
	"countriesL10nMessages.NA": "Namiibia",
	"countriesL10nMessages.NC": "Uus-Kaledoonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolki saar",
	"countriesL10nMessages.NG": "Nigeeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Holland",
	"countriesL10nMessages.NO": "Norra",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Uus-Meremaa",
	"countriesL10nMessages.OM": "Omaan",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peruu",
	"countriesL10nMessages.PF": "Prantsuse Polüneesia",
	"countriesL10nMessages.PG": "Paapua Uus-Guinea",
	"countriesL10nMessages.PH": "Filipiinid",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poola",
	"countriesL10nMessages.PM": "Saint-Pierre ja Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestiina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumeenia",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudi Araabia",
	"countriesL10nMessages.SB": "Saalomoni Saared",
	"countriesL10nMessages.SC": "Seišellid",
	"countriesL10nMessages.SE": "Rootsi",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Sloveenia",
	"countriesL10nMessages.SJ": "Svalbard ja Jan Mayen",
	"countriesL10nMessages.SK": "Slovakkia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somaalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Lõuna-Sudaan",
	"countriesL10nMessages.ST": "São Tomé ja Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Svaasimaa",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks ja Caicos",
	"countriesL10nMessages.TD": "Tšaad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tai",
	"countriesL10nMessages.TJ": "Tadžikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Ida-Timor",
	"countriesL10nMessages.TM": "Türkmenistan",
	"countriesL10nMessages.TN": "Tuneesia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Türgi",
	"countriesL10nMessages.TT": "Trinidad ja Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tansaania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Ameerika Ühendriigid",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Usbekistan",
	"countriesL10nMessages.VA": "Püha Tool (Vatikani Linnriik)",
	"countriesL10nMessages.VC": "Saint Vincent ja Grenadiinid",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Briti Neitsisaared",
	"countriesL10nMessages.VI": "USA Neitsisaared",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis ja Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jeemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Lõuna-Aafrika Vabariik",
	"countriesL10nMessages.ZM": "Sambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Saada mulle meili teel minu vastustest koopia",
	"ToggleableEmailField.emailLabel": "Sisestage oma meiliaadress"
};

var eu = {
	"AddressField.selectCountry": "Aukeratu herrialdea",
	"PhoneField.countryCode": "Herrialdearen kodea",
	"countriesL10nMessages.AC": "Ascension uhartea",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Arabiar Emirerri Batuak",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua eta Barbuda",
	"countriesL10nMessages.AI": "Aingira",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Estatubatuarra",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland uharteak",
	"countriesL10nMessages.AZ": "Azerbaijan",
	"countriesL10nMessages.BA": "Bosnia-Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgika",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "San Bartolome",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, San Eustakio eta Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Bahamak",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Cocos (Keeling) uharteak",
	"countriesL10nMessages.CD": "Kongoko Errepublika Demokratikoa",
	"countriesL10nMessages.CF": "Afrika Erdiko Errepublika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Suitza",
	"countriesL10nMessages.CI": "Boli Kosta",
	"countriesL10nMessages.CK": "Cook uharteak",
	"countriesL10nMessages.CL": "Txile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Txina",
	"countriesL10nMessages.CO": "Kolonbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cabo Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Christmas uhartea",
	"countriesL10nMessages.CY": "Zipre",
	"countriesL10nMessages.CZ": "Txekiako Errepublika",
	"countriesL10nMessages.DE": "Alemania",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Danimarka",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikar Errepublika",
	"countriesL10nMessages.DZ": "Aljeria",
	"countriesL10nMessages.EC": "Ekuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egipto",
	"countriesL10nMessages.EH": "Mendebaldeko Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Espainia",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finlandia",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falkland (Malvina) uharteak",
	"countriesL10nMessages.FM": "Mikronesiako Estatu Federatuak",
	"countriesL10nMessages.FO": "Faroe uharteak",
	"countriesL10nMessages.FR": "Frantzia",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Erresuma Batua",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guyana Frantsesa",
	"countriesL10nMessages.GG": "Guernesey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenlandia",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Ginea",
	"countriesL10nMessages.GP": "Guadalupe",
	"countriesL10nMessages.GQ": "Ekuatore Ginea",
	"countriesL10nMessages.GR": "Grezia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Ginea Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroazia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungaria",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Man uhartea",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Indiako Ozeanoko Britainiar Lurraldea",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islandia",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Jordania",
	"countriesL10nMessages.JP": "Japonia",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirgizistan",
	"countriesL10nMessages.KH": "Kanbodia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoreak",
	"countriesL10nMessages.KN": "Saint Kitts eta Nevis",
	"countriesL10nMessages.KR": "Hego Korea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Kaiman uharteak",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libano",
	"countriesL10nMessages.LC": "Santa Luzia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lituania",
	"countriesL10nMessages.LU": "Luxenburgo",
	"countriesL10nMessages.LV": "Letonia",
	"countriesL10nMessages.LY": "Libia",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavia",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "San Martin (frantsesa)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshall Uharteak",
	"countriesL10nMessages.MK": "Mazedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Ipar Mariana uharteak",
	"countriesL10nMessages.MQ": "Martinika",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurizio",
	"countriesL10nMessages.MV": "Maldivak",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexiko",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambike",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Kaledonia Berria",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolk uhartea",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Herbehereak",
	"countriesL10nMessages.NO": "Norvegia",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Zeelanda Berria",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinesia Frantsesa",
	"countriesL10nMessages.PG": "Papua Ginea Berria",
	"countriesL10nMessages.PH": "Filipinak",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polonia",
	"countriesL10nMessages.PM": "Saint-Pierre eta Mikelune",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguai",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Reunion",
	"countriesL10nMessages.RO": "Errumania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudi Arabia",
	"countriesL10nMessages.SB": "Salomon Uharteak",
	"countriesL10nMessages.SC": "Seychelleak",
	"countriesL10nMessages.SE": "Suedia",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Santa Helena",
	"countriesL10nMessages.SI": "Eslovenia",
	"countriesL10nMessages.SJ": "Svalbard eta Jan Mayen",
	"countriesL10nMessages.SK": "Eslovakia",
	"countriesL10nMessages.SL": "Sierra Leona",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Hego Sudan",
	"countriesL10nMessages.ST": "Sao Tome eta Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "San Martin (herbeheretarra)",
	"countriesL10nMessages.SZ": "Swazilandia",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turk eta Caico uharteak",
	"countriesL10nMessages.TD": "Txad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailandia",
	"countriesL10nMessages.TJ": "Tajikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Ekialdeko Timor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turkia",
	"countriesL10nMessages.TT": "Trinidad eta Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Ameriketako Estatu Batuak",
	"countriesL10nMessages.UY": "Uruguai",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Egoitza Santua (Vatikano Hiriko Estatua)",
	"countriesL10nMessages.VC": "Saint Vincent eta Grenadinak",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Birjina uharteak, britainiarrak",
	"countriesL10nMessages.VI": "Birjina uharteak, estatubatuarrak",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis eta Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Hegoafrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Bidal iezadazue nire erantzunen kopia bat posta elektroniko bidez",
	"ToggleableEmailField.emailLabel": "Idatzi zure helbide elektronikoa"
};

var fa = {
	"AddressField.selectCountry": "انتخاب کشور",
	"PhoneField.countryCode": "کد کشور",
	"countriesL10nMessages.AC": "جزیره آسنسیون",
	"countriesL10nMessages.AD": "آندورا",
	"countriesL10nMessages.AE": "امارات متحده عربی",
	"countriesL10nMessages.AF": "افغانستان",
	"countriesL10nMessages.AG": "آنتیگوا و باربودا",
	"countriesL10nMessages.AI": "آنگویلا",
	"countriesL10nMessages.AL": "آلبانی",
	"countriesL10nMessages.AM": "ارمنستان",
	"countriesL10nMessages.AO": "آنگولا",
	"countriesL10nMessages.AR": "آرژانتین",
	"countriesL10nMessages.AS": "ساموآی آمریکایی",
	"countriesL10nMessages.AT": "اتریش",
	"countriesL10nMessages.AU": "استرالیا",
	"countriesL10nMessages.AW": "آروبا",
	"countriesL10nMessages.AX": "جزایر آلند",
	"countriesL10nMessages.AZ": "جمهوری آذربایجان",
	"countriesL10nMessages.BA": "بوسنی و هرزگوین",
	"countriesL10nMessages.BB": "باربادوس",
	"countriesL10nMessages.BD": "بنگلادش",
	"countriesL10nMessages.BE": "بلژیک",
	"countriesL10nMessages.BF": "بورکینافاسو",
	"countriesL10nMessages.BG": "بلغارستان",
	"countriesL10nMessages.BH": "بحرین",
	"countriesL10nMessages.BI": "بوروندی",
	"countriesL10nMessages.BJ": "بنین",
	"countriesL10nMessages.BL": "سنت بارتلمی",
	"countriesL10nMessages.BM": "برمودا",
	"countriesL10nMessages.BN": "برونئی دارالسلام",
	"countriesL10nMessages.BO": "بولیوی",
	"countriesL10nMessages.BQ": "بونیر، سینت یوستیشس و سابا",
	"countriesL10nMessages.BR": "برزیل",
	"countriesL10nMessages.BS": "باهاما",
	"countriesL10nMessages.BT": "بوتان",
	"countriesL10nMessages.BW": "بوتسوانا",
	"countriesL10nMessages.BZ": "بلیز",
	"countriesL10nMessages.CA": "کانادا",
	"countriesL10nMessages.CC": "جزایر کوکوس (کیلینگ)",
	"countriesL10nMessages.CD": "جمهوری دموکراتیک کنگو",
	"countriesL10nMessages.CF": "جمهوری آفریقای مرکزی",
	"countriesL10nMessages.CG": "کنگو",
	"countriesL10nMessages.CH": "سوئیس",
	"countriesL10nMessages.CI": "ساحل عاج",
	"countriesL10nMessages.CK": "جزایر کوک",
	"countriesL10nMessages.CL": "شیلی",
	"countriesL10nMessages.CM": "کامرون",
	"countriesL10nMessages.CN": "چین",
	"countriesL10nMessages.CO": "کلمبیا",
	"countriesL10nMessages.CR": "کاستاریکا",
	"countriesL10nMessages.CV": "کیپ ورد",
	"countriesL10nMessages.CW": "کوراسائو",
	"countriesL10nMessages.CX": "جزیره کریسمس",
	"countriesL10nMessages.CY": "قبرس",
	"countriesL10nMessages.CZ": "جمهوری چک",
	"countriesL10nMessages.DE": "آلمان",
	"countriesL10nMessages.DJ": "جیبوتی",
	"countriesL10nMessages.DK": "دانمارک",
	"countriesL10nMessages.DM": "دومینیکا",
	"countriesL10nMessages.DO": "جمهوری دومینیکن",
	"countriesL10nMessages.DZ": "الجزایر",
	"countriesL10nMessages.EC": "اکوادور",
	"countriesL10nMessages.EE": "استونی",
	"countriesL10nMessages.EG": "مصر",
	"countriesL10nMessages.EH": "صحرای غربی",
	"countriesL10nMessages.ER": "اریتره",
	"countriesL10nMessages.ES": "اسپانیا",
	"countriesL10nMessages.ET": "اتیوپی",
	"countriesL10nMessages.FI": "فنلاند",
	"countriesL10nMessages.FJ": "فیجی",
	"countriesL10nMessages.FK": "جزایر فالکلند",
	"countriesL10nMessages.FM": "ایالات فدرال میکرونزی",
	"countriesL10nMessages.FO": "جزایر فارو",
	"countriesL10nMessages.FR": "فرانسه",
	"countriesL10nMessages.GA": "گابن",
	"countriesL10nMessages.GB": "انگلستان",
	"countriesL10nMessages.GD": "گرانادا",
	"countriesL10nMessages.GE": "گرجستان",
	"countriesL10nMessages.GF": "گویان فرانسه",
	"countriesL10nMessages.GG": "گرنزی",
	"countriesL10nMessages.GH": "غنا",
	"countriesL10nMessages.GI": "جبل‌الطارق",
	"countriesL10nMessages.GL": "گرینلند",
	"countriesL10nMessages.GM": "گامبیا",
	"countriesL10nMessages.GN": "گینه",
	"countriesL10nMessages.GP": "گوادلوپ",
	"countriesL10nMessages.GQ": "گینه استوایی",
	"countriesL10nMessages.GR": "یونان",
	"countriesL10nMessages.GT": "گواتمالا",
	"countriesL10nMessages.GU": "گوام",
	"countriesL10nMessages.GW": "گینه بیسائو",
	"countriesL10nMessages.GY": "گویان",
	"countriesL10nMessages.HK": "هنگ‌کنگ",
	"countriesL10nMessages.HN": "هندوراس",
	"countriesL10nMessages.HR": "کرواسی",
	"countriesL10nMessages.HT": "هائیتی",
	"countriesL10nMessages.HU": "مجارستان",
	"countriesL10nMessages.ID": "اندونزی",
	"countriesL10nMessages.IE": "ایرلند",
	"countriesL10nMessages.IL": "اسرائيل",
	"countriesL10nMessages.IM": "جزیره من",
	"countriesL10nMessages.IN": "هند",
	"countriesL10nMessages.IO": "قلمرو اقیانوس هند بریتانیا",
	"countriesL10nMessages.IQ": "عراق",
	"countriesL10nMessages.IS": "ایسلند",
	"countriesL10nMessages.IT": "ایتالیا",
	"countriesL10nMessages.JE": "جرسی",
	"countriesL10nMessages.JM": "جامائیکا",
	"countriesL10nMessages.JO": "اردن",
	"countriesL10nMessages.JP": "ژاپن",
	"countriesL10nMessages.KE": "کنیا",
	"countriesL10nMessages.KG": "قرقیزستان",
	"countriesL10nMessages.KH": "کامبوج",
	"countriesL10nMessages.KI": "کیریباتی",
	"countriesL10nMessages.KM": "کومور",
	"countriesL10nMessages.KN": "سنت کیتس و نویس",
	"countriesL10nMessages.KR": "کره جنوبی",
	"countriesL10nMessages.KW": "کویت",
	"countriesL10nMessages.KY": "جزایر کیمن",
	"countriesL10nMessages.KZ": "قزاقستان",
	"countriesL10nMessages.LA": "لائوس",
	"countriesL10nMessages.LB": "لبنان",
	"countriesL10nMessages.LC": "سنت لوسیا",
	"countriesL10nMessages.LI": "لیختن‌اشتاین",
	"countriesL10nMessages.LK": "سری لانکا",
	"countriesL10nMessages.LR": "لیبریا",
	"countriesL10nMessages.LS": "لسوتو",
	"countriesL10nMessages.LT": "لیتوانی",
	"countriesL10nMessages.LU": "لوکزامبورگ",
	"countriesL10nMessages.LV": "لتونی",
	"countriesL10nMessages.LY": "لیبی",
	"countriesL10nMessages.MA": "مراکش",
	"countriesL10nMessages.MC": "موناکو",
	"countriesL10nMessages.MD": "مولداوی",
	"countriesL10nMessages.ME": "مونته نگرو",
	"countriesL10nMessages.MF": "سنت مارتین (بخش فرانسوی)",
	"countriesL10nMessages.MG": "ماداگاسکار",
	"countriesL10nMessages.MH": "جزایر مارشال",
	"countriesL10nMessages.MK": "مقدونیه",
	"countriesL10nMessages.ML": "مالی",
	"countriesL10nMessages.MM": "میانمار",
	"countriesL10nMessages.MN": "مغولستان",
	"countriesL10nMessages.MO": "ماکائو",
	"countriesL10nMessages.MP": "جزیره ماریانای شمالی",
	"countriesL10nMessages.MQ": "مارتینیک",
	"countriesL10nMessages.MR": "موریتانی",
	"countriesL10nMessages.MS": "مونتسرات",
	"countriesL10nMessages.MT": "مالت",
	"countriesL10nMessages.MU": "موریس",
	"countriesL10nMessages.MV": "مالدیو",
	"countriesL10nMessages.MW": "مالاوی",
	"countriesL10nMessages.MX": "مکزیک",
	"countriesL10nMessages.MY": "مالزی",
	"countriesL10nMessages.MZ": "موزامبیک",
	"countriesL10nMessages.NA": "نامیبیا",
	"countriesL10nMessages.NC": "کالدونیای جدید",
	"countriesL10nMessages.NE": "نیجر",
	"countriesL10nMessages.NF": "جزیره نورفولک",
	"countriesL10nMessages.NG": "نیجریه",
	"countriesL10nMessages.NI": "نیکاراگوئه",
	"countriesL10nMessages.NL": "هلند",
	"countriesL10nMessages.NO": "نروژ",
	"countriesL10nMessages.NP": "نپال",
	"countriesL10nMessages.NR": "نائورو",
	"countriesL10nMessages.NU": "نیوئه",
	"countriesL10nMessages.NZ": "نیوزیلند",
	"countriesL10nMessages.OM": "عمان",
	"countriesL10nMessages.PA": "پاناما",
	"countriesL10nMessages.PE": "پرو",
	"countriesL10nMessages.PF": "پلینزی فرانسه",
	"countriesL10nMessages.PG": "پاپوآ گینه نو",
	"countriesL10nMessages.PH": "فیلیپین",
	"countriesL10nMessages.PK": "پاکستان",
	"countriesL10nMessages.PL": "لهستان",
	"countriesL10nMessages.PM": "سنت پیر و میکلون",
	"countriesL10nMessages.PR": "پورتوریکو",
	"countriesL10nMessages.PS": "فلسطین",
	"countriesL10nMessages.PT": "پرتغال",
	"countriesL10nMessages.PW": "پالائو",
	"countriesL10nMessages.PY": "پاراگوئه",
	"countriesL10nMessages.QA": "قطر",
	"countriesL10nMessages.RE": "رئونیون",
	"countriesL10nMessages.RO": "رومانی",
	"countriesL10nMessages.RS": "صربستان",
	"countriesL10nMessages.RW": "رواندا",
	"countriesL10nMessages.SA": "عربستان سعودی",
	"countriesL10nMessages.SB": "جزایر سلیمان",
	"countriesL10nMessages.SC": "سیشل",
	"countriesL10nMessages.SE": "سوئد",
	"countriesL10nMessages.SG": "سنگاپور",
	"countriesL10nMessages.SH": "سنت هلن",
	"countriesL10nMessages.SI": "اسلوونی",
	"countriesL10nMessages.SJ": "سوالبارد و یان ماین",
	"countriesL10nMessages.SK": "اسلواکی",
	"countriesL10nMessages.SL": "سیرالئون",
	"countriesL10nMessages.SM": "سن مارینو",
	"countriesL10nMessages.SN": "سنگال",
	"countriesL10nMessages.SO": "سومالی",
	"countriesL10nMessages.SR": "سورینام",
	"countriesL10nMessages.SS": "سودان جنوبی",
	"countriesL10nMessages.ST": "سائوتومه و پرنسیپ",
	"countriesL10nMessages.SV": "السالوادور",
	"countriesL10nMessages.SX": "سینت مارتن",
	"countriesL10nMessages.SZ": "سوازیلند",
	"countriesL10nMessages.TA": "تریستان دا کونا",
	"countriesL10nMessages.TC": "جزایر تورکس و کایکوس",
	"countriesL10nMessages.TD": "چاد",
	"countriesL10nMessages.TG": "توگو",
	"countriesL10nMessages.TH": "تایلند",
	"countriesL10nMessages.TJ": "تاجیکستان",
	"countriesL10nMessages.TK": "توکلائو",
	"countriesL10nMessages.TL": "تیمور لسته",
	"countriesL10nMessages.TM": "ترکمنستان",
	"countriesL10nMessages.TN": "تونس",
	"countriesL10nMessages.TO": "تونگا",
	"countriesL10nMessages.TR": "ترکیه",
	"countriesL10nMessages.TT": "ترینیداد و توباگو",
	"countriesL10nMessages.TV": "تووالو",
	"countriesL10nMessages.TW": "تایوان",
	"countriesL10nMessages.TZ": "تانزانیا",
	"countriesL10nMessages.UA": "اوکراین",
	"countriesL10nMessages.UG": "اوگاندا",
	"countriesL10nMessages.US": "ایالات متحده آمریکا",
	"countriesL10nMessages.UY": "اروگوئه",
	"countriesL10nMessages.UZ": "ازبکستان",
	"countriesL10nMessages.VA": "سریر مقدس (دولت شهر واتیکان)",
	"countriesL10nMessages.VC": "سنت وینسنت و گرنادین",
	"countriesL10nMessages.VE": "ونزوئلا",
	"countriesL10nMessages.VG": "جزایر ویرجین، بریتانیا",
	"countriesL10nMessages.VI": "جزایر ویرجین، ایالات متحده",
	"countriesL10nMessages.VN": "ویتنام",
	"countriesL10nMessages.VU": "وانواتو",
	"countriesL10nMessages.WF": "والیس و فوتونا",
	"countriesL10nMessages.WS": "ساموآ",
	"countriesL10nMessages.XK": "کوزوو",
	"countriesL10nMessages.YE": "یمن",
	"countriesL10nMessages.YT": "مایوت",
	"countriesL10nMessages.ZA": "آفریقای جنوبی",
	"countriesL10nMessages.ZM": "زامبیا",
	"countriesL10nMessages.ZW": "زیمبابوه",
	"ToggleableEmailField.checkboxLabel": "نسخه‌ای از پاسخ‌هایم ازطریق ایمیل برایم ارسال شود",
	"ToggleableEmailField.emailLabel": "وارد کردن نشانی ایمیل"
};

var fi = {
	"ActionMenuTagPin.PIN": "Kiinnitä",
	"ActionMenuTagPin.TAG": "Tunniste",
	"AddressField.selectCountry": "Valitse maa",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Vastattu: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "K{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Ohitettuja: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Asenneilmapiiri:",
	"AsyncJobLoadingState.LOADING": "Ladataan vastauksia",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Kerromme, kun se on valmis",
	"AsyncJobLoadingState.TAGGING_TITLE": "Tunnisteiden lisääminen vastauksiin",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Tunnisteet lisätään nykyisiin ja tuleviin vastauksiin. Jätä sanojen väliin välilyönti tai kirjoita <strong>OR</strong> käyttääksesi useita avainsanoja.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Lisää vastauksiin tunniste automaattisesti",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Valitse kuukausi",
	"CalendarPicker.months": "{months, select, January {tammikuu} February {helmikuu} March {maaliskuu} April {huhtikuu} May {toukokuu} June {kesäkuu} July {heinäkuu} August {elokuu} September {syyskuu} October {lokakuu} November {marraskuu} December {joulukuu} other {muu}}",
	"CalendarPicker.nextMonth": "Seuraava kuukausi",
	"CalendarPicker.previousMonth": "Edellinen kuukausi",
	"CalendarPicker.year": "Valitse vuosi",
	"ColorSwatches.TAG": "Tunniste: {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Monivärisissä sanapilvissä ilmaistaan sanan tai lausekkeen esiintymismäärä sekä värillä että koolla.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Näin sanapilvissä käytetään värejä",
	"ColorTab.DOWN_ARROW": "Valitse pudotusvalikon nuoli",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Monivärinen",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Yksi väri",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Peruuta",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Luo uusi sana",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Uusi sana korvaa jokaisen alla valitun sanan.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Muokkaa sanayhdistelmiä",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Yhdistä sanoja",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Luo uusi sana, joka korvaa yhden tai useamman valitun sanan. Voit muokata tätä myöhemmin saadaksesi alkuperäiset sanat takaisin.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Kirjoita uusi sana",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "YHDISTÄ",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Valitse yhdistettävät sanat",
	"CreateTagFromSearch.CLOSE_MENU": "PERUUTA",
	"CreateTagFromSearch.DUPLICATED_NAME": "Samanniminen tunniste on jo olemassa.",
	"CreateTagFromSearch.NEW_TAG": "Uusi tunniste",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Mitään vastausta ei \n\t\tmerkitä} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} vastaus \n\t\t\t\n\t\tmerkitään} other {\n\t\t\t\t{responseTaggedFormatted} vastausta \n\t\t\t\n\t\tmerkitään}}}}",
	"CreateTagFromSearch.SAVE": "Tallenna",
	"CreateTagFromSearch.TAG": "Tunniste",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Tunnisteen nimi ei voi olla tyhjä.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Tunnisteen nimi ei voi olla pelkkiä välilyöntejä.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Lisää tunniste vastauksiin, jotka täsmäävät näihin avainsanoihin. Valitse <strong>Luo tunniste</strong>. Valitse väri ja tallenna. Tunniste lisätään nykyisiin ja tuleviin vastauksiin.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Lisää tunniste vastauksiin, jotka täsmäävät",
	"DashboardMenuContent.CREATE": "LUO",
	"DashboardMenuContent.NAME": "Nimi",
	"DashboardMenuContent.NEW_DASHBOARD": "Uusi koontinäyttö",
	"DateInputWrapper.dateButton": "Avaa kalenteri",
	"DateInputWrapper.dateFormatIntl": "PP/KK/VVVV",
	"DateInputWrapper.dateFormatUs": "KK/PP/VVVV",
	"DateInputWrapper.dateLabel": "Päivämäärä",
	"DateTime.errorEnterHours": "Ilmoita tunnit käyttämällä numeroita 0–23.",
	"DateTime.errorEnterMinutes": "Ilmoita minuutit käyttämällä numeroita 0–59.",
	"DateTime.errorSelectPeriod": "Valitse ap/ip.",
	"DateTime.errorYearOutOfRange": "Anna vuosiluku väliltä 1900–9999.",
	"DateTime.formatErrorDateIntl": "Ilmoita päivämäärä muodossa PP/KK/VVVV.",
	"DateTime.formatErrorDateUs": "Ilmoita päivämäärä muodossa KK/PP/VVVV.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "PERUUTA",
	"EditorButtons.COPY_BUTTON": "KOPIOI KYSYMYS",
	"EditorButtons.MOVE_BUTTON": "SIIRRÄ KYSYMYS",
	"EditorButtons.NEXT_QUESTION_BUTTON": "Seuraava kysymys",
	"EditorButtons.SAVE_BUTTON": "Tallenna",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Tietojen suuntaukset eivät koske tätä kysymystä",
	"FallbackComponent.ERROR": "Virhe kysymyksen lataamisessa",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Valitse tiedosto</div> <div class={dragClass}>tai vedä haluamasi tiedosto tähän</div>",
	"FileUpload.fileUploaded": "Tiedoston lähetys onnistui",
	"FileUpload.fileUploading": "Tiedostoa lähetetään...",
	"FileUpload.removeFile": "Poista tiedosto",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Korvaa tiedosto</div> <div class={dragClass}>tai vedä haluamasi tiedosto tähän</div>",
	"FileUpload.supportedFiles": "Hyväksytyt tiedostot",
	"FilterMenuContent.APPLY_ACTIONS": "KÄYTÄ",
	"FilterMenuContent.CLEAR_ACTIONS": "Tyhjennä",
	"FilterMenuContent.CLOSE_MENU": "PERUUTA",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Peruuta",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Piilota",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Tallenna",
	"HideWordsModal.HIDE_WORDS_HEADER": "Piilota sanoja",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Valitse sanat, jotka haluat piilottaa sanapilvestä.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Valitse piilotettavat sanat",
	"IconHelpStyled.HELP": "Ohjeet",
	"IndividualResponseCard.NO_RESPONSE": "Vastaaja ohitti tämän kysymyksen",
	"IndividualResponseCard.QUESTION_NUMBER": "K{number}",
	"InfoBannerTagManagement.CLOSE": "Sulje",
	"InfoBannerTagManagement.NEW": "UUSI",
	"InfoBannerTagManagement.TAG_RESPONSES": "Lisää tunniste vastauksiin",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Määritä automaattiset tunnisteet ja pidä vastaukset järjestyksessä lisäämällä avainsanoja.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Lisää vastauksiin tunnisteita helpottaaksesi hallintaa",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Myönteinen: {percentage,number,percent}} negative {Kielteinen: {percentage,number,percent}} neutral {Neutraali: {percentage,number,percent}} unknown {Havaitsematon: {percentage,number,percent}} other {muu}}",
	"LoadingState.LOADING": "Ladataan vastauksia",
	"MatrixDropdown.EmptyTopNativeSelect": "Valitse vaihtoehto",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Luottamusväli",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Utiliteettitulos",
	"Modal.CloseModal": "Sulje työkaluvihje",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {maanantai} Tu {tiistai} We {keskiviikko} Th {torstai} Fr {perjantai} Sa {lauantai} Su {sunnuntai} other {muu}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {ma} Tu {ti} We {ke} Th {to} Fr {pe} Sa {la} Su {su} other {muu}}",
	"MultiColorSelectorMenu.APPLY": "Käytä",
	"MultiColorSelectorMenu.CANCEL": "Peruuta",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Muokkaa tietojesi asettelua, esittele löydöksiäsi ja jaa koontinäyttö haluamillesi henkilöille. Muokkaa tietojesi asettelua, esittele löydöksiäsi ja jaa koontinäyttö haluamillesi henkilöille.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Koontinäytöt",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Lue lisää",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">Asteikolla {minValue}–{maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} kohtaan {minLabelText}, {maxValue} kohtaan {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "mittausalue tuloksille {min}–{max}",
	"NpsGaugeChart.NPS_CHART": "NPS-tuloskaavio",
	"NpsGaugeChart.NPS_SCORE": "NPS-tulos: {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "kaaviomerkki tuloksen {score} sijaintia varten",
	"OrderControl.AriaNA": "Ei sovellu",
	"OrderControl.NA": "Muu",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "Kohdan {title} vastaukset ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "piilota Muu-vastausten luettelo",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "näytä Muu-vastausten luettelo",
	"PhoneField.countryCode": "Maakoodi",
	"QuestionCardItemFooter.FILTER_APPLIED": "Käytetyt säännöt: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Suodatin",
	"QuestionCardTitle.ANSWERED": "Vastattu: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Valittu: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Ohitettuja: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {Pisteet: {score}/{total}} other {Pisteet: {score}/{total}}}",
	"QuizResultScore.SKIPPED": "Ohitetut",
	"RangeTab.RANGE_TAB_LABEL": "Maininnat: {value}",
	"RangeTab.TOTAL_WORDS": "sanaa yhteensä",
	"Ranking.AriaRankAtBottom": "{label} siirrettiin luettelon loppuun",
	"Ranking.AriaRankAtTop": "{label} siirrettiin luettelon alkuun",
	"Ranking.AriaRankMovedDown": "{label} siirrettiin alemmaksi sijalle {position}",
	"Ranking.AriaRankMovedUp": "{label} siirrettiin ylemmäksi sijalle {position}",
	"RankingItem.AriaRankMoveDown": "Siirrä {label} alas",
	"RankingItem.AriaRankMoveUp": "Siirrä {label} ylös",
	"RatingChart.AVERAGE_RATING": "Pisteiden keskiarvo",
	"RatingChartLegend.ANSWERED": "Vastattu: {number}",
	"RatingChartLegend.RATING": "{rating} tähti",
	"RatingChartLegend.SKIPPED": "Ohitettuja: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Pyydä ominaisuutta",
	"RequestAccessButton.UPGRADE": "Korota tasoa",
	"RespondentQuestionTitle.REQUIRED": "(Pakollinen tieto.)",
	"ResponseItem.EXPAND_MENU_LABEL": "Laajenna valikkoa",
	"ResponseItem.MENU_LABEL": "Lisää tunnisteita",
	"ResponseItem.RESPONDENT_ID": "Vastaajan tunnus",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Näytä vastaajan vastaukset",
	"ResponseStatusBar.CLEAR": "Tyhjennä",
	"ResponseStatusBar.FAILED_TO_LOAD": "Vastausten lataaminen epäonnistui",
	"ResponseStatusBar.NO_RESPONSES_YET": "Ei vielä vastauksia",
	"ResponseStatusBar.SEARCHING": "Haetaan...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {myönteinen asenne} negative {kielteinen asenne} neutral {neutraali asenne} other {tuntematon asenne}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittu vastaus kohteelle {searchTextFormatted}, jossa on suodattimet {filters}   } other {Näytetään {resultsCountFormatted} vastaus kohteelle {searchTextFormatted}, jossa on suodattimet {filters}   }}} other {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittua vastausta kohteelle {searchTextFormatted}, jossa on suodattimet {filters}   } other {Näytetään {resultsCountFormatted} vastausta kohteelle {searchTextFormatted}, jossa on suodattimet {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittu vastaus suodattimilla {filters}   } other {Näytetään {resultsCountFormatted} vastaus suodattimilla {filters}   }}} other {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittua vastausta suodattimilla {filters}   } other {Näytetään {resultsCountFormatted} vastausta suodattimilla {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittu vastaus kohteelle {searchTextFormatted}   } other {Näytetään {resultsCountFormatted} vastaus kohteelle {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittua vastausta kohteelle {searchTextFormatted}   } other {Näytetään {resultsCountFormatted} vastausta kohteelle {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittu vastaus   } other {Näytetään {resultsCountFormatted} vastaus   }}} other {{hasSelectedResponse, select, true {Näytetään {resultsCountFormatted} valittua vastausta   } other {Näytetään {resultsCountFormatted} vastausta   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Tyhjennä",
	"ResponseToolbar.CREATE_TAG": "Luo tunniste",
	"ResponseToolbar.SELECT_ALL": "Valitse kaikki vastaukset",
	"ResponseToolbar.UNSELECT_ALL": "Poista valinta kaikista vastauksista",
	"SearchResponsesInput.CLEAR": "Tyhjennä haku",
	"SearchResponsesInput.SEARCH": "Hae",
	"SearchResponsesInput.SEARCH_RESPONSES": "Hae vastauksia",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Tee täsmällinen lausehaku käyttämällä lainausmerkkejä <strong>&quot;&quot;</strong>.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Kirjoita <strong>OR</strong> löytääksesi yhden kahdesta tai useammasta termistä.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Jos haluat löytää useamman kuin yhden termin, kirjoita <b>AND</b>.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Käytä sanaa <strong>NOT</strong> sulkeaksesi jonkun termin haun ulkopuolelle.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Jos haluat hakea etuliitteellä, lisää termin loppuun <strong>*</strong>.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Jos haluat yhdistää AND-, OR- ja NOT-perusteisia hakuja, käytä sulkeita.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Jos haluat tuloksia, jotka täsmäävät termiin ja joissa on enintään kaksi muuta merkkiä, lisää termin loppuun <strong>~2</strong>.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Hakuvaihtoehdot",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Lue lisää",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Ei kiitos",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Ota käyttöön",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "<span id=\"link\">Automaattiset havainnot</span> ovat nyt käytettävissä tililläsi. \n      Tähän asetukseen kuuluvat kaikki ominaisuudet, jotka hyödyntävät koneoppimista kyselytutkimuksen käsittelyssä ja havaintojen tarjoamisessa.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Voit halutessasi muuttaa asetuksia <span id=\"link\">Oma tili</span> ‑osiossa.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Automaattiset havainnot",
	"SentimentBanner.GENERATE_SENTIMENT": "Analysoi asenne",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Asenneanalyysi",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Tunnista asenteet tekstivastauksen taustalla, niin saat käsityksen ihmisten tuntemuksista.",
	"SentimentBanner.UPGRADE": "Korota tasoa",
	"SentimentBanner.WATCH_A_DEMO": "Katso demo",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Asenneilmapiiri:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Suodata myönteisen asenteen perusteella} negative {Suodata kielteisen asenteen perusteella} neutral {Suodata neutraalin asenteen perusteella} other {Suodata tuntemattoman asenteen perusteella}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Laajenna valikkoa",
	"SentimentFilterMenu.MENU_LABEL": "asenteen mukaan",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "PYYDÄ OMINAISUUTTA",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Havaitse tunteet ja asenteet kirjoitettujen vastausten taustalla.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "UUTTA!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Esittelyssä asenneanalyysi",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Korota tasoa",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Katso demo",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Ei kiitos",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Pyydä ominaisuutta",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Korota tasoa",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Asenteen korotettu kuva",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Ota asenneanalyysi käyttöön yhdellä klikkauksella. Luokittelemme tekstivastauksesi myönteisiksi, neutraaleiksi tai kielteisiksi paljastaen kirjoitetun tekstin taustalla olevat asenteet.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Selvitä asenneilmapiiri heti kättelyssä",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Asenneanalyysi",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Asenteet: KÄYTÖSSÄ} false {Asenteet: POIS KÄYTÖSTÄ} other {Asenteet: muu}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Asenteet: LADATAAN...",
	"SlideBoxNavigation.NEXT_BUTTON": "SEURAAVA",
	"SlideBoxNavigation.NEXT_SLIDE": "Seuraava dia",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "EDELLINEN",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Edellinen dia",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} / {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Tyhjennä",
	"Slider.STEP_MESSAGE": "Muokkasimme antamaasi numeroa liukuvan asteikon mukaan.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Vastattu: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "K{questionNumber}{questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Ohitettuja: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Edullisen ja kalliin risteyskohta antaa tuotteesi hinnalle yleisen suunnan.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Hintaherkkyys (Van Westendorp)",
	"SrText.CORRECT": "Oikein",
	"SrText.INCORRECT": "Väärin",
	"StarRating.Heart": "{count, plural, one {{count} sydän} other {{count} sydäntä}}",
	"StarRating.STAR": "{count, plural, one {{count} tähti} other {{count} tähteä}}",
	"StarRating.Smiley": "{count, plural, one {{count} hymiö} other {{count} hymiötä}}",
	"StarRating.Thumb": "{count, plural, one {{count} peukku} other {{count} peukkua}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Tunnistevälilehden lukkokuvake",
	"TabList.RESPONSES": "VASTAUKSET ({responseCount})",
	"TabList.TAGS": "TUNNISTEET ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "TEMAATTINEN ANALYYSI",
	"TabList.WORD_CLOUD": "SANAPILVI",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Laajenna valikkoa",
	"TagFilterMenu.MENU_LABEL": "Suodatus tunnisteen mukaan",
	"TagList.TAGS": "Tunnisteet",
	"TagList.TAGS_KEYWORDS": "Avainsanat",
	"TagList.TAG_CONTROLS": "Tunnisteiden hallinta",
	"TagList.TAG_NUMBER_PERCENT": "Tunnisteiden prosenttiosuus ja tunnisteiden lukumäärä",
	"TagList.TAG_PERCENTAGE_BAR": "Tunnisteen prosenttipalkki",
	"TagListItem.DELETE": "Poista",
	"TagListItem.EDIT": "MUOKKAA",
	"TagListItem.VIEW_ALL": "NÄYTÄ KAIKKI",
	"TagListPanel.LOADING": "Ladataan tunnisteita",
	"TagListPanel.NEW_TAG": "+ Uusi tunniste",
	"TagListPanel.TAGS": "Tunnisteet",
	"TagMenuContent.APPLY_ACTIONS": "KÄYTÄ",
	"TagMenuContent.CLEAR_ACTIONS": "Tyhjennä",
	"TagMenuContent.CLOSE_MENU": "PERUUTA",
	"TagMenuContent.DUPLICATED_NAME": "Samanniminen tunniste on jo olemassa.",
	"TagMenuContent.NEW_TAG": "Uusi tunniste",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Lue lisää >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Pyydä ominaisuutta",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Korota tasoa",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Sulje",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Hae, merkitse ja suodata kirjallisia vastauksia ja tarkastele sanapilviä usein esiintyvistä sanoista ja lauseista.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Tekstianalyysi",
	"TimeInputWrapper.am": "ap",
	"TimeInputWrapper.daytimeLabel": "AP/IP",
	"TimeInputWrapper.hhPlaceholder": "tt",
	"TimeInputWrapper.hour": "tunti",
	"TimeInputWrapper.minutes": "MINUUTTIA",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "IP",
	"TimeInputWrapper.timeLabel": "Aika",
	"ToggleableEmailField.checkboxLabel": "Lähetä kopio vastauksistani sähköpostiini",
	"ToggleableEmailField.emailLabel": "Anna sähköpostiosoitteesi",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Poista",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted} vastauksesta poistetaan tunniste.} other {{responseCountFormatted} vastauksesta poistetaan tunniste.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Tunnisteen poistaminen poistaa sen kaikista merkityistä vastauksista. Toimenpidettä ei voi perua.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Poistetaanko tunniste?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Tallenna",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Tallennetaanko muutokset?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Tämä poistaa tunnisteen kaikista vastauksista. Tunniste lisätään uudelleen vastauksiin, jotka\n täsmäävät muokattuihin avainsanoihin.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Peruuta",
	"UpgradeBanner.SUBTITLE": "Korota tilaus ja mukauta oma sanapilvesi.",
	"UpgradeBanner.TITLE": "Pidätkö näkemästäsi?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% vastauksista",
	"VanWestendorpChartDataTable.PRICE": "Hinta",
	"VanWestendorpChartDataTable.RESPONSES": "Vastaukset",
	"WordCloudCustomizationButton.CUSTOMIZE": "Muokkaa",
	"WordCloudCustomizationFooter.APPLY": "Käytä",
	"WordCloudCustomizationFooter.CANCEL": "Peruuta",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Käytä kaikissa kyselytutkimuksen sanapilvissä (kaikkiin ei välttämättä tehdä muutoksia)",
	"WordCloudCustomizationFooter.NO_THANKS": "Ei kiitos",
	"WordCloudCustomizationFooter.REQUEST": "Pyydä ominaisuutta",
	"WordCloudCustomizationFooter.SAVE": "Tallenna",
	"WordCloudCustomizationFooter.UPGRADE": "Korota tasoa",
	"WordCloudCustomizationTabs.COLOR": "VÄRI",
	"WordCloudCustomizationTabs.FONT": "FONTTI",
	"WordCloudCustomizationTabs.RANGE": "ASTEIKKO",
	"WordCloudLegend.FEWER": "vähemmän",
	"WordCloudLegend.MORE": "lisää",
	"WordCloudLegend.TITLE": "Mainintojen määrä",
	"WordCloudListFooter.COMBINED_WORDS": "Sanayhdistelmät",
	"WordCloudListFooter.HIDDEN_WORDS": "Piilotetut sanat",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Laajenna valikkoa",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Yhdistä seuraavaan:",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Muokkaa sanayhdistelmiä",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Laajenna valikkoa",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Piilota sanapilvestä",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Jaa yhdistettyjä sanoja",
	"WordCloudLoadingState.LOADING": "Ladataan sanapilveä...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Sulje-painike",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Esikatsele sanapilvien muokkaukseen tarkoitettuja ominaisuuksia.",
	"WordCloudPreviewOverlay.TITLE": "Kokeile!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Pilvinäkymä",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Luettelonäkymä",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Kertaesiintymät: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}. tärkein sana",
	"WordCount.IN_PROGRESS": "Tekstianalyysi käynnissä...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Tärkeitä sanoja ei löytynyt.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Näytetään {count} sana } other {Näytetään {count} sanaa }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Suodata myönteisen asenteen perusteella} negative {Suodata kielteisen asenteen perusteella} neutral {Suodata neutraalin asenteen perusteella} other {Suodata tuntemattoman asenteen perusteella}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Myönteinen} negative {Kielteinen} neutral {Neutraali} other {Tuntematon}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Myönteinen: {percentage,number,percent}} negative {Kielteinen: {percentage,number,percent}} neutral {Neutraali: {percentage,number,percent}} other {Tuntematon: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "PERUUTA",
	"copyTagEditor.DUPLICATED_NAME": "Samanniminen tunniste on jo olemassa.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Anna tälle tunnisteelle nimi",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Kirjoita yksi tai useampia avainsanoja",
	"copyTagEditor.KEYWORDS": "<strong>Avainsanat</strong> (valinnainen)",
	"copyTagEditor.SAVE": "Tallenna",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Tunniste lisätään kaikkiin vastauksiin, jotka täsmäävät avainsanoihin",
	"copyTagEditor.TAG_NAME": "<strong>Tunnisteen nimi</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Tunnisteen nimi ei voi olla tyhjä.",
	"copyTagEditor.TAG_NAME_SPACES": "Tunnisteen nimi ei voi olla pelkkiä välilyöntejä.",
	"copyTagEditor.TAG_TOO_LONG": "Tunnisteessa on oltava alle 128 merkkiä",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Yhdistyneet arabiemiirikunnat",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua ja Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentiina",
	"countriesL10nMessages.AS": "Amerikan Samoa",
	"countriesL10nMessages.AT": "Itävalta",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ahvenanmaa",
	"countriesL10nMessages.AZ": "Azerbaidžan",
	"countriesL10nMessages.BA": "Bosnia ja Hertsegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgia",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius ja Saba",
	"countriesL10nMessages.BR": "Brasilia",
	"countriesL10nMessages.BS": "Bahama",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kookossaaret (Keelingsaaret)",
	"countriesL10nMessages.CD": "Kongon demokraattinen tasavalta",
	"countriesL10nMessages.CF": "Keski-Afrikan tasavalta",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Sveitsi",
	"countriesL10nMessages.CI": "Norsunluurannikko",
	"countriesL10nMessages.CK": "Cookinsaaret",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kiina",
	"countriesL10nMessages.CO": "Kolumbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Kap Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Joulusaari",
	"countriesL10nMessages.CY": "Kypros",
	"countriesL10nMessages.CZ": "Tšekki",
	"countriesL10nMessages.DE": "Saksa",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Tanska",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Dominikaaninen tasavalta",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Viro",
	"countriesL10nMessages.EG": "Egypti",
	"countriesL10nMessages.EH": "Länsi-Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Espanja",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Suomi",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Falklandinsaaret",
	"countriesL10nMessages.FM": "Mikronesian liittovaltio",
	"countriesL10nMessages.FO": "Färsaaret",
	"countriesL10nMessages.FR": "Ranska",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Yhdistynyt Kuningaskunta",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Ranskan Guayana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grönlanti",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Päiväntasaajan Guinea",
	"countriesL10nMessages.GR": "Kreikka",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Unkari",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irlanti",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Mansaari",
	"countriesL10nMessages.IN": "Intia",
	"countriesL10nMessages.IO": "Brittiläinen Intian valtameren alue",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islanti",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Jordania",
	"countriesL10nMessages.JP": "Japani",
	"countriesL10nMessages.KE": "Kenia",
	"countriesL10nMessages.KG": "Kirgisia",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komorit",
	"countriesL10nMessages.KN": "Saint Kitts ja Nevis",
	"countriesL10nMessages.KR": "Etelä-Korea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Caymansaaret",
	"countriesL10nMessages.KZ": "Kazakstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Liettua",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Marokko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (ranskalainen osa)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshallinsaaret",
	"countriesL10nMessages.MK": "Makedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Pohjois-Mariaanit",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Malediivit",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Meksiko",
	"countriesL10nMessages.MY": "Malesia",
	"countriesL10nMessages.MZ": "Mosambik",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Uusi-Kaledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolkinsaari",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Alankomaat",
	"countriesL10nMessages.NO": "Norja",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Uusi-Seelanti",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Ranskan Polynesia",
	"countriesL10nMessages.PG": "Papua-Uusi-Guinea",
	"countriesL10nMessages.PH": "Filippiinit",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Puola",
	"countriesL10nMessages.PM": "Saint-Pierre ja Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestiina",
	"countriesL10nMessages.PT": "Portugali",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudi-Arabia",
	"countriesL10nMessages.SB": "Salomonsaaret",
	"countriesL10nMessages.SC": "Seychellit",
	"countriesL10nMessages.SE": "Ruotsi",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Huippuvuoret ja Jan Mayen",
	"countriesL10nMessages.SK": "Slovakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Etelä-Sudan",
	"countriesL10nMessages.ST": "São Tomé ja Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swazimaa",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- ja Caicossaaret",
	"countriesL10nMessages.TD": "Tšad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thaimaa",
	"countriesL10nMessages.TJ": "Tadžikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Itä-Timor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turkki",
	"countriesL10nMessages.TT": "Trinidad ja Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tansania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Yhdysvallat",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Vatikaanivaltio",
	"countriesL10nMessages.VC": "Saint Vincent ja Grenadiinit",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Brittiläiset Neitsytsaaret",
	"countriesL10nMessages.VI": "Yhdysvaltain Neitsytsaaret",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis ja Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Etelä-Afrikka",
	"countriesL10nMessages.ZM": "Sambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Eroamaton hintapiste",
	"helper.IPP": "Eroamaton hintapiste",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Optimaalinen hintapiste",
	"helper.OPP_DESCRIPTION": "Optimaalinen hintapiste",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Halpuuden marginaalipiste",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Kalleuden marginaalipiste",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Halpuuden marginaalipiste",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Kalleuden marginaalipiste"
};

var fr = {
	"ActionMenuTagPin.PIN": "Épingler",
	"ActionMenuTagPin.TAG": "Balise",
	"AddressField.selectCountry": "Sélectionnez un pays",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Réponse(s) obtenue(s) : {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "Q{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Question(s) ignorée(s) : {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Impression des participants :",
	"AsyncJobLoadingState.LOADING": "Chargement des réponses en cours",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Nous vous préviendrons dès que cela sera terminé.",
	"AsyncJobLoadingState.TAGGING_TITLE": "Balisage des réponses en cours",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Les balises s’appliquent aux réponses actuelles et futures. Laissez un espace ou saisissez <strong>OR</strong> entre les mots pour utiliser plusieurs mots-clés.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Baliser les réponses automatiquement",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Sélectionner un mois",
	"CalendarPicker.months": "{months, select, January {Janvier} February {Février} March {Mars} April {Avril} May {Mai} June {Juin} July {Juillet} August {Août} September {Septembre} October {Octobre} November {Novembre} December {Décembre} other {autre}}",
	"CalendarPicker.nextMonth": "Mois suivant",
	"CalendarPicker.previousMonth": "Mois précédent",
	"CalendarPicker.year": "Sélectionner une année",
	"ColorSwatches.TAG": "Balise {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Dans les nuages de mots multicolores, la couleur et la taille des mots ou expressions varient en fonction du nombre de fois qu'ils ont été mentionnés.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Comment les couleurs sont utilisées pour les nuages de mots",
	"ColorTab.DOWN_ARROW": "Sélectionnez la flèche de liste déroulante",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Multicolore",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Une couleur",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Annuler",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Créez un nouveau mot",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Le nouveau mot remplace chacun des mots sélectionnés ci-dessous.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Modifier les mots combinés",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Combiner les mots",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Créez un nouveau mot qui remplacera un ou plusieurs mots sélectionnés. Vous pourrez modifier ultérieurement pour revenir au(x) mot(s) d’origine.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Saisissez n’importe quel mot",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Combiner",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Choisissez les mots à combiner",
	"CreateTagFromSearch.CLOSE_MENU": "Annuler",
	"CreateTagFromSearch.DUPLICATED_NAME": "Une balise porte déjà ce nom.",
	"CreateTagFromSearch.NEW_TAG": "Nouvelle balise",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Aucune réponse \n\t\tne sera balisée.} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} réponse\n\t\t\t\n\t\tréponse sera balisée} other {\n\t\t\t\t{responseTaggedFormatted} réponses \n\t\t\t\n\t\tseront balisées}}}}",
	"CreateTagFromSearch.SAVE": "Enregistrer",
	"CreateTagFromSearch.TAG": "Balise",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Vous devez indiquer le nom de la balise.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Le nom de la balise ne peut pas être composé uniquement d'espaces.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Balisez les réponses qui correspondent à ces mots-clés. Sélectionnez <strong>Créer une balise</strong>. Choisissez une couleur et enregistrez. La balise s’applique aux réponses actuelles et futures.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Balisez les réponses correspondantes",
	"DashboardMenuContent.CREATE": "Créer",
	"DashboardMenuContent.NAME": "Nom",
	"DashboardMenuContent.NEW_DASHBOARD": "Nouveau tableau de bord",
	"DateInputWrapper.dateButton": "Ouvrir le calendrier",
	"DateInputWrapper.dateFormatIntl": "JJ/MM/AAAA",
	"DateInputWrapper.dateFormatUs": "MM/JJ/AAAA",
	"DateInputWrapper.dateLabel": "Date",
	"DateTime.errorEnterHours": "Veuillez saisir une valeur comprise entre 0 et 23.",
	"DateTime.errorEnterMinutes": "Veuillez saisir une valeur comprise entre 0 et 59.",
	"DateTime.errorSelectPeriod": "Veuillez sélectionner AM ou PM.",
	"DateTime.errorYearOutOfRange": "Veuillez indiquer une valeur comprise entre 1900 et 9999 pour l'année.",
	"DateTime.formatErrorDateIntl": "Veuillez saisir la date au format JJ/MM/AAAA.",
	"DateTime.formatErrorDateUs": "Veuillez saisir la date au format MM/JJ/AAAA.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "Annuler",
	"EditorButtons.COPY_BUTTON": "COPIER LA QUESTION",
	"EditorButtons.MOVE_BUTTON": "DÉPLACER LA QUESTION",
	"EditorButtons.NEXT_QUESTION_BUTTON": "QUESTION SUIVANTE",
	"EditorButtons.SAVE_BUTTON": "Enregistrer",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Les tendances des données ne s'appliquent pas à cette question.",
	"FallbackComponent.ERROR": "Erreur lors du chargement de la question",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Choisissez un fichier</div> <div class={dragClass}>ou faites-le glisser ici</div>.",
	"FileUpload.fileUploaded": "Chargement du fichier terminé",
	"FileUpload.fileUploading": "Chargement de fichier",
	"FileUpload.removeFile": "Supprimer le fichier",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Remplacez le fichier</div> <div class={dragClass}> ou faites-le glisser ici</div>.",
	"FileUpload.supportedFiles": "Fichiers pris en charge",
	"FilterMenuContent.APPLY_ACTIONS": "Appliquer",
	"FilterMenuContent.CLEAR_ACTIONS": "Effacer",
	"FilterMenuContent.CLOSE_MENU": "Annuler",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Annuler",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Masquer",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Enregistrer",
	"HideWordsModal.HIDE_WORDS_HEADER": "Masquer les mots",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Sélectionnez les mots à masquer dans le nuage de mots.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Sélectionnez les mots à masquer",
	"IconHelpStyled.HELP": "Aide",
	"IndividualResponseCard.NO_RESPONSE": "Le participant a ignoré la question",
	"IndividualResponseCard.QUESTION_NUMBER": "Q{number}",
	"InfoBannerTagManagement.CLOSE": "Fermer",
	"InfoBannerTagManagement.NEW": "NOUVEAU",
	"InfoBannerTagManagement.TAG_RESPONSES": "Baliser les réponses",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Ajoutez des mots-clés pour configurer des balises automatiques et organiser les réponses.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Balisez les réponses pour une gestion plus facile",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positif : {percentage,number,percent}} negative {Négatif : {percentage,number,percent}} neutral {Neutre : {percentage,number,percent}} unknown {Non décelé : {percentage,number,percent}} other {autre}}",
	"LoadingState.LOADING": "Chargement des réponses en cours",
	"MatrixDropdown.EmptyTopNativeSelect": "Sélectionner une option",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Intervalle de confiance",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Scores d'utilité",
	"Modal.CloseModal": "Fermer l'info-bulle",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Lundi} Tu {Mardi} We {Mercredi} Th {Jeudi} Fr {Vendredi} Sa {Samedi} Su {Dimanche} other {autre}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Lun} Tu {Mar} We {Mer} Th {Jeu} Fr {Ven} Sa {Sam} Su {Dim} other {autre}}",
	"MultiColorSelectorMenu.APPLY": "Appliquer",
	"MultiColorSelectorMenu.CANCEL": "Annuler",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Présentez vos données de façon personnalisée, expliquez vos conclusions et partagez votre tableau de bord. Présentez vos données de façon personnalisée, expliquez vos conclusions et partagez votre tableau de bord.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Tableaux de bord",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "En savoir plus",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">Sur une échelle de {minValue} à {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} pour {minLabelText}, {maxValue} pour {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "zone d'évaluation des scores compris entre {min} et {max}",
	"NpsGaugeChart.NPS_CHART": "Graphique d'évaluation NPS",
	"NpsGaugeChart.NPS_SCORE": "Score NPS : {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "coche graphique correspondant au score {score}",
	"OrderControl.AriaNA": "SANS OBJET",
	"OrderControl.NA": "S/O",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "Réponses « {title} » ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "Masquer la liste des réponses « Autre »",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "Afficher la liste des réponses « Autre »",
	"PhoneField.countryCode": "Pays",
	"QuestionCardItemFooter.FILTER_APPLIED": "Règles appliquées : {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtrer",
	"QuestionCardTitle.ANSWERED": "Réponse(s) obtenue(s) : {answeredCount}",
	"QuestionCardTitle.SELECTED": "Sélectionnées : {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Question(s) ignorée(s) : {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} point} other {{score}/{total} points}}",
	"QuizResultScore.SKIPPED": "Question ignorée",
	"RangeTab.RANGE_TAB_LABEL": "Occurrences : {value}",
	"RangeTab.TOTAL_WORDS": "mots au total",
	"Ranking.AriaRankAtBottom": "{label} déplacé au bas de la liste",
	"Ranking.AriaRankAtTop": "{label} déplacé en haut de la liste",
	"Ranking.AriaRankMovedDown": "{label} déplacé vers le bas en position N° {position}",
	"Ranking.AriaRankMovedUp": "{label} déplacé vers le haut en position N° {position}",
	"RankingItem.AriaRankMoveDown": "Déplacer vers le bas {label}",
	"RankingItem.AriaRankMoveUp": "Déplacer vers le haut {label}",
	"RatingChart.AVERAGE_RATING": "Évaluation moyenne",
	"RatingChartLegend.ANSWERED": "Réponse(s) obtenue(s) : {number}",
	"RatingChartLegend.RATING": "{rating} étoile",
	"RatingChartLegend.SKIPPED": "Question(s) ignorée(s) : {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Demander l'accès",
	"RequestAccessButton.UPGRADE": "Mettre à niveau",
	"RespondentQuestionTitle.REQUIRED": "(Obligatoire)",
	"ResponseItem.EXPAND_MENU_LABEL": "Développer le menu",
	"ResponseItem.MENU_LABEL": "Ajouter des balises",
	"ResponseItem.RESPONDENT_ID": "Identifiant du participant",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Afficher les réponses des participants",
	"ResponseStatusBar.CLEAR": "Effacer",
	"ResponseStatusBar.FAILED_TO_LOAD": "Échec du chargement des réponses",
	"ResponseStatusBar.NO_RESPONSES_YET": "Aucune réponse pour l’instant",
	"ResponseStatusBar.SEARCHING": "Recherche en cours...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {ressenti positif} negative {ressenti négatif} neutral {ressenti neutre} other {ressenti non décelé}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponse sélectionnée pour {searchTextFormatted} avec {filters}   } other {Affichage de {resultsCountFormatted} réponse pour {searchTextFormatted} avec {filters}   }}} other {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponses sélectionnées pour {searchTextFormatted} avec {filters}   } other {Affichage de {resultsCountFormatted} réponses pour {searchTextFormatted} avec {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponse sélectionnée avec {filters}   } other {Affichage de {resultsCountFormatted} réponse avec {filters}   }}} other {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponses sélectionnées avec {filters}   } other {Affichage de {resultsCountFormatted} réponses avec {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponse sélectionnée pour {searchTextFormatted}   } other {Affichage de {resultsCountFormatted} réponse pour {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponses sélectionnées pour {searchTextFormatted}   } other {Affichage de {resultsCountFormatted} réponses pour {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponse sélectionnée   } other {Affichage de {resultsCountFormatted} réponse   }}} other {{hasSelectedResponse, select, true {Affichage de {resultsCountFormatted} réponses sélectionnées   } other {Affichage de {resultsCountFormatted} réponses   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Effacer",
	"ResponseToolbar.CREATE_TAG": "Créer une balise",
	"ResponseToolbar.SELECT_ALL": "Sélectionner toutes les réponses",
	"ResponseToolbar.UNSELECT_ALL": "Désélectionner toutes les réponses",
	"SearchResponsesInput.CLEAR": "Effacer la recherche",
	"SearchResponsesInput.SEARCH": "Rechercher",
	"SearchResponsesInput.SEARCH_RESPONSES": "Rechercher des réponses",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Utilisez les guillemets <strong>“”</strong> pour rechercher une expression exacte.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Saisissez <strong>OR</strong> pour trouver au moins l’un des termes recherchés.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Tapez <b>AND</b> pour rechercher plusieurs termes.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Utilisez <strong>NOT</strong> pour exclure un terme de votre recherche.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Tapez <strong>*</strong> à la fin d'un terme pour effectuer une recherche par préfixe.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Utilisez des parenthèses pour combiner AND/OR/NOT en fonction de vos recherches.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Tapez <strong>~2</strong> à la fin d'un terme pour afficher les résultats qui correspondent à ce terme, plus un ou deux autres caractères.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Options de recherche",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "En savoir plus",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Non merci",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Activer",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "L'option <span id=\"link\">Insights automatiques</span> est désormais accessible dans votre compte.\n      Cela inclut toutes les fonctionnalités qui font appel à l'apprentissage automatique pour traiter les sondages et fournir des informations exploitables.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Vous pouvez modifier vos préférences à tout moment dans <span id=\"link\">Mon compte</span>.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Insights automatiques",
	"SentimentBanner.GENERATE_SENTIMENT": "Analyser le ressenti",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Analyse du ressenti",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Décelez le ressenti des participants à partir des réponses qu'ils ont rédigées.",
	"SentimentBanner.UPGRADE": "Mettre à niveau",
	"SentimentBanner.WATCH_A_DEMO": "Regarder une vidéo",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Impression des participants :",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtrer par ressenti positif} negative {Filtrer par ressenti négatif} neutral {Filtrer par ressenti neutre} other {Filtrer par ressenti non décelé}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Développer le menu",
	"SentimentFilterMenu.MENU_LABEL": "par sentiment",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Demander l'accès",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Identifiez les sentiments derrière certaines réponses.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Nouveau !",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Analyse du ressenti",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Mettre à niveau",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Regarder une vidéo",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Non merci",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Demander l'accès",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Mettre à niveau",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Image de la mise à niveau des sentiments",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Activez l’analyse du ressenti d’un simple clic. Les réponses texte sont classées dans différentes catégories : (sentiment/ressenti) positif, neutre ou négatif pour faire ressortir l'aspect émotionnel dissimulé dans les réponses des participants.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Sachez immédiatement ce que les personnes ressentent.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Analyse du ressenti",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Ressenti : ACTIVÉ} false {Ressenti : DÉSACTIVÉ} other {Ressenti : autre}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Sentiments : CHARGEMENT EN COURS...",
	"SlideBoxNavigation.NEXT_BUTTON": "SUIVANT",
	"SlideBoxNavigation.NEXT_SLIDE": "Diapositive suivante",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "Précédent",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Diapositive précédente",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} sur {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Effacer",
	"Slider.STEP_MESSAGE": "Nous avons modifié le nombre que vous avez saisi en fonction de l'échelle du curseur de défilement.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Réponse(s) obtenue(s) : {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "Q{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Question(s) ignorée(s) : {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "L'intersection des courbes « bon marché » et « cher » donne une idée générale du prix de votre produit.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Sensibilité aux prix (Van Westendorp)",
	"SrText.CORRECT": "Correct",
	"SrText.INCORRECT": "Incorrect",
	"StarRating.Heart": "{count, plural, one {{count} cœur} other {{count} cœurs}}",
	"StarRating.STAR": "{count, plural, one {{count} étoile} other {{count} étoiles}}",
	"StarRating.Smiley": "{count, plural, one {{count} smiley} other {{count} smileys}}",
	"StarRating.Thumb": "{count, plural, one {{count} pouce} other {{count} pouces}}",
	"TabList.BETA_BADGE": "Bêta",
	"TabList.LOCK_ICON": "Icône de verrouillage de l’onglet des balises",
	"TabList.RESPONSES": "Réponses ({responseCount})",
	"TabList.TAGS": "Balises ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "Analyse thématique",
	"TabList.WORD_CLOUD": "NUAGE DE MOTS",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Développer le menu",
	"TagFilterMenu.MENU_LABEL": "Filtrer : par balise",
	"TagList.TAGS": "Balises",
	"TagList.TAGS_KEYWORDS": "Mots-clés",
	"TagList.TAG_CONTROLS": "Contrôles des balises",
	"TagList.TAG_NUMBER_PERCENT": "Pourcentage de balises et nombre de balises",
	"TagList.TAG_PERCENTAGE_BAR": "Barre de pourcentage des balises",
	"TagListItem.DELETE": "Supprimer",
	"TagListItem.EDIT": "Modifier",
	"TagListItem.VIEW_ALL": "Tout afficher",
	"TagListPanel.LOADING": "Chargement des balises en cours",
	"TagListPanel.NEW_TAG": "+ Nouvelle balise",
	"TagListPanel.TAGS": "Balises",
	"TagMenuContent.APPLY_ACTIONS": "Appliquer",
	"TagMenuContent.CLEAR_ACTIONS": "Effacer",
	"TagMenuContent.CLOSE_MENU": "Annuler",
	"TagMenuContent.DUPLICATED_NAME": "Une balise porte déjà ce nom.",
	"TagMenuContent.NEW_TAG": "Nouvelle balise",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "En savoir plus >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Demander l'accès",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Mettre à niveau",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Fermer",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Recherchez, balisez et filtrez les réponses écrites et visualisez des nuages d'expressions et mots fréquents.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Analyse de texte",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "hh",
	"TimeInputWrapper.hour": "Heure",
	"TimeInputWrapper.minutes": "Minutes",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Heure",
	"ToggleableEmailField.checkboxLabel": "Recevoir une copie de mes réponses par email",
	"ToggleableEmailField.emailLabel": "Saisissez votre adresse email",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Supprimer",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted} réponse ne sera plus balisée.} other {{responseCountFormatted} réponses ne seront plus balisées.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Cette action supprime la balise de toutes les réponses balisées et est irréversible.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Supprimer la balise ?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Enregistrer",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Enregistrer les modifications ?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Cela supprime la balise de toutes les réponses. La balise est à nouveau appliquée aux réponses qui \n      correspondent aux mots-clés modifiés.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Annuler",
	"UpgradeBanner.SUBTITLE": "Passez à un abonnement supérieur pour personnaliser votre nuage de mots.",
	"UpgradeBanner.TITLE": "Inspiré(e) ?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% des réponses",
	"VanWestendorpChartDataTable.PRICE": "Prix",
	"VanWestendorpChartDataTable.RESPONSES": "Réponses ",
	"WordCloudCustomizationButton.CUSTOMIZE": "Personnaliser",
	"WordCloudCustomizationFooter.APPLY": "Appliquer",
	"WordCloudCustomizationFooter.CANCEL": "Annuler",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Appliquer à tous les nuages de mots du sondage (certains ne seront peut-être pas modifiés)",
	"WordCloudCustomizationFooter.NO_THANKS": "Non merci",
	"WordCloudCustomizationFooter.REQUEST": "Demander l'accès",
	"WordCloudCustomizationFooter.SAVE": "Enregistrer",
	"WordCloudCustomizationFooter.UPGRADE": "Mettre à niveau",
	"WordCloudCustomizationTabs.COLOR": "Couleur",
	"WordCloudCustomizationTabs.FONT": "Police",
	"WordCloudCustomizationTabs.RANGE": "PLAGE",
	"WordCloudLegend.FEWER": "moins",
	"WordCloudLegend.MORE": "plus",
	"WordCloudLegend.TITLE": "Nombre d'occurrences",
	"WordCloudListFooter.COMBINED_WORDS": "Mots combinés",
	"WordCloudListFooter.HIDDEN_WORDS": "Mots masqués",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Développer le menu",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Combiner avec",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Modifier les mots combinés",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Développer le menu",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Masquer dans le nuage de mots",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Dissocier les mots combinés",
	"WordCloudLoadingState.LOADING": "Chargement du nuage de mots en cours...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Bouton Fermer",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Découvrez les fonctionnalités de personnalisation des nuages de mots.",
	"WordCloudPreviewOverlay.TITLE": "Essayez !",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Affichage en nuage",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Affichage en liste",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Occurrences : {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}e mot le plus important",
	"WordCount.IN_PROGRESS": "Analyse de texte en cours...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Aucun mot important n’a été trouvé.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Affichage de {count} mot } other {Affichage de {count} mots }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtrer par ressenti positif} negative {Filtrer par ressenti négatif} neutral {Filtrer par ressenti neutre} other {Filtrer par ressenti non décelé}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positif} negative {Négatif} neutral {Neutre} other {Inconnu}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positif : {percentage,number,percent}} negative {Négatif : {percentage,number,percent}} neutral {Neutre : {percentage,number,percent}} other {Non décelé : {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "Annuler",
	"copyTagEditor.DUPLICATED_NAME": "Une balise porte déjà ce nom.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Saisissez le nom de cette balise",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Saisissez un ou plusieurs mots-clés",
	"copyTagEditor.KEYWORDS": "<strong>Mots-clés</strong> (facultatif)",
	"copyTagEditor.SAVE": "Enregistrer",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Les balises s’appliquent à toutes les réponses correspondant aux mots-clés",
	"copyTagEditor.TAG_NAME": "<strong>Nom de la balise</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Vous devez indiquer le nom de la balise.",
	"copyTagEditor.TAG_NAME_SPACES": "Le nom de la balise ne peut pas être composé uniquement d'espaces.",
	"copyTagEditor.TAG_TOO_LONG": "La balise doit comporter moins de 128 caractères.",
	"countriesL10nMessages.AC": "Île de l’Ascension",
	"countriesL10nMessages.AD": "Andorre",
	"countriesL10nMessages.AE": "Émirats arabes unis",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua-et-Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanie",
	"countriesL10nMessages.AM": "Arménie",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentine",
	"countriesL10nMessages.AS": "Samoa américaines",
	"countriesL10nMessages.AT": "Autriche",
	"countriesL10nMessages.AU": "Australie",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Îles d'Åland",
	"countriesL10nMessages.AZ": "Azerbaïdjan",
	"countriesL10nMessages.BA": "Bosnie-Herzégovine",
	"countriesL10nMessages.BB": "Barbade",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgique",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgarie",
	"countriesL10nMessages.BH": "Bahreïn",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Bénin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Bermudes",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivie",
	"countriesL10nMessages.BQ": "Bonaire, Saint-Eustache et Saba",
	"countriesL10nMessages.BR": "Brésil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhoutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Îles Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, République démocratique du",
	"countriesL10nMessages.CF": "République centrafricaine",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Suisse",
	"countriesL10nMessages.CI": "Côte d'Ivoire",
	"countriesL10nMessages.CK": "Îles Cook",
	"countriesL10nMessages.CL": "Chili",
	"countriesL10nMessages.CM": "Cameroun",
	"countriesL10nMessages.CN": "Chine",
	"countriesL10nMessages.CO": "Colombie",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cap-Vert",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Île Christmas",
	"countriesL10nMessages.CY": "Chypre",
	"countriesL10nMessages.CZ": "République Tchèque",
	"countriesL10nMessages.DE": "Allemagne",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Danemark",
	"countriesL10nMessages.DM": "Dominique",
	"countriesL10nMessages.DO": "République dominicaine",
	"countriesL10nMessages.DZ": "Algérie",
	"countriesL10nMessages.EC": "Équateur",
	"countriesL10nMessages.EE": "Estonie",
	"countriesL10nMessages.EG": "Égypte",
	"countriesL10nMessages.EH": "Sahara occidental",
	"countriesL10nMessages.ER": "Érythrée",
	"countriesL10nMessages.ES": "Espagne",
	"countriesL10nMessages.ET": "Éthiopie",
	"countriesL10nMessages.FI": "Finlande",
	"countriesL10nMessages.FJ": "Fidji",
	"countriesL10nMessages.FK": "Îles Malouines",
	"countriesL10nMessages.FM": "États fédérés de Micronésie",
	"countriesL10nMessages.FO": "Îles Féroé",
	"countriesL10nMessages.FR": "France",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Royaume-Uni",
	"countriesL10nMessages.GD": "Grenade",
	"countriesL10nMessages.GE": "Géorgie",
	"countriesL10nMessages.GF": "Guyane française",
	"countriesL10nMessages.GG": "Guernesey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenland",
	"countriesL10nMessages.GM": "Gambie",
	"countriesL10nMessages.GN": "Guinée",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Guinée équatoriale",
	"countriesL10nMessages.GR": "Grèce",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinée-Bissau",
	"countriesL10nMessages.GY": "Guyane",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croatie",
	"countriesL10nMessages.HT": "Haïti",
	"countriesL10nMessages.HU": "Hongrie",
	"countriesL10nMessages.ID": "Indonésie",
	"countriesL10nMessages.IE": "Irlande",
	"countriesL10nMessages.IL": "Israël",
	"countriesL10nMessages.IM": "Île de Man",
	"countriesL10nMessages.IN": "Inde",
	"countriesL10nMessages.IO": "Territoire britannique de l'océan Indien",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islande",
	"countriesL10nMessages.IT": "Italie",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaïque",
	"countriesL10nMessages.JO": "Jordanie",
	"countriesL10nMessages.JP": "Japon",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirghizistan",
	"countriesL10nMessages.KH": "Cambodge",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Îles Comores",
	"countriesL10nMessages.KN": "Saint-Kitts-et-Nevis",
	"countriesL10nMessages.KR": "Corée du Sud",
	"countriesL10nMessages.KW": "Koweït",
	"countriesL10nMessages.KY": "Îles Caïmans",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Liban",
	"countriesL10nMessages.LC": "Sainte-Lucie",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Libéria",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lituanie",
	"countriesL10nMessages.LU": "Luxembourg",
	"countriesL10nMessages.LV": "Lettonie",
	"countriesL10nMessages.LY": "Libye",
	"countriesL10nMessages.MA": "Maroc",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldavie",
	"countriesL10nMessages.ME": "République du Monténégro",
	"countriesL10nMessages.MF": "Saint-Martin (Antilles françaises)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Îles Marshall",
	"countriesL10nMessages.MK": "Macédoine",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolie",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Îles Mariannes du Nord",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritanie",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malte",
	"countriesL10nMessages.MU": "Île Maurice",
	"countriesL10nMessages.MV": "Maldives",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexique",
	"countriesL10nMessages.MY": "Malaisie",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibie",
	"countriesL10nMessages.NC": "Nouvelle-Calédonie",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Île Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Pays-Bas",
	"countriesL10nMessages.NO": "Norvège",
	"countriesL10nMessages.NP": "Népal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nouvelle-Zélande",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Pérou",
	"countriesL10nMessages.PF": "Polynésie française",
	"countriesL10nMessages.PG": "Papouasie-Nouvelle-Guinée",
	"countriesL10nMessages.PH": "Philippines",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Pologne",
	"countriesL10nMessages.PM": "Saint-Pierre-et-Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestine",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palaos",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Île de la Réunion",
	"countriesL10nMessages.RO": "Roumanie",
	"countriesL10nMessages.RS": "Serbie",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Arabie saoudite",
	"countriesL10nMessages.SB": "Îles Salomon",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Suède",
	"countriesL10nMessages.SG": "Singapour",
	"countriesL10nMessages.SH": "Sainte-Hélène",
	"countriesL10nMessages.SI": "Slovénie",
	"countriesL10nMessages.SJ": "Svalbard et Jan Mayen",
	"countriesL10nMessages.SK": "Slovaquie",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "Saint-Marin",
	"countriesL10nMessages.SN": "Sénégal",
	"countriesL10nMessages.SO": "Somalie",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Soudan du Sud",
	"countriesL10nMessages.ST": "Sao Tomé-et-Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Saint-Martin",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Îles Turques-et-Caïques",
	"countriesL10nMessages.TD": "Tchad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thaïlande",
	"countriesL10nMessages.TJ": "Tadjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Oriental",
	"countriesL10nMessages.TM": "Turkménistan",
	"countriesL10nMessages.TN": "Tunisie",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turquie",
	"countriesL10nMessages.TT": "Trinité-et-Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taïwan",
	"countriesL10nMessages.TZ": "Tanzanie",
	"countriesL10nMessages.UA": "Ukraine",
	"countriesL10nMessages.UG": "Ouganda",
	"countriesL10nMessages.US": "États-Unis",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Ouzbékistan",
	"countriesL10nMessages.VA": "Cité du Vatican (Saint-Siège)",
	"countriesL10nMessages.VC": "Saint-Vincent-et-les Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Îles Vierges britanniques",
	"countriesL10nMessages.VI": "Îles Vierges américaines",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "République de Vanuatu",
	"countriesL10nMessages.WF": "Wallis-et-Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "République du Yémen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Afrique du Sud",
	"countriesL10nMessages.ZM": "Zambie",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Prix d'indifférence",
	"helper.IPP": "Prix d'indifférence ou IPP (Indifference price point)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Prix optimal ou OPP (Optimal price point)",
	"helper.OPP_DESCRIPTION": "Prix optimal",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Seuil de la valeur relative du prix ou PMC (Point of marginal cheapness)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Seuil de rentabilité marginale ou PME (Point of marginal expensiveness)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Seuil de la valeur relative du prix",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Seuil de rentabilité marginale"
};

var ga = {
	"AddressField.selectCountry": "Roghnaigh tír",
	"PhoneField.countryCode": "Cód Tíre",
	"countriesL10nMessages.AC": "Oileán na Deascabhála",
	"countriesL10nMessages.AD": "Andóra",
	"countriesL10nMessages.AE": "Aontas na nÉimíríochtaí Arabacha",
	"countriesL10nMessages.AF": "an Afganastáin",
	"countriesL10nMessages.AG": "Antigua agus Barbúda",
	"countriesL10nMessages.AI": "Angaíle",
	"countriesL10nMessages.AL": "an Albáin",
	"countriesL10nMessages.AM": "an Airméin",
	"countriesL10nMessages.AO": "Angóla",
	"countriesL10nMessages.AR": "an Airgintín",
	"countriesL10nMessages.AS": "Samó Mheiriceá",
	"countriesL10nMessages.AT": "an Ostair",
	"countriesL10nMessages.AU": "an Astráil",
	"countriesL10nMessages.AW": "Arúba",
	"countriesL10nMessages.AX": "Oileáin Åland",
	"countriesL10nMessages.AZ": "an Asarbaiseáin",
	"countriesL10nMessages.BA": "an Bhoisnia agus an Heirseagaivéin",
	"countriesL10nMessages.BB": "Barbadós",
	"countriesL10nMessages.BD": "an Bhanglaidéis",
	"countriesL10nMessages.BE": "an Bheilg",
	"countriesL10nMessages.BF": "Buircíne Fasó",
	"countriesL10nMessages.BG": "an Bhulgáir",
	"countriesL10nMessages.BH": "Bairéin",
	"countriesL10nMessages.BI": "an Bhurúin",
	"countriesL10nMessages.BJ": "Beinin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Beirmiúda",
	"countriesL10nMessages.BN": "Brúiné Dárasalám",
	"countriesL10nMessages.BO": "an Bholaiv",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius agus Saba",
	"countriesL10nMessages.BR": "an Bhrasaíl",
	"countriesL10nMessages.BS": "na Bahámaí",
	"countriesL10nMessages.BT": "an Bhútáin",
	"countriesL10nMessages.BW": "an Bhotsuáin",
	"countriesL10nMessages.BZ": "an Bheilís",
	"countriesL10nMessages.CA": "Ceanada",
	"countriesL10nMessages.CC": "Oileáin Cocos (Keeling)",
	"countriesL10nMessages.CD": "Chongó, Poblacht Dhaonlathach an",
	"countriesL10nMessages.CF": "Poblacht na hAfraice Láir",
	"countriesL10nMessages.CG": "an Congó",
	"countriesL10nMessages.CH": "an Eilvéis",
	"countriesL10nMessages.CI": "an Cósta Eabhair",
	"countriesL10nMessages.CK": "Oileáin Cook",
	"countriesL10nMessages.CL": "an tSile",
	"countriesL10nMessages.CM": "Camarún",
	"countriesL10nMessages.CN": "an tSín",
	"countriesL10nMessages.CO": "an Cholóim",
	"countriesL10nMessages.CR": "Cósta Ríce",
	"countriesL10nMessages.CV": "Cabo Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Oileán na Nollag",
	"countriesL10nMessages.CY": "an Chipir",
	"countriesL10nMessages.CZ": "Poblacht na Seice",
	"countriesL10nMessages.DE": "an Ghearmáin",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "an Danmhairg",
	"countriesL10nMessages.DM": "Doiminice",
	"countriesL10nMessages.DO": "an Phoblacht Dhoiminiceach",
	"countriesL10nMessages.DZ": "an Ailgéir",
	"countriesL10nMessages.EC": "Eacuadór",
	"countriesL10nMessages.EE": "an Eastóin",
	"countriesL10nMessages.EG": "an Éigipt",
	"countriesL10nMessages.EH": "an Sahára Thiar",
	"countriesL10nMessages.ER": "an Eiritré",
	"countriesL10nMessages.ES": "an Spáinn",
	"countriesL10nMessages.ET": "an Aetóip",
	"countriesL10nMessages.FI": "an Fhionlainn",
	"countriesL10nMessages.FJ": "Fidsí",
	"countriesL10nMessages.FK": "Oileáin Fháclainne",
	"countriesL10nMessages.FM": "Stáit Chónaidhme na Micrinéise",
	"countriesL10nMessages.FO": "Oileáin Fharó",
	"countriesL10nMessages.FR": "an Fhrainc",
	"countriesL10nMessages.GA": "an Ghabúin",
	"countriesL10nMessages.GB": "an Ríocht Aontaithe",
	"countriesL10nMessages.GD": "Greanáda",
	"countriesL10nMessages.GE": "an tSeoirsia",
	"countriesL10nMessages.GF": "Guáin na Fraince",
	"countriesL10nMessages.GG": "Geansaí",
	"countriesL10nMessages.GH": "Gána",
	"countriesL10nMessages.GI": "Giobráltar",
	"countriesL10nMessages.GL": "an Ghraonlainn",
	"countriesL10nMessages.GM": "an Ghaimbia",
	"countriesL10nMessages.GN": "an Ghuine",
	"countriesL10nMessages.GP": "Guadalúip",
	"countriesL10nMessages.GQ": "an Ghuine Mheánchiorclach",
	"countriesL10nMessages.GR": "an Ghréig",
	"countriesL10nMessages.GT": "Guatamala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guine Bissau",
	"countriesL10nMessages.GY": "an Ghuáin",
	"countriesL10nMessages.HK": "Hong Cong",
	"countriesL10nMessages.HN": "Hondúras",
	"countriesL10nMessages.HR": "an Chróit",
	"countriesL10nMessages.HT": "Háítí",
	"countriesL10nMessages.HU": "an Ungáir",
	"countriesL10nMessages.ID": "an Indinéis",
	"countriesL10nMessages.IE": "Éire",
	"countriesL10nMessages.IL": "Iosrael",
	"countriesL10nMessages.IM": "Oileán Mhanann",
	"countriesL10nMessages.IN": "an India",
	"countriesL10nMessages.IO": "Críoch Aigéan Indiach na Breataine",
	"countriesL10nMessages.IQ": "an Iaráic",
	"countriesL10nMessages.IS": "an Íoslainn",
	"countriesL10nMessages.IT": "an Iodáil",
	"countriesL10nMessages.JE": "Geirsí",
	"countriesL10nMessages.JM": "Iamáice",
	"countriesL10nMessages.JO": "an Iordáin",
	"countriesL10nMessages.JP": "an tSeapáin",
	"countriesL10nMessages.KE": "an Chéinia",
	"countriesL10nMessages.KG": "an Chirgeastáin",
	"countriesL10nMessages.KH": "an Chambóid",
	"countriesL10nMessages.KI": "Ciribeas",
	"countriesL10nMessages.KM": "Oileáin Chomóra",
	"countriesL10nMessages.KN": "San Críostóir-Nimheas",
	"countriesL10nMessages.KR": "an Chóiré Theas",
	"countriesL10nMessages.KW": "Cuáit",
	"countriesL10nMessages.KY": "Oileáin Cayman",
	"countriesL10nMessages.KZ": "an Chasacstáin",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "an Liobáin",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Lichtinstéin",
	"countriesL10nMessages.LK": "Srí Lanca",
	"countriesL10nMessages.LR": "an Libéir",
	"countriesL10nMessages.LS": "Leosóta",
	"countriesL10nMessages.LT": "an Liotuáin",
	"countriesL10nMessages.LU": "Lucsamburg",
	"countriesL10nMessages.LV": "an Laitvia",
	"countriesL10nMessages.LY": "an Libia",
	"countriesL10nMessages.MA": "Maracó",
	"countriesL10nMessages.MC": "Monacó",
	"countriesL10nMessages.MD": "an Mholdóiv",
	"countriesL10nMessages.ME": "Montainéagró",
	"countriesL10nMessages.MF": "Saint-Martin (cuid na Fraince)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Oileáin Marshall",
	"countriesL10nMessages.MK": "an Mhacadóin",
	"countriesL10nMessages.ML": "Mailí",
	"countriesL10nMessages.MM": "Maenmar",
	"countriesL10nMessages.MN": "an Mhongóil",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Oileán Máirianach Thuaidh",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "an Mháratáin",
	"countriesL10nMessages.MS": "Montsarat",
	"countriesL10nMessages.MT": "Málta",
	"countriesL10nMessages.MU": "Oileán Mhuirís",
	"countriesL10nMessages.MV": "Oileáin Mhaildíve",
	"countriesL10nMessages.MW": "an Mhaláiv",
	"countriesL10nMessages.MX": "Meicsiceo",
	"countriesL10nMessages.MY": "an Mhalaeisia",
	"countriesL10nMessages.MZ": "Mósaimbíc",
	"countriesL10nMessages.NA": "an Namaib",
	"countriesL10nMessages.NC": "an Nua-Chaladóin",
	"countriesL10nMessages.NE": "an Nígir",
	"countriesL10nMessages.NF": "Oileán Norfolk",
	"countriesL10nMessages.NG": "an Nigéir",
	"countriesL10nMessages.NI": "Nicearagua",
	"countriesL10nMessages.NL": "an Ísiltír",
	"countriesL10nMessages.NO": "an Iorua",
	"countriesL10nMessages.NP": "Neipeal",
	"countriesL10nMessages.NR": "Nárú",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "an Nua-Shéalainn",
	"countriesL10nMessages.OM": "Óman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peiriú",
	"countriesL10nMessages.PF": "Polainéis na Fraince",
	"countriesL10nMessages.PG": "Nua-Ghuine Phapua",
	"countriesL10nMessages.PH": "na hOileáin Fhilipíneacha",
	"countriesL10nMessages.PK": "an Phacastáin",
	"countriesL10nMessages.PL": "an Pholainn",
	"countriesL10nMessages.PM": "Saint-Pierre-et-Miquelon",
	"countriesL10nMessages.PR": "Pórtó Ríce",
	"countriesL10nMessages.PS": "an Phalaistín",
	"countriesL10nMessages.PT": "an Phortaingéil",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragua",
	"countriesL10nMessages.QA": "Catar",
	"countriesL10nMessages.RE": "La Réunion",
	"countriesL10nMessages.RO": "an Rómáin",
	"countriesL10nMessages.RS": "an tSeirbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "an Araib Shádach",
	"countriesL10nMessages.SB": "Oileáin Sholaimh",
	"countriesL10nMessages.SC": "na Séiséil",
	"countriesL10nMessages.SE": "an tSualainn",
	"countriesL10nMessages.SG": "Singeapór",
	"countriesL10nMessages.SH": "San Héilin",
	"countriesL10nMessages.SI": "an tSlóivéin",
	"countriesL10nMessages.SJ": "Svalbard agus Jan Mayen",
	"countriesL10nMessages.SK": "an tSlóvaic",
	"countriesL10nMessages.SL": "Siarra Leon",
	"countriesL10nMessages.SM": "San Mairíne",
	"countriesL10nMessages.SN": "an tSeineagáil",
	"countriesL10nMessages.SO": "an tSomáil",
	"countriesL10nMessages.SR": "Suranam",
	"countriesL10nMessages.SS": "an tSúdáin Theas",
	"countriesL10nMessages.ST": "São Tomé agus Príncipe",
	"countriesL10nMessages.SV": "an tSalvadóir",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "an tSuasalainn",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Oileáin na dTurcach agus Caicos",
	"countriesL10nMessages.TD": "Sead",
	"countriesL10nMessages.TG": "Tóga",
	"countriesL10nMessages.TH": "an Téalainn",
	"countriesL10nMessages.TJ": "an Táidsíceastáin",
	"countriesL10nMessages.TK": "Tócaláú",
	"countriesL10nMessages.TL": "Tíomór Thoir",
	"countriesL10nMessages.TM": "an Tuircméanastáin",
	"countriesL10nMessages.TN": "an Túinéis",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "an Tuirc",
	"countriesL10nMessages.TT": "Oileán na Tríonóide agus Tobága",
	"countriesL10nMessages.TV": "Túvalú",
	"countriesL10nMessages.TW": "an Téaváin",
	"countriesL10nMessages.TZ": "an Tansáin",
	"countriesL10nMessages.UA": "an Úcráin",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "na Stáit Aontaithe",
	"countriesL10nMessages.UY": "Uragua",
	"countriesL10nMessages.UZ": "an Úisbéiceastáin",
	"countriesL10nMessages.VA": "Suí Naofa (Stát Chathair na Vatacáine)",
	"countriesL10nMessages.VC": "San Uinseann agus na Greanáidíní",
	"countriesL10nMessages.VE": "Veiniséala",
	"countriesL10nMessages.VG": "Oileáin Bhriotanacha na Maighdean",
	"countriesL10nMessages.VI": "Oileáin Mhaighdean na Stát Aontaithe",
	"countriesL10nMessages.VN": "Vítneam",
	"countriesL10nMessages.VU": "Vanuatú",
	"countriesL10nMessages.WF": "Wallis agus Futúna",
	"countriesL10nMessages.WS": "Samó",
	"countriesL10nMessages.XK": "an Chosaiv",
	"countriesL10nMessages.YE": "Éimin",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "an Afraic Theas",
	"countriesL10nMessages.ZM": "an tSaimbia",
	"countriesL10nMessages.ZW": "an tSiombáib",
	"ToggleableEmailField.checkboxLabel": "Seol cóip de m'fhreagraí chugam trí ríomhphost",
	"ToggleableEmailField.emailLabel": "Cuir isteach do sheoladh ríomhphoist le do thoil"
};

var he = {
	"AddressField.selectCountry": "בחירת מדינה",
	"PhoneField.countryCode": "קוד מדינה",
	"countriesL10nMessages.AC": "אי האסנסיון",
	"countriesL10nMessages.AD": "אנדורה",
	"countriesL10nMessages.AE": "איחוד האמירויות הערביות",
	"countriesL10nMessages.AF": "אפגניסטן",
	"countriesL10nMessages.AG": "אנטיגואה וברבודה",
	"countriesL10nMessages.AI": "אנגווילה",
	"countriesL10nMessages.AL": "אלבניה",
	"countriesL10nMessages.AM": "ארמניה",
	"countriesL10nMessages.AO": "אנגולה",
	"countriesL10nMessages.AR": "ארגנטינה",
	"countriesL10nMessages.AS": "סמואה האמריקנית",
	"countriesL10nMessages.AT": "אוסטריה",
	"countriesL10nMessages.AU": "אוסטרליה",
	"countriesL10nMessages.AW": "ארובה",
	"countriesL10nMessages.AX": "איי אלנד",
	"countriesL10nMessages.AZ": "אזרבייג'ן",
	"countriesL10nMessages.BA": "בוסניה והרצגובינה",
	"countriesL10nMessages.BB": "ברבדוס",
	"countriesL10nMessages.BD": "בנגלדש",
	"countriesL10nMessages.BE": "בלגיה",
	"countriesL10nMessages.BF": "בורקינה פאסו",
	"countriesL10nMessages.BG": "בולגריה",
	"countriesL10nMessages.BH": "בחריין",
	"countriesL10nMessages.BI": "בורונדי",
	"countriesL10nMessages.BJ": "בנין",
	"countriesL10nMessages.BL": "סן ברתלמי",
	"countriesL10nMessages.BM": "ברמודה",
	"countriesL10nMessages.BN": "ברוניי דארוסלאם",
	"countriesL10nMessages.BO": "בוליביה",
	"countriesL10nMessages.BQ": "בונייר, סנט אוסטטיוס וסבא",
	"countriesL10nMessages.BR": "ברזיל",
	"countriesL10nMessages.BS": "איי בהאמה",
	"countriesL10nMessages.BT": "בהוטן",
	"countriesL10nMessages.BW": "בוטסואנה",
	"countriesL10nMessages.BZ": "בליז",
	"countriesL10nMessages.CA": "קנדה",
	"countriesL10nMessages.CC": "איי קוקוס (קילינג)",
	"countriesL10nMessages.CD": "קונגו, הרפובליקה הדמוקרטית",
	"countriesL10nMessages.CF": "הרפובליקה המרכז-אפריקאית",
	"countriesL10nMessages.CG": "קונגו",
	"countriesL10nMessages.CH": "שוויץ",
	"countriesL10nMessages.CI": "חוף השנהב",
	"countriesL10nMessages.CK": "איי קוק",
	"countriesL10nMessages.CL": "צ'ילה",
	"countriesL10nMessages.CM": "קמרון",
	"countriesL10nMessages.CN": "סין",
	"countriesL10nMessages.CO": "קולומביה",
	"countriesL10nMessages.CR": "קוסטה ריקה",
	"countriesL10nMessages.CV": "קייפ ורדה",
	"countriesL10nMessages.CW": "קוראסאו",
	"countriesL10nMessages.CX": "האי כריסטמס",
	"countriesL10nMessages.CY": "קפריסין",
	"countriesL10nMessages.CZ": "הרפובליקה הצ'כית",
	"countriesL10nMessages.DE": "גרמניה",
	"countriesL10nMessages.DJ": "ג'יבוטי",
	"countriesL10nMessages.DK": "דנמרק",
	"countriesL10nMessages.DM": "דומיניקה",
	"countriesL10nMessages.DO": "הרפובליקה הדומיניקנית",
	"countriesL10nMessages.DZ": "אלג'יריה",
	"countriesL10nMessages.EC": "אקוודור",
	"countriesL10nMessages.EE": "אסטוניה",
	"countriesL10nMessages.EG": "מצרים",
	"countriesL10nMessages.EH": "סהרה המערבית",
	"countriesL10nMessages.ER": "אריתריאה",
	"countriesL10nMessages.ES": "ספרד",
	"countriesL10nMessages.ET": "אתיופיה",
	"countriesL10nMessages.FI": "פינלנד",
	"countriesL10nMessages.FJ": "פיג'י",
	"countriesL10nMessages.FK": "איי פוקלנד",
	"countriesL10nMessages.FM": "מיקרונזיה",
	"countriesL10nMessages.FO": "איי פארו",
	"countriesL10nMessages.FR": "צרפת",
	"countriesL10nMessages.GA": "גאבון",
	"countriesL10nMessages.GB": "הממלכה המאוחדת",
	"countriesL10nMessages.GD": "גרנדה",
	"countriesL10nMessages.GE": "גאורגיה",
	"countriesL10nMessages.GF": "גיאנה הצרפתית",
	"countriesL10nMessages.GG": "גרנזי",
	"countriesL10nMessages.GH": "גאנה",
	"countriesL10nMessages.GI": "גיברלטר",
	"countriesL10nMessages.GL": "גרינלנד",
	"countriesL10nMessages.GM": "גמביה",
	"countriesL10nMessages.GN": "גינאה",
	"countriesL10nMessages.GP": "גוואדלופ",
	"countriesL10nMessages.GQ": "גינאה המשוונית",
	"countriesL10nMessages.GR": "יוון",
	"countriesL10nMessages.GT": "גואטמלה",
	"countriesL10nMessages.GU": "גואם",
	"countriesL10nMessages.GW": "גינאה-ביסאו",
	"countriesL10nMessages.GY": "גיאנה",
	"countriesL10nMessages.HK": "הונג קונג",
	"countriesL10nMessages.HN": "הונדורס",
	"countriesL10nMessages.HR": "קרואטיה",
	"countriesL10nMessages.HT": "האיטי",
	"countriesL10nMessages.HU": "הונגריה",
	"countriesL10nMessages.ID": "אינדונזיה",
	"countriesL10nMessages.IE": "אירלנד",
	"countriesL10nMessages.IL": "ישראל",
	"countriesL10nMessages.IM": "האי מאן",
	"countriesL10nMessages.IN": "הודו",
	"countriesL10nMessages.IO": "הטריטוריה הבריטית באוקיינוס ההודי",
	"countriesL10nMessages.IQ": "עיראק",
	"countriesL10nMessages.IS": "איסלנד",
	"countriesL10nMessages.IT": "איטליה",
	"countriesL10nMessages.JE": "ג'רזי",
	"countriesL10nMessages.JM": "ג'מייקה",
	"countriesL10nMessages.JO": "ירדן",
	"countriesL10nMessages.JP": "יפן",
	"countriesL10nMessages.KE": "קניה",
	"countriesL10nMessages.KG": "קירגיזסטן",
	"countriesL10nMessages.KH": "קמבודיה",
	"countriesL10nMessages.KI": "קיריבטי",
	"countriesL10nMessages.KM": "קומורו",
	"countriesL10nMessages.KN": "סנט קיטס ונוויס",
	"countriesL10nMessages.KR": "דרום קוריאה",
	"countriesL10nMessages.KW": "כווית",
	"countriesL10nMessages.KY": "איי קיימן",
	"countriesL10nMessages.KZ": "קזחסטן",
	"countriesL10nMessages.LA": "לאוס",
	"countriesL10nMessages.LB": "לבנון",
	"countriesL10nMessages.LC": "סנט לוסיה",
	"countriesL10nMessages.LI": "ליכטנשטיין",
	"countriesL10nMessages.LK": "סרי לנקה",
	"countriesL10nMessages.LR": "ליבריה",
	"countriesL10nMessages.LS": "לסוטו",
	"countriesL10nMessages.LT": "ליטא",
	"countriesL10nMessages.LU": "לוקסמבורג",
	"countriesL10nMessages.LV": "לטביה",
	"countriesL10nMessages.LY": "לוב",
	"countriesL10nMessages.MA": "מרוקו",
	"countriesL10nMessages.MC": "מונקו",
	"countriesL10nMessages.MD": "מולדובה",
	"countriesL10nMessages.ME": "מונטנגרו",
	"countriesL10nMessages.MF": "סן מרטן (חלק צרפתי)",
	"countriesL10nMessages.MG": "מדגסקר",
	"countriesL10nMessages.MH": "איי מרשל",
	"countriesL10nMessages.MK": "מקדוניה",
	"countriesL10nMessages.ML": "מאלי",
	"countriesL10nMessages.MM": "מיאנמר",
	"countriesL10nMessages.MN": "מונגוליה",
	"countriesL10nMessages.MO": "מקאו",
	"countriesL10nMessages.MP": "האי מריאנה הצפוני",
	"countriesL10nMessages.MQ": "מרטיניק",
	"countriesL10nMessages.MR": "מאוריטניה",
	"countriesL10nMessages.MS": "מונטסראט",
	"countriesL10nMessages.MT": "מלטה",
	"countriesL10nMessages.MU": "מאוריציוס",
	"countriesL10nMessages.MV": "האיים המלדיביים",
	"countriesL10nMessages.MW": "מלאווי",
	"countriesL10nMessages.MX": "מקסיקו",
	"countriesL10nMessages.MY": "מלזיה",
	"countriesL10nMessages.MZ": "מוזמביק",
	"countriesL10nMessages.NA": "נמיביה",
	"countriesL10nMessages.NC": "קלדוניה החדשה",
	"countriesL10nMessages.NE": "ניז'ר",
	"countriesL10nMessages.NF": "האי נורפוק",
	"countriesL10nMessages.NG": "ניגריה",
	"countriesL10nMessages.NI": "ניקרגואה",
	"countriesL10nMessages.NL": "הולנד",
	"countriesL10nMessages.NO": "נורווגיה",
	"countriesL10nMessages.NP": "נפאל",
	"countriesL10nMessages.NR": "נאורו",
	"countriesL10nMessages.NU": "ניואה",
	"countriesL10nMessages.NZ": "ניו זילנד",
	"countriesL10nMessages.OM": "עומאן",
	"countriesL10nMessages.PA": "פנמה",
	"countriesL10nMessages.PE": "פרו",
	"countriesL10nMessages.PF": "פולינזיה הצרפתית",
	"countriesL10nMessages.PG": "פפואה גינאה החדשה",
	"countriesL10nMessages.PH": "הפיליפינים",
	"countriesL10nMessages.PK": "פקיסטן",
	"countriesL10nMessages.PL": "פולין",
	"countriesL10nMessages.PM": "סן-פייר ומיקלון",
	"countriesL10nMessages.PR": "פוארטו ריקו",
	"countriesL10nMessages.PS": "הרשות הפלסטינית",
	"countriesL10nMessages.PT": "פורטוגל",
	"countriesL10nMessages.PW": "פלאו",
	"countriesL10nMessages.PY": "פרגוואי",
	"countriesL10nMessages.QA": "קטאר",
	"countriesL10nMessages.RE": "ראוניון",
	"countriesL10nMessages.RO": "רומניה",
	"countriesL10nMessages.RS": "סרביה",
	"countriesL10nMessages.RW": "רואנדה",
	"countriesL10nMessages.SA": "ערב הסעודית",
	"countriesL10nMessages.SB": "איי שלמה",
	"countriesL10nMessages.SC": "איי סיישל",
	"countriesL10nMessages.SE": "שבדיה",
	"countriesL10nMessages.SG": "סינגפור",
	"countriesL10nMessages.SH": "סנט הלנה",
	"countriesL10nMessages.SI": "סלובניה",
	"countriesL10nMessages.SJ": "סבאלברד ויאן מאיין",
	"countriesL10nMessages.SK": "סלובקיה",
	"countriesL10nMessages.SL": "סיירה לאון",
	"countriesL10nMessages.SM": "סן מרינו",
	"countriesL10nMessages.SN": "סנגל",
	"countriesL10nMessages.SO": "סומליה",
	"countriesL10nMessages.SR": "סורינאם",
	"countriesL10nMessages.SS": "דרום סודן",
	"countriesL10nMessages.ST": "סאו טומה ופרינסיפה",
	"countriesL10nMessages.SV": "אל סלבדור",
	"countriesL10nMessages.SX": "סנט מארטן",
	"countriesL10nMessages.SZ": "סווזילנד",
	"countriesL10nMessages.TA": "טריסטן דה קונה",
	"countriesL10nMessages.TC": "איי טורקס וקאיקוס",
	"countriesL10nMessages.TD": "צ'אד",
	"countriesL10nMessages.TG": "טוגו",
	"countriesL10nMessages.TH": "תאילנד",
	"countriesL10nMessages.TJ": "טג'יקיסטן",
	"countriesL10nMessages.TK": "טוקלאו",
	"countriesL10nMessages.TL": "טימור-לסטה",
	"countriesL10nMessages.TM": "טורקמניסטן",
	"countriesL10nMessages.TN": "תוניסיה",
	"countriesL10nMessages.TO": "טונגה",
	"countriesL10nMessages.TR": "טורקיה",
	"countriesL10nMessages.TT": "טרינידד וטובגו",
	"countriesL10nMessages.TV": "טובאלו",
	"countriesL10nMessages.TW": "טאיוואן",
	"countriesL10nMessages.TZ": "טנזניה",
	"countriesL10nMessages.UA": "אוקראינה",
	"countriesL10nMessages.UG": "אוגנדה",
	"countriesL10nMessages.US": "ארצות הברית",
	"countriesL10nMessages.UY": "אורוגוואי",
	"countriesL10nMessages.UZ": "אוזבקיסטן",
	"countriesL10nMessages.VA": "הכס הקדוש (מדינת הוותיקן)",
	"countriesL10nMessages.VC": "סנט וינסנט והגרנדינים",
	"countriesL10nMessages.VE": "ונצואלה",
	"countriesL10nMessages.VG": "איי הבתולה, בריטניה",
	"countriesL10nMessages.VI": "איי הבתולה, ארה\"ב",
	"countriesL10nMessages.VN": "וייטנאם",
	"countriesL10nMessages.VU": "ונואטו",
	"countriesL10nMessages.WF": "ואליס ופוטונה",
	"countriesL10nMessages.WS": "סמואה",
	"countriesL10nMessages.XK": "קוסובו",
	"countriesL10nMessages.YE": "תימן",
	"countriesL10nMessages.YT": "מיוט",
	"countriesL10nMessages.ZA": "דרום אפריקה",
	"countriesL10nMessages.ZM": "זמביה",
	"countriesL10nMessages.ZW": "זימבבואה",
	"ToggleableEmailField.checkboxLabel": "שלח לי עותק של התשובות שלי בדוא\"ל",
	"ToggleableEmailField.emailLabel": "נא להזין כתובת דוא\"ל"
};

var hi = {
	"AddressField.selectCountry": "देश का चयन करें",
	"PhoneField.countryCode": "देश कोड",
	"countriesL10nMessages.AC": "असेन्शियन द्वीप",
	"countriesL10nMessages.AD": "अंडोरा",
	"countriesL10nMessages.AE": "संयुक्त अरब अमीरात",
	"countriesL10nMessages.AF": "अफ़गानिस्तान",
	"countriesL10nMessages.AG": "एंटीगुआ और बारबुडा",
	"countriesL10nMessages.AI": "अंगूलिया",
	"countriesL10nMessages.AL": "अल्बानिया",
	"countriesL10nMessages.AM": "अर्मेनिया",
	"countriesL10nMessages.AO": "अंगोला",
	"countriesL10nMessages.AR": "अर्जेंटीना",
	"countriesL10nMessages.AS": "अमेरिकन समोआ",
	"countriesL10nMessages.AT": "ऑस्ट्रिया",
	"countriesL10nMessages.AU": "ऑस्ट्रेलिया",
	"countriesL10nMessages.AW": "अरूबा",
	"countriesL10nMessages.AX": "अलैंड द्वीपसमूह",
	"countriesL10nMessages.AZ": "अज़रबैजान",
	"countriesL10nMessages.BA": "बोस्निया और हर्ज़ेगोविना",
	"countriesL10nMessages.BB": "बारबाडोस",
	"countriesL10nMessages.BD": "बांग्लादेश",
	"countriesL10nMessages.BE": "बेल्जियम",
	"countriesL10nMessages.BF": "बुर्किना फ़ासो",
	"countriesL10nMessages.BG": "बुल्गारिया",
	"countriesL10nMessages.BH": "बहरीन",
	"countriesL10nMessages.BI": "बुरुंडी",
	"countriesL10nMessages.BJ": "बेनिन",
	"countriesL10nMessages.BL": "सेंट बार्थेलेमी",
	"countriesL10nMessages.BM": "बरमूडा",
	"countriesL10nMessages.BN": "ब्रुनेई दारूसलम",
	"countriesL10nMessages.BO": "बोलिविया",
	"countriesL10nMessages.BQ": "बोनेयर, सिंट यूस्टैटियस और सबा",
	"countriesL10nMessages.BR": "ब्राज़ील",
	"countriesL10nMessages.BS": "बहामास",
	"countriesL10nMessages.BT": "भूटान",
	"countriesL10nMessages.BW": "बोत्सवाना",
	"countriesL10nMessages.BZ": "बेलिज़",
	"countriesL10nMessages.CA": "कनाडा",
	"countriesL10nMessages.CC": "कोकोस (कीलिंग) द्वीपसमूह",
	"countriesL10nMessages.CD": "कांगो लोकतांत्रिक गणराज्य",
	"countriesL10nMessages.CF": "मध्य अफ़्रीकी गणराज्य",
	"countriesL10nMessages.CG": "कांगो",
	"countriesL10nMessages.CH": "स्विट्ज़रलैंड",
	"countriesL10nMessages.CI": "कोट डे आइवोर",
	"countriesL10nMessages.CK": "कुक द्वीपसमूह",
	"countriesL10nMessages.CL": "चिली",
	"countriesL10nMessages.CM": "कैमरून",
	"countriesL10nMessages.CN": "चीन",
	"countriesL10nMessages.CO": "कोलंबिया",
	"countriesL10nMessages.CR": "कोस्टा रिका",
	"countriesL10nMessages.CV": "केप वर्ड",
	"countriesL10nMessages.CW": "कुरासाओ",
	"countriesL10nMessages.CX": "क्रिसमस द्वीप",
	"countriesL10nMessages.CY": "सायप्रस",
	"countriesL10nMessages.CZ": "चेक गणराज्य",
	"countriesL10nMessages.DE": "जर्मनी",
	"countriesL10nMessages.DJ": "जिबूती",
	"countriesL10nMessages.DK": "डेनमार्क",
	"countriesL10nMessages.DM": "डोमिनिका",
	"countriesL10nMessages.DO": "डोमिनिकन गणराज्य",
	"countriesL10nMessages.DZ": "अल्जीरिया",
	"countriesL10nMessages.EC": "इक्वाडोर",
	"countriesL10nMessages.EE": "एस्टोनिया",
	"countriesL10nMessages.EG": "मिस्र",
	"countriesL10nMessages.EH": "पश्चिमी सहारा",
	"countriesL10nMessages.ER": "इरित्रिया",
	"countriesL10nMessages.ES": "स्पेन",
	"countriesL10nMessages.ET": "इथियोपिया",
	"countriesL10nMessages.FI": "फ़िनलैंड",
	"countriesL10nMessages.FJ": "फ़िजी",
	"countriesL10nMessages.FK": "अलैंड द्वीपसमूह",
	"countriesL10nMessages.FM": "संघीकृत राज्य माइक्रोनेशिया",
	"countriesL10nMessages.FO": "फ़रो द्वीपसमूह",
	"countriesL10nMessages.FR": "फ़्रांस",
	"countriesL10nMessages.GA": "गेबॉन",
	"countriesL10nMessages.GB": "यूनाइटेड किंगडम",
	"countriesL10nMessages.GD": "ग्रेनाडा",
	"countriesL10nMessages.GE": "जॉर्जिया",
	"countriesL10nMessages.GF": "फ़्रेंच गयाना",
	"countriesL10nMessages.GG": "ग्वेर्नसे",
	"countriesL10nMessages.GH": "घाना",
	"countriesL10nMessages.GI": "जिब्राल्टर",
	"countriesL10nMessages.GL": "ग्रीनलैंड",
	"countriesL10nMessages.GM": "गाम्बिया",
	"countriesL10nMessages.GN": "गिनी",
	"countriesL10nMessages.GP": "गुआडेलूप",
	"countriesL10nMessages.GQ": "इक्वेटोरियल गिनी",
	"countriesL10nMessages.GR": "यूनान",
	"countriesL10nMessages.GT": "ग्वाटेमाला",
	"countriesL10nMessages.GU": "गुआम",
	"countriesL10nMessages.GW": "गिनी-बिसाऊ",
	"countriesL10nMessages.GY": "गयाना",
	"countriesL10nMessages.HK": "हांग कांग",
	"countriesL10nMessages.HN": "होंडुरास",
	"countriesL10nMessages.HR": "क्रोएशिया",
	"countriesL10nMessages.HT": "हैती",
	"countriesL10nMessages.HU": "हंगरी",
	"countriesL10nMessages.ID": "इंडोनेशिया",
	"countriesL10nMessages.IE": "आयरलैंड",
	"countriesL10nMessages.IL": "इसराइल",
	"countriesL10nMessages.IM": "आइल ऑफ़ मैन",
	"countriesL10nMessages.IN": "भारत",
	"countriesL10nMessages.IO": "ब्रिटिश हिंद महासागर क्षेत्र",
	"countriesL10nMessages.IQ": "इराक",
	"countriesL10nMessages.IS": "आइसलैंड",
	"countriesL10nMessages.IT": "इटली",
	"countriesL10nMessages.JE": "जर्सी",
	"countriesL10nMessages.JM": "जमैका",
	"countriesL10nMessages.JO": "जॉर्डन",
	"countriesL10nMessages.JP": "जापान",
	"countriesL10nMessages.KE": "केन्या",
	"countriesL10nMessages.KG": "किर्गिज़स्तान",
	"countriesL10nMessages.KH": "कंबोडिया",
	"countriesL10nMessages.KI": "किरिबाती",
	"countriesL10nMessages.KM": "कोमोरोस",
	"countriesL10nMessages.KN": "सेंट किट्स और नेविस",
	"countriesL10nMessages.KR": "दक्षिण कोरिया",
	"countriesL10nMessages.KW": "कुवैत",
	"countriesL10nMessages.KY": "केमैन द्वीपसमूह",
	"countriesL10nMessages.KZ": "कज़ाकस्तान",
	"countriesL10nMessages.LA": "लाओस",
	"countriesL10nMessages.LB": "लेबनान",
	"countriesL10nMessages.LC": "सेंट लूसिया",
	"countriesL10nMessages.LI": "लिस्टेंस्टीन",
	"countriesL10nMessages.LK": "श्रीलंका",
	"countriesL10nMessages.LR": "लाइबेरिया",
	"countriesL10nMessages.LS": "लेसोथो",
	"countriesL10nMessages.LT": "लिथुआनिया",
	"countriesL10nMessages.LU": "लक्ज़मबर्ग",
	"countriesL10nMessages.LV": "लातविया",
	"countriesL10nMessages.LY": "लीबिया",
	"countriesL10nMessages.MA": "मोरक्को",
	"countriesL10nMessages.MC": "मोनाको",
	"countriesL10nMessages.MD": "मोल्दोवा",
	"countriesL10nMessages.ME": "मोंटेनेग्रो",
	"countriesL10nMessages.MF": "सेंट मार्टिन (फ़्रांसीसी हिस्सा)",
	"countriesL10nMessages.MG": "मैडागास्कर",
	"countriesL10nMessages.MH": "मार्शेल द्वीपसमूह",
	"countriesL10nMessages.MK": "मेसिडोनिया",
	"countriesL10nMessages.ML": "माली",
	"countriesL10nMessages.MM": "म्यांमार",
	"countriesL10nMessages.MN": "मंगोलिया",
	"countriesL10nMessages.MO": "मकाउ",
	"countriesL10nMessages.MP": "उत्तरी मरियाना द्वीपसमूह",
	"countriesL10nMessages.MQ": "मार्टिनिक",
	"countriesL10nMessages.MR": "मॉरिटानिया",
	"countriesL10nMessages.MS": "मोंटेसेराट",
	"countriesL10nMessages.MT": "माल्टा",
	"countriesL10nMessages.MU": "मॉरीशस",
	"countriesL10nMessages.MV": "मालदीव",
	"countriesL10nMessages.MW": "मलावी",
	"countriesL10nMessages.MX": "मैक्सिको",
	"countriesL10nMessages.MY": "मलेशिया",
	"countriesL10nMessages.MZ": "मोज़ाम्बिक",
	"countriesL10nMessages.NA": "नामीबिया",
	"countriesL10nMessages.NC": "न्यू कैलेडोनिया",
	"countriesL10nMessages.NE": "नाइजर",
	"countriesL10nMessages.NF": "नॉरफ़्लॉक द्वीपसमूह",
	"countriesL10nMessages.NG": "नाइजीरिया",
	"countriesL10nMessages.NI": "निकारागुआ",
	"countriesL10nMessages.NL": "नीदरलैंड्स",
	"countriesL10nMessages.NO": "नॉर्वे",
	"countriesL10nMessages.NP": "नेपाल",
	"countriesL10nMessages.NR": "नाउरू",
	"countriesL10nMessages.NU": "नियू",
	"countriesL10nMessages.NZ": "न्यूज़ीलैंड",
	"countriesL10nMessages.OM": "ओमान",
	"countriesL10nMessages.PA": "पनामा",
	"countriesL10nMessages.PE": "पेरू",
	"countriesL10nMessages.PF": "फ़्रेंच पॉलिनेशिया",
	"countriesL10nMessages.PG": "पापुआ न्यू गिनी",
	"countriesL10nMessages.PH": "फ़िलीपींस",
	"countriesL10nMessages.PK": "पाकिस्तान",
	"countriesL10nMessages.PL": "पोलैंड",
	"countriesL10nMessages.PM": "सेंट पियर एंड मिकलान",
	"countriesL10nMessages.PR": "प्यूर्टो रिको",
	"countriesL10nMessages.PS": "फ़लस्तीन",
	"countriesL10nMessages.PT": "पुर्तगाल",
	"countriesL10nMessages.PW": "पलाउ",
	"countriesL10nMessages.PY": "पैराग्वे",
	"countriesL10nMessages.QA": "कतर",
	"countriesL10nMessages.RE": "रीयूनियन",
	"countriesL10nMessages.RO": "रोमानिया",
	"countriesL10nMessages.RS": "सर्बिया",
	"countriesL10nMessages.RW": "रवांडा",
	"countriesL10nMessages.SA": "सऊदी अरब",
	"countriesL10nMessages.SB": "सोलोमन द्वीप",
	"countriesL10nMessages.SC": "सेशेल्स",
	"countriesL10nMessages.SE": "स्वीडन",
	"countriesL10nMessages.SG": "सिंगापुर",
	"countriesL10nMessages.SH": "सेंट हेलेना",
	"countriesL10nMessages.SI": "स्लोवेनिया",
	"countriesL10nMessages.SJ": "स्वालबार्ड एंड जेन मेयेन",
	"countriesL10nMessages.SK": "स्लोवाकिया",
	"countriesL10nMessages.SL": "सिएरा लियोन",
	"countriesL10nMessages.SM": "सैन मैरिनो",
	"countriesL10nMessages.SN": "सेनेगल",
	"countriesL10nMessages.SO": "सोमालिया",
	"countriesL10nMessages.SR": "सूरीनाम",
	"countriesL10nMessages.SS": "दक्षिणी सूडान",
	"countriesL10nMessages.ST": "साओ तोमे और प्रिन्सिपी",
	"countriesL10nMessages.SV": "अल सल्वाडोर",
	"countriesL10nMessages.SX": "सिंट मार्टिन",
	"countriesL10nMessages.SZ": "स्वाज़ीलैंड",
	"countriesL10nMessages.TA": "ट्रिस्टन डा कून्हा",
	"countriesL10nMessages.TC": "तुर्क और कैकोस द्वीप",
	"countriesL10nMessages.TD": "चाड",
	"countriesL10nMessages.TG": "टोगो",
	"countriesL10nMessages.TH": "थाईलैंड",
	"countriesL10nMessages.TJ": "ताजिकिस्तान",
	"countriesL10nMessages.TK": "टोकेलाऊ",
	"countriesL10nMessages.TL": "तिमोर-लेस्त",
	"countriesL10nMessages.TM": "तुर्कमेनिस्तान",
	"countriesL10nMessages.TN": "ट्यूनीशिया",
	"countriesL10nMessages.TO": "टोंगा",
	"countriesL10nMessages.TR": "तुर्की",
	"countriesL10nMessages.TT": "त्रिनिदाद और टोबैगो",
	"countriesL10nMessages.TV": "तुवालू",
	"countriesL10nMessages.TW": "ताईवान",
	"countriesL10nMessages.TZ": "तंज़ानिया",
	"countriesL10nMessages.UA": "यूक्रेन",
	"countriesL10nMessages.UG": "युगांडा",
	"countriesL10nMessages.US": "संयुक्त राज्य",
	"countriesL10nMessages.UY": "उरुग्वे",
	"countriesL10nMessages.UZ": "उज़्बेकिस्तान",
	"countriesL10nMessages.VA": "होली सी (वैटिकन सिटी स्टेट)",
	"countriesL10nMessages.VC": "सेंट विंसेंट और द ग्रेनेडींस",
	"countriesL10nMessages.VE": "वेनेज़ुएला",
	"countriesL10nMessages.VG": "वर्जिन द्वीपसमूह, ब्रिटिश",
	"countriesL10nMessages.VI": "वर्जिन द्वीपसमूह, संयुक्त राज्य",
	"countriesL10nMessages.VN": "विएतनाम",
	"countriesL10nMessages.VU": "वनुआतू",
	"countriesL10nMessages.WF": "वैलिस और फ़्यूटूना",
	"countriesL10nMessages.WS": "समोआ",
	"countriesL10nMessages.XK": "कोसोवो",
	"countriesL10nMessages.YE": "यमन",
	"countriesL10nMessages.YT": "मयोटे",
	"countriesL10nMessages.ZA": "दक्षिण अफ़्रीका",
	"countriesL10nMessages.ZM": "ज़ाम्बिया",
	"countriesL10nMessages.ZW": "ज़िम्बाब्वे",
	"ToggleableEmailField.checkboxLabel": "मुझे ईमेल से मेरे जवाबों की एक प्रति भेजें",
	"ToggleableEmailField.emailLabel": "अपना ईमेल पता दर्ज करें"
};

var hr = {
	"AddressField.selectCountry": "Odaberi zemlju",
	"PhoneField.countryCode": "Šifra zemlje",
	"countriesL10nMessages.AC": "Otok Uzašašća",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Ujedinjeni Arapski Emirati",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigva i Barbuda",
	"countriesL10nMessages.AI": "Angvila",
	"countriesL10nMessages.AL": "Albanija",
	"countriesL10nMessages.AM": "Armenija",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Američka Samoa",
	"countriesL10nMessages.AT": "Austrija",
	"countriesL10nMessages.AU": "Australija",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ålandski otoci",
	"countriesL10nMessages.AZ": "Azerbejdžan",
	"countriesL10nMessages.BA": "Bosna i Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladeš",
	"countriesL10nMessages.BE": "Belgija",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bugarska",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Sveti Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivija",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius i Saba",
	"countriesL10nMessages.BR": "Brazil",
	"countriesL10nMessages.BS": "Bahami",
	"countriesL10nMessages.BT": "Butan",
	"countriesL10nMessages.BW": "Bocvana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosovi (Keelingovi) otoci",
	"countriesL10nMessages.CD": "Kongo, Demokratska Republika",
	"countriesL10nMessages.CF": "Centralna Afrička Republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Švicarska",
	"countriesL10nMessages.CI": "Obala Bjelokosti",
	"countriesL10nMessages.CK": "Cookovi otoci",
	"countriesL10nMessages.CL": "Čile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Kolumbija",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Kabo Verde",
	"countriesL10nMessages.CW": "Curacao",
	"countriesL10nMessages.CX": "Božićni otok",
	"countriesL10nMessages.CY": "Cipar",
	"countriesL10nMessages.CZ": "Češka Republika",
	"countriesL10nMessages.DE": "Njemačka",
	"countriesL10nMessages.DJ": "Džibuti",
	"countriesL10nMessages.DK": "Danska",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikanska Republika",
	"countriesL10nMessages.DZ": "Alžir",
	"countriesL10nMessages.EC": "Ekvador",
	"countriesL10nMessages.EE": "Estonija",
	"countriesL10nMessages.EG": "Egipat",
	"countriesL10nMessages.EH": "Zapadna Sahara",
	"countriesL10nMessages.ER": "Eritreja",
	"countriesL10nMessages.ES": "Španjolska",
	"countriesL10nMessages.ET": "Etiopija",
	"countriesL10nMessages.FI": "Finska",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Falklandski otoci",
	"countriesL10nMessages.FM": "Savezne Države Mikronezije",
	"countriesL10nMessages.FO": "Farski otoci",
	"countriesL10nMessages.FR": "Francuska",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Ujedinjeno Kraljevstvo",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzija",
	"countriesL10nMessages.GF": "Francuska Gvajana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grenland",
	"countriesL10nMessages.GM": "Gambija",
	"countriesL10nMessages.GN": "Gvineja",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Ekvatorijalna Gvineja",
	"countriesL10nMessages.GR": "Grčka",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Gvineja Bisau",
	"countriesL10nMessages.GY": "Gvajana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Hrvatska",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Mađarska",
	"countriesL10nMessages.ID": "Indonezija",
	"countriesL10nMessages.IE": "Irska",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Otok Man",
	"countriesL10nMessages.IN": "Indija",
	"countriesL10nMessages.IO": "Britanski teritorij Indijskog oceana",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italija",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamajka",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenija",
	"countriesL10nMessages.KG": "Kirgistan",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komori",
	"countriesL10nMessages.KN": "Sveti Kristofor i Nevis",
	"countriesL10nMessages.KR": "Južna Korea",
	"countriesL10nMessages.KW": "Kuvajt",
	"countriesL10nMessages.KY": "Kajmanski otoci",
	"countriesL10nMessages.KZ": "Kazahstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Sveta Lucija",
	"countriesL10nMessages.LI": "Lihtenštajn",
	"countriesL10nMessages.LK": "Šri Lanka",
	"countriesL10nMessages.LR": "Liberija",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Litva",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Latvija",
	"countriesL10nMessages.LY": "Libija",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavija",
	"countriesL10nMessages.ME": "Crna Gora",
	"countriesL10nMessages.MF": "Sveti Martin (francuski dio)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Maršalovi otoci",
	"countriesL10nMessages.MK": "Makedonija",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mianmar",
	"countriesL10nMessages.MN": "Mongolija",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Sjeverni marijanski otok",
	"countriesL10nMessages.MQ": "Martinik",
	"countriesL10nMessages.MR": "Mauritanija",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauricijus",
	"countriesL10nMessages.MV": "Maldivi",
	"countriesL10nMessages.MW": "Malavi",
	"countriesL10nMessages.MX": "Meksiko",
	"countriesL10nMessages.MY": "Malezija",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibija",
	"countriesL10nMessages.NC": "Nova Kaledonija",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Otok Norfolk",
	"countriesL10nMessages.NG": "Nigerija",
	"countriesL10nMessages.NI": "Nikaragva",
	"countriesL10nMessages.NL": "Nizozemska",
	"countriesL10nMessages.NO": "Norveška",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Novi Zeland",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francuska Polinezija",
	"countriesL10nMessages.PG": "Papua Nova Gvineja",
	"countriesL10nMessages.PH": "Filipini",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poljska",
	"countriesL10nMessages.PM": "Saint Pierre i Miquelon",
	"countriesL10nMessages.PR": "Portoriko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragvaj",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumunjska",
	"countriesL10nMessages.RS": "Srbija",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudijska Arabija",
	"countriesL10nMessages.SB": "Solomonski otoci",
	"countriesL10nMessages.SC": "Sejšeli",
	"countriesL10nMessages.SE": "Švedska",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Sveta Helena",
	"countriesL10nMessages.SI": "Slovenija",
	"countriesL10nMessages.SJ": "Svalbard i Jan Mayen",
	"countriesL10nMessages.SK": "Slovačka",
	"countriesL10nMessages.SL": "Sijera Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalija",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Južni Sudan",
	"countriesL10nMessages.ST": "Sao Tome i Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Otoci Turks i Caicos",
	"countriesL10nMessages.TD": "Čad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tajland",
	"countriesL10nMessages.TJ": "Tadžikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Istočni Timor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunis",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turska",
	"countriesL10nMessages.TT": "Trinidad i Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajvan",
	"countriesL10nMessages.TZ": "Tanzanija",
	"countriesL10nMessages.UA": "Ukrajina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Ujedinjene države",
	"countriesL10nMessages.UY": "Urugvaj",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Sveta Stolica (država Vatikan)",
	"countriesL10nMessages.VC": "Sveti Vincent i Grenadini",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Djevičanski otoci, Britanski",
	"countriesL10nMessages.VI": "Djevičanski otoci, SAD",
	"countriesL10nMessages.VN": "Vijetnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis i Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Južna Afrika",
	"countriesL10nMessages.ZM": "Zambija",
	"countriesL10nMessages.ZW": "Zimbabve",
	"ToggleableEmailField.checkboxLabel": "Pošaljite mi kopiju mojih odgovora putem e-pošte",
	"ToggleableEmailField.emailLabel": "Upišite svoju adresu e-pošte"
};

var hu = {
	"AddressField.selectCountry": "Ország választása",
	"PhoneField.countryCode": "Ország kód",
	"countriesL10nMessages.AC": "Ascension-sziget",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Egyesült Arab Emírségek",
	"countriesL10nMessages.AF": "Afganisztán",
	"countriesL10nMessages.AG": "Antigua és Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albánia",
	"countriesL10nMessages.AM": "Örményország",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentína",
	"countriesL10nMessages.AS": "Amerikai Szamoa",
	"countriesL10nMessages.AT": "Ausztria",
	"countriesL10nMessages.AU": "Ausztrália",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland-szigetek",
	"countriesL10nMessages.AZ": "Azerbajdzsán",
	"countriesL10nMessages.BA": "Bosznia és Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Banglades",
	"countriesL10nMessages.BE": "Belgium",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgária",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Szultanátus",
	"countriesL10nMessages.BO": "Bolívia",
	"countriesL10nMessages.BQ": "BES-szigetek (Bonaire, Sint Eustatius és Saba)",
	"countriesL10nMessages.BR": "Brazília",
	"countriesL10nMessages.BS": "Bahama-szigetek",
	"countriesL10nMessages.BT": "Bhután",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kókusz (Keeling)-szigetek",
	"countriesL10nMessages.CD": "Kongói Demokratikus Köztársaság",
	"countriesL10nMessages.CF": "Közép-Afrikai Köztársaság",
	"countriesL10nMessages.CG": "Kongó",
	"countriesL10nMessages.CH": "Svájc",
	"countriesL10nMessages.CI": "Elefántcsontpart",
	"countriesL10nMessages.CK": "Cook-szigetek",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kína",
	"countriesL10nMessages.CO": "Kolumbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Zöld-foki Köztársaság",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Karácsony-sziget",
	"countriesL10nMessages.CY": "Ciprus",
	"countriesL10nMessages.CZ": "Csehország",
	"countriesL10nMessages.DE": "Németország",
	"countriesL10nMessages.DJ": "Dzsibuti",
	"countriesL10nMessages.DK": "Dánia",
	"countriesL10nMessages.DM": "Dominikai Közösség",
	"countriesL10nMessages.DO": "Dominikai Köztársaság",
	"countriesL10nMessages.DZ": "Algéria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Észtország",
	"countriesL10nMessages.EG": "Egyiptom",
	"countriesL10nMessages.EH": "Nyugat-Szahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanyolország",
	"countriesL10nMessages.ET": "Etiópia",
	"countriesL10nMessages.FI": "Finnország",
	"countriesL10nMessages.FJ": "Fidzsi-szigetek",
	"countriesL10nMessages.FK": "Falkland-szigetek",
	"countriesL10nMessages.FM": "Mikronéziai Szövetségi Államok",
	"countriesL10nMessages.FO": "Feröer-szigetek",
	"countriesL10nMessages.FR": "Franciaország",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Egyesült Királyság",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Grúzia",
	"countriesL10nMessages.GF": "Francia Guyana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghána",
	"countriesL10nMessages.GI": "Gibraltár",
	"countriesL10nMessages.GL": "Grönland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Egyenlítői-Guinea",
	"countriesL10nMessages.GR": "Görögország",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Bissau-Guinea",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Horvátország",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Magyarország",
	"countriesL10nMessages.ID": "Indonézia",
	"countriesL10nMessages.IE": "Írország",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Man-sziget",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Brit Indiai-óceáni Terület",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Izland",
	"countriesL10nMessages.IT": "Olaszország",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordánia",
	"countriesL10nMessages.JP": "Japán",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirgizisztán",
	"countriesL10nMessages.KH": "Kambodzsa",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comore-szigetek",
	"countriesL10nMessages.KN": "Saint Kitts és Nevis",
	"countriesL10nMessages.KR": "Dél-Korea",
	"countriesL10nMessages.KW": "Kuvait",
	"countriesL10nMessages.KY": "Kajmán-szigetek",
	"countriesL10nMessages.KZ": "Kazahsztán",
	"countriesL10nMessages.LA": "Laosz",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Libéria",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litvánia",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Lettország",
	"countriesL10nMessages.LY": "Líbia",
	"countriesL10nMessages.MA": "Marokkó",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegró",
	"countriesL10nMessages.MF": "Szent Márton-sziget (francia rész)",
	"countriesL10nMessages.MG": "Madagaszkár",
	"countriesL10nMessages.MH": "Marshall-szigetek",
	"countriesL10nMessages.MK": "Macedónia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mianmar",
	"countriesL10nMessages.MN": "Mongólia",
	"countriesL10nMessages.MO": "Makaó",
	"countriesL10nMessages.MP": "Északi-Mariana-szigetek",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritánia",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Málta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldív-szigetek",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexikó",
	"countriesL10nMessages.MY": "Malajzia",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namíbia",
	"countriesL10nMessages.NC": "Új-Kaledónia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolk-sziget",
	"countriesL10nMessages.NG": "Nigéria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Hollandia",
	"countriesL10nMessages.NO": "Norvégia",
	"countriesL10nMessages.NP": "Nepál",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Új-Zéland",
	"countriesL10nMessages.OM": "Omán",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francia Polinézia",
	"countriesL10nMessages.PG": "Pápua Új-Guinea",
	"countriesL10nMessages.PH": "Fülöp-szigetek",
	"countriesL10nMessages.PK": "Pakisztán",
	"countriesL10nMessages.PL": "Lengyelország",
	"countriesL10nMessages.PM": "Saint-Pierre és Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palesztina",
	"countriesL10nMessages.PT": "Portugália",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Románia",
	"countriesL10nMessages.RS": "Szerbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Szaúd-Arábia",
	"countriesL10nMessages.SB": "Salamon-szigetek",
	"countriesL10nMessages.SC": "Seychelle-szigetek",
	"countriesL10nMessages.SE": "Svédország",
	"countriesL10nMessages.SG": "Szingapúr",
	"countriesL10nMessages.SH": "Szent Ilona-sziget",
	"countriesL10nMessages.SI": "Szlovénia",
	"countriesL10nMessages.SJ": "Svalbard és Jan Mayen",
	"countriesL10nMessages.SK": "Szlovákia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Szenegál",
	"countriesL10nMessages.SO": "Szomália",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Dél-Szudán",
	"countriesL10nMessages.ST": "Sao Tome és Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Szváziföld",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- és Caicos-szigetek",
	"countriesL10nMessages.TD": "Csád",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thaiföld",
	"countriesL10nMessages.TJ": "Tádzsikisztán",
	"countriesL10nMessages.TK": "Tokelau-szigetek",
	"countriesL10nMessages.TL": "Kelet-Timor",
	"countriesL10nMessages.TM": "Türkmenisztán",
	"countriesL10nMessages.TN": "Tunézia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Törökország",
	"countriesL10nMessages.TT": "Trinidad és Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajvan",
	"countriesL10nMessages.TZ": "Tanzánia",
	"countriesL10nMessages.UA": "Ukrajna",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Egyesült Államok",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Üzbegisztán",
	"countriesL10nMessages.VA": "Apostoli Szentszék (Vatikánvárosi Állam)",
	"countriesL10nMessages.VC": "Saint Vincent és a Grenadine-szigetek",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Brit Virgin-szigetek",
	"countriesL10nMessages.VI": "Virgin-szigetek, USA",
	"countriesL10nMessages.VN": "Vietnám",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis és Futuna",
	"countriesL10nMessages.WS": "Szamoa",
	"countriesL10nMessages.XK": "Koszovó",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Dél-Afrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Válaszaim másolatát küldje el e-mailben",
	"ToggleableEmailField.emailLabel": "Írja be az e-mail-címét"
};

var hy = {
	"AddressField.selectCountry": "Երկիր ընտրել",
	"PhoneField.countryCode": "Երկրի կոդը",
	"countriesL10nMessages.AC": "Համբարձման կղզի",
	"countriesL10nMessages.AD": "Անդորրա",
	"countriesL10nMessages.AE": "Արաբական Միացյալ Էմիրություններ",
	"countriesL10nMessages.AF": "Աֆղանստան",
	"countriesL10nMessages.AG": "Անտիգուա և Բարբուդա",
	"countriesL10nMessages.AI": "Անգուիլա",
	"countriesL10nMessages.AL": "Ալբանիա",
	"countriesL10nMessages.AM": "Հայաստան",
	"countriesL10nMessages.AO": "Անգոլա",
	"countriesL10nMessages.AR": "Արգենտինա",
	"countriesL10nMessages.AS": "Ամերիկյան Սամոա",
	"countriesL10nMessages.AT": "Ավստրիա",
	"countriesL10nMessages.AU": "Ավստրալիա",
	"countriesL10nMessages.AW": "Արուբա",
	"countriesL10nMessages.AX": "Ալանդյան կղզիներ",
	"countriesL10nMessages.AZ": "Ադրբեջան",
	"countriesL10nMessages.BA": "Բոսնիա և Հերցեգովինա",
	"countriesL10nMessages.BB": "Բարբադոս",
	"countriesL10nMessages.BD": "Բանգլադեշ",
	"countriesL10nMessages.BE": "Բելգիա",
	"countriesL10nMessages.BF": "Բուրկինա Ֆասո",
	"countriesL10nMessages.BG": "Բուլղարիա",
	"countriesL10nMessages.BH": "Բահրեյն",
	"countriesL10nMessages.BI": "Բուրունդի",
	"countriesL10nMessages.BJ": "Բենին",
	"countriesL10nMessages.BL": "Սուրբ Բարդուղիմեոսի կղզի",
	"countriesL10nMessages.BM": "Բերմուդա",
	"countriesL10nMessages.BN": "Բրունեյ",
	"countriesL10nMessages.BO": "Բոլիվիա",
	"countriesL10nMessages.BQ": "Բոնեյրե, Սինտ Էվստատիուս և Սաբա",
	"countriesL10nMessages.BR": "Բրազիլիա",
	"countriesL10nMessages.BS": "Բահամյան կղզիներ",
	"countriesL10nMessages.BT": "Բութան",
	"countriesL10nMessages.BW": "Բոտսվանա",
	"countriesL10nMessages.BZ": "Բելիզ",
	"countriesL10nMessages.CA": "Կանադա",
	"countriesL10nMessages.CC": "Կոկոսյան կղզիներ",
	"countriesL10nMessages.CD": "Կոնգոյի Դեմոկրատական Հանրապետություն",
	"countriesL10nMessages.CF": "Կենտրոնական Աֆրիկյան Հանրապետություն",
	"countriesL10nMessages.CG": "Կոնգո",
	"countriesL10nMessages.CH": "Շվեյցարիա",
	"countriesL10nMessages.CI": "Կոտ դ'Իվուար",
	"countriesL10nMessages.CK": "Կուկի Կղզիներ",
	"countriesL10nMessages.CL": "Չիլի",
	"countriesL10nMessages.CM": "Կամերուն",
	"countriesL10nMessages.CN": "Չինաստան",
	"countriesL10nMessages.CO": "Կոլումբիա",
	"countriesL10nMessages.CR": "Կոստա Ռիկա",
	"countriesL10nMessages.CV": "Կաբո Վերդե",
	"countriesL10nMessages.CW": "Կյուրասաո",
	"countriesL10nMessages.CX": "Սուրբ Ծննդյան կղզի",
	"countriesL10nMessages.CY": "Կիպրոս",
	"countriesL10nMessages.CZ": "Չեխիայի Հանրապետություն",
	"countriesL10nMessages.DE": "Գերմանիա",
	"countriesL10nMessages.DJ": "Ջիբութի",
	"countriesL10nMessages.DK": "Դանիա",
	"countriesL10nMessages.DM": "Դոմինիկա",
	"countriesL10nMessages.DO": "Դոմինիկյան Հանրապետություն",
	"countriesL10nMessages.DZ": "Ալժիր",
	"countriesL10nMessages.EC": "Էկվադոր",
	"countriesL10nMessages.EE": "Էստոնիա",
	"countriesL10nMessages.EG": "Եգիպտոս",
	"countriesL10nMessages.EH": "Արևմտյան Սահարա",
	"countriesL10nMessages.ER": "Էրիթրեա",
	"countriesL10nMessages.ES": "Իսպանիա",
	"countriesL10nMessages.ET": "Եթովպիա",
	"countriesL10nMessages.FI": "Ֆինլանդիա",
	"countriesL10nMessages.FJ": "Ֆիջի",
	"countriesL10nMessages.FK": "Ֆոլկլենդյան կղզիներ",
	"countriesL10nMessages.FM": "Միկրոնեզիայի Դաշնային Նահանգներ",
	"countriesL10nMessages.FO": "Ֆարերյան կղզիներ",
	"countriesL10nMessages.FR": "Ֆրանսիա",
	"countriesL10nMessages.GA": "Գաբոն",
	"countriesL10nMessages.GB": "Միացյալ Թագավորություն",
	"countriesL10nMessages.GD": "Գրենադա",
	"countriesL10nMessages.GE": "Վրաստան",
	"countriesL10nMessages.GF": "Ֆրանսիական Գվիանա",
	"countriesL10nMessages.GG": "Գերնսի",
	"countriesL10nMessages.GH": "Գանա",
	"countriesL10nMessages.GI": "Ջիբրալթար",
	"countriesL10nMessages.GL": "Գրենլանդիա",
	"countriesL10nMessages.GM": "Գամբիա",
	"countriesL10nMessages.GN": "Գվինեա",
	"countriesL10nMessages.GP": "Գվադելուպե",
	"countriesL10nMessages.GQ": "Հասարակածային Գվինեա",
	"countriesL10nMessages.GR": "Հունաստան",
	"countriesL10nMessages.GT": "Գվատեմալա",
	"countriesL10nMessages.GU": "Գուամ",
	"countriesL10nMessages.GW": "Գվինեա-Բիսաու",
	"countriesL10nMessages.GY": "Գայանա",
	"countriesL10nMessages.HK": "Հոնկ Կոնգ",
	"countriesL10nMessages.HN": "Հոնդուրաս",
	"countriesL10nMessages.HR": "Խորվաթիա",
	"countriesL10nMessages.HT": "Հաիթի",
	"countriesL10nMessages.HU": "Հունգարիա",
	"countriesL10nMessages.ID": "Ինդոնեզիա",
	"countriesL10nMessages.IE": "Իռլանդիա",
	"countriesL10nMessages.IL": "Իսրայել",
	"countriesL10nMessages.IM": "Մեն կղզի",
	"countriesL10nMessages.IN": "Հնդկաստան",
	"countriesL10nMessages.IO": "Հնդկական օվկիանոսի բրիտանական տարածքներ",
	"countriesL10nMessages.IQ": "Իրաք",
	"countriesL10nMessages.IS": "Իսլանդիա",
	"countriesL10nMessages.IT": "Իտալիա",
	"countriesL10nMessages.JE": "Ջերսի",
	"countriesL10nMessages.JM": "Ճամայկա",
	"countriesL10nMessages.JO": "Հորդանան",
	"countriesL10nMessages.JP": "Ճապոնիա",
	"countriesL10nMessages.KE": "Քենիա",
	"countriesL10nMessages.KG": "Ղրղզստան",
	"countriesL10nMessages.KH": "Կամբոջա",
	"countriesL10nMessages.KI": "Կիրիբատի",
	"countriesL10nMessages.KM": "Կոմորյան կղզիներ",
	"countriesL10nMessages.KN": "Սենթ Քիթս և Նևիս",
	"countriesL10nMessages.KR": "Հարավային Կորեա",
	"countriesL10nMessages.KW": "Քուվեյթ",
	"countriesL10nMessages.KY": "Կայմանյան կղզիներ",
	"countriesL10nMessages.KZ": "Ղազախստան",
	"countriesL10nMessages.LA": "Լաոս",
	"countriesL10nMessages.LB": "Լիբանան",
	"countriesL10nMessages.LC": "Սենթ Լյուսիա",
	"countriesL10nMessages.LI": "Լիխտենշտեյն",
	"countriesL10nMessages.LK": "Շրի Լանկա",
	"countriesL10nMessages.LR": "Լիբերիա",
	"countriesL10nMessages.LS": "Լեսոտո",
	"countriesL10nMessages.LT": "Լիտվա",
	"countriesL10nMessages.LU": "Լյուքսեմբուրգ",
	"countriesL10nMessages.LV": "Լատվիա",
	"countriesL10nMessages.LY": "Լիբիա",
	"countriesL10nMessages.MA": "Մարոկկո",
	"countriesL10nMessages.MC": "Մոնակո",
	"countriesL10nMessages.MD": "Մոլդովա",
	"countriesL10nMessages.ME": "Մոնտենեգրո",
	"countriesL10nMessages.MF": "Սեն Մարտեն (ֆրանսիական մաս)",
	"countriesL10nMessages.MG": "Մադագասկար",
	"countriesL10nMessages.MH": "Մարշալյան կղզիներ",
	"countriesL10nMessages.MK": "Մակեդոնիա",
	"countriesL10nMessages.ML": "Մալի",
	"countriesL10nMessages.MM": "Մյանմա",
	"countriesL10nMessages.MN": "Մոնղոլիա",
	"countriesL10nMessages.MO": "Մակաո",
	"countriesL10nMessages.MP": "Հյուսիսային Մարիանյան կղզի",
	"countriesL10nMessages.MQ": "Մարտինիկա",
	"countriesL10nMessages.MR": "Մավրիտանիա",
	"countriesL10nMessages.MS": "Մոնստերատ",
	"countriesL10nMessages.MT": "Մալթա",
	"countriesL10nMessages.MU": "Մավրիկիոս",
	"countriesL10nMessages.MV": "Մալդիվներ",
	"countriesL10nMessages.MW": "Մալավի",
	"countriesL10nMessages.MX": "Մեքսիկա",
	"countriesL10nMessages.MY": "Մալայզիա",
	"countriesL10nMessages.MZ": "Մոզամբիկ",
	"countriesL10nMessages.NA": "Նամիբիա",
	"countriesL10nMessages.NC": "Նոր Կալեդոնիա",
	"countriesL10nMessages.NE": "Նիգեր",
	"countriesL10nMessages.NF": "Նորֆոլք",
	"countriesL10nMessages.NG": "Նիգերիա",
	"countriesL10nMessages.NI": "Նիկարագուա",
	"countriesL10nMessages.NL": "Նիդեռլանդներ",
	"countriesL10nMessages.NO": "Նորվեգիա",
	"countriesL10nMessages.NP": "Նեպալ",
	"countriesL10nMessages.NR": "Նաուրու",
	"countriesL10nMessages.NU": "Նյուեյ",
	"countriesL10nMessages.NZ": "Նոր Զելանդիա",
	"countriesL10nMessages.OM": "Օման",
	"countriesL10nMessages.PA": "Պանամա",
	"countriesL10nMessages.PE": "Պերու",
	"countriesL10nMessages.PF": "Ֆրանսիական Պոլինեզիա",
	"countriesL10nMessages.PG": "Պապուա Նոր Գվինեա",
	"countriesL10nMessages.PH": "Ֆիլիպիններ",
	"countriesL10nMessages.PK": "Պակիստան",
	"countriesL10nMessages.PL": "Լեհաստան",
	"countriesL10nMessages.PM": "Սեն Պիեռ և Միքելոն",
	"countriesL10nMessages.PR": "Պուերտո Ռիկո",
	"countriesL10nMessages.PS": "Պաղեստին",
	"countriesL10nMessages.PT": "Պորտուգալիա",
	"countriesL10nMessages.PW": "Պալաու",
	"countriesL10nMessages.PY": "Պարագվայ",
	"countriesL10nMessages.QA": "Քաթար",
	"countriesL10nMessages.RE": "Ռեյունյոն",
	"countriesL10nMessages.RO": "Ռումինիա",
	"countriesL10nMessages.RS": "Սերբիա",
	"countriesL10nMessages.RW": "Ռուանդա",
	"countriesL10nMessages.SA": "Սաուդյան Արաբիա",
	"countriesL10nMessages.SB": "Սողոմոնյան Կղզիներ",
	"countriesL10nMessages.SC": "Սեյշելյան կղզիներ",
	"countriesL10nMessages.SE": "Շվեդիա",
	"countriesL10nMessages.SG": "Սինգապուր",
	"countriesL10nMessages.SH": "Սուրբ Հեղինեի կղզի",
	"countriesL10nMessages.SI": "Սլովենիա",
	"countriesL10nMessages.SJ": "Սվալբարդ և Յան Մայեն",
	"countriesL10nMessages.SK": "Սլովակիա",
	"countriesL10nMessages.SL": "Սիերա Լեոնե",
	"countriesL10nMessages.SM": "Սան Մարինո",
	"countriesL10nMessages.SN": "Սենեգալ",
	"countriesL10nMessages.SO": "Սոմալի",
	"countriesL10nMessages.SR": "Սուրինամ",
	"countriesL10nMessages.SS": "Հարավային Սուդան",
	"countriesL10nMessages.ST": "Սան Տոմե և Պրինսիպի",
	"countriesL10nMessages.SV": "Էլ Սալվադոր",
	"countriesL10nMessages.SX": "Սինտ Մաարտեն",
	"countriesL10nMessages.SZ": "Սվազիլենդ",
	"countriesL10nMessages.TA": "Տրիստան դա Կունյա",
	"countriesL10nMessages.TC": "Թերքս և Կայկոս կղզիներ",
	"countriesL10nMessages.TD": "Չադ",
	"countriesL10nMessages.TG": "Տոգո",
	"countriesL10nMessages.TH": "Թաիլանդ",
	"countriesL10nMessages.TJ": "Տաջիկստան",
	"countriesL10nMessages.TK": "Տոկելաու",
	"countriesL10nMessages.TL": "Արևելյան Թիմոր",
	"countriesL10nMessages.TM": "Թուրքմենստան",
	"countriesL10nMessages.TN": "Թունիս",
	"countriesL10nMessages.TO": "Տոնգա",
	"countriesL10nMessages.TR": "Թուրքիա",
	"countriesL10nMessages.TT": "Տրինիդադ և Տոբագո",
	"countriesL10nMessages.TV": "Տուվալու",
	"countriesL10nMessages.TW": "Թայվան",
	"countriesL10nMessages.TZ": "Տանզանիա",
	"countriesL10nMessages.UA": "Ուկրաինա",
	"countriesL10nMessages.UG": "Ուգանդա",
	"countriesL10nMessages.US": "Միացյալ Նահանգներ",
	"countriesL10nMessages.UY": "Ուրուգվայ",
	"countriesL10nMessages.UZ": "Ուզբեկստան",
	"countriesL10nMessages.VA": "Սուրբ Աթոռ (Վատիկան)",
	"countriesL10nMessages.VC": "Սենթ Վինսենթ և Գրենադիններ",
	"countriesL10nMessages.VE": "Վենեսուելա",
	"countriesL10nMessages.VG": "Վիրջինյան կղզիներ, բրիտանական",
	"countriesL10nMessages.VI": "Վիրջինյան կղզիներ, ԱՄՆ",
	"countriesL10nMessages.VN": "Վիետնամ",
	"countriesL10nMessages.VU": "Վանուատու",
	"countriesL10nMessages.WF": "Ուոլիս և Ֆուտունա",
	"countriesL10nMessages.WS": "Սամոա",
	"countriesL10nMessages.XK": "Կոսովո",
	"countriesL10nMessages.YE": "Եմեն",
	"countriesL10nMessages.YT": "Մայոտ",
	"countriesL10nMessages.ZA": "Հարավային Աֆրիկա",
	"countriesL10nMessages.ZM": "Զամբիա",
	"countriesL10nMessages.ZW": "Զիմբաբվե",
	"ToggleableEmailField.checkboxLabel": "Իմ պատասխանների պատճենը էլփոստով ուղարկեք ինձ",
	"ToggleableEmailField.emailLabel": "Մուտքագրեք ձեր էլփոստի հասցեն"
};

var id = {
	"AddressField.selectCountry": "Pilih negara",
	"PhoneField.countryCode": "Kode negara",
	"countriesL10nMessages.AC": "Pulau Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Uni Emirat Arab",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua dan Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Amerika",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Kepulauan Åland",
	"countriesL10nMessages.AZ": "Azerbaijan",
	"countriesL10nMessages.BA": "Bosnia dan Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgia",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius, dan Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Bahama",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kepulauan Cocos (Keeling)",
	"countriesL10nMessages.CD": "Kongo, Republik Demokratis",
	"countriesL10nMessages.CF": "Republik Afrika Tengah",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Swiss",
	"countriesL10nMessages.CI": "Pantai Gading",
	"countriesL10nMessages.CK": "Kepulauan Cook",
	"countriesL10nMessages.CL": "Chili",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Tiongkok",
	"countriesL10nMessages.CO": "Kolumbia",
	"countriesL10nMessages.CR": "Kosta Rika",
	"countriesL10nMessages.CV": "Tanjung Verde",
	"countriesL10nMessages.CW": "Curacao",
	"countriesL10nMessages.CX": "Pulau Natal",
	"countriesL10nMessages.CY": "Siprus",
	"countriesL10nMessages.CZ": "Republik Ceko",
	"countriesL10nMessages.DE": "Jerman",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Denmark",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Republik Dominika",
	"countriesL10nMessages.DZ": "Aljazair",
	"countriesL10nMessages.EC": "Ekuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Mesir",
	"countriesL10nMessages.EH": "Sahara Barat",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanyol",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finlandia",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Kepulauan Falkland",
	"countriesL10nMessages.FM": "Negara Federasi Mikronesia",
	"countriesL10nMessages.FO": "Kepulauan Faroe",
	"countriesL10nMessages.FR": "Prancis",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Britania Raya",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guyana Perancis",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Greenland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Guinea Khatulistiwa",
	"countriesL10nMessages.GR": "Yunani",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroasia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungaria",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irlandia",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Pulau Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Wilayah Samudra Hindia Britania",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islandia",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Yordania",
	"countriesL10nMessages.JP": "Jepang",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirgistan",
	"countriesL10nMessages.KH": "Kamboja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoro",
	"countriesL10nMessages.KN": "Saint Kitts dan Nevis",
	"countriesL10nMessages.KR": "Korea Selatan",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Kepulauan Cayman",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lithuania",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (bagian Prancis)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Kepulauan Marshall",
	"countriesL10nMessages.MK": "Makedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Makau",
	"countriesL10nMessages.MP": "Kepulauan Mariana Utara",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maladewa",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Meksiko",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Kaledonia Baru",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Pulau Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Belanda",
	"countriesL10nMessages.NO": "Norwegia",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Selandia Baru",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinesia Prancis",
	"countriesL10nMessages.PG": "Papua Nugini",
	"countriesL10nMessages.PH": "Filipina",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polandia",
	"countriesL10nMessages.PM": "Saint Pierre dan Miquelon",
	"countriesL10nMessages.PR": "Puerto Riko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Arab Saudi",
	"countriesL10nMessages.SB": "Kepulauan Solomon",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Swedia",
	"countriesL10nMessages.SG": "Singapura",
	"countriesL10nMessages.SH": "Santo Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard dan Jan Mayen",
	"countriesL10nMessages.SK": "Slowakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Sudan Selatan",
	"countriesL10nMessages.ST": "Sao Tome dan Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Eswatini",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Kepulauan Turks dan Caicos",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tajikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turki",
	"countriesL10nMessages.TT": "Trinidad dan Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Amerika Serikat",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Takhta Suci (Negara Kota Vatikan)",
	"countriesL10nMessages.VC": "Saint Vincent dan Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Kepulauan Virgin, Inggris",
	"countriesL10nMessages.VI": "Kepulauan Virgin, AS",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis dan Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yaman",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Afrika Selatan",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Kirimi saya salinan tanggapan saya melalui email",
	"ToggleableEmailField.emailLabel": "Masukkan alamat email Anda"
};

var is = {
	"AddressField.selectCountry": "Velja land",
	"PhoneField.countryCode": "Landsnúmer",
	"countriesL10nMessages.AC": "Ascension-eyja",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Sameinuðu arabísku furstadæmin",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antígva og Barbúda",
	"countriesL10nMessages.AI": "Angvilla",
	"countriesL10nMessages.AL": "Albanía",
	"countriesL10nMessages.AM": "Armenía",
	"countriesL10nMessages.AO": "Angóla",
	"countriesL10nMessages.AR": "Argentína",
	"countriesL10nMessages.AS": "Ameríska Samóa",
	"countriesL10nMessages.AT": "Austurríki",
	"countriesL10nMessages.AU": "Ástralía",
	"countriesL10nMessages.AW": "Arúba",
	"countriesL10nMessages.AX": "Álandseyjar",
	"countriesL10nMessages.AZ": "Aserbaídsjan",
	"countriesL10nMessages.BA": "Bosnía og Hersegóvína",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladess",
	"countriesL10nMessages.BE": "Belgía",
	"countriesL10nMessages.BF": "Búrkína Fasó",
	"countriesL10nMessages.BG": "Búlgaría",
	"countriesL10nMessages.BH": "Barein",
	"countriesL10nMessages.BI": "Búrúndí",
	"countriesL10nMessages.BJ": "Benín",
	"countriesL10nMessages.BL": "Sankti Bartólómeusareyjar",
	"countriesL10nMessages.BM": "Bermúdaeyjar",
	"countriesL10nMessages.BN": "Brúnei Darussalam",
	"countriesL10nMessages.BO": "Bólivía",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius og Saba",
	"countriesL10nMessages.BR": "Brasilía",
	"countriesL10nMessages.BS": "Bahamaeyjar",
	"countriesL10nMessages.BT": "Bútan",
	"countriesL10nMessages.BW": "Botsvana",
	"countriesL10nMessages.BZ": "Belís",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kókoseyjar",
	"countriesL10nMessages.CD": "Lýðstjórnarlýðveldið Kongó",
	"countriesL10nMessages.CF": "Mið-Afríkulýðveldið",
	"countriesL10nMessages.CG": "Kongó",
	"countriesL10nMessages.CH": "Sviss",
	"countriesL10nMessages.CI": "Fílabeinsströndin",
	"countriesL10nMessages.CK": "Færeyjar",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerún",
	"countriesL10nMessages.CN": "Kína",
	"countriesL10nMessages.CO": "Kólumbía",
	"countriesL10nMessages.CR": "Kosta Ríka",
	"countriesL10nMessages.CV": "Grænhöfðaeyjar",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Jólaey",
	"countriesL10nMessages.CY": "Kýpur",
	"countriesL10nMessages.CZ": "Tékkland",
	"countriesL10nMessages.DE": "Þýskaland",
	"countriesL10nMessages.DJ": "Djíbútí",
	"countriesL10nMessages.DK": "Danmörk",
	"countriesL10nMessages.DM": "Dóminíka",
	"countriesL10nMessages.DO": "Dóminíska lýðveldið",
	"countriesL10nMessages.DZ": "Alsír",
	"countriesL10nMessages.EC": "Ekvador",
	"countriesL10nMessages.EE": "Eistland",
	"countriesL10nMessages.EG": "Egyptaland",
	"countriesL10nMessages.EH": "Vestur-Sahara",
	"countriesL10nMessages.ER": "Erítrea",
	"countriesL10nMessages.ES": "Spánn",
	"countriesL10nMessages.ET": "Eþíópía",
	"countriesL10nMessages.FI": "Finnland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Álandseyjar",
	"countriesL10nMessages.FM": "Sambandsríki Míkrónesíu",
	"countriesL10nMessages.FO": "Færeyjar",
	"countriesL10nMessages.FR": "Frakkland",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Bretland",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgía",
	"countriesL10nMessages.GF": "Franska Gvæjana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gíbraltar",
	"countriesL10nMessages.GL": "Grænland",
	"countriesL10nMessages.GM": "Gambía",
	"countriesL10nMessages.GN": "Gínea",
	"countriesL10nMessages.GP": "Gvadelúpeyjar",
	"countriesL10nMessages.GQ": "Miðbaugs-Gínea",
	"countriesL10nMessages.GR": "Grikkland",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Gvam",
	"countriesL10nMessages.GW": "Gínea-Bissá",
	"countriesL10nMessages.GY": "Gvæjana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Hondúras",
	"countriesL10nMessages.HR": "Króatía",
	"countriesL10nMessages.HT": "Haítí",
	"countriesL10nMessages.HU": "Ungverjaland",
	"countriesL10nMessages.ID": "Indónesía",
	"countriesL10nMessages.IE": "Írland",
	"countriesL10nMessages.IL": "Ísrael",
	"countriesL10nMessages.IM": "Mön",
	"countriesL10nMessages.IN": "Indland",
	"countriesL10nMessages.IO": "Bresku Indlandshafseyjar",
	"countriesL10nMessages.IQ": "Írak",
	"countriesL10nMessages.IS": "Ísland",
	"countriesL10nMessages.IT": "Ítalía",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaíka",
	"countriesL10nMessages.JO": "Jórdanía",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenýa",
	"countriesL10nMessages.KG": "Kirgisistan",
	"countriesL10nMessages.KH": "Kambódía",
	"countriesL10nMessages.KI": "Kíribatí",
	"countriesL10nMessages.KM": "Kómorur",
	"countriesL10nMessages.KN": "Saint Kitts og Nevis",
	"countriesL10nMessages.KR": "Suður-Kórea",
	"countriesL10nMessages.KW": "Kúveit",
	"countriesL10nMessages.KY": "Cayman-eyjar",
	"countriesL10nMessages.KZ": "Kasakstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Líbanon",
	"countriesL10nMessages.LC": "Sankti Lúsía",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Líbería",
	"countriesL10nMessages.LS": "Lesótó",
	"countriesL10nMessages.LT": "Litháen",
	"countriesL10nMessages.LU": "Lúxemborg",
	"countriesL10nMessages.LV": "Lettland",
	"countriesL10nMessages.LY": "Líbýa",
	"countriesL10nMessages.MA": "Marokkó",
	"countriesL10nMessages.MC": "Mónakó",
	"countriesL10nMessages.MD": "Moldóva",
	"countriesL10nMessages.ME": "Montenegró",
	"countriesL10nMessages.MF": "Saint Martin (franski hlutinn)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshalleyjar",
	"countriesL10nMessages.MK": "Makedónía",
	"countriesL10nMessages.ML": "Malí",
	"countriesL10nMessages.MM": "Mjanmar",
	"countriesL10nMessages.MN": "Mongólía",
	"countriesL10nMessages.MO": "Makaó",
	"countriesL10nMessages.MP": "Norður-Maríanaeyjar",
	"countriesL10nMessages.MQ": "Martiník",
	"countriesL10nMessages.MR": "Máritanía",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Máritíus",
	"countriesL10nMessages.MV": "Maldívur",
	"countriesL10nMessages.MW": "Malaví",
	"countriesL10nMessages.MX": "Mexíkó",
	"countriesL10nMessages.MY": "Malasía",
	"countriesL10nMessages.MZ": "Mósambík",
	"countriesL10nMessages.NA": "Namibía",
	"countriesL10nMessages.NC": "Nýja-Kaledónía",
	"countriesL10nMessages.NE": "Níger",
	"countriesL10nMessages.NF": "Norfolkeyja",
	"countriesL10nMessages.NG": "Nígería",
	"countriesL10nMessages.NI": "Níkaragva",
	"countriesL10nMessages.NL": "Holland",
	"countriesL10nMessages.NO": "Noregur",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Naúrú",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nýja Sjáland",
	"countriesL10nMessages.OM": "Óman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Perú",
	"countriesL10nMessages.PF": "Franska Pólýnesía",
	"countriesL10nMessages.PG": "Papúa Nýja-Gínea",
	"countriesL10nMessages.PH": "Filippseyjar",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Pólland",
	"countriesL10nMessages.PM": "Saint Pierre og Miquelon",
	"countriesL10nMessages.PR": "Púertó Ríkó",
	"countriesL10nMessages.PS": "Palestína",
	"countriesL10nMessages.PT": "Portúgal",
	"countriesL10nMessages.PW": "Palaú",
	"countriesL10nMessages.PY": "Paragvæ",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rúmenía",
	"countriesL10nMessages.RS": "Serbía",
	"countriesL10nMessages.RW": "Rúanda",
	"countriesL10nMessages.SA": "Sádí-Arabía",
	"countriesL10nMessages.SB": "Salómonseyjar",
	"countriesL10nMessages.SC": "Seychelles-eyjar",
	"countriesL10nMessages.SE": "Svíþjóð",
	"countriesL10nMessages.SG": "Singapúr",
	"countriesL10nMessages.SH": "Sankti Helena",
	"countriesL10nMessages.SI": "Slóvenía",
	"countriesL10nMessages.SJ": "Svalbarði og Jan Mayen",
	"countriesL10nMessages.SK": "Slóvakía",
	"countriesL10nMessages.SL": "Síerra Leóne",
	"countriesL10nMessages.SM": "San Marínó",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Sómalía",
	"countriesL10nMessages.SR": "Súrínam",
	"countriesL10nMessages.SS": "Suður-Súdan",
	"countriesL10nMessages.ST": "Saó Tóme og Prinsípe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Esvatíní",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- og Caicos-eyjar",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Tógó",
	"countriesL10nMessages.TH": "Taíland",
	"countriesL10nMessages.TJ": "Tadsjikistan",
	"countriesL10nMessages.TK": "Tókelá",
	"countriesL10nMessages.TL": "Tímor-Leste",
	"countriesL10nMessages.TM": "Túrkmenistan",
	"countriesL10nMessages.TN": "Túnis",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Tyrkland",
	"countriesL10nMessages.TT": "Trínidad og Tóbagó",
	"countriesL10nMessages.TV": "Túvalú",
	"countriesL10nMessages.TW": "Taívan",
	"countriesL10nMessages.TZ": "Tansanía",
	"countriesL10nMessages.UA": "Úkraína",
	"countriesL10nMessages.UG": "Úganda",
	"countriesL10nMessages.US": "Bandaríkin",
	"countriesL10nMessages.UY": "Úrúgvæ",
	"countriesL10nMessages.UZ": "Úsbekistan",
	"countriesL10nMessages.VA": "Páfagarður (Vatíkanið)",
	"countriesL10nMessages.VC": "Saint Vincent og Grenadínur",
	"countriesL10nMessages.VE": "Venesúela",
	"countriesL10nMessages.VG": "Bresku Jómfrúaeyjar",
	"countriesL10nMessages.VI": "Bresku Jómfrúaeyjar",
	"countriesL10nMessages.VN": "Víetnam",
	"countriesL10nMessages.VU": "Vanúatú",
	"countriesL10nMessages.WF": "Wallis- og Fútúnaeyjar",
	"countriesL10nMessages.WS": "Samóa",
	"countriesL10nMessages.XK": "Kosovó",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Suður-Afríka",
	"countriesL10nMessages.ZM": "Sambía",
	"countriesL10nMessages.ZW": "Simbabve",
	"ToggleableEmailField.checkboxLabel": "Sendu mér afrit af svörum mínum með tölvupósti",
	"ToggleableEmailField.emailLabel": "Sláðu inn netfangið þitt"
};

var it = {
	"ActionMenuTagPin.PIN": "Aggiungi",
	"ActionMenuTagPin.TAG": "Tag",
	"AddressField.selectCountry": "Seleziona paese",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Hanno risposto: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "D{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Hanno saltato la domanda: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Come si sentono le persone:",
	"AsyncJobLoadingState.LOADING": "Caricamento risposte in corso",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Ti faremo sapere quando la procedura sarà stata completata",
	"AsyncJobLoadingState.TAGGING_TITLE": "Taggatura risposte in corso",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "I tag vengono applicati alle risposte esistenti e future. Lascia uno spazio o digita <strong>OR</strong> tra le parole per usare più parole chiave.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Tagga le risposte automaticamente",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Seleziona un mese",
	"CalendarPicker.months": "{months, select, January {Gennaio} February {Febbraio} March {Marzo} April {Aprile} May {Maggio} June {Giugno} July {Luglio} August {Agosto} September {Settembre} October {Ottobre} November {Novembre} December {Dicembre} other {altro}}",
	"CalendarPicker.nextMonth": "Mese successivo",
	"CalendarPicker.previousMonth": "Mese precedente",
	"CalendarPicker.year": "Seleziona un anno",
	"ColorSwatches.TAG": "Tag {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Le nuvole di parole multicolori utilizzano i colori e le dimensioni del carattere per indicare il numero di volte che una parola o frase è stata menzionata.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Uso del colore nelle nuvole di parole",
	"ColorTab.DOWN_ARROW": "Freccia del selettore a tendina",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Multicolore",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Un solo colore",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Annulla",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Crea una nuova parola",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "La nuova parola sostituirà tutte le parole selezionate di seguito.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Modifica parole combinate",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Combina parole",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Crea una nuova parola che sostituisca una o più parole selezionate. Puoi modificare questa impostazione successivamente e ripristinare le tue parole originali.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Digita una nuova parola",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Combina",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Scegli le parole da combinare",
	"CreateTagFromSearch.CLOSE_MENU": "ANNULLA",
	"CreateTagFromSearch.DUPLICATED_NAME": "Esiste già un tag con questo nome.",
	"CreateTagFromSearch.NEW_TAG": "Nuovo tag",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Non sarà taggata \n\t\talcuna risposta} other {{responseCount, plural, one {\n\t\t\t\tSarà taggata \n\t\t\t\n\t\t{responseTaggedFormatted} risposta} other {\n\t\t\t\tSaranno taggate \n\t\t\t\n\t\t{responseTaggedFormatted} risposte}}}}",
	"CreateTagFromSearch.SAVE": "SALVA",
	"CreateTagFromSearch.TAG": "Tag",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Il nome del tag è obbligatorio.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Il nome del tag non può contenere solo spazi.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Tagga le risposte corrispondenti a queste parole chiave. Seleziona <strong>Crea un tag</strong>. Scegli un colore e salva il tag. Il tag viene applicato alle risposte esistenti e future.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Tagga risposte corrispondenti",
	"DashboardMenuContent.CREATE": "CREA",
	"DashboardMenuContent.NAME": "Nome",
	"DashboardMenuContent.NEW_DASHBOARD": "Nuovo dashboard",
	"DateInputWrapper.dateButton": "Apri il calendario",
	"DateInputWrapper.dateFormatIntl": "GG/MM/AAAA",
	"DateInputWrapper.dateFormatUs": "MM/GG/AAAA",
	"DateInputWrapper.dateLabel": "Data",
	"DateTime.errorEnterHours": "Indica l'ora da 0 a 23.",
	"DateTime.errorEnterMinutes": "Indica i minuti da 0 a 59.",
	"DateTime.errorSelectPeriod": "Seleziona AM/PM.",
	"DateTime.errorYearOutOfRange": "Inserire un anno compreso tra 1900 e 9999.",
	"DateTime.formatErrorDateIntl": "Indica la data nel formato GG/MM/AAAA.",
	"DateTime.formatErrorDateUs": "Indica la data nel formato MM/GG/AAAA.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "ANNULLA",
	"EditorButtons.COPY_BUTTON": "COPIA DOMANDA",
	"EditorButtons.MOVE_BUTTON": "SPOSTA DOMANDA",
	"EditorButtons.NEXT_QUESTION_BUTTON": "DOMANDA SUCCESSIVA",
	"EditorButtons.SAVE_BUTTON": "SALVA",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Le tendenze dei dati non si applicano a questa domanda",
	"FallbackComponent.ERROR": "Errore durante il caricamento della domanda",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Seleziona un file</div> o <div class={dragClass}>trascinalo qui</div>",
	"FileUpload.fileUploaded": "File caricato correttamente",
	"FileUpload.fileUploading": "Caricamento file",
	"FileUpload.removeFile": "Rimuovi file",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Sostituisci il file</div> o <div class={dragClass}>trascinalo qui</div>",
	"FileUpload.supportedFiles": "File supportati",
	"FilterMenuContent.APPLY_ACTIONS": "APPLICA",
	"FilterMenuContent.CLEAR_ACTIONS": "Annulla selezione",
	"FilterMenuContent.CLOSE_MENU": "ANNULLA",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Annulla",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Nascondi",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Salva",
	"HideWordsModal.HIDE_WORDS_HEADER": "Nascondi parole",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Seleziona parole da nascondere dalla nuvola di parole.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Seleziona parole da nascondere",
	"IconHelpStyled.HELP": "Assistenza",
	"IndividualResponseCard.NO_RESPONSE": "Il rispondente ha saltato questa domanda",
	"IndividualResponseCard.QUESTION_NUMBER": "D{number}",
	"InfoBannerTagManagement.CLOSE": "Chiudi",
	"InfoBannerTagManagement.NEW": "NUOVO",
	"InfoBannerTagManagement.TAG_RESPONSES": "Tagga risposte",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Aggiungi parole chiave per configurare tag automatici e tenere le risposte organizzate.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Tagga le risposte per semplificarne la gestione",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positivo: {percentage,number,percent}} negative {Negativo: {percentage,number,percent}} neutral {Neutro: {percentage,number,percent}} unknown {Non rilevato: {percentage,number,percent}} other {altro}}",
	"LoadingState.LOADING": "Caricamento risposte in corso",
	"MatrixDropdown.EmptyTopNativeSelect": "Seleziona un'opzione",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Intervallo di confidenza",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Punteggio di utilità",
	"Modal.CloseModal": "Chiudi descrizione dei comandi",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Lunedì} Tu {Martedì} We {Mercoledì} Th {Giovedì} Fr {Venerdì} Sa {Sabato} Su {Domenica} other {altro}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Lu} Tu {Ma} We {Me} Th {Gio} Fr {Ve} Sa {Sa} Su {Do} other {altro}}",
	"MultiColorSelectorMenu.APPLY": "Applica",
	"MultiColorSelectorMenu.CANCEL": "Annulla",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Organizza i dati in un layout personalizzato, spiega i risultati e condividi il tuo dashboard con chiunque. Organizza i dati in un layout personalizzato, spiega i risultati e condividi il tuo dashboard con chiunque.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Dashboard",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Per saperne di più",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">Su una scala da {minValue} a {maxValue}, dove </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} è {minLabelText} e {maxValue} è {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "area indicatore per punteggi compresi tra {min} e {max}",
	"NpsGaugeChart.NPS_CHART": "grafico a indicatore NPS",
	"NpsGaugeChart.NPS_SCORE": "punteggio NPS® di {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "tacca grafica per la posizione del punteggio {score}",
	"OrderControl.AriaNA": "Non applicabile",
	"OrderControl.NA": "N/A",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "Risposte \"{title}\" ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "Nascondi elenco risposte \"Altro\"",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "Mostra elenco risposte \"Altro\"",
	"PhoneField.countryCode": "Codice paese",
	"QuestionCardItemFooter.FILTER_APPLIED": "Regole applicate: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtra",
	"QuestionCardTitle.ANSWERED": "Hanno risposto: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Selezionate: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Hanno saltato la domanda: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {Punteggio: {score}/{total}} other {Punteggio: {score}/{total}}}",
	"QuizResultScore.SKIPPED": "Domanda saltata da",
	"RangeTab.RANGE_TAB_LABEL": "Menzioni: {value}",
	"RangeTab.TOTAL_WORDS": "parole totali",
	"Ranking.AriaRankAtBottom": "La voce {label} è stata spostata al fondo dell'elenco",
	"Ranking.AriaRankAtTop": "La voce {label} è stata spostata all'inizio dell'elenco",
	"Ranking.AriaRankMovedDown": "La voce {label} è stata spostata più in basso al numero {position}",
	"Ranking.AriaRankMovedUp": "La voce {label} è stata spostata più in alto al numero {position}",
	"RankingItem.AriaRankMoveDown": "Sposta giù {label}",
	"RankingItem.AriaRankMoveUp": "Sposta su {label}",
	"RatingChart.AVERAGE_RATING": "Valutazione media",
	"RatingChartLegend.ANSWERED": "Hanno risposto: {number}",
	"RatingChartLegend.RATING": "{rating} stella",
	"RatingChartLegend.SKIPPED": "Hanno saltato la domanda: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Richiedi funzione",
	"RequestAccessButton.UPGRADE": "Aggiorna",
	"RespondentQuestionTitle.REQUIRED": "(Obbligatorio)",
	"ResponseItem.EXPAND_MENU_LABEL": "Espandi menu",
	"ResponseItem.MENU_LABEL": "Aggiungi tag",
	"ResponseItem.RESPONDENT_ID": "ID rispondente",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Visualizza le risposte del rispondente",
	"ResponseStatusBar.CLEAR": "Annulla selezione",
	"ResponseStatusBar.FAILED_TO_LOAD": "Impossibile caricare le risposte",
	"ResponseStatusBar.NO_RESPONSES_YET": "Ancora nessuna risposta",
	"ResponseStatusBar.SEARCHING": "Ricerca...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {stato d'animo positivo} negative {stato d'animo negativo} neutral {stato d'animo neutro} other {stato d'animo sconosciuto}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposta selezionata per {searchTextFormatted} con {filters}   } other {Visualizzazione di {resultsCountFormatted} risposta per {searchTextFormatted} con {filters}   }}} other {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposte selezionate per {searchTextFormatted} con {filters}   } other {Visualizzazione di {resultsCountFormatted} risposte per {searchTextFormatted} con {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposta selezionata con {filters}   } other {Visualizzazione di {resultsCountFormatted} risposta con {filters}   }}} other {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposte selezionate con {filters}   } other {Visualizzazione di {resultsCountFormatted} risposte con {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposta selezionata per {searchTextFormatted}   } other {Visualizzazione di {resultsCountFormatted} risposta per {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposte selezionate per {searchTextFormatted}   } other {Visualizzazione di {resultsCountFormatted} risposte per {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposta selezionata   } other {Visualizzazione di {resultsCountFormatted} risposta   }}} other {{hasSelectedResponse, select, true {Visualizzazione di {resultsCountFormatted} risposte selezionate   } other {Visualizzazione di {resultsCountFormatted} risposte   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Annulla selezione",
	"ResponseToolbar.CREATE_TAG": "Crea tag",
	"ResponseToolbar.SELECT_ALL": "Seleziona tutte le risposte",
	"ResponseToolbar.UNSELECT_ALL": "Deseleziona tutte le risposte",
	"SearchResponsesInput.CLEAR": "Annulla ricerca",
	"SearchResponsesInput.SEARCH": "Cerca",
	"SearchResponsesInput.SEARCH_RESPONSES": "Cerca tra le risposte",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Usa le virgolette <strong>“”</strong> per cercare una frase esatta.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Digita <strong>OR</strong> per trovare uno, due o più termini di ricerca.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Digita <b>AND</b> per trovare più di un termine.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Usa <strong>NOT</strong> per escludere un termine dalla ricerca.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Digita <strong>*</strong> dopo un termine per cercare in base al prefisso.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Usa le parentesi per combinare più ricerche basate su AND/OR/NOT.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Inserisci <strong>~2</strong> dopo un termine per ottenere risultati corrispondenti al termine, più fino a due altri caratteri.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Opzioni di ricerca",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Per saperne di più",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "No grazie",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Attiva",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "Gli <span id=\"link\">Approfondimenti automatici</span> sono ora disponibili nel tuo account.      L'impostazione include qualsiasi funzione che utilizza l'apprendimento automatico per elaborare le indagini e offrirti dati dettagliati.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Puoi modificare le tue preferenze in qualsiasi momento in <span id=\"link\">Il mio account</span>",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Approfondimenti automatici",
	"SentimentBanner.GENERATE_SENTIMENT": "Analizza stato d'animo",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Analisi dello stato d'animo",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Scopri lo stato d'animo che si cela dietro le risposte aperte per capire cosa pensano veramente le persone.",
	"SentimentBanner.UPGRADE": "Aggiorna",
	"SentimentBanner.WATCH_A_DEMO": "Guarda una demo",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Come si sentono le persone:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtra per stato d'animo positivo} negative {Filtra per stato d'animo negativo} neutral {Filtra per stato d'animo neutro} other {Filtra per stato d'animo sconosciuto}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Espandi menu",
	"SentimentFilterMenu.MENU_LABEL": "per stato d'animo",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Richiedi funzione",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Analizza lo stato d'animo e il sentiment dietro alle risposte scritte.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Novità!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Presentazione dell'Analisi dello stato d'animo",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Aggiorna",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Guarda una demo",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "No grazie",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Richiedi funzione",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Aggiorna",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Immagine upgrade Analisi dello stato d'animo",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Attiva l'analisi dello stato d'animo con un solo clic. Le risposte testuali verranno immediatamente classificate come positive, neutre o negative per rivelare le emozioni che si celano dietro le parole dei rispondenti.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Scopri subito come si sentono le persone",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Analisi dello stato d'animo",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Analisi dello stato d'animo: ATTIVA} false {Analisi dello stato d'animo: NON ATTIVA} other {Stati d'animo: altro}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Analisi dello stato d'animo: CARICAMENTO...",
	"SlideBoxNavigation.NEXT_BUTTON": "AVANTI",
	"SlideBoxNavigation.NEXT_SLIDE": "Slide successiva",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "INDIETRO",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Slide precedente",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} di {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Annulla selezione",
	"Slider.STEP_MESSAGE": "Il numero che hai inserito è stato regolato in base alla scala della barra di scorrimento.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Hanno risposto: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "D{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Hanno saltato la domanda: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Il punto di intersezione tra economico e costoso fornisce un'indicazione generale di prezzo per il tuo prodotto.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Sensibilità al prezzo (Van Westendorp)",
	"SrText.CORRECT": "Corrette",
	"SrText.INCORRECT": "Non corrette",
	"StarRating.Heart": "{count, plural, one {{count} cuore} other {{count} cuori}}",
	"StarRating.STAR": "{count, plural, one {{count} stella} other {{count} stelle}}",
	"StarRating.Smiley": "{count, plural, one {{count} sorriso} other {{count} sorrisi}}",
	"StarRating.Thumb": "{count, plural, one {{count} pollice su} other {{count} pollici su}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Icona di blocco scheda tag",
	"TabList.RESPONSES": "Risposte ({responseCount})",
	"TabList.TAGS": "Tag ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "Analisi tematica",
	"TabList.WORD_CLOUD": "NUVOLA DI PAROLE",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Espandi menu",
	"TagFilterMenu.MENU_LABEL": "Filtra: per tag",
	"TagList.TAGS": "Tag",
	"TagList.TAGS_KEYWORDS": "Parole chiave",
	"TagList.TAG_CONTROLS": "Controlli tag",
	"TagList.TAG_NUMBER_PERCENT": "Percentuale di tag e numero di tag",
	"TagList.TAG_PERCENTAGE_BAR": "Barra percentuale tag",
	"TagListItem.DELETE": "Elimina",
	"TagListItem.EDIT": "Modifica",
	"TagListItem.VIEW_ALL": "Visualizza tutto",
	"TagListPanel.LOADING": "Caricamento tag",
	"TagListPanel.NEW_TAG": "+ Nuovo tag",
	"TagListPanel.TAGS": "Tag",
	"TagMenuContent.APPLY_ACTIONS": "APPLICA",
	"TagMenuContent.CLEAR_ACTIONS": "Annulla selezione",
	"TagMenuContent.CLOSE_MENU": "ANNULLA",
	"TagMenuContent.DUPLICATED_NAME": "Esiste già un tag con questo nome.",
	"TagMenuContent.NEW_TAG": "Nuovo tag",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Per saperne di più >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Richiedi funzione",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Aggiorna",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Chiudi",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Cerca, tagga e filtra le risposte scritte, e visualizza nuvole di parole di termini e frasi frequenti.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Analisi testuale",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "hh",
	"TimeInputWrapper.hour": "Ora",
	"TimeInputWrapper.minutes": "Minuti",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Ora",
	"ToggleableEmailField.checkboxLabel": "Inviami una copia delle mie risposte via email",
	"ToggleableEmailField.emailLabel": "Inserisci il tuo indirizzo email",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Elimina",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted} risposta non sarà taggata.} other {{responseCountFormatted} risposte non saranno taggate.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Il tag eliminato verrà rimosso da tutte le risposte taggate. L'operazione è irreversibile.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Vuoi eliminare il tag?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Salva",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Salvare le modifiche?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Il tag sarà rimosso da tutte le risposte. Il tag verrà riapplicato alle risposte che \n corrispondono alle parole chiave modificate.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Annulla",
	"UpgradeBanner.SUBTITLE": "Passa a un piano di livello superiore per personalizzare la tua nuvola di parole.",
	"UpgradeBanner.TITLE": "Ti piace ciò che vedi?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% di risposte",
	"VanWestendorpChartDataTable.PRICE": "Prezzo",
	"VanWestendorpChartDataTable.RESPONSES": "Risposte",
	"WordCloudCustomizationButton.CUSTOMIZE": "Personalizza",
	"WordCloudCustomizationFooter.APPLY": "Applica",
	"WordCloudCustomizationFooter.CANCEL": "Annulla",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Applica a tutte le nuvole di parole dell'indagine (alcune potrebbero non subire modifiche)",
	"WordCloudCustomizationFooter.NO_THANKS": "No grazie",
	"WordCloudCustomizationFooter.REQUEST": "Richiedi funzione",
	"WordCloudCustomizationFooter.SAVE": "Salva",
	"WordCloudCustomizationFooter.UPGRADE": "Aggiorna",
	"WordCloudCustomizationTabs.COLOR": "COLORE",
	"WordCloudCustomizationTabs.FONT": "CARATTERE",
	"WordCloudCustomizationTabs.RANGE": "INTERVALLO",
	"WordCloudLegend.FEWER": "meno",
	"WordCloudLegend.MORE": "più",
	"WordCloudLegend.TITLE": "Numero di menzioni",
	"WordCloudListFooter.COMBINED_WORDS": "Parole combinate",
	"WordCloudListFooter.HIDDEN_WORDS": "Parole nascoste",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Espandi menu",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Combina con",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Modifica parole combinate",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Espandi menu",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Nascondi dalla nuvola di parole",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Separa parole combinate",
	"WordCloudLoadingState.LOADING": "Caricamento nuvola di parole...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Pulsante Chiudi",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Visualizza in anteprima le funzioni per la personalizzazione delle nuvole di parole.",
	"WordCloudPreviewOverlay.TITLE": "Prova subito!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Visualizzazione a nuvola",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Visualizzazione a elenco",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Occorrenze: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}ˆ parola più importante",
	"WordCount.IN_PROGRESS": "Analisi testuale in corso...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Nessuna parola importante trovata.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {{count} parola visualizzata } other {{count} parole visualizzate }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtra per stato d'animo positivo} negative {Filtra per stato d'animo negativo} neutral {Filtra per stato d'animo neutro} other {Filtra per stato d'animo non rilevato}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positivo} negative {Negativo} neutral {Neutrali} other {Sconosciuto}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positivo: {percentage,number,percent}} negative {Negativo: {percentage,number,percent}} neutral {Neutro: {percentage,number,percent}} other {Sconosciuto: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "ANNULLA",
	"copyTagEditor.DUPLICATED_NAME": "Esiste già un tag con questo nome.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Assegna un nome a questo tag",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Inserisci una o più parole chiave",
	"copyTagEditor.KEYWORDS": "<strong>Parole chiave</strong> (facoltativo)",
	"copyTagEditor.SAVE": "SALVA",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Il tag viene applicato a tutte le risposte con parole chiave corrispondenti",
	"copyTagEditor.TAG_NAME": "<strong>Nome tag</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Il nome del tag è obbligatorio.",
	"copyTagEditor.TAG_NAME_SPACES": "Il nome del tag non può contenere solo spazi.",
	"copyTagEditor.TAG_TOO_LONG": "Il tag deve contenere meno di 128 caratteri.",
	"countriesL10nMessages.AC": "Isola di Ascensione",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emirati Arabi Uniti",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua e Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa americane",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Isole Åland",
	"countriesL10nMessages.AZ": "Azerbajdzan",
	"countriesL10nMessages.BA": "Bosnia-Erzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgio",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "San Bartolomeo",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius e Saba",
	"countriesL10nMessages.BR": "Brasile",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Isole Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, Repubblica democratica del",
	"countriesL10nMessages.CF": "Repubblica centrafricana",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Svizzera",
	"countriesL10nMessages.CI": "Costa d'Avorio",
	"countriesL10nMessages.CK": "Isole Cook",
	"countriesL10nMessages.CL": "Cile",
	"countriesL10nMessages.CM": "Camerun",
	"countriesL10nMessages.CN": "Cina",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Capo Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Isole Christmas",
	"countriesL10nMessages.CY": "Cipro",
	"countriesL10nMessages.CZ": "Repubblica ceca",
	"countriesL10nMessages.DE": "Germania",
	"countriesL10nMessages.DJ": "Gibuti",
	"countriesL10nMessages.DK": "Danimarca",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Repubblica dominicana",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egitto",
	"countriesL10nMessages.EH": "Sahara occidentale",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spagna",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finlandia",
	"countriesL10nMessages.FJ": "Figi",
	"countriesL10nMessages.FK": "Isole Falkland",
	"countriesL10nMessages.FM": "Stati Federati di Micronesia",
	"countriesL10nMessages.FO": "Isole Faroer",
	"countriesL10nMessages.FR": "Francia",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Regno Unito",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guiana Francese",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibilterra",
	"countriesL10nMessages.GL": "Groenlandia",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadalupa",
	"countriesL10nMessages.GQ": "Guinea Equatoriale",
	"countriesL10nMessages.GR": "Grecia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croazia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungheria",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israele",
	"countriesL10nMessages.IM": "Isola di Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Territori britannici dell'oceano indiano",
	"countriesL10nMessages.IQ": "Iraq",
	"countriesL10nMessages.IS": "Islanda",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Giamaica",
	"countriesL10nMessages.JO": "Giordania",
	"countriesL10nMessages.JP": "Giappone",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirghizistan",
	"countriesL10nMessages.KH": "Cambogia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comore",
	"countriesL10nMessages.KN": "Saint Kitts e Nevis",
	"countriesL10nMessages.KR": "Corea del Sud",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Isole Cayman",
	"countriesL10nMessages.KZ": "Kazakistan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libano",
	"countriesL10nMessages.LC": "Santa Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lituania",
	"countriesL10nMessages.LU": "Lussemburgo",
	"countriesL10nMessages.LV": "Lettonia",
	"countriesL10nMessages.LY": "Libia",
	"countriesL10nMessages.MA": "Marocco",
	"countriesL10nMessages.MC": "Principato di Monaco",
	"countriesL10nMessages.MD": "Moldavia",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (parte francese)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Isole Marshall",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Birmania",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Isole Marianne Settentrionali",
	"countriesL10nMessages.MQ": "Martinica",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurizio",
	"countriesL10nMessages.MV": "Maldive",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Messico",
	"countriesL10nMessages.MY": "Malesia",
	"countriesL10nMessages.MZ": "Mozambico",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Nuova Caledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Isola Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Paesi Bassi",
	"countriesL10nMessages.NO": "Norvegia",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nuova Zelanda",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Perù",
	"countriesL10nMessages.PF": "Polinesia Francese",
	"countriesL10nMessages.PG": "Papua Nuova Guinea",
	"countriesL10nMessages.PH": "Filippine",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polonia",
	"countriesL10nMessages.PM": "St Pierre e Miquelon",
	"countriesL10nMessages.PR": "Porto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portogallo",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Isola della Riunione",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Arabia Saudita",
	"countriesL10nMessages.SB": "Isole Salomone",
	"countriesL10nMessages.SC": "Seicelle",
	"countriesL10nMessages.SE": "Svezia",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Sant'Elena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard e Jan Mayen",
	"countriesL10nMessages.SK": "Slovacchia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Sudan del Sud",
	"countriesL10nMessages.ST": "São Tomé e Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Isole Turks e Caicos",
	"countriesL10nMessages.TD": "Ciad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tailandia",
	"countriesL10nMessages.TJ": "Tadzikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor orientale",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turchia",
	"countriesL10nMessages.TT": "Trinidad e Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ucraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Stati Uniti",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Santa Sede (Città del Vaticano)",
	"countriesL10nMessages.VC": "Saint Vincent e Grenadine",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Isole Vergini britanniche",
	"countriesL10nMessages.VI": "Isole Vergini americane",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis e Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Sudafrica",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Punto di indifferenza del prezzo (Indifference Price Point)",
	"helper.IPP": "Punto di indifferenza del prezzo (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Punto di prezzo ottimale (OPP)",
	"helper.OPP_DESCRIPTION": "Punto di prezzo ottimale (Optimal Price Point)",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Punto di economicità marginale (PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Punto di dispendio marginale (PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Punto di economicità marginale (Point of Marginal Cheapness)",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Punto di dispendio marginale (Point of Marginal Expensiveness)"
};

var ja = {
	"ActionMenuTagPin.PIN": "ピン",
	"ActionMenuTagPin.TAG": "タグ",
	"AddressField.selectCountry": "国を選択",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "回答済み: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "Q{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "スキップ: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "人々の気持ち:",
	"AsyncJobLoadingState.LOADING": "回答を読み込んでいます",
	"AsyncJobLoadingState.TAGGING_CAPTION": "終了したらお知らせします",
	"AsyncJobLoadingState.TAGGING_TITLE": "回答にタグ付けしています",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "既存および今後の回答にタグを付けます。複数のキーワードを使うには、語句の間にスペースまたは「<strong>OR</strong>」を入力します。",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "回答を自動的にタグ付け",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "月を選択",
	"CalendarPicker.months": "{months, select, January {1月} February {2月} March {3月} April {4月} May {5月} June {6月} July {7月} August {8月} September {9月} October {10月} November {11月} December {12月} other {その他}}",
	"CalendarPicker.nextMonth": "翌月",
	"CalendarPicker.previousMonth": "前月",
	"CalendarPicker.year": "年を選択",
	"ColorSwatches.TAG": "タグ{color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "マルチカラーのワードクラウドでは、単語やフレーズの出現頻度を色とサイズの両方で表現します。",
	"ColorTab.COLOR_SELECTOR_TITLE": "ワードクラウドの色について",
	"ColorTab.DOWN_ARROW": "ドロップダウン矢印の選択",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "マルチカラー",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "単色",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "キャンセル",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. 新しい語句を作成します",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "新しい語句は、下で選択する各語句と置き換わります。",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "結合語を編集",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "単語を結合する",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "選択した1つ以上の語句と置き換える新しい語句を作成します。元の語句に戻したければ、後で編集できます。",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "任意の新しい語句を入力します",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "結合",
	"CombineWordsModal.WORD_LIST_HEADER": "2. 結合する語句を選択します",
	"CreateTagFromSearch.CLOSE_MENU": "キャンセル",
	"CreateTagFromSearch.DUPLICATED_NAME": "そのタグ名はすでに存在します。",
	"CreateTagFromSearch.NEW_TAG": "新しいタグ",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { 回答にタグは\n\t\t付けられません} other {{responseCount, plural, other {\n\t\t\t\t{responseTaggedFormatted}の回答に\n\t\t\t\n\t\tタグが付きます}}}}",
	"CreateTagFromSearch.SAVE": "保存",
	"CreateTagFromSearch.TAG": "タグ",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "タグ名を空白にすることはできません。",
	"CreateTagFromSearch.TAG_NAME_SPACES": "タグ名をスペースだけにすることはできません。",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "これらのキーワードに一致する回答にタグを付けます。<strong>タグを作成</strong>を選択します。色を選択して保存します。タグは既存の回答と今後の回答に適用されます。",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "一致する回答にタグ付け",
	"DashboardMenuContent.CREATE": "作成",
	"DashboardMenuContent.NAME": "名前",
	"DashboardMenuContent.NEW_DASHBOARD": "新しいダッシュボード",
	"DateInputWrapper.dateButton": "カレンダーを開く",
	"DateInputWrapper.dateFormatIntl": "DD/MM/YYYY",
	"DateInputWrapper.dateFormatUs": "MM/DD/YYYY",
	"DateInputWrapper.dateLabel": "日付",
	"DateTime.errorEnterHours": "時間には0～23を入力してください。",
	"DateTime.errorEnterMinutes": "分には0～59を入力してください。",
	"DateTime.errorSelectPeriod": "AM・PMを選択してください。",
	"DateTime.errorYearOutOfRange": "年は1900～9999の範囲で入力してください。",
	"DateTime.formatErrorDateIntl": "日付はDD/MM/YYYY（日/月/年）形式で入力してください。",
	"DateTime.formatErrorDateUs": "日付はMM/DD/YYYY（月/日/年）形式で入力してください。",
	"Day.dayOfMonth": "{dayInCalendar,date,full} ",
	"EditorButtons.CANCEL_BUTTON": "キャンセル",
	"EditorButtons.COPY_BUTTON": "質問をコピー",
	"EditorButtons.MOVE_BUTTON": "質問を移動",
	"EditorButtons.NEXT_QUESTION_BUTTON": "次の質問",
	"EditorButtons.SAVE_BUTTON": "保存",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "データトレンドはこの質問に適用されません",
	"FallbackComponent.ERROR": "質問の読み込みエラー",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>ファイルを選択</div><div class={dragClass}>するか、ここにドラッグします</div>",
	"FileUpload.fileUploaded": "ファイルをアップロードしました",
	"FileUpload.fileUploading": "ファイルをアップロード中",
	"FileUpload.removeFile": "ファイルの削除",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>ファイルを置き換える</div><div class={dragClass}>か、ここにドラッグします</div>",
	"FileUpload.supportedFiles": "対応ファイル",
	"FilterMenuContent.APPLY_ACTIONS": "適用",
	"FilterMenuContent.CLEAR_ACTIONS": "クリア",
	"FilterMenuContent.CLOSE_MENU": "キャンセル",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "キャンセル",
	"HideWordsModal.HIDE_BUTTON_LABEL": "非表示",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "保存",
	"HideWordsModal.HIDE_WORDS_HEADER": "単語を非表示",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "ワードクラウドで非表示にする語句を選択します。",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "非表示にする語句を選択",
	"IconHelpStyled.HELP": "ヘルプ",
	"IndividualResponseCard.NO_RESPONSE": "回答者は、この質問をスキップ",
	"IndividualResponseCard.QUESTION_NUMBER": "Q{number}",
	"InfoBannerTagManagement.CLOSE": "閉じる",
	"InfoBannerTagManagement.NEW": "新規",
	"InfoBannerTagManagement.TAG_RESPONSES": "回答にタグ付け",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "キーワードを追加して自動タグを設定し、回答を整理します。",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "回答にタグを付けて管理を簡単に",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {肯定的: {percentage,number,percent}} negative {否定的: {percentage,number,percent}} neutral {中立: {percentage,number,percent}} unknown {不明：{percentage,number,percent}} other {その他}}",
	"LoadingState.LOADING": "回答を読み込んでいます",
	"MatrixDropdown.EmptyTopNativeSelect": "オプションを選択",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "信頼区間",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "効用値",
	"Modal.CloseModal": "ツールチップを閉じる",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {月曜} Tu {火曜} We {水曜} Th {木曜} Fr {金曜} Sa {土曜} Su {日曜} other {その他}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {月} Tu {火} We {水} Th {木} Fr {金} Sa {土} Su {日} other {その他}}",
	"MultiColorSelectorMenu.APPLY": "適用",
	"MultiColorSelectorMenu.CANCEL": "キャンセル",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "データをカスタムにレイアウトし、発見事項を説明し、ダッシュボードをあらゆる人と共有しましょう。 データをカスタムにレイアウトし、発見事項を説明し、ダッシュボードをあらゆる人と共有しましょう。",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "ダッシュボード",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "詳細情報",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">{minValue}～{maxValue}の尺度で、</div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\">{minLabelText}は{minValue}、{maxLabelText}は{maxValue}</div>",
	"NpsGaugeChart.NPS_ARC": "{min}～{max}のスコアのゲージゾーン",
	"NpsGaugeChart.NPS_CHART": "NPSゲージグラフ",
	"NpsGaugeChart.NPS_SCORE": "NPSスコア {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "スコア{score}の位置を示すグラフの目盛",
	"OrderControl.AriaNA": "該当なし",
	"OrderControl.NA": "対象外",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "「{title}」の回答（{totalCount}）",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "「その他」の回答リストを表示しない",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "「その他」の回答リストを表示する",
	"PhoneField.countryCode": "国番号",
	"QuestionCardItemFooter.FILTER_APPLIED": "適用ルール: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "フィルター",
	"QuestionCardTitle.ANSWERED": "回答済み: {answeredCount}",
	"QuestionCardTitle.SELECTED": "選択済み: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "スキップ: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, other {{score}/{total}ポイント}}",
	"QuizResultScore.SKIPPED": "スキップ数",
	"RangeTab.RANGE_TAB_LABEL": "頻度: {value}",
	"RangeTab.TOTAL_WORDS": "総単語数",
	"Ranking.AriaRankAtBottom": "{label}がリストの一番下に移動しました",
	"Ranking.AriaRankAtTop": "{label}がリストの一番上に移動しました",
	"Ranking.AriaRankMovedDown": "{label}を{position}番目に下げました",
	"Ranking.AriaRankMovedUp": "{label}を{position}番目に上げました",
	"RankingItem.AriaRankMoveDown": "{label}を下に移動",
	"RankingItem.AriaRankMoveUp": "{label}を上に移動",
	"RatingChart.AVERAGE_RATING": "評価平均",
	"RatingChartLegend.ANSWERED": "回答済み: {number}",
	"RatingChartLegend.RATING": "{rating}つ星",
	"RatingChartLegend.SKIPPED": "スキップ: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label}（{percent,number,percent}）",
	"RequestAccessButton.REQUEST": "機能をリクエスト",
	"RequestAccessButton.UPGRADE": "アップグレード",
	"RespondentQuestionTitle.REQUIRED": "（必須）",
	"ResponseItem.EXPAND_MENU_LABEL": "メニューの展開",
	"ResponseItem.MENU_LABEL": "タグの追加",
	"ResponseItem.RESPONDENT_ID": "回答者ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "回答者の回答を表示",
	"ResponseStatusBar.CLEAR": "クリア",
	"ResponseStatusBar.FAILED_TO_LOAD": "回答を読み込めませんでした",
	"ResponseStatusBar.NO_RESPONSES_YET": "まだ回答はありません",
	"ResponseStatusBar.SEARCHING": "検索中...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {肯定的な感情} negative {否定的な感情} neutral {中立の感情} other {不明な感情}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {{searchTextFormatted}と{filters}の選択された回答{resultsCountFormatted}件を表示中   } other {{searchTextFormatted}と{filters}の回答{resultsCountFormatted}件を表示中   }}}}} other {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {{filters}の選択された回答{resultsCountFormatted}件を表示中   } other {{filters}の回答{resultsCountFormatted}件を表示中   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {{searchTextFormatted}の選択された回答{resultsCountFormatted}件を表示中   } other {{searchTextFormatted}の回答{resultsCountFormatted}件を表示中   }}}}} other {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {選択された回答{resultsCountFormatted}件を表示中   } other {{resultsCountFormatted}件の回答を表示中   }}}}}}}}",
	"ResponseToolbar.CLEAR": "クリア",
	"ResponseToolbar.CREATE_TAG": "タグを作成",
	"ResponseToolbar.SELECT_ALL": "すべての回答を選択",
	"ResponseToolbar.UNSELECT_ALL": "すべての回答を選択解除",
	"SearchResponsesInput.CLEAR": "検索のクリア",
	"SearchResponsesInput.SEARCH": "検索",
	"SearchResponsesInput.SEARCH_RESPONSES": "回答の検索",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "完全に一致するフレーズを検索するには引用符「<strong>“”</strong>」を使います。",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "2つ以上の検索語句のうちいずれかを探すには「<strong>OR</strong>」を入力します。",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "複数の語句を見つけるには「<b>AND</b>」を入力します。",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "検索で語句を除外<strong>しない</strong>ために使います。",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "接頭語で検索するには語句の後に「<strong>*</strong>」を入力します。",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "「AND/OR/NOT」を使用した検索を組み合わせるには、括弧を使用します。",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "その語句に、最大2文字が加わった語句を検索するには、語句の後に「<strong>~2</strong>」を付けます。",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "検索オプション",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "詳細情報",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "アップグレードしない",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "有効にする",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "お使いのアカウントで<span id=\"link\">自動インサイト</span>が使用可能になりました。\nこの設定には、機械学習を利用してアンケートを処理し、洞察を抽出する機能が含まれます。",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "設定はいつでも<span id=\"link\">マイアカウント</span>で変更できます。",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "自動インサイト",
	"SentimentBanner.GENERATE_SENTIMENT": "感情を分析",
	"SentimentBanner.SENTIMENT_ANALYSIS": "感情分析",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "回答文の背後にある感情を検出して、人々の気持ちを理解しましょう。",
	"SentimentBanner.UPGRADE": "アップグレード",
	"SentimentBanner.WATCH_A_DEMO": "デモを観る",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "人々の気持ち:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {肯定的な感情でフィルタリング} negative {否定的な感情でフィルタリング} neutral {中立の感情でフィルタリング} other {不明な感情でフィルタリング}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "メニューの展開",
	"SentimentFilterMenu.MENU_LABEL": "感情別",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "機能をリクエスト",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "回答の背後にある感情を検出します。",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "新機能",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "感情分析とは",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "アップグレード",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "デモを観る",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "今はしない",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "機能をリクエスト",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "アップグレード",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "感情機能のアップグレード画像",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "ワンクリックで感情分析をオンにしましょう。テキスト回答を肯定的、中立、否定的なものに分類して、回答の裏に隠れている感情を明らかにします。",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "人々の気持ちがすぐにわかります",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "感情分析",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {感情機能: オン} false {感情機能: オフ} other {感情機能: オン}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "感情機能：ロード中...",
	"SlideBoxNavigation.NEXT_BUTTON": "次へ",
	"SlideBoxNavigation.NEXT_SLIDE": "次のスライド",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "プレビュー",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "前のスライド",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} / {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "クリア",
	"Slider.STEP_MESSAGE": "入力した数値は、スライダーのスケールに応じて調整されています。",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "回答済み: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "Q{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title} ",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "スキップ: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "低価格と高価格の交わる点は、製品価格の全体的な方向性を示します。",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "価格感度（Van Westendorp）",
	"SrText.CORRECT": "正解",
	"SrText.INCORRECT": "不正解",
	"StarRating.Heart": "{count, plural, other {{count} ハート}}",
	"StarRating.STAR": "{count, plural, other {{count}つ星}}",
	"StarRating.Smiley": "{count, plural, other {{count} スマイリー}}",
	"StarRating.Thumb": "{count, plural, other {{count} 親指}}",
	"TabList.BETA_BADGE": "ベータ版",
	"TabList.LOCK_ICON": "タグ付けタブのロックアイコン",
	"TabList.RESPONSES": "回答（{responseCount}）",
	"TabList.TAGS": "タグ（{tagsCount}）",
	"TabList.THEMATIC_ANALYSIS": "テーマ分析",
	"TabList.WORD_CLOUD": "ワードクラウド",
	"TagFilterMenu.EXPAND_MENU_LABEL": "メニューの展開",
	"TagFilterMenu.MENU_LABEL": "フィルター: タグ別",
	"TagList.TAGS": "タグ",
	"TagList.TAGS_KEYWORDS": "キーワード",
	"TagList.TAG_CONTROLS": "タグコントロール",
	"TagList.TAG_NUMBER_PERCENT": "タグの割合とタグの数",
	"TagList.TAG_PERCENTAGE_BAR": "タグの割合バー",
	"TagListItem.DELETE": "削除",
	"TagListItem.EDIT": "編集",
	"TagListItem.VIEW_ALL": "すべてを表示",
	"TagListPanel.LOADING": "タグの読み込み中",
	"TagListPanel.NEW_TAG": "+ 新しいタグ",
	"TagListPanel.TAGS": "タグ",
	"TagMenuContent.APPLY_ACTIONS": "適用",
	"TagMenuContent.CLEAR_ACTIONS": "クリア",
	"TagMenuContent.CLOSE_MENU": "キャンセル",
	"TagMenuContent.DUPLICATED_NAME": "そのタグ名はすでに存在します。",
	"TagMenuContent.NEW_TAG": "新しいタグ",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "詳細情報 >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "機能をリクエスト",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "アップグレード",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "閉じる",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "入力された回答を検索、タグ付け、フィルタリングして、頻出する単語やフレーズをワードクラウドで確認できます。",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "テキスト分析",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "時",
	"TimeInputWrapper.hour": "時間",
	"TimeInputWrapper.minutes": "分",
	"TimeInputWrapper.mmPlaceholder": "分",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "時刻",
	"ToggleableEmailField.checkboxLabel": "回答のコピーをメールで受け取る",
	"ToggleableEmailField.emailLabel": "メールアドレスの入力",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "削除",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, other {{responseCountFormatted}件の回答からタグを外します。}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "タグを削除すると、そのタグはタグ付けされているすべての回答から削除され、元に戻すことはできません。",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "タグを削除しますか？",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "保存",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "変更を保存しますか？",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "これによってすべての回答からこのタグが削除されます。編集後のキーワードに一致する回答に\nタグが再適用されます。",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "キャンセル",
	"UpgradeBanner.SUBTITLE": "アップグレードして独自のワードクラウドにカスタマイズしましょう。",
	"UpgradeBanner.TITLE": "気に入りましたか？",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "回答の割合",
	"VanWestendorpChartDataTable.PRICE": "価格",
	"VanWestendorpChartDataTable.RESPONSES": "回答数",
	"WordCloudCustomizationButton.CUSTOMIZE": "カスタマイズ",
	"WordCloudCustomizationFooter.APPLY": "適用",
	"WordCloudCustomizationFooter.CANCEL": "キャンセル",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "アンケート内のすべてのワードクラウドに適用（一部変更されない場合があります）",
	"WordCloudCustomizationFooter.NO_THANKS": "今はしない",
	"WordCloudCustomizationFooter.REQUEST": "機能をリクエスト",
	"WordCloudCustomizationFooter.SAVE": "保存",
	"WordCloudCustomizationFooter.UPGRADE": "アップグレード",
	"WordCloudCustomizationTabs.COLOR": "色",
	"WordCloudCustomizationTabs.FONT": "フォント",
	"WordCloudCustomizationTabs.RANGE": "範囲",
	"WordCloudLegend.FEWER": "低い",
	"WordCloudLegend.MORE": "高い",
	"WordCloudLegend.TITLE": "頻度",
	"WordCloudListFooter.COMBINED_WORDS": "結合語",
	"WordCloudListFooter.HIDDEN_WORDS": "非表示の単語",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "メニューの展開",
	"WordCloudListViewMenuContent.COMBINE_WITH": "結合対象:",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "結合語を編集",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "メニューの展開",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "ワードクラウドで非表示",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "結合語を分ける",
	"WordCloudLoadingState.LOADING": "ワードクラウドを読み込んでいます...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "閉じるボタン",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "ワードクラウドのカスタマイズ機能をプレビューします。",
	"WordCloudPreviewOverlay.TITLE": "お試しください！",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "クラウドビュー",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "リストビュー",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "発生回数: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}番目に重要な単語",
	"WordCount.IN_PROGRESS": "テキスト分析をしています...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "重要な単語はありません。",
	"WordCount.SHOW_COUNT": "{countCheck, plural, other {{count}個の単語を表示中 }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {肯定的な感情でフィルタリング} negative {否定的な感情でフィルタリング} neutral {中立の感情でフィルタリング} other {未検出の感情でフィルタリング}}",
	"copySentiment.PERCENT": "{percentage,number,percent} ",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {肯定的} negative {否定的} neutral {中立} other {不明}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {肯定的: {percentage,number,percent}} negative {否定的: {percentage,number,percent}} neutral {中立: {percentage,number,percent}} other {不明: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "キャンセル",
	"copyTagEditor.DUPLICATED_NAME": "そのタグ名はすでに存在します。",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "このタグの名前を入力",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "複数のキーワードを入力",
	"copyTagEditor.KEYWORDS": "<strong>キーワード</strong>（任意）",
	"copyTagEditor.SAVE": "保存",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "一致するキーワードを含む回答すべてにタグを付けます",
	"copyTagEditor.TAG_NAME": "<strong>タグ名</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "タグ名を空白にすることはできません。",
	"copyTagEditor.TAG_NAME_SPACES": "タグ名をスペースだけにすることはできません。",
	"copyTagEditor.TAG_TOO_LONG": "タグは128文字未満にしてください。",
	"countriesL10nMessages.AC": "アセンション島",
	"countriesL10nMessages.AD": "アンドラ",
	"countriesL10nMessages.AE": "アラブ首長国連邦",
	"countriesL10nMessages.AF": "アフガニスタン",
	"countriesL10nMessages.AG": "アンティグア・バーブーダ",
	"countriesL10nMessages.AI": "アンギラ",
	"countriesL10nMessages.AL": "アルバニア",
	"countriesL10nMessages.AM": "アルメニア",
	"countriesL10nMessages.AO": "アンゴラ",
	"countriesL10nMessages.AR": "アルゼンチン",
	"countriesL10nMessages.AS": "アメリカ領サモア",
	"countriesL10nMessages.AT": "オーストリア",
	"countriesL10nMessages.AU": "豪州",
	"countriesL10nMessages.AW": "アルバ",
	"countriesL10nMessages.AX": "オーランド諸島",
	"countriesL10nMessages.AZ": "アゼルバイジャン",
	"countriesL10nMessages.BA": "ボスニアヘルツェゴビナ",
	"countriesL10nMessages.BB": "バルバドス",
	"countriesL10nMessages.BD": "バングラデシュ",
	"countriesL10nMessages.BE": "ベルギー",
	"countriesL10nMessages.BF": "ベルキナファソ",
	"countriesL10nMessages.BG": "ブルガリア",
	"countriesL10nMessages.BH": "バーレーン",
	"countriesL10nMessages.BI": "ブルンジ",
	"countriesL10nMessages.BJ": "ベナン",
	"countriesL10nMessages.BL": "セントバーツ",
	"countriesL10nMessages.BM": "バーミューダ",
	"countriesL10nMessages.BN": "ブルネイ・ダルサラーム",
	"countriesL10nMessages.BO": "ボリビア",
	"countriesL10nMessages.BQ": "ボネール、シント・ユースタティウスおよびサバ",
	"countriesL10nMessages.BR": "ブラジル",
	"countriesL10nMessages.BS": "バハマ諸島",
	"countriesL10nMessages.BT": "ブータン",
	"countriesL10nMessages.BW": "ボツワナ",
	"countriesL10nMessages.BZ": "ベリーズ",
	"countriesL10nMessages.CA": "カナダ",
	"countriesL10nMessages.CC": "ココス（キーリング）諸島",
	"countriesL10nMessages.CD": "コンゴ民主共和国",
	"countriesL10nMessages.CF": "中央アフリカ共和国",
	"countriesL10nMessages.CG": "コンゴ",
	"countriesL10nMessages.CH": "スイス",
	"countriesL10nMessages.CI": "コートジボワール",
	"countriesL10nMessages.CK": "クック諸島",
	"countriesL10nMessages.CL": "チリ",
	"countriesL10nMessages.CM": "カメルーン",
	"countriesL10nMessages.CN": "中国",
	"countriesL10nMessages.CO": "コロンビア",
	"countriesL10nMessages.CR": "コスタリカ",
	"countriesL10nMessages.CV": "カーボベルデ",
	"countriesL10nMessages.CW": "キュラソー島",
	"countriesL10nMessages.CX": "クリスマス島",
	"countriesL10nMessages.CY": "キプロス",
	"countriesL10nMessages.CZ": "チェコ共和国",
	"countriesL10nMessages.DE": "ドイツ",
	"countriesL10nMessages.DJ": "ジブチ",
	"countriesL10nMessages.DK": "デンマーク",
	"countriesL10nMessages.DM": "ドミニカ",
	"countriesL10nMessages.DO": "ドミニカ共和国",
	"countriesL10nMessages.DZ": "アルジェリア",
	"countriesL10nMessages.EC": "エクアドル",
	"countriesL10nMessages.EE": "エストニア",
	"countriesL10nMessages.EG": "エジプト",
	"countriesL10nMessages.EH": "西サハラ",
	"countriesL10nMessages.ER": "エリトリア",
	"countriesL10nMessages.ES": "スペイン",
	"countriesL10nMessages.ET": "エチオピア",
	"countriesL10nMessages.FI": "フィンランド",
	"countriesL10nMessages.FJ": "フィジー",
	"countriesL10nMessages.FK": "フォークランド諸島",
	"countriesL10nMessages.FM": "ミクロネシア連邦",
	"countriesL10nMessages.FO": "フェロー諸島",
	"countriesL10nMessages.FR": "フランス",
	"countriesL10nMessages.GA": "ガボン",
	"countriesL10nMessages.GB": "英国",
	"countriesL10nMessages.GD": "グレナダ",
	"countriesL10nMessages.GE": "グルジア",
	"countriesL10nMessages.GF": "フランス領ギニア",
	"countriesL10nMessages.GG": "ガーンジー",
	"countriesL10nMessages.GH": "ガーナ",
	"countriesL10nMessages.GI": "ジブラルタル",
	"countriesL10nMessages.GL": "グリーンランド",
	"countriesL10nMessages.GM": "ガンビア",
	"countriesL10nMessages.GN": "ギニア",
	"countriesL10nMessages.GP": "グアドループ",
	"countriesL10nMessages.GQ": "赤道ギニア",
	"countriesL10nMessages.GR": "ギリシャ",
	"countriesL10nMessages.GT": "ガテマラ",
	"countriesL10nMessages.GU": "グアム",
	"countriesL10nMessages.GW": "ギニアビサウ",
	"countriesL10nMessages.GY": "ガイアナ",
	"countriesL10nMessages.HK": "香港",
	"countriesL10nMessages.HN": "ホンデュラス",
	"countriesL10nMessages.HR": "クロアチア",
	"countriesL10nMessages.HT": "ハイチ",
	"countriesL10nMessages.HU": "ハンガリー",
	"countriesL10nMessages.ID": "インドネシア",
	"countriesL10nMessages.IE": "アイルランド",
	"countriesL10nMessages.IL": "イスラエル",
	"countriesL10nMessages.IM": "マン島",
	"countriesL10nMessages.IN": "インド",
	"countriesL10nMessages.IO": "イギリス領インド洋地域",
	"countriesL10nMessages.IQ": "イラク",
	"countriesL10nMessages.IS": "アイスランド",
	"countriesL10nMessages.IT": "イタリア",
	"countriesL10nMessages.JE": "ジャージー",
	"countriesL10nMessages.JM": "ジャマイカ",
	"countriesL10nMessages.JO": "ヨルダン",
	"countriesL10nMessages.JP": "日本",
	"countriesL10nMessages.KE": "ケニヤ",
	"countriesL10nMessages.KG": "キルギスタン",
	"countriesL10nMessages.KH": "カンボジア",
	"countriesL10nMessages.KI": "キリバス",
	"countriesL10nMessages.KM": "コモロ連合",
	"countriesL10nMessages.KN": "セントキッツ・ネービス",
	"countriesL10nMessages.KR": "韓国",
	"countriesL10nMessages.KW": "クウェート",
	"countriesL10nMessages.KY": "ケイマン諸島",
	"countriesL10nMessages.KZ": "カザフスタン",
	"countriesL10nMessages.LA": "ラオス",
	"countriesL10nMessages.LB": "レバノン",
	"countriesL10nMessages.LC": "セントルシア",
	"countriesL10nMessages.LI": "リヒテンシュタイン",
	"countriesL10nMessages.LK": "スリランカ",
	"countriesL10nMessages.LR": "リベリア",
	"countriesL10nMessages.LS": "レソト",
	"countriesL10nMessages.LT": "リトアニア",
	"countriesL10nMessages.LU": "ルクセンブルク",
	"countriesL10nMessages.LV": "ラトビア",
	"countriesL10nMessages.LY": "リビア",
	"countriesL10nMessages.MA": "モロッコ",
	"countriesL10nMessages.MC": "モナコ",
	"countriesL10nMessages.MD": "モルドバ",
	"countriesL10nMessages.ME": "モンテネグロ",
	"countriesL10nMessages.MF": "サン・マルタン島（仏領）",
	"countriesL10nMessages.MG": "マダガスカル",
	"countriesL10nMessages.MH": "マーシャル諸島",
	"countriesL10nMessages.MK": "マケドニア",
	"countriesL10nMessages.ML": "マリ",
	"countriesL10nMessages.MM": "ミャンマー",
	"countriesL10nMessages.MN": "モンゴル",
	"countriesL10nMessages.MO": "マカオ",
	"countriesL10nMessages.MP": "北マリアナ諸島",
	"countriesL10nMessages.MQ": "マルティニーク",
	"countriesL10nMessages.MR": "モーリタニア",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "マルタ",
	"countriesL10nMessages.MU": "モーリシャス",
	"countriesL10nMessages.MV": "モルディブ",
	"countriesL10nMessages.MW": "マラウィ",
	"countriesL10nMessages.MX": "メキシコ",
	"countriesL10nMessages.MY": "マレーシア",
	"countriesL10nMessages.MZ": "モザンビーク",
	"countriesL10nMessages.NA": "ナミビア",
	"countriesL10nMessages.NC": "ニューカレドニア",
	"countriesL10nMessages.NE": "ニジェール",
	"countriesL10nMessages.NF": "ノーフォーク島",
	"countriesL10nMessages.NG": "ナイジェリア",
	"countriesL10nMessages.NI": "ニカラグア",
	"countriesL10nMessages.NL": "オランダ",
	"countriesL10nMessages.NO": "ノルウェー",
	"countriesL10nMessages.NP": "ネパール",
	"countriesL10nMessages.NR": "ナウル",
	"countriesL10nMessages.NU": "ニウエ",
	"countriesL10nMessages.NZ": "ニュージーランド",
	"countriesL10nMessages.OM": "オマーン",
	"countriesL10nMessages.PA": "パナマ",
	"countriesL10nMessages.PE": "ペルー",
	"countriesL10nMessages.PF": "フランス領ポリネシア",
	"countriesL10nMessages.PG": "パプアニューギニア",
	"countriesL10nMessages.PH": "フィリピン",
	"countriesL10nMessages.PK": "パキスタン",
	"countriesL10nMessages.PL": "ポーランド",
	"countriesL10nMessages.PM": "サンピエール・ミクロン",
	"countriesL10nMessages.PR": "プエルトリコ",
	"countriesL10nMessages.PS": "パレスチナ",
	"countriesL10nMessages.PT": "ポルトガル",
	"countriesL10nMessages.PW": "パラオ",
	"countriesL10nMessages.PY": "パラグアイ",
	"countriesL10nMessages.QA": "カタール",
	"countriesL10nMessages.RE": "レユニオン",
	"countriesL10nMessages.RO": "ルーマニア",
	"countriesL10nMessages.RS": "セルビア",
	"countriesL10nMessages.RW": "ルワンダ",
	"countriesL10nMessages.SA": "サウジアラビア",
	"countriesL10nMessages.SB": "ソロモン諸島",
	"countriesL10nMessages.SC": "セイシェル",
	"countriesL10nMessages.SE": "スウェーデン",
	"countriesL10nMessages.SG": "シンガポール",
	"countriesL10nMessages.SH": "セントヘレナ",
	"countriesL10nMessages.SI": "スロベニア",
	"countriesL10nMessages.SJ": "スバールバル諸島・ヤンマイエン島",
	"countriesL10nMessages.SK": "スロバキア",
	"countriesL10nMessages.SL": "シエラレオーネ",
	"countriesL10nMessages.SM": "サンマリノ",
	"countriesL10nMessages.SN": "セネガル",
	"countriesL10nMessages.SO": "ソマリア",
	"countriesL10nMessages.SR": "スリナム",
	"countriesL10nMessages.SS": "南スーダン",
	"countriesL10nMessages.ST": "サントメ・プリンシペ",
	"countriesL10nMessages.SV": "エルサルバドル",
	"countriesL10nMessages.SX": "シント・マールテン",
	"countriesL10nMessages.SZ": "スワジランド",
	"countriesL10nMessages.TA": "トリスタンダクーニャ",
	"countriesL10nMessages.TC": "タークス・カイコス諸島",
	"countriesL10nMessages.TD": "チャド",
	"countriesL10nMessages.TG": "トーゴ",
	"countriesL10nMessages.TH": "タイ",
	"countriesL10nMessages.TJ": "タジキスタン",
	"countriesL10nMessages.TK": "トケラウ",
	"countriesL10nMessages.TL": "東ティモール",
	"countriesL10nMessages.TM": "トルクメニスタン",
	"countriesL10nMessages.TN": "チュニジア",
	"countriesL10nMessages.TO": "トンガ",
	"countriesL10nMessages.TR": "トルコ",
	"countriesL10nMessages.TT": "トリニダードトバゴ",
	"countriesL10nMessages.TV": "ツバル",
	"countriesL10nMessages.TW": "台湾",
	"countriesL10nMessages.TZ": "タンザニア",
	"countriesL10nMessages.UA": "ウクライナ",
	"countriesL10nMessages.UG": "ウガンダ",
	"countriesL10nMessages.US": "米国",
	"countriesL10nMessages.UY": "ウルグアイ",
	"countriesL10nMessages.UZ": "ウズベキスタン",
	"countriesL10nMessages.VA": "教皇庁（バチカン市国）",
	"countriesL10nMessages.VC": "セントビンセントおよびグレナディーン諸島",
	"countriesL10nMessages.VE": "ベネズエラ",
	"countriesL10nMessages.VG": "イギリス領ヴァージン諸島",
	"countriesL10nMessages.VI": "米国領ヴァージン諸島",
	"countriesL10nMessages.VN": "ベトナム",
	"countriesL10nMessages.VU": "バヌアツ",
	"countriesL10nMessages.WF": "ウォリス・フツナ",
	"countriesL10nMessages.WS": "サモア",
	"countriesL10nMessages.XK": "コソボ",
	"countriesL10nMessages.YE": "イエメン",
	"countriesL10nMessages.YT": "マヨット",
	"countriesL10nMessages.ZA": "南アフリカ",
	"countriesL10nMessages.ZM": "ザンビア",
	"countriesL10nMessages.ZW": "ジンバブエ",
	"helper.INDIFFERENCE_PRICE_POINT": "妥協価格ポイント",
	"helper.IPP": "妥協価格ポイント（IPP）",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "最適価格ポイント（OPP）",
	"helper.OPP_DESCRIPTION": "最適価格ポイント",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "最低許容価格（PMC）",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "最高許容価格（PME）",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "最低許容価格",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "最高許容価格"
};

var ka = {
	"AddressField.selectCountry": "აირჩიეთ ქვეყანა",
	"PhoneField.countryCode": "ქვეყნის კოდი",
	"countriesL10nMessages.AC": "ამაღლების კუნძული",
	"countriesL10nMessages.AD": "ანდორა",
	"countriesL10nMessages.AE": "არაბთა გაერთიანებული საამიროები",
	"countriesL10nMessages.AF": "ავღანეთი",
	"countriesL10nMessages.AG": "ანტიგუა და ბარბუდა",
	"countriesL10nMessages.AI": "ანგილა",
	"countriesL10nMessages.AL": "ალბანეთი",
	"countriesL10nMessages.AM": "სომხეთი",
	"countriesL10nMessages.AO": "ანგოლა",
	"countriesL10nMessages.AR": "არგენტინა",
	"countriesL10nMessages.AS": "ამერიკის სამოა",
	"countriesL10nMessages.AT": "ავსტრია",
	"countriesL10nMessages.AU": "ავსტრალია",
	"countriesL10nMessages.AW": "არუბა",
	"countriesL10nMessages.AX": "ალანდის კუნძულები",
	"countriesL10nMessages.AZ": "აზერბაიჯანი",
	"countriesL10nMessages.BA": "ბოსნია და ჰერცოგოვინა",
	"countriesL10nMessages.BB": "ბარბადოსი",
	"countriesL10nMessages.BD": "ბანგლადეში",
	"countriesL10nMessages.BE": "ბელგია",
	"countriesL10nMessages.BF": "ბურკინა-ფასო",
	"countriesL10nMessages.BG": "ბულგარეთი",
	"countriesL10nMessages.BH": "ბაჰრეინი",
	"countriesL10nMessages.BI": "ბურუნდი",
	"countriesL10nMessages.BJ": "ბენინი",
	"countriesL10nMessages.BL": "წმინდა ბართლემე",
	"countriesL10nMessages.BM": "ბერმუდის",
	"countriesL10nMessages.BN": "ბრუნეი-დარუსალამი",
	"countriesL10nMessages.BO": "ბოლივია",
	"countriesL10nMessages.BQ": "ბონერი, სინტ-ევსტათიუსი და საბა",
	"countriesL10nMessages.BR": "ბრაზილია",
	"countriesL10nMessages.BS": "ბაჰამის კუნძულები",
	"countriesL10nMessages.BT": "ბუტანი",
	"countriesL10nMessages.BW": "ბოტსვანა",
	"countriesL10nMessages.BZ": "ბელიზი",
	"countriesL10nMessages.CA": "კანადა",
	"countriesL10nMessages.CC": "კოკოსის (კილინგის) კუნძულები",
	"countriesL10nMessages.CD": "კონგოს დემოკრატიული რესპუბლიკა",
	"countriesL10nMessages.CF": "ცენტრალური აფრიკის რესპუბლიკა",
	"countriesL10nMessages.CG": "კონგო",
	"countriesL10nMessages.CH": "შვეიცარია",
	"countriesL10nMessages.CI": "კოტ-დ'ივუარი",
	"countriesL10nMessages.CK": "კუკის კუნძულები",
	"countriesL10nMessages.CL": "ჩილე",
	"countriesL10nMessages.CM": "კამერუნი",
	"countriesL10nMessages.CN": "ჩინეთი",
	"countriesL10nMessages.CO": "კოლუმბია",
	"countriesL10nMessages.CR": "კოსტა-რიკა",
	"countriesL10nMessages.CV": "კაბო-ვერდე",
	"countriesL10nMessages.CW": "კურასაო",
	"countriesL10nMessages.CX": "შობის კუნძული",
	"countriesL10nMessages.CY": "კვიპროსი",
	"countriesL10nMessages.CZ": "ჩეხეთის რესპუბლიკა",
	"countriesL10nMessages.DE": "გერმანია",
	"countriesL10nMessages.DJ": "ჯიბუტი",
	"countriesL10nMessages.DK": "დანია",
	"countriesL10nMessages.DM": "დომინიკა",
	"countriesL10nMessages.DO": "დომინიკის რესპუბლიკა",
	"countriesL10nMessages.DZ": "ალჟირი",
	"countriesL10nMessages.EC": "ეკვადორი",
	"countriesL10nMessages.EE": "ესტონეთი",
	"countriesL10nMessages.EG": "ეგვიპტე",
	"countriesL10nMessages.EH": "დასავლეთ საჰარა",
	"countriesL10nMessages.ER": "ერიტრეა",
	"countriesL10nMessages.ES": "ესპანეთი",
	"countriesL10nMessages.ET": "ეთიოპია",
	"countriesL10nMessages.FI": "ფინეთი",
	"countriesL10nMessages.FJ": "ფიჯი",
	"countriesL10nMessages.FK": "ფოლკლენდის კუნძულები",
	"countriesL10nMessages.FM": "მიკრონეზიის ფედერალური შტატები",
	"countriesL10nMessages.FO": "ფარერის კუნძულები",
	"countriesL10nMessages.FR": "საფრანგეთი",
	"countriesL10nMessages.GA": "გაბონი",
	"countriesL10nMessages.GB": "გაერთიანებული სამეფო",
	"countriesL10nMessages.GD": "გრენადა",
	"countriesL10nMessages.GE": "საქართველო",
	"countriesL10nMessages.GF": "ფრანგული გუიანა",
	"countriesL10nMessages.GG": "გერნსი",
	"countriesL10nMessages.GH": "განა",
	"countriesL10nMessages.GI": "გიბრალტარი",
	"countriesL10nMessages.GL": "გრენლანდია",
	"countriesL10nMessages.GM": "გამბია",
	"countriesL10nMessages.GN": "გვინეა",
	"countriesL10nMessages.GP": "გვადელუპე",
	"countriesL10nMessages.GQ": "ეკვატორული გვინეა",
	"countriesL10nMessages.GR": "საბერძნეთი",
	"countriesL10nMessages.GT": "გვატემალა",
	"countriesL10nMessages.GU": "გუამი",
	"countriesL10nMessages.GW": "გვინეა-ბისაუ",
	"countriesL10nMessages.GY": "გაიანა",
	"countriesL10nMessages.HK": "ჰონგ-კონგი",
	"countriesL10nMessages.HN": "ჰონდურასი",
	"countriesL10nMessages.HR": "ხორვატია",
	"countriesL10nMessages.HT": "ჰაიტი",
	"countriesL10nMessages.HU": "უნგრეთი",
	"countriesL10nMessages.ID": "ინდონეზია",
	"countriesL10nMessages.IE": "ირლანდია",
	"countriesL10nMessages.IL": "ისრაელი",
	"countriesL10nMessages.IM": "მენის კუნძული",
	"countriesL10nMessages.IN": "ინდოეთი",
	"countriesL10nMessages.IO": "ბრიტანეთის ინდოეთის ოკეანის ტერიტორია",
	"countriesL10nMessages.IQ": "ერაყი",
	"countriesL10nMessages.IS": "ისლანდია",
	"countriesL10nMessages.IT": "იტალია",
	"countriesL10nMessages.JE": "ჯერსი",
	"countriesL10nMessages.JM": "იამაიკა",
	"countriesL10nMessages.JO": "იორდანია",
	"countriesL10nMessages.JP": "იაპონია",
	"countriesL10nMessages.KE": "კენია",
	"countriesL10nMessages.KG": "ყირგიზეთი",
	"countriesL10nMessages.KH": "კამბოჯა",
	"countriesL10nMessages.KI": "კირიბატი",
	"countriesL10nMessages.KM": "კომორის კუნძულები",
	"countriesL10nMessages.KN": "სენტ კიტსი და ნევისი",
	"countriesL10nMessages.KR": "სამხრეთ კორეა",
	"countriesL10nMessages.KW": "ქუვეითი",
	"countriesL10nMessages.KY": "კაიმანის კუნძულები",
	"countriesL10nMessages.KZ": "ყაზახეთი",
	"countriesL10nMessages.LA": "ლაოსი",
	"countriesL10nMessages.LB": "ლიბანი",
	"countriesL10nMessages.LC": "სენტ-ლუსია",
	"countriesL10nMessages.LI": "ლიხტენშტეინი",
	"countriesL10nMessages.LK": "შრი-ლანკა",
	"countriesL10nMessages.LR": "ლიბერია",
	"countriesL10nMessages.LS": "ლესოტო",
	"countriesL10nMessages.LT": "ლიტვა",
	"countriesL10nMessages.LU": "ლუქსემბურგი",
	"countriesL10nMessages.LV": "ლატვია",
	"countriesL10nMessages.LY": "ლიბია",
	"countriesL10nMessages.MA": "მაროკო",
	"countriesL10nMessages.MC": "მონაკო",
	"countriesL10nMessages.MD": "მოლდოვა",
	"countriesL10nMessages.ME": "მონტენეგრო",
	"countriesL10nMessages.MF": "სენ-მარტინი (ფრანგული ნაწილი)",
	"countriesL10nMessages.MG": "მადაგასკარი",
	"countriesL10nMessages.MH": "მარშალის კუნძულები",
	"countriesL10nMessages.MK": "მაკედონია",
	"countriesL10nMessages.ML": "მალი",
	"countriesL10nMessages.MM": "მიანმარი",
	"countriesL10nMessages.MN": "მონღოლეთი",
	"countriesL10nMessages.MO": "მაკაო",
	"countriesL10nMessages.MP": "ჩრდილოეთ მარიანას კუნძული",
	"countriesL10nMessages.MQ": "მარტინიკი",
	"countriesL10nMessages.MR": "მავრიტანია",
	"countriesL10nMessages.MS": "მონსერატი",
	"countriesL10nMessages.MT": "მალტა",
	"countriesL10nMessages.MU": "მავრიკი",
	"countriesL10nMessages.MV": "მალდივები",
	"countriesL10nMessages.MW": "მალავი",
	"countriesL10nMessages.MX": "მექსიკა",
	"countriesL10nMessages.MY": "მალაიზია",
	"countriesL10nMessages.MZ": "მოზამბიკი",
	"countriesL10nMessages.NA": "ნამიბია",
	"countriesL10nMessages.NC": "ახალი კალედონია",
	"countriesL10nMessages.NE": "ნიგერი",
	"countriesL10nMessages.NF": "ნორფოლკის კუნძული",
	"countriesL10nMessages.NG": "ნიგერია",
	"countriesL10nMessages.NI": "ნიკარაგუა",
	"countriesL10nMessages.NL": "ნიდერლანდები",
	"countriesL10nMessages.NO": "ნორვეგია",
	"countriesL10nMessages.NP": "ნეპალი",
	"countriesL10nMessages.NR": "ნაურუ",
	"countriesL10nMessages.NU": "ნიუე",
	"countriesL10nMessages.NZ": "Ახალი ზელანდია",
	"countriesL10nMessages.OM": "ომანი",
	"countriesL10nMessages.PA": "პანამა",
	"countriesL10nMessages.PE": "პერუ",
	"countriesL10nMessages.PF": "ფრანგული პოლინეზია",
	"countriesL10nMessages.PG": "პაპუა ახალი გვინეა",
	"countriesL10nMessages.PH": "ფილიპინები",
	"countriesL10nMessages.PK": "პაკისტანი",
	"countriesL10nMessages.PL": "პოლონეთი",
	"countriesL10nMessages.PM": "სენტ პიერი და მიკელონი",
	"countriesL10nMessages.PR": "პუერტო-რიკო",
	"countriesL10nMessages.PS": "პალესტინა",
	"countriesL10nMessages.PT": "პორტუგალია",
	"countriesL10nMessages.PW": "პალაუ",
	"countriesL10nMessages.PY": "პარაგვაი",
	"countriesL10nMessages.QA": "ყატარი",
	"countriesL10nMessages.RE": "რეიუნიონი",
	"countriesL10nMessages.RO": "რუმინეთი",
	"countriesL10nMessages.RS": "სერბეთი",
	"countriesL10nMessages.RW": "რუანდა",
	"countriesL10nMessages.SA": "საუდის არაბეთი",
	"countriesL10nMessages.SB": "სოლომონის კუნძულები",
	"countriesL10nMessages.SC": "სეიშელის კუნძულები",
	"countriesL10nMessages.SE": "შვედეთი",
	"countriesL10nMessages.SG": "სინგაპური",
	"countriesL10nMessages.SH": "წმინდა ელენას კუნძული",
	"countriesL10nMessages.SI": "სლოვენია",
	"countriesL10nMessages.SJ": "სვალბარდი და იან მაიენი",
	"countriesL10nMessages.SK": "სლოვაკეთი",
	"countriesL10nMessages.SL": "სიერა-ლეონე",
	"countriesL10nMessages.SM": "სან-მარინო",
	"countriesL10nMessages.SN": "სენეგალი",
	"countriesL10nMessages.SO": "სომალი",
	"countriesL10nMessages.SR": "სურინამი",
	"countriesL10nMessages.SS": "სამხრეთ სუდანი",
	"countriesL10nMessages.ST": "სან-ტომე და პრინსიპი",
	"countriesL10nMessages.SV": "ელ-სალვადორი",
	"countriesL10nMessages.SX": "სინტ-მარტენი",
	"countriesL10nMessages.SZ": "სვაზილენდი",
	"countriesL10nMessages.TA": "ტრისტან და კუნია",
	"countriesL10nMessages.TC": "ტერქსისა და კაიკოსის კუნძულები",
	"countriesL10nMessages.TD": "ჩადი",
	"countriesL10nMessages.TG": "ტოგო",
	"countriesL10nMessages.TH": "ტაილანდი",
	"countriesL10nMessages.TJ": "ტაჯიკეთი",
	"countriesL10nMessages.TK": "ტოკელაუ",
	"countriesL10nMessages.TL": "ტიმორ-ლესტე",
	"countriesL10nMessages.TM": "თურქმენეთი",
	"countriesL10nMessages.TN": "ტუნისი",
	"countriesL10nMessages.TO": "ტონგა",
	"countriesL10nMessages.TR": "თურქეთი",
	"countriesL10nMessages.TT": "ტრინიდადი და ტობაგო",
	"countriesL10nMessages.TV": "ტუვალუ",
	"countriesL10nMessages.TW": "ტაივანი",
	"countriesL10nMessages.TZ": "ტანზანია",
	"countriesL10nMessages.UA": "უკრაინა",
	"countriesL10nMessages.UG": "უგანდა",
	"countriesL10nMessages.US": "შეერთებული შტატები",
	"countriesL10nMessages.UY": "ურუგვაი",
	"countriesL10nMessages.UZ": "უზბეკეთი",
	"countriesL10nMessages.VA": "წმინდა საყდარი (ვატიკანის ქალაქი-სახელმწიფო)",
	"countriesL10nMessages.VC": "სენტ-ვინსენტი და გრენადინები",
	"countriesL10nMessages.VE": "ვენესუელა",
	"countriesL10nMessages.VG": "ბრიტანეთის ვირჯინიის კუნძულები",
	"countriesL10nMessages.VI": "აშშ-ის ვირჯინის კუნძულები",
	"countriesL10nMessages.VN": "ვიეტნამი",
	"countriesL10nMessages.VU": "ვანუატუ",
	"countriesL10nMessages.WF": "უოლისი და ფუტუნა",
	"countriesL10nMessages.WS": "სამოა",
	"countriesL10nMessages.XK": "კოსოვო",
	"countriesL10nMessages.YE": "იემენი",
	"countriesL10nMessages.YT": "მაიოტი",
	"countriesL10nMessages.ZA": "სამხრეთ აფრიკა",
	"countriesL10nMessages.ZM": "ზამბია",
	"countriesL10nMessages.ZW": "ზიმბაბვე",
	"ToggleableEmailField.checkboxLabel": "გამომიგზავნეთ ჩემი პასუხების ასლი ელფოსტით",
	"ToggleableEmailField.emailLabel": "შეიყვანეთ თქვენი ელფოსტის მისამართი"
};

var ko = {
	"ActionMenuTagPin.PIN": "고정",
	"ActionMenuTagPin.TAG": "태그",
	"AddressField.selectCountry": "국가 선택",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "답변한 수: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "Q{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "건너뛴 수: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "사람들이 느끼는 감정:",
	"AsyncJobLoadingState.LOADING": "응답 로드 중",
	"AsyncJobLoadingState.TAGGING_CAPTION": "완료되면 알려드리겠습니다",
	"AsyncJobLoadingState.TAGGING_TITLE": "응답에 태그 지정 중",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "기존 응답과 앞으로의 응답에 태그가 적용됩니다. 두 개 이상의 키워드를 사용하려면 단어 사이에 공백을 두거나 <strong>OR</strong>을 입력합니다.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "응답에 자동으로 태그 지정",
	"Button.OK_LABEL": "확인",
	"CalendarPicker.month": "월 선택",
	"CalendarPicker.months": "{months, select, January {1월} February {2월} March {3월} April {4월} May {5월} June {6월} July {7월} August {8월} September {9월} October {10월} November {11월} December {12월} other {기타}}",
	"CalendarPicker.nextMonth": "다음 달",
	"CalendarPicker.previousMonth": "이전달",
	"CalendarPicker.year": "연도 선택",
	"ColorSwatches.TAG": "태그 {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "다색 단어 클라우드는 단어나 구가 언급되는 횟수를 나타내는 데 색과 크기를 모두 사용합니다.",
	"ColorTab.COLOR_SELECTOR_TITLE": "단어 클라우드 색 사용법",
	"ColorTab.DOWN_ARROW": "드롭다운 화살표 선택",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "다색",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "단색",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "취소",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. 새 단어 만들기",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "새 단어는 아래에 선택된 각 단어를 대체합니다.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "통합한 단어 수정",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "단어 통합",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "한 개 이상의 선택된 단어를 대체할 새 단어를 만드세요. 나중에 편집하여 원래의 단어로 되돌릴 수 있습니다.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "새 단어 입력",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "조합",
	"CombineWordsModal.WORD_LIST_HEADER": "2. 조합할 단어 선택",
	"CreateTagFromSearch.CLOSE_MENU": "취소",
	"CreateTagFromSearch.DUPLICATED_NAME": "해당 이름의 태그가 이미 있습니다.",
	"CreateTagFromSearch.NEW_TAG": "새 태그",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { 어떤 응답에도 태그가 \n\t\t지정되지 않습니다} other {{responseCount, plural, other {\n\t\t\t\t{responseTaggedFormatted} 응답에 \n\t\t\t\n\t\t태그가 지정됩니다}}}}",
	"CreateTagFromSearch.SAVE": "저장",
	"CreateTagFromSearch.TAG": "태그",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "태그 이름은 비워 둘 수 없습니다.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "태그 이름은 공백만으로 이루어질 수 없습니다.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "이러한 키워드에 일치하는 응답에 태그를 지정합니다. <strong>태그 만들기</strong>를 선택하세요. 색을 선택하고 저장하세요. 기존 응답과 앞으로의 응답에 태그가 적용됩니다.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "일치하는 응답에 태그 지정",
	"DashboardMenuContent.CREATE": "만들기",
	"DashboardMenuContent.NAME": "이름",
	"DashboardMenuContent.NEW_DASHBOARD": "새 대시보드",
	"DateInputWrapper.dateButton": "캘린더 열기",
	"DateInputWrapper.dateFormatIntl": "YYYY/MM/DD",
	"DateInputWrapper.dateFormatUs": "MM/DD/YYYY",
	"DateInputWrapper.dateLabel": "날짜",
	"DateTime.errorEnterHours": "0부터 23까지의 수로 시간을 입력하세요.",
	"DateTime.errorEnterMinutes": "0부터 59까지의 수로 분을 입력하세요.",
	"DateTime.errorSelectPeriod": "오전/오후를 선택하세요.",
	"DateTime.errorYearOutOfRange": "1900에서 9999 사이의 연도를 입력하세요.",
	"DateTime.formatErrorDateIntl": "DD/MM/YYYY(일/월/연도) 형식으로 날짜를 입력하세요.",
	"DateTime.formatErrorDateUs": "MM/DD/YYYY(월/일/연도) 형식으로 날짜를 입력하세요.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "취소",
	"EditorButtons.COPY_BUTTON": "질문 복사",
	"EditorButtons.MOVE_BUTTON": "질문 이동",
	"EditorButtons.NEXT_QUESTION_BUTTON": "다음 질문",
	"EditorButtons.SAVE_BUTTON": "저장",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "데이터 동향은 이 질문에 적용되지 않습니다.",
	"FallbackComponent.ERROR": "질문 로드 중 오류",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>파일을 선택하거나</div> <div class={dragClass}>여기에 끌어다 놓으세요</div>",
	"FileUpload.fileUploaded": "파일 업로드됨",
	"FileUpload.fileUploading": "파일 업로드 중",
	"FileUpload.removeFile": "파일 제거",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>파일을 바꾸거나</div> <div class={dragClass}>여기에 끌어다 놓으세요</div>",
	"FileUpload.supportedFiles": "지원되는 파일",
	"FilterMenuContent.APPLY_ACTIONS": "적용",
	"FilterMenuContent.CLEAR_ACTIONS": "비우기",
	"FilterMenuContent.CLOSE_MENU": "취소",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "취소",
	"HideWordsModal.HIDE_BUTTON_LABEL": "숨기기",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "저장",
	"HideWordsModal.HIDE_WORDS_HEADER": "단어 숨기기",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "단어 클라우드에서 숨길 단어를 선택하세요.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "숨길 단어 선택",
	"IconHelpStyled.HELP": "도움말",
	"IndividualResponseCard.NO_RESPONSE": "이 질문을 건너뛴 응답자",
	"IndividualResponseCard.QUESTION_NUMBER": "Q{number}",
	"InfoBannerTagManagement.CLOSE": "닫기",
	"InfoBannerTagManagement.NEW": "새 기능",
	"InfoBannerTagManagement.TAG_RESPONSES": "응답에 태그 지정",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "키워드를 추가하여 자동 태그를 설정하고 응답을 체계적으로 정리하세요.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "응답에 태그를 지정하여 쉽게 관리",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {긍정적: {percentage,number,percent}} negative {부정적: {percentage,number,percent}} neutral {중립적: {percentage,number,percent}} unknown {감지되지 않음: {percentage,number,percent}} other {기타}}",
	"LoadingState.LOADING": "응답 로드 중",
	"MatrixDropdown.EmptyTopNativeSelect": "옵션 선택",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "신뢰 구간",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "효용 점수",
	"Modal.CloseModal": "도구 설명 닫기",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {월요일} Tu {화요일} We {수요일} Th {목요일} Fr {금요일} Sa {토요일} Su {일요일} other {기타}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {월} Tu {화} We {수} Th {목} Fr {금} Sa {토} Su {일} other {기타}}",
	"MultiColorSelectorMenu.APPLY": "적용",
	"MultiColorSelectorMenu.CANCEL": "취소",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "데이터를 사용자 지정 레이아웃으로 정리하고, 발결한 사실에 대해 설명하고, 누구와든 대시보드를 공유하세요. 데이터를 사용자 지정 레이아웃으로 정리하고, 발결한 사실에 대해 설명하고, 누구와든 대시보드를 공유하세요.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "대시보드",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "자세히 알아보기",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">{minValue}~{maxValue}의 척도에서 </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue}은(는) {minLabelText}, {maxValue}은(는) {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "{min}~{max}의 점수에 대한 게이지 영역",
	"NpsGaugeChart.NPS_CHART": "NPS 게이지 차트",
	"NpsGaugeChart.NPS_SCORE": "NPS 점수 {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "{score} 점수 마크 위치에 대한 차트 틱",
	"OrderControl.AriaNA": "해당 없음",
	"OrderControl.NA": "해당 없음",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "‘{title}’ 응답({totalCount}개)",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "기타 응답 목록 숨기기",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "기타 응답 목록 표시",
	"PhoneField.countryCode": "국가 코드",
	"QuestionCardItemFooter.FILTER_APPLIED": "적용된 규칙: {filterCount,number}개",
	"QuestionCardItemFooter.FILTER_LABEL": "필터",
	"QuestionCardTitle.ANSWERED": "답변한 수: {answeredCount}",
	"QuestionCardTitle.SELECTED": "선택됨: {selectedCount}개",
	"QuestionCardTitle.SKIPPED": "건너뛴 수: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, other {{score}/{total}포인트}}",
	"QuizResultScore.SKIPPED": "건너뛴 수",
	"RangeTab.RANGE_TAB_LABEL": "멘션: {value}",
	"RangeTab.TOTAL_WORDS": "총 단어 수",
	"Ranking.AriaRankAtBottom": "{label}을(를) 목록의 맨 아래로 내림",
	"Ranking.AriaRankAtTop": "{label}을(를) 목록의 맨 위로 올림",
	"Ranking.AriaRankMovedDown": "{label}을(를) {position}번째로 내림",
	"Ranking.AriaRankMovedUp": "{label}을(를) {position}번째로 올림",
	"RankingItem.AriaRankMoveDown": "{label}을(를) 아래로 이동",
	"RankingItem.AriaRankMoveUp": "{label}을(를) 위로 이동",
	"RatingChart.AVERAGE_RATING": "평균 등급",
	"RatingChartLegend.ANSWERED": "답변한 수: {number}",
	"RatingChartLegend.RATING": "별 {rating}개",
	"RatingChartLegend.SKIPPED": "건너뛴 수: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "기능 요청",
	"RequestAccessButton.UPGRADE": "업그레이드",
	"RespondentQuestionTitle.REQUIRED": "(필수)",
	"ResponseItem.EXPAND_MENU_LABEL": "메뉴 확장",
	"ResponseItem.MENU_LABEL": "태그 추가",
	"ResponseItem.RESPONDENT_ID": "응답자 ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "응답자 답변 보기",
	"ResponseStatusBar.CLEAR": "비우기",
	"ResponseStatusBar.FAILED_TO_LOAD": "응답을 로드하지 못함",
	"ResponseStatusBar.NO_RESPONSES_YET": "아직 응답이 없음",
	"ResponseStatusBar.SEARCHING": "검색 중...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {긍정적 감성} negative {부정적 감성} neutral {중립적 감성} other {알 수 없는 감성}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {{filters}(으)로 {searchTextFormatted}에 대해 선택된 {resultsCountFormatted}개의 응답 표시 중   } other {{filters}(으)로 {searchTextFormatted}에 대한 {resultsCountFormatted}개의 응답 표시 중   }}}}} other {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {{filters}(으)로 선택된 {resultsCountFormatted}개의 응답 표시 중   } other {{filters}(으)로 {resultsCountFormatted}개의 응답 표시 중   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {{searchTextFormatted}에 대해 선택된 {resultsCountFormatted}개의 응답 표시 중   } other {{searchTextFormatted}에 대한 {resultsCountFormatted}개의 응답 표시 중   }}}}} other {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {선택된 {resultsCountFormatted}개의 응답 표시 중   } other {{resultsCountFormatted}개의 응답 표시 중   }}}}}}}}",
	"ResponseToolbar.CLEAR": "비우기",
	"ResponseToolbar.CREATE_TAG": "태그 만들기",
	"ResponseToolbar.SELECT_ALL": "모든 응답 선택",
	"ResponseToolbar.UNSELECT_ALL": "모든 응답 선택 해제",
	"SearchResponsesInput.CLEAR": "검색 비우기",
	"SearchResponsesInput.SEARCH": "검색",
	"SearchResponsesInput.SEARCH_RESPONSES": "응답 검색",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "<strong>“”</strong> 따옴표를 사용하여 정확한 구문을 검색합니다.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "<strong>OR</strong>를 입력하여 두 개 이상의 검색어를 찾습니다.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "<b>AND</b>를 입력하여 둘 이상의 검색어를 찾으세요.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "<strong>NOT</strong>을 사용하여 검색에서 특정 단어를 제외합니다.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "용어 뒤에 <strong>*</strong>를 입력하여 접두사로 검색하세요.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "괄호를 사용하여 AND/OR/NOT 기반의 검색을 결합하세요.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "용어 뒤에 <strong>~2</strong>를 입력하여 용어에 일치하는 검색 결과와 두 개까지의 다른 문자도 포함하여 찾으세요.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "검색 옵션",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "자세히 알아보기",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "안 함",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "사용",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "이제 계정에서 자동 <span id=\"link\">인사이트</span>를 이용할 수 있습니다.\n      이 설정은 머신 러닝을 사용하여 설문조사를 처리하고 인사이트를 제공하는 일체의 기능을 포함합니다.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "언제든 <span id=\"link\">내 계정</span>에서 기본 설정을 변경할 수 있습니다",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "자동 통찰력",
	"SentimentBanner.GENERATE_SENTIMENT": "감성 분석",
	"SentimentBanner.SENTIMENT_ANALYSIS": "감성 분석",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "서술형 응답에 담겨 있는 감성을 감지하여 사람들이 어떻게 느끼는지 파악하세요.",
	"SentimentBanner.UPGRADE": "업그레이드",
	"SentimentBanner.WATCH_A_DEMO": "데모 보기",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "사람들이 느끼는 감정:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {긍정적 감성별 필터링} negative {부정적 감성별 필터링} neutral {중립적 감성별 필터링} other {알 수 없는 감성별 필터링}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "메뉴 확장",
	"SentimentFilterMenu.MENU_LABEL": "정서별",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "기능 요청",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "서술된 응답에 담겨 있는 감정과 정서 상태를 감지하세요.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "새 기능!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "감성 분석 소개",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "업그레이드",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "데모 보기",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "안 함",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "기능 요청",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "업그레이드",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "정서 업그레이드 이미지",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "단 한 번의 클릭으로 감성 분석 기능을 사용하세요. 텍스트 응답을 긍정적, 중립적 또는 부정적으로 분류하여 응답자들이 제공한 답변에 담겨 있는 감성를 밝혀 드립니다.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "사람들이 어떤 감정을 느끼고 있는지 바로 알아보세요.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "감성 분석",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {감성: 켜짐} false {감성: 꺼짐} other {감성: 기타}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "정서: 로드 중...",
	"SlideBoxNavigation.NEXT_BUTTON": "다음",
	"SlideBoxNavigation.NEXT_SLIDE": "다음 슬라이드",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "이전",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "이전 슬라이드",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number}/{total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "비우기",
	"Slider.STEP_MESSAGE": "슬라이더 척도에 따라 입력하신 수치를 조정했습니다.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "답변한 수: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "Q{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "건너뛴 수: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "저가와 고가 사이의 교차점은 제품의 전반적인 가격 방향을 제시합니다.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "가격 민감도(판 베스텐도르프)",
	"SrText.CORRECT": "정답 수",
	"SrText.INCORRECT": "오답 수",
	"StarRating.Heart": "{count, plural, other {하트 {count}개}}",
	"StarRating.STAR": "{count, plural, other {별 {count}개}}",
	"StarRating.Smiley": "{count, plural, other {스마일 {count}개}}",
	"StarRating.Thumb": "{count, plural, other {엄지 척 {count}개}}",
	"TabList.BETA_BADGE": "베타",
	"TabList.LOCK_ICON": "태그 탭 잠금 아이콘",
	"TabList.RESPONSES": "응답({responseCount}개)",
	"TabList.TAGS": "태그({tagsCount}개)",
	"TabList.THEMATIC_ANALYSIS": "테마별 분석",
	"TabList.WORD_CLOUD": "단어 클라우드",
	"TagFilterMenu.EXPAND_MENU_LABEL": "메뉴 확장",
	"TagFilterMenu.MENU_LABEL": "필터: 태그별",
	"TagList.TAGS": "태그",
	"TagList.TAGS_KEYWORDS": "키워드",
	"TagList.TAG_CONTROLS": "태그 제어",
	"TagList.TAG_NUMBER_PERCENT": "태그 비율과 태그 수",
	"TagList.TAG_PERCENTAGE_BAR": "태그 비율 바",
	"TagListItem.DELETE": "삭제",
	"TagListItem.EDIT": "수정",
	"TagListItem.VIEW_ALL": "모두 보기",
	"TagListPanel.LOADING": "태그 로드 중",
	"TagListPanel.NEW_TAG": "+ 새 태그",
	"TagListPanel.TAGS": "태그",
	"TagMenuContent.APPLY_ACTIONS": "적용",
	"TagMenuContent.CLEAR_ACTIONS": "비우기",
	"TagMenuContent.CLOSE_MENU": "취소",
	"TagMenuContent.DUPLICATED_NAME": "해당 이름의 태그가 이미 있습니다.",
	"TagMenuContent.NEW_TAG": "새 태그",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "자세히 알아보기 >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "기능 요청",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "업그레이드",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "닫기",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "서술형 응답을 검색하고, 태그를 달며, 필터링하고, 자주 사용된 단어와 구를 단어 클라우드에서 보세요.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "텍스트 분석",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "hh",
	"TimeInputWrapper.hour": "시간",
	"TimeInputWrapper.minutes": "분",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "시간",
	"ToggleableEmailField.checkboxLabel": "이메일로 응답 복사본 받기",
	"ToggleableEmailField.emailLabel": "이메일 주소 입력",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "삭제",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, other {{responseCountFormatted}개 응답에서 태그가 해제됩니다.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "태그를 삭제하면 태그가 지정된 모든 응답에서 태그가 제거되며 이 작업은 실행 취소할 수 없습니다.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "태그를 삭제할까요?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "저장",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "변경 사항을 저장할까요?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "이로써 모든 응답에서 태그를 제거합니다. 편집된 키워드에 일치하는 응답에 태그가 다시 적용됩니다.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "취소",
	"UpgradeBanner.SUBTITLE": "단어 클라우드를 맞춤화하려면 업그레이드하세요.",
	"UpgradeBanner.TITLE": "마음에 드시나요?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "응답률(%)",
	"VanWestendorpChartDataTable.PRICE": "가격",
	"VanWestendorpChartDataTable.RESPONSES": "응답",
	"WordCloudCustomizationButton.CUSTOMIZE": "사용자 지정",
	"WordCloudCustomizationFooter.APPLY": "적용",
	"WordCloudCustomizationFooter.CANCEL": "취소",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "설문조사의 모든 단어 클라우드에 적용(일부는 변경되지 않을 수 있음)",
	"WordCloudCustomizationFooter.NO_THANKS": "안 함",
	"WordCloudCustomizationFooter.REQUEST": "기능 요청",
	"WordCloudCustomizationFooter.SAVE": "저장",
	"WordCloudCustomizationFooter.UPGRADE": "업그레이드",
	"WordCloudCustomizationTabs.COLOR": "색",
	"WordCloudCustomizationTabs.FONT": "글꼴",
	"WordCloudCustomizationTabs.RANGE": "범위",
	"WordCloudLegend.FEWER": "더 적음",
	"WordCloudLegend.MORE": "더 많음",
	"WordCloudLegend.TITLE": "멘션 수",
	"WordCloudListFooter.COMBINED_WORDS": "통합한 단어",
	"WordCloudListFooter.HIDDEN_WORDS": "숨겨진 단어",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "메뉴 확장",
	"WordCloudListViewMenuContent.COMBINE_WITH": "다음과 조합:",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "통합한 단어 수정",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "메뉴 확장",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "단어 클라우드에서 숨기기",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "통합한 단어 나누기",
	"WordCloudLoadingState.LOADING": "단어 클라우드 로드 중...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "닫기 버튼",
	"WordCloudPreviewOverlay.OK_BUTTON": "확인",
	"WordCloudPreviewOverlay.SUBTITLE": "사용자 지정 단어 클라우드 기능을 미리보세요.",
	"WordCloudPreviewOverlay.TITLE": "사용해 보세요!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "클라우드 뷰",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "목록 뷰",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "되풀이 항목: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "#{number}번째로 가장 중요한 단어",
	"WordCount.IN_PROGRESS": "텍스트 분석 진행 중...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "중요한 단어를 찾을 수 없습니다.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, other {{count}개 단어 표시 중 }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {긍정적 감성별 필터링} negative {부정적 감성별 필터링} neutral {중립적 감성별 필터링} other {감지되지 않은 감성별 필터링}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {긍정적} negative {부정적} neutral {중립적} other {알 수 없음}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {긍정적: {percentage,number,percent}} negative {부정적: {percentage,number,percent}} neutral {중립적: {percentage,number,percent}} other {알 수 없음: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "취소",
	"copyTagEditor.DUPLICATED_NAME": "해당 이름의 태그가 이미 있습니다.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "이 태그의 이름 입력",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "1개 이상의 키워드 입력",
	"copyTagEditor.KEYWORDS": "<strong>키워드</strong>(선택 사항)",
	"copyTagEditor.SAVE": "저장",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "일치하는 키워드가 있는 모든 응답에 태그가 적용됩니다.",
	"copyTagEditor.TAG_NAME": "<strong>태그 이름</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "태그 이름은 비워 둘 수 없습니다.",
	"copyTagEditor.TAG_NAME_SPACES": "태그 이름은 공백만으로 이루어질 수 없습니다.",
	"copyTagEditor.TAG_TOO_LONG": "태그는 128자 미만이어야 합니다.",
	"countriesL10nMessages.AC": "어센션 섬",
	"countriesL10nMessages.AD": "안도라",
	"countriesL10nMessages.AE": "아랍 에미리트 연합",
	"countriesL10nMessages.AF": "아프가니스탄",
	"countriesL10nMessages.AG": "앤티가 바부다",
	"countriesL10nMessages.AI": "앵귈라",
	"countriesL10nMessages.AL": "알바니아",
	"countriesL10nMessages.AM": "아르메니아",
	"countriesL10nMessages.AO": "앙골라",
	"countriesL10nMessages.AR": "아르헨티나",
	"countriesL10nMessages.AS": "미국령 사모아(America Samoa)",
	"countriesL10nMessages.AT": "오스트리아",
	"countriesL10nMessages.AU": "호주",
	"countriesL10nMessages.AW": "아루바",
	"countriesL10nMessages.AX": "알란드 섬",
	"countriesL10nMessages.AZ": "아제르바이잔",
	"countriesL10nMessages.BA": "보스니아 헤르체고비나",
	"countriesL10nMessages.BB": "바베이도스",
	"countriesL10nMessages.BD": "방글라데시",
	"countriesL10nMessages.BE": "벨기에",
	"countriesL10nMessages.BF": "부르키나 파소",
	"countriesL10nMessages.BG": "불가리아",
	"countriesL10nMessages.BH": "바레인",
	"countriesL10nMessages.BI": "부룬디",
	"countriesL10nMessages.BJ": "베닌",
	"countriesL10nMessages.BL": "생바르텔레미",
	"countriesL10nMessages.BM": "버뮤다",
	"countriesL10nMessages.BN": "브루나이",
	"countriesL10nMessages.BO": "볼리비아",
	"countriesL10nMessages.BQ": "보네르, 신트외스타티우스 및 사바",
	"countriesL10nMessages.BR": "브라질",
	"countriesL10nMessages.BS": "바하마",
	"countriesL10nMessages.BT": "부탄",
	"countriesL10nMessages.BW": "보츠와나",
	"countriesL10nMessages.BZ": "벨리즈",
	"countriesL10nMessages.CA": "캐나다",
	"countriesL10nMessages.CC": "코코스제도(Cocos (Keeling) Islands)",
	"countriesL10nMessages.CD": "콩고민주공화국",
	"countriesL10nMessages.CF": "중앙 아프리카 공화국",
	"countriesL10nMessages.CG": "콩고",
	"countriesL10nMessages.CH": "스위스",
	"countriesL10nMessages.CI": "코트디부아르",
	"countriesL10nMessages.CK": "쿡 제도",
	"countriesL10nMessages.CL": "칠레",
	"countriesL10nMessages.CM": "카메룬",
	"countriesL10nMessages.CN": "중국",
	"countriesL10nMessages.CO": "콜롬비아",
	"countriesL10nMessages.CR": "코스타리카",
	"countriesL10nMessages.CV": "카보베르데",
	"countriesL10nMessages.CW": "퀴라소",
	"countriesL10nMessages.CX": "크리스마스아일랜드(Christmas Island)",
	"countriesL10nMessages.CY": "키프로스",
	"countriesL10nMessages.CZ": "체코",
	"countriesL10nMessages.DE": "독일",
	"countriesL10nMessages.DJ": "지부티",
	"countriesL10nMessages.DK": "덴마크",
	"countriesL10nMessages.DM": "도미니카",
	"countriesL10nMessages.DO": "도미니카 공화국",
	"countriesL10nMessages.DZ": "알제리",
	"countriesL10nMessages.EC": "에콰도르",
	"countriesL10nMessages.EE": "에스토니아",
	"countriesL10nMessages.EG": "이집트",
	"countriesL10nMessages.EH": "서 사하라",
	"countriesL10nMessages.ER": "에리트레아",
	"countriesL10nMessages.ES": "스페인",
	"countriesL10nMessages.ET": "이디오피아",
	"countriesL10nMessages.FI": "핀란드",
	"countriesL10nMessages.FJ": "피지",
	"countriesL10nMessages.FK": "포클랜드 제도",
	"countriesL10nMessages.FM": "미크로네시아 연방공화국",
	"countriesL10nMessages.FO": "페로 군도",
	"countriesL10nMessages.FR": "프랑스",
	"countriesL10nMessages.GA": "가봉",
	"countriesL10nMessages.GB": "영국",
	"countriesL10nMessages.GD": "그레나다",
	"countriesL10nMessages.GE": "조지아",
	"countriesL10nMessages.GF": "프랑스령 기아나",
	"countriesL10nMessages.GG": "건지",
	"countriesL10nMessages.GH": "가나",
	"countriesL10nMessages.GI": "지브롤터",
	"countriesL10nMessages.GL": "그린란드",
	"countriesL10nMessages.GM": "감비아",
	"countriesL10nMessages.GN": "기니",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "적도 기니",
	"countriesL10nMessages.GR": "그리스",
	"countriesL10nMessages.GT": "과테말라",
	"countriesL10nMessages.GU": "괌(Guam)",
	"countriesL10nMessages.GW": "기니비사우",
	"countriesL10nMessages.GY": "가이아나",
	"countriesL10nMessages.HK": "홍콩",
	"countriesL10nMessages.HN": "온두라스",
	"countriesL10nMessages.HR": "크로아티아",
	"countriesL10nMessages.HT": "아이티",
	"countriesL10nMessages.HU": "헝가리",
	"countriesL10nMessages.ID": "인도네시아",
	"countriesL10nMessages.IE": "아일랜드",
	"countriesL10nMessages.IL": "이스라엘",
	"countriesL10nMessages.IM": "맨 섬",
	"countriesL10nMessages.IN": "인도",
	"countriesL10nMessages.IO": "영인도 제도",
	"countriesL10nMessages.IQ": "이라크",
	"countriesL10nMessages.IS": "아이슬란드",
	"countriesL10nMessages.IT": "이탈리아",
	"countriesL10nMessages.JE": "저지",
	"countriesL10nMessages.JM": "자메이카",
	"countriesL10nMessages.JO": "요르단",
	"countriesL10nMessages.JP": "일본",
	"countriesL10nMessages.KE": "케냐",
	"countriesL10nMessages.KG": "키르기스스탄",
	"countriesL10nMessages.KH": "캄보디아",
	"countriesL10nMessages.KI": "키리바시",
	"countriesL10nMessages.KM": "코모로",
	"countriesL10nMessages.KN": "세인트 크리스토퍼 네비스",
	"countriesL10nMessages.KR": "한국",
	"countriesL10nMessages.KW": "쿠웨이트",
	"countriesL10nMessages.KY": "케이맨 제도",
	"countriesL10nMessages.KZ": "카자흐스탄",
	"countriesL10nMessages.LA": "라오스",
	"countriesL10nMessages.LB": "레바논",
	"countriesL10nMessages.LC": "세인트 루시아",
	"countriesL10nMessages.LI": "리히텐슈타인",
	"countriesL10nMessages.LK": "스리랑카",
	"countriesL10nMessages.LR": "리베리아",
	"countriesL10nMessages.LS": "레소토",
	"countriesL10nMessages.LT": "리투아니아",
	"countriesL10nMessages.LU": "룩셈부르크",
	"countriesL10nMessages.LV": "라트비아",
	"countriesL10nMessages.LY": "리비아",
	"countriesL10nMessages.MA": "모로코",
	"countriesL10nMessages.MC": "모나코(Monaco)",
	"countriesL10nMessages.MD": "몰도바",
	"countriesL10nMessages.ME": "몬테네그로",
	"countriesL10nMessages.MF": "세인트 마틴(프랑스 영역)",
	"countriesL10nMessages.MG": "마다가스카르",
	"countriesL10nMessages.MH": "마샬 군도",
	"countriesL10nMessages.MK": "마케도니아",
	"countriesL10nMessages.ML": "말리",
	"countriesL10nMessages.MM": "미얀마",
	"countriesL10nMessages.MN": "몽고",
	"countriesL10nMessages.MO": "마카오",
	"countriesL10nMessages.MP": "북마리아나 제도",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "모라타니아",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "몰타",
	"countriesL10nMessages.MU": "모리셔스",
	"countriesL10nMessages.MV": "몰디브",
	"countriesL10nMessages.MW": "말라위",
	"countriesL10nMessages.MX": "멕시코",
	"countriesL10nMessages.MY": "말레이시아",
	"countriesL10nMessages.MZ": "모잠비크",
	"countriesL10nMessages.NA": "나미비아",
	"countriesL10nMessages.NC": "뉴 칼레도니아",
	"countriesL10nMessages.NE": "니제르",
	"countriesL10nMessages.NF": "노폭 섬(Norfolk Island)",
	"countriesL10nMessages.NG": "나이지리아",
	"countriesL10nMessages.NI": "니카라과",
	"countriesL10nMessages.NL": "네덜란드",
	"countriesL10nMessages.NO": "노르웨이",
	"countriesL10nMessages.NP": "네팔",
	"countriesL10nMessages.NR": "나우루",
	"countriesL10nMessages.NU": "니우에",
	"countriesL10nMessages.NZ": "뉴질랜드",
	"countriesL10nMessages.OM": "오만",
	"countriesL10nMessages.PA": "파나마",
	"countriesL10nMessages.PE": "페루",
	"countriesL10nMessages.PF": "프랑스령 폴리네시아",
	"countriesL10nMessages.PG": "파푸아뉴기니",
	"countriesL10nMessages.PH": "필리핀",
	"countriesL10nMessages.PK": "파키스탄",
	"countriesL10nMessages.PL": "폴란드",
	"countriesL10nMessages.PM": "세인트 피에르 미퀠론",
	"countriesL10nMessages.PR": "푸에르토리코",
	"countriesL10nMessages.PS": "팔레스타인",
	"countriesL10nMessages.PT": "포르투갈",
	"countriesL10nMessages.PW": "팔라우(Palau)",
	"countriesL10nMessages.PY": "파라과이",
	"countriesL10nMessages.QA": "카타르",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "루마니아",
	"countriesL10nMessages.RS": "세르비아",
	"countriesL10nMessages.RW": "르완다",
	"countriesL10nMessages.SA": "사우디아라비아",
	"countriesL10nMessages.SB": "솔로몬 군도",
	"countriesL10nMessages.SC": "세이셸",
	"countriesL10nMessages.SE": "스웨덴",
	"countriesL10nMessages.SG": "싱가포르",
	"countriesL10nMessages.SH": "세인트 헬레나",
	"countriesL10nMessages.SI": "슬로베니아",
	"countriesL10nMessages.SJ": "스발바르 얀마옌 제도",
	"countriesL10nMessages.SK": "슬로바키아",
	"countriesL10nMessages.SL": "시에라리온",
	"countriesL10nMessages.SM": "산마리노(San Marino)",
	"countriesL10nMessages.SN": "세네갈",
	"countriesL10nMessages.SO": "소말리아",
	"countriesL10nMessages.SR": "수리남",
	"countriesL10nMessages.SS": "남수단",
	"countriesL10nMessages.ST": "상투메 프린시페",
	"countriesL10nMessages.SV": "엘살바도르",
	"countriesL10nMessages.SX": "신트마르턴",
	"countriesL10nMessages.SZ": "스와질랜드",
	"countriesL10nMessages.TA": "트리스탄다쿠냐",
	"countriesL10nMessages.TC": "터크스 카이코스 군도",
	"countriesL10nMessages.TD": "차드",
	"countriesL10nMessages.TG": "토고",
	"countriesL10nMessages.TH": "태국",
	"countriesL10nMessages.TJ": "타지키스탄",
	"countriesL10nMessages.TK": "토켈라우",
	"countriesL10nMessages.TL": "동티모르",
	"countriesL10nMessages.TM": "투르크메니스탄",
	"countriesL10nMessages.TN": "튀니지",
	"countriesL10nMessages.TO": "통가",
	"countriesL10nMessages.TR": "터키",
	"countriesL10nMessages.TT": "트리니다드토바고",
	"countriesL10nMessages.TV": "투발루",
	"countriesL10nMessages.TW": "대만",
	"countriesL10nMessages.TZ": "탄자니아",
	"countriesL10nMessages.UA": "우크라이나",
	"countriesL10nMessages.UG": "우간다",
	"countriesL10nMessages.US": "미국",
	"countriesL10nMessages.UY": "우루과이",
	"countriesL10nMessages.UZ": "우즈베키스탄",
	"countriesL10nMessages.VA": "교황청(바티칸 시국)",
	"countriesL10nMessages.VC": "세인트빈센트 그레나딘",
	"countriesL10nMessages.VE": "베네수엘라",
	"countriesL10nMessages.VG": "영국령 버진 아일랜드",
	"countriesL10nMessages.VI": "미국령 버진 아일랜드",
	"countriesL10nMessages.VN": "베트남",
	"countriesL10nMessages.VU": "바누아투",
	"countriesL10nMessages.WF": "월리스푸투나",
	"countriesL10nMessages.WS": "사모아",
	"countriesL10nMessages.XK": "코소보",
	"countriesL10nMessages.YE": "예멘",
	"countriesL10nMessages.YT": "마요트",
	"countriesL10nMessages.ZA": "남아프리카 공화국",
	"countriesL10nMessages.ZM": "잠비아",
	"countriesL10nMessages.ZW": "짐바브웨",
	"helper.INDIFFERENCE_PRICE_POINT": "무관심 가격 포인트",
	"helper.IPP": "무관심 가격 포인트(IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "최적 가격 포인트(OPP)",
	"helper.OPP_DESCRIPTION": "최적 가격 포인트",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "최저 한계 가격(PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "최고 한계 가격(PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "최저 한계 가격",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "최고 한계 가격"
};

var lt = {
	"AddressField.selectCountry": "Pasirinkite šalį",
	"PhoneField.countryCode": "Šalies kodas",
	"countriesL10nMessages.AC": "Dangun Žengimo Sala",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Jungtiniai Arabų Emyratai",
	"countriesL10nMessages.AF": "Afganistanas",
	"countriesL10nMessages.AG": "Antigva ir Barbuda",
	"countriesL10nMessages.AI": "Angilija",
	"countriesL10nMessages.AL": "Albanija",
	"countriesL10nMessages.AM": "Armėnija",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Amerikos Samoa",
	"countriesL10nMessages.AT": "Austrija",
	"countriesL10nMessages.AU": "Australija",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Alandų Salos",
	"countriesL10nMessages.AZ": "Azerbaidžanas",
	"countriesL10nMessages.BA": "Bosnija ir Hercegovina",
	"countriesL10nMessages.BB": "Barbadosas",
	"countriesL10nMessages.BD": "Bangladešas",
	"countriesL10nMessages.BE": "Belgija",
	"countriesL10nMessages.BF": "Burkina Fasas",
	"countriesL10nMessages.BG": "Bulgarija",
	"countriesL10nMessages.BH": "Bahreinas",
	"countriesL10nMessages.BI": "Burundis",
	"countriesL10nMessages.BJ": "Beninas",
	"countriesL10nMessages.BL": "Šv. Bartolomėjaus sala",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunėjaus Darusalamas",
	"countriesL10nMessages.BO": "Bolivija",
	"countriesL10nMessages.BQ": "Boneras, Sint Eustatijus ir Saba",
	"countriesL10nMessages.BR": "Brazilija",
	"countriesL10nMessages.BS": "Bahamos",
	"countriesL10nMessages.BT": "Butanas",
	"countriesL10nMessages.BW": "Botsvana",
	"countriesL10nMessages.BZ": "Belizas",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosų (Kilingo) Salos",
	"countriesL10nMessages.CD": "Kongo Demokratinė Respublika",
	"countriesL10nMessages.CF": "Centrinės Afrikos Respublika",
	"countriesL10nMessages.CG": "Kongas",
	"countriesL10nMessages.CH": "Šveicarija",
	"countriesL10nMessages.CI": "Dramblio Kaulo Krantas",
	"countriesL10nMessages.CK": "Kuko Salos",
	"countriesL10nMessages.CL": "Čilė",
	"countriesL10nMessages.CM": "Kamerūnas",
	"countriesL10nMessages.CN": "Kinija",
	"countriesL10nMessages.CO": "Kolumbija",
	"countriesL10nMessages.CR": "Kosta Rika",
	"countriesL10nMessages.CV": "Žaliasis Kyšulys",
	"countriesL10nMessages.CW": "Kiurasao",
	"countriesL10nMessages.CX": "Kalėdų Sala",
	"countriesL10nMessages.CY": "Kipras",
	"countriesL10nMessages.CZ": "Čekijos Respublika",
	"countriesL10nMessages.DE": "Vokietija",
	"countriesL10nMessages.DJ": "Džibutis",
	"countriesL10nMessages.DK": "Danija",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikos Respublika",
	"countriesL10nMessages.DZ": "Alžyras",
	"countriesL10nMessages.EC": "Ekvadoras",
	"countriesL10nMessages.EE": "Estija",
	"countriesL10nMessages.EG": "Egiptas",
	"countriesL10nMessages.EH": "Vakarų Sachara",
	"countriesL10nMessages.ER": "Eritrėja",
	"countriesL10nMessages.ES": "Ispanija",
	"countriesL10nMessages.ET": "Etiopija",
	"countriesL10nMessages.FI": "Suomija",
	"countriesL10nMessages.FJ": "Fidžis",
	"countriesL10nMessages.FK": "Folklando Salos",
	"countriesL10nMessages.FM": "Mikronezijos Federacinės Valstijos",
	"countriesL10nMessages.FO": "Farerai",
	"countriesL10nMessages.FR": "Prancūzija",
	"countriesL10nMessages.GA": "Gabonas",
	"countriesL10nMessages.GB": "Jungtinė Karalystė",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzija",
	"countriesL10nMessages.GF": "Prancūzijos Gviana",
	"countriesL10nMessages.GG": "Gernsis",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltaras",
	"countriesL10nMessages.GL": "Grenlandija",
	"countriesL10nMessages.GM": "Gambija",
	"countriesL10nMessages.GN": "Gvinėja",
	"countriesL10nMessages.GP": "Gvadelupa",
	"countriesL10nMessages.GQ": "Pusiaujo Gvinėja",
	"countriesL10nMessages.GR": "Graikija",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Guamas",
	"countriesL10nMessages.GW": "Bisau Gvinėja",
	"countriesL10nMessages.GY": "Gajana",
	"countriesL10nMessages.HK": "Honkongas",
	"countriesL10nMessages.HN": "Hondūras",
	"countriesL10nMessages.HR": "Kroatija",
	"countriesL10nMessages.HT": "Haitis",
	"countriesL10nMessages.HU": "Vengrija",
	"countriesL10nMessages.ID": "Indonezija",
	"countriesL10nMessages.IE": "Airija",
	"countriesL10nMessages.IL": "Izraelis",
	"countriesL10nMessages.IM": "Meno Sala",
	"countriesL10nMessages.IN": "Indija",
	"countriesL10nMessages.IO": "Indijos Vandenyno Britų Sritis",
	"countriesL10nMessages.IQ": "Irakas",
	"countriesL10nMessages.IS": "Islandija",
	"countriesL10nMessages.IT": "Italija",
	"countriesL10nMessages.JE": "Džersis",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Jordanija",
	"countriesL10nMessages.JP": "Japonija",
	"countriesL10nMessages.KE": "Kenija",
	"countriesL10nMessages.KG": "Kirgizija",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribatis",
	"countriesL10nMessages.KM": "Komorai",
	"countriesL10nMessages.KN": "Sent Kitsas ir Nevis",
	"countriesL10nMessages.KR": "Pietų Korėja",
	"countriesL10nMessages.KW": "Kuveitas",
	"countriesL10nMessages.KY": "Kaimanų Salos",
	"countriesL10nMessages.KZ": "Kazachstanas",
	"countriesL10nMessages.LA": "Laosas",
	"countriesL10nMessages.LB": "Libanas",
	"countriesL10nMessages.LC": "Sent Lusija",
	"countriesL10nMessages.LI": "Lichtenšteinas",
	"countriesL10nMessages.LK": "Šri Lanka",
	"countriesL10nMessages.LR": "Liberija",
	"countriesL10nMessages.LS": "Lesotas",
	"countriesL10nMessages.LT": "Lietuva",
	"countriesL10nMessages.LU": "Liuksemburgas",
	"countriesL10nMessages.LV": "Latvija",
	"countriesL10nMessages.LY": "Libija",
	"countriesL10nMessages.MA": "Marokas",
	"countriesL10nMessages.MC": "Monakas",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Juodkalnija",
	"countriesL10nMessages.MF": "Sen Martenas (Prancūzijos dalis)",
	"countriesL10nMessages.MG": "Madagaskaras",
	"countriesL10nMessages.MH": "Maršalo Salos",
	"countriesL10nMessages.MK": "Makedonija",
	"countriesL10nMessages.ML": "Malis",
	"countriesL10nMessages.MM": "Mianmaras",
	"countriesL10nMessages.MN": "Mongolija",
	"countriesL10nMessages.MO": "Makao",
	"countriesL10nMessages.MP": "Marianos Šiaurinės Salos",
	"countriesL10nMessages.MQ": "Martinika",
	"countriesL10nMessages.MR": "Mauritanija",
	"countriesL10nMessages.MS": "Montseratas",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauricijus",
	"countriesL10nMessages.MV": "Maldyvai",
	"countriesL10nMessages.MW": "Malavis",
	"countriesL10nMessages.MX": "Meksika",
	"countriesL10nMessages.MY": "Malaizija",
	"countriesL10nMessages.MZ": "Mozambikas",
	"countriesL10nMessages.NA": "Namibija",
	"countriesL10nMessages.NC": "Naujoji Kaledonija",
	"countriesL10nMessages.NE": "Nigeris",
	"countriesL10nMessages.NF": "Norfolko Sala",
	"countriesL10nMessages.NG": "Nigerija",
	"countriesL10nMessages.NI": "Nikaragva",
	"countriesL10nMessages.NL": "Nyderlandai",
	"countriesL10nMessages.NO": "Norvegija",
	"countriesL10nMessages.NP": "Nepalas",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niujė",
	"countriesL10nMessages.NZ": "Naujoji Zelandija",
	"countriesL10nMessages.OM": "Omanas",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Prancūzijos Polinezija",
	"countriesL10nMessages.PG": "Papua Naujoji Gvinėja",
	"countriesL10nMessages.PH": "Filipinai",
	"countriesL10nMessages.PK": "Pakistanas",
	"countriesL10nMessages.PL": "Lenkija",
	"countriesL10nMessages.PM": "Sen Pjeras ir Mikelonas",
	"countriesL10nMessages.PR": "Puerto Rikas",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugalija",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragvajus",
	"countriesL10nMessages.QA": "Kataras",
	"countriesL10nMessages.RE": "Reunjonas",
	"countriesL10nMessages.RO": "Rumunija",
	"countriesL10nMessages.RS": "Serbija",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudo Arabija",
	"countriesL10nMessages.SB": "Saliamono Salos",
	"countriesL10nMessages.SC": "Seišeliai",
	"countriesL10nMessages.SE": "Švedija",
	"countriesL10nMessages.SG": "Singapūras",
	"countriesL10nMessages.SH": "Šv. Elenos Sala",
	"countriesL10nMessages.SI": "Slovėnija",
	"countriesL10nMessages.SJ": "Svalbardas ir Janas Majenas",
	"countriesL10nMessages.SK": "Slovakija",
	"countriesL10nMessages.SL": "Siera Leonė",
	"countriesL10nMessages.SM": "San Marinas",
	"countriesL10nMessages.SN": "Senegalas",
	"countriesL10nMessages.SO": "Somalis",
	"countriesL10nMessages.SR": "Surinamas",
	"countriesL10nMessages.SS": "Pietų Sudanas",
	"countriesL10nMessages.ST": "San Tomė ir Prinsipė",
	"countriesL10nMessages.SV": "Salvadoras",
	"countriesL10nMessages.SX": "Šventojo Martyno Sala",
	"countriesL10nMessages.SZ": "Svazilandas",
	"countriesL10nMessages.TA": "Tristanas da Kunja",
	"countriesL10nMessages.TC": "Terkso ir Kaikoso Salos",
	"countriesL10nMessages.TD": "Čadas",
	"countriesL10nMessages.TG": "Togas",
	"countriesL10nMessages.TH": "Tailandas",
	"countriesL10nMessages.TJ": "Tadžikistanas",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Rytų Timoras",
	"countriesL10nMessages.TM": "Turkmėnistanas",
	"countriesL10nMessages.TN": "Tunisas",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turkija",
	"countriesL10nMessages.TT": "Trinidadas ir Tobagas",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taivanas",
	"countriesL10nMessages.TZ": "Tanzanija",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Jungtinės Valstijos",
	"countriesL10nMessages.UY": "Urugvajus",
	"countriesL10nMessages.UZ": "Uzbekistanas",
	"countriesL10nMessages.VA": "Šventasis Sostas (Vatikano Miesto Valstybė)",
	"countriesL10nMessages.VC": "Sent Vinsentas ir Grenadinai",
	"countriesL10nMessages.VE": "Venesuela",
	"countriesL10nMessages.VG": "Didžiosios Britanijos Mergelių Salos",
	"countriesL10nMessages.VI": "Jungtinių Valstijų Mergelių Salos",
	"countriesL10nMessages.VN": "Vietnamas",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Volisas ir Futūna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovas",
	"countriesL10nMessages.YE": "Jemenas",
	"countriesL10nMessages.YT": "Majotas",
	"countriesL10nMessages.ZA": "Pietų Afrika",
	"countriesL10nMessages.ZM": "Zambija",
	"countriesL10nMessages.ZW": "Zimbabvė",
	"ToggleableEmailField.checkboxLabel": "Atsiųskite man mano atsakymus el. paštu",
	"ToggleableEmailField.emailLabel": "Įveskite el. pašto adresą"
};

var lv = {
	"AddressField.selectCountry": "Atlasīt valsti",
	"PhoneField.countryCode": "Valsts kods",
	"countriesL10nMessages.AC": "Debesbraukšanas sala",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Apvienotie Arābu Emirāti",
	"countriesL10nMessages.AF": "Afganistāna",
	"countriesL10nMessages.AG": "Antigva un Barbuda",
	"countriesL10nMessages.AI": "Angilja",
	"countriesL10nMessages.AL": "Albānija",
	"countriesL10nMessages.AM": "Armēnija",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentīna",
	"countriesL10nMessages.AS": "ASV Samoa",
	"countriesL10nMessages.AT": "Austrija",
	"countriesL10nMessages.AU": "Austrālija",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Olandes salas",
	"countriesL10nMessages.AZ": "Azerbaidžāna",
	"countriesL10nMessages.BA": "Bosnija un Hercegovina",
	"countriesL10nMessages.BB": "Barbadosa",
	"countriesL10nMessages.BD": "Bangladeša",
	"countriesL10nMessages.BE": "Beļģija",
	"countriesL10nMessages.BF": "Burkinafaso",
	"countriesL10nMessages.BG": "Bulgārija",
	"countriesL10nMessages.BH": "Bahreina",
	"countriesL10nMessages.BI": "Burundija",
	"countriesL10nMessages.BJ": "Benina",
	"countriesL10nMessages.BL": "Senbartelmī",
	"countriesL10nMessages.BM": "Bermudu salas",
	"countriesL10nMessages.BN": "Bruneja Darusalama",
	"countriesL10nMessages.BO": "Bolīvija",
	"countriesL10nMessages.BQ": "Bonaire, Sintēstatiusa un Saba",
	"countriesL10nMessages.BR": "Brazīlija",
	"countriesL10nMessages.BS": "Bahamu Salas",
	"countriesL10nMessages.BT": "Butāna",
	"countriesL10nMessages.BW": "Botsvāna",
	"countriesL10nMessages.BZ": "Beliza",
	"countriesL10nMessages.CA": "Kanāda",
	"countriesL10nMessages.CC": "Kokosu un Kīlinga salas",
	"countriesL10nMessages.CD": "Kongo Demokrātiskā Republika",
	"countriesL10nMessages.CF": "Centrālāfrikas Republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Šveice",
	"countriesL10nMessages.CI": "Kotdivuāra",
	"countriesL10nMessages.CK": "Kuka Salas",
	"countriesL10nMessages.CL": "Čīle",
	"countriesL10nMessages.CM": "Kamerūna",
	"countriesL10nMessages.CN": "Ķīna",
	"countriesL10nMessages.CO": "Kolumbija",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Kaboverde",
	"countriesL10nMessages.CW": "Kirasao",
	"countriesL10nMessages.CX": "Ziemsvētku sala",
	"countriesL10nMessages.CY": "Kipra",
	"countriesL10nMessages.CZ": "Čehijas Republika",
	"countriesL10nMessages.DE": "Vācija",
	"countriesL10nMessages.DJ": "Džibutija",
	"countriesL10nMessages.DK": "Dānija",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikānas Republika",
	"countriesL10nMessages.DZ": "Alžīrija",
	"countriesL10nMessages.EC": "Ekvadora",
	"countriesL10nMessages.EE": "Igaunija",
	"countriesL10nMessages.EG": "Ēģipte",
	"countriesL10nMessages.EH": "Rietumsahāra",
	"countriesL10nMessages.ER": "Eritreja",
	"countriesL10nMessages.ES": "Spānija",
	"countriesL10nMessages.ET": "Etiopija",
	"countriesL10nMessages.FI": "Somija",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Folklenda salas",
	"countriesL10nMessages.FM": "Mikronēzijas Federatīvās Valstis",
	"countriesL10nMessages.FO": "Fēru salas",
	"countriesL10nMessages.FR": "Francija",
	"countriesL10nMessages.GA": "Gabona",
	"countriesL10nMessages.GB": "Apvienotā Karaliste",
	"countriesL10nMessages.GD": "Grenāda",
	"countriesL10nMessages.GE": "Gruzija",
	"countriesL10nMessages.GF": "Francijas Gviāna",
	"countriesL10nMessages.GG": "Gērnsija",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltārs",
	"countriesL10nMessages.GL": "Grenlande",
	"countriesL10nMessages.GM": "Gambija",
	"countriesL10nMessages.GN": "Gvineja",
	"countriesL10nMessages.GP": "Gvadelupa",
	"countriesL10nMessages.GQ": "Ekvatoriālā Gvineja",
	"countriesL10nMessages.GR": "Grieķija",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Guama",
	"countriesL10nMessages.GW": "Gvineja-Bisava",
	"countriesL10nMessages.GY": "Gajāna",
	"countriesL10nMessages.HK": "Honkonga",
	"countriesL10nMessages.HN": "Hondurasa",
	"countriesL10nMessages.HR": "Horvātija",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungārija",
	"countriesL10nMessages.ID": "Indonēzija",
	"countriesL10nMessages.IE": "Īrija",
	"countriesL10nMessages.IL": "Izraēla",
	"countriesL10nMessages.IM": "Menas Sala",
	"countriesL10nMessages.IN": "Indija",
	"countriesL10nMessages.IO": "Indijas Okeāna Britu teritorija",
	"countriesL10nMessages.IQ": "Irāka",
	"countriesL10nMessages.IS": "Islande",
	"countriesL10nMessages.IT": "Itālija",
	"countriesL10nMessages.JE": "Džērsija",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Jordānija",
	"countriesL10nMessages.JP": "Japāna",
	"countriesL10nMessages.KE": "Kenija",
	"countriesL10nMessages.KG": "Kirgiztāna",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoras",
	"countriesL10nMessages.KN": "Sentkitsa un Nevisa",
	"countriesL10nMessages.KR": "Dienvidkoreja",
	"countriesL10nMessages.KW": "Kuveita",
	"countriesL10nMessages.KY": "Kaimanu salas",
	"countriesL10nMessages.KZ": "Kazahstāna",
	"countriesL10nMessages.LA": "Laosa",
	"countriesL10nMessages.LB": "Libāna",
	"countriesL10nMessages.LC": "Sentlūsija",
	"countriesL10nMessages.LI": "Lihtenšteina",
	"countriesL10nMessages.LK": "Šrilanka",
	"countriesL10nMessages.LR": "Libērija",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Lietuva",
	"countriesL10nMessages.LU": "Luksemburga",
	"countriesL10nMessages.LV": "Latvija",
	"countriesL10nMessages.LY": "Lībija",
	"countriesL10nMessages.MA": "Maroka",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Melnkalne",
	"countriesL10nMessages.MF": "Francijas Senmartēnas teritorija",
	"countriesL10nMessages.MG": "Madagaskara",
	"countriesL10nMessages.MH": "Māršala Salas",
	"countriesL10nMessages.MK": "Maķedonija",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mjanma",
	"countriesL10nMessages.MN": "Mongolija",
	"countriesL10nMessages.MO": "Makao",
	"countriesL10nMessages.MP": "Ziemeļu Marianas Salas",
	"countriesL10nMessages.MQ": "Martinika",
	"countriesL10nMessages.MR": "Mauritānija",
	"countriesL10nMessages.MS": "Montserrata",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurīcija",
	"countriesL10nMessages.MV": "Maldīvija",
	"countriesL10nMessages.MW": "Malāvija",
	"countriesL10nMessages.MX": "Meksika",
	"countriesL10nMessages.MY": "Malaizija",
	"countriesL10nMessages.MZ": "Mozambika",
	"countriesL10nMessages.NA": "Namībija",
	"countriesL10nMessages.NC": "Jaunkaledonija",
	"countriesL10nMessages.NE": "Nigēra",
	"countriesL10nMessages.NF": "Norfolkas sala",
	"countriesL10nMessages.NG": "Nigērija",
	"countriesL10nMessages.NI": "Nikaragva",
	"countriesL10nMessages.NL": "Nīderlande",
	"countriesL10nMessages.NO": "Norvēģija",
	"countriesL10nMessages.NP": "Nepāla",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Jaunzēlande",
	"countriesL10nMessages.OM": "Omāna",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francijas Polinēzija",
	"countriesL10nMessages.PG": "Papua–Jaungvineja",
	"countriesL10nMessages.PH": "Filipīnas",
	"countriesL10nMessages.PK": "Pakistāna",
	"countriesL10nMessages.PL": "Polija",
	"countriesL10nMessages.PM": "Senpjēra un Mikelona",
	"countriesL10nMessages.PR": "Puertoriko",
	"countriesL10nMessages.PS": "Palestīna",
	"countriesL10nMessages.PT": "Portugāle",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragvaja",
	"countriesL10nMessages.QA": "Katara",
	"countriesL10nMessages.RE": "Reinjona",
	"countriesL10nMessages.RO": "Rumānija",
	"countriesL10nMessages.RS": "Serbija",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saūda Arābija",
	"countriesL10nMessages.SB": "Zālamana Salas",
	"countriesL10nMessages.SC": "Seišelu Salas",
	"countriesL10nMessages.SE": "Zviedrija",
	"countriesL10nMessages.SG": "Singapūra",
	"countriesL10nMessages.SH": "Sv.Helēnas sala",
	"countriesL10nMessages.SI": "Slovēnija",
	"countriesL10nMessages.SJ": "Svalbāras arhipelāgs un Jana Majena sala",
	"countriesL10nMessages.SK": "Slovākija",
	"countriesL10nMessages.SL": "Sjerraleone",
	"countriesL10nMessages.SM": "Sanmarīno",
	"countriesL10nMessages.SN": "Senegāla",
	"countriesL10nMessages.SO": "Somālija",
	"countriesL10nMessages.SR": "Surinama",
	"countriesL10nMessages.SS": "Dienvidsudāna",
	"countriesL10nMessages.ST": "Santomē un Prinsipi",
	"countriesL10nMessages.SV": "Salvadora",
	"countriesL10nMessages.SX": "Sintmārtena",
	"countriesL10nMessages.SZ": "Svatini",
	"countriesL10nMessages.TA": "Tristana da Kuņjas salas",
	"countriesL10nMessages.TC": "Tērksas un Kaikosas salas",
	"countriesL10nMessages.TD": "Čada",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Taizeme",
	"countriesL10nMessages.TJ": "Tadžikistāna",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Austrumtimora",
	"countriesL10nMessages.TM": "Turkmenistāna",
	"countriesL10nMessages.TN": "Tunisija",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turcija",
	"countriesL10nMessages.TT": "Trinidada un Tobāgo",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taivāna",
	"countriesL10nMessages.TZ": "Tanzānija",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Amerikas Savienotās Valstis",
	"countriesL10nMessages.UY": "Urugvaja",
	"countriesL10nMessages.UZ": "Uzbekistāna",
	"countriesL10nMessages.VA": "Vatikāns (Svētais Krēsls)",
	"countriesL10nMessages.VC": "Sentvinsenta un Grenadīnas",
	"countriesL10nMessages.VE": "Venecuēla",
	"countriesL10nMessages.VG": "Britu Virdžīnas",
	"countriesL10nMessages.VI": "ASV Virdžīnas",
	"countriesL10nMessages.VN": "Vjetnama",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Volisa un Futunas salas",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosova",
	"countriesL10nMessages.YE": "Jemena",
	"countriesL10nMessages.YT": "Majota",
	"countriesL10nMessages.ZA": "Dienvidāfrikas Republika",
	"countriesL10nMessages.ZM": "Zambija",
	"countriesL10nMessages.ZW": "Zimbabve",
	"ToggleableEmailField.checkboxLabel": "Nosūtīt man manu atbilžu kopiju pa e-pastu",
	"ToggleableEmailField.emailLabel": "Ievadiet savu e-pasta adresi"
};

var mk = {
	"AddressField.selectCountry": "Изберете земја",
	"PhoneField.countryCode": "Код на земја",
	"countriesL10nMessages.AC": "Остров Асенсион",
	"countriesL10nMessages.AD": "Андора",
	"countriesL10nMessages.AE": "Обединети Арапски Емирати",
	"countriesL10nMessages.AF": "Авганистан",
	"countriesL10nMessages.AG": "Антига и Барбуда",
	"countriesL10nMessages.AI": "Ангвила",
	"countriesL10nMessages.AL": "Албанија",
	"countriesL10nMessages.AM": "Ерменија",
	"countriesL10nMessages.AO": "Ангола",
	"countriesL10nMessages.AR": "Аргентина",
	"countriesL10nMessages.AS": "Американска Самоа",
	"countriesL10nMessages.AT": "Австрија",
	"countriesL10nMessages.AU": "Австралија",
	"countriesL10nMessages.AW": "Аруба",
	"countriesL10nMessages.AX": "Оландски Острови",
	"countriesL10nMessages.AZ": "Азербејџан",
	"countriesL10nMessages.BA": "Босна и Херцеговина",
	"countriesL10nMessages.BB": "Барбадос",
	"countriesL10nMessages.BD": "Бангладеш",
	"countriesL10nMessages.BE": "Белгија",
	"countriesL10nMessages.BF": "Буркина Фасо",
	"countriesL10nMessages.BG": "Бугарија",
	"countriesL10nMessages.BH": "Бахреин",
	"countriesL10nMessages.BI": "Бурунди",
	"countriesL10nMessages.BJ": "Бенин",
	"countriesL10nMessages.BL": "Свети Вартоломеј",
	"countriesL10nMessages.BM": "Бермуди",
	"countriesL10nMessages.BN": "Брунеј Дарусалам",
	"countriesL10nMessages.BO": "Боливија",
	"countriesL10nMessages.BQ": "Бонер, Св. Евстахиј и Саба",
	"countriesL10nMessages.BR": "Бразил",
	"countriesL10nMessages.BS": "Бахами",
	"countriesL10nMessages.BT": "Бутан",
	"countriesL10nMessages.BW": "Боцвана",
	"countriesL10nMessages.BZ": "Белизе",
	"countriesL10nMessages.CA": "Канада",
	"countriesL10nMessages.CC": "Кокосови (Килиншки) Острови",
	"countriesL10nMessages.CD": "Демократска Република Конго",
	"countriesL10nMessages.CF": "Централноафриканска Република",
	"countriesL10nMessages.CG": "Конго",
	"countriesL10nMessages.CH": "Швајцарија",
	"countriesL10nMessages.CI": "Брегот на Слоновата Коска",
	"countriesL10nMessages.CK": "Кукови Острови",
	"countriesL10nMessages.CL": "Чиле",
	"countriesL10nMessages.CM": "Камерун",
	"countriesL10nMessages.CN": "Кина",
	"countriesL10nMessages.CO": "Колумбија",
	"countriesL10nMessages.CR": "Костарика",
	"countriesL10nMessages.CV": "Кабо Верде",
	"countriesL10nMessages.CW": "Курасао",
	"countriesL10nMessages.CX": "Божиќен Остров",
	"countriesL10nMessages.CY": "Кипар",
	"countriesL10nMessages.CZ": "Чешка Република",
	"countriesL10nMessages.DE": "Германија",
	"countriesL10nMessages.DJ": "Џибути",
	"countriesL10nMessages.DK": "Данска",
	"countriesL10nMessages.DM": "Доминика",
	"countriesL10nMessages.DO": "Доминиканска Република",
	"countriesL10nMessages.DZ": "Алжир",
	"countriesL10nMessages.EC": "Еквадор",
	"countriesL10nMessages.EE": "Естонија",
	"countriesL10nMessages.EG": "Египет",
	"countriesL10nMessages.EH": "Западна Сахара",
	"countriesL10nMessages.ER": "Еритреја",
	"countriesL10nMessages.ES": "Шпанија",
	"countriesL10nMessages.ET": "Етиопија",
	"countriesL10nMessages.FI": "Финска",
	"countriesL10nMessages.FJ": "Фиџи",
	"countriesL10nMessages.FK": "Фолкландски Острови",
	"countriesL10nMessages.FM": "Сојузни Држави на Микронезија",
	"countriesL10nMessages.FO": "Фарски Острови",
	"countriesL10nMessages.FR": "Франција",
	"countriesL10nMessages.GA": "Габон",
	"countriesL10nMessages.GB": "Обединето Кралство",
	"countriesL10nMessages.GD": "Гренада",
	"countriesL10nMessages.GE": "Грузија",
	"countriesL10nMessages.GF": "Француска Гвајана",
	"countriesL10nMessages.GG": "Гернзи",
	"countriesL10nMessages.GH": "Гана",
	"countriesL10nMessages.GI": "Гибралтар",
	"countriesL10nMessages.GL": "Гренланд",
	"countriesL10nMessages.GM": "Гамбија",
	"countriesL10nMessages.GN": "Гвинеја",
	"countriesL10nMessages.GP": "Гвадалупе",
	"countriesL10nMessages.GQ": "Екваторска Гвинеја",
	"countriesL10nMessages.GR": "Грција",
	"countriesL10nMessages.GT": "Гватемала",
	"countriesL10nMessages.GU": "Гвам",
	"countriesL10nMessages.GW": "Гвинеја Бисао",
	"countriesL10nMessages.GY": "Гвајана",
	"countriesL10nMessages.HK": "Хонгконг",
	"countriesL10nMessages.HN": "Хондурас",
	"countriesL10nMessages.HR": "Хрватска",
	"countriesL10nMessages.HT": "Хаити",
	"countriesL10nMessages.HU": "Унгарија",
	"countriesL10nMessages.ID": "Индонезија",
	"countriesL10nMessages.IE": "Ирска",
	"countriesL10nMessages.IL": "Израел",
	"countriesL10nMessages.IM": "Остров Ман",
	"countriesL10nMessages.IN": "Индија",
	"countriesL10nMessages.IO": "Британска Индоокеанска Територија",
	"countriesL10nMessages.IQ": "Ирак",
	"countriesL10nMessages.IS": "Исланд",
	"countriesL10nMessages.IT": "Италија",
	"countriesL10nMessages.JE": "Џерси",
	"countriesL10nMessages.JM": "Јамајка",
	"countriesL10nMessages.JO": "Јордан",
	"countriesL10nMessages.JP": "Јапонија",
	"countriesL10nMessages.KE": "Кенија",
	"countriesL10nMessages.KG": "Киргистан",
	"countriesL10nMessages.KH": "Камбоџа",
	"countriesL10nMessages.KI": "Кирибати",
	"countriesL10nMessages.KM": "Коморски Острови",
	"countriesL10nMessages.KN": "Свети Китс и Невис",
	"countriesL10nMessages.KR": "Јужна Кореја",
	"countriesL10nMessages.KW": "Кувајт",
	"countriesL10nMessages.KY": "Кајмански Острови",
	"countriesL10nMessages.KZ": "Казахстан",
	"countriesL10nMessages.LA": "Лаос",
	"countriesL10nMessages.LB": "Либан",
	"countriesL10nMessages.LC": "Сент Лусија",
	"countriesL10nMessages.LI": "Лихтенштајн",
	"countriesL10nMessages.LK": "Шри Ланка",
	"countriesL10nMessages.LR": "Либерија",
	"countriesL10nMessages.LS": "Лесото",
	"countriesL10nMessages.LT": "Литванија",
	"countriesL10nMessages.LU": "Луксембург",
	"countriesL10nMessages.LV": "Латвија",
	"countriesL10nMessages.LY": "Либија",
	"countriesL10nMessages.MA": "Мароко",
	"countriesL10nMessages.MC": "Монако",
	"countriesL10nMessages.MD": "Молдавија",
	"countriesL10nMessages.ME": "Црна Гора",
	"countriesL10nMessages.MF": "Сент Мартин (француски дел)",
	"countriesL10nMessages.MG": "Мадагаскар",
	"countriesL10nMessages.MH": "Маршалски Острови",
	"countriesL10nMessages.MK": "Македонија",
	"countriesL10nMessages.ML": "Мали",
	"countriesL10nMessages.MM": "Мјанмар",
	"countriesL10nMessages.MN": "Монголија",
	"countriesL10nMessages.MO": "Макао",
	"countriesL10nMessages.MP": "Северни Маријански Острови",
	"countriesL10nMessages.MQ": "Мартиник",
	"countriesL10nMessages.MR": "Мавританија",
	"countriesL10nMessages.MS": "Монсерат",
	"countriesL10nMessages.MT": "Малта",
	"countriesL10nMessages.MU": "Маврициус",
	"countriesL10nMessages.MV": "Малдиви",
	"countriesL10nMessages.MW": "Малави",
	"countriesL10nMessages.MX": "Мексико",
	"countriesL10nMessages.MY": "Малезија",
	"countriesL10nMessages.MZ": "Мозамбик",
	"countriesL10nMessages.NA": "Намибија",
	"countriesL10nMessages.NC": "Нова Каледонија",
	"countriesL10nMessages.NE": "Нигер",
	"countriesL10nMessages.NF": "Норфолшки Остров",
	"countriesL10nMessages.NG": "Нигерија",
	"countriesL10nMessages.NI": "Никарагва",
	"countriesL10nMessages.NL": "Холандија",
	"countriesL10nMessages.NO": "Норвешка",
	"countriesL10nMessages.NP": "Непал",
	"countriesL10nMessages.NR": "Науру",
	"countriesL10nMessages.NU": "Ниуе",
	"countriesL10nMessages.NZ": "Нов Зеланд",
	"countriesL10nMessages.OM": "Оман",
	"countriesL10nMessages.PA": "Панама",
	"countriesL10nMessages.PE": "Перу",
	"countriesL10nMessages.PF": "Француска Полинезија",
	"countriesL10nMessages.PG": "Папуа Нова Гвинеја",
	"countriesL10nMessages.PH": "Филипини",
	"countriesL10nMessages.PK": "Пакистан",
	"countriesL10nMessages.PL": "Полска",
	"countriesL10nMessages.PM": "Сент Пјер и Микелан",
	"countriesL10nMessages.PR": "Порторико",
	"countriesL10nMessages.PS": "Палестина",
	"countriesL10nMessages.PT": "Португалија",
	"countriesL10nMessages.PW": "Палау",
	"countriesL10nMessages.PY": "Парагвај",
	"countriesL10nMessages.QA": "Катар",
	"countriesL10nMessages.RE": "Рејунион",
	"countriesL10nMessages.RO": "Романија",
	"countriesL10nMessages.RS": "Србија",
	"countriesL10nMessages.RW": "Руанда",
	"countriesL10nMessages.SA": "Саудиска Арабија",
	"countriesL10nMessages.SB": "Соломонски Острови",
	"countriesL10nMessages.SC": "Сејшели",
	"countriesL10nMessages.SE": "Шведска",
	"countriesL10nMessages.SG": "Сингапур",
	"countriesL10nMessages.SH": "Света Елена",
	"countriesL10nMessages.SI": "Словенија",
	"countriesL10nMessages.SJ": "Свалбард и Јан Мајен",
	"countriesL10nMessages.SK": "Словачка",
	"countriesL10nMessages.SL": "Сиера Леоне",
	"countriesL10nMessages.SM": "Сан Марино",
	"countriesL10nMessages.SN": "Сенегал",
	"countriesL10nMessages.SO": "Сомалија",
	"countriesL10nMessages.SR": "Суринам",
	"countriesL10nMessages.SS": "Јужен Судан",
	"countriesL10nMessages.ST": "Саун Томе и Принсип",
	"countriesL10nMessages.SV": "Ел Салвадор",
	"countriesL10nMessages.SX": "Свети Мартин",
	"countriesL10nMessages.SZ": "Свазиленд",
	"countriesL10nMessages.TA": "Тристан да Куња",
	"countriesL10nMessages.TC": "Острови Туркс и Каикос",
	"countriesL10nMessages.TD": "Чад",
	"countriesL10nMessages.TG": "Того",
	"countriesL10nMessages.TH": "Тајланд",
	"countriesL10nMessages.TJ": "Таџикистан",
	"countriesL10nMessages.TK": "Токелау",
	"countriesL10nMessages.TL": "Источен Тимор",
	"countriesL10nMessages.TM": "Туркменистан",
	"countriesL10nMessages.TN": "Тунис",
	"countriesL10nMessages.TO": "Тонга",
	"countriesL10nMessages.TR": "Турција",
	"countriesL10nMessages.TT": "Тринидад и Тобаго",
	"countriesL10nMessages.TV": "Тувалу",
	"countriesL10nMessages.TW": "Тајван",
	"countriesL10nMessages.TZ": "Танзанија",
	"countriesL10nMessages.UA": "Украина",
	"countriesL10nMessages.UG": "Уганда",
	"countriesL10nMessages.US": "Соединети Американски Држави",
	"countriesL10nMessages.UY": "Уругвај",
	"countriesL10nMessages.UZ": "Узбекистан",
	"countriesL10nMessages.VA": "Светиот Престол (Ватикан)",
	"countriesL10nMessages.VC": "Сент Винсент и Гренадини",
	"countriesL10nMessages.VE": "Венецуела",
	"countriesL10nMessages.VG": "Британски Девствени Острови",
	"countriesL10nMessages.VI": "Американски Девствени Острови",
	"countriesL10nMessages.VN": "Виетнам",
	"countriesL10nMessages.VU": "Вануату",
	"countriesL10nMessages.WF": "Валис и Футуна",
	"countriesL10nMessages.WS": "Самоа",
	"countriesL10nMessages.XK": "Косово",
	"countriesL10nMessages.YE": "Јемен",
	"countriesL10nMessages.YT": "Мајот",
	"countriesL10nMessages.ZA": "Јужноафриканска Република",
	"countriesL10nMessages.ZM": "Замбија",
	"countriesL10nMessages.ZW": "Зимбабве",
	"ToggleableEmailField.checkboxLabel": "Испрати ми копија од моите одговори преку е-пошта",
	"ToggleableEmailField.emailLabel": "Внесете ја вашата адреса на е-пошта"
};

var ml = {
	"AddressField.selectCountry": "രാജ്യം തിരഞ്ഞെടുക്കുക",
	"PhoneField.countryCode": "രാജ്യ കോഡ്",
	"countriesL10nMessages.AC": "അസെൻഷൻ ഐലൻ്റ്",
	"countriesL10nMessages.AD": "അണ്ടോറ",
	"countriesL10nMessages.AE": "യുണൈറ്റഡ് അറബ് എമിറേറ്റ്സ്",
	"countriesL10nMessages.AF": "അഫ്ഗാനിസ്ഥാൻ",
	"countriesL10nMessages.AG": "ആൻ്റിഗ്വ ആൻ്റ് ബാർബുഡ",
	"countriesL10nMessages.AI": "ആംഗില",
	"countriesL10nMessages.AL": "അൽബേനിയ",
	"countriesL10nMessages.AM": "അർമേനിയ",
	"countriesL10nMessages.AO": "അങ്കോള",
	"countriesL10nMessages.AR": "അർജൻ്റീന",
	"countriesL10nMessages.AS": "അമേരിക്കൻ സമോവ",
	"countriesL10nMessages.AT": "ഓസ്ട്രിയ",
	"countriesL10nMessages.AU": "ഓസ്ട്രേലിയ",
	"countriesL10nMessages.AW": "അറൂബ",
	"countriesL10nMessages.AX": "ഓലാൻഡ് ദ്വീപുകൾ",
	"countriesL10nMessages.AZ": "അസർബൈജാൻ",
	"countriesL10nMessages.BA": "ബോസ്നിയ ആൻ്റ് ഹെർസഗോവിന",
	"countriesL10nMessages.BB": "ബാർബഡോസ്",
	"countriesL10nMessages.BD": "ബംഗ്ലാദേശ്",
	"countriesL10nMessages.BE": "ബെൽജിയം",
	"countriesL10nMessages.BF": "ബുർക്കിന ഫാസോ",
	"countriesL10nMessages.BG": "ബൾഗേറിയ",
	"countriesL10nMessages.BH": "ബഹറൈൻ",
	"countriesL10nMessages.BI": "ബുറുണ്ടി",
	"countriesL10nMessages.BJ": "ബെനിൻ",
	"countriesL10nMessages.BL": "സെൻ്റ് ബത്തലമി",
	"countriesL10nMessages.BM": "ബെർമുഡ",
	"countriesL10nMessages.BN": "ബ്രൂണൈ ദാറുസ്സലം",
	"countriesL10nMessages.BO": "ബൊളീവിയ",
	"countriesL10nMessages.BQ": "ബോണെയർ, സെൻ്റ് യൂറ്റേഷ്യസ് ആൻ്റ് സാബ",
	"countriesL10nMessages.BR": "ബ്രസീൽ",
	"countriesL10nMessages.BS": "ബഹാമാസ്",
	"countriesL10nMessages.BT": "ഭൂട്ടാൻ",
	"countriesL10nMessages.BW": "ബോട്സ്വാന",
	"countriesL10nMessages.BZ": "ബെലീസ്",
	"countriesL10nMessages.CA": "കാനഡ",
	"countriesL10nMessages.CC": "കോകോസ് (കീലിംഗ്) ദ്വീപുകൾ",
	"countriesL10nMessages.CD": "ദി ഡെമോക്രാറ്റിക് റിപബ്ലിക് ഓഫ് കോംഗോ",
	"countriesL10nMessages.CF": "സെൻട്രൽ ആഫ്രിക്കൻ റിപബ്ലിക്",
	"countriesL10nMessages.CG": "കോംഗോ",
	"countriesL10nMessages.CH": "സ്വിറ്റ്സർലാൻഡ്",
	"countriesL10nMessages.CI": "ഐവറി കോസ്റ്റ്",
	"countriesL10nMessages.CK": "കുക്ക് ദ്വീപുകൾ",
	"countriesL10nMessages.CL": "ചിലി",
	"countriesL10nMessages.CM": "കാമറൂൺ",
	"countriesL10nMessages.CN": "ചൈന",
	"countriesL10nMessages.CO": "കൊളംബിയ",
	"countriesL10nMessages.CR": "കോസ്റ്ററിക്ക",
	"countriesL10nMessages.CV": "കേപ്പ് വെർഡെ",
	"countriesL10nMessages.CW": "കുറകാവോ",
	"countriesL10nMessages.CX": "ക്രിസ്മസ് ദ്വീപ്",
	"countriesL10nMessages.CY": "സൈപ്രസ്",
	"countriesL10nMessages.CZ": "ചെക്ക് റിപബ്ലിക്",
	"countriesL10nMessages.DE": "ജർമ്മനി",
	"countriesL10nMessages.DJ": "ജിബൂട്ടി",
	"countriesL10nMessages.DK": "ഡെൻമാർക്ക്",
	"countriesL10nMessages.DM": "ഡൊമിനിക്ക",
	"countriesL10nMessages.DO": "ഡൊമിനിക്കൻ റിപബ്ലിക്",
	"countriesL10nMessages.DZ": "അൾജീരിയ",
	"countriesL10nMessages.EC": "ഇക്വഡോർ",
	"countriesL10nMessages.EE": "എസ്റ്റോണിയ",
	"countriesL10nMessages.EG": "ഈജിപ്റ്റ്",
	"countriesL10nMessages.EH": "പശ്ചിമ സഹാറ",
	"countriesL10nMessages.ER": "എറിത്രിയ",
	"countriesL10nMessages.ES": "സ്പെയിൻ",
	"countriesL10nMessages.ET": "എത്യോപ്യ",
	"countriesL10nMessages.FI": "ഫിൻലാൻഡ്",
	"countriesL10nMessages.FJ": "ഫിജി",
	"countriesL10nMessages.FK": "ഫോക്ക്‌ലാൻഡ് ദ്വീപുകൾ",
	"countriesL10nMessages.FM": "ഫെഡറേഷൻ സ്റ്റേറ്റ്സ് ഓഫ് മൈക്രോനേഷ്യ",
	"countriesL10nMessages.FO": "ഫറോ ദ്വീപുകൾ",
	"countriesL10nMessages.FR": "ഫ്രാൻസ്",
	"countriesL10nMessages.GA": "ഗാബോൺ",
	"countriesL10nMessages.GB": "യുണൈറ്റഡ് കിംഗ്ഡം",
	"countriesL10nMessages.GD": "ഗ്രെനാഡ",
	"countriesL10nMessages.GE": "ജോർജിയ",
	"countriesL10nMessages.GF": "ഫ്രഞ്ച് ഗയാന",
	"countriesL10nMessages.GG": "ഗേൺസി",
	"countriesL10nMessages.GH": "ഘാന",
	"countriesL10nMessages.GI": "ജിബ്രാൾട്ടർ",
	"countriesL10nMessages.GL": "ഗ്രീൻലാൻഡ്",
	"countriesL10nMessages.GM": "ഗാംബിയ",
	"countriesL10nMessages.GN": "ഗിനിയ",
	"countriesL10nMessages.GP": "ഗ്വാഡലൂപ്പ്",
	"countriesL10nMessages.GQ": "ഇക്വറ്റോറിയൽ ഗിനിയ",
	"countriesL10nMessages.GR": "ഗ്രീസ്",
	"countriesL10nMessages.GT": "ഗ്വാട്ടിമല",
	"countriesL10nMessages.GU": "ഗുവാം",
	"countriesL10nMessages.GW": "ഗിനിയ-ബിസാവു",
	"countriesL10nMessages.GY": "ഗയാന",
	"countriesL10nMessages.HK": "ഹോങ്കോംഗ്",
	"countriesL10nMessages.HN": "ഹോണ്ടുറാസ്",
	"countriesL10nMessages.HR": "ക്രൊയേഷ്യ",
	"countriesL10nMessages.HT": "ഹെയ്തി",
	"countriesL10nMessages.HU": "ഹംഗറി",
	"countriesL10nMessages.ID": "ഇന്തോനേഷ്യ",
	"countriesL10nMessages.IE": "അയർലാൻഡ്",
	"countriesL10nMessages.IL": "ഇസ്രയേൽ",
	"countriesL10nMessages.IM": "ഐൽ ഓഫ് മാൻ",
	"countriesL10nMessages.IN": "ഇന്ത്യ",
	"countriesL10nMessages.IO": "ബ്രിട്ടീഷ് ഇന്ത്യൻ ഓഷ്യൻ പ്രദേശം",
	"countriesL10nMessages.IQ": "ഇറാഖ്",
	"countriesL10nMessages.IS": "ഐസ്‌ലാൻഡ്",
	"countriesL10nMessages.IT": "ഇറ്റലി",
	"countriesL10nMessages.JE": "ജേഴ്സി",
	"countriesL10nMessages.JM": "ജമൈക്ക",
	"countriesL10nMessages.JO": "ജോർദ്ദാൻ",
	"countriesL10nMessages.JP": "ജപ്പാൻ",
	"countriesL10nMessages.KE": "കെനിയ",
	"countriesL10nMessages.KG": "കിർഗിസ്ഥാൻ",
	"countriesL10nMessages.KH": "കംബോഡിയ",
	"countriesL10nMessages.KI": "കിരിബാസ്",
	"countriesL10nMessages.KM": "കൊമോറോസ്",
	"countriesL10nMessages.KN": "സെൻ്റ് കിറ്റ്സ് ആൻ്റ് നെവിസ്",
	"countriesL10nMessages.KR": "ദക്ഷിണ കൊറിയ",
	"countriesL10nMessages.KW": "കുവൈറ്റ്",
	"countriesL10nMessages.KY": "കെയ്മൻ ദ്വീപുകൾ",
	"countriesL10nMessages.KZ": "കസാക്കിസ്ഥാൻ",
	"countriesL10nMessages.LA": "ലാവോസ്",
	"countriesL10nMessages.LB": "ലെബനോൻ",
	"countriesL10nMessages.LC": "സെൻ്റ് ലൂസിയ",
	"countriesL10nMessages.LI": "ലെയ്ക്റ്റെൻസ്റ്റെയിൻ",
	"countriesL10nMessages.LK": "ശ്രീലങ്ക",
	"countriesL10nMessages.LR": "ലൈബീരിയ",
	"countriesL10nMessages.LS": "ലെസോത്തോ",
	"countriesL10nMessages.LT": "ലിത്വേനിയ",
	"countriesL10nMessages.LU": "ലക്സംബർഗ്",
	"countriesL10nMessages.LV": "ലാത്വിയ",
	"countriesL10nMessages.LY": "ലിബിയ",
	"countriesL10nMessages.MA": "മൊറോക്കോ",
	"countriesL10nMessages.MC": "മൊനാക്കോ",
	"countriesL10nMessages.MD": "മോൾഡോവ",
	"countriesL10nMessages.ME": "മോണ്ടിനെഗ്രോ",
	"countriesL10nMessages.MF": "സെൻ്റ് മാർട്ടിൻ (ഫ്രഞ്ച് ഭാഗം)",
	"countriesL10nMessages.MG": "മഡഗാസ്ക്കർ",
	"countriesL10nMessages.MH": "മാർഷൽ ദ്വീപുകൾ",
	"countriesL10nMessages.MK": "മാസിഡോണിയ",
	"countriesL10nMessages.ML": "മാലി",
	"countriesL10nMessages.MM": "മ്യാൻമാർ",
	"countriesL10nMessages.MN": "മംഗോളിയ",
	"countriesL10nMessages.MO": "മക്കാവു",
	"countriesL10nMessages.MP": "വടക്കൻ മരിയാനാ ദ്വീപ്",
	"countriesL10nMessages.MQ": "മാർട്ടിനീക്",
	"countriesL10nMessages.MR": "മൗറിറ്റാനിയ",
	"countriesL10nMessages.MS": "മോൺസെറാട്ട്",
	"countriesL10nMessages.MT": "മാൾട്ട",
	"countriesL10nMessages.MU": "മൗറീഷ്യസ്",
	"countriesL10nMessages.MV": "മാലിദ്വീപ്",
	"countriesL10nMessages.MW": "മലാവി",
	"countriesL10nMessages.MX": "മെക്സിക്കോ",
	"countriesL10nMessages.MY": "മലേഷ്യ",
	"countriesL10nMessages.MZ": "മൊസാംബിക്",
	"countriesL10nMessages.NA": "നമബിയ",
	"countriesL10nMessages.NC": "ന്യൂ കാലിഡോണിയ",
	"countriesL10nMessages.NE": "നൈജർ",
	"countriesL10nMessages.NF": "നോർഫോർക്ക് ദ്വീപ്",
	"countriesL10nMessages.NG": "നൈജീരിയ",
	"countriesL10nMessages.NI": "നിക്കാരഗ്വ",
	"countriesL10nMessages.NL": "നെതർലാൻഡ്സ്",
	"countriesL10nMessages.NO": "നോർവേ",
	"countriesL10nMessages.NP": "നേപ്പാൾ",
	"countriesL10nMessages.NR": "നാവൂറു",
	"countriesL10nMessages.NU": "നിവൂ",
	"countriesL10nMessages.NZ": "ന്യൂസിലാൻഡ്",
	"countriesL10nMessages.OM": "ഒമാൻ",
	"countriesL10nMessages.PA": "പനാമ",
	"countriesL10nMessages.PE": "പെറു",
	"countriesL10nMessages.PF": "ഫ്രഞ്ച് പോളിനേഷ്യ",
	"countriesL10nMessages.PG": "പാപ്പുവ ന്യൂഗിനിയ",
	"countriesL10nMessages.PH": "ഫിലിപ്പൈൻസ്",
	"countriesL10nMessages.PK": "പാക്കിസ്ഥാൻ",
	"countriesL10nMessages.PL": "പോളണ്ട്",
	"countriesL10nMessages.PM": "സെൻ്റ് പിയർ ആൻ്റ് മിക്‌ലോൺ",
	"countriesL10nMessages.PR": "പോർട്ടോ റിക്കോ",
	"countriesL10nMessages.PS": "പാലസ്റ്റീൻ",
	"countriesL10nMessages.PT": "പോർച്ചുഗൽ",
	"countriesL10nMessages.PW": "പലാവു",
	"countriesL10nMessages.PY": "പരാഗ്വേ",
	"countriesL10nMessages.QA": "ഖത്തർ",
	"countriesL10nMessages.RE": "റീയൂണിയൻ",
	"countriesL10nMessages.RO": "റൊമാനിയ",
	"countriesL10nMessages.RS": "സെർബിയ",
	"countriesL10nMessages.RW": "റുവാണ്ട",
	"countriesL10nMessages.SA": "സൗദി അറേബ്യ",
	"countriesL10nMessages.SB": "സോളമൻ ദ്വീപുകൾ",
	"countriesL10nMessages.SC": "സീഷെൽസ്",
	"countriesL10nMessages.SE": "സ്വീഡൻ",
	"countriesL10nMessages.SG": "സിംഗപ്പൂർ",
	"countriesL10nMessages.SH": "സെൻ്റ് ഹെലെന",
	"countriesL10nMessages.SI": "സ്ലോവേനിയ",
	"countriesL10nMessages.SJ": "സ്വാൾബാർഡ് ആൻ്റ് ജാൻ മയൻ",
	"countriesL10nMessages.SK": "സ്ലോവാക്യ",
	"countriesL10nMessages.SL": "സിയറ ലിയോൺ",
	"countriesL10nMessages.SM": "സാൻ മറീനോ",
	"countriesL10nMessages.SN": "സെനഗൽ",
	"countriesL10nMessages.SO": "സൊമാലിയ",
	"countriesL10nMessages.SR": "സുരിനാം",
	"countriesL10nMessages.SS": "ദക്ഷിണ സുഡാൻ",
	"countriesL10nMessages.ST": "സാവോ ടോം ആൻ്റ് പ്രിൻസിപ്പ്",
	"countriesL10nMessages.SV": "എൽ സാൽവഡോർ",
	"countriesL10nMessages.SX": "സെൻ്റ് മാർട്ടൻ",
	"countriesL10nMessages.SZ": "സ്വാസിലാൻഡ്",
	"countriesL10nMessages.TA": "ട്രിസ്റ്റൻ ഡാ കൻഹ",
	"countriesL10nMessages.TC": "ടർക്ക്സ് ആൻ്റ് കൈക്കോസ് ദ്വീപുകൾ",
	"countriesL10nMessages.TD": "ചാഡ്",
	"countriesL10nMessages.TG": "ടാഗോ",
	"countriesL10nMessages.TH": "തായ്‌ലൻഡ്",
	"countriesL10nMessages.TJ": "താജിക്കിസ്ഥാൻ",
	"countriesL10nMessages.TK": "ടോക്കലാവു",
	"countriesL10nMessages.TL": "തിമോർ-ലെസ്തെ",
	"countriesL10nMessages.TM": "തുർക്ക്‌മെനിസ്ഥാൻ",
	"countriesL10nMessages.TN": "ടുണീഷ്യ",
	"countriesL10nMessages.TO": "ടോംഗ",
	"countriesL10nMessages.TR": "തുർക്കി",
	"countriesL10nMessages.TT": "ട്രിനിഡാഡ് ആൻ്റ് ടൊബാഗോ",
	"countriesL10nMessages.TV": "തുവാലു",
	"countriesL10nMessages.TW": "തായ്‌വാൻ",
	"countriesL10nMessages.TZ": "ടാൻസാനിയ",
	"countriesL10nMessages.UA": "ഉക്രെയ്ൻ",
	"countriesL10nMessages.UG": "ഉഗാണ്ട",
	"countriesL10nMessages.US": "യൂണൈറ്റഡ് സ്റ്റേറ്റ്സ്",
	"countriesL10nMessages.UY": "ഉറുഗ്വായ്",
	"countriesL10nMessages.UZ": "ഉസ്ബെക്കിസ്ഥാൻ",
	"countriesL10nMessages.VA": "ഹോളി സീ (വത്തിക്കാൻ സിറ്റി സ്റ്റേറ്റ്)",
	"countriesL10nMessages.VC": "സെൻ്റ് വിൻസൻ്റ് ആൻ്റ് ദി ഗ്രെനാഡൈൻസ്",
	"countriesL10nMessages.VE": "വെനിസ്വേല",
	"countriesL10nMessages.VG": "ബ്രിട്ടീഷ് വിർജിൻ ദ്വീപുകൾ",
	"countriesL10nMessages.VI": "യു.എസ് വിർജിൻ ദ്വീപുകൾ",
	"countriesL10nMessages.VN": "വിയറ്റ്നാം",
	"countriesL10nMessages.VU": "വനുവാതു",
	"countriesL10nMessages.WF": "വാലിസ് ആൻ്റ് ഫുടൂണ",
	"countriesL10nMessages.WS": "സമോവ",
	"countriesL10nMessages.XK": "കൊസൊവോ",
	"countriesL10nMessages.YE": "യെമെൻ",
	"countriesL10nMessages.YT": "മയോട്ടെ",
	"countriesL10nMessages.ZA": "ദക്ഷിണാഫ്രിക്ക",
	"countriesL10nMessages.ZM": "സാംബിയ",
	"countriesL10nMessages.ZW": "സിംബാബ്‌വെ",
	"ToggleableEmailField.checkboxLabel": "പ്രതികരണങ്ങളുടെ പകർപ്പ് എനിക്ക് ഇമെയിൽ വഴി അയയ്ക്കുക",
	"ToggleableEmailField.emailLabel": "നിങ്ങളുടെ ഇമെയിൽ വിലാസം നൽകുക"
};

var ms = {
	"AddressField.selectCountry": "Pilih negara",
	"PhoneField.countryCode": "Kod Negara",
	"countriesL10nMessages.AC": "Pulau Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emiriah Arab Bersatu",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua dan Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Amerika",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Kepulauan Åland",
	"countriesL10nMessages.AZ": "Azerbaijan",
	"countriesL10nMessages.BA": "Bosnia dan Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgium",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius dan Saba",
	"countriesL10nMessages.BR": "Brazil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kepulauan Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, Republik Demokratik",
	"countriesL10nMessages.CF": "Republik Afrika Tengah",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Switzerland",
	"countriesL10nMessages.CI": "Côte d'Ivoire",
	"countriesL10nMessages.CK": "Kepulauan Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Cameroon",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa rica",
	"countriesL10nMessages.CV": "Cape Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Pulau Krismas",
	"countriesL10nMessages.CY": "Cyprus",
	"countriesL10nMessages.CZ": "Republik Czech",
	"countriesL10nMessages.DE": "Jerman",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Denmark",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Republik Dominican",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Mesir",
	"countriesL10nMessages.EH": "Sahara Barat",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Sepanyol",
	"countriesL10nMessages.ET": "Habsyah",
	"countriesL10nMessages.FI": "Finland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Kepulauan Falkland",
	"countriesL10nMessages.FM": "Persekutuan Negeri-negeri Micronesia",
	"countriesL10nMessages.FO": "Kepulauan Faroe",
	"countriesL10nMessages.FR": "Perancis",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "United Kingdom",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guiana Perancis",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Greenland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Guinea Khatulistiwa",
	"countriesL10nMessages.GR": "Greece",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungary",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Ireland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Isle of Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Wilayah Lautan Hindi British",
	"countriesL10nMessages.IQ": "Iraq",
	"countriesL10nMessages.IS": "Iceland",
	"countriesL10nMessages.IT": "Itali",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Jepun",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kyrgyzstan",
	"countriesL10nMessages.KH": "Kemboja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoros",
	"countriesL10nMessages.KN": "Saint Kitts dan Nevis",
	"countriesL10nMessages.KR": "Korea Selatan",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Kepulauan Cayman",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Lubnan",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lithuania",
	"countriesL10nMessages.LU": "Luxembourg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Maghribi",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (bahagian Perancis)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Kepulauan Marshall",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Pulau Mariana Utara",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldives",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexico",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "New Caledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Pulau Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Belanda",
	"countriesL10nMessages.NO": "Norway",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "New Zealand",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polynesia Perancis",
	"countriesL10nMessages.PG": "Papua New Guinea",
	"countriesL10nMessages.PH": "Filipina",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poland",
	"countriesL10nMessages.PM": "Saint Pierre dan Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestin",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Arab Saudi",
	"countriesL10nMessages.SB": "Kepulauan Solomon",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Sweden",
	"countriesL10nMessages.SG": "Singapura",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard dan Jan Mayen",
	"countriesL10nMessages.SK": "Slovakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Sudan Selatan",
	"countriesL10nMessages.ST": "Sao Tome dan Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Kepulauan Turks dan Caicos",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tajikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turki",
	"countriesL10nMessages.TT": "Trinidad dan Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraine",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Amerika Syarikat",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Holy See (Negeri Kota Vatican)",
	"countriesL10nMessages.VC": "Saint Vincent dan Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Kepulauan Virgin, British",
	"countriesL10nMessages.VI": "Kepulauan Virgin, AS",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis dan Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yaman",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Afrika Selatan",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Hantarkan saya salinan jawapan saya melalui e-mel",
	"ToggleableEmailField.emailLabel": "Masukkan alamat e-mel anda"
};

var nl = {
	"ActionMenuTagPin.PIN": "Vastzetten",
	"ActionMenuTagPin.TAG": "Tag",
	"AddressField.selectCountry": "Selecteer land",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Beantwoord: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "V{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Overgeslagen: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Wat mensen vinden:",
	"AsyncJobLoadingState.LOADING": "Reacties laden",
	"AsyncJobLoadingState.TAGGING_CAPTION": "U krijgt een bericht wanneer dit klaar is",
	"AsyncJobLoadingState.TAGGING_TITLE": "Tags toevoegen aan reacties",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Tags zijn van toepassing op bestaande en toekomstige reacties. Voeg een spatie of <strong>OR</strong> toe tussen woorden als u meer dan één trefwoord wilt gebruiken.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Automatisch tags toevoegen aan reacties",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Een maand selecteren",
	"CalendarPicker.months": "{months, select, January {Januari} February {Februari} March {Maart} April {April} May {Mei} June {Juni} July {Juli} August {Augustus} September {September} October {Oktober} November {November} December {December} other {andere}}",
	"CalendarPicker.nextMonth": "Volgende maand",
	"CalendarPicker.previousMonth": "Vorige maand",
	"CalendarPicker.year": "Een jaar selecteren",
	"ColorSwatches.TAG": "Tag {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Meerkleurige woordwolken gebruiken kleur en formaat om aan te geven hoe vaak een woord of zinsdeel wordt vermeld.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Wat de kleuren in woordwolken betekenen",
	"ColorTab.DOWN_ARROW": "Pijl vervolgkeuzemenu selecteren",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Meerdere kleuren",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Eén kleur",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Annuleren",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Een nieuw woord maken",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Het nieuwe woord vervangt alle hieronder geselecteerde woorden.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Gecombineerde woorden bewerken",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Woorden combineren",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Maak een nieuw woord dat een of meer van de hieronder geselecteerde woorden vervangt. U kunt dit later bewerken om uw oorspronkelijke woorden te herstellen.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Typ een nieuw woord",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Combineren",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Woorden kiezen om te combineren",
	"CreateTagFromSearch.CLOSE_MENU": "Annuleren",
	"CreateTagFromSearch.DUPLICATED_NAME": "U hebt al een tag met die naam.",
	"CreateTagFromSearch.NEW_TAG": "Nieuwe tag",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Er wordt geen \n\t\treactie getagd} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} reactie \n\t\t\t\n\t\twordt getagd} other {\n\t\t\t\t{responseTaggedFormatted} reacties \n\t\t\t\n\t\tworden getagd}}}}",
	"CreateTagFromSearch.SAVE": "Opslaan",
	"CreateTagFromSearch.TAG": "Tag",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Tagnaam mag niet leeg zijn.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Tagnaam mag niet alleen uit spaties bestaan.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Voeg tags toe aan reacties die overeenkomen met deze trefwoorden. Selecteer <strong>Tag maken</strong>. Kies een kleur en sla op. De tag is van toepassing op bestaande en toekomstige reacties.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Tag die overeenkomt met reacties",
	"DashboardMenuContent.CREATE": "Maken",
	"DashboardMenuContent.NAME": "Naam",
	"DashboardMenuContent.NEW_DASHBOARD": "Nieuw dashboard",
	"DateInputWrapper.dateButton": "Agenda openen",
	"DateInputWrapper.dateFormatIntl": "DD/MM/JJJJ",
	"DateInputWrapper.dateFormatUs": "MM-DD-JJJJ",
	"DateInputWrapper.dateLabel": "Datum",
	"DateTime.errorEnterHours": "Voer een uurwaarde in tussen 0 en 23.",
	"DateTime.errorEnterMinutes": "Voer een minuutwaarde in tussen 0 en 59.",
	"DateTime.errorSelectPeriod": "Selecteer AM/PM.",
	"DateTime.errorYearOutOfRange": "Geef het jaartal op tussen 1900 en 9999.",
	"DateTime.formatErrorDateIntl": "Geef een datum op in de notatie DD/MM/JJJJ.",
	"DateTime.formatErrorDateUs": "Geef een datum op in de notatie MM/DD/JJJJ.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "Annuleren",
	"EditorButtons.COPY_BUTTON": "VRAAG KOPIËREN",
	"EditorButtons.MOVE_BUTTON": "VRAAG VERPLAATSEN",
	"EditorButtons.NEXT_QUESTION_BUTTON": "Volgende vraag",
	"EditorButtons.SAVE_BUTTON": "Opslaan",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Er gelden geen gegevenstrends voor deze vraag",
	"FallbackComponent.ERROR": "Bug bij laden van vraag",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Kies een bestand</div> <div class={dragClass}>of sleep het hierheen</div>",
	"FileUpload.fileUploaded": "Bestand is geüpload",
	"FileUpload.fileUploading": "Bestand uploaden",
	"FileUpload.removeFile": "Bestand verwijderen",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Vervang een bestand</div> <div class={dragClass}>of sleep het hierheen</div>",
	"FileUpload.supportedFiles": "Ondersteunde bestanden",
	"FilterMenuContent.APPLY_ACTIONS": "Toepassen",
	"FilterMenuContent.CLEAR_ACTIONS": "Wissen",
	"FilterMenuContent.CLOSE_MENU": "Annuleren",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Annuleren",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Verbergen",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Opslaan",
	"HideWordsModal.HIDE_WORDS_HEADER": "Woorden verbergen",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Selecteer woorden om te verbergen voor de woordwolk.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Te verbergen woorden selecteren",
	"IconHelpStyled.HELP": "Help",
	"IndividualResponseCard.NO_RESPONSE": "Respondent heeft deze vraag overgeslagen",
	"IndividualResponseCard.QUESTION_NUMBER": "V{number}",
	"InfoBannerTagManagement.CLOSE": "Sluiten",
	"InfoBannerTagManagement.NEW": "NIEUW",
	"InfoBannerTagManagement.TAG_RESPONSES": "Tag toevoegen aan reacties",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Voeg trefwoorden toe om automatische tags in te stellen en reacties geordend te houden.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Tags toevoegen aan reacties voor eenvoudig beheer",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positief: {percentage,number,percent}} negative {Negatief: {percentage,number,percent}} neutral {Neutraal: {percentage,number,percent}} unknown {Niet waargenomen: {percentage,number,percent}} other {andere}}",
	"LoadingState.LOADING": "Reacties laden",
	"MatrixDropdown.EmptyTopNativeSelect": "Een optie selecteren",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Betrouwbaarheidsinterval",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Bruikbaarheidsscore",
	"Modal.CloseModal": "Knopinfo sluiten",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Maandag} Tu {Dinsdag} We {Woensdag} Th {Donderdag} Fr {Vrijdag} Sa {Zaterdag} Su {Zondag} other {andere}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Ma} Tu {Di} We {Wo} Th {Do} Fr {Vr} Sa {Za} Su {Zo} other {andere}}",
	"MultiColorSelectorMenu.APPLY": "Toepassen",
	"MultiColorSelectorMenu.CANCEL": "Annuleren",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Orden uw gegevens in een aangepaste lay-out, licht uw bevindingen toe en deel uw dashboard. Orden uw gegevens in een aangepaste lay-out, licht uw bevindingen toe en deel uw dashboard.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Dashboards",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Meer informatie",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">Op een schaal van {minValue} tot {maxValue}</div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} voor {minLabelText}, {maxValue} voor {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "meetbereik voor scores tussen {min} en {max}",
	"NpsGaugeChart.NPS_CHART": "NPS-meetgrafiek",
	"NpsGaugeChart.NPS_SCORE": "NPS-score van {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "maatstreep voor positie van scoremarkering {score}",
	"OrderControl.AriaNA": "Niet van toepassing",
	"OrderControl.NA": "N.v.t.",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "Reacties van het type ''{title}' ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "lijst met overige reacties verbergen",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "lijst met overige reacties weergeven",
	"PhoneField.countryCode": "Landcode",
	"QuestionCardItemFooter.FILTER_APPLIED": "Toegepaste regels: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filteren",
	"QuestionCardTitle.ANSWERED": "Beantwoord: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Geselecteerd: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Overgeslagen: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} punt} other {{score}/{total} punten}}",
	"QuizResultScore.SKIPPED": "Overgeslagen",
	"RangeTab.RANGE_TAB_LABEL": "Vermeldingen: {value}",
	"RangeTab.TOTAL_WORDS": "totale aantal woorden",
	"Ranking.AriaRankAtBottom": "{label} is naar onderaan de lijst verplaatst",
	"Ranking.AriaRankAtTop": "{label} is naar bovenaan de lijst verplaatst",
	"Ranking.AriaRankMovedDown": "{label} is omlaag verplaatst naar nummer {position}",
	"Ranking.AriaRankMovedUp": "{label} is omhoog verplaatst naar nummer {position}",
	"RankingItem.AriaRankMoveDown": "{label} omlaag verplaatsen",
	"RankingItem.AriaRankMoveUp": "{label} omhoog verplaatsen",
	"RatingChart.AVERAGE_RATING": "Gemiddelde waardering",
	"RatingChartLegend.ANSWERED": "Beantwoord: {number}",
	"RatingChartLegend.RATING": "{rating} ster",
	"RatingChartLegend.SKIPPED": "Overgeslagen: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Functie aanvragen",
	"RequestAccessButton.UPGRADE": "Upgraden",
	"RespondentQuestionTitle.REQUIRED": "(Vereist.)",
	"ResponseItem.EXPAND_MENU_LABEL": "Menu uitvouwen",
	"ResponseItem.MENU_LABEL": "Tags toevoegen",
	"ResponseItem.RESPONDENT_ID": "Respondent-ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Antwoorden van respondent bekijken",
	"ResponseStatusBar.CLEAR": "Wissen",
	"ResponseStatusBar.FAILED_TO_LOAD": "Laden van reacties is mislukt",
	"ResponseStatusBar.NO_RESPONSES_YET": "Nog geen enquêtereacties",
	"ResponseStatusBar.SEARCHING": "Zoeken...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {positieve stemming} negative {negatieve stemming} neutral {neutrale stemming} other {onbekende stemming}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reactie voor {searchTextFormatted} met {filters}   } other {Weergave van {resultsCountFormatted} reactie voor {searchTextFormatted} met {filters}   }}} other {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reacties voor {searchTextFormatted} met {filters}   } other {Weergave van {resultsCountFormatted} reacties voor {searchTextFormatted} met {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reactie met {filters}   } other {Weergave van {resultsCountFormatted} reactie met {filters}   }}} other {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reacties met {filters}   } other {Weergave van {resultsCountFormatted} reacties met {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reactie voor {searchTextFormatted}   } other {Weergave van {resultsCountFormatted} reactie voor {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reacties voor {searchTextFormatted}   } other {Weergave van {resultsCountFormatted} reacties voor {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reactie   } other {Weergave van {resultsCountFormatted} reactie   }}} other {{hasSelectedResponse, select, true {Weergave van {resultsCountFormatted} geselecteerde reacties   } other {Weergave van {resultsCountFormatted} reacties   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Wissen",
	"ResponseToolbar.CREATE_TAG": "Tag maken",
	"ResponseToolbar.SELECT_ALL": "Alle reacties selecteren",
	"ResponseToolbar.UNSELECT_ALL": "Alle reacties deselecteren",
	"SearchResponsesInput.CLEAR": "Zoekopdracht wissen",
	"SearchResponsesInput.SEARCH": "Zoeken",
	"SearchResponsesInput.SEARCH_RESPONSES": "Reacties doorzoeken",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Gebruik aanhalingstekens <strong>“”</strong> om te zoeken naar een exacte woordgroep.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Typ <strong>OR</strong> om te zoeken naar één, twee of meer zoektermen.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Typ <b>AND</b> om naar meer dan één term te zoeken.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Gebruik <strong>NOT</strong> om een term uit te sluiten van uw zoekopdracht.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Typ <strong>*</strong> na een term om te zoeken op voorvoegsel.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Gebruik haakjes om op AND/OR/NOT gebaseerde zoekopdrachten te combineren.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Plaats <strong>~2</strong> na een term voor resultaten die overeenkomen met de term plus maximaal twee andere tekens.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Zoekopties",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Meer informatie",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Nee, bedankt",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Inschakelen",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "De functie <span id=\"link\">Automatische inzichten</span> is nu onderdeel van uw account. \n      Bij deze instelling worden zelflerende systemen gebruikt voor de verwerking van uw enquête. U ontvangt deze als inzichten.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "U kunt uw voorkeuren altijd wijzigen in <span id=\"link\">Mijn account</span>",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Automatische inzichten",
	"SentimentBanner.GENERATE_SENTIMENT": "Stemming analyseren",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Stemmingsanalyse",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Ontdek de stemming achter schriftelijke reacties om inzicht te krijgen in wat mensen vinden.",
	"SentimentBanner.UPGRADE": "Upgraden",
	"SentimentBanner.WATCH_A_DEMO": "Demo bekijken",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Wat mensen vinden:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filteren op positieve stemming} negative {Filteren op negatieve stemming} neutral {Filteren op neutrale stemming} other {Filteren op onbekende stemming}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Menu uitvouwen",
	"SentimentFilterMenu.MENU_LABEL": "op stemming",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Functie aanvragen",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Ontdek het gevoel en de stemming achter geschreven reacties.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Nieuw!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Dit is de stemmingsanalyse",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Upgraden",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Demo bekijken",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Nee, bedankt",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Functie aanvragen",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Upgraden",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Upgrade-afbeelding voor stemming",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Schakel met een klik stemmingsanalyses in. Wij classificeren uw tekstreacties als positief, neutraal of negatief. Zo weet u niet alleen wat mensen vinden, maar ook hoe ze zich voelen.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Kom meteen te weten wat mensen vinden",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Stemmingsanalyse",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Stemmingen: Aan} false {Stemmingen: Uit} other {Stemmingen: andere}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Stemmingen: Laden...",
	"SlideBoxNavigation.NEXT_BUTTON": "Volgende",
	"SlideBoxNavigation.NEXT_SLIDE": "Volgende dia",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "Vorige",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Vorige dia",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} van {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Wissen",
	"Slider.STEP_MESSAGE": "We hebben de ingevoerde waarde aangepast op basis van de schuifregelaarschaal.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Beantwoord: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "V{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Overgeslagen: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Het snijpunt tussen goedkoop en duur geeft de algehele prijsrichting voor uw product aan.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Prijsgevoeligheid (Van Westendorp)",
	"SrText.CORRECT": "Juist",
	"SrText.INCORRECT": "Niet juist",
	"StarRating.Heart": "{count, plural, one {{count} hartje} other {{count} hartjes}}",
	"StarRating.STAR": "{count, plural, one {{count} ster} other {{count} sterren}}",
	"StarRating.Smiley": "{count, plural, one {{count} smiley} other {{count} smileys}}",
	"StarRating.Thumb": "{count, plural, one {{count} duim} other {{count} duimen}}",
	"TabList.BETA_BADGE": "Bèta",
	"TabList.LOCK_ICON": "Slotpictogram tabblad Tags",
	"TabList.RESPONSES": "Reacties ({responseCount})",
	"TabList.TAGS": "Tags ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "Thematische analyse",
	"TabList.WORD_CLOUD": "Woordwolk",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Menu uitvouwen",
	"TagFilterMenu.MENU_LABEL": "Filteren: op tag",
	"TagList.TAGS": "Tags",
	"TagList.TAGS_KEYWORDS": "Trefwoorden",
	"TagList.TAG_CONTROLS": "Tagbesturingselementen",
	"TagList.TAG_NUMBER_PERCENT": "Percentage tags en aantal tags",
	"TagList.TAG_PERCENTAGE_BAR": "Tagpercentagebalk",
	"TagListItem.DELETE": "Verwijderen",
	"TagListItem.EDIT": "Bewerken",
	"TagListItem.VIEW_ALL": "Alles weergeven",
	"TagListPanel.LOADING": "Tags laden",
	"TagListPanel.NEW_TAG": "+ Nieuwe tag",
	"TagListPanel.TAGS": "Tags",
	"TagMenuContent.APPLY_ACTIONS": "Toepassen",
	"TagMenuContent.CLEAR_ACTIONS": "Wissen",
	"TagMenuContent.CLOSE_MENU": "Annuleren",
	"TagMenuContent.DUPLICATED_NAME": "U hebt al een tag met die naam.",
	"TagMenuContent.NEW_TAG": "Nieuwe tag",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Meer informatie >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Functie aanvragen",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Upgraden",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Sluiten",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Zoek, label en filter geschreven reacties en bekijk woordwolken van veelvoorkomende woorden en zinsdelen.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Tekstanalyse",
	"TimeInputWrapper.am": "a.m.",
	"TimeInputWrapper.daytimeLabel": "A.m/p.m",
	"TimeInputWrapper.hhPlaceholder": "uu",
	"TimeInputWrapper.hour": "Uur",
	"TimeInputWrapper.minutes": "Minuten",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "p.m.",
	"TimeInputWrapper.timeLabel": "Tijd",
	"ToggleableEmailField.checkboxLabel": "Stuur mij een kopie van mijn reacties per e-mail",
	"ToggleableEmailField.emailLabel": "Voer uw e-mailadres in",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Verwijderen",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {Van {responseCountFormatted} reactie wordt de tag verwijderd.} other {Van {responseCountFormatted} reacties wordt de tag verwijderd.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Als u een tag verwijdert, wordt deze uit alle reacties met een tag verwijderd. Dit kan niet ongedaan worden gemaakt.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Tag verwijderen?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Opslaan",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Wijzigingen opslaan?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Hierdoor verwijdert u de tag voor alle reacties. De tag wordt opnieuw toegepast op reacties die \n      overeenkomen met de bewerkte keywords.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Annuleren",
	"UpgradeBanner.SUBTITLE": "Voer een upgrade uit om uw eigen woordwolk aan te passen.",
	"UpgradeBanner.TITLE": "Bevalt wat u ziet?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% van reacties",
	"VanWestendorpChartDataTable.PRICE": "Prijs",
	"VanWestendorpChartDataTable.RESPONSES": "Reacties",
	"WordCloudCustomizationButton.CUSTOMIZE": "Aanpassen",
	"WordCloudCustomizationFooter.APPLY": "Toepassen",
	"WordCloudCustomizationFooter.CANCEL": "Annuleren",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Toepassen op alle woordwolken in de enquête (sommige worden mogelijk niet gewijzigd)",
	"WordCloudCustomizationFooter.NO_THANKS": "Nee, bedankt",
	"WordCloudCustomizationFooter.REQUEST": "Functie aanvragen",
	"WordCloudCustomizationFooter.SAVE": "Opslaan",
	"WordCloudCustomizationFooter.UPGRADE": "Upgraden",
	"WordCloudCustomizationTabs.COLOR": "Kleur",
	"WordCloudCustomizationTabs.FONT": "Lettertype",
	"WordCloudCustomizationTabs.RANGE": "Bereik",
	"WordCloudLegend.FEWER": "minder",
	"WordCloudLegend.MORE": "meer",
	"WordCloudLegend.TITLE": "Aantal vermeldingen",
	"WordCloudListFooter.COMBINED_WORDS": "Gecombineerde woorden",
	"WordCloudListFooter.HIDDEN_WORDS": "Verborgen woorden",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Menu uitvouwen",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Combineren met",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Gecombineerde woorden bewerken",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Menu uitvouwen",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Verbergen in woordwolk",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Gecombineerde woorden splitsen",
	"WordCloudLoadingState.LOADING": "Woordwolk laden...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Knop Sluiten",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Bekijk een voorbeeld van de functies voor het aanpassen van woordwolken.",
	"WordCloudPreviewOverlay.TITLE": "Probeer het nu!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Wolkweergave",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Lijstweergave",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Aantal keren: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "Nr. {number} belangrijkste woord",
	"WordCount.IN_PROGRESS": "Tekstanalyse wordt uitgevoerd...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Geen belangrijke woorden gevonden.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Weergave van {count} woord } other {Weergave van {count} woorden }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filteren op positieve stemming} negative {Filteren op negatieve stemming} neutral {Filteren op neutrale stemming} other {Filteren op niet-gedetecteerde stemming}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positief} negative {Negatief} neutral {Neutraal} other {Onbekend}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positief: {percentage,number,percent}} negative {Negatief: {percentage,number,percent}} neutral {Neutraal: {percentage,number,percent}} other {Onbekend: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "Annuleren",
	"copyTagEditor.DUPLICATED_NAME": "U hebt al een tag met die naam.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Voer een naam in voor deze tag",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Voer een of meer trefwoorden in",
	"copyTagEditor.KEYWORDS": "<strong>Trefwoorden</strong> (optioneel)",
	"copyTagEditor.SAVE": "Opslaan",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "De tag wordt toegepast op alle reacties met overeenkomende trefwoorden",
	"copyTagEditor.TAG_NAME": "<strong>Tagnaam</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Tagnaam mag niet leeg zijn.",
	"copyTagEditor.TAG_NAME_SPACES": "Tagnaam mag niet alleen uit spaties bestaan.",
	"copyTagEditor.TAG_TOO_LONG": "Tag moet korter zijn dan 128 tekens.",
	"countriesL10nMessages.AC": "Ascension-eiland",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Verenigde Arabische Emiraten",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua en Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanië",
	"countriesL10nMessages.AM": "Armenië",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentinië",
	"countriesL10nMessages.AS": "Amerikaans Samoa",
	"countriesL10nMessages.AT": "Oostenrijk",
	"countriesL10nMessages.AU": "Australië",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland-eilanden",
	"countriesL10nMessages.AZ": "Azerbeidzjan",
	"countriesL10nMessages.BA": "Bosnië en Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "België",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgarije",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius en Saba",
	"countriesL10nMessages.BR": "Brazilië",
	"countriesL10nMessages.BS": "Bahama's",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Cocos (Keeling)-eilanden",
	"countriesL10nMessages.CD": "Congo, DRC",
	"countriesL10nMessages.CF": "Centraal-Afrikaanse Republiek",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Zwitserland",
	"countriesL10nMessages.CI": "Ivoorkust",
	"countriesL10nMessages.CK": "Cookeilanden",
	"countriesL10nMessages.CL": "Chili",
	"countriesL10nMessages.CM": "Kameroen",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Kaapverdië",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Christmaseiland",
	"countriesL10nMessages.CY": "Cyprus",
	"countriesL10nMessages.CZ": "Tsjechië",
	"countriesL10nMessages.DE": "Duitsland",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Denemarken",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Dominicaanse Republiek",
	"countriesL10nMessages.DZ": "Algerije",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estland",
	"countriesL10nMessages.EG": "Egypte",
	"countriesL10nMessages.EH": "Westelijke Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanje",
	"countriesL10nMessages.ET": "Ethiopië",
	"countriesL10nMessages.FI": "Finland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falklandeilanden",
	"countriesL10nMessages.FM": "Federale Staten van Micronesië",
	"countriesL10nMessages.FO": "Faröer",
	"countriesL10nMessages.FR": "Frankrijk",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Verenigd koninkrijk",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgië",
	"countriesL10nMessages.GF": "Frans-Guyana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Equatoriaal-Guinea",
	"countriesL10nMessages.GR": "Griekenland",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinee-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroatië",
	"countriesL10nMessages.HT": "Haïti",
	"countriesL10nMessages.HU": "Hongarije",
	"countriesL10nMessages.ID": "Indonesië",
	"countriesL10nMessages.IE": "Ierland",
	"countriesL10nMessages.IL": "Israël",
	"countriesL10nMessages.IM": "Isle of Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Britse gebieden in de Indische Oceaan",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "IJsland",
	"countriesL10nMessages.IT": "Italië",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordanië",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenia",
	"countriesL10nMessages.KG": "Kirgizië",
	"countriesL10nMessages.KH": "Cambodja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoren",
	"countriesL10nMessages.KN": "Saint Kitts en Nevis",
	"countriesL10nMessages.KR": "Zuid-Korea",
	"countriesL10nMessages.KW": "Koeweit",
	"countriesL10nMessages.KY": "Kaaimaneilanden",
	"countriesL10nMessages.KZ": "Kazachstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litouwen",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Letland",
	"countriesL10nMessages.LY": "Libië",
	"countriesL10nMessages.MA": "Marokko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldavië",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (Franse gedeelte)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Marshalleilanden",
	"countriesL10nMessages.MK": "Macedonië",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolië",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Noordelijke Marianen",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauretanië",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldiven",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexico",
	"countriesL10nMessages.MY": "Maleisië",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibië",
	"countriesL10nMessages.NC": "Nieuw-Caledonië",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Nederland",
	"countriesL10nMessages.NO": "Noorwegen",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nieuw-Zeeland",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Frans-Polynesië",
	"countriesL10nMessages.PG": "Papoea-Nieuw-Guinea",
	"countriesL10nMessages.PH": "Filipijnen",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polen",
	"countriesL10nMessages.PM": "Saint-Pierre en Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Roemenië",
	"countriesL10nMessages.RS": "Servië",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saoedi-Arabië",
	"countriesL10nMessages.SB": "Salomonseilanden",
	"countriesL10nMessages.SC": "Seychellen",
	"countriesL10nMessages.SE": "Zweden",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenië",
	"countriesL10nMessages.SJ": "Svalbard en Jan Mayen",
	"countriesL10nMessages.SK": "Slovakije",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalië",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Zuid-Soedan",
	"countriesL10nMessages.ST": "Sao Tomé en Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- en Caicoseilanden",
	"countriesL10nMessages.TD": "Tsjaad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tadzjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunesië",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turkije",
	"countriesL10nMessages.TT": "Trinidad en Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Oekraïne",
	"countriesL10nMessages.UG": "Oeganda",
	"countriesL10nMessages.US": "Verenigde Staten",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Oezbekistan",
	"countriesL10nMessages.VA": "Vaticaanstad",
	"countriesL10nMessages.VC": "Saint Vincent en de Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Maagdeneilanden, Britse",
	"countriesL10nMessages.VI": "Maagdeneilanden, VS",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis en Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Zuid-Afrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Prijspunt van onverschilligheid",
	"helper.IPP": "Prijspunt van onverschilligheid",
	"helper.IPP_TITLE": "PPO",
	"helper.OPP": "Optimaal prijspunt",
	"helper.OPP_DESCRIPTION": "Optimaal prijspunt",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Punt van marginale goedkoopheid",
	"helper.PMC_TITLE": "PMG",
	"helper.PME": "Punt van marginale duurheid",
	"helper.PME_TITLE": "PMD",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Punt van marginale goedkoopheid",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Punt van marginale duurheid"
};

var no = {
	"ActionMenuTagPin.PIN": "Fest",
	"ActionMenuTagPin.TAG": "Merke",
	"AddressField.selectCountry": "Velg land",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Besvart: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "Spm{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Hoppet over: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Hva folk føler:",
	"AsyncJobLoadingState.LOADING": "Laster inn svar",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Vi gir beskjed når det er klart",
	"AsyncJobLoadingState.TAGGING_TITLE": "Tagg svar",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Merker gjelder for eksisterende og fremtidige svar. Legg inn et mellomrom eller skriv <strong>OR</strong> mellom ord for å bruke mer enn ett nøkkelord.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Tagg svar automatisk",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Velg en måned",
	"CalendarPicker.months": "{months, select, January {Januar} February {Februar} March {Mars} April {April} May {Mai} June {Juni} July {Juli} August {August} September {September} October {Oktober} November {November} December {Desember} other {annet}}",
	"CalendarPicker.nextMonth": "Neste måned",
	"CalendarPicker.previousMonth": "Forrige måned",
	"CalendarPicker.year": "Velg et år",
	"ColorSwatches.TAG": "Tagg {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Flerfargede ordskyer bruker både farge og størrelse for å angi antall ganger et ord eller en setning ble nevnt.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Slik bruker ordskyer farger",
	"ColorTab.DOWN_ARROW": "Velg rullegardin-pil",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Flerfarget",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Én farge",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Avbryt",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Oppgi et nytt ord",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Det nye ordet erstatter hvert av de valgte ordene under.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Rediger kombinerte ord",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Kombiner ord",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Angi et nytt ord som erstatter ett eller flere valgte ord. Du kan redigere dette igjen senere for å få tilbake de opprinnelige ordene.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Skriv inn et hvilket som helst nytt ord",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Kombinere",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Velg ord du vil kombinere",
	"CreateTagFromSearch.CLOSE_MENU": "Avbryt",
	"CreateTagFromSearch.DUPLICATED_NAME": "Du har allerede en tagg med dette navnet.",
	"CreateTagFromSearch.NEW_TAG": "Nytt merke",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Ingen svar \n\t\tblir tagget} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} svar \n\t\t\t\n\t\tblir tagget} other {\n\t\t\t\t{responseTaggedFormatted} svar \n\t\t\t\n\t\tblir tagget}}}}",
	"CreateTagFromSearch.SAVE": "Lagre",
	"CreateTagFromSearch.TAG": "Merke",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Taggnavnet kan ikke være tomt.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Taggnavnet kan ikke ha bare mellomrom.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Tagg svar som matcher disse nøkkelordene. Velg <strong>Opprett tagg</strong>. Velg en farge og lagre. Taggen gjelder for eksisterende og fremtidige svar.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Tagg svar som matcher",
	"DashboardMenuContent.CREATE": "Opprett",
	"DashboardMenuContent.NAME": "Navn",
	"DashboardMenuContent.NEW_DASHBOARD": "Nytt dashboard",
	"DateInputWrapper.dateButton": "Åpne kalenderen",
	"DateInputWrapper.dateFormatIntl": "DD/MM/ÅÅÅÅ",
	"DateInputWrapper.dateFormatUs": "MM/DD/ÅÅÅÅ",
	"DateInputWrapper.dateLabel": "Dato",
	"DateTime.errorEnterHours": "Angi timer fra 0 til 23.",
	"DateTime.errorEnterMinutes": "Legg inn minutter fra 0 til 59.",
	"DateTime.errorSelectPeriod": "Velg AM/PM.",
	"DateTime.errorYearOutOfRange": "Oppgi et år mellom 1900 og 9999.",
	"DateTime.formatErrorDateIntl": "Oppgi datoen i formatet DD/MM/ÅÅÅÅ.",
	"DateTime.formatErrorDateUs": "Oppgi datoen i formatet MM/DD/ÅÅÅÅ.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "Avbryt",
	"EditorButtons.COPY_BUTTON": "KOPIER SPØRSMÅL",
	"EditorButtons.MOVE_BUTTON": "FLYTT SPØRSMÅL",
	"EditorButtons.NEXT_QUESTION_BUTTON": "Neste spørsmål",
	"EditorButtons.SAVE_BUTTON": "Lagre",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Datatrender gjelder ikke for dette spørsmålet",
	"FallbackComponent.ERROR": "Kunne ikke laste inn spørsmål",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Velg en fil</div> <div class={dragClass}>eller dra den hit</div>",
	"FileUpload.fileUploaded": "Filen ble lastet opp",
	"FileUpload.fileUploading": "Filen lastes opp",
	"FileUpload.removeFile": "Fjern fil",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Erstatt filen</div> <div class={dragClass}>eller dra den hit</div>",
	"FileUpload.supportedFiles": "Støttede filer",
	"FilterMenuContent.APPLY_ACTIONS": "Bruk",
	"FilterMenuContent.CLEAR_ACTIONS": "Fjern",
	"FilterMenuContent.CLOSE_MENU": "Avbryt",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Avbryt",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Skjul",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Lagre",
	"HideWordsModal.HIDE_WORDS_HEADER": "Skjul ord",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Velg ord som skal skjules fra ordskyen.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Velg ord å skjule",
	"IconHelpStyled.HELP": "Hjelp",
	"IndividualResponseCard.NO_RESPONSE": "Respondenten hoppet over dette spørsmålet",
	"IndividualResponseCard.QUESTION_NUMBER": "Spm{number}",
	"InfoBannerTagManagement.CLOSE": "Lukk",
	"InfoBannerTagManagement.NEW": "Ny",
	"InfoBannerTagManagement.TAG_RESPONSES": "Tagg svar",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Legg til nøkkelord for å konfigurere automatiske tagger og holde svarene organiserte.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Tagg svar for enkel administrasjon",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positiv: {percentage,number,percent}} negative {Negativ: {percentage,number,percent}} neutral {Nøytral: {percentage,number,percent}} unknown {Ikke gjenkjent: {percentage,number,percent}} other {annet}}",
	"LoadingState.LOADING": "Laster inn svar",
	"MatrixDropdown.EmptyTopNativeSelect": "Velg et alternativ",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Konfidensintervall",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Nyttepoeng",
	"Modal.CloseModal": "Lukk verktøytips",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Mandag} Tu {Tirsdag} We {Onsdag} Th {Torsdag} Fr {Fredag} Sa {Lørdag} Su {Søndag} other {annet}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Ma.} Tu {Ti.} We {On.} Th {To.} Fr {Fr.} Sa {Lø.} Su {Sø.} other {annet}}",
	"MultiColorSelectorMenu.APPLY": "Bruk",
	"MultiColorSelectorMenu.CANCEL": "Avbryt",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Arranger data i et egendefinert oppsett, forklar resultatene og del dashboardet med hvem du vil. Arranger data i et egendefinert oppsett, forklar resultatene og del dashboardet med hvem du vil.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Dashboard",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Les mer",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">På en skala fra {minValue} til {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} for {minLabelText}, {maxValue} for {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "målesone for scorer mellom {min} og {max}",
	"NpsGaugeChart.NPS_CHART": "NPS-målediagram",
	"NpsGaugeChart.NPS_SCORE": "NPS-score på {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "diagrampunkt for merking av {score}-score",
	"OrderControl.AriaNA": "Ikke aktuelt",
	"OrderControl.NA": "Ikke tilgjengelig",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "«{title}»-svar ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "skjul liste med svar for «annet»",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "vis liste med svar for «annet»",
	"PhoneField.countryCode": "Landskode",
	"QuestionCardItemFooter.FILTER_APPLIED": "Regler som er brukt: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtrer",
	"QuestionCardTitle.ANSWERED": "Besvart: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Valgt: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Hoppet over: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} poeng} other {{score}/{total} poeng}}",
	"QuizResultScore.SKIPPED": "Hoppet over",
	"RangeTab.RANGE_TAB_LABEL": "Nevninger: {value}",
	"RangeTab.TOTAL_WORDS": "ord totalt",
	"Ranking.AriaRankAtBottom": "{label} ble flyttet til nederst på listen",
	"Ranking.AriaRankAtTop": "{label} ble flyttet til øverst på listen",
	"Ranking.AriaRankMovedDown": "{label} ble flyttet ned til nummer {position}",
	"Ranking.AriaRankMovedUp": "{label} ble flyttet opp til nummer {position}",
	"RankingItem.AriaRankMoveDown": "Flytt ned {label}",
	"RankingItem.AriaRankMoveUp": "Flytt opp {label}",
	"RatingChart.AVERAGE_RATING": "gjennomsnittlig vurdering",
	"RatingChartLegend.ANSWERED": "Besvart: {number}",
	"RatingChartLegend.RATING": "{rating} stjerne",
	"RatingChartLegend.SKIPPED": "Hoppet over: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Be om funksjon",
	"RequestAccessButton.UPGRADE": "Oppgrader",
	"RespondentQuestionTitle.REQUIRED": "(Obligatorisk.)",
	"ResponseItem.EXPAND_MENU_LABEL": "Utvid menyen",
	"ResponseItem.MENU_LABEL": "Legg til tagger",
	"ResponseItem.RESPONDENT_ID": "Respondent-ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Se respondentens svar",
	"ResponseStatusBar.CLEAR": "Fjern",
	"ResponseStatusBar.FAILED_TO_LOAD": "Kunne ikke laste svarene",
	"ResponseStatusBar.NO_RESPONSES_YET": "Ingen svar ennå",
	"ResponseStatusBar.SEARCHING": "Søker …",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {positiv holdning} negative {negativ holdning} neutral {nøytral holdning} other {ukjent holdning}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgt svar for {searchTextFormatted} med {filters}   } other {Viser {resultsCountFormatted} svar for {searchTextFormatted} med {filters}   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte svar for {searchTextFormatted} med {filters}   } other {Viser {resultsCountFormatted} svar for {searchTextFormatted} med {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgt svar med {filters}   } other {Viser {resultsCountFormatted} svar med {filters}   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte svar med {filters}   } other {Viser {resultsCountFormatted} svar med {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgt svar for {searchTextFormatted}   } other {Viser {resultsCountFormatted} svar for {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte svar for {searchTextFormatted}   } other {Viser {resultsCountFormatted} svar for {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgt svar   } other {Viser {resultsCountFormatted} svar   }}} other {{hasSelectedResponse, select, true {Viser {resultsCountFormatted} valgte svar   } other {Viser {resultsCountFormatted} svar   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Fjern",
	"ResponseToolbar.CREATE_TAG": "Opprett tagg",
	"ResponseToolbar.SELECT_ALL": "Velg alle svar",
	"ResponseToolbar.UNSELECT_ALL": "Fjern valg av alle svar",
	"SearchResponsesInput.CLEAR": "Fjern søk",
	"SearchResponsesInput.SEARCH": "Søk",
	"SearchResponsesInput.SEARCH_RESPONSES": "Søk etter svar",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Bruk anførselstegn <strong>“”</strong> for å søke etter en nøyaktig frase.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Skriv inn <strong>ELLER</strong> for å finne ett eller flere søkeord.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Skriv <b>AND</b> for å finne mer enn ett ord.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Bruk <strong>IKKE</strong> for å ekskludere et ord fra søket.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Skriv inn <strong>*</strong> etter et ord for å søke med prefiks.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Bruk parentes for å kombinere søk som er basert på AND/OR/NOT.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Bruk <strong>~2</strong> etter et ord for å få resultater som matcher ordet pluss opptil to andre tegn.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Søkealternativer",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Les mer",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Nei takk",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Aktiver",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "<span id=\"link\">Automatisk innsikt</span> er nå tilgjengelig på kontoen din. \n      Denne innstillingen omfatter enhver funksjon som bruker maskinlæring til å behandle spørreundersøkelsen din og gi deg innsikt.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Innstillingene kan alltid endres i <span id=\"link\">Min konto</span>",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Automatisk innsikt",
	"SentimentBanner.GENERATE_SENTIMENT": "Analyser holdning",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Holdningsanalyse",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Avdekk holdningen bak skriftlige svar for å forstå hva folk mener.",
	"SentimentBanner.UPGRADE": "Oppgrader",
	"SentimentBanner.WATCH_A_DEMO": "Se en demonstrasjon",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Hva folk føler:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtrer etter positiv holdning} negative {Filtrer etter negativ holdning} neutral {Filtrer etter nøytral holdning} other {Filtrer etter ukjent holdning}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Utvid menyen",
	"SentimentFilterMenu.MENU_LABEL": "etter holdning",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Be om funksjon",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Registrer hvilke følelser og holdninger som ligger i skriftlige svar.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Nytt!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Vi introduserer holdningsanalyse",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Oppgrader",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Se en demonstrasjon",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Nei takk",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Be om funksjon",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Oppgrader",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Bilde for holdningsoppgradering",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Slå på holdningsanalyse med bare et klikk. Vi klassifiserer tekstsvaret ditt som positivt, nøytralt eller negativt for å avdekke følelsene bak det folk sier.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Få kunnskap om hva folk føler med én gang",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Holdningsanalyse",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Holdninger: PÅ} false {Holdninger: AV} other {Holdninger: annet}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Holdninger: LASTER ...",
	"SlideBoxNavigation.NEXT_BUTTON": "NESTE",
	"SlideBoxNavigation.NEXT_SLIDE": "Neste lysbilde",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "FORRIGE",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Forrige lysbilde",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} av {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Fjern",
	"Slider.STEP_MESSAGE": "Vi justerte tallet du skrev inn basert på skalaen på glidebryteren.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Besvart: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "Spm{questionNumber}{questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Hoppet over: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Skjæringspunktet mellom billig og dyrt gir en generell prisveiledning for produktet ditt.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Prisfølsomhet (Van Westendorp)",
	"SrText.CORRECT": "Riktig",
	"SrText.INCORRECT": "Galt",
	"StarRating.Heart": "{count, plural, one {{count} hjerte} other {{count} hjerter}}",
	"StarRating.STAR": "{count, plural, one {{count} stjerne} other {{count} stjerner}}",
	"StarRating.Smiley": "{count, plural, one {{count} smilefjes} other {{count} smilefjes}}",
	"StarRating.Thumb": "{count, plural, one {{count} tommel} other {{count} tomler}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Låseikon for taggefane",
	"TabList.RESPONSES": "SVAR ({responseCount})",
	"TabList.TAGS": "TAGGER ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "TEMATISK ANALYSE",
	"TabList.WORD_CLOUD": "ORDSKY",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Utvid menyen",
	"TagFilterMenu.MENU_LABEL": "Filtrer: etter tagg",
	"TagList.TAGS": "Koder",
	"TagList.TAGS_KEYWORDS": "Nøkkelord",
	"TagList.TAG_CONTROLS": "Tagg-kontroll",
	"TagList.TAG_NUMBER_PERCENT": "Prosent av tagger og antall tagger",
	"TagList.TAG_PERCENTAGE_BAR": "Prosentlinje for tagger",
	"TagListItem.DELETE": "Slett",
	"TagListItem.EDIT": "Rediger",
	"TagListItem.VIEW_ALL": "Se alle",
	"TagListPanel.LOADING": "Laster tagger",
	"TagListPanel.NEW_TAG": "+ nytt merke",
	"TagListPanel.TAGS": "Koder",
	"TagMenuContent.APPLY_ACTIONS": "Bruk",
	"TagMenuContent.CLEAR_ACTIONS": "Fjern",
	"TagMenuContent.CLOSE_MENU": "Avbryt",
	"TagMenuContent.DUPLICATED_NAME": "Du har allerede en tagg med dette navnet.",
	"TagMenuContent.NEW_TAG": "Nytt merke",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Les mer >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Be om funksjon",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Oppgrader",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Lukk",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Søk, merk og filtrer skriftlige svar og se ordskyer med ofte brukte ord og uttrykk.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Tekstanalyse",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "tt",
	"TimeInputWrapper.hour": "time",
	"TimeInputWrapper.minutes": "Minutter",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Tid",
	"ToggleableEmailField.checkboxLabel": "Send meg en kopi av svarene mine via e-post",
	"ToggleableEmailField.emailLabel": "Skriv inn e-postadressen din",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Slett",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted} svar får taggen fjernet.} other {{responseCountFormatted} svar får taggen fjernet.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Når du sletter en tagg fjernes den fra alle taggede svar, og dette kan ikke angres.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Slette tagg?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Lagre",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Vil du lagre endringene?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Dette fjerner taggen fra alle svarene. Taggen gjelder svarene som\n      matcher de redigerte nøkkelordene.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Avbryt",
	"UpgradeBanner.SUBTITLE": "Oppgrader for å tilpasse din egen ordsky.",
	"UpgradeBanner.TITLE": "Liker du det du ser?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% av svarene",
	"VanWestendorpChartDataTable.PRICE": "Pris",
	"VanWestendorpChartDataTable.RESPONSES": "Svar",
	"WordCloudCustomizationButton.CUSTOMIZE": "Tilpass",
	"WordCloudCustomizationFooter.APPLY": "Bruk",
	"WordCloudCustomizationFooter.CANCEL": "Avbryt",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Bruk på alle ordskyer i spørreundersøkelsen (noen vil kanskje ikke endre seg)",
	"WordCloudCustomizationFooter.NO_THANKS": "Nei takk",
	"WordCloudCustomizationFooter.REQUEST": "Be om funksjon",
	"WordCloudCustomizationFooter.SAVE": "Lagre",
	"WordCloudCustomizationFooter.UPGRADE": "Oppgrader",
	"WordCloudCustomizationTabs.COLOR": "FARGE",
	"WordCloudCustomizationTabs.FONT": "SKRIFTTYPE",
	"WordCloudCustomizationTabs.RANGE": "POENGOMRÅDE",
	"WordCloudLegend.FEWER": "færre",
	"WordCloudLegend.MORE": "flere",
	"WordCloudLegend.TITLE": "Antall forekomster",
	"WordCloudListFooter.COMBINED_WORDS": "Kombinerte ord",
	"WordCloudListFooter.HIDDEN_WORDS": "Skjulte ord",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Utvid menyen",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Kombiner med",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Rediger kombinerte ord",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Utvid menyen",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Skjul fra ordsky",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Skill kombinerte ord",
	"WordCloudLoadingState.LOADING": "Laster inn ordsky …",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Lukkeknapp",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Forhåndsvis funksjonene for å tilpasse ordskyer.",
	"WordCloudPreviewOverlay.TITLE": "Prøv det!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Skyvisning",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Listevisning",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Forekomster: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "Nummer {number} viktigste ord",
	"WordCount.IN_PROGRESS": "Tekstanalyse pågår …",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Fant ingen viktige ord.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Viser {count} ord } other {Viser {count} ord }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtrer etter positiv holdning} negative {Filtrer etter negativ holdning} neutral {Filtrer etter nøytral holdning} other {Filtrer etter holdning som ikke er oppdaget}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positiv} negative {Negativ} neutral {Nøytral} other {Ukjent}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positiv: {percentage,number,percent}} negative {Negativ: {percentage,number,percent}} neutral {Nøytral: {percentage,number,percent}} other {Ukjent: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "Avbryt",
	"copyTagEditor.DUPLICATED_NAME": "Du har allerede en tagg med dette navnet.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Angi et navn for dette merket",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Angi ett eller flere nøkkelord",
	"copyTagEditor.KEYWORDS": "<strong>Nøkkelord</strong> (valgfritt)",
	"copyTagEditor.SAVE": "Lagre",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Merket gjelder for alle svar med matchende nøkkelord",
	"copyTagEditor.TAG_NAME": "<strong>Merkenavn</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Taggnavnet kan ikke være tomt.",
	"copyTagEditor.TAG_NAME_SPACES": "Taggnavnet kan ikke ha bare mellomrom.",
	"copyTagEditor.TAG_TOO_LONG": "Taggen må bestå av mindre enn 128 tegn.",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "De forente arabiske emirater",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua og Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Amerikansk Samoa",
	"countriesL10nMessages.AT": "Østerrike",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland",
	"countriesL10nMessages.AZ": "Aserbajdsjan",
	"countriesL10nMessages.BA": "Bosnia-Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgia",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius og Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Kokosøyene (Keeling)",
	"countriesL10nMessages.CD": "Den demokratiske republikken Kongo",
	"countriesL10nMessages.CF": "Den sentralafrikanske republikk",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Sveits",
	"countriesL10nMessages.CI": "Elfenbenskysten",
	"countriesL10nMessages.CK": "Cookøyene",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Kapp Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Christmasøya",
	"countriesL10nMessages.CY": "Kypros",
	"countriesL10nMessages.CZ": "Tsjekkia",
	"countriesL10nMessages.DE": "Tyskland",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Danmark",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Den dominikanske republikk",
	"countriesL10nMessages.DZ": "Algerie",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estland",
	"countriesL10nMessages.EG": "Egypt",
	"countriesL10nMessages.EH": "Vest-Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spania",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falklandsøyene",
	"countriesL10nMessages.FM": "Mikronesiaføderasjonen",
	"countriesL10nMessages.FO": "Færøyene",
	"countriesL10nMessages.FR": "Frankrike",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Storbritannia og Nord-Irland",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Fransk Guyana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grønland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Ekvatorial-Guinea",
	"countriesL10nMessages.GR": "Hellas",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungarn",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Det britiske territoriet i Indiahavet",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirgisistan",
	"countriesL10nMessages.KH": "Kambodsja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoros",
	"countriesL10nMessages.KN": "St. Kitts og Nevis",
	"countriesL10nMessages.KR": "Sør-Korea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Caymanøyene",
	"countriesL10nMessages.KZ": "Kasakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litauen",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Marokko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint-Martin (den franske delen)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshalløyene",
	"countriesL10nMessages.MK": "Makedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Nord-Marianene",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldivene",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexico",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mosambik",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Ny-Caledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolkøya",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Nederland",
	"countriesL10nMessages.NO": "Norge",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "New Zealand",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Fransk Polynesia",
	"countriesL10nMessages.PG": "Papua Ny-Guinea",
	"countriesL10nMessages.PH": "Filippinene",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polen",
	"countriesL10nMessages.PM": "Saint Pierre og Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudi-Arabia",
	"countriesL10nMessages.SB": "Salomonøyene",
	"countriesL10nMessages.SC": "Seychellene",
	"countriesL10nMessages.SE": "Sverige",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Sankt Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard og Jan Mayen",
	"countriesL10nMessages.SK": "Slovakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sør-Sudan",
	"countriesL10nMessages.ST": "Sao Tome og Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- og Caicosøyene",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tadsjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Øst-Timor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Tyrkia",
	"countriesL10nMessages.TT": "Trinidad og Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "USA",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Usbekistan",
	"countriesL10nMessages.VA": "Vatikanstaten",
	"countriesL10nMessages.VC": "Saint Vincent og Grenadinene",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Jomfruøyene (Storbritannia)",
	"countriesL10nMessages.VI": "De amerikanske Jomfruøyer",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis og Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Sør-Afrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Indifferens-prispunkt (IPP)",
	"helper.IPP": "Indifferens-prispunkt (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Optimalt prispunkt (OPP)",
	"helper.OPP_DESCRIPTION": "Optimalt prispunkt",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Punkt for marginal billighet (PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Punkt for marginal kostbarhet (PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Punkt for marginal billighet (PMC)",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Punkt for marginal kostbarhet (PME)"
};

var pa = {
	"AddressField.selectCountry": "ਦੇਸ਼ ਚੁਣੋ",
	"PhoneField.countryCode": "ਦੇਸ਼ ਦਾ ਕੋਡ",
	"countriesL10nMessages.AC": "ਅਸੈਸ਼ਨ ਟਾਪੂ",
	"countriesL10nMessages.AD": "ਅੰਡੋਰਾ",
	"countriesL10nMessages.AE": "ਸੰਯੁਕਤ ਅਰਬ ਅਮੀਰਾਤ",
	"countriesL10nMessages.AF": "ਅਫਗਾਨਿਸਤਾਨ",
	"countriesL10nMessages.AG": "ਐਂਟੀਗੁਆ ਅਤੇ ਬਾਰਬੁਡਾ",
	"countriesL10nMessages.AI": "ਐਂਗੁਇਲਾ",
	"countriesL10nMessages.AL": "ਅਲਬਾਨੀਆ",
	"countriesL10nMessages.AM": "ਅਰਮੀਨੀਆ",
	"countriesL10nMessages.AO": "ਅੰਗੋਲਾ",
	"countriesL10nMessages.AR": "ਅਰਜਨਟੀਨਾ",
	"countriesL10nMessages.AS": "ਅਮਰੀਕੀ ਸਮੋਆ",
	"countriesL10nMessages.AT": "ਆਸਟਰੀਆ",
	"countriesL10nMessages.AU": "ਆਸਟ੍ਰੇਲੀਆ",
	"countriesL10nMessages.AW": "ਅਰੂਬਾ",
	"countriesL10nMessages.AX": "ਫਾਕਲੈਂਡ ਟਾਪੂ",
	"countriesL10nMessages.AZ": "ਅਜ਼ਰਬਾਈਜਾਨ",
	"countriesL10nMessages.BA": "ਬੋਸਨੀਆ ਅਤੇ ਹਰਜ਼ੇਗੋਵਿਨਾ",
	"countriesL10nMessages.BB": "ਬਾਰਬਾਡੋਸ",
	"countriesL10nMessages.BD": "ਬੰਗਲਾਦੇਸ਼",
	"countriesL10nMessages.BE": "ਬੈਲਜੀਅਮ",
	"countriesL10nMessages.BF": "ਬੁਰਕੀਨਾ ਫਾਸੋ",
	"countriesL10nMessages.BG": "ਬੁਲਗਾਰੀਆ",
	"countriesL10nMessages.BH": "ਬਹਿਰੀਨ",
	"countriesL10nMessages.BI": "ਬੁਰੂੰਡੀ",
	"countriesL10nMessages.BJ": "ਬੇਨਿਨ",
	"countriesL10nMessages.BL": "ਸੇਂਟ ਬਾਰਥਲੇਮੀ",
	"countriesL10nMessages.BM": "ਬਰਮੂਡਾ",
	"countriesL10nMessages.BN": "ਬਰੂਨੇਈ ਦਾਰੂਸਲਾਮ",
	"countriesL10nMessages.BO": "ਬੋਲੀਵੀਆ",
	"countriesL10nMessages.BQ": "ਬੋਨੇਅਰ, ਸਿੰਟ ਯੂਸਟੈਟੀਅਸ ਅਤੇ ਸਾਬਾ",
	"countriesL10nMessages.BR": "ਬ੍ਰਾਜ਼ੀਲ",
	"countriesL10nMessages.BS": "ਬਹਾਮਾਸ",
	"countriesL10nMessages.BT": "ਭੂਟਾਨ",
	"countriesL10nMessages.BW": "ਬੋਤਸਵਾਨਾ",
	"countriesL10nMessages.BZ": "ਬੇਲੀਜ਼",
	"countriesL10nMessages.CA": "ਕੈਨੇਡਾ",
	"countriesL10nMessages.CC": "ਕੋਕੋਸ (ਕੀਲਿੰਗ) ਟਾਪੂ",
	"countriesL10nMessages.CD": "ਕਾਂਗੋ, ਲੋਕਤੰਤਰੀ ਗਣਰਾਜ",
	"countriesL10nMessages.CF": "ਮੱਧ ਅਫ਼ਰੀਕੀ ਗਣਰਾਜ",
	"countriesL10nMessages.CG": "ਕਾਂਗੋ",
	"countriesL10nMessages.CH": "ਸਵਿੱਟਜਰਲੈਂਡ",
	"countriesL10nMessages.CI": "ਕੋਟ ਡਿਵੁਆਰ",
	"countriesL10nMessages.CK": "ਫਾਰੋ ਟਾਪੂ",
	"countriesL10nMessages.CL": "ਚਿਲੀ",
	"countriesL10nMessages.CM": "ਕੈਮਰੂਨ",
	"countriesL10nMessages.CN": "ਚੀਨ",
	"countriesL10nMessages.CO": "ਕੋਲੰਬੀਆ",
	"countriesL10nMessages.CR": "ਕੋਸਟਾਰੀਕਾ",
	"countriesL10nMessages.CV": "ਕੇਪ ਵਰਡੇ",
	"countriesL10nMessages.CW": "ਕੁਰਕਾਓ",
	"countriesL10nMessages.CX": "ਕ੍ਰਿਸਮਸ ਟਾਪੂ",
	"countriesL10nMessages.CY": "ਸਾਈਪ੍ਰਸ",
	"countriesL10nMessages.CZ": "ਚੇਕ ਗਣਤੰਤਰ",
	"countriesL10nMessages.DE": "ਜਰਮਨੀ",
	"countriesL10nMessages.DJ": "ਜਿਬੂਟੀ",
	"countriesL10nMessages.DK": "ਡੈਨਮਾਰਕ",
	"countriesL10nMessages.DM": "ਡੋਮਿਨਿਕਾ",
	"countriesL10nMessages.DO": "ਚੇਕ ਗਣਤੰਤਰ",
	"countriesL10nMessages.DZ": "ਅਲਜੀਰੀਆ",
	"countriesL10nMessages.EC": "ਇਕਵਾਡੋਰ",
	"countriesL10nMessages.EE": "ਐਸਟੋਨੀਆ",
	"countriesL10nMessages.EG": "ਮਿਸਰ",
	"countriesL10nMessages.EH": "ਪੱਛਮੀ ਸਹਾਰਾ",
	"countriesL10nMessages.ER": "ਇਰੀਟਰੀਆ",
	"countriesL10nMessages.ES": "ਸਪੇਨ",
	"countriesL10nMessages.ET": "ਇਥੋਪੀਆ",
	"countriesL10nMessages.FI": "ਫਿਨਲੈਂਡ",
	"countriesL10nMessages.FJ": "ਫਿਜੀ",
	"countriesL10nMessages.FK": "ਫਾਕਲੈਂਡ ਟਾਪੂ",
	"countriesL10nMessages.FM": "ਮਾਈਕ੍ਰੋਨੇਸ਼ੀਆ ਦੇ ਸੰਘੀ ਰਾਜ",
	"countriesL10nMessages.FO": "ਫਾਰੋ ਟਾਪੂ",
	"countriesL10nMessages.FR": "ਫਰਾਂਸ",
	"countriesL10nMessages.GA": "ਗੈਬੋਨ",
	"countriesL10nMessages.GB": "ਯੁਨਾਇਟੇਡ ਕਿਂਗਡਮ",
	"countriesL10nMessages.GD": "ਗ੍ਰੇਨਾਡਾ",
	"countriesL10nMessages.GE": "ਜਾਰਜੀਆ",
	"countriesL10nMessages.GF": "ਫ੍ਰੈਂਚ ਗੁਆਨਾ",
	"countriesL10nMessages.GG": "ਗਰਨਸੇ",
	"countriesL10nMessages.GH": "ਘਾਨਾ",
	"countriesL10nMessages.GI": "ਜਿਬਰਾਲਟਰ",
	"countriesL10nMessages.GL": "ਗ੍ਰੀਨਲੈਂਡ",
	"countriesL10nMessages.GM": "ਗੈਂਬੀਆ",
	"countriesL10nMessages.GN": "ਗਿਨੀ",
	"countriesL10nMessages.GP": "ਗੁਆਡੇਲੂਪ",
	"countriesL10nMessages.GQ": "ਇਕੂਟੇਰੀਅਲ ਗਿਨੀ",
	"countriesL10nMessages.GR": "ਗ੍ਰੀਸ",
	"countriesL10nMessages.GT": "ਗੁਆਟੇਮਾਲਾ",
	"countriesL10nMessages.GU": "ਗੁਆਮ",
	"countriesL10nMessages.GW": "ਗਿਨੀ-ਬਿਸਾਉ",
	"countriesL10nMessages.GY": "ਗੁਆਨਾ",
	"countriesL10nMessages.HK": "ਹਾਂਗ ਕਾਂਗ",
	"countriesL10nMessages.HN": "ਹੋਂਡੁਰਾਸ",
	"countriesL10nMessages.HR": "ਕਰੋਸ਼ੀਆ",
	"countriesL10nMessages.HT": "ਹੈਤੀ",
	"countriesL10nMessages.HU": "ਹੰਗਰੀ",
	"countriesL10nMessages.ID": "ਇੰਡੋਨੇਸ਼ੀਆ",
	"countriesL10nMessages.IE": "ਆਇਰਲੈਂਡ",
	"countriesL10nMessages.IL": "ਇਜ਼ਰਾਈਲ",
	"countriesL10nMessages.IM": "ਆਇਲ ਆਫ ਮੈਨ",
	"countriesL10nMessages.IN": "ਭਾਰਤ",
	"countriesL10nMessages.IO": "ਬ੍ਰਿਟਿਸ਼ ਹਿੰਦ ਮਹਾਸਾਗਰ ਖੇਤਰ",
	"countriesL10nMessages.IQ": "ਇਰਾਕ",
	"countriesL10nMessages.IS": "ਆਈਸਲੈਂਡ",
	"countriesL10nMessages.IT": "ਇਟਲੀ",
	"countriesL10nMessages.JE": "ਜਰਸੀ",
	"countriesL10nMessages.JM": "ਜਮਾਏਕਾ",
	"countriesL10nMessages.JO": "ਜਾਰਡਨ",
	"countriesL10nMessages.JP": "ਜਪਾਨ",
	"countriesL10nMessages.KE": "ਕੀਨੀਆ",
	"countriesL10nMessages.KG": "ਕਿਰਗਿਸਤਾਨ",
	"countriesL10nMessages.KH": "ਕੰਬੋਡੀਆ",
	"countriesL10nMessages.KI": "ਕਿਰੀਬਾਤੀ",
	"countriesL10nMessages.KM": "ਕੋਮੋਰੋਸ",
	"countriesL10nMessages.KN": "ਸੇਂਟ ਕਿਟਸ ਅਤੇ ਨੇਵਿਸ",
	"countriesL10nMessages.KR": "ਦੱਖਣੀ ਕੋਰੀਆ",
	"countriesL10nMessages.KW": "ਕੁਵੈਤ",
	"countriesL10nMessages.KY": "ਕੇਮੈਨ ਟਾਪੂ",
	"countriesL10nMessages.KZ": "ਕਜ਼ਾਕਿਸਤਾਨ",
	"countriesL10nMessages.LA": "ਲਾਓਸ",
	"countriesL10nMessages.LB": "ਲੇਬਨਾਨ",
	"countriesL10nMessages.LC": "ਸੇਂਟ ਲੂਸੀਆ",
	"countriesL10nMessages.LI": "ਲਿਚਟਨਸਟਾਈਨ",
	"countriesL10nMessages.LK": "ਸ਼੍ਰੀਲੰਕਾ",
	"countriesL10nMessages.LR": "ਲਾਇਬੇਰੀਆ",
	"countriesL10nMessages.LS": "ਲੈਸੋਥੋ",
	"countriesL10nMessages.LT": "ਲਿਥੁਆਨੀਆ",
	"countriesL10nMessages.LU": "ਲਕਸਮਬਰਗ",
	"countriesL10nMessages.LV": "ਲਾਤਵੀਆ",
	"countriesL10nMessages.LY": "ਲੀਬੀਆ",
	"countriesL10nMessages.MA": "ਮੋਰਾਕੋ",
	"countriesL10nMessages.MC": "ਮੋਨਾਕੋ",
	"countriesL10nMessages.MD": "ਮੋਲਡੋਵਾ",
	"countriesL10nMessages.ME": "ਮੋਂਟੇਨੇਗਰੋ",
	"countriesL10nMessages.MF": "ਸੇਂਟ ਮਾਰਟਿਨ (ਫਰਾਂਸੀਸੀ ਭਾਗ)",
	"countriesL10nMessages.MG": "ਮੈਡਾਗਾਸਕਰ",
	"countriesL10nMessages.MH": "ਮਾਰਸ਼ਲ ਟਾਪੂ",
	"countriesL10nMessages.MK": "ਮੈਸੇਡੋਨੀਆ",
	"countriesL10nMessages.ML": "ਮਾਲੀ",
	"countriesL10nMessages.MM": "ਮਿਆਂਮਾਰ",
	"countriesL10nMessages.MN": "ਮੰਗੋਲੀਆ",
	"countriesL10nMessages.MO": "ਮਕਾਓ",
	"countriesL10nMessages.MP": "ਉੱਤਰੀ ਮਾਰੀਆਨਾ ਟਾਪੂ",
	"countriesL10nMessages.MQ": "ਮਾਰਟੀਨਿਕ",
	"countriesL10nMessages.MR": "ਮੌਰੀਤਾਨੀਆ",
	"countriesL10nMessages.MS": "ਮੋਂਟਸੇਰਾਟ",
	"countriesL10nMessages.MT": "ਮਾਲਟਾ",
	"countriesL10nMessages.MU": "ਮਾਰੀਸ਼ਸ",
	"countriesL10nMessages.MV": "ਮਾਲਦੀਵ",
	"countriesL10nMessages.MW": "ਮਲਾਵੀ",
	"countriesL10nMessages.MX": "ਮੈਕਸੀਕੋ",
	"countriesL10nMessages.MY": "ਮਲੇਸ਼ੀਆ",
	"countriesL10nMessages.MZ": "ਮੋਜ਼ਾਮਬੀਕ",
	"countriesL10nMessages.NA": "ਨਾਮੀਬੀਆ",
	"countriesL10nMessages.NC": "ਨਿਊ ਕੈਲੇਡੋਨੀਆ",
	"countriesL10nMessages.NE": "ਨਾਈਜਰ",
	"countriesL10nMessages.NF": "ਨਾਰਫੋਕ ਟਾਪੂ",
	"countriesL10nMessages.NG": "ਨਾਈਜੀਰੀਆ",
	"countriesL10nMessages.NI": "ਨਿਕਾਰਾਗੁਆ",
	"countriesL10nMessages.NL": "ਨੀਦਰਲੈਂਡ",
	"countriesL10nMessages.NO": "ਨਾਰਵੇ",
	"countriesL10nMessages.NP": "ਨੇਪਾਲ",
	"countriesL10nMessages.NR": "ਨੌਰੂ",
	"countriesL10nMessages.NU": "ਨਿਉ",
	"countriesL10nMessages.NZ": "ਨਿਊਜ਼ੀਲੈਂਡ",
	"countriesL10nMessages.OM": "ਓਮਾਨ",
	"countriesL10nMessages.PA": "ਪਨਾਮਾ",
	"countriesL10nMessages.PE": "ਪੇਰੂ",
	"countriesL10nMessages.PF": "ਫ੍ਰੈਂਚ ਪੋਲੀਨੇਸ਼ੀਆ",
	"countriesL10nMessages.PG": "ਪਾਪੂਆ ਨਿਊ ਗਿਨੀ",
	"countriesL10nMessages.PH": "ਫਿਲੀਪੀਨਜ਼",
	"countriesL10nMessages.PK": "ਪਾਕਿਸਤਾਨ",
	"countriesL10nMessages.PL": "ਪੋਲੈਂਡ",
	"countriesL10nMessages.PM": "ਸੇਂਟ ਪੀਅਰੇ ਅਤੇ ਮਿਕਲੋਨ",
	"countriesL10nMessages.PR": "ਪੋਰਟੋ ਰੀਕੋ",
	"countriesL10nMessages.PS": "ਫਿਲਸਤੀਨ",
	"countriesL10nMessages.PT": "ਪੁਰਤਗਾਲ",
	"countriesL10nMessages.PW": "ਪਲਾਊ",
	"countriesL10nMessages.PY": "ਪੈਰਾਗੁਏ",
	"countriesL10nMessages.QA": "ਕਤਰ",
	"countriesL10nMessages.RE": "ਰੀਯੂਨੀਅਨ",
	"countriesL10nMessages.RO": "ਰੋਮਾਨੀਆ",
	"countriesL10nMessages.RS": "ਸਰਬੀਆ",
	"countriesL10nMessages.RW": "ਰਵਾਂਡਾ",
	"countriesL10nMessages.SA": "ਸਊਦੀ ਅਰਬ",
	"countriesL10nMessages.SB": "ਸੋਲੋਮਨ ਟਾਪੂ",
	"countriesL10nMessages.SC": "ਸੇਸ਼ੇਲਸ",
	"countriesL10nMessages.SE": "ਸਵੀਡਨ",
	"countriesL10nMessages.SG": "ਸਿੰਗਾਪੁਰ",
	"countriesL10nMessages.SH": "ਸੇਂਟ ਹੇਲੇਨਾ",
	"countriesL10nMessages.SI": "ਸਲੋਵੇਨੀਆ",
	"countriesL10nMessages.SJ": "ਸਵੈਲਬਾਰਡ ਅਤੇ ਜਾਨ ਮਾਯੇਨ",
	"countriesL10nMessages.SK": "ਸਲੋਵਾਕੀਆ",
	"countriesL10nMessages.SL": "ਸੀਅਰਾ ਲਿਓਨ",
	"countriesL10nMessages.SM": "ਸੈਨ ਮਾਰੀਨੋ",
	"countriesL10nMessages.SN": "ਸੇਨੇਗਲ",
	"countriesL10nMessages.SO": "ਸੋਮਾਲੀਆ",
	"countriesL10nMessages.SR": "ਸੂਰੀਨਾਮ",
	"countriesL10nMessages.SS": "ਦੱਖਣੀ ਸੁਡਾਨ",
	"countriesL10nMessages.ST": "ਸਾਓ ਟੋਮ ਅਤੇ ਪ੍ਰਿੰਸੀਪੇ",
	"countriesL10nMessages.SV": "ਅਲ ਸੈਲਵਾਡੋਰ",
	"countriesL10nMessages.SX": "ਸਿੰਟ ਮਾਰਟਨ",
	"countriesL10nMessages.SZ": "ਸਵਾਜ਼ੀਲੈਂਡ",
	"countriesL10nMessages.TA": "ਟ੍ਰਿਸਟਨ ਦਾ ਕੁਨਹਾ",
	"countriesL10nMessages.TC": "ਤੁਰਕਸ ਅਤੇ ਕੈਕੋਸ ਟਾਪੂ",
	"countriesL10nMessages.TD": "ਚਾਡ",
	"countriesL10nMessages.TG": "ਟੋਗੋ",
	"countriesL10nMessages.TH": "ਥਾਈਲੈਂਡ",
	"countriesL10nMessages.TJ": "ਤਾਜਿਕਸਤਾਨ",
	"countriesL10nMessages.TK": "ਟੋਕੇਲਾਉ",
	"countriesL10nMessages.TL": "ਤਿਮੋਰ-ਲੇਸਤੇ",
	"countriesL10nMessages.TM": "ਤੁਰਕਮੇਨਿਸਤਾਨ",
	"countriesL10nMessages.TN": "ਟਿਊਨੀਸ਼ੀਆ",
	"countriesL10nMessages.TO": "ਟੋਂਗਾ",
	"countriesL10nMessages.TR": "ਤੁਰਕੀ",
	"countriesL10nMessages.TT": "ਤ੍ਰਿਨੀਦਾਦ ਅਤੇ ਟੋਬੈਗੋ",
	"countriesL10nMessages.TV": "ਟੁਵਾਲੂ",
	"countriesL10nMessages.TW": "ਤਾਈਵਾਨ",
	"countriesL10nMessages.TZ": "ਤਨਜ਼ਾਨੀਆ",
	"countriesL10nMessages.UA": "ਯੂਕਰੇਨ",
	"countriesL10nMessages.UG": "ਯੂਗਾਂਡਾ",
	"countriesL10nMessages.US": "ਸੰਯੁਕਤ ਰਾਜ",
	"countriesL10nMessages.UY": "ਉਰੂਗਵੇ",
	"countriesL10nMessages.UZ": "ਉਜ਼ਬੇਕਿਸਤਾਨ",
	"countriesL10nMessages.VA": "ਹੋਲੀ ਸੀ (ਵੈਟੀਕਨ ਸਿਟੀ ਸਟੇਟ)",
	"countriesL10nMessages.VC": "ਸੇਂਟ ਵਿਨਸੈਂਟ ਅਤੇ ਗ੍ਰੇਨਾਡਾਈਨਜ਼",
	"countriesL10nMessages.VE": "ਵੈਨੇਜ਼ੁਏਲਾ",
	"countriesL10nMessages.VG": "ਵਰਜਿਨ ਟਾਪੂ, ਬ੍ਰਿਟਿਸ਼",
	"countriesL10nMessages.VI": "ਵਰਜਿਨ ਟਾਪੂ, ਯੂ.ਐੱਸ.",
	"countriesL10nMessages.VN": "ਵੀਅਤਨਾਮ",
	"countriesL10nMessages.VU": "ਵੈਨੂਆਟੂ",
	"countriesL10nMessages.WF": "ਵਾਲਿਸ ਅਤੇ ਫੁਟੁਨਾ",
	"countriesL10nMessages.WS": "ਸਮੋਆ",
	"countriesL10nMessages.XK": "ਕੋਸੋਵੋ",
	"countriesL10nMessages.YE": "ਯਮਨ",
	"countriesL10nMessages.YT": "ਮੇਓਟ",
	"countriesL10nMessages.ZA": "ਦੱਖਣੀ ਅਫਰੀਕਾ",
	"countriesL10nMessages.ZM": "ਜ਼ਾਂਬੀਆ",
	"countriesL10nMessages.ZW": "ਜ਼ਿੰਬਾਬਵੇ",
	"ToggleableEmailField.checkboxLabel": "ਮੈਨੂੰ ਈਮੇਲ ਰਾਹੀਂ ਮੇਰੇ ਜਵਾਬਾਂ ਦੀ ਇੱਕ ਕਾਪੀ ਭੇਜੋ",
	"ToggleableEmailField.emailLabel": "ਆਪਣਾ ਈਮੇਲ ਪਤੇ ਦਾਖਲ ਕਰੋ"
};

var pl = {
	"AddressField.selectCountry": "Wybierz kraj",
	"PhoneField.countryCode": "Kod kraju",
	"countriesL10nMessages.AC": "Wyspa Wniebowstąpienia",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Zjednoczone Emiraty Arabskie",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua i Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentyna",
	"countriesL10nMessages.AS": "Samoa Amerykańskie",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Wyspy Alandzkie",
	"countriesL10nMessages.AZ": "Azerbejdżan",
	"countriesL10nMessages.BA": "Bośnia i Hercegowina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesz",
	"countriesL10nMessages.BE": "Belgia",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bułgaria",
	"countriesL10nMessages.BH": "Bahrajn",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Bermudy",
	"countriesL10nMessages.BN": "Brunei",
	"countriesL10nMessages.BO": "Boliwia",
	"countriesL10nMessages.BQ": "Holandia Karaibska",
	"countriesL10nMessages.BR": "Brazylia",
	"countriesL10nMessages.BS": "Bahamy",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Wyspy Kokosowe (Keelinga)",
	"countriesL10nMessages.CD": "Demokratyczna Republika Konga",
	"countriesL10nMessages.CF": "Republika Środkowoafrykańska",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Szwajcaria",
	"countriesL10nMessages.CI": "Wybrzeże Kości Słoniowej",
	"countriesL10nMessages.CK": "Wyspy Cooka",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Chiny",
	"countriesL10nMessages.CO": "Kolumbia",
	"countriesL10nMessages.CR": "Kostaryka",
	"countriesL10nMessages.CV": "Wyspy Zielonego Przylądka",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Wyspa Bożego Narodzenia",
	"countriesL10nMessages.CY": "Cypr",
	"countriesL10nMessages.CZ": "Czechy",
	"countriesL10nMessages.DE": "Niemcy",
	"countriesL10nMessages.DJ": "Dżibuti",
	"countriesL10nMessages.DK": "Dania",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikana",
	"countriesL10nMessages.DZ": "Algieria",
	"countriesL10nMessages.EC": "Ekwador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egipt",
	"countriesL10nMessages.EH": "Sahara Zachodnia",
	"countriesL10nMessages.ER": "Erytrea",
	"countriesL10nMessages.ES": "Hiszpania",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finlandia",
	"countriesL10nMessages.FJ": "Fidżi",
	"countriesL10nMessages.FK": "Falklandy",
	"countriesL10nMessages.FM": "Mikronezja",
	"countriesL10nMessages.FO": "Wyspy Owcze",
	"countriesL10nMessages.FR": "Francja",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Wielka Brytania",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzja",
	"countriesL10nMessages.GF": "Gujana Francuska",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grenlandia",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Gwinea",
	"countriesL10nMessages.GP": "Gwadelupa",
	"countriesL10nMessages.GQ": "Gwinea Równikowa",
	"countriesL10nMessages.GR": "Grecja",
	"countriesL10nMessages.GT": "Gwatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Gwinea Bissau",
	"countriesL10nMessages.GY": "Gujana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Chorwacja",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Węgry",
	"countriesL10nMessages.ID": "Indonezja",
	"countriesL10nMessages.IE": "Irlandia",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Wyspa Man",
	"countriesL10nMessages.IN": "Indie",
	"countriesL10nMessages.IO": "Brytyjskie Terytorium Oceanu Indyjskiego",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islandia",
	"countriesL10nMessages.IT": "Włochy",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamajka",
	"countriesL10nMessages.JO": "Jordania",
	"countriesL10nMessages.JP": "Japonia",
	"countriesL10nMessages.KE": "Kenia",
	"countriesL10nMessages.KG": "Kirgistan",
	"countriesL10nMessages.KH": "Kambodża",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komory",
	"countriesL10nMessages.KN": "Saint Kitts i Nevis",
	"countriesL10nMessages.KR": "Korea Południowa",
	"countriesL10nMessages.KW": "Kuwejt",
	"countriesL10nMessages.KY": "Kajmany",
	"countriesL10nMessages.KZ": "Kazachstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Liban",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litwa",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Łotwa",
	"countriesL10nMessages.LY": "Libia",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Mołdawia",
	"countriesL10nMessages.ME": "Czarnogóra",
	"countriesL10nMessages.MF": "Saint Martin (część francuska)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Wyspy Marshalla",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mjanma",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Makau",
	"countriesL10nMessages.MP": "Mariany Północne",
	"countriesL10nMessages.MQ": "Martynika",
	"countriesL10nMessages.MR": "Mauretania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Malediwy",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Meksyk",
	"countriesL10nMessages.MY": "Malezja",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Nowa Kaledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Wyspa Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Holandia",
	"countriesL10nMessages.NO": "Norwegia",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nowa Zelandia",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinezja Francuska",
	"countriesL10nMessages.PG": "Papua Nowa Gwinea",
	"countriesL10nMessages.PH": "Filipiny",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polska",
	"countriesL10nMessages.PM": "Saint-Pierre i Miquelon",
	"countriesL10nMessages.PR": "Portoryko",
	"countriesL10nMessages.PS": "Palestyna",
	"countriesL10nMessages.PT": "Portugalia",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragwaj",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Reunion",
	"countriesL10nMessages.RO": "Rumunia",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Arabia Saudyjska",
	"countriesL10nMessages.SB": "Wyspy Salomona",
	"countriesL10nMessages.SC": "Seszele",
	"countriesL10nMessages.SE": "Szwecja",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Wyspa Świętej Heleny",
	"countriesL10nMessages.SI": "Słowenia",
	"countriesL10nMessages.SJ": "Svalbard i Jan Mayen",
	"countriesL10nMessages.SK": "Słowacja",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sudan Południowy",
	"countriesL10nMessages.ST": "Wyspy Świętego Tomasza i Książęca",
	"countriesL10nMessages.SV": "Salwador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Suazi",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Wyspy Turks i Caicos",
	"countriesL10nMessages.TD": "Czad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tajlandia",
	"countriesL10nMessages.TJ": "Tadżykistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor Wschodni",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunezja",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turcja",
	"countriesL10nMessages.TT": "Trynidad i Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Stany Zjednoczone",
	"countriesL10nMessages.UY": "Urugwaj",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Stolica Apostolska (Watykan)",
	"countriesL10nMessages.VC": "Saint Vincent i Grenadyny",
	"countriesL10nMessages.VE": "Wenezuela",
	"countriesL10nMessages.VG": "Brytyjskie Wyspy Dziewicze",
	"countriesL10nMessages.VI": "Wyspy Dziewicze Stanów Zjednoczonych",
	"countriesL10nMessages.VN": "Wietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis i Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosowo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Majotta",
	"countriesL10nMessages.ZA": "Republika Południowej Afryki",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Prześlij mi kopię odpowiedzi za pośrednictwem poczty e-mail",
	"ToggleableEmailField.emailLabel": "Wprowadź adres e-mail"
};

var pt = {
	"AddressField.selectCountry": "Selecionar país",
	"PhoneField.countryCode": "Código de país",
	"countriesL10nMessages.AC": "Ilha da Ascensão",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emirados Árabes Unidos",
	"countriesL10nMessages.AF": "Afeganistão",
	"countriesL10nMessages.AG": "Antígua e Barbuda",
	"countriesL10nMessages.AI": "Anguila",
	"countriesL10nMessages.AL": "Albânia",
	"countriesL10nMessages.AM": "Arménia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Americana",
	"countriesL10nMessages.AT": "Áustria",
	"countriesL10nMessages.AU": "Austrália",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ilhas Alanda",
	"countriesL10nMessages.AZ": "Azerbaijão",
	"countriesL10nMessages.BA": "Bósnia-Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladexe",
	"countriesL10nMessages.BE": "Bélgica",
	"countriesL10nMessages.BF": "Burquina Fasso",
	"countriesL10nMessages.BG": "Bulgária",
	"countriesL10nMessages.BH": "Barém",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benim",
	"countriesL10nMessages.BL": "São Bartolomeu",
	"countriesL10nMessages.BM": "Bermudas",
	"countriesL10nMessages.BN": "Brunei",
	"countriesL10nMessages.BO": "Bolívia",
	"countriesL10nMessages.BQ": "Bonaire, Santo Eustáquio e Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Baamas",
	"countriesL10nMessages.BT": "Butão",
	"countriesL10nMessages.BW": "Botsuana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canadá",
	"countriesL10nMessages.CC": "Ilhas dos Cocos",
	"countriesL10nMessages.CD": "Congo, República Democrática do",
	"countriesL10nMessages.CF": "República Centro-Africana",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Suíça",
	"countriesL10nMessages.CI": "Costa do Marfim",
	"countriesL10nMessages.CK": "Ilhas Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Camarões",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colômbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cabo Verde",
	"countriesL10nMessages.CW": "Curaçau",
	"countriesL10nMessages.CX": "Ilha do Natal",
	"countriesL10nMessages.CY": "Chipre",
	"countriesL10nMessages.CZ": "República Checa",
	"countriesL10nMessages.DE": "Alemanha",
	"countriesL10nMessages.DJ": "Jibuti",
	"countriesL10nMessages.DK": "Dinamarca",
	"countriesL10nMessages.DM": "Domínica",
	"countriesL10nMessages.DO": "República Dominicana",
	"countriesL10nMessages.DZ": "Argélia",
	"countriesL10nMessages.EC": "Equador",
	"countriesL10nMessages.EE": "Estónia",
	"countriesL10nMessages.EG": "Egito",
	"countriesL10nMessages.EH": "Sara Ocidental",
	"countriesL10nMessages.ER": "Eritreia",
	"countriesL10nMessages.ES": "Espanha",
	"countriesL10nMessages.ET": "Etiópia",
	"countriesL10nMessages.FI": "Finlândia",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Ilhas Falkland",
	"countriesL10nMessages.FM": "Estados Federados da Micronésia",
	"countriesL10nMessages.FO": "Ilhas Faroé",
	"countriesL10nMessages.FR": "França",
	"countriesL10nMessages.GA": "Gabão",
	"countriesL10nMessages.GB": "Reino Unido",
	"countriesL10nMessages.GD": "Granada",
	"countriesL10nMessages.GE": "Geórgia",
	"countriesL10nMessages.GF": "Guiana Francesa",
	"countriesL10nMessages.GG": "Guernesey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Gronelândia",
	"countriesL10nMessages.GM": "Gâmbia",
	"countriesL10nMessages.GN": "Guiné",
	"countriesL10nMessages.GP": "Guadalupe",
	"countriesL10nMessages.GQ": "Guiné Equatorial",
	"countriesL10nMessages.GR": "Grécia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guame",
	"countriesL10nMessages.GW": "Guiné-Bissau",
	"countriesL10nMessages.GY": "Guiana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croácia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungria",
	"countriesL10nMessages.ID": "Indonésia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Ilha de Man",
	"countriesL10nMessages.IN": "Índia",
	"countriesL10nMessages.IO": "Território Britânico do Oceano Índico",
	"countriesL10nMessages.IQ": "Iraque",
	"countriesL10nMessages.IS": "Islândia",
	"countriesL10nMessages.IT": "Itália",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordânia",
	"countriesL10nMessages.JP": "Japão",
	"countriesL10nMessages.KE": "Quénia",
	"countriesL10nMessages.KG": "Quirguistão",
	"countriesL10nMessages.KH": "Camboja",
	"countriesL10nMessages.KI": "Quiribáti",
	"countriesL10nMessages.KM": "Comores",
	"countriesL10nMessages.KN": "São Cristóvão e Neves",
	"countriesL10nMessages.KR": "Coreia do Sul",
	"countriesL10nMessages.KW": "Koweit",
	"countriesL10nMessages.KY": "Ilhas Caimão",
	"countriesL10nMessages.KZ": "Cazaquistão",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Líbano",
	"countriesL10nMessages.LC": "Santa Lúcia",
	"countriesL10nMessages.LI": "Listenstaine",
	"countriesL10nMessages.LK": "Seri Lanca",
	"countriesL10nMessages.LR": "Libéria",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Lituânia",
	"countriesL10nMessages.LU": "Luxemburgo",
	"countriesL10nMessages.LV": "Letónia",
	"countriesL10nMessages.LY": "Líbia",
	"countriesL10nMessages.MA": "Marrocos",
	"countriesL10nMessages.MC": "Mónaco",
	"countriesL10nMessages.MD": "Moldávia",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "São Martinho (parte francesa)",
	"countriesL10nMessages.MG": "Madagáscar",
	"countriesL10nMessages.MH": "Ilhas Marshall",
	"countriesL10nMessages.MK": "Macedónia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mianmar",
	"countriesL10nMessages.MN": "Mongólia",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Ilhas Marianas do Norte",
	"countriesL10nMessages.MQ": "Martinica",
	"countriesL10nMessages.MR": "Mauritânia",
	"countriesL10nMessages.MS": "Monserrate",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurícia",
	"countriesL10nMessages.MV": "Maldivas",
	"countriesL10nMessages.MW": "Maláui",
	"countriesL10nMessages.MX": "México",
	"countriesL10nMessages.MY": "Malásia",
	"countriesL10nMessages.MZ": "Moçambique",
	"countriesL10nMessages.NA": "Namíbia",
	"countriesL10nMessages.NC": "Nova Caledónia",
	"countriesL10nMessages.NE": "Níger",
	"countriesL10nMessages.NF": "Ilha Norfolk",
	"countriesL10nMessages.NG": "Nigéria",
	"countriesL10nMessages.NI": "Nicarágua",
	"countriesL10nMessages.NL": "Países Baixos",
	"countriesL10nMessages.NO": "Noruega",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niuê",
	"countriesL10nMessages.NZ": "Nova Zelândia",
	"countriesL10nMessages.OM": "Omã",
	"countriesL10nMessages.PA": "Panamá",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinésia Francesa",
	"countriesL10nMessages.PG": "Papua-Nova Guiné",
	"countriesL10nMessages.PH": "Filipinas",
	"countriesL10nMessages.PK": "Paquistão",
	"countriesL10nMessages.PL": "Polónia",
	"countriesL10nMessages.PM": "São Pedro e Miquelão",
	"countriesL10nMessages.PR": "Porto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguai",
	"countriesL10nMessages.QA": "Catar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Roménia",
	"countriesL10nMessages.RS": "Sérvia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Arábia Saudita",
	"countriesL10nMessages.SB": "Ilhas Salomão",
	"countriesL10nMessages.SC": "Seicheles",
	"countriesL10nMessages.SE": "Suécia",
	"countriesL10nMessages.SG": "Singapura",
	"countriesL10nMessages.SH": "Santa Helena",
	"countriesL10nMessages.SI": "Eslovénia",
	"countriesL10nMessages.SJ": "Svalbard e Jan Mayen",
	"countriesL10nMessages.SK": "Eslováquia",
	"countriesL10nMessages.SL": "Serra Leoa",
	"countriesL10nMessages.SM": "São Marinho",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somália",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Sudão do Sul",
	"countriesL10nMessages.ST": "São Tomé e Príncipe",
	"countriesL10nMessages.SV": "Salvador",
	"countriesL10nMessages.SX": "São Martinho",
	"countriesL10nMessages.SZ": "Suazilândia",
	"countriesL10nMessages.TA": "Tristão da Cunha",
	"countriesL10nMessages.TC": "Ilhas Turcas e Caicos",
	"countriesL10nMessages.TD": "Chade",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tailândia",
	"countriesL10nMessages.TJ": "Tajiquistão",
	"countriesL10nMessages.TK": "Toquelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turquemenistão",
	"countriesL10nMessages.TN": "Tunísia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turquia",
	"countriesL10nMessages.TT": "Trindade e Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzânia",
	"countriesL10nMessages.UA": "Ucrânia",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Estados Unidos",
	"countriesL10nMessages.UY": "Uruguai",
	"countriesL10nMessages.UZ": "Usbequistão",
	"countriesL10nMessages.VA": "Santa Sé (Estado da Cidade do Vaticano)",
	"countriesL10nMessages.VC": "São Vicente e Granadinas",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Ilhas Virgens Britânicas",
	"countriesL10nMessages.VI": "Ilhas Virgens Americanas",
	"countriesL10nMessages.VN": "Vietname",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis e Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Cossovo",
	"countriesL10nMessages.YE": "Iémen",
	"countriesL10nMessages.YT": "Maiote",
	"countriesL10nMessages.ZA": "África do Sul",
	"countriesL10nMessages.ZM": "Zâmbia",
	"countriesL10nMessages.ZW": "Zimbabué",
	"ToggleableEmailField.checkboxLabel": "Enviem-me uma cópia das minhas respostas por correio eletrónico",
	"ToggleableEmailField.emailLabel": "Introduza o seu endereço de correio eletrónico"
};

var ptBr = {
	"ActionMenuTagPin.PIN": "Fixar",
	"ActionMenuTagPin.TAG": "Tag",
	"AddressField.selectCountry": "Selecione o país",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Respondidas: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "P{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Ignoradas: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Como as pessoas se sentem:",
	"AsyncJobLoadingState.LOADING": "Carregando respostas",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Avisaremos quando estiver concluído",
	"AsyncJobLoadingState.TAGGING_TITLE": "Marcando as respostas",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "As tags se aplicam às respostas atuais e futuras. Deixe um espaço ou digite <strong>OR</strong> entre as palavras para usar mais de uma palavra-chave.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Marcar respostas com tags automaticamente",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Selecione um mês",
	"CalendarPicker.months": "{months, select, January {Janeiro} February {Fevereiro} March {Março} April {Abril} May {Maio} June {Junho} July {Julho} August {Agosto} September {Setembro} October {Outubro} November {Novembro} December {Dezembro} other {outro}}",
	"CalendarPicker.nextMonth": "Próximo mês",
	"CalendarPicker.previousMonth": "Mês anterior",
	"CalendarPicker.year": "Selecione um ano",
	"ColorSwatches.TAG": "Tag {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Nuvens de palavras coloridas usam diferentes cores e tamanhos para indicar o número de menções de uma palavra ou frase.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Como as nuvens de palavras usam cores",
	"ColorTab.DOWN_ARROW": "Selecione a seta do menu suspenso",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Várias cores",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Uma cor",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Cancelar",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Crie uma palavra",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "A palavra nova substitui cada uma das palavras selecionadas abaixo.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Editar palavras combinadas",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Combinar palavras",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Crie uma palavra para substituir uma ou mais palavras selecionadas. Você pode voltar às palavras originais a qualquer momento.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Digite uma palavra nova",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Combinar",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Escolha que palavras combinar",
	"CreateTagFromSearch.CLOSE_MENU": "CANCELAR",
	"CreateTagFromSearch.DUPLICATED_NAME": "Já existe uma tag com esse nome.",
	"CreateTagFromSearch.NEW_TAG": "Nova tag",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Nenhuma resposta\n\t\tserá marcada com tag} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} resposta\n\t\t\t\n\t\tserá marcada com tag} other {\n\t\t\t\t{responseTaggedFormatted} respostas\n\t\t\t\n\t\tserão marcadas com tag}}}}",
	"CreateTagFromSearch.SAVE": "SALVAR",
	"CreateTagFromSearch.TAG": "Tag",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "O nome da tag não pode estar vazio.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "O nome da tag não pode conter somente espaços.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Marque as respostas que correspondem a essas palavras-chave com uma tag. Selecione <strong>Criar tag</strong>. Escolha uma cor e salve. A tag será aplicada às respostas atuais e futuras.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Marque as respostas correspondentes com uma tag",
	"DashboardMenuContent.CREATE": "CRIAR",
	"DashboardMenuContent.NAME": "Nome",
	"DashboardMenuContent.NEW_DASHBOARD": "Novo painel",
	"DateInputWrapper.dateButton": "Abrir calendário",
	"DateInputWrapper.dateFormatIntl": "DD/MM/AAAA",
	"DateInputWrapper.dateFormatUs": "MM/DD/AAAA",
	"DateInputWrapper.dateLabel": "Data",
	"DateTime.errorEnterHours": "Informe a hora no formato de 0 a 23.",
	"DateTime.errorEnterMinutes": "Informe os minutos de 0 a 59.",
	"DateTime.errorSelectPeriod": "Selecione “am” ou “pm”.",
	"DateTime.errorYearOutOfRange": "Forneça um ano entre 1900 e 9999.",
	"DateTime.formatErrorDateIntl": "Informe a data no formato DD/MM/AAAA.",
	"DateTime.formatErrorDateUs": "Informe a data no formato MM/DD/AAAA.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "CANCELAR",
	"EditorButtons.COPY_BUTTON": "COPIAR PERGUNTA",
	"EditorButtons.MOVE_BUTTON": "MOVER PERGUNTA",
	"EditorButtons.NEXT_QUESTION_BUTTON": "PRÓXIMA PERGUNTA",
	"EditorButtons.SAVE_BUTTON": "SALVAR",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "As Tendências dos dados não se aplicam a esta pergunta",
	"FallbackComponent.ERROR": "Erro ao carregar pergunta",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Escolha um arquivo</div> <div class={dragClass}>ou arraste-o até aqui</div>",
	"FileUpload.fileUploaded": "O arquivo foi carregado.",
	"FileUpload.fileUploading": "Carregando arquivo",
	"FileUpload.removeFile": "Remover arquivo",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Substitua o arquivo</div> <div class={dragClass}>ou arraste-o até aqui</div>",
	"FileUpload.supportedFiles": "Arquivos aceitos",
	"FilterMenuContent.APPLY_ACTIONS": "APLICAR",
	"FilterMenuContent.CLEAR_ACTIONS": "Apagar",
	"FilterMenuContent.CLOSE_MENU": "CANCELAR",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Cancelar",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Ocultar",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Salvar",
	"HideWordsModal.HIDE_WORDS_HEADER": "Ocultar palavras",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Selecione palavras para ocultar da nuvem de palavras.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Selecione palavras para ocultar",
	"IconHelpStyled.HELP": "Ajuda",
	"IndividualResponseCard.NO_RESPONSE": "O respondente ignorou esta pergunta",
	"IndividualResponseCard.QUESTION_NUMBER": "P{number}",
	"InfoBannerTagManagement.CLOSE": "Encerrar",
	"InfoBannerTagManagement.NEW": "NOVO",
	"InfoBannerTagManagement.TAG_RESPONSES": "Marcar respostas",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Adicione palavras-chave para configurar tags automáticas e manter as respostas organizadas.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Marque as respostas com tags para facilitar o gerenciamento",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positivos: {percentage,number,percent}} negative {Negativos: {percentage,number,percent}} neutral {Neutros: {percentage,number,percent}} unknown {Não detectados: {percentage,number,percent}} other {outro}}",
	"LoadingState.LOADING": "Carregando respostas",
	"MatrixDropdown.EmptyTopNativeSelect": "Selecione uma opção",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Intervalo de confiança",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Pontuação de utilidade",
	"Modal.CloseModal": "Fechar dica",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Segunda} Tu {Terça} We {Quarta} Th {Quinta} Fr {Sexta} Sa {Sábado} Su {Domingo} other {outro}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Seg} Tu {Ter} We {Qua} Th {Qui} Fr {Sex} Sa {Sáb} Su {Dom} other {outro}}",
	"MultiColorSelectorMenu.APPLY": "Aplicar",
	"MultiColorSelectorMenu.CANCEL": "Cancelar",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Organize seus dados em um layout personalizado, explique suas descobertas e compartilhe seu painel com qualquer pessoa. Organize seus dados em um layout personalizado, explique suas descobertas e compartilhe seu painel com qualquer pessoa.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Painéis",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Saiba mais",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">Em uma escala de {minValue} a {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} para {minLabelText}, {maxValue} para {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "zona de avaliação de pontuações entre {min} e {max}",
	"NpsGaugeChart.NPS_CHART": "Gráfico de avaliação do NPS",
	"NpsGaugeChart.NPS_SCORE": "Pontuação do NPS de {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "marcação na posição da pontuação {score}",
	"OrderControl.AriaNA": "Não se aplica",
	"OrderControl.NA": "N/A",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "“{title}” responses ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "ocultar lista de respostas da opção “outro”",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "mostrar lista de respostas da opção “outro”",
	"PhoneField.countryCode": "Código do país",
	"QuestionCardItemFooter.FILTER_APPLIED": "Regras aplicadas: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtrar",
	"QuestionCardTitle.ANSWERED": "Respondidas: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Selecionadas: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Ignoradas: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} ponto} other {{score}/{total} pontos}}",
	"QuizResultScore.SKIPPED": "Ignoradas",
	"RangeTab.RANGE_TAB_LABEL": "Menções: {value}",
	"RangeTab.TOTAL_WORDS": "total de palavras",
	"Ranking.AriaRankAtBottom": "{label} foi movida para o fim da lista",
	"Ranking.AriaRankAtTop": "{label} foi movida para o início da lista",
	"Ranking.AriaRankMovedDown": "{label} foi movida para baixo, no número {position}",
	"Ranking.AriaRankMovedUp": "{label} foi movida para cima, no número {position}",
	"RankingItem.AriaRankMoveDown": "Mover {label} para baixo",
	"RankingItem.AriaRankMoveUp": "Mover {label} para cima",
	"RatingChart.AVERAGE_RATING": "Avaliação média",
	"RatingChartLegend.ANSWERED": "Respondidas: {number}",
	"RatingChartLegend.RATING": "{rating} estrela",
	"RatingChartLegend.SKIPPED": "Ignoradas: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Solicitar recurso",
	"RequestAccessButton.UPGRADE": "Upgrade",
	"RespondentQuestionTitle.REQUIRED": "(Obrigatório)",
	"ResponseItem.EXPAND_MENU_LABEL": "Expandir menu",
	"ResponseItem.MENU_LABEL": "Adicionar tags",
	"ResponseItem.RESPONDENT_ID": "ID de respondente",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Ver respostas dos respondentes",
	"ResponseStatusBar.CLEAR": "Apagar",
	"ResponseStatusBar.FAILED_TO_LOAD": "Erro ao carregar respostas",
	"ResponseStatusBar.NO_RESPONSES_YET": "Ainda não há respostas",
	"ResponseStatusBar.SEARCHING": "Procurando...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {sentimento positivo} negative {sentimento negativo} neutral {sentimento neutro} other {sentimento desconhecido}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} resposta selecionada para {searchTextFormatted} categorizada como {filters}   } other {Mostrando {resultsCountFormatted} resposta para {searchTextFormatted} categorizada como {filters}   }}} other {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} respostas selecionadas para {searchTextFormatted} categorizadas como {filters}   } other {Mostrando {resultsCountFormatted} respostas para {searchTextFormatted} categorizadas como {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} resposta selecionada categorizada como {filters}   } other {Mostrando {resultsCountFormatted} resposta categorizada como {filters}   }}} other {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} respostas selecionadas categorizadas como {filters}   } other {Mostrando {resultsCountFormatted} respostas categorizadas como {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} resposta selecionada para {searchTextFormatted}   } other {Mostrando {resultsCountFormatted} resposta para {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} respostas selecionadas para {searchTextFormatted}   } other {Mostrando {resultsCountFormatted} respostas para {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} resposta selecionada   } other {Mostrando {resultsCountFormatted} resposta   }}} other {{hasSelectedResponse, select, true {Mostrando {resultsCountFormatted} respostas selecionadas   } other {Mostrando {resultsCountFormatted} respostas   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Apagar",
	"ResponseToolbar.CREATE_TAG": "Criar tag",
	"ResponseToolbar.SELECT_ALL": "Selecionar todas as respostas",
	"ResponseToolbar.UNSELECT_ALL": "Desmarcar todas as respostas",
	"SearchResponsesInput.CLEAR": "Apagar busca",
	"SearchResponsesInput.SEARCH": "Procurar",
	"SearchResponsesInput.SEARCH_RESPONSES": "Pesquisar respostas",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Use aspas <strong>“”</strong> para pesquisar uma frase exata.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Digite <strong>OR</strong> para encontrar um de dois ou mais termos de busca.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Digite <b>AND</b> para encontrar mais de um termo.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Use <strong>NOT</strong> para excluir um termo da sua busca.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Digite <strong>*</strong> após um termo para procurar por prefixo.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Use parêntesis para combinar buscas AND/OR/NOT.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Insira <strong>~2</strong> após um termo para buscar resultados correspondentes ao termo em questão com até dois outros caracteres.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Opções de busca",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Saiba mais",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Não, obrigado",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Habilitar",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "Os <span id=\"link\">Insights automáticos</span> já estão disponíveis na sua conta.\n      A configuração inclui qualquer recurso que use aprendizado de máquina para processar os resultados do questionário e proporciona insights a você.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Você pode mudar suas preferências a qualquer momento em <span id=\"link\">Minha conta</span>.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Insights automáticos",
	"SentimentBanner.GENERATE_SENTIMENT": "Analisar sentimento",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Análise de sentimento",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Identifique a opinião por trás de respostas dissertativas para entender como as pessoas se sentem.",
	"SentimentBanner.UPGRADE": "Upgrade",
	"SentimentBanner.WATCH_A_DEMO": "Assistir a uma demonstração",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Como as pessoas se sentem:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtrar por sentimento positivo} negative {Filtrar por sentimento negativo} neutral {Filtrar por sentimento neutro} other {Filtrar por sentimento desconhecido}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Expandir menu",
	"SentimentFilterMenu.MENU_LABEL": "por sentimento",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Solicitar recurso",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Identifique o sentimento por trás de cada resposta dissertativa.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Novo!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Apresentamos a análise de sentimento",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Upgrade",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Assistir a uma demonstração",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Agora não",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Solicitar recurso",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Upgrade",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Imagem do upgrade de sentimentos",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Habilite a análise de sentimento com apenas um clique. Classificaremos suas respostas de texto como positivas, neutras ou negativas para revelar a emoção das pessoas por trás de suas afirmações.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Saiba de imediato o que as pessoas estão sentindo",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Análise de sentimento",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Sentimentos: habilitado} false {Sentimentos: desabilitado} other {Sentimentos: outro}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Sentimentos: CARREGANDO...",
	"SlideBoxNavigation.NEXT_BUTTON": "SEGUINTE",
	"SlideBoxNavigation.NEXT_SLIDE": "Próximo slide",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "ANTERIOR",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Slide anterior",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} de {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Apagar",
	"Slider.STEP_MESSAGE": "Ajustamos o número inserido com base na escala da barra deslizante.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Respondidas: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "P{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Ignoradas: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "A interseção entre caro e barato resulta em uma direção geral de preço para seu produto.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Sensibilidade de preço (Van Westendorp)",
	"SrText.CORRECT": "Corretas",
	"SrText.INCORRECT": "Incorretas",
	"StarRating.Heart": "{count, plural, one {{count} coração} other {{count} corações}}",
	"StarRating.STAR": "{count, plural, one {{count} estrela} other {{count} estrelas}}",
	"StarRating.Smiley": "{count, plural, one {{count} sorriso} other {{count} sorrisos}}",
	"StarRating.Thumb": "{count, plural, one {{count} joia} other {{count} joias}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Ícone de bloqueio da guia das tags",
	"TabList.RESPONSES": "RESPOSTAS ({responseCount})",
	"TabList.TAGS": "TAGS ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "ANÁLISE TEMÁTICA",
	"TabList.WORD_CLOUD": "NUVEM DE PALAVRAS",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Expandir menu",
	"TagFilterMenu.MENU_LABEL": "Filtrar: por tag",
	"TagList.TAGS": "Tags",
	"TagList.TAGS_KEYWORDS": "Palavras-chave",
	"TagList.TAG_CONTROLS": "Controles de tags",
	"TagList.TAG_NUMBER_PERCENT": "Porcentagem de tags e número de tags",
	"TagList.TAG_PERCENTAGE_BAR": "Barra de porcentagem de tags",
	"TagListItem.DELETE": "Excluir",
	"TagListItem.EDIT": "Editar",
	"TagListItem.VIEW_ALL": "Exibir tudo",
	"TagListPanel.LOADING": "Carregando tags",
	"TagListPanel.NEW_TAG": "Adicionar tag",
	"TagListPanel.TAGS": "Tags",
	"TagMenuContent.APPLY_ACTIONS": "APLICAR",
	"TagMenuContent.CLEAR_ACTIONS": "Apagar",
	"TagMenuContent.CLOSE_MENU": "CANCELAR",
	"TagMenuContent.DUPLICATED_NAME": "Já existe uma tag com esse nome.",
	"TagMenuContent.NEW_TAG": "Nova tag",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Saiba mais >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Solicitar recurso",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Upgrade",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Encerrar",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Pesquise, marque e filtre respostas por extenso e veja uma nuvem de palavras com as palavras e frases mais frequentes.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Análise de texto",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "hh",
	"TimeInputWrapper.hour": "Hora",
	"TimeInputWrapper.minutes": "minutos",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Hora",
	"ToggleableEmailField.checkboxLabel": "Envie-me uma cópia das respostas por email",
	"ToggleableEmailField.emailLabel": "Digite seu endereço de email",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Excluir",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {As tags de {responseCountFormatted} resposta serão removidas.} other {As tags de {responseCountFormatted} respostaspostas serão removidas.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Se excluir uma tag, ela será removida de todas as respostas com tag, e isso não poderá ser desfeito.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Excluir tag?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Salvar",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Salvar alterações?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Isso remove a tag de todas as respostas. A tag é reaplicada às respostas que \n      correspondem com as palavras-chave editadas.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Cancelar",
	"UpgradeBanner.SUBTITLE": "Faça upgrade para personalizar sua nuvem de palavras.",
	"UpgradeBanner.TITLE": "Gostou?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% de respostas",
	"VanWestendorpChartDataTable.PRICE": "Preço",
	"VanWestendorpChartDataTable.RESPONSES": "Respostas",
	"WordCloudCustomizationButton.CUSTOMIZE": "Personalizar",
	"WordCloudCustomizationFooter.APPLY": "Aplicar",
	"WordCloudCustomizationFooter.CANCEL": "Cancelar",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Aplicar a todas as nuvens de palavras do questionário (nem todas serão alteradas)",
	"WordCloudCustomizationFooter.NO_THANKS": "Agora não",
	"WordCloudCustomizationFooter.REQUEST": "Solicitar recurso",
	"WordCloudCustomizationFooter.SAVE": "Salvar",
	"WordCloudCustomizationFooter.UPGRADE": "Upgrade",
	"WordCloudCustomizationTabs.COLOR": "COR",
	"WordCloudCustomizationTabs.FONT": "FONTE",
	"WordCloudCustomizationTabs.RANGE": "INTERVALO",
	"WordCloudLegend.FEWER": "menos",
	"WordCloudLegend.MORE": "mais",
	"WordCloudLegend.TITLE": "Número de menções",
	"WordCloudListFooter.COMBINED_WORDS": "Palavras combinadas",
	"WordCloudListFooter.HIDDEN_WORDS": "Palavras ocultas",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Expandir menu",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Combinar com",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Editar palavras combinadas",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Expandir menu",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Ocultar da nuvem de palavras",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Separar palavras combinadas",
	"WordCloudLoadingState.LOADING": "Carregando nuvem de palavras...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Botão Fechar",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Conheça os recursos de personalização de nuvens de palavras.",
	"WordCloudPreviewOverlay.TITLE": "Faça o teste.",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Exibição em nuvem",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Exibição em lista",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Ocorrências: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "A {number}ª palavra mais importante",
	"WordCount.IN_PROGRESS": "Análise de texto em andamento...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Nenhuma palavra importante encontrada.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Mostrando {count} palavra } other {Mostrando {count} palavras }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtrar por sentimento positivo} negative {Filtrar por sentimento negativo} neutral {Filtrar por sentimento neutro} other {Filtrar por sentimento não detectado}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positivo} negative {Negativo} neutral {Neutro(a)} other {Desconhecido}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positivos: {percentage,number,percent}} negative {Negativos: {percentage,number,percent}} neutral {Neutros: {percentage,number,percent}} other {Desconhecido: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "CANCELAR",
	"copyTagEditor.DUPLICATED_NAME": "Já existe uma tag com esse nome.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Dê um nome para essa tag",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Insira uma ou mais palavras-chave",
	"copyTagEditor.KEYWORDS": "<strong>Palavras-chave</strong> (opcional)",
	"copyTagEditor.SAVE": "SALVAR",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "A tag se aplica a todas as respostas com palavras-chave correspondentes.",
	"copyTagEditor.TAG_NAME": "<strong>Nome da tag</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "O nome da tag não pode estar vazio.",
	"copyTagEditor.TAG_NAME_SPACES": "O nome da tag não pode conter somente espaços.",
	"copyTagEditor.TAG_TOO_LONG": "A tag deve ter menos de 128 caracteres.",
	"countriesL10nMessages.AC": "Ilha de Ascensão",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emirados Árabes Unidos",
	"countriesL10nMessages.AF": "Afeganistão",
	"countriesL10nMessages.AG": "Antígua e Barbuda",
	"countriesL10nMessages.AI": "Anguila",
	"countriesL10nMessages.AL": "Albânia",
	"countriesL10nMessages.AM": "Armênia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Americana",
	"countriesL10nMessages.AT": "Áustria",
	"countriesL10nMessages.AU": "Austrália",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ilhas Åland",
	"countriesL10nMessages.AZ": "Azerbaijão",
	"countriesL10nMessages.BA": "Bósnia e Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Bélgica",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgária",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolívia",
	"countriesL10nMessages.BQ": "Bonaire, Santo Eustáquio e Saba",
	"countriesL10nMessages.BR": "Brasil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Butão",
	"countriesL10nMessages.BW": "Botsuana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canadá",
	"countriesL10nMessages.CC": "Ilhas Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, República Democrática do",
	"countriesL10nMessages.CF": "República Centro-Africana",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Suíça",
	"countriesL10nMessages.CI": "Costa do Marfim",
	"countriesL10nMessages.CK": "Ilhas Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Camarões",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colômbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cabo Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Ilhas Christmas",
	"countriesL10nMessages.CY": "Chipre",
	"countriesL10nMessages.CZ": "República Tcheca",
	"countriesL10nMessages.DE": "Alemanha",
	"countriesL10nMessages.DJ": "Djibuti",
	"countriesL10nMessages.DK": "Dinamarca",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "República Dominicana",
	"countriesL10nMessages.DZ": "Argélia",
	"countriesL10nMessages.EC": "Equador",
	"countriesL10nMessages.EE": "Estônia",
	"countriesL10nMessages.EG": "Egito",
	"countriesL10nMessages.EH": "Saara Ocidental",
	"countriesL10nMessages.ER": "Eritreia",
	"countriesL10nMessages.ES": "Espanha",
	"countriesL10nMessages.ET": "Etiópia",
	"countriesL10nMessages.FI": "Finlândia",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Ilhas Falkland",
	"countriesL10nMessages.FM": "Estados Federados da Micronésia",
	"countriesL10nMessages.FO": "Ilhas Faroés",
	"countriesL10nMessages.FR": "França",
	"countriesL10nMessages.GA": "Gabão",
	"countriesL10nMessages.GB": "Reino Unido",
	"countriesL10nMessages.GD": "Granada",
	"countriesL10nMessages.GE": "Geórgia",
	"countriesL10nMessages.GF": "Guiana Francesa",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenlândia",
	"countriesL10nMessages.GM": "Gâmbia",
	"countriesL10nMessages.GN": "Guiné",
	"countriesL10nMessages.GP": "Guadalupe",
	"countriesL10nMessages.GQ": "Guiné Equatorial",
	"countriesL10nMessages.GR": "Grécia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guiné-Bissau",
	"countriesL10nMessages.GY": "Guiana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croácia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungria",
	"countriesL10nMessages.ID": "Indonésia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Ilha de Man",
	"countriesL10nMessages.IN": "Índia",
	"countriesL10nMessages.IO": "Território Britânico do Oceano Índico",
	"countriesL10nMessages.IQ": "Iraque",
	"countriesL10nMessages.IS": "Islândia",
	"countriesL10nMessages.IT": "Itália",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordânia",
	"countriesL10nMessages.JP": "Japão",
	"countriesL10nMessages.KE": "Quênia",
	"countriesL10nMessages.KG": "Quirguistão",
	"countriesL10nMessages.KH": "Camboja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comores",
	"countriesL10nMessages.KN": "São Cristóvão e Névis",
	"countriesL10nMessages.KR": "Coreia do Sul",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Ilhas Cayman",
	"countriesL10nMessages.KZ": "Cazaquistão",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Líbano",
	"countriesL10nMessages.LC": "Santa Lúcia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Libéria",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Lituânia",
	"countriesL10nMessages.LU": "Luxemburgo",
	"countriesL10nMessages.LV": "Letônia",
	"countriesL10nMessages.LY": "Líbia",
	"countriesL10nMessages.MA": "Marrocos",
	"countriesL10nMessages.MC": "Mônaco",
	"countriesL10nMessages.MD": "Moldávia",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "São Martinho (parte francesa)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Ilhas Marshall",
	"countriesL10nMessages.MK": "Macedônia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongólia",
	"countriesL10nMessages.MO": "Macau",
	"countriesL10nMessages.MP": "Ilhas Marianas do Norte",
	"countriesL10nMessages.MQ": "Martinica",
	"countriesL10nMessages.MR": "Mauritânia",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurício",
	"countriesL10nMessages.MV": "Maldivas",
	"countriesL10nMessages.MW": "Malaui",
	"countriesL10nMessages.MX": "México",
	"countriesL10nMessages.MY": "Malásia",
	"countriesL10nMessages.MZ": "Moçambique",
	"countriesL10nMessages.NA": "Namíbia",
	"countriesL10nMessages.NC": "Nova Caledônia",
	"countriesL10nMessages.NE": "Níger",
	"countriesL10nMessages.NF": "Ilha Norfolk",
	"countriesL10nMessages.NG": "Nigéria",
	"countriesL10nMessages.NI": "Nicarágua",
	"countriesL10nMessages.NL": "Países Baixos",
	"countriesL10nMessages.NO": "Noruega",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nova Zelândia",
	"countriesL10nMessages.OM": "Omã",
	"countriesL10nMessages.PA": "Panamá",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinésia Francesa",
	"countriesL10nMessages.PG": "Papua Nova Guiné",
	"countriesL10nMessages.PH": "Filipinas",
	"countriesL10nMessages.PK": "Paquistão",
	"countriesL10nMessages.PL": "Polônia",
	"countriesL10nMessages.PM": "Saint-Pierre e Miquelon",
	"countriesL10nMessages.PR": "Porto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguai",
	"countriesL10nMessages.QA": "Catar",
	"countriesL10nMessages.RE": "Reunião",
	"countriesL10nMessages.RO": "Romênia",
	"countriesL10nMessages.RS": "Sérvia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Arábia Saudita",
	"countriesL10nMessages.SB": "Ilhas Salomão",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Suécia",
	"countriesL10nMessages.SG": "Singapura",
	"countriesL10nMessages.SH": "Santa Helena",
	"countriesL10nMessages.SI": "Eslovênia",
	"countriesL10nMessages.SJ": "Svalbard e Jan Mayen",
	"countriesL10nMessages.SK": "Eslováquia",
	"countriesL10nMessages.SL": "Serra Leoa",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somália",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Sudão do Sul",
	"countriesL10nMessages.ST": "São Tomé e Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "São Martinho",
	"countriesL10nMessages.SZ": "Suazilândia",
	"countriesL10nMessages.TA": "Tristão da Cunha",
	"countriesL10nMessages.TC": "Ilhas Turks e Caicos",
	"countriesL10nMessages.TD": "Chade",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tailândia",
	"countriesL10nMessages.TJ": "Tadjiquistão",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turcomenistão",
	"countriesL10nMessages.TN": "Tunísia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turquia",
	"countriesL10nMessages.TT": "Trinidad e Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzânia",
	"countriesL10nMessages.UA": "Ucrânia",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Estados Unidos",
	"countriesL10nMessages.UY": "Uruguai",
	"countriesL10nMessages.UZ": "Uzbequistão",
	"countriesL10nMessages.VA": "Santa Sé (Estado da Cidade do Vaticano)",
	"countriesL10nMessages.VC": "São Vicente e Granadinas",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Ilhas Virgens Britânicas",
	"countriesL10nMessages.VI": "Ilhas Virgens, EUA",
	"countriesL10nMessages.VN": "Vietnã",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis e Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Iêmen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "África do Sul",
	"countriesL10nMessages.ZM": "Zâmbia",
	"countriesL10nMessages.ZW": "Zimbábue",
	"helper.INDIFFERENCE_PRICE_POINT": "Ponto de preço indiferente",
	"helper.IPP": "Ponto do preço indiferente (IPP, na sigla em inglês)",
	"helper.IPP_TITLE": "IPP, na sigla em inglês",
	"helper.OPP": "Ponto de preço ideal (OPP, na sigla em inglês)",
	"helper.OPP_DESCRIPTION": "OPP, na sigla em inglês",
	"helper.OPP_TITLE": "Ponto de preço ideal",
	"helper.PMC": "Ponto de economia marginal (PMC, na sigla em inglês)",
	"helper.PMC_TITLE": "PMC, na sigla em inglês",
	"helper.PME": "Ponto de dispêndio marginal (PME, na sigla em inglês)",
	"helper.PME_TITLE": "PME, na sigla em inglês",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Ponto de economia marginal",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Ponto de dispêndio marginal"
};

var ro = {
	"AddressField.selectCountry": "Selectare țară",
	"PhoneField.countryCode": "Cod de țară",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emiratele Arabe Unite",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua și Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Samoa Americană",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Insulele Åland",
	"countriesL10nMessages.AZ": "Azerbaidjan",
	"countriesL10nMessages.BA": "Bosnia și Herțegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgia",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint-Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius și Saba",
	"countriesL10nMessages.BR": "Brazilia",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Insulele Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, Republica Democrată",
	"countriesL10nMessages.CF": "Republica Centrafricană",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Elveția",
	"countriesL10nMessages.CI": "Coasta de Fildeș",
	"countriesL10nMessages.CK": "Insulele Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Camerun",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Columbia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Capul Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Insula Crăciunului",
	"countriesL10nMessages.CY": "Cipru",
	"countriesL10nMessages.CZ": "Republica Cehă",
	"countriesL10nMessages.DE": "Germania",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Danemarca",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Republica Dominicană",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egipt",
	"countriesL10nMessages.EH": "Sahara Occidentală",
	"countriesL10nMessages.ER": "Eritreea",
	"countriesL10nMessages.ES": "Spania",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finlanda",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Insulele Falkland",
	"countriesL10nMessages.FM": "Statele Federate ale Microneziei",
	"countriesL10nMessages.FO": "Insulele Feroe",
	"countriesL10nMessages.FR": "Franța",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Marea Britanie",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guiana Franceză",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Groenlanda",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guineea",
	"countriesL10nMessages.GP": "Guadelupa",
	"countriesL10nMessages.GQ": "Guineea Ecuatorială",
	"countriesL10nMessages.GR": "Grecia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guineea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croația",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungaria",
	"countriesL10nMessages.ID": "Indonezia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Insula Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Teritoriul britanic din Oceanul Indian",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islanda",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Iordania",
	"countriesL10nMessages.JP": "Japonia",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kârgâzstan",
	"countriesL10nMessages.KH": "Cambodgia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoros",
	"countriesL10nMessages.KN": "Saint Kitts și Nevis",
	"countriesL10nMessages.KR": "Coreea de Sud",
	"countriesL10nMessages.KW": "Kuweit",
	"countriesL10nMessages.KY": "Insulele Cayman",
	"countriesL10nMessages.KZ": "Kazahstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Liban",
	"countriesL10nMessages.LC": "Sfânta Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lituania",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Letonia",
	"countriesL10nMessages.LY": "Libia",
	"countriesL10nMessages.MA": "Maroc",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Muntenegru",
	"countriesL10nMessages.MF": "Saint Martin (partea franceză)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Insulele Marshall",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Insulele Mariane de Nord",
	"countriesL10nMessages.MQ": "Martinica",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldive",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexic",
	"countriesL10nMessages.MY": "Malaezia",
	"countriesL10nMessages.MZ": "Mozambic",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Noua Caledonie",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Insula Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Olanda",
	"countriesL10nMessages.NO": "Norvegia",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Noua Zeelandă",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinezia Franceză",
	"countriesL10nMessages.PG": "Papua Noua Guinee",
	"countriesL10nMessages.PH": "Filipine",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polonia",
	"countriesL10nMessages.PM": "Saint Pierre și Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugalia",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "România",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Arabia Saudită",
	"countriesL10nMessages.SB": "Insulele Solomon",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Suedia",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Sfânta Elena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard și Jan Mayen",
	"countriesL10nMessages.SK": "Slovacia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sudanul de Sud",
	"countriesL10nMessages.ST": "Sao Tome și Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sfântul Martin",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Insulele Turks și Caicos",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailanda",
	"countriesL10nMessages.TJ": "Tadjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timorul de Est",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turcia",
	"countriesL10nMessages.TT": "Trinidad și Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ucraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Statele Unite ale Americii",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Sfântul Scaun (Cetatea Vaticanului)",
	"countriesL10nMessages.VC": "Saint Vincent și Grenadine",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Insulele Virgine, britanice",
	"countriesL10nMessages.VI": "Insulele Virgine, S.U.A.",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis și Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Africa de Sud",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Trimite-mi o copie a răspunsurilor mele prin e-mail",
	"ToggleableEmailField.emailLabel": "Introduceți adresa de e-mail"
};

var ru = {
	"ActionMenuTagPin.PIN": "Прикрепить",
	"ActionMenuTagPin.TAG": "Тег",
	"AddressField.selectCountry": "Выберите страну",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Ответов: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "В{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Пропущено: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Как настроение?",
	"AsyncJobLoadingState.LOADING": "Загрузка ответов",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Когда операция будет закончена, мы сообщим вам",
	"AsyncJobLoadingState.TAGGING_TITLE": "Применение тегов к ответам",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Теги применяются к существующим и будущим ответам. Оставьте пробел или введите «<strong>OR</strong>» между словами для использования нескольких ключевых слов.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Автоматически помечать ответы тегами",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Выберите месяц",
	"CalendarPicker.months": "{months, select, January {январь} February {февраль} March {март} April {апрель} May {май} June {июнь} July {июль} August {август} September {сентябрь} October {октябрь} November {ноябрь} December {декабрь} other {другое}}",
	"CalendarPicker.nextMonth": "Следующий месяц",
	"CalendarPicker.previousMonth": "Предыдущий месяц",
	"CalendarPicker.year": "Выберите год",
	"ColorSwatches.TAG": "Пометка {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "В многоцветных облаках слов количество упоминаний слова или фразы обозначается как цветом, так и размером.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Использование цвета в облаках слов",
	"ColorTab.DOWN_ARROW": "Нажмите стрелку раскрывающегося меню",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Многоцветные",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Один цвет",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Отмена",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Создайте новое слово",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Новое слово заменяет каждое из выделенных ниже слов.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Изменить объединенные слова",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Объединить слова",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Создайте новое слово, которое заменит одно или несколько выделенных слов. Позже вы сможете вернуть исходные слова.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Введите любое новое слово",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Объединение",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Выберите слова для объединения",
	"CreateTagFromSearch.CLOSE_MENU": "ОТМЕНА",
	"CreateTagFromSearch.DUPLICATED_NAME": "Тег с таким именем уже существует.",
	"CreateTagFromSearch.NEW_TAG": "Новый тег",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Ни один ответ \n\t\tне будет помечен} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} ответ \n\t\t\t\n\t\tбудет помечен} few {\n\t\t\t\t{responseTaggedFormatted} ответа \n\t\t\t\n\t\tбудут помечены} many {\n\t\t\t\t{responseTaggedFormatted} ответов \n\t\t\t\n\t\tбудут помечены} other {\n\t\t\t\t{responseTaggedFormatted} ответа \n\t\t\t\n\t\tбудут помечены}}}}",
	"CreateTagFromSearch.SAVE": "СОХРАНИТЬ",
	"CreateTagFromSearch.TAG": "Тег",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Имя тега не может быть пустым.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Имя тега не может состоять из одних пробелов.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Помечайте тегами ответы, соответствующие этим ключевым словам. Выберите <strong>Создать тег</strong>. Выберите цвет и сохраните. Тег применяется к существующим и будущим ответам.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Помечать соответствующие ответы",
	"DashboardMenuContent.CREATE": "СОЗДАТЬ",
	"DashboardMenuContent.NAME": "Имя",
	"DashboardMenuContent.NEW_DASHBOARD": "Новая панель управления",
	"DateInputWrapper.dateButton": "Открыть календарь",
	"DateInputWrapper.dateFormatIntl": "ДД.ММ.ГГГГ",
	"DateInputWrapper.dateFormatUs": "MM/ДД/ГГГГ",
	"DateInputWrapper.dateLabel": "Дата",
	"DateTime.errorEnterHours": "Введите часы в пределах от 0 до 23.",
	"DateTime.errorEnterMinutes": "Введите минуты в пределах от 0 до 59.",
	"DateTime.errorSelectPeriod": "Выберите AM или PM.",
	"DateTime.errorYearOutOfRange": "Укажите год в пределах от 1900 до 9999.",
	"DateTime.formatErrorDateIntl": "Укажите дату в формате ДД/ММ/ГГГГ.",
	"DateTime.formatErrorDateUs": "Укажите дату в формате ММ/ДД/ГГГГ.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "ОТМЕНА",
	"EditorButtons.COPY_BUTTON": "КОПИРОВАНИЕ ВОПРОСА",
	"EditorButtons.MOVE_BUTTON": "ПЕРЕМЕЩЕНИЕ ВОПРОСА",
	"EditorButtons.NEXT_QUESTION_BUTTON": "СЛЕДУЮЩИЙ ВОПРОС",
	"EditorButtons.SAVE_BUTTON": "СОХРАНИТЬ",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Анализ тенденций данных не применим для данного вопроса",
	"FallbackComponent.ERROR": "Ошибка при загрузке вопроса",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Выберите файл</div> <div class={dragClass}>или перетащите его сюда</div>",
	"FileUpload.fileUploaded": "Файл успешно передан",
	"FileUpload.fileUploading": "Передача файла",
	"FileUpload.removeFile": "Удалить файл",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Замените файл</div> <div class={dragClass}>или перетащите его сюда</div>",
	"FileUpload.supportedFiles": "Поддерживаемые файлы",
	"FilterMenuContent.APPLY_ACTIONS": "ПРИМЕНИТЬ",
	"FilterMenuContent.CLEAR_ACTIONS": "Очистить",
	"FilterMenuContent.CLOSE_MENU": "ОТМЕНА",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Отмена",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Скрыть",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Сохранить",
	"HideWordsModal.HIDE_WORDS_HEADER": "Скрыть слова",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Выберите слова, которые надо скрыть в облаке слов.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Выберите слова, которые надо скрыть",
	"IconHelpStyled.HELP": "Справка",
	"IndividualResponseCard.NO_RESPONSE": "Респондент пропустил этот вопрос",
	"IndividualResponseCard.QUESTION_NUMBER": "В{number}",
	"InfoBannerTagManagement.CLOSE": "Закрыть",
	"InfoBannerTagManagement.NEW": "НОВЫЙ",
	"InfoBannerTagManagement.TAG_RESPONSES": "Помечать ответы",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Добавьте ключевые слова для автоматического применения тегов и систематизации ответов.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Помечайте ответы тегами для более удобного управления",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Положительная: {percentage,number,percent}} negative {Отрицательная: {percentage,number,percent}} neutral {Нейтральная: {percentage,number,percent}} unknown {Не определено: {percentage,number,percent}} other {другое}}",
	"LoadingState.LOADING": "Загрузка ответов",
	"MatrixDropdown.EmptyTopNativeSelect": "Выберите вариант",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Доверительный интервал",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Оценка полезности",
	"Modal.CloseModal": "Закрыть подсказку",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {понедельник} Tu {вторник} We {среда} Th {четверг} Fr {пятница} Sa {суббота} Su {воскресенье} other {другое}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Пн} Tu {Вт} We {Ср} Th {Чт} Fr {Пт} Sa {Сб} Su {Вс} other {другое}}",
	"MultiColorSelectorMenu.APPLY": "Применить",
	"MultiColorSelectorMenu.CANCEL": "Отмена",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Расположите свои данные в специально выбранной схеме, объясните результаты и поделитесь своей панелью управления с кем желаете. Расположите свои данные в специально выбранной схеме, объясните результаты и поделитесь своей панелью управления с кем желаете.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Панели управления",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Подробнее",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">По шкале от {minValue} до {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> где {minValue} — {minLabelText}, {maxValue} — {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "индикатор для оценок в пределах от {min} до {max}",
	"NpsGaugeChart.NPS_CHART": "Индикаторная диаграмма NPS",
	"NpsGaugeChart.NPS_SCORE": "Показатель NPS равен {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "деление диаграммы для отметки оценки {score}",
	"OrderControl.AriaNA": "Неприменимо",
	"OrderControl.NA": "Не указано",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "Ответы «{title}» ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "скрыть список ответов «прочее»",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "показать список ответов «прочее»",
	"PhoneField.countryCode": "Код страны",
	"QuestionCardItemFooter.FILTER_APPLIED": "Применены правила: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Фильтр",
	"QuestionCardTitle.ANSWERED": "Ответов: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Выбрано: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Пропущено: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} балла} few {{score}/{total} баллов} many {{score}/{total} баллов} other {{score}/{total} балла}}",
	"QuizResultScore.SKIPPED": "Пропущено",
	"RangeTab.RANGE_TAB_LABEL": "Упоминания: {value}",
	"RangeTab.TOTAL_WORDS": "слов",
	"Ranking.AriaRankAtBottom": "Вариант {label} перемещен в конец списка",
	"Ranking.AriaRankAtTop": "Вариант {label} перемещен в начало списка",
	"Ranking.AriaRankMovedDown": "Вариант {label} перемещен вниз на позицию {position}",
	"Ranking.AriaRankMovedUp": "Вариант {label} перемещен вверх на позицию {position}",
	"RankingItem.AriaRankMoveDown": "Переместить вниз {label}",
	"RankingItem.AriaRankMoveUp": "Переместить вверх {label}",
	"RatingChart.AVERAGE_RATING": "Средняя оценка",
	"RatingChartLegend.ANSWERED": "Ответов: {number}",
	"RatingChartLegend.RATING": "{rating} звезда",
	"RatingChartLegend.SKIPPED": "Пропущено: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "ЗАКАЗАТЬ ФУНКЦИЮ",
	"RequestAccessButton.UPGRADE": "Изменить план",
	"RespondentQuestionTitle.REQUIRED": "(Обязательный)",
	"ResponseItem.EXPAND_MENU_LABEL": "Развернуть меню",
	"ResponseItem.MENU_LABEL": "Добавьте теги",
	"ResponseItem.RESPONDENT_ID": "ID респондента",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Просмотреть ответы респондента",
	"ResponseStatusBar.CLEAR": "Очистить",
	"ResponseStatusBar.FAILED_TO_LOAD": "Не удалось загрузить ответы",
	"ResponseStatusBar.NO_RESPONSES_YET": "Еще нет ответов",
	"ResponseStatusBar.SEARCHING": "Поиск…",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {позитивная тональность} negative {негативная тональность} neutral {нейтральная тональность} other {неизвестная тональность}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Показан {resultsCountFormatted} выбранный ответ на запрос {searchTextFormatted} с фильтрами {filters}   } other {Показан {resultsCountFormatted} ответ на запрос {searchTextFormatted} с фильтрами {filters}   }}} few {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответа на запрос {searchTextFormatted} с фильтрами {filters}   } other {Показаны {resultsCountFormatted} ответа на запрос {searchTextFormatted} с фильтрами {filters}   }}} many {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответов на запрос {searchTextFormatted} с фильтрами {filters}   } other {Показаны {resultsCountFormatted} ответов на запрос {searchTextFormatted} с фильтрами {filters}   }}} other {{hasSelectedResponse, select, true {Показано {resultsCountFormatted} выбранного ответа на запрос {searchTextFormatted} с фильтрами {filters}   } other {Показано {resultsCountFormatted} ответа на запрос {searchTextFormatted} с фильтрами {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Показан {resultsCountFormatted} выбранный ответ с фильтрами {filters}   } other {Показан {resultsCountFormatted} ответ с фильтрами {filters}   }}} few {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответа с фильтрами {filters}   } other {Показаны {resultsCountFormatted} ответа с фильтрами {filters}   }}} many {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответов с фильтрами {filters}   } other {Показаны {resultsCountFormatted} ответов с фильтрами {filters}   }}} other {{hasSelectedResponse, select, true {Показано {resultsCountFormatted} выбранного ответа с фильтрами {filters}   } other {Показано {resultsCountFormatted} ответа с фильтрами {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Показан {resultsCountFormatted} выбранный ответ на запрос {searchTextFormatted}   } other {Показан {resultsCountFormatted} ответ на запрос {searchTextFormatted}   }}} few {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответа на запрос {searchTextFormatted}   } other {Показаны {resultsCountFormatted} ответа на запрос {searchTextFormatted}   }}} many {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответов на запрос {searchTextFormatted}   } other {Показаны {resultsCountFormatted} ответов на запрос {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Показано {resultsCountFormatted} выбранного ответа на запрос {searchTextFormatted}   } other {Показано {resultsCountFormatted} ответа на запрос {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Показан {resultsCountFormatted} выбранный ответ   } other {Показан {resultsCountFormatted} ответ   }}} few {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответа   } other {Показаны {resultsCountFormatted} ответа   }}} many {{hasSelectedResponse, select, true {Показаны {resultsCountFormatted} выбранных ответов   } other {Показаны {resultsCountFormatted} ответов   }}} other {{hasSelectedResponse, select, true {Показано {resultsCountFormatted} выбранного ответа   } other {Показано {resultsCountFormatted} ответа   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Очистить",
	"ResponseToolbar.CREATE_TAG": "Создать тег",
	"ResponseToolbar.SELECT_ALL": "Выбрать все ответы",
	"ResponseToolbar.UNSELECT_ALL": "Отменить выбор всех ответов",
	"SearchResponsesInput.CLEAR": "Очистить условия поиска",
	"SearchResponsesInput.SEARCH": "Поиск",
	"SearchResponsesInput.SEARCH_RESPONSES": "Поиск ответов",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Используйте кавычки «<strong>“”</strong>» для поиска точного текста.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Введите «<strong>ИЛИ</strong>», чтобы найти один из двух или нескольких терминов.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Чтобы найти больше чем один термин, введите <b>AND</b>.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Используйте «<strong>НЕ</strong>», чтобы исключить термин из поиска.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Введите после термина <strong>*</strong> для поиска с любым окончанием.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Для сочетания операций поиска с AND/OR/NOT используйте скобки.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Чтобы найти термин и один или два любых символа, введите <strong>~2</strong> после термина.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Параметры поиска",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Подробнее",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Нет, спасибо",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Включить",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "В вашей учетной записи теперь доступна <span id=\"link\">Автоматическая аналитика</span>. \n      Эта настройка охватывает все функции, использующие технологию машинного обучения для обработки опроса и предоставления вам аналитической информации.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Настройки всегда можно изменить на странице <span id=\"link\">Моя учетная запись</span>.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Автоматическая аналитика",
	"SentimentBanner.GENERATE_SENTIMENT": "Анализировать тональность",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Анализ настроений",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Определяйте тональность письменных ответов, чтобы понять, что чувствуют респонденты.",
	"SentimentBanner.UPGRADE": "Изменить план",
	"SentimentBanner.WATCH_A_DEMO": "Посмотреть демо",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Как настроение?",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Фильтр по позитивной тональности} negative {Фильтр по негативной тональности} neutral {Фильтр по нейтральной тональности} other {Фильтр по неизвестной тональности}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Развернуть меню",
	"SentimentFilterMenu.MENU_LABEL": "по тональности",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "ЗАКАЗАТЬ ФУНКЦИЮ",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Определяйте настроение и тональность письменных ответов.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Новинка!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Представляем анализ тональности",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Изменить план",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Посмотреть демо",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Нет, спасибо",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "ЗАКАЗАТЬ ФУНКЦИЮ",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Изменить план",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Изображение анализа тональности",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Включите анализ настроений одним щелчком мыши. Мы оценим текстовые ответы как положительные, нейтральные или отрицательные и выясним, какие эмоции испытывают респонденты.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Оперативно узнавайте, что чувствуют люди",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Анализ настроений",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Оценка тональности: ВКЛ.} false {Оценка тональности: ВЫКЛ.} other {Оценка тональности: другое}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Оценка тональности: ЗАГРУЗКА...",
	"SlideBoxNavigation.NEXT_BUTTON": "ДАЛЕЕ",
	"SlideBoxNavigation.NEXT_SLIDE": "Следующий слайд",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "НАЗАД",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Предыдущий слайд",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} из {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Очистить",
	"Slider.STEP_MESSAGE": "Введенное число исправлено в соответствии со шкалой ползунка.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Ответов: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "В{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Пропущено: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Пересечение недорогих и дорогих цен дает общее ценовое направление для вашего продукта.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Ценовая чувствительность (по методу Ван Вестендорпа)",
	"SrText.CORRECT": "Правильно",
	"SrText.INCORRECT": "Неправильно",
	"StarRating.Heart": "{count, plural, one {{count} сердечко} few {{count} сердечка} many {{count} сердечек} other {{count} сердечка}}",
	"StarRating.STAR": "{count, plural, one {{count} звезда} few {{count} звезды} many {{count} звезд} other {{count} звезды}}",
	"StarRating.Smiley": "{count, plural, one {{count} смайлик} few {{count} смайлика} many {{count} смайликов} other {{count} смайлика}}",
	"StarRating.Thumb": "{count, plural, one {{count} палец} few {{count} пальца} many {{count} пальцев} other {{count} пальца}}",
	"TabList.BETA_BADGE": "Бета-версия",
	"TabList.LOCK_ICON": "Значок блокировки вкладки тегов",
	"TabList.RESPONSES": "ОТВЕТЫ ({responseCount})",
	"TabList.TAGS": "ПОМЕТКИ ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "ТЕМАТИЧЕСКИЙ АНАЛИЗ",
	"TabList.WORD_CLOUD": "Oблако слов",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Развернуть меню",
	"TagFilterMenu.MENU_LABEL": "Фильтровать: по тегам",
	"TagList.TAGS": "Теги",
	"TagList.TAGS_KEYWORDS": "Ключевые слова",
	"TagList.TAG_CONTROLS": "Элементы управления тегами",
	"TagList.TAG_NUMBER_PERCENT": "Процент тегов и количество тегов",
	"TagList.TAG_PERCENTAGE_BAR": "Процентный индикатор тегов",
	"TagListItem.DELETE": "Удалить",
	"TagListItem.EDIT": "Изменить",
	"TagListItem.VIEW_ALL": "Показать все",
	"TagListPanel.LOADING": "Загрузка тегов",
	"TagListPanel.NEW_TAG": "+ Новый тег",
	"TagListPanel.TAGS": "Теги",
	"TagMenuContent.APPLY_ACTIONS": "ПРИМЕНИТЬ",
	"TagMenuContent.CLEAR_ACTIONS": "Очистить",
	"TagMenuContent.CLOSE_MENU": "ОТМЕНА",
	"TagMenuContent.DUPLICATED_NAME": "Тег с таким именем уже существует.",
	"TagMenuContent.NEW_TAG": "Новый тег",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Подробнее >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "ЗАКАЗАТЬ ФУНКЦИЮ",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Изменить план",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Закрыть",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Ищите, помечайте и фильтруйте письменные ответы и создавайте облака слов из часто встречающихся слов и фраз.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Анализ текста",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "AM/PM",
	"TimeInputWrapper.hhPlaceholder": "чч",
	"TimeInputWrapper.hour": "Час",
	"TimeInputWrapper.minutes": "Мин.",
	"TimeInputWrapper.mmPlaceholder": "мм",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Время",
	"ToggleableEmailField.checkboxLabel": "Отправить мне копию моих ответов по электронной почте",
	"ToggleableEmailField.emailLabel": "Введите свой адрес электронной почты",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Удалить",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {С {responseCountFormatted} ответа будет снята пометка.} few {С {responseCountFormatted} ответов будут сняты пометки.} many {С {responseCountFormatted} ответов будут сняты пометки.} other {С {responseCountFormatted} ответа будет сняты пометки.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "При удалении тега он убирается из всех помеченных этим тегом ответов; отменить удаление нельзя.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Удалить тег?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Сохранить",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Сохранить изменения?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Тег будет удален из всех ответов. Тег повторно применяется к ответам, которые соответствуют измененным ключевым словам.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Отмена",
	"UpgradeBanner.SUBTITLE": "Измените план, чтобы самостоятельно настроить облако слов.",
	"UpgradeBanner.TITLE": "Понравилось?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% ответов",
	"VanWestendorpChartDataTable.PRICE": "Цена",
	"VanWestendorpChartDataTable.RESPONSES": "Ответы",
	"WordCloudCustomizationButton.CUSTOMIZE": "Настроить",
	"WordCloudCustomizationFooter.APPLY": "Применить",
	"WordCloudCustomizationFooter.CANCEL": "Отмена",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Применить ко всем облакам слов в опросе (некоторые из них могут не измениться)",
	"WordCloudCustomizationFooter.NO_THANKS": "Нет, спасибо",
	"WordCloudCustomizationFooter.REQUEST": "ЗАКАЗАТЬ ФУНКЦИЮ",
	"WordCloudCustomizationFooter.SAVE": "Сохранить",
	"WordCloudCustomizationFooter.UPGRADE": "Изменить план",
	"WordCloudCustomizationTabs.COLOR": "ЦВЕТ",
	"WordCloudCustomizationTabs.FONT": "ШРИФТ",
	"WordCloudCustomizationTabs.RANGE": "ДИАПАЗОН",
	"WordCloudLegend.FEWER": "меньше",
	"WordCloudLegend.MORE": "больше",
	"WordCloudLegend.TITLE": "Количество упоминаний",
	"WordCloudListFooter.COMBINED_WORDS": "Объединенные слова",
	"WordCloudListFooter.HIDDEN_WORDS": "Скрытые слова",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Развернуть меню",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Объединить с",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Изменить объединенные слова",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Развернуть меню",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Скрыть из облака слов",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Разделить объединенные слова",
	"WordCloudLoadingState.LOADING": "Загрузка облака слов...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Кнопка закрытия",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Просмотрите возможности настройки облаков слов.",
	"WordCloudPreviewOverlay.TITLE": "Попробуйте прямо сейчас!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Облако",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Список",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Совпадения: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}-е слово в порядке важности",
	"WordCount.IN_PROGRESS": "Выполняется анализ текста...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Важные слова не найдены.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Показано {count} слово } few {Показаны {count} слова } many {Показаны {count} слов } other {Показано {count} слова }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Фильтр по позитивной тональности} negative {Фильтр по негативной тональности} neutral {Фильтр по нейтральной тональности} other {Фильтр по неопределенной тональности}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Положительное} negative {Отрицательное} neutral {Нейтральное отношение} other {Неизвестно}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Положительная: {percentage,number,percent}} negative {Отрицательная: {percentage,number,percent}} neutral {Нейтральная: {percentage,number,percent}} other {Неизвестно: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "ОТМЕНА",
	"copyTagEditor.DUPLICATED_NAME": "Тег с таким именем уже существует.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Введите имя для этого тега",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Введите одно или несколько ключевых слов",
	"copyTagEditor.KEYWORDS": "<strong>Ключевые слова</strong> (необязательно)",
	"copyTagEditor.SAVE": "СОХРАНИТЬ",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Тег применяется ко всем ответам, содержащим указанные ключевые слова",
	"copyTagEditor.TAG_NAME": "<strong>Имя тега</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Имя тега не может быть пустым.",
	"copyTagEditor.TAG_NAME_SPACES": "Имя тега не может состоять из одних пробелов.",
	"copyTagEditor.TAG_TOO_LONG": "Тег должен содержать не более 128 символов.",
	"countriesL10nMessages.AC": "остров Вознесения",
	"countriesL10nMessages.AD": "Андорра",
	"countriesL10nMessages.AE": "Объединенные Арабские Эмираты",
	"countriesL10nMessages.AF": "Афганистан",
	"countriesL10nMessages.AG": "Антигуа и Барбуда",
	"countriesL10nMessages.AI": "Ангилья",
	"countriesL10nMessages.AL": "Албания",
	"countriesL10nMessages.AM": "Армения",
	"countriesL10nMessages.AO": "Ангола",
	"countriesL10nMessages.AR": "Аргентина",
	"countriesL10nMessages.AS": "Американское Самоа",
	"countriesL10nMessages.AT": "Австрия",
	"countriesL10nMessages.AU": "Австралия",
	"countriesL10nMessages.AW": "Аруба",
	"countriesL10nMessages.AX": "Аландские острова",
	"countriesL10nMessages.AZ": "Азербайджан",
	"countriesL10nMessages.BA": "Босния и Герцеговина",
	"countriesL10nMessages.BB": "Барбадос",
	"countriesL10nMessages.BD": "Бангладеш",
	"countriesL10nMessages.BE": "Бельгия",
	"countriesL10nMessages.BF": "Буркина-Фасо",
	"countriesL10nMessages.BG": "Болгария",
	"countriesL10nMessages.BH": "Бахрейн",
	"countriesL10nMessages.BI": "Бурунди",
	"countriesL10nMessages.BJ": "Бенин",
	"countriesL10nMessages.BL": "Сен-Бартелеми",
	"countriesL10nMessages.BM": "Бермудские острова",
	"countriesL10nMessages.BN": "Бруней",
	"countriesL10nMessages.BO": "Боливия",
	"countriesL10nMessages.BQ": "Бонайре, Синт-Эстатиус и Саба",
	"countriesL10nMessages.BR": "Бразилия",
	"countriesL10nMessages.BS": "Багамские острова",
	"countriesL10nMessages.BT": "Бутан",
	"countriesL10nMessages.BW": "Ботсвана",
	"countriesL10nMessages.BZ": "Белиз",
	"countriesL10nMessages.CA": "Канада",
	"countriesL10nMessages.CC": "Кокосовые (Килинг) острова",
	"countriesL10nMessages.CD": "Конго, Демократическая Республика",
	"countriesL10nMessages.CF": "Центральноафриканская Республика",
	"countriesL10nMessages.CG": "Конго",
	"countriesL10nMessages.CH": "Швейцария",
	"countriesL10nMessages.CI": "Кот-д'Ивуар",
	"countriesL10nMessages.CK": "Острова Кука",
	"countriesL10nMessages.CL": "Чили",
	"countriesL10nMessages.CM": "Камерун",
	"countriesL10nMessages.CN": "Китай",
	"countriesL10nMessages.CO": "Колумбия",
	"countriesL10nMessages.CR": "Коста-Рика",
	"countriesL10nMessages.CV": "Кабо-Верде",
	"countriesL10nMessages.CW": "Кюрасао",
	"countriesL10nMessages.CX": "Остров Рождества",
	"countriesL10nMessages.CY": "Кипр",
	"countriesL10nMessages.CZ": "Чешская Республика",
	"countriesL10nMessages.DE": "Германия",
	"countriesL10nMessages.DJ": "Джибути",
	"countriesL10nMessages.DK": "Дания",
	"countriesL10nMessages.DM": "Доминика",
	"countriesL10nMessages.DO": "Доминиканская Республика",
	"countriesL10nMessages.DZ": "Алжир",
	"countriesL10nMessages.EC": "Эквадор",
	"countriesL10nMessages.EE": "Эстония",
	"countriesL10nMessages.EG": "Египет",
	"countriesL10nMessages.EH": "Западная Сахара",
	"countriesL10nMessages.ER": "Эритрея",
	"countriesL10nMessages.ES": "Испания",
	"countriesL10nMessages.ET": "Эфиопия",
	"countriesL10nMessages.FI": "Финляндия",
	"countriesL10nMessages.FJ": "Фиджи",
	"countriesL10nMessages.FK": "Фолклендские о-ва",
	"countriesL10nMessages.FM": "Федеративные Штаты Микронезии",
	"countriesL10nMessages.FO": "Фарерские острова",
	"countriesL10nMessages.FR": "Франция",
	"countriesL10nMessages.GA": "Габон",
	"countriesL10nMessages.GB": "Великобритания",
	"countriesL10nMessages.GD": "Гренада",
	"countriesL10nMessages.GE": "Грузия",
	"countriesL10nMessages.GF": "Французская Гвиана",
	"countriesL10nMessages.GG": "Гернси",
	"countriesL10nMessages.GH": "Гана",
	"countriesL10nMessages.GI": "Гибралтар",
	"countriesL10nMessages.GL": "Гренландия",
	"countriesL10nMessages.GM": "Гамбия",
	"countriesL10nMessages.GN": "Гвинея",
	"countriesL10nMessages.GP": "Гваделупа",
	"countriesL10nMessages.GQ": "Экваториальная Гвинея",
	"countriesL10nMessages.GR": "Греция",
	"countriesL10nMessages.GT": "Гватемала",
	"countriesL10nMessages.GU": "Гуам",
	"countriesL10nMessages.GW": "Гвинея-Бисау",
	"countriesL10nMessages.GY": "Гайана",
	"countriesL10nMessages.HK": "Гонконг",
	"countriesL10nMessages.HN": "Гондурас",
	"countriesL10nMessages.HR": "Хорватия",
	"countriesL10nMessages.HT": "Гаити",
	"countriesL10nMessages.HU": "Венгрия",
	"countriesL10nMessages.ID": "Индонезия",
	"countriesL10nMessages.IE": "Ирландия",
	"countriesL10nMessages.IL": "Израиль",
	"countriesL10nMessages.IM": "Остров Мэн",
	"countriesL10nMessages.IN": "Индия",
	"countriesL10nMessages.IO": "Британская территория в Индийском океане",
	"countriesL10nMessages.IQ": "Ирак",
	"countriesL10nMessages.IS": "Исландия",
	"countriesL10nMessages.IT": "Италия",
	"countriesL10nMessages.JE": "Джерси",
	"countriesL10nMessages.JM": "Ямайка",
	"countriesL10nMessages.JO": "Иордания",
	"countriesL10nMessages.JP": "Япония",
	"countriesL10nMessages.KE": "Кения",
	"countriesL10nMessages.KG": "Киргизстан",
	"countriesL10nMessages.KH": "Камбоджа",
	"countriesL10nMessages.KI": "Кирибати",
	"countriesL10nMessages.KM": "Коморские острова",
	"countriesL10nMessages.KN": "Сент-Китс и Невис",
	"countriesL10nMessages.KR": "Южная Корея",
	"countriesL10nMessages.KW": "Кувейт",
	"countriesL10nMessages.KY": "Каймановы острова",
	"countriesL10nMessages.KZ": "Казахстан",
	"countriesL10nMessages.LA": "Лаос",
	"countriesL10nMessages.LB": "Ливан",
	"countriesL10nMessages.LC": "Сент-Люсия",
	"countriesL10nMessages.LI": "Лихтенштейн",
	"countriesL10nMessages.LK": "Шри-Ланка",
	"countriesL10nMessages.LR": "Либерия",
	"countriesL10nMessages.LS": "Лесото",
	"countriesL10nMessages.LT": "Литва",
	"countriesL10nMessages.LU": "Люксембург",
	"countriesL10nMessages.LV": "Латвия",
	"countriesL10nMessages.LY": "Ливия",
	"countriesL10nMessages.MA": "Марокко",
	"countriesL10nMessages.MC": "Монако",
	"countriesL10nMessages.MD": "Молдова",
	"countriesL10nMessages.ME": "Черногория",
	"countriesL10nMessages.MF": "Сен-Мартен (Франция)",
	"countriesL10nMessages.MG": "Мадагаскар",
	"countriesL10nMessages.MH": "Маршалловы острова",
	"countriesL10nMessages.MK": "Македония",
	"countriesL10nMessages.ML": "Мали",
	"countriesL10nMessages.MM": "Мьянма",
	"countriesL10nMessages.MN": "Монголия",
	"countriesL10nMessages.MO": "Макао",
	"countriesL10nMessages.MP": "Северные Марианские о-ва",
	"countriesL10nMessages.MQ": "Мартиника",
	"countriesL10nMessages.MR": "Мавритания",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Мальта",
	"countriesL10nMessages.MU": "Маврикий",
	"countriesL10nMessages.MV": "Мальдивы",
	"countriesL10nMessages.MW": "Малави",
	"countriesL10nMessages.MX": "Мексика",
	"countriesL10nMessages.MY": "Малайзия",
	"countriesL10nMessages.MZ": "Мозамбик",
	"countriesL10nMessages.NA": "Намибия",
	"countriesL10nMessages.NC": "Новая Каледония",
	"countriesL10nMessages.NE": "Нигер",
	"countriesL10nMessages.NF": "Остров Норфолк",
	"countriesL10nMessages.NG": "Нигерия",
	"countriesL10nMessages.NI": "Никарагуа",
	"countriesL10nMessages.NL": "Нидерланды",
	"countriesL10nMessages.NO": "Норвегия",
	"countriesL10nMessages.NP": "Непал",
	"countriesL10nMessages.NR": "Науру",
	"countriesL10nMessages.NU": "Ниуэ",
	"countriesL10nMessages.NZ": "Новая Зеландия",
	"countriesL10nMessages.OM": "Оман",
	"countriesL10nMessages.PA": "Панама",
	"countriesL10nMessages.PE": "Перу",
	"countriesL10nMessages.PF": "Французская Полинезия",
	"countriesL10nMessages.PG": "Папуа-Новая Гвинея",
	"countriesL10nMessages.PH": "Филиппины",
	"countriesL10nMessages.PK": "Пакистан",
	"countriesL10nMessages.PL": "Польша",
	"countriesL10nMessages.PM": "Сен-Пьер и Микелон",
	"countriesL10nMessages.PR": "Пуэрто-Рико",
	"countriesL10nMessages.PS": "Палестина",
	"countriesL10nMessages.PT": "Португалия",
	"countriesL10nMessages.PW": "Палау",
	"countriesL10nMessages.PY": "Парагвай",
	"countriesL10nMessages.QA": "Катар",
	"countriesL10nMessages.RE": "Реюньон",
	"countriesL10nMessages.RO": "Румыния",
	"countriesL10nMessages.RS": "Сербия",
	"countriesL10nMessages.RW": "Руанда",
	"countriesL10nMessages.SA": "Саудовская Аравия",
	"countriesL10nMessages.SB": "Соломоновы острова",
	"countriesL10nMessages.SC": "Сейшельские острова",
	"countriesL10nMessages.SE": "Швеция",
	"countriesL10nMessages.SG": "Сингапур",
	"countriesL10nMessages.SH": "Святой Елены, остров",
	"countriesL10nMessages.SI": "Словения",
	"countriesL10nMessages.SJ": "Шпицберген и Ян-Майен",
	"countriesL10nMessages.SK": "Словакия",
	"countriesL10nMessages.SL": "Сьерра-Леоне",
	"countriesL10nMessages.SM": "Сан-Марино",
	"countriesL10nMessages.SN": "Сенегал",
	"countriesL10nMessages.SO": "Сомали",
	"countriesL10nMessages.SR": "Суринам",
	"countriesL10nMessages.SS": "Южный Судан",
	"countriesL10nMessages.ST": "Сан-Томе и Принсипи",
	"countriesL10nMessages.SV": "Сальвадор",
	"countriesL10nMessages.SX": "Синт-Маартен",
	"countriesL10nMessages.SZ": "Свазиленд",
	"countriesL10nMessages.TA": "Тристан-да-Кунья",
	"countriesL10nMessages.TC": "Острова Теркс и Кайкос",
	"countriesL10nMessages.TD": "Чад",
	"countriesL10nMessages.TG": "Того",
	"countriesL10nMessages.TH": "Таиланд",
	"countriesL10nMessages.TJ": "Таджикистан",
	"countriesL10nMessages.TK": "Токелау",
	"countriesL10nMessages.TL": "Тимор-Лешти",
	"countriesL10nMessages.TM": "Туркменистан",
	"countriesL10nMessages.TN": "Тунис",
	"countriesL10nMessages.TO": "Тонга",
	"countriesL10nMessages.TR": "Турция",
	"countriesL10nMessages.TT": "Тринидад и Тобаго",
	"countriesL10nMessages.TV": "Тувалу",
	"countriesL10nMessages.TW": "Тайвань",
	"countriesL10nMessages.TZ": "Танзания",
	"countriesL10nMessages.UA": "Украина",
	"countriesL10nMessages.UG": "Уганда",
	"countriesL10nMessages.US": "Соединенные Штаты Америки",
	"countriesL10nMessages.UY": "Уругвай",
	"countriesL10nMessages.UZ": "Узбекистан",
	"countriesL10nMessages.VA": "Ватикан (Город-государство Ватикан)",
	"countriesL10nMessages.VC": "Сент-Винсент и Гренадины",
	"countriesL10nMessages.VE": "Венесуэла",
	"countriesL10nMessages.VG": "Виргинские острова, Британские",
	"countriesL10nMessages.VI": "Виргинские острова, США",
	"countriesL10nMessages.VN": "Вьетнам",
	"countriesL10nMessages.VU": "Вануату",
	"countriesL10nMessages.WF": "Уоллис и Футуна",
	"countriesL10nMessages.WS": "Самоа",
	"countriesL10nMessages.XK": "Косово",
	"countriesL10nMessages.YE": "Йемен",
	"countriesL10nMessages.YT": "Майотта",
	"countriesL10nMessages.ZA": "Южно-Африканская Республика",
	"countriesL10nMessages.ZM": "Замбия",
	"countriesL10nMessages.ZW": "Зимбабве",
	"helper.INDIFFERENCE_PRICE_POINT": "Ценовая точка безразличия",
	"helper.IPP": "Безразличная ценовая точка (БЦТ)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Оптимальная ценовая точка (ОЦТ)",
	"helper.OPP_DESCRIPTION": "Оптимальная ценовая точка",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Точка предельной дешевизны (PMC — Point of Marginal Cheapness)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Точка предельной дороговизны (PME — Point of Marginal Expensiveness)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Точка предельной дешевизны",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Точка предельной дороговизны"
};

var sk = {
	"AddressField.selectCountry": "Vybrať krajinu",
	"PhoneField.countryCode": "Kód krajiny",
	"countriesL10nMessages.AC": "Ostrov Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Spojené Arabské Emiráty",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua a Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albánsko",
	"countriesL10nMessages.AM": "Arménsko",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentína",
	"countriesL10nMessages.AS": "Americká Samoa",
	"countriesL10nMessages.AT": "Rakúsko",
	"countriesL10nMessages.AU": "Austrália",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Alandské ostrovy",
	"countriesL10nMessages.AZ": "Azerbajdžan",
	"countriesL10nMessages.BA": "Bosna a Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladéš",
	"countriesL10nMessages.BE": "Belgicko",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulharsko",
	"countriesL10nMessages.BH": "Bahrajn",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Svätý Bartolomej",
	"countriesL10nMessages.BM": "Bermudy",
	"countriesL10nMessages.BN": "Brunej Darussalam",
	"countriesL10nMessages.BO": "Bolívia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius a Saba",
	"countriesL10nMessages.BR": "Brazília",
	"countriesL10nMessages.BS": "Bahamy",
	"countriesL10nMessages.BT": "Bhután",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosové (Keelingove) ostrovy",
	"countriesL10nMessages.CD": "Kongo, Demokratická republika",
	"countriesL10nMessages.CF": "Stredoafrická republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Švajčiarsko",
	"countriesL10nMessages.CI": "Pobrežie Slonoviny",
	"countriesL10nMessages.CK": "Cookove ostrovy",
	"countriesL10nMessages.CL": "Čile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Čína",
	"countriesL10nMessages.CO": "Kolumbia",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Kapverdy",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Vianočný ostrov",
	"countriesL10nMessages.CY": "Cyprus",
	"countriesL10nMessages.CZ": "Česká republika",
	"countriesL10nMessages.DE": "Nemecko",
	"countriesL10nMessages.DJ": "Džibutsko",
	"countriesL10nMessages.DK": "Dánsko",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikánska republika",
	"countriesL10nMessages.DZ": "Alžírsko",
	"countriesL10nMessages.EC": "Ekvádor",
	"countriesL10nMessages.EE": "Estónsko",
	"countriesL10nMessages.EG": "Egypt",
	"countriesL10nMessages.EH": "Západná Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Španielsko",
	"countriesL10nMessages.ET": "Etiópia",
	"countriesL10nMessages.FI": "Fínsko",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Falklandské ostrovy",
	"countriesL10nMessages.FM": "Federatívne štáty Mikronézie",
	"countriesL10nMessages.FO": "Faerské ostrovy",
	"countriesL10nMessages.FR": "Francúzsko",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Spojené kráľovstvo",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzínsko",
	"countriesL10nMessages.GF": "Francúzska Guiana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltár",
	"countriesL10nMessages.GL": "Grónsko",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Rovníková Guinea",
	"countriesL10nMessages.GR": "Grécko",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Chorvátsko",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Maďarsko",
	"countriesL10nMessages.ID": "Indonézia",
	"countriesL10nMessages.IE": "Írsko",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Ostrov Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Britské územie v Indickom oceáne",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Taliansko",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamajka",
	"countriesL10nMessages.JO": "Jordánsko",
	"countriesL10nMessages.JP": "Japonsko",
	"countriesL10nMessages.KE": "Keňa",
	"countriesL10nMessages.KG": "Kirgizsko",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komory",
	"countriesL10nMessages.KN": "Svätý Krištof a Nevis",
	"countriesL10nMessages.KR": "Južná Kórea",
	"countriesL10nMessages.KW": "Kuvajt",
	"countriesL10nMessages.KY": "Kajmanské ostrovy",
	"countriesL10nMessages.KZ": "Kazachstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Svätá Lucia",
	"countriesL10nMessages.LI": "Lichtenštajnsko",
	"countriesL10nMessages.LK": "Srí Lanka",
	"countriesL10nMessages.LR": "Libéria",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litva",
	"countriesL10nMessages.LU": "Luxembursko",
	"countriesL10nMessages.LV": "Lotyšsko",
	"countriesL10nMessages.LY": "Líbya",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavsko",
	"countriesL10nMessages.ME": "Čierna Hora",
	"countriesL10nMessages.MF": "Svätý Martin (francúzska časť)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Maršalove ostrovy",
	"countriesL10nMessages.MK": "Macedónsko",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mjanmarsko",
	"countriesL10nMessages.MN": "Mongolsko",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Ostrov Severné Mariány",
	"countriesL10nMessages.MQ": "Martinik",
	"countriesL10nMessages.MR": "Mauritánia",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Maurícius",
	"countriesL10nMessages.MV": "Maldivy",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexiko",
	"countriesL10nMessages.MY": "Malajzia",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namíbia",
	"countriesL10nMessages.NC": "Nová Kaledónia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Ostrov Norfolk",
	"countriesL10nMessages.NG": "Nigéria",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Holandsko",
	"countriesL10nMessages.NO": "Nórsko",
	"countriesL10nMessages.NP": "Nepál",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nový Zéland",
	"countriesL10nMessages.OM": "Omán",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francúzska Polynézia",
	"countriesL10nMessages.PG": "Papua-Nová Guinea",
	"countriesL10nMessages.PH": "Filipíny",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poľsko",
	"countriesL10nMessages.PM": "Svätý Pierre a Miquelon",
	"countriesL10nMessages.PR": "Portoriko",
	"countriesL10nMessages.PS": "Palestíne",
	"countriesL10nMessages.PT": "Portugalsko",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguaj",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumunsko",
	"countriesL10nMessages.RS": "Srbsko",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudská Arábia",
	"countriesL10nMessages.SB": "Šalamúnove ostrovy",
	"countriesL10nMessages.SC": "Seychely",
	"countriesL10nMessages.SE": "Švédsko",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Svätá Helena",
	"countriesL10nMessages.SI": "Slovinsko",
	"countriesL10nMessages.SJ": "Svalbard a Jan Mayen",
	"countriesL10nMessages.SK": "Slovensko",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Maríno",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somálsko",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Južný Sudán",
	"countriesL10nMessages.ST": "Svätý Tomáš a Princov ostrov",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Svätý Martin",
	"countriesL10nMessages.SZ": "Svazijsko",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Ostrovy Turks a Caicos",
	"countriesL10nMessages.TD": "Čad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thajsko",
	"countriesL10nMessages.TJ": "Tadžikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Východný Timor",
	"countriesL10nMessages.TM": "Turkménsko",
	"countriesL10nMessages.TN": "Tunisko",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turecko",
	"countriesL10nMessages.TT": "Trinidad a Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzánia",
	"countriesL10nMessages.UA": "Ukrajina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Spojené štáty americké",
	"countriesL10nMessages.UY": "Uruguaj",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Svätá stolica (Vatikánsky mestský štát)",
	"countriesL10nMessages.VC": "Svätý Vincent a Grenadíny",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Panenské ostrovy, Britské",
	"countriesL10nMessages.VI": "Panenské ostrovy, USA",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis a Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Južná Afrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Kópiu mojich odpovedí mi prosím zašlite na e-mail",
	"ToggleableEmailField.emailLabel": "Zadajte vašu e-mailovú adresy"
};

var sl = {
	"AddressField.selectCountry": "Izberite državo",
	"PhoneField.countryCode": "Koda države",
	"countriesL10nMessages.AC": "Otok Ascension",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Združeni arabski emirati",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigva in Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanija",
	"countriesL10nMessages.AM": "Armenija",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Ameriška Samoa",
	"countriesL10nMessages.AT": "Avstrija",
	"countriesL10nMessages.AU": "Avstralija",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Alandski otoki",
	"countriesL10nMessages.AZ": "Azerbajdžan",
	"countriesL10nMessages.BA": "Bosna in Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladeš",
	"countriesL10nMessages.BE": "Belgija",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bolgarija",
	"countriesL10nMessages.BH": "Bahrajn",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Sv. Bartolomej",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunej Darussalam",
	"countriesL10nMessages.BO": "Bolivija",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius in Saba",
	"countriesL10nMessages.BR": "Brazilija",
	"countriesL10nMessages.BS": "Bahami",
	"countriesL10nMessages.BT": "Butan",
	"countriesL10nMessages.BW": "Bocvana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosovi otoki",
	"countriesL10nMessages.CD": "Kongo, Demokratična republika",
	"countriesL10nMessages.CF": "Centralna afriška republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Švica",
	"countriesL10nMessages.CI": "Slonokoščena obala",
	"countriesL10nMessages.CK": "Cookovi otoki",
	"countriesL10nMessages.CL": "Čile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kitajska",
	"countriesL10nMessages.CO": "Kolumbija",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Zelenortski otoki",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Božični otok",
	"countriesL10nMessages.CY": "Ciper",
	"countriesL10nMessages.CZ": "Češka republika",
	"countriesL10nMessages.DE": "Nemčija",
	"countriesL10nMessages.DJ": "Džibuti",
	"countriesL10nMessages.DK": "Danska",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikanska republika",
	"countriesL10nMessages.DZ": "Alžirija",
	"countriesL10nMessages.EC": "Ekvador",
	"countriesL10nMessages.EE": "Estonija",
	"countriesL10nMessages.EG": "Egipt",
	"countriesL10nMessages.EH": "Zahodna Sahara",
	"countriesL10nMessages.ER": "Eritreja",
	"countriesL10nMessages.ES": "Španija",
	"countriesL10nMessages.ET": "Etiopija",
	"countriesL10nMessages.FI": "Finska",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Falklandski otoki",
	"countriesL10nMessages.FM": "Federativne države Mikronezije",
	"countriesL10nMessages.FO": "Ferski otoki",
	"countriesL10nMessages.FR": "Francija",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Združeno kraljestvo",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzija",
	"countriesL10nMessages.GF": "Francoska Gvajana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grenlandija",
	"countriesL10nMessages.GM": "Gambija",
	"countriesL10nMessages.GN": "Gvineja",
	"countriesL10nMessages.GP": "Guadaloupe",
	"countriesL10nMessages.GQ": "Ekvatorialna Gvineja",
	"countriesL10nMessages.GR": "Grčija",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Gvineja Bissau",
	"countriesL10nMessages.GY": "Gvajana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Hrvaška",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Madžarska",
	"countriesL10nMessages.ID": "Indonezija",
	"countriesL10nMessages.IE": "Irska",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Otok Man",
	"countriesL10nMessages.IN": "Indija",
	"countriesL10nMessages.IO": "Britansko ozemlje v Indijskem oceanu",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Islandija",
	"countriesL10nMessages.IT": "Italija",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamajka",
	"countriesL10nMessages.JO": "Jordanija",
	"countriesL10nMessages.JP": "Japonska",
	"countriesL10nMessages.KE": "Kenija",
	"countriesL10nMessages.KG": "Krigizistan",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komori",
	"countriesL10nMessages.KN": "Sveti Krištof in Nevis",
	"countriesL10nMessages.KR": "Južna Koreja",
	"countriesL10nMessages.KW": "Kuvajt",
	"countriesL10nMessages.KY": "Kajmanski otoki",
	"countriesL10nMessages.KZ": "Kazahstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "Sveta Lucija",
	"countriesL10nMessages.LI": "Lihtenštajn",
	"countriesL10nMessages.LK": "Šrilanka",
	"countriesL10nMessages.LR": "Liberija",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Litva",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Latvija",
	"countriesL10nMessages.LY": "Libija",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavija",
	"countriesL10nMessages.ME": "Črna gora",
	"countriesL10nMessages.MF": "Sveti Martin (francoski del)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshallovi otoki",
	"countriesL10nMessages.MK": "Makedonija",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mjanmar",
	"countriesL10nMessages.MN": "Mongolija",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Severni Marijanski otoki",
	"countriesL10nMessages.MQ": "Martinik",
	"countriesL10nMessages.MR": "Mavretanija",
	"countriesL10nMessages.MS": "Monserat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mavricij",
	"countriesL10nMessages.MV": "Maldivi",
	"countriesL10nMessages.MW": "Malavi",
	"countriesL10nMessages.MX": "Mehika",
	"countriesL10nMessages.MY": "Malezija",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibija",
	"countriesL10nMessages.NC": "Nova Kaledonija",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Otok Norfolk",
	"countriesL10nMessages.NG": "Nigerija",
	"countriesL10nMessages.NI": "Nikaragva",
	"countriesL10nMessages.NL": "Nizozemska",
	"countriesL10nMessages.NO": "Norveška",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nova Zelandija",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francoska Polinezija",
	"countriesL10nMessages.PG": "Papua Nova Gvineja",
	"countriesL10nMessages.PH": "Filipini",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poljska",
	"countriesL10nMessages.PM": "Sveti Peter in Mihael",
	"countriesL10nMessages.PR": "Portoriko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugalska",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragvaj",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Reunion",
	"countriesL10nMessages.RO": "Romunija",
	"countriesL10nMessages.RS": "Srbija",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudova Arabija",
	"countriesL10nMessages.SB": "Solomonski otoki",
	"countriesL10nMessages.SC": "Sejšeli",
	"countriesL10nMessages.SE": "Švedska",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Sveta Helena",
	"countriesL10nMessages.SI": "Slovenija",
	"countriesL10nMessages.SJ": "Svalbard in Jan Mayen",
	"countriesL10nMessages.SK": "Slovakija",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalija",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Južni Sudan",
	"countriesL10nMessages.ST": "Sveti Tomaž in Princ",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sveti Martin",
	"countriesL10nMessages.SZ": "Svazi",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Otoki Turks in Caicos",
	"countriesL10nMessages.TD": "Čad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tajska",
	"countriesL10nMessages.TJ": "Tadžikistan",
	"countriesL10nMessages.TK": "Tokelav",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunizija",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turčija",
	"countriesL10nMessages.TT": "Trinidad in Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajvan",
	"countriesL10nMessages.TZ": "Tanzanija",
	"countriesL10nMessages.UA": "Ukrajina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Združene države",
	"countriesL10nMessages.UY": "Urugvaj",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Sveti sedež (Vatikanska mestna država)",
	"countriesL10nMessages.VC": "Sveti Vincencij in Grenadine",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Deviški otoki, britanski",
	"countriesL10nMessages.VI": "Deviški otoki, ZDA",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis in Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Južna Afrika",
	"countriesL10nMessages.ZM": "Zambija",
	"countriesL10nMessages.ZW": "Zimbabve",
	"ToggleableEmailField.checkboxLabel": "Pošlji mi kopijo mojih odgovorov po elektronski pošti",
	"ToggleableEmailField.emailLabel": "Vnesite svoj e-poštni naslov"
};

var sq = {
	"AddressField.selectCountry": "Zgjidh shtetin",
	"PhoneField.countryCode": "Kodi i shtetit",
	"countriesL10nMessages.AC": "Ishulli Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Emiratet e Bashkuara Arabe",
	"countriesL10nMessages.AF": "Afganistani",
	"countriesL10nMessages.AG": "Antigua dhe Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Shqipëria",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argjentina",
	"countriesL10nMessages.AS": "Samoa Amerikane",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Ishujt Oland",
	"countriesL10nMessages.AZ": "Azerbajxhani",
	"countriesL10nMessages.BA": "Bosnja dhe Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladeshi",
	"countriesL10nMessages.BE": "Belgjika",
	"countriesL10nMessages.BF": "Burkina-Faso",
	"countriesL10nMessages.BG": "Bullgaria",
	"countriesL10nMessages.BH": "Bahrejni",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benini",
	"countriesL10nMessages.BL": "Shën Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Vend i Paqes",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius dhe Saba",
	"countriesL10nMessages.BR": "Brazili",
	"countriesL10nMessages.BS": "Bahama",
	"countriesL10nMessages.BT": "Butani",
	"countriesL10nMessages.BW": "Botsvana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanadaja",
	"countriesL10nMessages.CC": "Ishujt Kokos e Keeling",
	"countriesL10nMessages.CD": "Republika Demokratike e Kongos",
	"countriesL10nMessages.CF": "Republika e Afrikës Qendrore",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Zvicra",
	"countriesL10nMessages.CI": "Bregu i Fildishtë",
	"countriesL10nMessages.CK": "Ishujt Kuk",
	"countriesL10nMessages.CL": "Kili",
	"countriesL10nMessages.CM": "Kameruni",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Kolumbia",
	"countriesL10nMessages.CR": "Kosta-Rika",
	"countriesL10nMessages.CV": "Kepi i Gjelbër",
	"countriesL10nMessages.CW": "Kuraçao",
	"countriesL10nMessages.CX": "Ishulli i Krishtlindjeve",
	"countriesL10nMessages.CY": "Qipro",
	"countriesL10nMessages.CZ": "Republika Çeke",
	"countriesL10nMessages.DE": "Germania",
	"countriesL10nMessages.DJ": "Xhibuti",
	"countriesL10nMessages.DK": "Danimarka",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Republika Dominikane",
	"countriesL10nMessages.DZ": "Algjeria",
	"countriesL10nMessages.EC": "Ekuadori",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egjipti",
	"countriesL10nMessages.EH": "Saharaja Perëndimore",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanja",
	"countriesL10nMessages.ET": "Etiopia",
	"countriesL10nMessages.FI": "Finlanda",
	"countriesL10nMessages.FJ": "Fixhi",
	"countriesL10nMessages.FK": "Ishujt Falkland",
	"countriesL10nMessages.FM": "Federata Mikroneziane",
	"countriesL10nMessages.FO": "Ishujt Faroe",
	"countriesL10nMessages.FR": "Franca",
	"countriesL10nMessages.GA": "Gaboni",
	"countriesL10nMessages.GB": "Mbretëria e Bashkuar",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gjeorgjia",
	"countriesL10nMessages.GF": "Guajana Franceze",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gjibraltari",
	"countriesL10nMessages.GL": "Grënlanda",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guineja",
	"countriesL10nMessages.GP": "Guadelupë",
	"countriesL10nMessages.GQ": "Guineja Ekuatoriale",
	"countriesL10nMessages.GR": "Greqia",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guineja-Bisau",
	"countriesL10nMessages.GY": "Guajana",
	"countriesL10nMessages.HK": "Hong-Kongu",
	"countriesL10nMessages.HN": "Hondurasi",
	"countriesL10nMessages.HR": "Kroacia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungaria",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Irlanda",
	"countriesL10nMessages.IL": "Izraeli",
	"countriesL10nMessages.IM": "Ishulli i Manit",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Territori Britanik në Oqeanin Indian",
	"countriesL10nMessages.IQ": "Iraku",
	"countriesL10nMessages.IS": "Islanda",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Xhersi",
	"countriesL10nMessages.JM": "Xhamajka",
	"countriesL10nMessages.JO": "Jordania",
	"countriesL10nMessages.JP": "Japonia",
	"countriesL10nMessages.KE": "Kenia",
	"countriesL10nMessages.KG": "Kirgistani",
	"countriesL10nMessages.KH": "Kamboxhia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoret",
	"countriesL10nMessages.KN": "Shën Kits dhe Nevis",
	"countriesL10nMessages.KR": "Korea e Jugut",
	"countriesL10nMessages.KW": "Kuvajti",
	"countriesL10nMessages.KY": "Ishujt Kajman",
	"countriesL10nMessages.KZ": "Kazakistani",
	"countriesL10nMessages.LA": "Laosi",
	"countriesL10nMessages.LB": "Libani",
	"countriesL10nMessages.LC": "Shën Luçia",
	"countriesL10nMessages.LI": "Lihtenshtajni",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Lituania",
	"countriesL10nMessages.LU": "Luksemburgu",
	"countriesL10nMessages.LV": "Letonia",
	"countriesL10nMessages.LY": "Libia",
	"countriesL10nMessages.MA": "Maroku",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavia",
	"countriesL10nMessages.ME": "Mali i Zi",
	"countriesL10nMessages.MF": "Shën Martini (pjesa franceze)",
	"countriesL10nMessages.MG": "Madagaskari",
	"countriesL10nMessages.MH": "Ishujt Marshall",
	"countriesL10nMessages.MK": "Maqedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mianmari",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Makao",
	"countriesL10nMessages.MP": "Ishujt e Marianës Veriore",
	"countriesL10nMessages.MQ": "Martinika",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserati",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauric",
	"countriesL10nMessages.MV": "Maldivet",
	"countriesL10nMessages.MW": "Malavia",
	"countriesL10nMessages.MX": "Meksika",
	"countriesL10nMessages.MY": "Malajzia",
	"countriesL10nMessages.MZ": "Mozambiku",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Kaledonia e Re",
	"countriesL10nMessages.NE": "Nigeri",
	"countriesL10nMessages.NF": "Ishulli Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Holanda",
	"countriesL10nMessages.NO": "Norvegjia",
	"countriesL10nMessages.NP": "Nepali",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Zelanda e Re",
	"countriesL10nMessages.OM": "Omani",
	"countriesL10nMessages.PA": "Panamaja",
	"countriesL10nMessages.PE": "Peruja",
	"countriesL10nMessages.PF": "Polinezia Franceze",
	"countriesL10nMessages.PG": "Papua Guineja e Re",
	"countriesL10nMessages.PH": "Filipinet",
	"countriesL10nMessages.PK": "Pakistani",
	"countriesL10nMessages.PL": "Polonia",
	"countriesL10nMessages.PM": "Shën Pier dhe Mikelon",
	"countriesL10nMessages.PR": "Porto-Riko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugalia",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguai",
	"countriesL10nMessages.QA": "Katari",
	"countriesL10nMessages.RE": "Reunioni",
	"countriesL10nMessages.RO": "Rumania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Arabia Saudite",
	"countriesL10nMessages.SB": "Ishujt Solomon",
	"countriesL10nMessages.SC": "Sejshellet",
	"countriesL10nMessages.SE": "Suedia",
	"countriesL10nMessages.SG": "Singapori",
	"countriesL10nMessages.SH": "Shën Elena",
	"countriesL10nMessages.SI": "Sllovenia",
	"countriesL10nMessages.SJ": "Svalbardi dhe Jan Majeni",
	"countriesL10nMessages.SK": "Sllovakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegali",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinami",
	"countriesL10nMessages.SS": "Sudani i Jugut",
	"countriesL10nMessages.ST": "Sao-Tome-e-Principe",
	"countriesL10nMessages.SV": "Salvador",
	"countriesL10nMessages.SX": "Sint Marten",
	"countriesL10nMessages.SZ": "Suazilenda",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Ishujt Turks dhe Kaikos",
	"countriesL10nMessages.TD": "Çadi",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tajlanda",
	"countriesL10nMessages.TJ": "Taxhikistani",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timori Lindor",
	"countriesL10nMessages.TM": "Turkmenistani",
	"countriesL10nMessages.TN": "Tunizia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turqia",
	"countriesL10nMessages.TT": "Trinidad-e-Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajvani",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Shtetet e Bashkuara",
	"countriesL10nMessages.UY": "Uruguai",
	"countriesL10nMessages.UZ": "Uzbekistani",
	"countriesL10nMessages.VA": "Selia e Shenjtë (Shteti i Qytetit të Vatikanit)",
	"countriesL10nMessages.VC": "Sent-Vinsent dhe Grenadine",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Ishujt e Virgjër, Britanik",
	"countriesL10nMessages.VI": "Ishujt e Virgjër, SHBA",
	"countriesL10nMessages.VN": "Vietnami",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Uollis dhe Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosova",
	"countriesL10nMessages.YE": "Jemeni",
	"countriesL10nMessages.YT": "Majota",
	"countriesL10nMessages.ZA": "Afrika e Jugut",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabveja",
	"ToggleableEmailField.checkboxLabel": "Më dërgo me email një kopje të përgjigjeve të mia",
	"ToggleableEmailField.emailLabel": "Jep emrin e adresës"
};

var sr = {
	"AddressField.selectCountry": "Izaberite zemlju",
	"PhoneField.countryCode": "Pozivni broj za zemlju",
	"countriesL10nMessages.AC": "Ostrvo Asension",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Ujedinjeni Arapski Emirati",
	"countriesL10nMessages.AF": "Avganistan",
	"countriesL10nMessages.AG": "Antigva i Barbuda",
	"countriesL10nMessages.AI": "Angvila",
	"countriesL10nMessages.AL": "Albanija",
	"countriesL10nMessages.AM": "Jermenija",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "Američka Samoa",
	"countriesL10nMessages.AT": "Austrija",
	"countriesL10nMessages.AU": "Australija",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Olandska Ostrva",
	"countriesL10nMessages.AZ": "Azerbejdžan",
	"countriesL10nMessages.BA": "Bosna i Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladeš",
	"countriesL10nMessages.BE": "Belgija",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bugarska",
	"countriesL10nMessages.BH": "Bahrein",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Sveti Bartolomej",
	"countriesL10nMessages.BM": "Bermudi",
	"countriesL10nMessages.BN": "Brunej Darusalam",
	"countriesL10nMessages.BO": "Bolivija",
	"countriesL10nMessages.BQ": "Boner, Sveti Eustahije i Saba",
	"countriesL10nMessages.BR": "Brazil",
	"countriesL10nMessages.BS": "Bahami",
	"countriesL10nMessages.BT": "Butan",
	"countriesL10nMessages.BW": "Bocvana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosova (Kilingova) Ostrva",
	"countriesL10nMessages.CD": "Demokratska Republika Kongo",
	"countriesL10nMessages.CF": "Centralnoafrička Republika",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Švajcarska",
	"countriesL10nMessages.CI": "Obala Slonovače",
	"countriesL10nMessages.CK": "Kukova Ostrva",
	"countriesL10nMessages.CL": "Čile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Kolumbija",
	"countriesL10nMessages.CR": "Kostarika",
	"countriesL10nMessages.CV": "Zelenortska Ostrva",
	"countriesL10nMessages.CW": "Kurasao",
	"countriesL10nMessages.CX": "Božićno Ostrvo",
	"countriesL10nMessages.CY": "Kipar",
	"countriesL10nMessages.CZ": "Češka Republika",
	"countriesL10nMessages.DE": "Nemačka",
	"countriesL10nMessages.DJ": "Džibuti",
	"countriesL10nMessages.DK": "Danska",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominikanska Republika",
	"countriesL10nMessages.DZ": "Alžir",
	"countriesL10nMessages.EC": "Ekvador",
	"countriesL10nMessages.EE": "Estonija",
	"countriesL10nMessages.EG": "Egipat",
	"countriesL10nMessages.EH": "Zapadna Sahara",
	"countriesL10nMessages.ER": "Eritreja",
	"countriesL10nMessages.ES": "Španija",
	"countriesL10nMessages.ET": "Etiopija",
	"countriesL10nMessages.FI": "Finska",
	"countriesL10nMessages.FJ": "Fidži",
	"countriesL10nMessages.FK": "Foklandska Ostrva",
	"countriesL10nMessages.FM": "Savezne Države Mikronezije",
	"countriesL10nMessages.FO": "Farska Ostrva",
	"countriesL10nMessages.FR": "Francuska",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Ujedinjeno Kraljevstvo",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gruzija",
	"countriesL10nMessages.GF": "Francuska Gvajana",
	"countriesL10nMessages.GG": "Gernzi",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grenland",
	"countriesL10nMessages.GM": "Gambija",
	"countriesL10nMessages.GN": "Gvineja",
	"countriesL10nMessages.GP": "Gvadalupe",
	"countriesL10nMessages.GQ": "Ekvatorijalna Gvineja",
	"countriesL10nMessages.GR": "Grčka",
	"countriesL10nMessages.GT": "Gvatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Gvineja Bisao",
	"countriesL10nMessages.GY": "Gvajana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Hrvatska",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Mađarska",
	"countriesL10nMessages.ID": "Indonezija",
	"countriesL10nMessages.IE": "Irska",
	"countriesL10nMessages.IL": "Izrael",
	"countriesL10nMessages.IM": "Ostrvo Man",
	"countriesL10nMessages.IN": "Indija",
	"countriesL10nMessages.IO": "Britanska teritorija Indijskog okeana",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italija",
	"countriesL10nMessages.JE": "Džersi",
	"countriesL10nMessages.JM": "Jamajka",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenija",
	"countriesL10nMessages.KG": "Kirgistan",
	"countriesL10nMessages.KH": "Kambodža",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoros",
	"countriesL10nMessages.KN": "Sent Kits i Nevis",
	"countriesL10nMessages.KR": "Južna Koreja",
	"countriesL10nMessages.KW": "Kuvajt",
	"countriesL10nMessages.KY": "Kajmanska Ostrva",
	"countriesL10nMessages.KZ": "Kazahstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Liban",
	"countriesL10nMessages.LC": "Sveta Lucija",
	"countriesL10nMessages.LI": "Lihtenštajn",
	"countriesL10nMessages.LK": "Šri Lanka",
	"countriesL10nMessages.LR": "Liberija",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Litvanija",
	"countriesL10nMessages.LU": "Luksemburg",
	"countriesL10nMessages.LV": "Letonija",
	"countriesL10nMessages.LY": "Libija",
	"countriesL10nMessages.MA": "Maroko",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldavija",
	"countriesL10nMessages.ME": "Crna Gora",
	"countriesL10nMessages.MF": "Sveti Martin (francuski deo)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Maršalska Ostrva",
	"countriesL10nMessages.MK": "Makedonija",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Mijanmar",
	"countriesL10nMessages.MN": "Mongolija",
	"countriesL10nMessages.MO": "Makao",
	"countriesL10nMessages.MP": "Severna Marijanska ostrva",
	"countriesL10nMessages.MQ": "Martinik",
	"countriesL10nMessages.MR": "Mauritanija",
	"countriesL10nMessages.MS": "Monserat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauricijus",
	"countriesL10nMessages.MV": "Maldivi",
	"countriesL10nMessages.MW": "Malavi",
	"countriesL10nMessages.MX": "Meksiko",
	"countriesL10nMessages.MY": "Malezija",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibija",
	"countriesL10nMessages.NC": "Nova Kaledonija",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Ostrvo Norfok",
	"countriesL10nMessages.NG": "Nigerija",
	"countriesL10nMessages.NI": "Nikaragva",
	"countriesL10nMessages.NL": "Holandija",
	"countriesL10nMessages.NO": "Norveška",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Novi Zeland",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Francuska Polinezija",
	"countriesL10nMessages.PG": "Papua Nova Gvineja",
	"countriesL10nMessages.PH": "Filipini",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poljska",
	"countriesL10nMessages.PM": "Sveti Petar i Mikelon",
	"countriesL10nMessages.PR": "Portoriko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugalija",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragvaj",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Reunion",
	"countriesL10nMessages.RO": "Rumunija",
	"countriesL10nMessages.RS": "Srbija",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Saudijska Arabija",
	"countriesL10nMessages.SB": "Solomonska Ostrva",
	"countriesL10nMessages.SC": "Sejšeli",
	"countriesL10nMessages.SE": "Švedska",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Sveta Jelena",
	"countriesL10nMessages.SI": "Slovenija",
	"countriesL10nMessages.SJ": "Svalbard i Jan Majen",
	"countriesL10nMessages.SK": "Slovačka",
	"countriesL10nMessages.SL": "Sijera Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalija",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Južni Sudan",
	"countriesL10nMessages.ST": "Sao Tome i Principe",
	"countriesL10nMessages.SV": "Salvador",
	"countriesL10nMessages.SX": "Sveti Martin",
	"countriesL10nMessages.SZ": "Esvatini",
	"countriesL10nMessages.TA": "Tristan da Kunja",
	"countriesL10nMessages.TC": "Ostrva Turks i Kaikos",
	"countriesL10nMessages.TD": "Čad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tajland",
	"countriesL10nMessages.TJ": "Tadžikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunis",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turska",
	"countriesL10nMessages.TT": "Trinidad i Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tajvan",
	"countriesL10nMessages.TZ": "Tanzanija",
	"countriesL10nMessages.UA": "Ukrajina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Sjedinjene Države",
	"countriesL10nMessages.UY": "Urugvaj",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Sveta stolica (grad-država Vatikan)",
	"countriesL10nMessages.VC": "Sveti Vinsent i Grenadini",
	"countriesL10nMessages.VE": "Venecuela",
	"countriesL10nMessages.VG": "Devičanska Ostrva (britanska)",
	"countriesL10nMessages.VI": "Devičanska Ostrva (SAD)",
	"countriesL10nMessages.VN": "Vijetnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Valis i Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Jemen",
	"countriesL10nMessages.YT": "Majot",
	"countriesL10nMessages.ZA": "Južnoafrička Republika",
	"countriesL10nMessages.ZM": "Zambija",
	"countriesL10nMessages.ZW": "Zimbabve",
	"ToggleableEmailField.checkboxLabel": "Pošalji mi kopiju odgovora imejlom",
	"ToggleableEmailField.emailLabel": "Unesite imejl adresu"
};

var sv = {
	"ActionMenuTagPin.PIN": "Fäst",
	"ActionMenuTagPin.TAG": "Tagg",
	"AddressField.selectCountry": "Välj land",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Besvarade: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "F{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Hoppade över: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "Så här ser det ut:",
	"AsyncJobLoadingState.LOADING": "Läser in svar",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Vi meddelar dig när det är klart",
	"AsyncJobLoadingState.TAGGING_TITLE": "Taggar svar",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Taggar gäller för befintliga och framtida svar. Lägg till ett mellanslag eller skriv <strong>OR</strong> mellan ord för att använda mer än ett sökord.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Tagga svar automatiskt",
	"Button.OK_LABEL": "OK",
	"CalendarPicker.month": "Välj månad",
	"CalendarPicker.months": "{months, select, January {januari} February {februari} March {mars} April {april} May {maj} June {juni} July {juli} August {augusti} September {september} October {oktober} November {november} December {december} other {annan}}",
	"CalendarPicker.nextMonth": "Nästa månad",
	"CalendarPicker.previousMonth": "Föregående månad",
	"CalendarPicker.year": "Välj år",
	"ColorSwatches.TAG": "Tagg {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "I ordmoln med flera färger används både färg och storlek för att visa antalet gånger ett ord eller en fras förekommer.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Så här används färg i ordmoln",
	"ColorTab.DOWN_ARROW": "Välj listpil",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Flera färger",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "En färg",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "Avbryt",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Skapa ett nytt ord",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Det nya ordet ersätter vart och ett av de valda orden nedan.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Redigera kombinerade ord",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Kombinera ord",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Skapa ett nytt ord som ersätter ett eller flera valda ord. Du kan redigera detta senare för att få tillbaka dina ursprungliga ord.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Skriv valfritt nytt ord",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Kombinera",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Välj ord som ska kombineras",
	"CreateTagFromSearch.CLOSE_MENU": "Avbryt",
	"CreateTagFromSearch.DUPLICATED_NAME": "Du har redan en tagg med det namnet.",
	"CreateTagFromSearch.NEW_TAG": "Ny tagg",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Inget svar \n\t\ttaggas} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} svar \n\t\t\t\n\t\ttaggas} other {\n\t\t\t\t{responseTaggedFormatted}  svar \n\t\t\t\n\t\ttaggas}}}}",
	"CreateTagFromSearch.SAVE": "Spara",
	"CreateTagFromSearch.TAG": "Tagg",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Taggnamn får inte lämnas tomt.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Taggnamnet får inte bestå av enbart mellanslag.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Tagga svar som matchar dessa sökord. Välj <strong>Skapa tagg</strong>. Välj en färg och spara. Taggen gäller för befintliga och framtida svar.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Tagga matchande svar",
	"DashboardMenuContent.CREATE": "Skapa",
	"DashboardMenuContent.NAME": "Namn",
	"DashboardMenuContent.NEW_DASHBOARD": "Ny instrumentpanel",
	"DateInputWrapper.dateButton": "Öppna kalender",
	"DateInputWrapper.dateFormatIntl": "DD/MM/ÅÅÅÅ",
	"DateInputWrapper.dateFormatUs": "MM/DD/ÅÅÅÅ",
	"DateInputWrapper.dateLabel": "Datum",
	"DateTime.errorEnterHours": "Ange timmar från 0 till 23.",
	"DateTime.errorEnterMinutes": "Ange minuter från 0 till 59.",
	"DateTime.errorSelectPeriod": "Välj FM/EM.",
	"DateTime.errorYearOutOfRange": "Ange år mellan 1900 och 9999.",
	"DateTime.formatErrorDateIntl": "Ange datum i formatet DD/MM/ÅÅÅÅ.",
	"DateTime.formatErrorDateUs": "Ange datum i formatet MM/DD/ÅÅÅÅ.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "Avbryt",
	"EditorButtons.COPY_BUTTON": "KOPIERA FRÅGA",
	"EditorButtons.MOVE_BUTTON": "Flytta fråga",
	"EditorButtons.NEXT_QUESTION_BUTTON": "NÄSTA FRÅGA",
	"EditorButtons.SAVE_BUTTON": "Spara",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Datatrender gäller inte för denna fråga",
	"FallbackComponent.ERROR": "Fel vid inläsning av fråga",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Välj en fil</div> <div class={dragClass}>eller dra den hit</div>",
	"FileUpload.fileUploaded": "Filen har laddats upp",
	"FileUpload.fileUploading": "Filen laddas upp",
	"FileUpload.removeFile": "Ta bort fil",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Ersätt fil</div> <div class={dragClass}>eller dra den hit</div>",
	"FileUpload.supportedFiles": "Filer som stöds",
	"FilterMenuContent.APPLY_ACTIONS": "Tillämpa",
	"FilterMenuContent.CLEAR_ACTIONS": "Rensa",
	"FilterMenuContent.CLOSE_MENU": "Avbryt",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "Avbryt",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Dölj",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Spara",
	"HideWordsModal.HIDE_WORDS_HEADER": "Dölj ord",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Välj ord för att dölja dem från ordmolnet.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Välj ord som ska döljas",
	"IconHelpStyled.HELP": "Hjälp",
	"IndividualResponseCard.NO_RESPONSE": "Svaranden hoppade över denna fråga",
	"IndividualResponseCard.QUESTION_NUMBER": "F{number}",
	"InfoBannerTagManagement.CLOSE": "Stäng",
	"InfoBannerTagManagement.NEW": "Ny",
	"InfoBannerTagManagement.TAG_RESPONSES": "Tagga svar",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Lägg till sökord för att konfigurera automatiska taggar och hålla ordning på svaren.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Tagga svaren för enkel hantering",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Positiva: {percentage,number,percent}} negative {Negativa: {percentage,number,percent}} neutral {Neutrala: {percentage,number,percent}} unknown {Inte identifierat: {percentage,number,percent}} other {annan}}",
	"LoadingState.LOADING": "Läser in svar",
	"MatrixDropdown.EmptyTopNativeSelect": "Välj ett alternativ",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Konfidensnivå",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Nyttopoäng",
	"Modal.CloseModal": "Stäng verktygstips",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {måndag} Tu {tisdag} We {onsdag} Th {torsdag} Fr {fredag} Sa {lördag} Su {söndag} other {annan}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {må} Tu {ti} We {on} Th {to} Fr {fr} Sa {lö} Su {sö} other {annan}}",
	"MultiColorSelectorMenu.APPLY": "Tillämpa",
	"MultiColorSelectorMenu.CANCEL": "Avbryt",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Organisera dina data i en anpassad layout, förklara dina resultat och dela din instrumentpanel med vem som helst. Organisera dina data i en anpassad layout, förklara dina resultat och dela din instrumentpanel med vem som helst.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Instrumentpaneler",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Läs mer",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">På en skala från {minValue} till {maxValue}, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} för {minLabelText}, {maxValue} för {maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "mätzon för poäng mellan {min} och {max}",
	"NpsGaugeChart.NPS_CHART": "NPS-mättabell",
	"NpsGaugeChart.NPS_SCORE": "NPS-poäng på {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "bock i diagram för {score}-poängens position",
	"OrderControl.AriaNA": "Ej tillämpligt",
	"OrderControl.NA": "Ej tillämpligt",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "”{title}” svar ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "dölj lista med annat svar",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "visa lista med annat svar",
	"PhoneField.countryCode": "Landskod",
	"QuestionCardItemFooter.FILTER_APPLIED": "Tillämpade regler: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtrera",
	"QuestionCardTitle.ANSWERED": "Besvarade: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Valda: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Hoppade över: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} poäng} other {{score}/{total} poäng}}",
	"QuizResultScore.SKIPPED": "Överhoppad",
	"RangeTab.RANGE_TAB_LABEL": "Omnämnanden: {value}",
	"RangeTab.TOTAL_WORDS": "ord totalt",
	"Ranking.AriaRankAtBottom": "{label} har flyttats längst ner i listan",
	"Ranking.AriaRankAtTop": "{label} har flyttats längst upp i listan",
	"Ranking.AriaRankMovedDown": "{label} har flyttats ner till nummer {position}",
	"Ranking.AriaRankMovedUp": "{label} har flyttats upp till nummer {position}",
	"RankingItem.AriaRankMoveDown": "Flytta ner {label}",
	"RankingItem.AriaRankMoveUp": "Flytta upp {label}",
	"RatingChart.AVERAGE_RATING": "i medelbetyg",
	"RatingChartLegend.ANSWERED": "Besvarade: {number}",
	"RatingChartLegend.RATING": "{rating} stjärna",
	"RatingChartLegend.SKIPPED": "Hoppade över: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Begär funktion",
	"RequestAccessButton.UPGRADE": "Uppgradera",
	"RespondentQuestionTitle.REQUIRED": "(Obligatoriskt)",
	"ResponseItem.EXPAND_MENU_LABEL": "Expandera meny",
	"ResponseItem.MENU_LABEL": "Lägg till taggar",
	"ResponseItem.RESPONDENT_ID": "Svarande-ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Visa svarandes svar",
	"ResponseStatusBar.CLEAR": "Rensa",
	"ResponseStatusBar.FAILED_TO_LOAD": "Det gick inte att läsa in svaren",
	"ResponseStatusBar.NO_RESPONSES_YET": "Inga svar ännu",
	"ResponseStatusBar.SEARCHING": "Söker ...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {positiv attityd} negative {negativ attityd} neutral {neutral attityd} other {okänd attityd}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visar {resultsCountFormatted} valt svar för {searchTextFormatted} med {filters}   } other {Visar {resultsCountFormatted} svar för {searchTextFormatted} med {filters}   }}} other {{hasSelectedResponse, select, true {Visar {resultsCountFormatted}  valda svar för {searchTextFormatted}   med {filters}   } other {Visar {resultsCountFormatted} svar för {searchTextFormatted} med {filters}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visar {resultsCountFormatted} valt svar med {filters}   } other {Visar {resultsCountFormatted} svar med {filters}   }}} other {{hasSelectedResponse, select, true {Visar {resultsCountFormatted}  valda svar med {filters}   } other {Visar {resultsCountFormatted}   svar med {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visar {resultsCountFormatted} valt svar för {searchTextFormatted}   } other {Visar {resultsCountFormatted} svar för {searchTextFormatted}   }}} other {{hasSelectedResponse, select, true {Visar {resultsCountFormatted} valda svar för {searchTextFormatted}   } other {Visar {resultsCountFormatted} svar för {searchTextFormatted}   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Visar {resultsCountFormatted} valt svar   } other {Visar {resultsCountFormatted} svar   }}} other {{hasSelectedResponse, select, true {Showing {resultsCountFormatted} valda svar   } other {Visar {resultsCountFormatted} svar   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Rensa",
	"ResponseToolbar.CREATE_TAG": "Skapa tagg",
	"ResponseToolbar.SELECT_ALL": "Välj alla svar",
	"ResponseToolbar.UNSELECT_ALL": "Avmarkera alla svar",
	"SearchResponsesInput.CLEAR": "Rensa sökning",
	"SearchResponsesInput.SEARCH": "Sök",
	"SearchResponsesInput.SEARCH_RESPONSES": "Sök bland svar",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Använd citattecken <strong>“”</strong> för att söka efter en exakt fras.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "Skriv <strong>OR</strong> för att hitta en av två eller flera söktermer.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Skriv <b>AND</b> för att hitta mer än en term.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Använd <strong>NOT</strong> för att utesluta en term från sökningen.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Skriv <strong>*</strong> efter en term för att söka efter prefix.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "Använd parenteser för att kombinera AND/OR/NOT-baserade sökningar.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Skriv <strong>~2</strong> efter en term om du vill ha resultat som matchar termen plus upp till två andra tecken.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Sökalternativ",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Läs mer",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Nej tack",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Aktivera",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "<span id=\"link\">Automatiska insikter</span> är nu tillgängligt i ditt konto.\n      Den här inställningen omfattar alla funktioner som använder maskininlärning för att bearbeta enkäten och leverera insikter till dig.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Du kan alltid ändra inställningarna i <span id=\"link\">Mitt konto</span>",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Automatiska insikter",
	"SentimentBanner.GENERATE_SENTIMENT": "Analysera attityd",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Attitydanalys",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "Identifiera attityderna bakom skriftliga svar för att förstå vad folk tycker.",
	"SentimentBanner.UPGRADE": "Uppgradera",
	"SentimentBanner.WATCH_A_DEMO": "Titta på en demo",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "Så här ser det ut:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Filtrera efter positiv attityd} negative {Filtrera efter negativ attityd} neutral {Filtrera efter neutral attityd} other {Filtrera efter okänd attityd}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Expandera meny",
	"SentimentFilterMenu.MENU_LABEL": "efter attityd",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Begär funktion",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Identifiera känslor och attityder bakom skriftliga svar.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "Nyhet!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Vi presenterar attitydanalys",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Uppgradera",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Titta på en demo",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Nej tack",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Begär funktion",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Uppgradera",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Bild för uppgradering till attitydanalys",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Aktivera attitydanalys med ett enda klick. Vi delar upp dina textsvar som positiva, neutrala eller negativa, för att avslöja känslorna bakom det folk svarar.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "Få reda på vad folk tycker direkt",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Attitydanalys",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Attityder: PÅ} false {Attityder: AV} other {Attityder: annan}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Attityder: LADDAS ...",
	"SlideBoxNavigation.NEXT_BUTTON": "Nästa",
	"SlideBoxNavigation.NEXT_SLIDE": "Nästa bild",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "FÖREGÅENDE",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Föregående bild",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} av {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Rensa",
	"Slider.STEP_MESSAGE": "Vi har justerat det angivna numret baserat på skjutreglagets skala.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Besvarade: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "F{questionNumber}{questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Hoppade över: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Skärningspunkten mellan billig och dyr ger en övergripande prisindikation för din produkt.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Priskänslighet (Van Westendorp)",
	"SrText.CORRECT": "Rätt",
	"SrText.INCORRECT": "Fel",
	"StarRating.Heart": "{count, plural, one {{count} hjärta} other {{count} hjärtan}}",
	"StarRating.STAR": "{count, plural, one {{count} stjärna} other {{count} stjärnor}}",
	"StarRating.Smiley": "{count, plural, one {{count} glatt ansikte} other {{count} glada ansikten}}",
	"StarRating.Thumb": "{count, plural, one {{count} tumme} other {{count} tummar}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Låsikon för taggtabb",
	"TabList.RESPONSES": "SVAR ({responseCount})",
	"TabList.TAGS": "TAGGAR ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "TEMATISK ANALYS",
	"TabList.WORD_CLOUD": "ORDMOLN",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Expandera meny",
	"TagFilterMenu.MENU_LABEL": "Filtrering: efter tagg",
	"TagList.TAGS": "Taggar",
	"TagList.TAGS_KEYWORDS": "Sökord",
	"TagList.TAG_CONTROLS": "Taggkontroller",
	"TagList.TAG_NUMBER_PERCENT": "Procent taggar och antal taggar",
	"TagList.TAG_PERCENTAGE_BAR": "Indikator för taggprocent",
	"TagListItem.DELETE": "Ta bort",
	"TagListItem.EDIT": "Redigera",
	"TagListItem.VIEW_ALL": "Visa alla",
	"TagListPanel.LOADING": "Läser in taggar",
	"TagListPanel.NEW_TAG": "+ Ny tagg",
	"TagListPanel.TAGS": "Taggar",
	"TagMenuContent.APPLY_ACTIONS": "Tillämpa",
	"TagMenuContent.CLEAR_ACTIONS": "Rensa",
	"TagMenuContent.CLOSE_MENU": "Avbryt",
	"TagMenuContent.DUPLICATED_NAME": "Du har redan en tagg med det namnet.",
	"TagMenuContent.NEW_TAG": "Ny tagg",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Läs mer >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Begär funktion",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Uppgradera",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Stäng",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Sök, tagga och filtrera skrivna svar och se ordmoln med vanliga ord och fraser.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Textanalys",
	"TimeInputWrapper.am": "FM",
	"TimeInputWrapper.daytimeLabel": "FM/EM",
	"TimeInputWrapper.hhPlaceholder": "hh",
	"TimeInputWrapper.hour": "timme",
	"TimeInputWrapper.minutes": "minuter",
	"TimeInputWrapper.mmPlaceholder": "mm",
	"TimeInputWrapper.pm": "PM",
	"TimeInputWrapper.timeLabel": "Tid",
	"ToggleableEmailField.checkboxLabel": "E-posta mig kopia av mina svar",
	"ToggleableEmailField.emailLabel": "Ange mejladress",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Ta bort",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted} svar kommer inte att taggas längre.} other {{responseCountFormatted} svar kommer inte att taggas längre.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Om du raderar en tagg tas den bort från alla taggade svar. Detta kan inte ångras.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Radera tagg?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Spara",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Vill du spara ändringar?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Detta tar bort taggen från alla svar. Taggen används igen för svar som \n      matchar de redigerade nyckelorden.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "Avbryt",
	"UpgradeBanner.SUBTITLE": "Uppgradera så kan du anpassa ditt eget ordmoln.",
	"UpgradeBanner.TITLE": "Gillar du det du ser?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% av svar",
	"VanWestendorpChartDataTable.PRICE": "PRIS",
	"VanWestendorpChartDataTable.RESPONSES": "Svar",
	"WordCloudCustomizationButton.CUSTOMIZE": "Anpassa",
	"WordCloudCustomizationFooter.APPLY": "Tillämpa",
	"WordCloudCustomizationFooter.CANCEL": "Avbryt",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Använd för alla ordmoln i enkäten (några kanske inte ändras)",
	"WordCloudCustomizationFooter.NO_THANKS": "Nej tack",
	"WordCloudCustomizationFooter.REQUEST": "Begär funktion",
	"WordCloudCustomizationFooter.SAVE": "Spara",
	"WordCloudCustomizationFooter.UPGRADE": "Uppgradera",
	"WordCloudCustomizationTabs.COLOR": "Färg",
	"WordCloudCustomizationTabs.FONT": "Typsnitt",
	"WordCloudCustomizationTabs.RANGE": "INTERVALL",
	"WordCloudLegend.FEWER": "färre",
	"WordCloudLegend.MORE": "fler",
	"WordCloudLegend.TITLE": "Antal förekomster",
	"WordCloudListFooter.COMBINED_WORDS": "Kombinerade ord",
	"WordCloudListFooter.HIDDEN_WORDS": "Dolda ord",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Expandera meny",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Kombinera med",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Redigera kombinerade ord",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Expandera meny",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Dölj från ordmoln",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Dela kombinerade ord",
	"WordCloudLoadingState.LOADING": "Läser in ordmoln ...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Stäng-knapp",
	"WordCloudPreviewOverlay.OK_BUTTON": "OK",
	"WordCloudPreviewOverlay.SUBTITLE": "Förhandsgranska funktionerna för att anpassa ordmoln.",
	"WordCloudPreviewOverlay.TITLE": "Testa det!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Molnvy",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Listvy",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Förekomster: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "Viktigaste ord nr {number}",
	"WordCount.IN_PROGRESS": "Textanalys pågår ...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Inga viktiga ord hittades.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {Visar {count} ord } other {Visar {count} ord }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Filtrera efter positiv attityd} negative {Filtrera efter negativ attityd} neutral {Filtrera efter neutral attityd} other {Filtrera efter oidentifierad attityd}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Positiv} negative {Negativ} neutral {Neutral} other {Okänd}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Positiva: {percentage,number,percent}} negative {Negativa: {percentage,number,percent}} neutral {Neutrala: {percentage,number,percent}} other {Okänt: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "Avbryt",
	"copyTagEditor.DUPLICATED_NAME": "Du har redan en tagg med det namnet.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Ange ett namn för taggen",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Ange ett eller flera sökord",
	"copyTagEditor.KEYWORDS": "<strong>Sökord</strong> (valfritt)",
	"copyTagEditor.SAVE": "Spara",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Taggen gäller för alla svar med matchande sökord",
	"copyTagEditor.TAG_NAME": "<strong>Taggnamn</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Taggnamn får inte lämnas tomt.",
	"copyTagEditor.TAG_NAME_SPACES": "Taggnamnet får inte bestå av enbart mellanslag.",
	"copyTagEditor.TAG_TOO_LONG": "Taggar får inte vara längre än 128 tecken.",
	"countriesL10nMessages.AC": "Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Förenade arabemiraten",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua och Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albanien",
	"countriesL10nMessages.AM": "Armenien",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "American Samoa",
	"countriesL10nMessages.AT": "Österrike",
	"countriesL10nMessages.AU": "Australien",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland",
	"countriesL10nMessages.AZ": "Azerbajdzjan",
	"countriesL10nMessages.BA": "Bosnien och Hercegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgien",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgarien",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius och Saba",
	"countriesL10nMessages.BR": "Brasilien",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Kokosöarna",
	"countriesL10nMessages.CD": "Demokratiska republiken Kongo",
	"countriesL10nMessages.CF": "Centralafrikanska republiken",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Schweiz",
	"countriesL10nMessages.CI": "Elfenbenskusten",
	"countriesL10nMessages.CK": "Cooköarna",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Kina",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Kap Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Julön",
	"countriesL10nMessages.CY": "Cypern",
	"countriesL10nMessages.CZ": "Tjeckien",
	"countriesL10nMessages.DE": "Tyskland",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Danmark",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Dominikanska republiken",
	"countriesL10nMessages.DZ": "Algeriet",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estland",
	"countriesL10nMessages.EG": "Egypten",
	"countriesL10nMessages.EH": "Västsahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Spanien",
	"countriesL10nMessages.ET": "Etiopien",
	"countriesL10nMessages.FI": "Finland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falklandsöarna",
	"countriesL10nMessages.FM": "Mikronesiska federationen",
	"countriesL10nMessages.FO": "Färöarna",
	"countriesL10nMessages.FR": "Frankrike",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Storbritannien",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgien",
	"countriesL10nMessages.GF": "Franska Guyana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Grönland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Ekvatorialguinea",
	"countriesL10nMessages.GR": "Grekland",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hongkong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Kroatien",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Ungern",
	"countriesL10nMessages.ID": "Indonesien",
	"countriesL10nMessages.IE": "Irland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Isle of Man",
	"countriesL10nMessages.IN": "Indien",
	"countriesL10nMessages.IO": "Brittiska territoriet i Indiska oceanen",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "Island",
	"countriesL10nMessages.IT": "Italien",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordanien",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirgizstan",
	"countriesL10nMessages.KH": "Kambodja",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komorerna",
	"countriesL10nMessages.KN": "S:t Kitts och Nevis",
	"countriesL10nMessages.KR": "Sydkorea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Caymanöarna",
	"countriesL10nMessages.KZ": "Kazakstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Libanon",
	"countriesL10nMessages.LC": "S:t Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Litauen",
	"countriesL10nMessages.LU": "Luxemburg",
	"countriesL10nMessages.LV": "Lettland",
	"countriesL10nMessages.LY": "Libyen",
	"countriesL10nMessages.MA": "Marocko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldavien",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (franska delen)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshallöarna",
	"countriesL10nMessages.MK": "Makedonien",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongoliet",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Nordmarianerna",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritanien",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldiverna",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexiko",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Moçambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "Nya Kaledonien",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolkön",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Nederländerna",
	"countriesL10nMessages.NO": "Norge",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nya Zeeland",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Franska Polynesien",
	"countriesL10nMessages.PG": "Papua Nya Guinea",
	"countriesL10nMessages.PH": "Filippinerna",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polen",
	"countriesL10nMessages.PM": "S:t Pierre och Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Rumänien",
	"countriesL10nMessages.RS": "Serbien",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudiarabien",
	"countriesL10nMessages.SB": "Salomonöarna",
	"countriesL10nMessages.SC": "Seychellerna",
	"countriesL10nMessages.SE": "Sverige",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Sankt Helena",
	"countriesL10nMessages.SI": "Slovenien",
	"countriesL10nMessages.SJ": "Svalbard och Jan Mayen",
	"countriesL10nMessages.SK": "Slovakien",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Sydsudan",
	"countriesL10nMessages.ST": "São Tomé och Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks- och Caicosöarna",
	"countriesL10nMessages.TD": "Tchad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tadzjikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Östtimor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisien",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turkiet",
	"countriesL10nMessages.TT": "Trinidad och Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraina",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "USA",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Vatikanstaten",
	"countriesL10nMessages.VC": "Saint Vincent & Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Brittiska jungfruöarna",
	"countriesL10nMessages.VI": "Amerikanska Jungfruöarna",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis- och Futunaöarna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Sydafrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"helper.INDIFFERENCE_PRICE_POINT": "Likgiltighetsprispunkt (IPP)",
	"helper.IPP": "Likgiltighetsprispunkt (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "Optimal prispunkt (OPP)",
	"helper.OPP_DESCRIPTION": "Optimal prispunkt",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Punkt för marginellt billigt (PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Punkt för marginellt dyrt (PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Punkt för marginellt billigt (PMC)",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Punkt för marginellt dyrt (PME)"
};

var sw = {
	"AddressField.selectCountry": "Chagua nchi",
	"PhoneField.countryCode": "Msimbo wa Nchi",
	"countriesL10nMessages.AC": "Kisiwa cha Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Falme za Kiarabu",
	"countriesL10nMessages.AF": "Afuganistani",
	"countriesL10nMessages.AG": "Antigua na Babuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Ajentina",
	"countriesL10nMessages.AS": "Samoa ya Marekani",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Visiwa vya Åland",
	"countriesL10nMessages.AZ": "Azebaijani",
	"countriesL10nMessages.BA": "Bosnia na Hezegovina",
	"countriesL10nMessages.BB": "Babados",
	"countriesL10nMessages.BD": "Bangladeshi",
	"countriesL10nMessages.BE": "Ubelgiji",
	"countriesL10nMessages.BF": "Bukinafaso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahareni",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bamuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius na Saba",
	"countriesL10nMessages.BR": "Brazili",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Butani",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Visiwa vya Cocos (Keeling)",
	"countriesL10nMessages.CD": "Jamhuri ya Kidemokrasia ya Kongo",
	"countriesL10nMessages.CF": "Jamhuri ya Afrika ya Kati",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "Uswisi",
	"countriesL10nMessages.CI": "Kodivaa",
	"countriesL10nMessages.CK": "Visiwa vya Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Kameruni",
	"countriesL10nMessages.CN": "Uchina",
	"countriesL10nMessages.CO": "Kolombia",
	"countriesL10nMessages.CR": "Kostarica",
	"countriesL10nMessages.CV": "Kepuvede",
	"countriesL10nMessages.CW": "Kurakao",
	"countriesL10nMessages.CX": "Kisiwa cha Krismasi",
	"countriesL10nMessages.CY": "Saiprasi",
	"countriesL10nMessages.CZ": "Jamhuri ya Zechia",
	"countriesL10nMessages.DE": "Ujerumani",
	"countriesL10nMessages.DJ": "Jibuti",
	"countriesL10nMessages.DK": "Denmaki",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Jamhuri ya Dominika",
	"countriesL10nMessages.DZ": "Aljeria",
	"countriesL10nMessages.EC": "Ekwado",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Misri",
	"countriesL10nMessages.EH": "Sahara Magharibi",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Uhispania",
	"countriesL10nMessages.ET": "Ethiopia",
	"countriesL10nMessages.FI": "Ufini",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Visiwa vya Falkland",
	"countriesL10nMessages.FM": "Maikronesia",
	"countriesL10nMessages.FO": "Visiwa vya Faroe",
	"countriesL10nMessages.FR": "Ufaransa",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Uingereza",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Jojia",
	"countriesL10nMessages.GF": "Guiana ya Ufaransa",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibralta",
	"countriesL10nMessages.GL": "Greenland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Gine",
	"countriesL10nMessages.GP": "Gwadelupe",
	"countriesL10nMessages.GQ": "Ginekweta",
	"countriesL10nMessages.GR": "Ugiriki",
	"countriesL10nMessages.GT": "Gwatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Ginebissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Hondurasi",
	"countriesL10nMessages.HR": "Korasia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungaria",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Ayalandi",
	"countriesL10nMessages.IL": "Israeli",
	"countriesL10nMessages.IM": "Isle of Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "Eneo la Bahari ya Hindi la Uingereza",
	"countriesL10nMessages.IQ": "Iraki",
	"countriesL10nMessages.IS": "Aisilandi",
	"countriesL10nMessages.IT": "Italia",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Yordani",
	"countriesL10nMessages.JP": "Japani",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kirigizistani",
	"countriesL10nMessages.KH": "Kambodia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komoro",
	"countriesL10nMessages.KN": "Saint Kitts na Nevis",
	"countriesL10nMessages.KR": "Korea Kusini",
	"countriesL10nMessages.KW": "Kuwaiti",
	"countriesL10nMessages.KY": "Visiwa vya Cayman",
	"countriesL10nMessages.KZ": "Kazakistani",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Lebanoni",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Lishenteni",
	"countriesL10nMessages.LK": "Srilanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Litwania",
	"countriesL10nMessages.LU": "Lasembagi",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Moroko",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (sehemu ya Ufaransa)",
	"countriesL10nMessages.MG": "Madagaska",
	"countriesL10nMessages.MH": "Visiwa vya Marshall",
	"countriesL10nMessages.MK": "Maseedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanma",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Makao",
	"countriesL10nMessages.MP": "Kisiwa cha Mariana Kaskazini",
	"countriesL10nMessages.MQ": "Matinikiu",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserati",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Morisi",
	"countriesL10nMessages.MV": "Maldivi",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Meksiko",
	"countriesL10nMessages.MY": "Malesia",
	"countriesL10nMessages.MZ": "Msumbiji",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "New Caledonia",
	"countriesL10nMessages.NE": "Nijeri",
	"countriesL10nMessages.NF": "Kisiwa cha Norfolk",
	"countriesL10nMessages.NG": "Naijeria",
	"countriesL10nMessages.NI": "Nikaragwa",
	"countriesL10nMessages.NL": "Uholanzi",
	"countriesL10nMessages.NO": "Norwe",
	"countriesL10nMessages.NP": "Nepali",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Nyuzilandi",
	"countriesL10nMessages.OM": "Omani",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polinesia ya Ufaransa",
	"countriesL10nMessages.PG": "Papua",
	"countriesL10nMessages.PH": "Ufilipino",
	"countriesL10nMessages.PK": "Pakistani",
	"countriesL10nMessages.PL": "Polandi",
	"countriesL10nMessages.PM": "Saint Pierre na Miquelon",
	"countriesL10nMessages.PR": "Pwetoriko",
	"countriesL10nMessages.PS": "Palestina",
	"countriesL10nMessages.PT": "Ureno",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paragwai",
	"countriesL10nMessages.QA": "Katari",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudia",
	"countriesL10nMessages.SB": "Visiwa vya Solomon",
	"countriesL10nMessages.SC": "Ushelisheli",
	"countriesL10nMessages.SE": "Uswidi",
	"countriesL10nMessages.SG": "Singapoo",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard na Jan Mayen",
	"countriesL10nMessages.SK": "Slovakia",
	"countriesL10nMessages.SL": "Sieraleoni",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegali",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Sudan Kusini",
	"countriesL10nMessages.ST": "Sao Tome na Principe",
	"countriesL10nMessages.SV": "Elsalvado",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Uswazi",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "visiwa vya Turks na Caicos",
	"countriesL10nMessages.TD": "Chadi",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailandi",
	"countriesL10nMessages.TJ": "Tajikistani",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistani",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Uturuki",
	"countriesL10nMessages.TT": "Trinidad na Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwani",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraini",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Marekani",
	"countriesL10nMessages.UY": "Urugwai",
	"countriesL10nMessages.UZ": "Uzbekistani",
	"countriesL10nMessages.VA": "Holy See (Jimbo la Jiji la Vatican)",
	"countriesL10nMessages.VC": "Saint Vincent na Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Visiwa vya Virgin, Uingereza",
	"countriesL10nMessages.VI": "Visiwa vya Virgin, Marekani",
	"countriesL10nMessages.VN": "Vietnamu",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis na Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemeni",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Afrika",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Nitumie nakala ya majibu yangu kupitia barua pepe",
	"ToggleableEmailField.emailLabel": "Weka anwani yako ya barua pepe"
};

var ta = {
	"AddressField.selectCountry": "நாட்டைத் தேர்ந்தெடுக்கவும்",
	"PhoneField.countryCode": "தேசக் குறியீடு",
	"countriesL10nMessages.AC": "அசென்ஷன் தீவு",
	"countriesL10nMessages.AD": "அண்டோரா",
	"countriesL10nMessages.AE": "ஐக்கிய அரபு நாடுகள்",
	"countriesL10nMessages.AF": "ஆப்கானிஸ்தான்",
	"countriesL10nMessages.AG": "ஆண்டிகுவா மற்றும் பார்புடா",
	"countriesL10nMessages.AI": "அங்குயில்லா",
	"countriesL10nMessages.AL": "அல்பேனியா",
	"countriesL10nMessages.AM": "அர்மீனியா",
	"countriesL10nMessages.AO": "அங்கோலா",
	"countriesL10nMessages.AR": "அர்ஜென்டினா",
	"countriesL10nMessages.AS": "அமெரிக்க சமோவா",
	"countriesL10nMessages.AT": "ஆஸ்திரியா",
	"countriesL10nMessages.AU": "ஆஸ்திரேலியா",
	"countriesL10nMessages.AW": "அருபா",
	"countriesL10nMessages.AX": "ஆலண்ட் தீவுகள்",
	"countriesL10nMessages.AZ": "அஜர்பைஜான்",
	"countriesL10nMessages.BA": "போஸ்னியா மற்றும் ஹெர்சகோவினா",
	"countriesL10nMessages.BB": "பார்படாஸ்",
	"countriesL10nMessages.BD": "பங்களாதேஷ்",
	"countriesL10nMessages.BE": "பெல்ஜியம்",
	"countriesL10nMessages.BF": "புர்கினா ஃபாசோ",
	"countriesL10nMessages.BG": "பல்கேரியா",
	"countriesL10nMessages.BH": "பஹ்ரைன்",
	"countriesL10nMessages.BI": "புருண்டி",
	"countriesL10nMessages.BJ": "பெனின்",
	"countriesL10nMessages.BL": "செயின்ட் பார்தெலெமி",
	"countriesL10nMessages.BM": "பெர்முடா",
	"countriesL10nMessages.BN": "புருனே தருசலாம்",
	"countriesL10nMessages.BO": "பொலிவியா",
	"countriesL10nMessages.BQ": "பொனயர், சின்ட் யூஸ்டாஷியஸ் மற்றும் சபா",
	"countriesL10nMessages.BR": "பிரேசில்",
	"countriesL10nMessages.BS": "பஹாமாஸ்",
	"countriesL10nMessages.BT": "பூட்டான்",
	"countriesL10nMessages.BW": "போட்ஸ்வானா",
	"countriesL10nMessages.BZ": "பெலிஸ்",
	"countriesL10nMessages.CA": "கனடா",
	"countriesL10nMessages.CC": "கோகோஸ் (கீலிங்) தீவுகள்",
	"countriesL10nMessages.CD": "காங்கோ, ஜனநாயக குடியரசு",
	"countriesL10nMessages.CF": "மத்திய ஆப்பிரிக்க குடியரசு",
	"countriesL10nMessages.CG": "காங்கோ",
	"countriesL10nMessages.CH": "சுவிட்சர்லாந்து",
	"countriesL10nMessages.CI": "கோட் டி 'ஐவோரி",
	"countriesL10nMessages.CK": "குக் தீவுகள்",
	"countriesL10nMessages.CL": "சிலி",
	"countriesL10nMessages.CM": "கேமரூன்",
	"countriesL10nMessages.CN": "சீனா",
	"countriesL10nMessages.CO": "கொலம்பியா",
	"countriesL10nMessages.CR": "கோஸ்ட்டா ரிக்கா",
	"countriesL10nMessages.CV": "கேப் வெர்டே",
	"countriesL10nMessages.CW": "குராசோ",
	"countriesL10nMessages.CX": "கிறிஸ்துமஸ் தீவு",
	"countriesL10nMessages.CY": "சைப்ரஸ்",
	"countriesL10nMessages.CZ": "செக் குடியரசு",
	"countriesL10nMessages.DE": "ஜெர்மனி",
	"countriesL10nMessages.DJ": "ஜிபூட்டி",
	"countriesL10nMessages.DK": "டென்மார்க்",
	"countriesL10nMessages.DM": "டொமினிகா",
	"countriesL10nMessages.DO": "டொமினிக்கன் குடியரசு",
	"countriesL10nMessages.DZ": "அல்ஜீரியா",
	"countriesL10nMessages.EC": "ஈக்வடார்",
	"countriesL10nMessages.EE": "எஸ்டோனியா",
	"countriesL10nMessages.EG": "எகிப்து",
	"countriesL10nMessages.EH": "மேற்கு சாஹாரா",
	"countriesL10nMessages.ER": "எரித்திரியா",
	"countriesL10nMessages.ES": "ஸ்பெயின்",
	"countriesL10nMessages.ET": "எத்தியோப்பியா",
	"countriesL10nMessages.FI": "ஃபின்லாந்து",
	"countriesL10nMessages.FJ": "ஃபிஜி",
	"countriesL10nMessages.FK": "ஃபாக்லாந்து தீவுகள்",
	"countriesL10nMessages.FM": "மைக்ரோனேசியாவின் கூட்டாட்சி மாநிலங்கள்",
	"countriesL10nMessages.FO": "ஃபாரோ தீவுகள்",
	"countriesL10nMessages.FR": "ஃபிரான்ஸ்",
	"countriesL10nMessages.GA": "காபோன்",
	"countriesL10nMessages.GB": "யுனைட்டெட் கிங்டம்",
	"countriesL10nMessages.GD": "கிரெனடா",
	"countriesL10nMessages.GE": "ஜார்ஜியா",
	"countriesL10nMessages.GF": "பிரஞ்சு கயானா",
	"countriesL10nMessages.GG": "குர்ன்சி",
	"countriesL10nMessages.GH": "கானா",
	"countriesL10nMessages.GI": "ஜிப்ரால்டர்",
	"countriesL10nMessages.GL": "கிரீன்லாந்து",
	"countriesL10nMessages.GM": "காம்பியா",
	"countriesL10nMessages.GN": "கினியா",
	"countriesL10nMessages.GP": "குவாடலூப்",
	"countriesL10nMessages.GQ": "எக்குவடோரியல் கினியா",
	"countriesL10nMessages.GR": "கிரீஸ்",
	"countriesL10nMessages.GT": "குவாத்தமாலா",
	"countriesL10nMessages.GU": "குவாம்",
	"countriesL10nMessages.GW": "கினியா-பிசாவ்",
	"countriesL10nMessages.GY": "கயானா",
	"countriesL10nMessages.HK": "ஹாங்காங்",
	"countriesL10nMessages.HN": "ஹோண்டுராஸ்",
	"countriesL10nMessages.HR": "குரோஷியா",
	"countriesL10nMessages.HT": "ஹைட்டி",
	"countriesL10nMessages.HU": "ஹங்கேரி",
	"countriesL10nMessages.ID": "இந்தோனேசியா",
	"countriesL10nMessages.IE": "அயர்லாந்து",
	"countriesL10nMessages.IL": "இஸ்ரேல்",
	"countriesL10nMessages.IM": "ஐல் ஆஃப் மேன்",
	"countriesL10nMessages.IN": "இந்தியா",
	"countriesL10nMessages.IO": "பிரிட்டிஷ் இந்தியப் பெருங்கடல் பகுதி",
	"countriesL10nMessages.IQ": "ஈராக்",
	"countriesL10nMessages.IS": "ஐஸ்லாந்து",
	"countriesL10nMessages.IT": "இத்தாலி",
	"countriesL10nMessages.JE": "ஜெர்சி",
	"countriesL10nMessages.JM": "ஜமைக்கா",
	"countriesL10nMessages.JO": "ஜோர்டான்",
	"countriesL10nMessages.JP": "ஜப்பான்",
	"countriesL10nMessages.KE": "கென்யா",
	"countriesL10nMessages.KG": "கிர்கிஸ்தான்",
	"countriesL10nMessages.KH": "கம்போடியா",
	"countriesL10nMessages.KI": "கிரிபாதி",
	"countriesL10nMessages.KM": "கொமொரோஸ்",
	"countriesL10nMessages.KN": "செயின்ட் கிட்ஸ் மற்றும் நெவிஸ்",
	"countriesL10nMessages.KR": "தென் கொரியா",
	"countriesL10nMessages.KW": "குவைத்",
	"countriesL10nMessages.KY": "கெய்மன் தீவுகள்",
	"countriesL10nMessages.KZ": "கஜகஸ்தான்",
	"countriesL10nMessages.LA": "லாவோஸ்",
	"countriesL10nMessages.LB": "லெபனான்",
	"countriesL10nMessages.LC": "செயின்ட் லூசியா",
	"countriesL10nMessages.LI": "லிச்சென்ஸ்டீன்",
	"countriesL10nMessages.LK": "இலங்கை",
	"countriesL10nMessages.LR": "லைபீரியா",
	"countriesL10nMessages.LS": "லெசோதோ",
	"countriesL10nMessages.LT": "லிதுவேனியா",
	"countriesL10nMessages.LU": "லக்சம்பர்க்",
	"countriesL10nMessages.LV": "லாட்வியா",
	"countriesL10nMessages.LY": "லிபியா",
	"countriesL10nMessages.MA": "மொராக்கோ",
	"countriesL10nMessages.MC": "மொனாக்கோ",
	"countriesL10nMessages.MD": "மால்டோவா",
	"countriesL10nMessages.ME": "மாண்டினீக்ரோ",
	"countriesL10nMessages.MF": "செயின்ட் மார்ட்டின் (பிரெஞ்சு பகுதி)",
	"countriesL10nMessages.MG": "மடகாஸ்கர்",
	"countriesL10nMessages.MH": "மார்ஷல் தீவுகள்",
	"countriesL10nMessages.MK": "மாசிடோனியா",
	"countriesL10nMessages.ML": "மாலி",
	"countriesL10nMessages.MM": "மியான்மர்",
	"countriesL10nMessages.MN": "மங்கோலியா",
	"countriesL10nMessages.MO": "மக்காவ்",
	"countriesL10nMessages.MP": "வடக்கு மரியானா தீவு",
	"countriesL10nMessages.MQ": "மார்டினிக்",
	"countriesL10nMessages.MR": "மொரிட்டானியா",
	"countriesL10nMessages.MS": "மாண்ட்செராட்",
	"countriesL10nMessages.MT": "மால்டா",
	"countriesL10nMessages.MU": "மொரீஷியஸ்",
	"countriesL10nMessages.MV": "மாலத்தீவுகள்",
	"countriesL10nMessages.MW": "மலாவி",
	"countriesL10nMessages.MX": "மெக்சிகோ",
	"countriesL10nMessages.MY": "மலேசியா",
	"countriesL10nMessages.MZ": "மொசாம்பிக்",
	"countriesL10nMessages.NA": "நமீபியா",
	"countriesL10nMessages.NC": "புதிய கலிடோனியா",
	"countriesL10nMessages.NE": "நைஜர்",
	"countriesL10nMessages.NF": "நார்ஃபோக் தீவு",
	"countriesL10nMessages.NG": "நைஜீரியா",
	"countriesL10nMessages.NI": "நிகரகுவா",
	"countriesL10nMessages.NL": "நெதர்லாந்து",
	"countriesL10nMessages.NO": "நார்வே",
	"countriesL10nMessages.NP": "நேபாளம்",
	"countriesL10nMessages.NR": "நவ்ரு",
	"countriesL10nMessages.NU": "நியு",
	"countriesL10nMessages.NZ": "நியூசிலாந்து",
	"countriesL10nMessages.OM": "ஓமன்",
	"countriesL10nMessages.PA": "பனாமா",
	"countriesL10nMessages.PE": "பெரு",
	"countriesL10nMessages.PF": "ஃபிரெஞ்சு பாலினேசியா",
	"countriesL10nMessages.PG": "பப்புவா நியூ கினியா",
	"countriesL10nMessages.PH": "பிலிப்பைன்ஸ்",
	"countriesL10nMessages.PK": "பாகிஸ்தான்",
	"countriesL10nMessages.PL": "போலந்து",
	"countriesL10nMessages.PM": "செயிண்ட் பியர் மற்றும் மிக்குலோன்",
	"countriesL10nMessages.PR": "போர்ட்டோ ரிக்கோ",
	"countriesL10nMessages.PS": "பாலஸ்தீனம்",
	"countriesL10nMessages.PT": "போர்ச்சுகல்",
	"countriesL10nMessages.PW": "பலாவ்",
	"countriesL10nMessages.PY": "பராகுவே",
	"countriesL10nMessages.QA": "கத்தார்",
	"countriesL10nMessages.RE": "ரீயூனியன்",
	"countriesL10nMessages.RO": "ருமேனியா",
	"countriesL10nMessages.RS": "செர்பியா",
	"countriesL10nMessages.RW": "ருவாண்டா",
	"countriesL10nMessages.SA": "சவூதி அரேபியா",
	"countriesL10nMessages.SB": "சாலமன் தீவுகள்",
	"countriesL10nMessages.SC": "சீஷெல்ஸ்",
	"countriesL10nMessages.SE": "ஸ்வீடன்",
	"countriesL10nMessages.SG": "சிங்கப்பூர்",
	"countriesL10nMessages.SH": "செயின்ட் ஹெலினா",
	"countriesL10nMessages.SI": "ஸ்லோவேனியா",
	"countriesL10nMessages.SJ": "ஸ்வால்பார்ட் மற்றும் ஜான் மாயன்",
	"countriesL10nMessages.SK": "ஸ்லோவாக்கியா",
	"countriesL10nMessages.SL": "சியரா லியோன்",
	"countriesL10nMessages.SM": "சான் மரினோ",
	"countriesL10nMessages.SN": "சினகல்",
	"countriesL10nMessages.SO": "சோமாலியா",
	"countriesL10nMessages.SR": "சுரினாம்",
	"countriesL10nMessages.SS": "தெற்கு சூடான்",
	"countriesL10nMessages.ST": "சாவோ டோம் மற்றும் பிரின்சிபி",
	"countriesL10nMessages.SV": "எல் சல்வடோர்",
	"countriesL10nMessages.SX": "சிண்ட் மார்டன்",
	"countriesL10nMessages.SZ": "சுவாசிலாந்து",
	"countriesL10nMessages.TA": "டிரிஸ்டன் டா குன்ஹா",
	"countriesL10nMessages.TC": "டர்க்ஸ் மற்றும் கைகோஸ் தீவுகள்",
	"countriesL10nMessages.TD": "சாட்",
	"countriesL10nMessages.TG": "டோகோ",
	"countriesL10nMessages.TH": "தாய்லாந்து",
	"countriesL10nMessages.TJ": "தஜிகிஸ்தான்",
	"countriesL10nMessages.TK": "டோகெலாவ்",
	"countriesL10nMessages.TL": "திமோர்-லெஸ்டே",
	"countriesL10nMessages.TM": "துர்க்மெனிஸ்தான்",
	"countriesL10nMessages.TN": "துனிசியா",
	"countriesL10nMessages.TO": "டோங்கா",
	"countriesL10nMessages.TR": "துருக்கி",
	"countriesL10nMessages.TT": "டிரினிடாட் மற்றும் டொபாகோ",
	"countriesL10nMessages.TV": "துவாலு",
	"countriesL10nMessages.TW": "தைவான்",
	"countriesL10nMessages.TZ": "தான்சானியா",
	"countriesL10nMessages.UA": "உக்ரைன்",
	"countriesL10nMessages.UG": "உகாண்டா",
	"countriesL10nMessages.US": "அமெரிக்கா",
	"countriesL10nMessages.UY": "உருகுவே",
	"countriesL10nMessages.UZ": "உஸ்பெகிஸ்தான்",
	"countriesL10nMessages.VA": "ஹோலி சீ (வாடிகன் நகர மாநிலம்)",
	"countriesL10nMessages.VC": "செயின்ட் வின்சென்ட் மற்றும் கிரெனடைன்ஸ்",
	"countriesL10nMessages.VE": "வெனிசுலா",
	"countriesL10nMessages.VG": "விர்ஜின் தீவுகள், பிரிட்டிஷ்",
	"countriesL10nMessages.VI": "விர்ஜின் தீவுகள், அமெரிக்கா",
	"countriesL10nMessages.VN": "வியட்நாம்",
	"countriesL10nMessages.VU": "வனுவாடு",
	"countriesL10nMessages.WF": "வாலிஸ் மற்றும் ஃபுடுனா",
	"countriesL10nMessages.WS": "சமோவா",
	"countriesL10nMessages.XK": "கொசோவோ",
	"countriesL10nMessages.YE": "ஏமன்",
	"countriesL10nMessages.YT": "மயோட்டி",
	"countriesL10nMessages.ZA": "தென்னாப்பிரிக்கா",
	"countriesL10nMessages.ZM": "ஜாம்பியா",
	"countriesL10nMessages.ZW": "ஜிம்பாப்வே",
	"ToggleableEmailField.checkboxLabel": "என் பதிலின் நகலை மின்னஞ்சலில் அனுப்பு",
	"ToggleableEmailField.emailLabel": "உங்கள் மின்னஞ்சல் முகவரியை உள்ளிடவும்"
};

var te = {
	"AddressField.selectCountry": "దేశాన్ని ఎంచుకోండి",
	"PhoneField.countryCode": "దేశం కోడ్",
	"countriesL10nMessages.AC": "అసెన్షన్ ద్వీపం",
	"countriesL10nMessages.AD": "అండోరా",
	"countriesL10nMessages.AE": "యునైటెడ్ అరబ్ ఎమిరేట్స్",
	"countriesL10nMessages.AF": "ఆఫ్ఘనిస్తాన్",
	"countriesL10nMessages.AG": "ఆంటిగ్వా మరియు బార్బుడా",
	"countriesL10nMessages.AI": "అంగీలా",
	"countriesL10nMessages.AL": "అల్బేనియా",
	"countriesL10nMessages.AM": "ఆర్మేనియా",
	"countriesL10nMessages.AO": "అంగోలా",
	"countriesL10nMessages.AR": "అర్జెంటీనా",
	"countriesL10nMessages.AS": "అమెరికన్ సమోవా",
	"countriesL10nMessages.AT": "ఆస్ట్రియా",
	"countriesL10nMessages.AU": "ఆస్ట్రేలియా",
	"countriesL10nMessages.AW": "అరుబా",
	"countriesL10nMessages.AX": "ఆలాండ్ దీవులు",
	"countriesL10nMessages.AZ": "అజర్‌బైజాన్",
	"countriesL10nMessages.BA": "బోస్నియా మరియు హెర్జెగోవినా",
	"countriesL10nMessages.BB": "బార్బడోస్",
	"countriesL10nMessages.BD": "బంగ్లాదేశ్",
	"countriesL10nMessages.BE": "బెల్జియం",
	"countriesL10nMessages.BF": "బుర్కినా ఫాసో",
	"countriesL10nMessages.BG": "బల్గేరియా",
	"countriesL10nMessages.BH": "బహ్రెయిన్",
	"countriesL10nMessages.BI": "బురుండి",
	"countriesL10nMessages.BJ": "బెనిన్",
	"countriesL10nMessages.BL": "సెయింట్ బార్తెలెమీ",
	"countriesL10nMessages.BM": "బెర్ముడా",
	"countriesL10nMessages.BN": "బ్రూనై దారుస్సలాం",
	"countriesL10nMessages.BO": "బొలీవియా",
	"countriesL10nMessages.BQ": "బోనైర్, సింట్ యుస్టాటియస్ మరియు సబా",
	"countriesL10nMessages.BR": "బ్రెజిల్",
	"countriesL10nMessages.BS": "బహమాస్",
	"countriesL10nMessages.BT": "భూటాన్",
	"countriesL10nMessages.BW": "బాట్స్‌వానా",
	"countriesL10nMessages.BZ": "బెలిజ్",
	"countriesL10nMessages.CA": "కెనడా",
	"countriesL10nMessages.CC": "కోకోస్ (కీలింగ్) దీవులు",
	"countriesL10nMessages.CD": "కాంగో, డెమొక్రాటిక్ రిపబ్లిక్",
	"countriesL10nMessages.CF": "సెంట్రల్ ఆఫ్రికన్ రిపబ్లిక్",
	"countriesL10nMessages.CG": "కాంగో",
	"countriesL10nMessages.CH": "స్విట్జర్లాండ్",
	"countriesL10nMessages.CI": "కోట్ డి ఐవరీ",
	"countriesL10nMessages.CK": "కుక్ దీవులు",
	"countriesL10nMessages.CL": "చిలీ",
	"countriesL10nMessages.CM": "కామెరూన్",
	"countriesL10nMessages.CN": "చైనా",
	"countriesL10nMessages.CO": "కొలంబియా",
	"countriesL10nMessages.CR": "కోస్టా రికా",
	"countriesL10nMessages.CV": "కేప్ వర్దె",
	"countriesL10nMessages.CW": "కురాకో",
	"countriesL10nMessages.CX": "క్రిస్మస్ ద్వీపం",
	"countriesL10nMessages.CY": "సైప్రస్",
	"countriesL10nMessages.CZ": "చెక్ రిపబ్లిక్",
	"countriesL10nMessages.DE": "జర్మనీ",
	"countriesL10nMessages.DJ": "జిబౌటీ",
	"countriesL10nMessages.DK": "డెన్మార్క్",
	"countriesL10nMessages.DM": "డొమినికా",
	"countriesL10nMessages.DO": "డొమినికన్ రిపబ్లిక్",
	"countriesL10nMessages.DZ": "అల్జీరియా",
	"countriesL10nMessages.EC": "ఈక్వెడార్",
	"countriesL10nMessages.EE": "ఎస్టోనియా",
	"countriesL10nMessages.EG": "ఈజిప్ట్",
	"countriesL10nMessages.EH": "వెస్టర్న్ సహారా",
	"countriesL10nMessages.ER": "ఎరిత్రియా",
	"countriesL10nMessages.ES": "స్పెయిన్",
	"countriesL10nMessages.ET": "ఇథియోపియా",
	"countriesL10nMessages.FI": "ఫిన్లాండ్",
	"countriesL10nMessages.FJ": "ఫిజీ",
	"countriesL10nMessages.FK": "ఫాక్లాండ్ దీవులు",
	"countriesL10nMessages.FM": "ఫెడరేటెడ్ స్టేట్స్ ఆఫ్ మైక్రోనేషియా",
	"countriesL10nMessages.FO": "ఫారో దీవులు",
	"countriesL10nMessages.FR": "ఫ్రాన్స్",
	"countriesL10nMessages.GA": "గాబోన్",
	"countriesL10nMessages.GB": "యునైటెడ్ కింగ్‌డమ్",
	"countriesL10nMessages.GD": "గ్రెనడా",
	"countriesL10nMessages.GE": "జార్జియా",
	"countriesL10nMessages.GF": "ఫ్రెంచ్ గయానా",
	"countriesL10nMessages.GG": "గ్వెర్న్సీ",
	"countriesL10nMessages.GH": "ఘనా",
	"countriesL10nMessages.GI": "జిబ్రాల్టర్",
	"countriesL10nMessages.GL": "గ్రీన్లాండ్",
	"countriesL10nMessages.GM": "గాంబియా",
	"countriesL10nMessages.GN": "గినియా",
	"countriesL10nMessages.GP": "గ్వాడెలోప్",
	"countriesL10nMessages.GQ": "ఈక్వటోరియల్ గినియా",
	"countriesL10nMessages.GR": "గ్రీస్",
	"countriesL10nMessages.GT": "గ్వాటెమాల",
	"countriesL10nMessages.GU": "గ్వామ్",
	"countriesL10nMessages.GW": "గినియా-బిస్సావు",
	"countriesL10nMessages.GY": "గయానా",
	"countriesL10nMessages.HK": "హాంగ్ కాంగ్",
	"countriesL10nMessages.HN": "హోండురాస్",
	"countriesL10nMessages.HR": "క్రొయేషియా",
	"countriesL10nMessages.HT": "హైతీ",
	"countriesL10nMessages.HU": "హంగేరి",
	"countriesL10nMessages.ID": "ఇండోనేషియా",
	"countriesL10nMessages.IE": "ఐర్లాండ్",
	"countriesL10nMessages.IL": "ఇజ్రాయెల్",
	"countriesL10nMessages.IM": "ఐల్ ఆఫ్ మ్యాన్",
	"countriesL10nMessages.IN": "ఇండియా",
	"countriesL10nMessages.IO": "బ్రిటీష్ ఇండియన్ ఓషన్ టెరిటరీ",
	"countriesL10nMessages.IQ": "ఇరాక్",
	"countriesL10nMessages.IS": "ఐస్‌లాండ్",
	"countriesL10nMessages.IT": "ఇటలీ",
	"countriesL10nMessages.JE": "జెర్సీ",
	"countriesL10nMessages.JM": "జమైకా",
	"countriesL10nMessages.JO": "జోర్డాన్",
	"countriesL10nMessages.JP": "జపాన్",
	"countriesL10nMessages.KE": "కెన్యా",
	"countriesL10nMessages.KG": "కిర్గిజిస్తాన్",
	"countriesL10nMessages.KH": "కంబోడియా",
	"countriesL10nMessages.KI": "కిరిబాటి",
	"countriesL10nMessages.KM": "కామరోస్",
	"countriesL10nMessages.KN": "సెయింట్ కిట్స్ అండ్ నెవిస్",
	"countriesL10nMessages.KR": "సౌత్ కొరియా",
	"countriesL10nMessages.KW": "కువైట్",
	"countriesL10nMessages.KY": "కేమన్ ఐలాండ్స్",
	"countriesL10nMessages.KZ": "కజకిస్తాన్",
	"countriesL10nMessages.LA": "లావోస్",
	"countriesL10nMessages.LB": "లెబనాన్",
	"countriesL10nMessages.LC": "సెయింట్ లూసియా",
	"countriesL10nMessages.LI": "లీచ్టెన్‌స్టీన్",
	"countriesL10nMessages.LK": "శ్రీలంక",
	"countriesL10nMessages.LR": "లైబీరియా",
	"countriesL10nMessages.LS": "లెసోతో",
	"countriesL10nMessages.LT": "లిథువేనియా",
	"countriesL10nMessages.LU": "లక్సంబర్గ్",
	"countriesL10nMessages.LV": "లాట్వియా",
	"countriesL10nMessages.LY": "లిబియా",
	"countriesL10nMessages.MA": "మొరాకో",
	"countriesL10nMessages.MC": "మొనాకో",
	"countriesL10nMessages.MD": "మోల్డోవా",
	"countriesL10nMessages.ME": "మోంటెనెగ్రో",
	"countriesL10nMessages.MF": "సెయింట్ మార్టిన్ (ఫ్రెంచ్ పార్ట్)",
	"countriesL10nMessages.MG": "మడగాస్కర్",
	"countriesL10nMessages.MH": "మార్షల్ ఐలాండ్స్",
	"countriesL10nMessages.MK": "మాసిడోనియా",
	"countriesL10nMessages.ML": "మాలి",
	"countriesL10nMessages.MM": "మయన్మార్",
	"countriesL10nMessages.MN": "మంగోలియా",
	"countriesL10nMessages.MO": "మకావో",
	"countriesL10nMessages.MP": "నార్తరన్ మరియానా ఐలాండ్",
	"countriesL10nMessages.MQ": "మార్టినిక్",
	"countriesL10nMessages.MR": "మారిటానియా",
	"countriesL10nMessages.MS": "మాంట్‌సెరాట్",
	"countriesL10nMessages.MT": "మాల్టా",
	"countriesL10nMessages.MU": "మారిషస్",
	"countriesL10nMessages.MV": "మాల్దీవ్స్",
	"countriesL10nMessages.MW": "మలావి",
	"countriesL10nMessages.MX": "మెక్సికో",
	"countriesL10nMessages.MY": "మలేషియా",
	"countriesL10nMessages.MZ": "మొజాంబిక్",
	"countriesL10nMessages.NA": "నమీబియా",
	"countriesL10nMessages.NC": "న్యూ కాలెడోనియా",
	"countriesL10nMessages.NE": "నైజర్",
	"countriesL10nMessages.NF": "నార్ఫోక్ ఐలాండ్",
	"countriesL10nMessages.NG": "నైజీరియా",
	"countriesL10nMessages.NI": "నికరాగువా",
	"countriesL10nMessages.NL": "నెదర్లాండ్స్",
	"countriesL10nMessages.NO": "నార్వే",
	"countriesL10nMessages.NP": "నేపాల్",
	"countriesL10nMessages.NR": "నౌరు",
	"countriesL10nMessages.NU": "నియు",
	"countriesL10nMessages.NZ": "న్యూజిలాండ్",
	"countriesL10nMessages.OM": "ఒమన్",
	"countriesL10nMessages.PA": "పనామా",
	"countriesL10nMessages.PE": "పెరూ",
	"countriesL10nMessages.PF": "ఫ్రెంచ్ పాలినేషియా",
	"countriesL10nMessages.PG": "పపువా న్యూ గినియా",
	"countriesL10nMessages.PH": "ఫిలిప్పీన్స్",
	"countriesL10nMessages.PK": "పాకిస్తాన్",
	"countriesL10nMessages.PL": "పోలాండ్",
	"countriesL10nMessages.PM": "సెయింట్ పియర్ అండ్ మిక్వెలాన్",
	"countriesL10nMessages.PR": "పోర్టో రికో",
	"countriesL10nMessages.PS": "పాలస్తీనా",
	"countriesL10nMessages.PT": "పోర్చుగల్",
	"countriesL10nMessages.PW": "పలావు",
	"countriesL10nMessages.PY": "పరాగ్వే",
	"countriesL10nMessages.QA": "ఖతార్",
	"countriesL10nMessages.RE": "రీయూనియన్",
	"countriesL10nMessages.RO": "రొమేనియా",
	"countriesL10nMessages.RS": "సెర్బియా",
	"countriesL10nMessages.RW": "రువాండా",
	"countriesL10nMessages.SA": "సౌదీ అరేబియా",
	"countriesL10nMessages.SB": "సాలమన్ ఐలాండ్స్",
	"countriesL10nMessages.SC": "సేషెల్స్",
	"countriesL10nMessages.SE": "స్వీడెన్",
	"countriesL10nMessages.SG": "సింగపూర్",
	"countriesL10nMessages.SH": "సెయింట్ హెలెనా",
	"countriesL10nMessages.SI": "స్లోవేనియా",
	"countriesL10nMessages.SJ": "స్వాల్‌బార్డ్ అండ్ జాన్ మాయెన్",
	"countriesL10nMessages.SK": "స్లోవేకియా",
	"countriesL10nMessages.SL": "సియెర్రా లియోన్",
	"countriesL10nMessages.SM": "శాన్ మారినో",
	"countriesL10nMessages.SN": "సెనెగల్",
	"countriesL10nMessages.SO": "సోమాలియా",
	"countriesL10nMessages.SR": "సురినామ్",
	"countriesL10nMessages.SS": "సౌత్ సూడాన్",
	"countriesL10nMessages.ST": "సావో టోమ్ అండ్ ప్రిన్సిపె",
	"countriesL10nMessages.SV": "ఎల్ సాల్వెడార్",
	"countriesL10nMessages.SX": "సింట్ మార్టెన్",
	"countriesL10nMessages.SZ": "స్వాజిలాండ్",
	"countriesL10nMessages.TA": "ట్రిస్టన్ డా కున్హా",
	"countriesL10nMessages.TC": "టర్క్స్ అండ్ కైకోస్ ఐలాండ్స్",
	"countriesL10nMessages.TD": "చాద్",
	"countriesL10nMessages.TG": "టోగో",
	"countriesL10nMessages.TH": "థాయ్‌లాండ్",
	"countriesL10nMessages.TJ": "తజికిస్తాన్",
	"countriesL10nMessages.TK": "టోకెలావ్",
	"countriesL10nMessages.TL": "తైమూర్-లెస్టే",
	"countriesL10nMessages.TM": "తుర్కమెనిస్తాన్",
	"countriesL10nMessages.TN": "టునీషియా",
	"countriesL10nMessages.TO": "టోంగా",
	"countriesL10nMessages.TR": "టర్కీ",
	"countriesL10nMessages.TT": "ట్రినిడాడ్ అండ్ టొబాగో",
	"countriesL10nMessages.TV": "తువాలు",
	"countriesL10nMessages.TW": "తైవాన్",
	"countriesL10nMessages.TZ": "టాంజానియా",
	"countriesL10nMessages.UA": "ఉక్రెయిన్",
	"countriesL10nMessages.UG": "ఉగాండా",
	"countriesL10nMessages.US": "యునైటెడ్ స్టేట్స్",
	"countriesL10nMessages.UY": "ఉరుగ్వే",
	"countriesL10nMessages.UZ": "ఉబ్జెకిస్తాన్",
	"countriesL10nMessages.VA": "హోలీ సీ (వాటికన్ సిటీ స్టేట్)",
	"countriesL10nMessages.VC": "సెయింట్ విన్సెంట్ అండ్ ద గ్రెనడైన్స్",
	"countriesL10nMessages.VE": "వెనిజులా",
	"countriesL10nMessages.VG": "వర్జిన్ ఐలాండ్స్, బ్రిటీష్",
	"countriesL10nMessages.VI": "వర్జిన్ ఐలాండ్స్, యు.ఎస్.",
	"countriesL10nMessages.VN": "వియత్నాం",
	"countriesL10nMessages.VU": "వనాటు",
	"countriesL10nMessages.WF": "వాలిస్ అండ్ ఫుటునా",
	"countriesL10nMessages.WS": "సమోవా",
	"countriesL10nMessages.XK": "కొసావో",
	"countriesL10nMessages.YE": "యెమెన్",
	"countriesL10nMessages.YT": "మయోట్టే",
	"countriesL10nMessages.ZA": "సౌత్ ఆఫ్రికా",
	"countriesL10nMessages.ZM": "జాంబియా",
	"countriesL10nMessages.ZW": "జింబాబ్వే",
	"ToggleableEmailField.checkboxLabel": "నా ప్రతిస్పందనల కాపీని ఇమెయిల్ ద్వారా పంపండి",
	"ToggleableEmailField.emailLabel": "ఇమెయిల్ చిరునామాను నమోదు చేయండి"
};

var th = {
	"AddressField.selectCountry": "เลือกประเทศ",
	"PhoneField.countryCode": "รหัสประเทศ",
	"countriesL10nMessages.AC": "เกาะแอสเซนชัน",
	"countriesL10nMessages.AD": "อันดอร์รา",
	"countriesL10nMessages.AE": "สหรัฐอาหรับเอมิเรตส์",
	"countriesL10nMessages.AF": "อัฟกานิสถาน",
	"countriesL10nMessages.AG": "แอนทีกาและบาร์บิวดา",
	"countriesL10nMessages.AI": "แองกวิลลา",
	"countriesL10nMessages.AL": "แอลเบเนีย",
	"countriesL10nMessages.AM": "อาร์เมเนีย",
	"countriesL10nMessages.AO": "แองโกลา",
	"countriesL10nMessages.AR": "อาร์เจนตินา",
	"countriesL10nMessages.AS": "อเมริกันซามัว",
	"countriesL10nMessages.AT": "ออสเตรีย",
	"countriesL10nMessages.AU": "ออสเตรเลีย",
	"countriesL10nMessages.AW": "อารูบา",
	"countriesL10nMessages.AX": "หมู่เกาะโอลันด์",
	"countriesL10nMessages.AZ": "อาเซอร์ไบจาน",
	"countriesL10nMessages.BA": "บอสเนียและเฮอร์เซโกวีนา",
	"countriesL10nMessages.BB": "บาร์เบโดส",
	"countriesL10nMessages.BD": "บังคลาเทศ",
	"countriesL10nMessages.BE": "เบลเยียม",
	"countriesL10nMessages.BF": "บูร์กินาฟาโซ",
	"countriesL10nMessages.BG": "บัลแกเรีย",
	"countriesL10nMessages.BH": "บาห์เรน",
	"countriesL10nMessages.BI": "บุรุนดี",
	"countriesL10nMessages.BJ": "เบนิน",
	"countriesL10nMessages.BL": "แซ็ง-บาร์เตเลมี",
	"countriesL10nMessages.BM": "เบอร์มิวดา",
	"countriesL10nMessages.BN": "บรูไนดารุสซาลาม",
	"countriesL10nMessages.BO": "โบลิเวีย",
	"countriesL10nMessages.BQ": "โบแนร์ ซินต์เอิสทาทิอุส และซาบา",
	"countriesL10nMessages.BR": "บราซิล",
	"countriesL10nMessages.BS": "บาฮามาส",
	"countriesL10nMessages.BT": "ภูฏาน",
	"countriesL10nMessages.BW": "บอตสวานา",
	"countriesL10nMessages.BZ": "เบลีซ",
	"countriesL10nMessages.CA": "แคนาดา",
	"countriesL10nMessages.CC": "หมู่เกาะโคโคส (คีลิง)",
	"countriesL10nMessages.CD": "สาธารณรัฐประชาธิปไตยคองโก",
	"countriesL10nMessages.CF": "สาธารณรัฐอัฟริกากลาง",
	"countriesL10nMessages.CG": "คองโก",
	"countriesL10nMessages.CH": "สวิตเซอร์แลนด์",
	"countriesL10nMessages.CI": "โกตดิวัวร์",
	"countriesL10nMessages.CK": "หมู่เกาะคุก",
	"countriesL10nMessages.CL": "ชิลี",
	"countriesL10nMessages.CM": "แคเมอรูน",
	"countriesL10nMessages.CN": "จีน",
	"countriesL10nMessages.CO": "โคลอมเบีย",
	"countriesL10nMessages.CR": "คอสตาริกา",
	"countriesL10nMessages.CV": "เคปเวิร์ด",
	"countriesL10nMessages.CW": "กูราเซา",
	"countriesL10nMessages.CX": "เกาะคริสต์มาส",
	"countriesL10nMessages.CY": "ไซปรัส",
	"countriesL10nMessages.CZ": "สาธารณรัฐเช็ก",
	"countriesL10nMessages.DE": "เยอรมนี",
	"countriesL10nMessages.DJ": "จิบูตี",
	"countriesL10nMessages.DK": "เดนมาร์ก",
	"countriesL10nMessages.DM": "ดอมินีกา",
	"countriesL10nMessages.DO": "สาธารณรัฐโดมินิกัน",
	"countriesL10nMessages.DZ": "แอลจีเรีย",
	"countriesL10nMessages.EC": "เอกวาดอร์",
	"countriesL10nMessages.EE": "เอสโตเนีย",
	"countriesL10nMessages.EG": "อียิปต์",
	"countriesL10nMessages.EH": "เวสเทิร์นซาฮาร่า",
	"countriesL10nMessages.ER": "เอริเทรีย",
	"countriesL10nMessages.ES": "สเปน",
	"countriesL10nMessages.ET": "เอธิโอเปีย",
	"countriesL10nMessages.FI": "ฟินแลนด์",
	"countriesL10nMessages.FJ": "ฟิจิ",
	"countriesL10nMessages.FK": "หมู่เกาะฟอล์กแลนด์",
	"countriesL10nMessages.FM": "สหพันธรัฐไมโครนีเชีย",
	"countriesL10nMessages.FO": "หมู่เกาะแฟโร",
	"countriesL10nMessages.FR": "ฝรั่งเศส",
	"countriesL10nMessages.GA": "กาบอง",
	"countriesL10nMessages.GB": "สหราชอาณาจักร",
	"countriesL10nMessages.GD": "กรีเนดา",
	"countriesL10nMessages.GE": "จอร์เจีย",
	"countriesL10nMessages.GF": "เฟรนช์เกียนา",
	"countriesL10nMessages.GG": "เกิร์นซีย์",
	"countriesL10nMessages.GH": "กานา",
	"countriesL10nMessages.GI": "ยิบรอลตาร์",
	"countriesL10nMessages.GL": "กรีนแลนด์",
	"countriesL10nMessages.GM": "แกมเบีย",
	"countriesL10nMessages.GN": "กินี",
	"countriesL10nMessages.GP": "กวาเดอลูป",
	"countriesL10nMessages.GQ": "อิเควทอเรียลกินี",
	"countriesL10nMessages.GR": "กรีซ",
	"countriesL10nMessages.GT": "กัวเตมาลา",
	"countriesL10nMessages.GU": "กวม",
	"countriesL10nMessages.GW": "กินี-บิสเซา",
	"countriesL10nMessages.GY": "กายอานา",
	"countriesL10nMessages.HK": "ฮ่องกง",
	"countriesL10nMessages.HN": "ฮอนดูรัส",
	"countriesL10nMessages.HR": "โครเอเชีย",
	"countriesL10nMessages.HT": "เฮติ",
	"countriesL10nMessages.HU": "ฮังการี",
	"countriesL10nMessages.ID": "อินโดนีเซีย",
	"countriesL10nMessages.IE": "ไอร์แลนด์",
	"countriesL10nMessages.IL": "อิสราเอล",
	"countriesL10nMessages.IM": "ไอล์ออฟแมน",
	"countriesL10nMessages.IN": "อินเดีย",
	"countriesL10nMessages.IO": "บริติชอินเดียนโอเชียนเทร์ริทอรี",
	"countriesL10nMessages.IQ": "อิรัก",
	"countriesL10nMessages.IS": "ไอซ์แลนด์",
	"countriesL10nMessages.IT": "อิตาลี",
	"countriesL10nMessages.JE": "เจอร์ซีย์",
	"countriesL10nMessages.JM": "จาเมกา",
	"countriesL10nMessages.JO": "จอร์แดน",
	"countriesL10nMessages.JP": "ญี่ปุ่น",
	"countriesL10nMessages.KE": "เคนยา",
	"countriesL10nMessages.KG": "คีร์กีซสถาน",
	"countriesL10nMessages.KH": "กัมพูชา",
	"countriesL10nMessages.KI": "คิริบาส",
	"countriesL10nMessages.KM": "คอโมโรส",
	"countriesL10nMessages.KN": "เซนต์คิตส์และเนวิส",
	"countriesL10nMessages.KR": "เกาหลีใต้",
	"countriesL10nMessages.KW": "คูเวต",
	"countriesL10nMessages.KY": "หมู่เกาะเคย์เเมน",
	"countriesL10nMessages.KZ": "คาซัคสถาน",
	"countriesL10nMessages.LA": "ลาว",
	"countriesL10nMessages.LB": "เลบานอน",
	"countriesL10nMessages.LC": "เซนต์ลูเซีย",
	"countriesL10nMessages.LI": "ลิกเตนสไตน์",
	"countriesL10nMessages.LK": "ศรีลังกา",
	"countriesL10nMessages.LR": "ไลบีเรีย",
	"countriesL10nMessages.LS": "เลโซโท",
	"countriesL10nMessages.LT": "ลิทัวเนีย",
	"countriesL10nMessages.LU": "ลักเซมเบิร์ก",
	"countriesL10nMessages.LV": "ลัตเวีย",
	"countriesL10nMessages.LY": "ลิเบีย",
	"countriesL10nMessages.MA": "โมร็อกโก",
	"countriesL10nMessages.MC": "โมนาโก",
	"countriesL10nMessages.MD": "มอลโดวา",
	"countriesL10nMessages.ME": "มอนเตเนโกร",
	"countriesL10nMessages.MF": "เซนต์มาร์ติน (ส่วนของฝรั่งเศส)",
	"countriesL10nMessages.MG": "มาดากัสการ์",
	"countriesL10nMessages.MH": "หมู่เกาะมาร์แชลล์",
	"countriesL10nMessages.MK": "มาซิโดเนีย",
	"countriesL10nMessages.ML": "มาลี",
	"countriesL10nMessages.MM": "เมียนมา",
	"countriesL10nMessages.MN": "มองโกเลีย",
	"countriesL10nMessages.MO": "มาเก๊า",
	"countriesL10nMessages.MP": "เกาะนอร์เทิร์นมาเรียนา",
	"countriesL10nMessages.MQ": "มาร์ตีนีก",
	"countriesL10nMessages.MR": "มอริเตเนีย",
	"countriesL10nMessages.MS": "มอนต์เซอร์รัต",
	"countriesL10nMessages.MT": "มอลตา",
	"countriesL10nMessages.MU": "มอริเชียส",
	"countriesL10nMessages.MV": "มัลดีฟส์",
	"countriesL10nMessages.MW": "มาลาวี",
	"countriesL10nMessages.MX": "เม็กซิโก",
	"countriesL10nMessages.MY": "มาเลเซีย",
	"countriesL10nMessages.MZ": "โมซัมบิก",
	"countriesL10nMessages.NA": "นามิเบีย",
	"countriesL10nMessages.NC": "นิวแคลิโดเนีย",
	"countriesL10nMessages.NE": "ไนเจอร์",
	"countriesL10nMessages.NF": "เกาะนอร์ฟอล์ก",
	"countriesL10nMessages.NG": "ไนจีเรีย",
	"countriesL10nMessages.NI": "นิการากัว",
	"countriesL10nMessages.NL": "เนเธอร์แลนด์",
	"countriesL10nMessages.NO": "นอร์เวย์",
	"countriesL10nMessages.NP": "เนปาล",
	"countriesL10nMessages.NR": "นาอูรู",
	"countriesL10nMessages.NU": "นีวเว",
	"countriesL10nMessages.NZ": "นิวซีแลนด์",
	"countriesL10nMessages.OM": "โอมาน",
	"countriesL10nMessages.PA": "ปานามา",
	"countriesL10nMessages.PE": "เปรู",
	"countriesL10nMessages.PF": "เฟรนช์พอลินีเชีย",
	"countriesL10nMessages.PG": "ปาปัวนิวกินี",
	"countriesL10nMessages.PH": "ฟิลิปปินส์",
	"countriesL10nMessages.PK": "ปากีสถาน",
	"countriesL10nMessages.PL": "โปแลนด์",
	"countriesL10nMessages.PM": "แซ็งปีแยร์และมีเกอลง",
	"countriesL10nMessages.PR": "เปอร์โตรีโก",
	"countriesL10nMessages.PS": "ปาเลสไตน์",
	"countriesL10nMessages.PT": "โปรตุเกส",
	"countriesL10nMessages.PW": "ปาเลา",
	"countriesL10nMessages.PY": "ปารากวัย",
	"countriesL10nMessages.QA": "กาตาร์",
	"countriesL10nMessages.RE": "เรอูนียง",
	"countriesL10nMessages.RO": "โรมาเนีย",
	"countriesL10nMessages.RS": "เซอร์เบีย",
	"countriesL10nMessages.RW": "รวันดา",
	"countriesL10nMessages.SA": "ซาอุดีอาระเบีย",
	"countriesL10nMessages.SB": "หมู่เกาะโซโลมอน",
	"countriesL10nMessages.SC": "เซเชลส์",
	"countriesL10nMessages.SE": "สวีเดน",
	"countriesL10nMessages.SG": "สิงคโปร์",
	"countriesL10nMessages.SH": "เซนต์เฮเลนา",
	"countriesL10nMessages.SI": "สโลวีเนีย",
	"countriesL10nMessages.SJ": "สฟาลบาร์และยานไมเอน",
	"countriesL10nMessages.SK": "สโลวาเกีย",
	"countriesL10nMessages.SL": "เซียร์ราลีโอน",
	"countriesL10nMessages.SM": "ซานมารีโน",
	"countriesL10nMessages.SN": "เซเนกัล",
	"countriesL10nMessages.SO": "โซมาเลีย",
	"countriesL10nMessages.SR": "ซูรินาม",
	"countriesL10nMessages.SS": "ซูดานใต้",
	"countriesL10nMessages.ST": "เซาตูเมและปรินซิปี",
	"countriesL10nMessages.SV": "เอลซัลวาดอร์",
	"countriesL10nMessages.SX": "ซินต์มาร์เติน",
	"countriesL10nMessages.SZ": "สวาซิแลนด์",
	"countriesL10nMessages.TA": "ตริสตันดากูนยา",
	"countriesL10nMessages.TC": "หมู่เกาะเติกส์และเคคอส",
	"countriesL10nMessages.TD": "ชาด",
	"countriesL10nMessages.TG": "โตโก",
	"countriesL10nMessages.TH": "ไทย",
	"countriesL10nMessages.TJ": "ทาจิกิสถาน",
	"countriesL10nMessages.TK": "โตเกเลา",
	"countriesL10nMessages.TL": "ติมอร์-เลสเต",
	"countriesL10nMessages.TM": "เติร์กเมนิสถาน",
	"countriesL10nMessages.TN": "ตูนิเซีย",
	"countriesL10nMessages.TO": "ตองกา",
	"countriesL10nMessages.TR": "ตุรกี",
	"countriesL10nMessages.TT": "ตรินิแดดและโตเบโก",
	"countriesL10nMessages.TV": "ตูวาลู",
	"countriesL10nMessages.TW": "ไต้หวัน",
	"countriesL10nMessages.TZ": "แทนซาเนีย",
	"countriesL10nMessages.UA": "ยูเครน",
	"countriesL10nMessages.UG": "ยูกันดา",
	"countriesL10nMessages.US": "สหรัฐอเมริกา",
	"countriesL10nMessages.UY": "อุรุกวัย",
	"countriesL10nMessages.UZ": "อุซเบกิสถาน",
	"countriesL10nMessages.VA": "สันตะสำนัก (นครรัฐวาติกัน)",
	"countriesL10nMessages.VC": "เซนต์วินเซนต์และเกรนาดีนส์",
	"countriesL10nMessages.VE": "เวเนซุเอลา",
	"countriesL10nMessages.VG": "หมู่เกาะบริติชเวอร์จิน",
	"countriesL10nMessages.VI": "หมู่เกาะเวอร์จินของสหรัฐอเมริกา",
	"countriesL10nMessages.VN": "เวียดนาม",
	"countriesL10nMessages.VU": "วานูวาตู",
	"countriesL10nMessages.WF": "วอลิสและฟูตูนา",
	"countriesL10nMessages.WS": "ซามัว",
	"countriesL10nMessages.XK": "โคโซโว",
	"countriesL10nMessages.YE": "เยเมน",
	"countriesL10nMessages.YT": "มายอต",
	"countriesL10nMessages.ZA": "แอฟริกาใต้",
	"countriesL10nMessages.ZM": "แซมเบีย",
	"countriesL10nMessages.ZW": "ซิมบับเว",
	"ToggleableEmailField.checkboxLabel": "ส่งสำเนาคำตอบของฉันมาทางอีเมล",
	"ToggleableEmailField.emailLabel": "กรอกที่อยู่อีเมลของคุณ"
};

var tl = {
	"AddressField.selectCountry": "Piliin ang bansa",
	"PhoneField.countryCode": "Code ng Bansa",
	"countriesL10nMessages.AC": "Ascension Island",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "United Arab Emirates",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua and Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "American Samoa",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland Islands",
	"countriesL10nMessages.AZ": "Azerbaijan",
	"countriesL10nMessages.BA": "Bosnia and Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Belgium",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius and Saba",
	"countriesL10nMessages.BR": "Brazil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Cocos (Keeling) Islands",
	"countriesL10nMessages.CD": "Congo, The Democratic Republic of the",
	"countriesL10nMessages.CF": "Central African Republic",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Switzerland",
	"countriesL10nMessages.CI": "Côte d'Ivoire",
	"countriesL10nMessages.CK": "Cook Islands",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Cameroon",
	"countriesL10nMessages.CN": "China",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cape Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Christmas Island",
	"countriesL10nMessages.CY": "Cyprus",
	"countriesL10nMessages.CZ": "Czech Republic",
	"countriesL10nMessages.DE": "Germany",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Denmark",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Dominican Republic",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Egypt",
	"countriesL10nMessages.EH": "Western Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Espanya",
	"countriesL10nMessages.ET": "Ethiopia",
	"countriesL10nMessages.FI": "Finland",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falkland Islands",
	"countriesL10nMessages.FM": "Federated States of Micronesia",
	"countriesL10nMessages.FO": "Faroe Islands",
	"countriesL10nMessages.FR": "France",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "United Kingdom",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "French Guiana",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Greenland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Equatorial Guinea",
	"countriesL10nMessages.GR": "Greece",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungary",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Ireland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Isle of Man",
	"countriesL10nMessages.IN": "India",
	"countriesL10nMessages.IO": "British Indian Ocean Territory",
	"countriesL10nMessages.IQ": "Iraq",
	"countriesL10nMessages.IS": "Iceland",
	"countriesL10nMessages.IT": "Italya",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Japan",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kyrgyzstan",
	"countriesL10nMessages.KH": "Cambodia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoros",
	"countriesL10nMessages.KN": "Saint Kitts and Nevis",
	"countriesL10nMessages.KR": "South Korea",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Cayman Islands",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Lebanon",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lithuania",
	"countriesL10nMessages.LU": "Luxembourg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Morocco",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (bahagi ng France)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Marshall Islands",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mongolia",
	"countriesL10nMessages.MO": "Macao",
	"countriesL10nMessages.MP": "Northern Mariana Island",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldives",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexico",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "New Caledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Norfolk Island",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Netherlands",
	"countriesL10nMessages.NO": "Norway",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "New Zealand",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "French Polynesia",
	"countriesL10nMessages.PG": "Papua New Guinea",
	"countriesL10nMessages.PH": "Pilipinas",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Poland",
	"countriesL10nMessages.PM": "Saint Pierre and Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestine",
	"countriesL10nMessages.PT": "Portugal",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Saudi Arabia",
	"countriesL10nMessages.SB": "Solomon Islands",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Sweden",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard and Jan Mayen",
	"countriesL10nMessages.SK": "Slovakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "South Sudan",
	"countriesL10nMessages.ST": "Sao Tome and Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks and Caicos Islands",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thailand",
	"countriesL10nMessages.TJ": "Tajikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Turkey",
	"countriesL10nMessages.TT": "Trinidad and Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Taiwan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraine",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Estados Unidos",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Holy See (Vatican City State)",
	"countriesL10nMessages.VC": "Saint Vincent and the Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Virgin Islands, British",
	"countriesL10nMessages.VI": "Virgin Islands, U.S.",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis and Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "South Africa",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Padalhan ako ng kopya ng mga sagot ko sa pamamagitan ng email",
	"ToggleableEmailField.emailLabel": "Ilagay ang iyong email address"
};

var tr = {
	"ActionMenuTagPin.PIN": "Tuttur",
	"ActionMenuTagPin.TAG": "Etiket",
	"AddressField.selectCountry": "Ülke seçin",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "Yanıtlanan: {answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "S{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "Atlanan: {skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "İnsanlar nasıl hissediyor:",
	"AsyncJobLoadingState.LOADING": "Yanıtlar yükleniyor",
	"AsyncJobLoadingState.TAGGING_CAPTION": "Tamamlandığında size haber vereceğiz",
	"AsyncJobLoadingState.TAGGING_TITLE": "Yanıtları etiketleme",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "Etiketler mevcut ve gelecek yanıtlar için geçerlidir. Birden fazla anahtar kelime kullanmak istiyorsanız bir boşluk bırakın veya kelimeler arasına <strong>OR</strong> yazın.",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "Yanıtları otomatik etiketle",
	"Button.OK_LABEL": "Tamam",
	"CalendarPicker.month": "Bir ay seçin",
	"CalendarPicker.months": "{months, select, January {Ocak} February {Şubat} March {Mart} April {Nisan} May {Mayıs} June {Haziran} July {Temmuz} August {Ağustos} September {Eylül} October {Ekim} November {Kasım} December {Aralık} other {diğer}}",
	"CalendarPicker.nextMonth": "Sonraki Ay",
	"CalendarPicker.previousMonth": "Önceki Ay",
	"CalendarPicker.year": "Bir yıl seçin",
	"ColorSwatches.TAG": "Etiket: {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "Çok renkli sözcük bulutlarında sözcük veya ifadenin kaç defa tekrarlandığını göstermek için hem renk hem de boyut kullanılır.",
	"ColorTab.COLOR_SELECTOR_TITLE": "Sözcük bulutlarında renkler nasıl kullanılır?",
	"ColorTab.DOWN_ARROW": "Aşağı açılır oku seçin",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "Çok renkli",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "Tek renk",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "İptal",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. Yeni sözcük oluşturun",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "Yeni sözcük aşağıda seçilen her sözcüğün yerini alır.",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "Birleşik sözcükleri düzenle",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "Sözcükleri birleştir",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "Bir veya daha fazla seçilen sözcüğün yerini alacak yeni bir sözcük oluşturun. Daha sonra bunu düzenleyerek orijinal sözcüklerinizi geri alabilirsiniz.",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "Yeni bir sözcük yazın",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "Birleştir",
	"CombineWordsModal.WORD_LIST_HEADER": "2. Birleştirilecek sözcükleri seçin",
	"CreateTagFromSearch.CLOSE_MENU": "İPTAL",
	"CreateTagFromSearch.DUPLICATED_NAME": "O ada sahip bir etiketiniz zaten var.",
	"CreateTagFromSearch.NEW_TAG": "Yeni Etiket",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { Hiçbir yanıt \n\t\tetiketlenmeyecek} other {{responseCount, plural, one {\n\t\t\t\t{responseTaggedFormatted} yanıt \n\t\t\t\n\t\tetiketlenecek} other {\n\t\t\t\t{responseTaggedFormatted} yanıt \n\t\t\t\n\t\tetiketlenecek}}}}",
	"CreateTagFromSearch.SAVE": "KAYDET",
	"CreateTagFromSearch.TAG": "Etiket",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "Etiket adı boş olamaz.",
	"CreateTagFromSearch.TAG_NAME_SPACES": "Etiket adı yalnızca boşluklardan oluşamaz.",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "Bu anahtar kelimelerle eşleşen yanıtları etiketleyin. <strong>Etiket oluştur</strong>'u seçin. Bir renk seçin ve etiketi kaydedin. Etiket mevcut ve gelecek yanıtlar için geçerlidir.",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "Eşleşen yanıtları etiketleyin",
	"DashboardMenuContent.CREATE": "OLUŞTUR",
	"DashboardMenuContent.NAME": "Ad",
	"DashboardMenuContent.NEW_DASHBOARD": "Yeni Pano",
	"DateInputWrapper.dateButton": "Takimi aç",
	"DateInputWrapper.dateFormatIntl": "GG/AA/YYYY",
	"DateInputWrapper.dateFormatUs": "AA/GG/YYYY",
	"DateInputWrapper.dateLabel": "Tarih",
	"DateTime.errorEnterHours": "Lütfen 0 ve 23 arasında bir saat değeri girin.",
	"DateTime.errorEnterMinutes": "Lütfen 0 ve 59 arasında bir dakika değeri girin.",
	"DateTime.errorSelectPeriod": "Lütfen AM/PM seçimini yapın.",
	"DateTime.errorYearOutOfRange": "Lütfen 1900 ile 9999 arasında bir yıl girin.",
	"DateTime.formatErrorDateIntl": "Lütfen tarihi GG/AA/YYYY formatında girin.",
	"DateTime.formatErrorDateUs": "Lütfen tarihi AA/GG/YYYY formatında girin.",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "İPTAL",
	"EditorButtons.COPY_BUTTON": "SORUYU KOPYALA",
	"EditorButtons.MOVE_BUTTON": "SORUYU TAŞI",
	"EditorButtons.NEXT_QUESTION_BUTTON": "Sonraki soru",
	"EditorButtons.SAVE_BUTTON": "KAYDET",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "Bu soruda Veri Trendleri geçerli değildir",
	"FallbackComponent.ERROR": "Soru yüklenirken hata oluştu",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>Bir dosya seçin</div> <div class={dragClass}>veya dosyayı buraya sürükleyin</div>",
	"FileUpload.fileUploaded": "Dosya başarıyla yüklendi",
	"FileUpload.fileUploading": "Dosya yükleniyor",
	"FileUpload.removeFile": "Dosyayı kaldır",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>Dosyayı değiştirin</div> <div class={dragClass}>veya buraya sürükleyin</div>",
	"FileUpload.supportedFiles": "Desteklenen Dosyalar",
	"FilterMenuContent.APPLY_ACTIONS": "UYGULA",
	"FilterMenuContent.CLEAR_ACTIONS": "Temizle",
	"FilterMenuContent.CLOSE_MENU": "İPTAL",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "İptal",
	"HideWordsModal.HIDE_BUTTON_LABEL": "Gizle",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "Kaydet",
	"HideWordsModal.HIDE_WORDS_HEADER": "Sözcükleri gizle",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "Sözcük bulutundan gizlenecek sözcükleri seçin.",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "Gizlenecek sözcükleri seçin",
	"IconHelpStyled.HELP": "Yardım",
	"IndividualResponseCard.NO_RESPONSE": "Yanıtlayan bu soruyu atladı",
	"IndividualResponseCard.QUESTION_NUMBER": "S{number}",
	"InfoBannerTagManagement.CLOSE": "Kapat",
	"InfoBannerTagManagement.NEW": "YENİ",
	"InfoBannerTagManagement.TAG_RESPONSES": "Yanıtları etiketleyin",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "Otomatik etiketler ayarlamak için anahtar kelime ekleyin ve yanıtları düzenli tutun.",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "Daha kolay yönetmek için yanıtları etiketleyin",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {Olumlu: {percentage,number,percent}} negative {Olumsuz: {percentage,number,percent}} neutral {Nötr: {percentage,number,percent}} unknown {Tespit edilemedi: {percentage,number,percent}} other {diğer}}",
	"LoadingState.LOADING": "Yanıtlar yükleniyor",
	"MatrixDropdown.EmptyTopNativeSelect": "Bir seçenek belirleyin",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "Güven aralığı",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "Fayda puanı",
	"Modal.CloseModal": "Araç bilgisini kapat",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {Pazartesi} Tu {Salı} We {Çarşamba} Th {Perşembe} Fr {Cuma} Sa {Cumartesi} Su {Pazar} other {diğer}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {Pt} Tu {Sa} We {Ça} Th {Pe} Fr {Cu} Sa {Ct} Su {Pa} other {diğer}}",
	"MultiColorSelectorMenu.APPLY": "Uygula",
	"MultiColorSelectorMenu.CANCEL": "İptal",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "Verilerinizi özel bir yerleşimde düzenleyin, bulgularınızı açıklayın ve panonuzu herkesle paylaşın. Verilerinizi özel bir yerleşimde düzenleyin, bulgularınızı açıklayın ve panonuzu herkesle paylaşın.",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "Panolar",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "Daha fazla bilgi",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">{minValue} ile {maxValue} arası bir ölçekte, </div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minLabelText} için {minValue}, {maxLabelText} için {maxValue}</div>",
	"NpsGaugeChart.NPS_ARC": "{min} ve {max} arasındaki puanlar için ölçüm grafiği",
	"NpsGaugeChart.NPS_CHART": "NPS ölçüm grafiği",
	"NpsGaugeChart.NPS_SCORE": "NPS puanı {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "{score} puan işaret konumu için grafik işareti",
	"OrderControl.AriaNA": "Geçerli Değil",
	"OrderControl.NA": "Yok",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "\"{title}\" yanıtları ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "diğer yanıtlar listesini gizle",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "diğer yanıtlar listesini göster",
	"PhoneField.countryCode": "Ülke Kodu",
	"QuestionCardItemFooter.FILTER_APPLIED": "Uygulanan kurallar: {filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "Filtrele",
	"QuestionCardTitle.ANSWERED": "Yanıtlanan: {answeredCount}",
	"QuestionCardTitle.SELECTED": "Seçilen: {selectedCount}",
	"QuestionCardTitle.SKIPPED": "Atlanan: {skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, one {{score}/{total} puan} other {{score}/{total} puan}}",
	"QuizResultScore.SKIPPED": "Atlandı",
	"RangeTab.RANGE_TAB_LABEL": "Tekrarlar: {value}",
	"RangeTab.TOTAL_WORDS": "toplam sözcük",
	"Ranking.AriaRankAtBottom": "{label} listenin en altına taşındı",
	"Ranking.AriaRankAtTop": "{label} listenin en üstüne taşındı",
	"Ranking.AriaRankMovedDown": "{label}, {position}. sıraya indirildi",
	"Ranking.AriaRankMovedUp": "{label}, {position}. sıraya çıkarıldı",
	"RankingItem.AriaRankMoveDown": "{label} etiketini aşağı taşı",
	"RankingItem.AriaRankMoveUp": "{label} etiketini yukarı taşı",
	"RatingChart.AVERAGE_RATING": "Ortalama Değerlendirme",
	"RatingChartLegend.ANSWERED": "Yanıtlanan: {number}",
	"RatingChartLegend.RATING": "{rating} yıldız",
	"RatingChartLegend.SKIPPED": "Atlanan: {skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "Özelliği talep et",
	"RequestAccessButton.UPGRADE": "Yükselt",
	"RespondentQuestionTitle.REQUIRED": "(Zorunlu.)",
	"ResponseItem.EXPAND_MENU_LABEL": "Menüyü genişlet",
	"ResponseItem.MENU_LABEL": "Etiket ekle",
	"ResponseItem.RESPONDENT_ID": "Yanıtlayan Kimliği",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "Yanıtlayanın yanıtlarını görüntüleyin",
	"ResponseStatusBar.CLEAR": "Temizle",
	"ResponseStatusBar.FAILED_TO_LOAD": "Yanıtlar yüklenemedi",
	"ResponseStatusBar.NO_RESPONSES_YET": "Henüz yanıt yok",
	"ResponseStatusBar.SEARCHING": "Aranıyor...",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {olumlu duygu} negative {olumsuz duygu} neutral {nötr duygu} other {bilinmeyen duygu}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {{filters} filtreli {searchTextFormatted} araması için seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{filters} filtreli {searchTextFormatted} araması için {resultsCountFormatted} yanıt gösteriliyor   }}} other {{hasSelectedResponse, select, true {{filters} filtreli {searchTextFormatted} araması için seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{filters} filtreli {searchTextFormatted} araması için {resultsCountFormatted} yanıt gösteriliyor   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {{filters} için seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{filters} için {resultsCountFormatted} yanıt gösteriliyor   }}} other {{hasSelectedResponse, select, true {{filters} için seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{filters} için {resultsCountFormatted} yanıt gösteriliyor   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {{searchTextFormatted} için seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{searchTextFormatted} için {resultsCountFormatted} yanıt gösteriliyor   }}} other {{hasSelectedResponse, select, true {{searchTextFormatted} için seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{searchTextFormatted} için {resultsCountFormatted} yanıt gösteriliyor   }}}}} other {{responseResultsCount, plural, one {{hasSelectedResponse, select, true {Seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{resultsCountFormatted} yanıt gösteriliyor   }}} other {{hasSelectedResponse, select, true {Seçili {resultsCountFormatted} yanıt gösteriliyor   } other {{resultsCountFormatted} yanıt gösteriliyor   }}}}}}}}",
	"ResponseToolbar.CLEAR": "Temizle",
	"ResponseToolbar.CREATE_TAG": "Etiket oluştur",
	"ResponseToolbar.SELECT_ALL": "Tüm yanıtları seçin",
	"ResponseToolbar.UNSELECT_ALL": "Tüm yanıtların seçimini kaldırın",
	"SearchResponsesInput.CLEAR": "Aramayı temizle",
	"SearchResponsesInput.SEARCH": "Ara",
	"SearchResponsesInput.SEARCH_RESPONSES": "Yanıtları Ara",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "Tam ifadeyi aramak için tırnak işareti <strong>“”</strong> kullanın.",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "İki veya daha fazla arama teriminden birini bulmak için <strong>OR</strong> yazın.",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "Birden fazla terim bulmak için <b>AND</b> yazın.",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "Aramanızdan bir terimi hariç tutmak için <strong>NOT</strong> kullanın.",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "Önek ile aramak için bir terimin ardından <strong>*</strong> yazın.",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "AND/OR/NOT tabanlı aramaları birleştirmek için parantez kullanın.",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "Eşleşen terime ek olarak en fazla iki karakterin eşleştiği sonuçları görmek için terimin ardından <strong>~2</strong> yazın.",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "Arama seçenekleri",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "Daha fazla bilgi",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "Hayır, Teşekkürler",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "Etkinleştir",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "<span id=\"link\">Otomatik Görüşler </span>artık hesabınızda mevcuttur.\n      Bu özellik anketinizi işlemek ve size görüşler sunmak için makine öğrenimi teknolojisini kullanan tüm özellikleri içerir.",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "Tercihlerinizi her zaman <span id=\"link\">Hesabım</span> bölümünden değiştirebilirsiniz",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "Otomatik Görüşler",
	"SentimentBanner.GENERATE_SENTIMENT": "Duyguları analiz edin",
	"SentimentBanner.SENTIMENT_ANALYSIS": "Duygu Analizi",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "İnsanların nasıl hissettiğini anlamak için yazılı yanıtların altında yatan duyguları tespit edin.",
	"SentimentBanner.UPGRADE": "Yükselt",
	"SentimentBanner.WATCH_A_DEMO": "Demoyu izleyin",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "İnsanlar nasıl hissediyor:",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {Olumlu duyguya göre filtrele} negative {Olumsuz duyguya göre filtrele} neutral {Nötr duyguya göre filtrele} other {Bilinmeyen duyguya göre filtrele}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "Menüyü genişlet",
	"SentimentFilterMenu.MENU_LABEL": "duyguya göre",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "Özellik Talep Et",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "Yazılı yanıtların ardındaki duygu ve hassasiyeti tespit edin.",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "YENİ!",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "Karşınızda Duygu Analizi",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "Yükselt",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "Demoyu izleyin",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "Hayır teşekkürler",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "Özelliği talep et",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "Yükselt",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "Duygu görüntü yükseltme",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "Duygu analizini tek bir tıkla çalıştırın. Metin yanıtlarınız, insanların ifadelerinin ardındaki duyguları ortaya koyacak şekilde olumlu, nötr veya olumsuz olarak sınırlandırılır.",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "İnsanların neler hissettiğini anında öğrenin",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "Duygu Analizi",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {Duygu Özelliği: AÇIK} false {Duygu Özelliği: KAPALI} other {Duygu Özelliği: diğer}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "Duygular: YÜKLENİYOR...",
	"SlideBoxNavigation.NEXT_BUTTON": "SONRAKİ",
	"SlideBoxNavigation.NEXT_SLIDE": "Sonraki slayt",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "Önceki",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "Önceki slayt",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} / {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "Temizle",
	"Slider.STEP_MESSAGE": "Girmiş olduğunuz sayı, kaydırma ölçeğine göre düzeltildi.",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "Yanıtlanan: {answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "S{questionNumber} {questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "Atlanan: {skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "Ucuz ve pahalı alanlarının kesiştiği bölüm, ürününüz için genel bir fiyat yönü verir.",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "Fiyat hassasiyeti (Van Westendorp)",
	"SrText.CORRECT": "Doğru",
	"SrText.INCORRECT": "Yanlış",
	"StarRating.Heart": "{count, plural, one {{count} kalp} other {{count} kalp}}",
	"StarRating.STAR": "{count, plural, one {{count} yıldız} other {{count} yıldız}}",
	"StarRating.Smiley": "{count, plural, one {{count} gülücük} other {{count} gülücük}}",
	"StarRating.Thumb": "{count, plural, one {{count} baş parmak} other {{count} baş parmak}}",
	"TabList.BETA_BADGE": "Beta",
	"TabList.LOCK_ICON": "Etiketler sekmesi kilit simgesi",
	"TabList.RESPONSES": "YANITLAR ({responseCount})",
	"TabList.TAGS": "ETİKETLER ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "TEMATİK ANALİZ",
	"TabList.WORD_CLOUD": "SÖZCÜK BULUTU",
	"TagFilterMenu.EXPAND_MENU_LABEL": "Menüyü genişlet",
	"TagFilterMenu.MENU_LABEL": "Filtre: etikete göre",
	"TagList.TAGS": "Etiketler",
	"TagList.TAGS_KEYWORDS": "Anahtar sözcükler",
	"TagList.TAG_CONTROLS": "Etiket kontrolleri",
	"TagList.TAG_NUMBER_PERCENT": "Etiketlerin yüzdesi ve etiket sayısı",
	"TagList.TAG_PERCENTAGE_BAR": "Etiket yüzde çubuğu",
	"TagListItem.DELETE": "Sil",
	"TagListItem.EDIT": "Düzenle",
	"TagListItem.VIEW_ALL": "Tümünü göster",
	"TagListPanel.LOADING": "Etiketler yükleniyor",
	"TagListPanel.NEW_TAG": "+ Yeni Etiket",
	"TagListPanel.TAGS": "Etiketler",
	"TagMenuContent.APPLY_ACTIONS": "UYGULA",
	"TagMenuContent.CLEAR_ACTIONS": "Temizle",
	"TagMenuContent.CLOSE_MENU": "İPTAL",
	"TagMenuContent.DUPLICATED_NAME": "O ada sahip bir etiketiniz zaten var.",
	"TagMenuContent.NEW_TAG": "Yeni Etiket",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "Daha fazla bilgi >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "Özelliği talep et",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "Yükselt",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "Kapat",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "Yazılı yanıtları arayın, etiketleyin ve filtreleyin, sık kullanılan sözcük ve ifadeler için sözük bulutlarını inceleyin.",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "Metin Analizi",
	"TimeInputWrapper.am": "AM",
	"TimeInputWrapper.daytimeLabel": "ÖÖ/ÖS",
	"TimeInputWrapper.hhPlaceholder": "ss",
	"TimeInputWrapper.hour": "Saat",
	"TimeInputWrapper.minutes": "Dakika",
	"TimeInputWrapper.mmPlaceholder": "dd",
	"TimeInputWrapper.pm": "ÖS",
	"TimeInputWrapper.timeLabel": "Saat",
	"ToggleableEmailField.checkboxLabel": "Yanıtlarımın bir kopyasını e-posta ile bana gönder",
	"ToggleableEmailField.emailLabel": "E-posta adresinizi girin",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "Sil",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, one {{responseCountFormatted} yanıtın etiketi kaldırılacak.} other {{responseCountFormatted} yanıtın etiketi kaldırılacak.}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "Etiket silindiğinde etiketlenen tüm yanıtlardan kaldırılır ve geri alınamaz.",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "Etiket silinsin mi?",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "Kaydet",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "Değişiklikler kaydedilsin mi?",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "Bu işlem etiketi tüm yanıtlardan kaldırır. Etiket düzenlenen anahtar sözcüklerle \n      eşleşen yanıtlara yeniden uygulanır.",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "İptal",
	"UpgradeBanner.SUBTITLE": "Kendi sözcük bulutunuzu özelleştirmek için yükseltin.",
	"UpgradeBanner.TITLE": "Gördüklerinizi beğendiniz mi?",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "Yanıt yüzdesi",
	"VanWestendorpChartDataTable.PRICE": "Fiyat",
	"VanWestendorpChartDataTable.RESPONSES": "Yanıtlar",
	"WordCloudCustomizationButton.CUSTOMIZE": "Özelleştir",
	"WordCloudCustomizationFooter.APPLY": "Uygula",
	"WordCloudCustomizationFooter.CANCEL": "İptal",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "Anketteki tüm sözcük bulutlarına uygulayın (Bazıları değişmeyebilir)",
	"WordCloudCustomizationFooter.NO_THANKS": "Hayır teşekkürler",
	"WordCloudCustomizationFooter.REQUEST": "Özelliği talep et",
	"WordCloudCustomizationFooter.SAVE": "Kaydet",
	"WordCloudCustomizationFooter.UPGRADE": "Yükselt",
	"WordCloudCustomizationTabs.COLOR": "RENK",
	"WordCloudCustomizationTabs.FONT": "YAZI TİPİ",
	"WordCloudCustomizationTabs.RANGE": "ARALIK",
	"WordCloudLegend.FEWER": "daha az",
	"WordCloudLegend.MORE": "daha fazla",
	"WordCloudLegend.TITLE": "Tekrar sayısı",
	"WordCloudListFooter.COMBINED_WORDS": "Birleşik kelimeler",
	"WordCloudListFooter.HIDDEN_WORDS": "Gizli sözcükler",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "Menüyü genişlet",
	"WordCloudListViewMenuContent.COMBINE_WITH": "Şununla birleştir",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "Birleşik sözcükleri düzenle",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "Menüyü genişlet",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "Sözcük bulutundan gizle",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "Birleşik sözcükleri ayır",
	"WordCloudLoadingState.LOADING": "Sözcük bulutu yükleniyor...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "Kapat düğmesi",
	"WordCloudPreviewOverlay.OK_BUTTON": "Tamam",
	"WordCloudPreviewOverlay.SUBTITLE": "Sözcük bulutlarını özelleştirme özelliklerini önizleyin.",
	"WordCloudPreviewOverlay.TITLE": "Deneyin!",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "Bulut Görünümü",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "Liste Görünümü",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "Kullanım: {weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number}. en önemli sözcük",
	"WordCount.IN_PROGRESS": "Metin Analizi devam ediyor...",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "Önemli sözcük bulunamadı.",
	"WordCount.SHOW_COUNT": "{countCheck, plural, one {{count} sözcük gösteriliyor } other {{count} sözcük gösteriliyor }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {Olumlu duyguya göre filtrele} negative {Olumsuz duyguya göre filtrele} neutral {Nötr duyguya göre filtrele} other {Tespit edilmemiş duyguya göre filtrele}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {Olumlu} negative {Olumuz} neutral {Nötr} other {Bilinmiyor}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {Olumlu: {percentage,number,percent}} negative {Olumsuz: {percentage,number,percent}} neutral {Nötr: {percentage,number,percent}} other {Bilinmeyen: {percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "İPTAL",
	"copyTagEditor.DUPLICATED_NAME": "O ada sahip bir etiketiniz zaten var.",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "Bu etiket için bir ad girin",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "Bir veya daha fazla anahtar kelime girin",
	"copyTagEditor.KEYWORDS": "<strong>Anahtar kelimeler</strong> (isteğe bağlı)",
	"copyTagEditor.SAVE": "KAYDET",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "Etiket eşleşen anahtar kelimeleri içeren tüm yanıtlar için geçerlidir",
	"copyTagEditor.TAG_NAME": "<strong>Etiket adı</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "Etiket adı boş olamaz.",
	"copyTagEditor.TAG_NAME_SPACES": "Etiket adı yalnızca boşluklardan oluşamaz.",
	"copyTagEditor.TAG_TOO_LONG": "Etiket en fazla 128 karakter olabilir.",
	"countriesL10nMessages.AC": "Ascension Adası",
	"countriesL10nMessages.AD": "Andora",
	"countriesL10nMessages.AE": "Birleşik Arap Emirlikleri",
	"countriesL10nMessages.AF": "Afganistan",
	"countriesL10nMessages.AG": "Antigua ve Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Arnavutluk",
	"countriesL10nMessages.AM": "Ermenistan",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Arjantin",
	"countriesL10nMessages.AS": "Amerikan Samoası",
	"countriesL10nMessages.AT": "Avusturya",
	"countriesL10nMessages.AU": "Avustralya",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Åland Adaları",
	"countriesL10nMessages.AZ": "Azerbaycan",
	"countriesL10nMessages.BA": "Bosna-Hersek",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladeş",
	"countriesL10nMessages.BE": "Belçika",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaristan",
	"countriesL10nMessages.BH": "Bahreyn",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darü's-Selam",
	"countriesL10nMessages.BO": "Bolivya",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius and Saba",
	"countriesL10nMessages.BR": "Brezilya",
	"countriesL10nMessages.BS": "Bahamalar",
	"countriesL10nMessages.BT": "Butan",
	"countriesL10nMessages.BW": "Botsvana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Kanada",
	"countriesL10nMessages.CC": "Cocos (Keyling) Adaları",
	"countriesL10nMessages.CD": "Kongo Demokratik Cumhuriyeti",
	"countriesL10nMessages.CF": "Orta Afrika Cumhuriyeti",
	"countriesL10nMessages.CG": "Kongo",
	"countriesL10nMessages.CH": "İsviçre",
	"countriesL10nMessages.CI": "Fildişi Sahili",
	"countriesL10nMessages.CK": "Cook Adaları",
	"countriesL10nMessages.CL": "Şili",
	"countriesL10nMessages.CM": "Kamerun",
	"countriesL10nMessages.CN": "Çin",
	"countriesL10nMessages.CO": "Kolombiya",
	"countriesL10nMessages.CR": "Kosta Rika",
	"countriesL10nMessages.CV": "Cabo Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Christmas Adası",
	"countriesL10nMessages.CY": "Kıbrıs",
	"countriesL10nMessages.CZ": "Çek Cumhuriyeti",
	"countriesL10nMessages.DE": "Almanya",
	"countriesL10nMessages.DJ": "Cibuti",
	"countriesL10nMessages.DK": "Danimarka",
	"countriesL10nMessages.DM": "Dominika",
	"countriesL10nMessages.DO": "Dominik Cumhuriyeti",
	"countriesL10nMessages.DZ": "Cezayir",
	"countriesL10nMessages.EC": "Ekvador",
	"countriesL10nMessages.EE": "Estonya",
	"countriesL10nMessages.EG": "Mısır",
	"countriesL10nMessages.EH": "Batı Sahra",
	"countriesL10nMessages.ER": "Eritre",
	"countriesL10nMessages.ES": "İspanya",
	"countriesL10nMessages.ET": "Etiyopya",
	"countriesL10nMessages.FI": "Finlandiya",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Falkland Adaları",
	"countriesL10nMessages.FM": "Mikronezya Federal Devletleri",
	"countriesL10nMessages.FO": "Faroe Adaları",
	"countriesL10nMessages.FR": "Fransa",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Birleşik Krallık",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Gürcistan",
	"countriesL10nMessages.GF": "Fransız Guyanası",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Gana",
	"countriesL10nMessages.GI": "Cebelitarık",
	"countriesL10nMessages.GL": "Grönland",
	"countriesL10nMessages.GM": "Gambiya",
	"countriesL10nMessages.GN": "Gine",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Ekvator Ginesi",
	"countriesL10nMessages.GR": "Yunanistan",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Gine-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hong Kong",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Hırvatistan",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Macaristan",
	"countriesL10nMessages.ID": "Endonezya",
	"countriesL10nMessages.IE": "İrlanda",
	"countriesL10nMessages.IL": "İsrail",
	"countriesL10nMessages.IM": "Man Adası",
	"countriesL10nMessages.IN": "Hindistan",
	"countriesL10nMessages.IO": "Britanya Hint Okyanusu Toprakları",
	"countriesL10nMessages.IQ": "Irak",
	"countriesL10nMessages.IS": "İzlanda",
	"countriesL10nMessages.IT": "İtalya",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaika",
	"countriesL10nMessages.JO": "Ürdün",
	"countriesL10nMessages.JP": "Japonya",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kırgızistan",
	"countriesL10nMessages.KH": "Kamboçya",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Komorlar",
	"countriesL10nMessages.KN": "Saint Kitts ve Nevis",
	"countriesL10nMessages.KR": "Güney Kore",
	"countriesL10nMessages.KW": "Kuveyt",
	"countriesL10nMessages.KY": "Cayman Adaları",
	"countriesL10nMessages.KZ": "Kazakistan",
	"countriesL10nMessages.LA": "Laos",
	"countriesL10nMessages.LB": "Lübnan",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberya",
	"countriesL10nMessages.LS": "Lesoto",
	"countriesL10nMessages.LT": "Litvanya",
	"countriesL10nMessages.LU": "Lüksemburg",
	"countriesL10nMessages.LV": "Letonya",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Fas",
	"countriesL10nMessages.MC": "Monako",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Karadağ",
	"countriesL10nMessages.MF": "Saint Martin (Fransız bölgesi)",
	"countriesL10nMessages.MG": "Madagaskar",
	"countriesL10nMessages.MH": "Marshall Adaları",
	"countriesL10nMessages.MK": "Makedonya",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Moğolistan",
	"countriesL10nMessages.MO": "Makao",
	"countriesL10nMessages.MP": "Kuzey Mariana Adaları",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Moritanya",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldivler",
	"countriesL10nMessages.MW": "Malavi",
	"countriesL10nMessages.MX": "Meksika",
	"countriesL10nMessages.MY": "Malezya",
	"countriesL10nMessages.MZ": "Mozambik",
	"countriesL10nMessages.NA": "Namibya",
	"countriesL10nMessages.NC": "Yeni Kaledonya",
	"countriesL10nMessages.NE": "Nijer",
	"countriesL10nMessages.NF": "Norfolk Adası",
	"countriesL10nMessages.NG": "Nijerya",
	"countriesL10nMessages.NI": "Nikaragua",
	"countriesL10nMessages.NL": "Hollanda",
	"countriesL10nMessages.NO": "Norveç",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "Yeni Zelanda",
	"countriesL10nMessages.OM": "Umman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Fransız Polinezyası",
	"countriesL10nMessages.PG": "Papua Yeni Gine",
	"countriesL10nMessages.PH": "Filipinler",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Polonya",
	"countriesL10nMessages.PM": "Saint Pierre ve Miquelon",
	"countriesL10nMessages.PR": "Porto Riko",
	"countriesL10nMessages.PS": "Filistin",
	"countriesL10nMessages.PT": "Portekiz",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Katar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romanya",
	"countriesL10nMessages.RS": "Sırbistan",
	"countriesL10nMessages.RW": "Ruanda",
	"countriesL10nMessages.SA": "Suudi Arabistan",
	"countriesL10nMessages.SB": "Solomon Adaları",
	"countriesL10nMessages.SC": "Seyşeller",
	"countriesL10nMessages.SE": "İsveç",
	"countriesL10nMessages.SG": "Singapur",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenya",
	"countriesL10nMessages.SJ": "Svalbard ve Jan Mayen",
	"countriesL10nMessages.SK": "Slovakya",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somali",
	"countriesL10nMessages.SR": "Surinam",
	"countriesL10nMessages.SS": "Güney Sudan",
	"countriesL10nMessages.ST": "Sao Tome ve Principe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Svaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Turks ve Caicos Adaları",
	"countriesL10nMessages.TD": "Çad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Tayland",
	"countriesL10nMessages.TJ": "Tacikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Timor-Leste",
	"countriesL10nMessages.TM": "Türkmenistan",
	"countriesL10nMessages.TN": "Tunus",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Türkiye",
	"countriesL10nMessages.TT": "Trinidad ve Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Tayvan",
	"countriesL10nMessages.TZ": "Tanzanya",
	"countriesL10nMessages.UA": "Ukrayna",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Birleşik Devletler",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Özbekistan",
	"countriesL10nMessages.VA": "Papalık (Vatikan Şehir Devleti)",
	"countriesL10nMessages.VC": "Saint Vincent ve Grenadinler",
	"countriesL10nMessages.VE": "Venezüella",
	"countriesL10nMessages.VG": "Virgin Adaları, Britanya",
	"countriesL10nMessages.VI": "Virgin Adaları, ABD",
	"countriesL10nMessages.VN": "Vietnam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis ve Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosova",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Güney Afrika",
	"countriesL10nMessages.ZM": "Zambiya",
	"countriesL10nMessages.ZW": "Zimbabve",
	"helper.INDIFFERENCE_PRICE_POINT": "Kayıtsızlık fiyat noktası",
	"helper.IPP": "Kayıtsızlık fiyat noktası (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "En uygun fiyat noktası (OPP)",
	"helper.OPP_DESCRIPTION": "En uygun fiyat noktası",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "Marjinal ucuzluk noktası (PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "Marjinal pahalılık noktası (PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "Marjinal ucuzluk noktası (PMC)",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "Marjinal pahalılık noktası (PME)"
};

var uk = {
	"AddressField.selectCountry": "Вибрати країну",
	"PhoneField.countryCode": "Код країни",
	"countriesL10nMessages.AC": "Вознесіння о-в",
	"countriesL10nMessages.AD": "Андорра",
	"countriesL10nMessages.AE": "Об'єднані Арабські Емірати",
	"countriesL10nMessages.AF": "Афганістан",
	"countriesL10nMessages.AG": "Антигуа і Барбуда",
	"countriesL10nMessages.AI": "Ангілья",
	"countriesL10nMessages.AL": "Албанія",
	"countriesL10nMessages.AM": "Вірменія",
	"countriesL10nMessages.AO": "Ангола",
	"countriesL10nMessages.AR": "Аргентина",
	"countriesL10nMessages.AS": "Американське Самоа",
	"countriesL10nMessages.AT": "Австрія",
	"countriesL10nMessages.AU": "Австралія",
	"countriesL10nMessages.AW": "Аруба",
	"countriesL10nMessages.AX": "Аландські о-ви",
	"countriesL10nMessages.AZ": "Азербайджан",
	"countriesL10nMessages.BA": "Боснія і Герцеґовина",
	"countriesL10nMessages.BB": "Барбадос",
	"countriesL10nMessages.BD": "Бангладеш",
	"countriesL10nMessages.BE": "Бельгія",
	"countriesL10nMessages.BF": "Буркіна Фасо",
	"countriesL10nMessages.BG": "Болгарія",
	"countriesL10nMessages.BH": "Бахрейн",
	"countriesL10nMessages.BI": "Бурунді",
	"countriesL10nMessages.BJ": "Бенін",
	"countriesL10nMessages.BL": "Сен-Бартелемі",
	"countriesL10nMessages.BM": "Бермудські о-ви",
	"countriesL10nMessages.BN": "Бруней",
	"countriesL10nMessages.BO": "Болівія",
	"countriesL10nMessages.BQ": "Бонайре, Сінт-Естатіус і Саба",
	"countriesL10nMessages.BR": "Бразилія",
	"countriesL10nMessages.BS": "Багамські о-ви",
	"countriesL10nMessages.BT": "Бутан",
	"countriesL10nMessages.BW": "Ботсвана",
	"countriesL10nMessages.BZ": "Беліз",
	"countriesL10nMessages.CA": "Канада",
	"countriesL10nMessages.CC": "Кокосові (Кілінґ) о-ви",
	"countriesL10nMessages.CD": "Конго, Демократична Республіка",
	"countriesL10nMessages.CF": "Центральноафриканська Республіка",
	"countriesL10nMessages.CG": "Конго",
	"countriesL10nMessages.CH": "Швейцарія",
	"countriesL10nMessages.CI": "Кот д'Івуар",
	"countriesL10nMessages.CK": "Кука о-ви",
	"countriesL10nMessages.CL": "Чилі",
	"countriesL10nMessages.CM": "Камерун",
	"countriesL10nMessages.CN": "Китай",
	"countriesL10nMessages.CO": "Колумбія",
	"countriesL10nMessages.CR": "Коста-Ріка",
	"countriesL10nMessages.CV": "Кабо-Верде",
	"countriesL10nMessages.CW": "Кюрасао",
	"countriesL10nMessages.CX": "Різдва о-в",
	"countriesL10nMessages.CY": "Кіпр",
	"countriesL10nMessages.CZ": "Чеська Республіка",
	"countriesL10nMessages.DE": "Німеччина",
	"countriesL10nMessages.DJ": "Джібуті",
	"countriesL10nMessages.DK": "Данія",
	"countriesL10nMessages.DM": "Домініка",
	"countriesL10nMessages.DO": "Домініканська Республіка",
	"countriesL10nMessages.DZ": "Алжир",
	"countriesL10nMessages.EC": "Еквадор",
	"countriesL10nMessages.EE": "Естонія",
	"countriesL10nMessages.EG": "Єгипет",
	"countriesL10nMessages.EH": "Західна Сахара",
	"countriesL10nMessages.ER": "Еритрея",
	"countriesL10nMessages.ES": "Іспанія",
	"countriesL10nMessages.ET": "Ефіопія",
	"countriesL10nMessages.FI": "Фінляндія",
	"countriesL10nMessages.FJ": "Фіджі",
	"countriesL10nMessages.FK": "Фолклендські о-ви",
	"countriesL10nMessages.FM": "Федеративні Штати Мікронезії",
	"countriesL10nMessages.FO": "Фарерські о-ви",
	"countriesL10nMessages.FR": "Франція",
	"countriesL10nMessages.GA": "Габон",
	"countriesL10nMessages.GB": "Сполучене Королівство",
	"countriesL10nMessages.GD": "Гренада",
	"countriesL10nMessages.GE": "Грузія",
	"countriesL10nMessages.GF": "Французька Гвіана",
	"countriesL10nMessages.GG": "Гернсі",
	"countriesL10nMessages.GH": "Гана",
	"countriesL10nMessages.GI": "Гібралтар",
	"countriesL10nMessages.GL": "Гренландія",
	"countriesL10nMessages.GM": "Гамбія",
	"countriesL10nMessages.GN": "Гвінея",
	"countriesL10nMessages.GP": "Гваделупа",
	"countriesL10nMessages.GQ": "Екваторіальна Гвінея",
	"countriesL10nMessages.GR": "Греція",
	"countriesL10nMessages.GT": "Гватемала",
	"countriesL10nMessages.GU": "Гуам",
	"countriesL10nMessages.GW": "Гвінея-Бісау",
	"countriesL10nMessages.GY": "Гайана",
	"countriesL10nMessages.HK": "Гонконг",
	"countriesL10nMessages.HN": "Гондурас",
	"countriesL10nMessages.HR": "Хорватія",
	"countriesL10nMessages.HT": "Гаїті",
	"countriesL10nMessages.HU": "Угорщина",
	"countriesL10nMessages.ID": "Індонезія",
	"countriesL10nMessages.IE": "Ірландія",
	"countriesL10nMessages.IL": "Ізраїль",
	"countriesL10nMessages.IM": "Мен, о-в",
	"countriesL10nMessages.IN": "Індія",
	"countriesL10nMessages.IO": "Британська територія в Індійському океані",
	"countriesL10nMessages.IQ": "Ірак",
	"countriesL10nMessages.IS": "Ісландія",
	"countriesL10nMessages.IT": "Італія",
	"countriesL10nMessages.JE": "Джерсі",
	"countriesL10nMessages.JM": "Ямайка",
	"countriesL10nMessages.JO": "Йорданія",
	"countriesL10nMessages.JP": "Японія",
	"countriesL10nMessages.KE": "Кенія",
	"countriesL10nMessages.KG": "Киргизстан",
	"countriesL10nMessages.KH": "Камбоджа",
	"countriesL10nMessages.KI": "Кірібаті",
	"countriesL10nMessages.KM": "Коморські о-ви",
	"countriesL10nMessages.KN": "Сент-Кітс і Невіс",
	"countriesL10nMessages.KR": "Південна Корея",
	"countriesL10nMessages.KW": "Кувейт",
	"countriesL10nMessages.KY": "Кайманові о-ви",
	"countriesL10nMessages.KZ": "Казахстан",
	"countriesL10nMessages.LA": "Лаос",
	"countriesL10nMessages.LB": "Ліван",
	"countriesL10nMessages.LC": "Сент-Люсія",
	"countriesL10nMessages.LI": "Ліхтенштейн",
	"countriesL10nMessages.LK": "Шрі Ланка",
	"countriesL10nMessages.LR": "Ліберія",
	"countriesL10nMessages.LS": "Лесото",
	"countriesL10nMessages.LT": "Литва",
	"countriesL10nMessages.LU": "Люксембург",
	"countriesL10nMessages.LV": "Латвія",
	"countriesL10nMessages.LY": "Лівія",
	"countriesL10nMessages.MA": "Марокко",
	"countriesL10nMessages.MC": "Монако",
	"countriesL10nMessages.MD": "Молдова",
	"countriesL10nMessages.ME": "Чорногорія",
	"countriesL10nMessages.MF": "Сен-Мартен (французька частина)",
	"countriesL10nMessages.MG": "Мадагаскар",
	"countriesL10nMessages.MH": "Маршаллові о-ви",
	"countriesL10nMessages.MK": "Македонія",
	"countriesL10nMessages.ML": "Малі",
	"countriesL10nMessages.MM": "М'янма",
	"countriesL10nMessages.MN": "Монголія",
	"countriesL10nMessages.MO": "Макао",
	"countriesL10nMessages.MP": "Північні Маріанські о-ви",
	"countriesL10nMessages.MQ": "Мартініка",
	"countriesL10nMessages.MR": "Мавританія",
	"countriesL10nMessages.MS": "Монтсеррат",
	"countriesL10nMessages.MT": "Мальта",
	"countriesL10nMessages.MU": "Маврикій",
	"countriesL10nMessages.MV": "Мальдіви",
	"countriesL10nMessages.MW": "Малаві",
	"countriesL10nMessages.MX": "Мексика",
	"countriesL10nMessages.MY": "Малайзія",
	"countriesL10nMessages.MZ": "Мозамбік",
	"countriesL10nMessages.NA": "Намібія",
	"countriesL10nMessages.NC": "Нова Каледонія",
	"countriesL10nMessages.NE": "Нігер",
	"countriesL10nMessages.NF": "Норфолк, о-в",
	"countriesL10nMessages.NG": "Нігерія",
	"countriesL10nMessages.NI": "Нікарагуа",
	"countriesL10nMessages.NL": "Нідерланди",
	"countriesL10nMessages.NO": "Норвегія",
	"countriesL10nMessages.NP": "Непал",
	"countriesL10nMessages.NR": "Науру",
	"countriesL10nMessages.NU": "Ніуе",
	"countriesL10nMessages.NZ": "Нова Зеландія",
	"countriesL10nMessages.OM": "Оман",
	"countriesL10nMessages.PA": "Панама",
	"countriesL10nMessages.PE": "Перу",
	"countriesL10nMessages.PF": "Французька Полінезія",
	"countriesL10nMessages.PG": "Папуа-Нова Гвінея",
	"countriesL10nMessages.PH": "Філіппіни",
	"countriesL10nMessages.PK": "Пакистан",
	"countriesL10nMessages.PL": "Польща",
	"countriesL10nMessages.PM": "Сен-П’єр і Мікелон",
	"countriesL10nMessages.PR": "Пуерто-Ріко",
	"countriesL10nMessages.PS": "Палестина",
	"countriesL10nMessages.PT": "Португалія",
	"countriesL10nMessages.PW": "Палау",
	"countriesL10nMessages.PY": "Парагвай",
	"countriesL10nMessages.QA": "Катар",
	"countriesL10nMessages.RE": "Реюньйон",
	"countriesL10nMessages.RO": "Румунія",
	"countriesL10nMessages.RS": "Сербія",
	"countriesL10nMessages.RW": "Руанда",
	"countriesL10nMessages.SA": "Саудівська Аравія",
	"countriesL10nMessages.SB": "Соломонові о-ви",
	"countriesL10nMessages.SC": "Сейшельські о-ви",
	"countriesL10nMessages.SE": "Швеція",
	"countriesL10nMessages.SG": "Сінгапур",
	"countriesL10nMessages.SH": "Святої Єлени о-в",
	"countriesL10nMessages.SI": "Словенія",
	"countriesL10nMessages.SJ": "Шпіцберген і Ян-Маєн",
	"countriesL10nMessages.SK": "Словаччина",
	"countriesL10nMessages.SL": "Сьєрра-Леоне",
	"countriesL10nMessages.SM": "Сан-Марино",
	"countriesL10nMessages.SN": "Сенегал",
	"countriesL10nMessages.SO": "Сомалі",
	"countriesL10nMessages.SR": "Сурінам",
	"countriesL10nMessages.SS": "Південний Судан",
	"countriesL10nMessages.ST": "Сан-Томе і Принсіпі",
	"countriesL10nMessages.SV": "Сальвадор",
	"countriesL10nMessages.SX": "Сінт-Мартен",
	"countriesL10nMessages.SZ": "Свазіленд",
	"countriesL10nMessages.TA": "Трістан-да-Кунья",
	"countriesL10nMessages.TC": "Теркс і Кайкос, о-ви",
	"countriesL10nMessages.TD": "Чад",
	"countriesL10nMessages.TG": "Того",
	"countriesL10nMessages.TH": "Таїланд",
	"countriesL10nMessages.TJ": "Таджикистан",
	"countriesL10nMessages.TK": "Токелау",
	"countriesL10nMessages.TL": "Тимор-Лешті",
	"countriesL10nMessages.TM": "Туркменістан",
	"countriesL10nMessages.TN": "Туніс",
	"countriesL10nMessages.TO": "Тонга",
	"countriesL10nMessages.TR": "Туреччина",
	"countriesL10nMessages.TT": "Тринідад і Тобаго",
	"countriesL10nMessages.TV": "Тувалу",
	"countriesL10nMessages.TW": "Тайвань",
	"countriesL10nMessages.TZ": "Танзанія",
	"countriesL10nMessages.UA": "Україна",
	"countriesL10nMessages.UG": "Уганда",
	"countriesL10nMessages.US": "США",
	"countriesL10nMessages.UY": "Уругвай",
	"countriesL10nMessages.UZ": "Узбекистан",
	"countriesL10nMessages.VA": "Ватикан",
	"countriesL10nMessages.VC": "Сент-Вінсент і Гренадини",
	"countriesL10nMessages.VE": "Венесуела",
	"countriesL10nMessages.VG": "Віргінські о-ви, британські",
	"countriesL10nMessages.VI": "Віргінські о-ви, США",
	"countriesL10nMessages.VN": "В'єтнам",
	"countriesL10nMessages.VU": "Вануату",
	"countriesL10nMessages.WF": "Уолліс і Футуна",
	"countriesL10nMessages.WS": "Самоа",
	"countriesL10nMessages.XK": "Косово",
	"countriesL10nMessages.YE": "Ємен",
	"countriesL10nMessages.YT": "Майотта",
	"countriesL10nMessages.ZA": "Південна Африка",
	"countriesL10nMessages.ZM": "Замбія",
	"countriesL10nMessages.ZW": "Зімбабве",
	"ToggleableEmailField.checkboxLabel": "Надіслати мені копію моїх відповідей електронною поштою",
	"ToggleableEmailField.emailLabel": "Введіть свою адресу електронної пошти"
};

var vi = {
	"AddressField.selectCountry": "Chọn quốc gia",
	"PhoneField.countryCode": "Mã quốc gia",
	"countriesL10nMessages.AC": "Đảo Ascension",
	"countriesL10nMessages.AD": "Andorra",
	"countriesL10nMessages.AE": "Các tiểu Vương quốc Ả rập Thống nhất",
	"countriesL10nMessages.AF": "Afghanistan",
	"countriesL10nMessages.AG": "Antigua và Barbuda",
	"countriesL10nMessages.AI": "Anguilla",
	"countriesL10nMessages.AL": "Albania",
	"countriesL10nMessages.AM": "Armenia",
	"countriesL10nMessages.AO": "Angola",
	"countriesL10nMessages.AR": "Argentina",
	"countriesL10nMessages.AS": "American Samoa",
	"countriesL10nMessages.AT": "Austria",
	"countriesL10nMessages.AU": "Australia",
	"countriesL10nMessages.AW": "Aruba",
	"countriesL10nMessages.AX": "Quần đảo Åland",
	"countriesL10nMessages.AZ": "Azerbaijan",
	"countriesL10nMessages.BA": "Bosnia và Herzegovina",
	"countriesL10nMessages.BB": "Barbados",
	"countriesL10nMessages.BD": "Bangladesh",
	"countriesL10nMessages.BE": "Bỉ",
	"countriesL10nMessages.BF": "Burkina Faso",
	"countriesL10nMessages.BG": "Bulgaria",
	"countriesL10nMessages.BH": "Bahrain",
	"countriesL10nMessages.BI": "Burundi",
	"countriesL10nMessages.BJ": "Benin",
	"countriesL10nMessages.BL": "Saint Barthélemy",
	"countriesL10nMessages.BM": "Bermuda",
	"countriesL10nMessages.BN": "Brunei Darussalam",
	"countriesL10nMessages.BO": "Bolivia",
	"countriesL10nMessages.BQ": "Bonaire, Sint Eustatius and Saba",
	"countriesL10nMessages.BR": "Brazil",
	"countriesL10nMessages.BS": "Bahamas",
	"countriesL10nMessages.BT": "Bhutan",
	"countriesL10nMessages.BW": "Botswana",
	"countriesL10nMessages.BZ": "Belize",
	"countriesL10nMessages.CA": "Canada",
	"countriesL10nMessages.CC": "Lãnh thổ Quần đảo Cocos (Keeling)",
	"countriesL10nMessages.CD": "Congo, Cộng hòa dân chủ của",
	"countriesL10nMessages.CF": "Cộng hoà Trung Phi",
	"countriesL10nMessages.CG": "Congo",
	"countriesL10nMessages.CH": "Switzerland",
	"countriesL10nMessages.CI": "Bờ Biển Ngà",
	"countriesL10nMessages.CK": "Quần đảo Cook",
	"countriesL10nMessages.CL": "Chile",
	"countriesL10nMessages.CM": "Cameroon",
	"countriesL10nMessages.CN": "Trung Quốc",
	"countriesL10nMessages.CO": "Colombia",
	"countriesL10nMessages.CR": "Costa Rica",
	"countriesL10nMessages.CV": "Cape Verde",
	"countriesL10nMessages.CW": "Curaçao",
	"countriesL10nMessages.CX": "Đảo Giáng Sinh",
	"countriesL10nMessages.CY": "Cyprus",
	"countriesL10nMessages.CZ": "Cộng hòa Séc",
	"countriesL10nMessages.DE": "Đức",
	"countriesL10nMessages.DJ": "Djibouti",
	"countriesL10nMessages.DK": "Đan Mạch",
	"countriesL10nMessages.DM": "Dominica",
	"countriesL10nMessages.DO": "Cộng hoà Dominica",
	"countriesL10nMessages.DZ": "Algeria",
	"countriesL10nMessages.EC": "Ecuador",
	"countriesL10nMessages.EE": "Estonia",
	"countriesL10nMessages.EG": "Ai Cập",
	"countriesL10nMessages.EH": "Tây Sahara",
	"countriesL10nMessages.ER": "Eritrea",
	"countriesL10nMessages.ES": "Tây Ban Nha",
	"countriesL10nMessages.ET": "Ethiopia",
	"countriesL10nMessages.FI": "Phần Lan",
	"countriesL10nMessages.FJ": "Fiji",
	"countriesL10nMessages.FK": "Quần đảo Falkland",
	"countriesL10nMessages.FM": "Liên bang Micronesia",
	"countriesL10nMessages.FO": "Quần đảo Faroe",
	"countriesL10nMessages.FR": "Pháp",
	"countriesL10nMessages.GA": "Gabon",
	"countriesL10nMessages.GB": "Vương Quốc Anh",
	"countriesL10nMessages.GD": "Grenada",
	"countriesL10nMessages.GE": "Georgia",
	"countriesL10nMessages.GF": "Guiana thuộc Pháp",
	"countriesL10nMessages.GG": "Guernsey",
	"countriesL10nMessages.GH": "Ghana",
	"countriesL10nMessages.GI": "Gibraltar",
	"countriesL10nMessages.GL": "Greenland",
	"countriesL10nMessages.GM": "Gambia",
	"countriesL10nMessages.GN": "Guinea",
	"countriesL10nMessages.GP": "Guadeloupe",
	"countriesL10nMessages.GQ": "Guinea Xích đạo",
	"countriesL10nMessages.GR": "Hy Lạp",
	"countriesL10nMessages.GT": "Guatemala",
	"countriesL10nMessages.GU": "Guam",
	"countriesL10nMessages.GW": "Guinea-Bissau",
	"countriesL10nMessages.GY": "Guyana",
	"countriesL10nMessages.HK": "Hồng Kông",
	"countriesL10nMessages.HN": "Honduras",
	"countriesL10nMessages.HR": "Croatia",
	"countriesL10nMessages.HT": "Haiti",
	"countriesL10nMessages.HU": "Hungary",
	"countriesL10nMessages.ID": "Indonesia",
	"countriesL10nMessages.IE": "Ireland",
	"countriesL10nMessages.IL": "Israel",
	"countriesL10nMessages.IM": "Đảo Man",
	"countriesL10nMessages.IN": "Ấn Độ",
	"countriesL10nMessages.IO": "Lãnh thổ Ấn Độ Dương thuộc Anh",
	"countriesL10nMessages.IQ": "Iraq",
	"countriesL10nMessages.IS": "Iceland",
	"countriesL10nMessages.IT": "Ý",
	"countriesL10nMessages.JE": "Jersey",
	"countriesL10nMessages.JM": "Jamaica",
	"countriesL10nMessages.JO": "Jordan",
	"countriesL10nMessages.JP": "Nhật Bản",
	"countriesL10nMessages.KE": "Kenya",
	"countriesL10nMessages.KG": "Kyrgyzstan",
	"countriesL10nMessages.KH": "Campuchia",
	"countriesL10nMessages.KI": "Kiribati",
	"countriesL10nMessages.KM": "Comoros",
	"countriesL10nMessages.KN": "Saint Kitts và Nevis",
	"countriesL10nMessages.KR": "Hàn Quốc",
	"countriesL10nMessages.KW": "Kuwait",
	"countriesL10nMessages.KY": "Quần đảo Cayman",
	"countriesL10nMessages.KZ": "Kazakhstan",
	"countriesL10nMessages.LA": "Lào",
	"countriesL10nMessages.LB": "Liban",
	"countriesL10nMessages.LC": "Saint Lucia",
	"countriesL10nMessages.LI": "Liechtenstein",
	"countriesL10nMessages.LK": "Sri Lanka",
	"countriesL10nMessages.LR": "Liberia",
	"countriesL10nMessages.LS": "Lesotho",
	"countriesL10nMessages.LT": "Lithuania",
	"countriesL10nMessages.LU": "Luxembourg",
	"countriesL10nMessages.LV": "Latvia",
	"countriesL10nMessages.LY": "Libya",
	"countriesL10nMessages.MA": "Morocco",
	"countriesL10nMessages.MC": "Monaco",
	"countriesL10nMessages.MD": "Moldova",
	"countriesL10nMessages.ME": "Montenegro",
	"countriesL10nMessages.MF": "Saint Martin (thuộc Pháp)",
	"countriesL10nMessages.MG": "Madagascar",
	"countriesL10nMessages.MH": "Quần đảo Marshall",
	"countriesL10nMessages.MK": "Macedonia",
	"countriesL10nMessages.ML": "Mali",
	"countriesL10nMessages.MM": "Myanmar",
	"countriesL10nMessages.MN": "Mông Cổ",
	"countriesL10nMessages.MO": "Ma Cao",
	"countriesL10nMessages.MP": "Quần đảo Bắc Mariana",
	"countriesL10nMessages.MQ": "Martinique",
	"countriesL10nMessages.MR": "Mauritania",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "Malta",
	"countriesL10nMessages.MU": "Mauritius",
	"countriesL10nMessages.MV": "Maldives",
	"countriesL10nMessages.MW": "Malawi",
	"countriesL10nMessages.MX": "Mexico",
	"countriesL10nMessages.MY": "Malaysia",
	"countriesL10nMessages.MZ": "Mozambique",
	"countriesL10nMessages.NA": "Namibia",
	"countriesL10nMessages.NC": "New Caledonia",
	"countriesL10nMessages.NE": "Niger",
	"countriesL10nMessages.NF": "Đảo Norfolk",
	"countriesL10nMessages.NG": "Nigeria",
	"countriesL10nMessages.NI": "Nicaragua",
	"countriesL10nMessages.NL": "Hà Lan",
	"countriesL10nMessages.NO": "Na Uy",
	"countriesL10nMessages.NP": "Nepal",
	"countriesL10nMessages.NR": "Nauru",
	"countriesL10nMessages.NU": "Niue",
	"countriesL10nMessages.NZ": "New Zealand",
	"countriesL10nMessages.OM": "Oman",
	"countriesL10nMessages.PA": "Panama",
	"countriesL10nMessages.PE": "Peru",
	"countriesL10nMessages.PF": "Polynesia thuộc Pháp",
	"countriesL10nMessages.PG": "Papua New Guinea",
	"countriesL10nMessages.PH": "Philippines",
	"countriesL10nMessages.PK": "Pakistan",
	"countriesL10nMessages.PL": "Ba Lan",
	"countriesL10nMessages.PM": "Saint Pierre và Miquelon",
	"countriesL10nMessages.PR": "Puerto Rico",
	"countriesL10nMessages.PS": "Palestine",
	"countriesL10nMessages.PT": "Bồ Đào Nha",
	"countriesL10nMessages.PW": "Palau",
	"countriesL10nMessages.PY": "Paraguay",
	"countriesL10nMessages.QA": "Qatar",
	"countriesL10nMessages.RE": "Réunion",
	"countriesL10nMessages.RO": "Romania",
	"countriesL10nMessages.RS": "Serbia",
	"countriesL10nMessages.RW": "Rwanda",
	"countriesL10nMessages.SA": "Ả Rập Xê-út",
	"countriesL10nMessages.SB": "Quần đảo Solomon",
	"countriesL10nMessages.SC": "Seychelles",
	"countriesL10nMessages.SE": "Thụy Điển",
	"countriesL10nMessages.SG": "Singapore",
	"countriesL10nMessages.SH": "Saint Helena",
	"countriesL10nMessages.SI": "Slovenia",
	"countriesL10nMessages.SJ": "Svalbard và Jan Mayen",
	"countriesL10nMessages.SK": "Slovakia",
	"countriesL10nMessages.SL": "Sierra Leone",
	"countriesL10nMessages.SM": "San Marino",
	"countriesL10nMessages.SN": "Senegal",
	"countriesL10nMessages.SO": "Somalia",
	"countriesL10nMessages.SR": "Suriname",
	"countriesL10nMessages.SS": "Nam Sudan",
	"countriesL10nMessages.ST": "São Tomé và Príncipe",
	"countriesL10nMessages.SV": "El Salvador",
	"countriesL10nMessages.SX": "Sint Maarten",
	"countriesL10nMessages.SZ": "Swaziland",
	"countriesL10nMessages.TA": "Tristan da Cunha",
	"countriesL10nMessages.TC": "Quần đảo Turks & Caicos",
	"countriesL10nMessages.TD": "Chad",
	"countriesL10nMessages.TG": "Togo",
	"countriesL10nMessages.TH": "Thái Lan",
	"countriesL10nMessages.TJ": "Tajikistan",
	"countriesL10nMessages.TK": "Tokelau",
	"countriesL10nMessages.TL": "Đông Timor",
	"countriesL10nMessages.TM": "Turkmenistan",
	"countriesL10nMessages.TN": "Tunisia",
	"countriesL10nMessages.TO": "Tonga",
	"countriesL10nMessages.TR": "Thổ Nhĩ Kỳ",
	"countriesL10nMessages.TT": "Trinidad và Tobago",
	"countriesL10nMessages.TV": "Tuvalu",
	"countriesL10nMessages.TW": "Đài Loan",
	"countriesL10nMessages.TZ": "Tanzania",
	"countriesL10nMessages.UA": "Ukraine",
	"countriesL10nMessages.UG": "Uganda",
	"countriesL10nMessages.US": "Hoa Kỳ)",
	"countriesL10nMessages.UY": "Uruguay",
	"countriesL10nMessages.UZ": "Uzbekistan",
	"countriesL10nMessages.VA": "Tòa thánh (Thành quốc Vatican)",
	"countriesL10nMessages.VC": "St. Vincent và Grenadines",
	"countriesL10nMessages.VE": "Venezuela",
	"countriesL10nMessages.VG": "Quần đảo Virgin thuộc Anh",
	"countriesL10nMessages.VI": "Quần đảo Virgin thuộc Hoa Kỳ",
	"countriesL10nMessages.VN": "Việt Nam",
	"countriesL10nMessages.VU": "Vanuatu",
	"countriesL10nMessages.WF": "Wallis và Futuna",
	"countriesL10nMessages.WS": "Samoa",
	"countriesL10nMessages.XK": "Kosovo",
	"countriesL10nMessages.YE": "Yemen",
	"countriesL10nMessages.YT": "Mayotte",
	"countriesL10nMessages.ZA": "Nam Phi",
	"countriesL10nMessages.ZM": "Zambia",
	"countriesL10nMessages.ZW": "Zimbabwe",
	"ToggleableEmailField.checkboxLabel": "Gửi bản sao các phản hồi của tôi qua email",
	"ToggleableEmailField.emailLabel": "Nhập địa chỉ email của bạn"
};

var zhCn = {
	"AddressField.selectCountry": "选择国家",
	"PhoneField.countryCode": "国家代码",
	"countriesL10nMessages.AC": "阿森松岛",
	"countriesL10nMessages.AD": "安道尔",
	"countriesL10nMessages.AE": "阿拉伯联合酋长国",
	"countriesL10nMessages.AF": "阿富汗",
	"countriesL10nMessages.AG": "安提瓜和巴布达",
	"countriesL10nMessages.AI": "安圭拉",
	"countriesL10nMessages.AL": "阿尔巴尼亚",
	"countriesL10nMessages.AM": "亚美尼亚",
	"countriesL10nMessages.AO": "安哥拉",
	"countriesL10nMessages.AR": "阿根廷",
	"countriesL10nMessages.AS": "美属萨摩亚",
	"countriesL10nMessages.AT": "奥地利",
	"countriesL10nMessages.AU": "澳大利亚",
	"countriesL10nMessages.AW": "阿鲁巴岛",
	"countriesL10nMessages.AX": "奥兰群岛",
	"countriesL10nMessages.AZ": "阿塞拜疆",
	"countriesL10nMessages.BA": "波斯尼亚和黑塞哥维那",
	"countriesL10nMessages.BB": "巴巴多斯",
	"countriesL10nMessages.BD": "孟加拉国",
	"countriesL10nMessages.BE": "比利时",
	"countriesL10nMessages.BF": "布基纳法索",
	"countriesL10nMessages.BG": "保加利亚",
	"countriesL10nMessages.BH": "巴林",
	"countriesL10nMessages.BI": "布隆迪",
	"countriesL10nMessages.BJ": "贝宁",
	"countriesL10nMessages.BL": "圣巴泰勒米",
	"countriesL10nMessages.BM": "百慕大群岛",
	"countriesL10nMessages.BN": "文莱达鲁萨兰国",
	"countriesL10nMessages.BO": "玻利维亚",
	"countriesL10nMessages.BQ": "博奈尔岛、圣尤斯特歇斯岛和萨巴岛",
	"countriesL10nMessages.BR": "巴西",
	"countriesL10nMessages.BS": "巴哈马",
	"countriesL10nMessages.BT": "不丹",
	"countriesL10nMessages.BW": "博茨瓦纳",
	"countriesL10nMessages.BZ": "伯利兹",
	"countriesL10nMessages.CA": "加拿大",
	"countriesL10nMessages.CC": "科科斯（基林）群岛",
	"countriesL10nMessages.CD": "刚果民主共和国",
	"countriesL10nMessages.CF": "中非共和国",
	"countriesL10nMessages.CG": "刚果",
	"countriesL10nMessages.CH": "瑞士",
	"countriesL10nMessages.CI": "科特迪瓦",
	"countriesL10nMessages.CK": "库克群岛",
	"countriesL10nMessages.CL": "智利",
	"countriesL10nMessages.CM": "喀麦隆",
	"countriesL10nMessages.CN": "中国",
	"countriesL10nMessages.CO": "哥伦比亚",
	"countriesL10nMessages.CR": "哥斯达黎加",
	"countriesL10nMessages.CV": "佛得角",
	"countriesL10nMessages.CW": "库拉索",
	"countriesL10nMessages.CX": "圣诞岛",
	"countriesL10nMessages.CY": "塞浦路斯",
	"countriesL10nMessages.CZ": "捷克共和国",
	"countriesL10nMessages.DE": "德国",
	"countriesL10nMessages.DJ": "吉布提",
	"countriesL10nMessages.DK": "丹麦",
	"countriesL10nMessages.DM": "多米尼加",
	"countriesL10nMessages.DO": "多明尼加共和国",
	"countriesL10nMessages.DZ": "阿尔及利亚",
	"countriesL10nMessages.EC": "厄瓜多尔",
	"countriesL10nMessages.EE": "爱沙尼亚",
	"countriesL10nMessages.EG": "埃及",
	"countriesL10nMessages.EH": "撒哈拉沙漠西部",
	"countriesL10nMessages.ER": "厄立特里亚",
	"countriesL10nMessages.ES": "西班牙",
	"countriesL10nMessages.ET": "埃塞俄比亚",
	"countriesL10nMessages.FI": "芬兰",
	"countriesL10nMessages.FJ": "斐济",
	"countriesL10nMessages.FK": "福克兰群岛",
	"countriesL10nMessages.FM": "密克罗尼西亚联邦",
	"countriesL10nMessages.FO": "法罗群岛",
	"countriesL10nMessages.FR": "法国",
	"countriesL10nMessages.GA": "加蓬",
	"countriesL10nMessages.GB": "英国",
	"countriesL10nMessages.GD": "格林纳达",
	"countriesL10nMessages.GE": "格鲁吉亚",
	"countriesL10nMessages.GF": "法属圭亚那",
	"countriesL10nMessages.GG": "根西岛",
	"countriesL10nMessages.GH": "加纳",
	"countriesL10nMessages.GI": "直布罗陀",
	"countriesL10nMessages.GL": "格陵兰",
	"countriesL10nMessages.GM": "冈比亚",
	"countriesL10nMessages.GN": "几内亚",
	"countriesL10nMessages.GP": "瓜德罗普岛",
	"countriesL10nMessages.GQ": "赤道几内亚",
	"countriesL10nMessages.GR": "希腊",
	"countriesL10nMessages.GT": "危地马拉",
	"countriesL10nMessages.GU": "关岛",
	"countriesL10nMessages.GW": "几内亚比绍",
	"countriesL10nMessages.GY": "圭亚那",
	"countriesL10nMessages.HK": "香港",
	"countriesL10nMessages.HN": "洪都拉斯",
	"countriesL10nMessages.HR": "克罗地亚",
	"countriesL10nMessages.HT": "海地",
	"countriesL10nMessages.HU": "匈牙利",
	"countriesL10nMessages.ID": "印度尼西亚",
	"countriesL10nMessages.IE": "爱尔兰",
	"countriesL10nMessages.IL": "以色列",
	"countriesL10nMessages.IM": "马恩岛",
	"countriesL10nMessages.IN": "印度",
	"countriesL10nMessages.IO": "英属印度洋领地",
	"countriesL10nMessages.IQ": "伊拉克",
	"countriesL10nMessages.IS": "冰岛",
	"countriesL10nMessages.IT": "意大利",
	"countriesL10nMessages.JE": "泽西岛",
	"countriesL10nMessages.JM": "牙买加",
	"countriesL10nMessages.JO": "约旦",
	"countriesL10nMessages.JP": "日本",
	"countriesL10nMessages.KE": "肯尼亚",
	"countriesL10nMessages.KG": "吉尔吉斯斯坦",
	"countriesL10nMessages.KH": "柬埔寨",
	"countriesL10nMessages.KI": "基里巴斯",
	"countriesL10nMessages.KM": "科摩罗",
	"countriesL10nMessages.KN": "圣基茨和尼维斯",
	"countriesL10nMessages.KR": "韩国",
	"countriesL10nMessages.KW": "科威特",
	"countriesL10nMessages.KY": "开曼群岛",
	"countriesL10nMessages.KZ": "哈萨克斯坦",
	"countriesL10nMessages.LA": "老挝",
	"countriesL10nMessages.LB": "黎巴嫩",
	"countriesL10nMessages.LC": "圣卢西亚",
	"countriesL10nMessages.LI": "列支敦士登",
	"countriesL10nMessages.LK": "斯里兰卡",
	"countriesL10nMessages.LR": "利比里亚",
	"countriesL10nMessages.LS": "莱索托",
	"countriesL10nMessages.LT": "立陶宛",
	"countriesL10nMessages.LU": "卢森堡",
	"countriesL10nMessages.LV": "拉脱维亚",
	"countriesL10nMessages.LY": "利比亚",
	"countriesL10nMessages.MA": "摩洛哥",
	"countriesL10nMessages.MC": "摩纳哥",
	"countriesL10nMessages.MD": "摩尔多瓦",
	"countriesL10nMessages.ME": "黑山",
	"countriesL10nMessages.MF": "圣马丁岛（法属）",
	"countriesL10nMessages.MG": "马达加斯加",
	"countriesL10nMessages.MH": "马绍尔群岛",
	"countriesL10nMessages.MK": "马其顿",
	"countriesL10nMessages.ML": "马里",
	"countriesL10nMessages.MM": "缅甸",
	"countriesL10nMessages.MN": "蒙古",
	"countriesL10nMessages.MO": "澳门",
	"countriesL10nMessages.MP": "北马里亚纳岛",
	"countriesL10nMessages.MQ": "马提尼克岛",
	"countriesL10nMessages.MR": "毛里塔尼亚",
	"countriesL10nMessages.MS": "蒙特塞拉特",
	"countriesL10nMessages.MT": "马耳他",
	"countriesL10nMessages.MU": "毛里求斯",
	"countriesL10nMessages.MV": "马尔代夫",
	"countriesL10nMessages.MW": "马拉维",
	"countriesL10nMessages.MX": "墨西哥",
	"countriesL10nMessages.MY": "马来西亚",
	"countriesL10nMessages.MZ": "莫桑比克",
	"countriesL10nMessages.NA": "纳米比亚",
	"countriesL10nMessages.NC": "新喀里多尼亚",
	"countriesL10nMessages.NE": "尼日尔",
	"countriesL10nMessages.NF": "诺福克岛",
	"countriesL10nMessages.NG": "尼日利亚",
	"countriesL10nMessages.NI": "尼加拉瓜",
	"countriesL10nMessages.NL": "荷兰",
	"countriesL10nMessages.NO": "挪威",
	"countriesL10nMessages.NP": "尼泊尔",
	"countriesL10nMessages.NR": "瑙鲁",
	"countriesL10nMessages.NU": "纽埃",
	"countriesL10nMessages.NZ": "新西兰",
	"countriesL10nMessages.OM": "阿曼",
	"countriesL10nMessages.PA": "巴拿马",
	"countriesL10nMessages.PE": "秘鲁",
	"countriesL10nMessages.PF": "法属波利尼西亚",
	"countriesL10nMessages.PG": "巴布亚新几内亚",
	"countriesL10nMessages.PH": "菲律宾",
	"countriesL10nMessages.PK": "巴基斯坦",
	"countriesL10nMessages.PL": "波兰",
	"countriesL10nMessages.PM": "圣皮埃尔和密克隆群岛",
	"countriesL10nMessages.PR": "波多黎各",
	"countriesL10nMessages.PS": "巴勒斯坦",
	"countriesL10nMessages.PT": "葡萄牙",
	"countriesL10nMessages.PW": "帕劳",
	"countriesL10nMessages.PY": "巴拉圭",
	"countriesL10nMessages.QA": "卡塔尔",
	"countriesL10nMessages.RE": "留尼汪",
	"countriesL10nMessages.RO": "罗马尼亚",
	"countriesL10nMessages.RS": "塞尔维亚",
	"countriesL10nMessages.RW": "卢旺达",
	"countriesL10nMessages.SA": "沙特阿拉伯",
	"countriesL10nMessages.SB": "所罗门群岛",
	"countriesL10nMessages.SC": "塞舌尔",
	"countriesL10nMessages.SE": "瑞典",
	"countriesL10nMessages.SG": "新加坡",
	"countriesL10nMessages.SH": "圣赫勒拿岛",
	"countriesL10nMessages.SI": "斯洛文尼亚",
	"countriesL10nMessages.SJ": "斯瓦尔巴群岛和扬马延岛",
	"countriesL10nMessages.SK": "斯洛伐克",
	"countriesL10nMessages.SL": "塞拉利昂",
	"countriesL10nMessages.SM": "圣马力诺",
	"countriesL10nMessages.SN": "塞内加尔",
	"countriesL10nMessages.SO": "索马里",
	"countriesL10nMessages.SR": "苏里南",
	"countriesL10nMessages.SS": "南苏丹",
	"countriesL10nMessages.ST": "圣多美和普林西比",
	"countriesL10nMessages.SV": "萨尔瓦多",
	"countriesL10nMessages.SX": "圣马丁岛",
	"countriesL10nMessages.SZ": "斯威士兰",
	"countriesL10nMessages.TA": "特里斯坦-达库尼亚群岛",
	"countriesL10nMessages.TC": "特克斯和凯科斯群岛",
	"countriesL10nMessages.TD": "乍得",
	"countriesL10nMessages.TG": "多哥",
	"countriesL10nMessages.TH": "泰国",
	"countriesL10nMessages.TJ": "塔吉克斯坦",
	"countriesL10nMessages.TK": "托克劳",
	"countriesL10nMessages.TL": "东帝汶",
	"countriesL10nMessages.TM": "土库曼斯坦",
	"countriesL10nMessages.TN": "突尼斯",
	"countriesL10nMessages.TO": "汤加",
	"countriesL10nMessages.TR": "土耳其",
	"countriesL10nMessages.TT": "特立尼达和多巴哥",
	"countriesL10nMessages.TV": "图瓦卢",
	"countriesL10nMessages.TW": "台湾",
	"countriesL10nMessages.TZ": "坦桑尼亚",
	"countriesL10nMessages.UA": "乌克兰",
	"countriesL10nMessages.UG": "乌干达",
	"countriesL10nMessages.US": "美国",
	"countriesL10nMessages.UY": "乌拉圭",
	"countriesL10nMessages.UZ": "乌兹别克斯坦",
	"countriesL10nMessages.VA": "罗马教廷（梵蒂冈城国）",
	"countriesL10nMessages.VC": "圣文森特和格林纳丁斯",
	"countriesL10nMessages.VE": "委内瑞拉",
	"countriesL10nMessages.VG": "英属维尔京群岛",
	"countriesL10nMessages.VI": "美属维尔京群岛",
	"countriesL10nMessages.VN": "越南",
	"countriesL10nMessages.VU": "瓦努阿图",
	"countriesL10nMessages.WF": "瓦利斯和富图纳群岛",
	"countriesL10nMessages.WS": "萨摩亚",
	"countriesL10nMessages.XK": "科索沃",
	"countriesL10nMessages.YE": "也门",
	"countriesL10nMessages.YT": "马约特岛",
	"countriesL10nMessages.ZA": "南非",
	"countriesL10nMessages.ZM": "赞比亚",
	"countriesL10nMessages.ZW": "津巴布韦",
	"ToggleableEmailField.checkboxLabel": "通过电子邮件发给我一份我的回复",
	"ToggleableEmailField.emailLabel": "输入您的电子邮件地址"
};

var zhTw = {
	"ActionMenuTagPin.PIN": "釘選",
	"ActionMenuTagPin.TAG": "標籤",
	"AddressField.selectCountry": "選擇國家",
	"AnalyzeQuestionLayoutTemplate.ANSWERED_QUESTIONS": "已回答：{answered}",
	"AnalyzeQuestionLayoutTemplate.QUESTION_NUMBER": "問題{number}",
	"AnalyzeQuestionLayoutTemplate.SKIPPED_QUESTIONS": "已略過：{skipped}",
	"AnalyzeSentimentChartCard.SENTIMENT_CHART_TITLE": "人們的感受：",
	"AsyncJobLoadingState.LOADING": "正在載入回覆",
	"AsyncJobLoadingState.TAGGING_CAPTION": "完成後，我們將會通知您",
	"AsyncJobLoadingState.TAGGING_TITLE": "正在標記回覆",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_DESC": "標記會套用至現有及未來的回覆。若要使用多個關鍵字，請在不同字詞之間留下一個空格或輸入 <strong>OR</strong>。",
	"AutomaticTagResponsesPopout.AUTOMATIC_TAG_RESPONSES_TIP_LABEL": "自動標記回覆",
	"Button.OK_LABEL": "確定",
	"CalendarPicker.month": "選擇月份",
	"CalendarPicker.months": "{months, select, January {一月} February {二月} March {三月} April {四月} May {五月} June {六月} July {七月} August {八月} September {九月} October {十月} November {十一月} December {十二月} other {其他}}",
	"CalendarPicker.nextMonth": "下個月",
	"CalendarPicker.previousMonth": "上個月",
	"CalendarPicker.year": "選擇年份",
	"ColorSwatches.TAG": "標記 {color}",
	"ColorTab.COLOR_SELECTOR_DESCRIPTION": "多色文字雲可同時使用顏色和大小，來表示字詞或片語被提及的次數。",
	"ColorTab.COLOR_SELECTOR_TITLE": "文字雲如何運用色彩",
	"ColorTab.DOWN_ARROW": "選擇下拉式箭頭",
	"ColorTab.MUTLI_COLOR_SELECTOR_LABEL": "多色",
	"ColorTab.ONE_COLOR_SELECTOR_LABEL": "單一色彩",
	"CombineWordsModal.CANCEL_BUTTON_LABEL": "取消",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL": "1. 建立新字詞",
	"CombineWordsModal.COMBINED_WORDS_INPUT_LABEL_HELPER_TEXT": "新字詞會取代您在下方所選取的各個字詞。",
	"CombineWordsModal.COMBINE_WORDS_EDIT_HEADER": "編輯合併的字詞",
	"CombineWordsModal.COMBINE_WORDS_HEADER": "合併字詞",
	"CombineWordsModal.COMBINE_WORDS_HELP_TEXT": "建立一個新字詞來取代您所選取的一個或多個字詞。您之後可以再進行編輯，以回復原本的字詞。",
	"CombineWordsModal.COMBINE_WORDS_INPUT_LABEL_PLACEHOLDER": "輸入任何新字詞",
	"CombineWordsModal.SAVE_BUTTON_LABEL": "合併",
	"CombineWordsModal.WORD_LIST_HEADER": "2. 選擇要合併的字詞",
	"CreateTagFromSearch.CLOSE_MENU": "取消",
	"CreateTagFromSearch.DUPLICATED_NAME": "您已經有另一個標籤使用此名稱。",
	"CreateTagFromSearch.NEW_TAG": "新增標籤",
	"CreateTagFromSearch.RESPONSE_TO_BE_TAGGED": "{hasNoResponseCount, select, true { 不會標記\n\t\t任何回覆} other {{responseCount, plural, other {\n\t\t\t\t將會標記\n\t\t\t\n\t\t{responseTaggedFormatted} 則回覆}}}}",
	"CreateTagFromSearch.SAVE": "儲存",
	"CreateTagFromSearch.TAG": "標籤",
	"CreateTagFromSearch.TAG_NAME_NOT_EMPTY": "標籤名稱不能為空白。",
	"CreateTagFromSearch.TAG_NAME_SPACES": "標記名稱不能只有空格。",
	"CreateTagPopout.CREATE_TAG_TIP_DESC": "標記符合這些關鍵字的回覆。選擇<strong>建立標記</strong>。選擇色彩並儲存。標記會套用至現有及未來的回覆。",
	"CreateTagPopout.CREATE_TAG_TIP_LABEL": "標記相符的回覆",
	"DashboardMenuContent.CREATE": "建立",
	"DashboardMenuContent.NAME": "名稱",
	"DashboardMenuContent.NEW_DASHBOARD": "新增控制面板",
	"DateInputWrapper.dateButton": "開啟日曆",
	"DateInputWrapper.dateFormatIntl": "日/月/年",
	"DateInputWrapper.dateFormatUs": "月/日/年",
	"DateInputWrapper.dateLabel": "日期",
	"DateTime.errorEnterHours": "請輸入 0 至 23 之間的數字來表示「時」。",
	"DateTime.errorEnterMinutes": "請輸入 0 至 59 之間的數字來表示「分」。",
	"DateTime.errorSelectPeriod": "請選擇上午/下午。",
	"DateTime.errorYearOutOfRange": "請提供 1990 至 9999 之間的年份。",
	"DateTime.formatErrorDateIntl": "請以「日/月/年 (DD/MM/YYYY)」的格式輸入日期。",
	"DateTime.formatErrorDateUs": "請以「月/日/年 (MM/DD/YYYY)」的格式輸入日期。",
	"Day.dayOfMonth": "{dayInCalendar,date,full}",
	"EditorButtons.CANCEL_BUTTON": "取消",
	"EditorButtons.COPY_BUTTON": "複製問題",
	"EditorButtons.MOVE_BUTTON": "移動問題",
	"EditorButtons.NEXT_QUESTION_BUTTON": "下一個問題",
	"EditorButtons.SAVE_BUTTON": "儲存",
	"EmptyInsightsDataTrends.VIEW_DISABLED_TEXT": "資料趨勢不適用於此問題",
	"FallbackComponent.ERROR": "載入問題時發生錯誤",
	"FileUpload.chooseDragFile": "<div class={chooseClass}>請選擇檔案</div><div class={dragClass}>或將其拖曳到此處</div>",
	"FileUpload.fileUploaded": "上傳檔案成功",
	"FileUpload.fileUploading": "檔案上傳",
	"FileUpload.removeFile": "移除檔案",
	"FileUpload.replaceDragFile": "<div class={chooseClass}>請取代檔案</div><div class={dragClass}>或將其拖曳到此處</div>",
	"FileUpload.supportedFiles": "支援的檔案",
	"FilterMenuContent.APPLY_ACTIONS": "套用",
	"FilterMenuContent.CLEAR_ACTIONS": "清除",
	"FilterMenuContent.CLOSE_MENU": "取消",
	"HideWordsModal.CANCEL_BUTTON_LABEL": "取消",
	"HideWordsModal.HIDE_BUTTON_LABEL": "隱藏",
	"HideWordsModal.HIDE_BUTTON_LABEL_ON_EDIT": "儲存",
	"HideWordsModal.HIDE_WORDS_HEADER": "隱藏字詞",
	"HideWordsModal.HIDE_WORDS_HELP_TEXT": "選擇要從文字雲當中隱藏起來的字詞。",
	"HideWordsModal.HIDE_WORDS_LIST_LABEL": "請選擇要隱藏的字詞",
	"IconHelpStyled.HELP": "說明",
	"IndividualResponseCard.NO_RESPONSE": "受訪者已略過此問題",
	"IndividualResponseCard.QUESTION_NUMBER": "問題{number}",
	"InfoBannerTagManagement.CLOSE": "關閉",
	"InfoBannerTagManagement.NEW": "新增",
	"InfoBannerTagManagement.TAG_RESPONSES": "標記回覆",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_BODY": "新增關鍵字以設定自動標記，使回覆保持井然有序。",
	"InfoBannerTagManagement.TAG_RESPONSE_MANAGEMENT_TITLE": "標記回覆，方便管理",
	"LegendItem.SENTIMENT_WITH_PERCENT": "{sentimentName, select, positive {正面：{percentage,number,percent}} negative {負面：{percentage,number,percent}} neutral {中性： {percentage,number,percent}} unknown {未測出：{percentage,number,percent}} other {其他}}",
	"LoadingState.LOADING": "正在載入回覆",
	"MatrixDropdown.EmptyTopNativeSelect": "選擇一個選項",
	"MaxDiffChart.CONFIDENCE_INTERVAL_TOOLTIP": "信賴區間",
	"MaxDiffChart.UTILITY_SCORE_TOOLTIP": "效用分數",
	"Modal.CloseModal": "關閉工具提示",
	"Month.dayOfWeekLegendLong": "{dayOfTheWeek, select, Mo {星期一} Tu {星期二} We {星期三} Th {星期四} Fr {星期五} Sa {星期六} Su {星期日} other {其他}}",
	"Month.dayOfWeekLegendShort": "{dayOfTheWeek, select, Mo {週一} Tu {週二} We {週三} Th {週四} Fr {週五} Sa {週六} Su {週日} other {其他}}",
	"MultiColorSelectorMenu.APPLY": "套用",
	"MultiColorSelectorMenu.CANCEL": "取消",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_BODY": "自訂資料呈現方式，說明您的調查發現，並與他人分享您的控制面板。 自訂資料呈現方式，說明您的調查發現，並與他人分享您的控制面板。",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_HEADER": "控制面板",
	"NewDashboardPopOut.NEW_DASHBOARD_HELP_POP_OUT_LEARN_MORE": "瞭解更多",
	"Nps.QUESTION_TITLE": "<div class=\"{srOnlyClass}\">範圍從 {minValue} 到 {maxValue}，</div>{heading}<div id=\"{instructionsId}\" class=\"{srOnlyClass}\"> {minValue} 表示{minLabelText}，{maxValue} 表示{maxLabelText}</div>",
	"NpsGaugeChart.NPS_ARC": "分數介於 {min} 至 {max} 之間的量測區",
	"NpsGaugeChart.NPS_CHART": "NPS 量測計圖",
	"NpsGaugeChart.NPS_SCORE": "NPS 分數為 {score}",
	"NpsGaugeChart.NPS_SCORE_TICK": "圖表刻度 {score} 分標記位置",
	"OrderControl.AriaNA": "不適用",
	"OrderControl.NA": "不適用",
	"OtherResponseList.OTHER_RESPONSES_HEADER": "「{title}」的回覆 ({totalCount})",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_DOWN": "隱藏其他回覆清單",
	"OtherResponseList.OTHER_RESPONSES_HEADER_CARET_UP": "顯示其他回覆清單",
	"PhoneField.countryCode": "國家代碼",
	"QuestionCardItemFooter.FILTER_APPLIED": "套用的規則：{filterCount,number}",
	"QuestionCardItemFooter.FILTER_LABEL": "篩選條件",
	"QuestionCardTitle.ANSWERED": "已回答：{answeredCount}",
	"QuestionCardTitle.SELECTED": "已選取：{selectedCount}",
	"QuestionCardTitle.SKIPPED": "已略過：{skippedCount}",
	"QuizResultScore.SCORE": "{total, plural, other {{score}/{total} 分}}",
	"QuizResultScore.SKIPPED": "已跳過",
	"RangeTab.RANGE_TAB_LABEL": "提及次數：{value}",
	"RangeTab.TOTAL_WORDS": "個總字數",
	"Ranking.AriaRankAtBottom": "已將 {label} 移至清單底部",
	"Ranking.AriaRankAtTop": "已將 {label} 移至清單頂端",
	"Ranking.AriaRankMovedDown": "已將 {label} 往下移至第 {position} 排",
	"Ranking.AriaRankMovedUp": "已將 {label} 往上移至第 {position} 排",
	"RankingItem.AriaRankMoveDown": "將 {label} 向下移動",
	"RankingItem.AriaRankMoveUp": "將 {label} 向上移動",
	"RatingChart.AVERAGE_RATING": "平均分數",
	"RatingChartLegend.ANSWERED": "已回答：{number}",
	"RatingChartLegend.RATING": "{rating} 顆星星",
	"RatingChartLegend.SKIPPED": "已略過：{skipped}",
	"RatingChartLegend.TOOLTIP": "{label} ({percent,number,percent})",
	"RequestAccessButton.REQUEST": "申請此功能",
	"RequestAccessButton.UPGRADE": "升級",
	"RespondentQuestionTitle.REQUIRED": "(必答。)",
	"ResponseItem.EXPAND_MENU_LABEL": "展開功能表",
	"ResponseItem.MENU_LABEL": "新增標籤",
	"ResponseItem.RESPONDENT_ID": "受訪者 ID",
	"ResponseItem.VIEW_RESPONDENTS_ANSWERS": "檢視受訪者的回答",
	"ResponseStatusBar.CLEAR": "清除",
	"ResponseStatusBar.FAILED_TO_LOAD": "無法載入回覆",
	"ResponseStatusBar.NO_RESPONSES_YET": "尚無回覆",
	"ResponseStatusBar.SEARCHING": "正在搜尋……",
	"ResponseStatusBar.SENTIMENT_LABEL": "{sentiment, select, positive {正面情感} negative {負面情感} neutral {中性情感} other {未知情感}}",
	"ResponseStatusBar.SHOWING_RESPONSES": "{hasFilters, select, true {{hasSearchText, select, true {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {顯示 {resultsCountFormatted} 則已選取的 {searchTextFormatted} 回覆，篩選條件 {filters}   } other {顯示 {resultsCountFormatted} 則 {searchTextFormatted} 回覆，篩選條件 {filters}   }}}}} other {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {顯示 {resultsCountFormatted} 則已選取的回覆，篩選條件 {filters}   } other {顯示 {resultsCountFormatted} 則回覆，篩選條件 {filters}   }}}}}}} other {{hasSearchText, select, true {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {顯示 {resultsCountFormatted} 則已選取的 {searchTextFormatted} 回覆   } other {顯示 {resultsCountFormatted} 則 {searchTextFormatted} 的回覆   }}}}} other {{responseResultsCount, plural, other {{hasSelectedResponse, select, true {顯示 {resultsCountFormatted} 則已選取的回覆   } other {顯示 {resultsCountFormatted} 則回覆   }}}}}}}}",
	"ResponseToolbar.CLEAR": "清除",
	"ResponseToolbar.CREATE_TAG": "建立標籤",
	"ResponseToolbar.SELECT_ALL": "選取所有回覆",
	"ResponseToolbar.UNSELECT_ALL": "取消選取所有回覆",
	"SearchResponsesInput.CLEAR": "清除搜尋",
	"SearchResponsesInput.SEARCH": "搜尋",
	"SearchResponsesInput.SEARCH_RESPONSES": "搜尋回覆",
	"SearchTipsPopout.SEARCH_TIP1_DESC": "使用雙引號 <strong>“ ”</strong>，來搜尋完全吻合的詞組。",
	"SearchTipsPopout.SEARCH_TIP2_DESC": "輸入 <strong>OR</strong>，以搜尋其中一個詞彙。",
	"SearchTipsPopout.SEARCH_TIP3_DESC": "輸入 <b>AND</b> 以尋找多個詞彙。",
	"SearchTipsPopout.SEARCH_TIP4_DESC": "使用 <strong>NOT</strong> 從搜尋中排除特定詞彙。",
	"SearchTipsPopout.SEARCH_TIP5_DESC": "在詞彙後輸入 <strong>*</strong> 以搜尋字首相符的結果。",
	"SearchTipsPopout.SEARCH_TIP6_DESC": "使用括號來結合以 AND/OR/NOT 為運算子的搜尋。",
	"SearchTipsPopout.SEARCH_TIP7_DESC": "在詞彙後加上 <strong>~2</strong>，以取得與該詞彙相符且另外包含最多兩個字元的結果。",
	"SearchTipsPopout.SEARCH_TIPS_HEADER": "搜尋選項",
	"SearchTipsPopout.SEARCH_TIPS_POPOUT_LEARN_MORE": "瞭解更多",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CLOSE_BUTTON_TEXT": "不用了，謝謝",
	"SentimentAnalysisSwitchModal.SE_SWITCH_CONFIRM_BUTTON_TEXT": "啟用",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_DESCRIPTION": "您的帳戶現在可以使用<span id=\"link\">自動深入解析</span>了。\n      此設定包含所有使用機器學習來處理調查問卷的功能，能為您呈現深入解析。",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_SECTION_INFO": "您隨時可以在<span id=\"link\">我的帳戶</span>中變更您的喜好設定",
	"SentimentAnalysisSwitchModal.SE_SWITCH_MODAL_TITLE": "自動深入分析",
	"SentimentBanner.GENERATE_SENTIMENT": "分析情感",
	"SentimentBanner.SENTIMENT_ANALYSIS": "情感分析",
	"SentimentBanner.SENTIMENT_ANALYSIS_DESCRIPTION": "偵測書面回覆所隱含的情緒感受，以瞭解受訪者的心情。",
	"SentimentBanner.UPGRADE": "升級",
	"SentimentBanner.WATCH_A_DEMO": "觀看示範影片",
	"SentimentChartCard.SENTIMENT_CHART_TITLE": "人們的感受：",
	"SentimentFilter.SENTIMENT_LABEL": "{sentiment, select, positive {依正面情感篩選} negative {依負面情感篩選} neutral {依中性情感篩選} other {依未知情感篩選}}",
	"SentimentFilterMenu.EXPAND_MENU_LABEL": "展開功能表",
	"SentimentFilterMenu.MENU_LABEL": "依情感",
	"SentimentOnboarding.SA_ONBOARDING_REQUEST": "申請此功能",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_DESCRIPTION": "從受訪者寫下的回覆偵測出其背後隱藏的感情和情緒。",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_NEW": "新功能！",
	"SentimentOnboarding.SA_ONBOARDING_TEXT_TITLE": "全新推出情感分析",
	"SentimentOnboarding.SA_ONBOARDING_UPGRADE": "升級",
	"SentimentOnboarding.SA_WATCH_A_DEMO": "觀看示範影片",
	"SentimentUpgradeModal.SENTIMENT_CLOSE_BUTTON_TEXT": "不用了，謝謝",
	"SentimentUpgradeModal.SENTIMENT_REQUEST_BUTTON_TEXT": "申請此功能",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_BUTTON_TEXT": "升級",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_ALT_TEXT": "情感分析升級影像",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_DESCRIPTION": "滑鼠一按，即可開啟情感分析。我們會將文字回覆分類為正面、中性或負面，顯露出人們的發言背後的情緒。",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_SECTION_TITLE": "立即得知人們的感受",
	"SentimentUpgradeModal.SENTIMENT_UPGRADE_MODAL_TITLE": "情感分析",
	"SentimentsToggle.SENTIMENTS_LABEL": "{sentimentsEnabled, select, true {情感功能：開啟} false {情感功能：關閉} other {情感功能：其他}}",
	"SentimentsToggle.SENTIMENTS_LOADING": "情感功能：正在載入...",
	"SlideBoxNavigation.NEXT_BUTTON": "繼續",
	"SlideBoxNavigation.NEXT_SLIDE": "下一張",
	"SlideBoxNavigation.PREVIOUS_BUTTON": "返回",
	"SlideBoxNavigation.PREVIOUS_SLIDE": "上一張",
	"SlideBoxNavigation.SLIDE_INDICATOR": "{position,number} / {total,number}",
	"Slider.CLEAR_BUTTON_LABEL": "清除",
	"Slider.STEP_MESSAGE": "我們會依照數字滑桿的比例尺調整您輸入的數字。",
	"SolutionsCardLayoutTemplate.ANSWERED_QUESTIONS": "已回答：{answered}",
	"SolutionsCardLayoutTemplate.QUESTION_NUMBER": "問 {questionNumber}{questionHeader}",
	"SolutionsCardLayoutTemplate.QUESTION_TITLE": "{title}",
	"SolutionsCardLayoutTemplate.SKIPPED_QUESTIONS": "已略過：{skipped}",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_CAPTION": "便宜和昂貴之間的交叉點，可為您的產品提供整體定價方向。",
	"SolutionsPriceSensitivityItem.SOLUTIONS_PRICE_SENSITIVITY_ITEM_HEADER": "價格敏感度 (Van Westendorp 模型)",
	"SrText.CORRECT": "答對",
	"SrText.INCORRECT": "答錯",
	"StarRating.Heart": "{count, plural, other {{count} 個愛心}}",
	"StarRating.STAR": "{count, plural, other {{count} 顆星星}}",
	"StarRating.Smiley": "{count, plural, other {{count} 個笑臉}}",
	"StarRating.Thumb": "{count, plural, other {{count} 個大姆指}}",
	"TabList.BETA_BADGE": "測試版",
	"TabList.LOCK_ICON": "標籤分頁鎖定圖示",
	"TabList.RESPONSES": "回覆 ({responseCount})",
	"TabList.TAGS": "標記 ({tagsCount})",
	"TabList.THEMATIC_ANALYSIS": "主題分析",
	"TabList.WORD_CLOUD": "字詞雲端庫",
	"TagFilterMenu.EXPAND_MENU_LABEL": "展開功能表",
	"TagFilterMenu.MENU_LABEL": "篩選條件：依標籤",
	"TagList.TAGS": "標記",
	"TagList.TAGS_KEYWORDS": "關鍵字",
	"TagList.TAG_CONTROLS": "控制標籤",
	"TagList.TAG_NUMBER_PERCENT": "標籤的百分比和數量",
	"TagList.TAG_PERCENTAGE_BAR": "標籤百分比列",
	"TagListItem.DELETE": "刪除",
	"TagListItem.EDIT": "編輯",
	"TagListItem.VIEW_ALL": "檢視全部",
	"TagListPanel.LOADING": "正在載入標籤",
	"TagListPanel.NEW_TAG": "+ 新增標籤",
	"TagListPanel.TAGS": "標記",
	"TagMenuContent.APPLY_ACTIONS": "套用",
	"TagMenuContent.CLEAR_ACTIONS": "清除",
	"TagMenuContent.CLOSE_MENU": "取消",
	"TagMenuContent.DUPLICATED_NAME": "您已經有另一個標籤使用此名稱。",
	"TagMenuContent.NEW_TAG": "新增標籤",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_LEARN_MORE_LINK": "瞭解更多 >>",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_REQUEST_BUTTON": "申請此功能",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_BUTTON": "升級",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_CLOSE_BUTTON_TITLE": "關閉",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_DESCRIPTION": "搜尋、標記和篩選書面回覆，並查看文字雲上的關鍵字詞片語。",
	"TextAnalysisUpgradeDialog.TEXT_ANALYSIS_UPGRADE_TEXT": "文字分析",
	"TimeInputWrapper.am": "截圖",
	"TimeInputWrapper.daytimeLabel": "上午/下午",
	"TimeInputWrapper.hhPlaceholder": "小時",
	"TimeInputWrapper.hour": "小時",
	"TimeInputWrapper.minutes": "分鐘",
	"TimeInputWrapper.mmPlaceholder": "分",
	"TimeInputWrapper.pm": "下午",
	"TimeInputWrapper.timeLabel": "時間",
	"ToggleableEmailField.checkboxLabel": "透過電子郵件傳送一份回覆的複本給我",
	"ToggleableEmailField.emailLabel": "輸入您的電子郵件地址",
	"UpdateTagModal.DELETE_TAG_CONFIRM_BUTTON_TEXT": "刪除",
	"UpdateTagModal.DELETE_TAG_MODAL_RESPONSES_AFFECTED": "{responseCount, plural, other {將取消 {responseCountFormatted} 則回覆的標記。}}",
	"UpdateTagModal.DELETE_TAG_MODAL_SECTION_DESCRIPTION": "刪除後，此標籤將從所有被標記的回覆上移除。此動作無法復原。",
	"UpdateTagModal.DELETE_TAG_MODAL_TITLE": "要刪除標籤嗎？",
	"UpdateTagModal.EDIT_TAG_CONFIRM_BUTTON_TEXT": "儲存",
	"UpdateTagModal.EDIT_TAG_MODAL_TITLE": "儲存變更？",
	"UpdateTagModal.EDIT_TAG_SECTION_DESCRIPTION": "此動作將從所有回覆中移除該標籤。該標籤會被重新套用於符合編輯後之關鍵字\n      的回覆。",
	"UpdateTagModal.UPDATE_TAG_CLOSE_BUTTON_TEXT": "取消",
	"UpgradeBanner.SUBTITLE": "升級後即可自訂您的文字雲。",
	"UpgradeBanner.TITLE": "喜歡現在的樣子嗎？",
	"VanWestendorpChartDataTable.PERCENTAGE_OF_RESPONSES": "% 的回覆",
	"VanWestendorpChartDataTable.PRICE": "價格",
	"VanWestendorpChartDataTable.RESPONSES": "回覆",
	"WordCloudCustomizationButton.CUSTOMIZE": "自訂",
	"WordCloudCustomizationFooter.APPLY": "套用",
	"WordCloudCustomizationFooter.CANCEL": "取消",
	"WordCloudCustomizationFooter.CHECKBOX_LABEL": "套用到調查問卷中的所有文字雲 (部分資料可能維持不變)",
	"WordCloudCustomizationFooter.NO_THANKS": "不用了，謝謝",
	"WordCloudCustomizationFooter.REQUEST": "申請此功能",
	"WordCloudCustomizationFooter.SAVE": "儲存",
	"WordCloudCustomizationFooter.UPGRADE": "升級",
	"WordCloudCustomizationTabs.COLOR": "色彩",
	"WordCloudCustomizationTabs.FONT": "字型",
	"WordCloudCustomizationTabs.RANGE": "範圍",
	"WordCloudLegend.FEWER": "較少",
	"WordCloudLegend.MORE": "較多",
	"WordCloudLegend.TITLE": "提及次數",
	"WordCloudListFooter.COMBINED_WORDS": "合併的字詞",
	"WordCloudListFooter.HIDDEN_WORDS": "隱藏的字詞",
	"WordCloudListRowItem.EXPAND_MENU_LABEL": "展開功能表",
	"WordCloudListViewMenuContent.COMBINE_WITH": "合併",
	"WordCloudListViewMenuContent.EDIT_COMBINED_WORDS": "編輯合併的字詞",
	"WordCloudListViewMenuContent.EXPAND_MENU_LABEL": "展開功能表",
	"WordCloudListViewMenuContent.HIDE_FROM_WORD_CLOUD": "從文字雲中隱藏",
	"WordCloudListViewMenuContent.SPLIT_COMBINED_WORDS": "拆開合併的字詞",
	"WordCloudLoadingState.LOADING": "正在載入文字雲...",
	"WordCloudPreviewOverlay.CLOSE_ICON": "關閉按鈕",
	"WordCloudPreviewOverlay.OK_BUTTON": "確定",
	"WordCloudPreviewOverlay.SUBTITLE": "預覽自訂文字雲的功能。",
	"WordCloudPreviewOverlay.TITLE": "馬上試試看！",
	"WordCloudShowCustomization.CLOUD_VIEW_BTN": "文字雲檢視",
	"WordCloudShowCustomization.LIST_VIEW_BTN": "清單檢視",
	"WordCloudTooltip.TOOLTIP_OCCURRENCES": "出現次數：{weight}",
	"WordCloudTooltip.TOOLTIP_TITLE": "{number} 個最重要的字詞",
	"WordCount.IN_PROGRESS": "正在進行文字分析……",
	"WordCount.NO_IMPORTANT_WORDS_FOUND": "未找到重要的字詞。",
	"WordCount.SHOW_COUNT": "{countCheck, plural, other {顯示 {count} 個字詞 }}",
	"copySentiment.FILTER_BY_SENTIMENT": "{sentiment, select, positive {依正面情感篩選} negative {依負面情感篩選} neutral {依中性情感篩選} other {依未測出情感篩選}}",
	"copySentiment.PERCENT": "{percentage,number,percent}",
	"copySentiment.SENTIMENT": "{sentiment, select, positive {正面} negative {負面} neutral {中立} other {不明}}",
	"copySentiment.SENTIMENT_WITH_PERCENT": "{sentiment, select, positive {正面：{percentage,number,percent}} negative {負面：{percentage,number,percent}} neutral {中性： {percentage,number,percent}} other {未知：{percentage,number,percent}}}",
	"copyTagEditor.CANCEL": "取消",
	"copyTagEditor.DUPLICATED_NAME": "您已經有另一個標籤使用此名稱。",
	"copyTagEditor.ENTER_NAME_FOR_THIS_TAG": "輸入此標記的名稱",
	"copyTagEditor.ENTER_ONE_OR_MORE_KEYWORDS": "輸入一個或多個關鍵字",
	"copyTagEditor.KEYWORDS": "<strong>關鍵字</strong> (選填)",
	"copyTagEditor.SAVE": "儲存",
	"copyTagEditor.TAG_APPLIES_RESPONSES_MATCHING_KEYWORDS": "標記會套用至所有包含關鍵字的回覆",
	"copyTagEditor.TAG_NAME": "<strong>標記名稱</strong>",
	"copyTagEditor.TAG_NAME_NOT_EMPTY": "標籤名稱不能為空白。",
	"copyTagEditor.TAG_NAME_SPACES": "標記名稱不能只有空格。",
	"copyTagEditor.TAG_TOO_LONG": "標籤不能超過 128 個字元。",
	"countriesL10nMessages.AC": "阿森松島",
	"countriesL10nMessages.AD": "安道爾",
	"countriesL10nMessages.AE": "阿拉伯聯合大公國",
	"countriesL10nMessages.AF": "阿富汗",
	"countriesL10nMessages.AG": "安地卡及巴布達",
	"countriesL10nMessages.AI": "安圭拉",
	"countriesL10nMessages.AL": "阿爾巴尼亞",
	"countriesL10nMessages.AM": "亞美尼亞",
	"countriesL10nMessages.AO": "安哥拉",
	"countriesL10nMessages.AR": "阿根廷",
	"countriesL10nMessages.AS": "美屬薩摩亞",
	"countriesL10nMessages.AT": "奧地利",
	"countriesL10nMessages.AU": "澳大利亞",
	"countriesL10nMessages.AW": "阿魯巴",
	"countriesL10nMessages.AX": "奧蘭群島",
	"countriesL10nMessages.AZ": "亞塞拜然",
	"countriesL10nMessages.BA": "波士尼亞及赫塞哥維納",
	"countriesL10nMessages.BB": "巴貝多",
	"countriesL10nMessages.BD": "孟加拉",
	"countriesL10nMessages.BE": "比利時",
	"countriesL10nMessages.BF": "布吉納法索",
	"countriesL10nMessages.BG": "保加利亞",
	"countriesL10nMessages.BH": "巴林",
	"countriesL10nMessages.BI": "蒲隆地",
	"countriesL10nMessages.BJ": "貝南",
	"countriesL10nMessages.BL": "聖巴瑟米",
	"countriesL10nMessages.BM": "百慕達",
	"countriesL10nMessages.BN": "汶萊",
	"countriesL10nMessages.BO": "玻利維亞",
	"countriesL10nMessages.BQ": "波內赫、聖尤斯特歇斯和薩巴",
	"countriesL10nMessages.BR": "巴西",
	"countriesL10nMessages.BS": "巴哈馬",
	"countriesL10nMessages.BT": "不丹",
	"countriesL10nMessages.BW": "波札那",
	"countriesL10nMessages.BZ": "貝里斯",
	"countriesL10nMessages.CA": "加拿大",
	"countriesL10nMessages.CC": "可可斯 (基林) 群島",
	"countriesL10nMessages.CD": "剛果民主共和國",
	"countriesL10nMessages.CF": "中非共和國",
	"countriesL10nMessages.CG": "剛果",
	"countriesL10nMessages.CH": "瑞士",
	"countriesL10nMessages.CI": "象牙海岸",
	"countriesL10nMessages.CK": "庫克群島",
	"countriesL10nMessages.CL": "智利",
	"countriesL10nMessages.CM": "喀麥隆",
	"countriesL10nMessages.CN": "中國",
	"countriesL10nMessages.CO": "哥倫比亞",
	"countriesL10nMessages.CR": "哥斯大黎加",
	"countriesL10nMessages.CV": "維德角",
	"countriesL10nMessages.CW": "庫拉索",
	"countriesL10nMessages.CX": "聖誕島",
	"countriesL10nMessages.CY": "賽普勒斯",
	"countriesL10nMessages.CZ": "捷克共和國",
	"countriesL10nMessages.DE": "德國",
	"countriesL10nMessages.DJ": "吉布地",
	"countriesL10nMessages.DK": "丹麥",
	"countriesL10nMessages.DM": "多明尼加",
	"countriesL10nMessages.DO": "多明尼加共和國",
	"countriesL10nMessages.DZ": "阿爾及利亞",
	"countriesL10nMessages.EC": "厄瓜多",
	"countriesL10nMessages.EE": "愛沙尼亞",
	"countriesL10nMessages.EG": "埃及",
	"countriesL10nMessages.EH": "西撒哈拉",
	"countriesL10nMessages.ER": "厄利垂亞",
	"countriesL10nMessages.ES": "西班牙",
	"countriesL10nMessages.ET": "衣索比亞",
	"countriesL10nMessages.FI": "芬蘭",
	"countriesL10nMessages.FJ": "斐濟",
	"countriesL10nMessages.FK": "福克蘭群島",
	"countriesL10nMessages.FM": "密克羅尼西亞聯邦",
	"countriesL10nMessages.FO": "法羅群島",
	"countriesL10nMessages.FR": "法國",
	"countriesL10nMessages.GA": "加彭",
	"countriesL10nMessages.GB": "英國",
	"countriesL10nMessages.GD": "格瑞那達",
	"countriesL10nMessages.GE": "喬治亞",
	"countriesL10nMessages.GF": "法屬圭亞那",
	"countriesL10nMessages.GG": "根西",
	"countriesL10nMessages.GH": "迦納",
	"countriesL10nMessages.GI": "直布羅陀",
	"countriesL10nMessages.GL": "格陵蘭",
	"countriesL10nMessages.GM": "甘比亞",
	"countriesL10nMessages.GN": "幾內亞",
	"countriesL10nMessages.GP": "瓜德羅普",
	"countriesL10nMessages.GQ": "赤道幾內亞",
	"countriesL10nMessages.GR": "希臘",
	"countriesL10nMessages.GT": "瓜地馬拉",
	"countriesL10nMessages.GU": "關島",
	"countriesL10nMessages.GW": "幾內亞比索",
	"countriesL10nMessages.GY": "蓋亞那",
	"countriesL10nMessages.HK": "香港",
	"countriesL10nMessages.HN": "宏都拉斯",
	"countriesL10nMessages.HR": "克羅埃西亞",
	"countriesL10nMessages.HT": "海地",
	"countriesL10nMessages.HU": "匈牙利",
	"countriesL10nMessages.ID": "印尼",
	"countriesL10nMessages.IE": "愛爾蘭",
	"countriesL10nMessages.IL": "以色列",
	"countriesL10nMessages.IM": "曼島",
	"countriesL10nMessages.IN": "印度",
	"countriesL10nMessages.IO": "英屬印度洋領地",
	"countriesL10nMessages.IQ": "伊拉克",
	"countriesL10nMessages.IS": "冰島",
	"countriesL10nMessages.IT": "義大利",
	"countriesL10nMessages.JE": "澤西",
	"countriesL10nMessages.JM": "牙買加",
	"countriesL10nMessages.JO": "約旦",
	"countriesL10nMessages.JP": "日本",
	"countriesL10nMessages.KE": "肯亞",
	"countriesL10nMessages.KG": "吉爾吉斯",
	"countriesL10nMessages.KH": "柬埔寨",
	"countriesL10nMessages.KI": "吉里巴斯",
	"countriesL10nMessages.KM": "葛摩",
	"countriesL10nMessages.KN": "聖克里斯多福及尼維斯",
	"countriesL10nMessages.KR": "南韓",
	"countriesL10nMessages.KW": "科威特",
	"countriesL10nMessages.KY": "開曼群島",
	"countriesL10nMessages.KZ": "哈薩克",
	"countriesL10nMessages.LA": "寮國",
	"countriesL10nMessages.LB": "黎巴嫩",
	"countriesL10nMessages.LC": "聖露西亞",
	"countriesL10nMessages.LI": "列支敦斯登",
	"countriesL10nMessages.LK": "斯里蘭卡",
	"countriesL10nMessages.LR": "賴比瑞亞",
	"countriesL10nMessages.LS": "賴索托",
	"countriesL10nMessages.LT": "立陶宛",
	"countriesL10nMessages.LU": "盧森堡",
	"countriesL10nMessages.LV": "拉脫維亞",
	"countriesL10nMessages.LY": "利比亞",
	"countriesL10nMessages.MA": "摩洛哥",
	"countriesL10nMessages.MC": "摩納哥",
	"countriesL10nMessages.MD": "摩爾多瓦",
	"countriesL10nMessages.ME": "蒙地內哥羅",
	"countriesL10nMessages.MF": "法屬聖馬丁",
	"countriesL10nMessages.MG": "馬達加斯加",
	"countriesL10nMessages.MH": "馬紹爾群島",
	"countriesL10nMessages.MK": "馬其頓",
	"countriesL10nMessages.ML": "馬利",
	"countriesL10nMessages.MM": "緬甸",
	"countriesL10nMessages.MN": "蒙古",
	"countriesL10nMessages.MO": "澳門",
	"countriesL10nMessages.MP": "北馬里亞納群島",
	"countriesL10nMessages.MQ": "馬提尼克",
	"countriesL10nMessages.MR": "茅利塔尼亞",
	"countriesL10nMessages.MS": "Montserrat",
	"countriesL10nMessages.MT": "馬爾他",
	"countriesL10nMessages.MU": "模里西斯",
	"countriesL10nMessages.MV": "馬爾地夫",
	"countriesL10nMessages.MW": "馬拉威",
	"countriesL10nMessages.MX": "墨西哥",
	"countriesL10nMessages.MY": "馬來西亞",
	"countriesL10nMessages.MZ": "莫三比克",
	"countriesL10nMessages.NA": "那米比亞",
	"countriesL10nMessages.NC": "新喀里多尼亞",
	"countriesL10nMessages.NE": "尼日",
	"countriesL10nMessages.NF": "諾福克島",
	"countriesL10nMessages.NG": "奈及利亞",
	"countriesL10nMessages.NI": "尼加拉瓜",
	"countriesL10nMessages.NL": "荷蘭",
	"countriesL10nMessages.NO": "挪威",
	"countriesL10nMessages.NP": "尼泊爾",
	"countriesL10nMessages.NR": "諾魯",
	"countriesL10nMessages.NU": "紐埃",
	"countriesL10nMessages.NZ": "紐西蘭",
	"countriesL10nMessages.OM": "阿曼",
	"countriesL10nMessages.PA": "巴拿馬",
	"countriesL10nMessages.PE": "祕魯",
	"countriesL10nMessages.PF": "法屬玻里尼西亞",
	"countriesL10nMessages.PG": "巴布亞紐幾內亞",
	"countriesL10nMessages.PH": "菲律賓",
	"countriesL10nMessages.PK": "巴基斯坦",
	"countriesL10nMessages.PL": "波蘭",
	"countriesL10nMessages.PM": "聖皮埃與密克隆",
	"countriesL10nMessages.PR": "波多黎各",
	"countriesL10nMessages.PS": "巴勒斯坦",
	"countriesL10nMessages.PT": "葡萄牙",
	"countriesL10nMessages.PW": "帛琉",
	"countriesL10nMessages.PY": "巴拉圭",
	"countriesL10nMessages.QA": "卡達",
	"countriesL10nMessages.RE": "留尼旺",
	"countriesL10nMessages.RO": "羅馬尼亞",
	"countriesL10nMessages.RS": "塞爾維亞",
	"countriesL10nMessages.RW": "盧安達",
	"countriesL10nMessages.SA": "沙烏地阿拉伯",
	"countriesL10nMessages.SB": "索羅門群島",
	"countriesL10nMessages.SC": "塞席爾",
	"countriesL10nMessages.SE": "瑞典",
	"countriesL10nMessages.SG": "新加坡",
	"countriesL10nMessages.SH": "聖赫勒拿島",
	"countriesL10nMessages.SI": "斯洛伐尼亞",
	"countriesL10nMessages.SJ": "斯瓦巴和揚馬延",
	"countriesL10nMessages.SK": "斯洛伐克",
	"countriesL10nMessages.SL": "獅子山",
	"countriesL10nMessages.SM": "聖馬利諾",
	"countriesL10nMessages.SN": "塞內加爾",
	"countriesL10nMessages.SO": "索馬利亞",
	"countriesL10nMessages.SR": "蘇利南",
	"countriesL10nMessages.SS": "南蘇丹",
	"countriesL10nMessages.ST": "聖多美普林西比",
	"countriesL10nMessages.SV": "薩爾瓦多",
	"countriesL10nMessages.SX": "荷屬聖馬丁",
	"countriesL10nMessages.SZ": "史瓦帝尼",
	"countriesL10nMessages.TA": "特里斯坦達庫尼亞島",
	"countriesL10nMessages.TC": "土克凱可群島",
	"countriesL10nMessages.TD": "查德",
	"countriesL10nMessages.TG": "多哥",
	"countriesL10nMessages.TH": "泰國",
	"countriesL10nMessages.TJ": "塔吉克",
	"countriesL10nMessages.TK": "托克勞",
	"countriesL10nMessages.TL": "東帝汶",
	"countriesL10nMessages.TM": "土庫曼",
	"countriesL10nMessages.TN": "突尼西亞",
	"countriesL10nMessages.TO": "東加",
	"countriesL10nMessages.TR": "土耳其",
	"countriesL10nMessages.TT": "千里達及托巴哥",
	"countriesL10nMessages.TV": "吐瓦魯",
	"countriesL10nMessages.TW": "台灣",
	"countriesL10nMessages.TZ": "坦尚尼亞",
	"countriesL10nMessages.UA": "烏克蘭",
	"countriesL10nMessages.UG": "烏干達",
	"countriesL10nMessages.US": "美國",
	"countriesL10nMessages.UY": "烏拉圭",
	"countriesL10nMessages.UZ": "烏茲別克",
	"countriesL10nMessages.VA": "教廷 (梵蒂岡城)",
	"countriesL10nMessages.VC": "聖文森及格瑞那丁",
	"countriesL10nMessages.VE": "委內瑞拉",
	"countriesL10nMessages.VG": "英屬維京群島",
	"countriesL10nMessages.VI": "美屬維京群島",
	"countriesL10nMessages.VN": "越南",
	"countriesL10nMessages.VU": "萬那杜",
	"countriesL10nMessages.WF": "瓦利斯和富圖納",
	"countriesL10nMessages.WS": "薩摩亞",
	"countriesL10nMessages.XK": "科索沃",
	"countriesL10nMessages.YE": "葉門",
	"countriesL10nMessages.YT": "馬約特",
	"countriesL10nMessages.ZA": "南非",
	"countriesL10nMessages.ZM": "尚比亞",
	"countriesL10nMessages.ZW": "辛巴威",
	"helper.INDIFFERENCE_PRICE_POINT": "無異價格",
	"helper.IPP": "無異價格 (IPP)",
	"helper.IPP_TITLE": "IPP",
	"helper.OPP": "最佳價格 (OPP)",
	"helper.OPP_DESCRIPTION": "最佳價格點",
	"helper.OPP_TITLE": "OPP",
	"helper.PMC": "可採納的最低價格 (PMC)",
	"helper.PMC_TITLE": "PMC",
	"helper.PME": "可採納的最高價格 (PME)",
	"helper.PME_TITLE": "PME",
	"helper.POINT_OF_MARGINAL_CHEAPNESS": "可採納的最低價格",
	"helper.POINT_OF_MARGINAL_EXPENSIVENESS": "可採納的最高價格"
};

const CountryLabels = {
    ar,
    bg,
    bn,
    bs,
    ca,
    cs,
    cy,
    da,
    de,
    el,
    // 'es-LA', // Spanish (Latin America)
    es,
    et,
    eu,
    fa,
    fi,
    fr,
    ga,
    he,
    hi,
    hr,
    hu,
    hy,
    id,
    is,
    it,
    ja,
    ka,
    ko,
    lt,
    lv,
    mk,
    ml,
    ms,
    nl,
    no,
    pa,
    pl,
    'pt-br': ptBr,
    pt,
    ro,
    ru,
    sk,
    sl,
    sq,
    sr,
    sv,
    sw,
    ta,
    te,
    th,
    tl,
    tr,
    uk,
    vi,
    'zh-tw': zhTw,
    'zh-cn': zhCn, // Chinese (Simplified)
};

const useStyles$9 = createUseStyles((theme) => {
    const { fontSize = 'inherit', fontFamily = 'inherit', color = 'inherit' } = theme.questionBody ?? {};
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    const fontWeight = theme.name.includes('Pencils')
        ? fontWeightOptions.bold
        : fontWeightOptions.medium;
    const isAnswerColorDark = isDark(theme.answerColor);
    return {
        phoneTextInput: {
            display: 'flex',
            alignItems: 'center',
            maxWidth: '100%',
            transition: 'all 0.1s linear',
            '& .PhoneInputInput': {
                width: '13ch',
                minWidth: 0,
                backgroundColor: '#fff',
                border: `1px solid ${theme.answerColor}`,
                height: '28px',
                fontFamily,
                fontWeight,
                fontSize,
                [breakpointMedium]: {
                    fontSize: '16px',
                },
                padding: '6px',
                lineHeight: '1.5em',
                color: isAnswerColorDark ? theme.answerColor : '#000',
            },
            '& .PhoneInputCountry': {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                marginRight: '0.35em',
                border: `1px solid ${theme.answerColor}`,
                height: '40px',
                width: '100px',
                justifyContent: 'center',
                alignSelf: 'auto',
                lineHeight: '1.5em',
                backgroundColor: '#fff',
            },
            '& .PhoneInputCountrySelect': {
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                zIndex: 1,
                border: 0,
                opacity: 0,
                cursor: 'pointer',
            },
            '& .PhoneInputCountrySelectArrow': {
                display: 'block',
                content: '',
                borderStyle: 'solid',
                margin: '0px 8px',
                borderTop: '6px solid black',
                borderRight: '6px solid transparent',
                borderLeft: '6px solid transparent',
                borderBottom: '0px solid transparent',
                borderRadius: '2px',
            },
            '& .PhoneInputCountryIcon': {
                height: '1.5em',
                width: '2.25em',
                border: `1px solid black`,
                margin: '0px 8px',
            },
            '& .PhoneInputCountryIconImg': {
                display: 'block',
                height: '100%',
                width: '100%',
            },
            '& .CountryCallingCode': {
                marginRight: '5px',
                marginLeft: '5px',
                fontWeight,
                fontFamily,
                fontSize,
            },
            '& .PhoneInputCountry:focus, & .PhoneInputCountry:hover': {
                outline: `2px solid ${theme.primaryAccentColor}`,
            },
            '& .PhoneInputInput:focus, & .PhoneInputInput:hover': {
                outline: `2px solid ${theme.primaryAccentColor}`,
            },
            '& .PhoneInputInput::placeholder': {
                fontWeight: fontWeightOptions.regular,
                fontFamily,
                fontSize,
                [breakpointMedium]: {
                    fontSize: '16px',
                },
                color: '#999',
            },
            '& .PhoneInputInput:read-only:not(:disabled)': {
                borderColor: 'transparent',
                backgroundColor: theme.input.bgColor,
                color: theme.isDark ? '#fff' : '#000',
                opacity: 0.5,
            },
            '& .PhoneInputInput:disabled': {
                opacity: 0.4,
            },
        },
        phoneLabelContainer: {
            display: 'flex',
            paddingBottom: '8px',
        },
        phoneLabelClass: {
            fontFamily,
            color,
            fontSize,
            alignSelf: 'flex-end',
            overflowWrap: 'break-word',
            width: '120px',
        },
        countryLabelClass: {
            fontFamily,
            color,
            fontSize,
            width: '100px',
            overflowWrap: 'break-word',
            alignSelf: 'flex-end',
            marginRight: '.65em',
        },
        extensionLabelClass: {
            paddingBottom: '8px',
            color,
            fontFamily,
            fontSize,
        },
        extensionFieldClass: {
            minWidth: 0,
            backgroundColor: '#fff',
            color: isAnswerColorDark ? theme.answerColor : '#000',
            border: `1px solid ${theme.answerColor}`,
            height: '42px',
            width: '85px',
            fontFamily: theme.questionBody.fontFamily ?? {},
            fontSize,
            [breakpointMedium]: {
                fontSize: '16px',
            },
            lineHeight: '1.5em',
            padding: '6px',
            '&::placeholder': {
                fontWeight: fontWeightOptions.regular,
                fontFamily,
                fontSize,
                color: '#999',
            },
        },
        questionContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
        },
        fieldContainer: {
            display: 'flex',
            flexDirection: 'column',
            padding: '0px 8px 8px 0px',
            alignSelf: 'flex-end',
        },
    };
});

const COPY$6 = defineMessages({
    COUNTRY_CODE: {
        id: 'PhoneField.countryCode',
        defaultMessage: 'Country Code',
        description: '[Type: label][Vis: high] - label for country code dropdown',
    },
});
function PhoneField({ id: questionId, required, disabled, readOnly, responses: defaultResponses = [], size, hasCountrySelect, answerOptions, countryCode, languageCode, ...fieldProps }) {
    const { error, errorId = createErrorId(questionId), title: { id: titleId }, } = fieldProps;
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value;
    };
    let phoneDefault = '';
    let extDefault = '';
    answerOptions.forEach((option) => {
        if (option.visible && option.subtype === 'phone_number') {
            phoneDefault = getDefaultValueById(option.id) ?? '';
        }
        else if (option.visible && option.subtype === 'extension') {
            extDefault = getDefaultValueById(option.id) ?? '';
        }
    });
    const [phoneValue, setPhoneValue] = useState(phoneDefault);
    const handleInputChange = (e) => {
        // only accepts digits
        e.target.value = e.target.value.replace(/[^\d]/, '');
    };
    const handleChangeWithCountry = (v) => {
        setPhoneValue(v);
    };
    // defaults to english if locale doesn't exist in CountryLabels
    const labels = languageCode in CountryLabels
        ? CountryLabels[languageCode]
        : {};
    // export phone country labels from smQuestions localeMessages
    const phoneLabels = Object.keys(labels).reduce((acc, key) => {
        if (key.includes('countriesL10nMessages.')) {
            acc[key.replace('countriesL10nMessages.', '')] =
                labels[key];
        }
        return acc;
    }, {});
    const { phoneTextInput, phoneLabelContainer, phoneLabelClass, countryLabelClass, extensionLabelClass, extensionFieldClass, questionContainer, fieldContainer, } = useStyles$9({ size: 40 });
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, ...fieldProps },
        React__default.createElement("div", { key: questionId, className: questionContainer }, answerOptions.map((option) => {
            return (option?.visible && (React__default.createElement("div", { key: option.id, className: fieldContainer },
                option.subtype === 'phone_number' && (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement("div", { className: phoneLabelContainer },
                        hasCountrySelect && (React__default.createElement("div", { className: countryLabelClass },
                            React__default.createElement(RichText, { element: "span", text: t(COPY$6.COUNTRY_CODE) }))),
                        React__default.createElement("div", { className: phoneLabelClass },
                            React__default.createElement(RichText, { element: "span", text: option.label }))),
                    React__default.createElement(PhoneCountryInput, { id: `${questionId}_${option.id}`, name: `${questionId}_${option.id}`, className: classNames(phoneTextInput), labels: Object.keys(phoneLabels).length
                            ? phoneLabels
                            : undefined, value: phoneValue, required: required, disabled: disabled, readOnly: readOnly, size: size, onChange: handleChangeWithCountry, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS, placeholder: option.placeholder, addInternationalOption: false, defaultCountry: countryCode, countries: COUNTRY_CODES, focusInputOnCountrySelection: false, countrySelectComponent: CountrySelectWithIcon, international: true, countryCallingCodeEditable: false }))),
                option.subtype === 'extension' && (React__default.createElement(React__default.Fragment, null,
                    React__default.createElement("label", { className: extensionLabelClass, htmlFor: `${questionId}_${option.id}` },
                        required && React__default.createElement("span", { "aria-hidden": true }, "* "),
                        React__default.createElement(RichText, { element: "span", text: option.label })),
                    React__default.createElement(TextInput$1, { id: `${questionId}_${option.id}`, name: `${questionId}_${option.id}`, onChange: handleInputChange, className: extensionFieldClass, defaultValue: extDefault, required: required, disabled: disabled, readOnly: readOnly, placeholder: option.placeholder ?? '', size: size, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS }))))));
        }))));
}
var PhoneField$1 = withErrorBoundary(PhoneField);

const useStyles$8 = createUseStyles((theme) => {
    const { fontSize = 'inherit', fontFamily = 'inherit' } = theme.questionBody ?? {};
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    return {
        emailInput: {
            backgroundColor: '#fff',
            border: `1px solid ${theme.answerColor}`,
            fontFamily,
            fontSize,
            [breakpointMedium]: {
                fontSize: '16px',
            },
            padding: '6px',
            lineHeight: '1.5em',
            height: '40px',
            maxWidth: '100%',
            width: '100%',
            [breakpointXsMin]: {
                // extra 12px to account for the padding
                width: ({ size }) => `calc(${size}ch + 12px)`,
            },
            '&::placeholder': {
                fontWeight: fontWeightOptions.regular,
                fontSize,
                [breakpointMedium]: {
                    fontSize: '16px',
                },
            },
        },
        emailSubtitle: {
            display: 'flex',
            paddingBottom: '8px',
            fontFamily: theme.questionBody.fontFamily ?? {},
            fontSize: theme.questionBody.fontSize,
            color: theme.questionBody.color,
        },
    };
});

function EmailField({ id: questionId, required, disabled, readOnly, responses: defaultResponses = [], onChange, size, answerOptions, ...fieldProps }) {
    const { error, errorId = createErrorId(questionId), title: { id: titleId }, } = fieldProps;
    const defaultValue = useMemo(() => defaultResponses.find((r) => !!r.value)?.value ?? '', 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const handleChange = (e) => {
        const responses = e.target.value
            ? [{ id: questionId, value: e.target.value }]
            : [];
        onChange?.(responses);
    };
    const { emailInput, emailSubtitle } = useStyles$8({ size: 40 });
    const { id: optionId, label, placeholder } = answerOptions;
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, ...fieldProps },
        React__default.createElement("div", { className: emailSubtitle },
            React__default.createElement(RichText, { element: "span", text: label })),
        React__default.createElement(TextInput$1, { id: `${questionId}_${optionId}`, name: `${questionId}_${optionId}`, className: emailInput, defaultValue: defaultValue, required: required, disabled: disabled, readOnly: readOnly, size: size, onChange: handleChange, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS, placeholder: placeholder })));
}
var EmailField$1 = withErrorBoundary(EmailField);

/*
 * This file includes the list of countries used in
 * SMQuestions address & phone QT and their translations
 */
const COPY$5 = defineMessages({
    COUNTRY_AF: {
        id: 'countriesL10nMessages.AF',
        defaultMessage: 'Afghanistan',
        description: '[Type: label][Vis: high] - country label for Afghanistan',
    },
    COUNTRY_AX: {
        id: 'countriesL10nMessages.AX',
        defaultMessage: 'Åland Islands',
        description: '[Type: label][Vis: high] - country label for Åland Islands',
    },
    COUNTRY_AL: {
        id: 'countriesL10nMessages.AL',
        defaultMessage: 'Albania',
        description: '[Type: label][Vis: high] - country label for Albania',
    },
    COUNTRY_DZ: {
        id: 'countriesL10nMessages.DZ',
        defaultMessage: 'Algeria',
        description: '[Type: label][Vis: high] - country label for Algeria',
    },
    COUNTRY_AD: {
        id: 'countriesL10nMessages.AD',
        defaultMessage: 'Andorra',
        description: '[Type: label][Vis: high] - country label for Andorra',
    },
    COUNTRY_AO: {
        id: 'countriesL10nMessages.AO',
        defaultMessage: 'Angola',
        description: '[Type: label][Vis: high] - country label for Angola',
    },
    COUNTRY_AI: {
        id: 'countriesL10nMessages.AI',
        defaultMessage: 'Anguilla',
        description: '[Type: label][Vis: high] - country label for Anguilla',
    },
    COUNTRY_AS: {
        id: 'countriesL10nMessages.AS',
        defaultMessage: 'American Samoa',
        description: '[Type: label][Vis: high] - country label for American Samoa',
    },
    COUNTRY_AG: {
        id: 'countriesL10nMessages.AG',
        defaultMessage: 'Antigua and Barbuda',
        description: '[Type: label][Vis: high] - country label for Antigua and Barbuda',
    },
    COUNTRY_AR: {
        id: 'countriesL10nMessages.AR',
        defaultMessage: 'Argentina',
        description: '[Type: label][Vis: high] - country label for Argentina',
    },
    COUNTRY_AM: {
        id: 'countriesL10nMessages.AM',
        defaultMessage: 'Armenia',
        description: '[Type: label][Vis: high] - country label for Armenia',
    },
    COUNTRY_AC: {
        id: 'countriesL10nMessages.AC',
        defaultMessage: 'Ascension Island',
        description: '[Type: label][Vis: high] - country label for Ascension Island',
    },
    COUNTRY_AW: {
        id: 'countriesL10nMessages.AW',
        defaultMessage: 'Aruba',
        description: '[Type: label][Vis: high] - country label for Aruba',
    },
    COUNTRY_AU: {
        id: 'countriesL10nMessages.AU',
        defaultMessage: 'Australia',
        description: '[Type: label][Vis: high] - country label for Australia',
    },
    COUNTRY_AT: {
        id: 'countriesL10nMessages.AT',
        defaultMessage: 'Austria',
        description: '[Type: label][Vis: high] - country label for Austria',
    },
    COUNTRY_AZ: {
        id: 'countriesL10nMessages.AZ',
        defaultMessage: 'Azerbaijan',
        description: '[Type: label][Vis: high] - country label for Azerbaijan',
    },
    COUNTRY_BS: {
        id: 'countriesL10nMessages.BS',
        defaultMessage: 'Bahamas',
        description: '[Type: label][Vis: high] - country label for Bahamas',
    },
    COUNTRY_BH: {
        id: 'countriesL10nMessages.BH',
        defaultMessage: 'Bahrain',
        description: '[Type: label][Vis: high] - country label for Bahrain',
    },
    COUNTRY_BD: {
        id: 'countriesL10nMessages.BD',
        defaultMessage: 'Bangladesh',
        description: '[Type: label][Vis: high] - country label for Bangladesh',
    },
    COUNTRY_BB: {
        id: 'countriesL10nMessages.BB',
        defaultMessage: 'Barbados',
        description: '[Type: label][Vis: high] - country label for Barbados',
    },
    COUNTRY_BE: {
        id: 'countriesL10nMessages.BE',
        defaultMessage: 'Belgium',
        description: '[Type: label][Vis: high] - country label for Belgium',
    },
    COUNTRY_BZ: {
        id: 'countriesL10nMessages.BZ',
        defaultMessage: 'Belize',
        description: '[Type: label][Vis: high] - country label for Belize',
    },
    COUNTRY_BJ: {
        id: 'countriesL10nMessages.BJ',
        defaultMessage: 'Benin',
        description: '[Type: label][Vis: high] - country label for Benin',
    },
    COUNTRY_BM: {
        id: 'countriesL10nMessages.BM',
        defaultMessage: 'Bermuda',
        description: '[Type: label][Vis: high] - country label for Bermuda',
    },
    COUNTRY_BT: {
        id: 'countriesL10nMessages.BT',
        defaultMessage: 'Bhutan',
        description: '[Type: label][Vis: high] - country label for Bhutan',
    },
    COUNTRY_BO: {
        id: 'countriesL10nMessages.BO',
        defaultMessage: 'Bolivia',
        description: '[Type: label][Vis: high] - country label for Bolivia, Plurinational State of',
    },
    COUNTRY_BQ: {
        id: 'countriesL10nMessages.BQ',
        defaultMessage: 'Bonaire, Sint Eustatius and Saba',
        description: '[Type: label][Vis: high] - country label for Bonaire, Sint Eustatius and Saba',
    },
    COUNTRY_BA: {
        id: 'countriesL10nMessages.BA',
        defaultMessage: 'Bosnia and Herzegovina',
        description: '[Type: label][Vis: high] - country label for Bosnia and Herzegovina',
    },
    COUNTRY_BW: {
        id: 'countriesL10nMessages.BW',
        defaultMessage: 'Botswana',
        description: '[Type: label][Vis: high] - country label for Botswana',
    },
    COUNTRY_BR: {
        id: 'countriesL10nMessages.BR',
        defaultMessage: 'Brazil',
        description: '[Type: label][Vis: high] - country label for Brazil',
    },
    COUNTRY_IO: {
        id: 'countriesL10nMessages.IO',
        defaultMessage: 'British Indian Ocean Territory',
        description: '[Type: label][Vis: high] - country label for British Indian Ocean Territory',
    },
    COUNTRY_BN: {
        id: 'countriesL10nMessages.BN',
        defaultMessage: 'Brunei Darussalam',
        description: '[Type: label][Vis: high] - country label for Brunei Darussalam',
    },
    COUNTRY_BG: {
        id: 'countriesL10nMessages.BG',
        defaultMessage: 'Bulgaria',
        description: '[Type: label][Vis: high] - country label for Bulgaria',
    },
    COUNTRY_BF: {
        id: 'countriesL10nMessages.BF',
        defaultMessage: 'Burkina Faso',
        description: '[Type: label][Vis: high] - country label for Burkina Faso',
    },
    COUNTRY_BI: {
        id: 'countriesL10nMessages.BI',
        defaultMessage: 'Burundi',
        description: '[Type: label][Vis: high] - country label for Burundi',
    },
    COUNTRY_KH: {
        id: 'countriesL10nMessages.KH',
        defaultMessage: 'Cambodia',
        description: '[Type: label][Vis: high] - country label for Cambodia',
    },
    COUNTRY_CM: {
        id: 'countriesL10nMessages.CM',
        defaultMessage: 'Cameroon',
        description: '[Type: label][Vis: high] - country label for Cameroon',
    },
    COUNTRY_CA: {
        id: 'countriesL10nMessages.CA',
        defaultMessage: 'Canada',
        description: '[Type: label][Vis: high] - country label for Canada',
    },
    COUNTRY_CV: {
        id: 'countriesL10nMessages.CV',
        defaultMessage: 'Cape Verde',
        description: '[Type: label][Vis: high] - country label for Cape Verde',
    },
    COUNTRY_KY: {
        id: 'countriesL10nMessages.KY',
        defaultMessage: 'Cayman Islands',
        description: '[Type: label][Vis: high] - country label for Cayman Islands',
    },
    COUNTRY_CF: {
        id: 'countriesL10nMessages.CF',
        defaultMessage: 'Central African Republic',
        description: '[Type: label][Vis: high] - country label for Central African Republic',
    },
    COUNTRY_TD: {
        id: 'countriesL10nMessages.TD',
        defaultMessage: 'Chad',
        description: '[Type: label][Vis: high] - country label for Chad',
    },
    COUNTRY_CL: {
        id: 'countriesL10nMessages.CL',
        defaultMessage: 'Chile',
        description: '[Type: label][Vis: high] - country label for Chile',
    },
    COUNTRY_CN: {
        id: 'countriesL10nMessages.CN',
        defaultMessage: 'China',
        description: '[Type: label][Vis: high] - country label for China',
    },
    COUNTRY_CX: {
        id: 'countriesL10nMessages.CX',
        defaultMessage: 'Christmas Island',
        description: '[Type: label][Vis: high] - country label for Christmas Island',
    },
    COUNTRY_CC: {
        id: 'countriesL10nMessages.CC',
        defaultMessage: 'Cocos (Keeling) Islands',
        description: '[Type: label][Vis: high] - country label for Cocos (Keeling) Islands',
    },
    COUNTRY_CO: {
        id: 'countriesL10nMessages.CO',
        defaultMessage: 'Colombia',
        description: '[Type: label][Vis: high] - country label for Colombia',
    },
    COUNTRY_KM: {
        id: 'countriesL10nMessages.KM',
        defaultMessage: 'Comoros',
        description: '[Type: label][Vis: high] - country label for Comoros',
    },
    COUNTRY_CG: {
        id: 'countriesL10nMessages.CG',
        defaultMessage: 'Congo',
        description: '[Type: label][Vis: high] - country label for Congo',
    },
    COUNTRY_CD: {
        id: 'countriesL10nMessages.CD',
        defaultMessage: 'Congo, The Democratic Republic of the',
        description: '[Type: label][Vis: high] - country label for Congo, The Democratic Republic of the',
    },
    COUNTRY_CK: {
        id: 'countriesL10nMessages.CK',
        defaultMessage: 'Cook Islands',
        description: '[Type: label][Vis: high] - country label for Cook Islands',
    },
    COUNTRY_CR: {
        id: 'countriesL10nMessages.CR',
        defaultMessage: 'Costa Rica',
        description: '[Type: label][Vis: high] - country label for Costa Rica',
    },
    COUNTRY_CI: {
        id: 'countriesL10nMessages.CI',
        defaultMessage: "Côte d'Ivoire",
        description: "[Type: label][Vis: high] - country label for Côte d'Ivoire",
    },
    COUNTRY_HR: {
        id: 'countriesL10nMessages.HR',
        defaultMessage: 'Croatia',
        description: '[Type: label][Vis: high] - country label for Croatia',
    },
    COUNTRY_CW: {
        id: 'countriesL10nMessages.CW',
        defaultMessage: 'Curaçao',
        description: '[Type: label][Vis: high] - country label for Curaçao',
    },
    COUNTRY_CY: {
        id: 'countriesL10nMessages.CY',
        defaultMessage: 'Cyprus',
        description: '[Type: label][Vis: high] - country label for Cyprus',
    },
    COUNTRY_CZ: {
        id: 'countriesL10nMessages.CZ',
        defaultMessage: 'Czech Republic',
        description: '[Type: label][Vis: high] - country label for Czech Republic',
    },
    COUNTRY_DK: {
        id: 'countriesL10nMessages.DK',
        defaultMessage: 'Denmark',
        description: '[Type: label][Vis: high] - country label for Denmark',
    },
    COUNTRY_DJ: {
        id: 'countriesL10nMessages.DJ',
        defaultMessage: 'Djibouti',
        description: '[Type: label][Vis: high] - country label for Djibouti',
    },
    COUNTRY_DM: {
        id: 'countriesL10nMessages.DM',
        defaultMessage: 'Dominica',
        description: '[Type: label][Vis: high] - country label for Dominica',
    },
    COUNTRY_DO: {
        id: 'countriesL10nMessages.DO',
        defaultMessage: 'Dominican Republic',
        description: '[Type: label][Vis: high] - country label for Dominican Republic',
    },
    COUNTRY_EC: {
        id: 'countriesL10nMessages.EC',
        defaultMessage: 'Ecuador',
        description: '[Type: label][Vis: high] - country label for Ecuador',
    },
    COUNTRY_EG: {
        id: 'countriesL10nMessages.EG',
        defaultMessage: 'Egypt',
        description: '[Type: label][Vis: high] - country label for Egypt',
    },
    COUNTRY_SV: {
        id: 'countriesL10nMessages.SV',
        defaultMessage: 'El Salvador',
        description: '[Type: label][Vis: high] - country label for El Salvador',
    },
    COUNTRY_GQ: {
        id: 'countriesL10nMessages.GQ',
        defaultMessage: 'Equatorial Guinea',
        description: '[Type: label][Vis: high] - country label for Equatorial Guinea',
    },
    COUNTRY_ER: {
        id: 'countriesL10nMessages.ER',
        defaultMessage: 'Eritrea',
        description: '[Type: label][Vis: high] - country label for Eritrea',
    },
    COUNTRY_EE: {
        id: 'countriesL10nMessages.EE',
        defaultMessage: 'Estonia',
        description: '[Type: label][Vis: high] - country label for Estonia',
    },
    COUNTRY_ET: {
        id: 'countriesL10nMessages.ET',
        defaultMessage: 'Ethiopia',
        description: '[Type: label][Vis: high] - country label for Ethiopia',
    },
    COUNTRY_FK: {
        id: 'countriesL10nMessages.FK',
        defaultMessage: 'Falkland Islands',
        description: '[Type: label][Vis: high] - country label for Falkland Islands',
    },
    COUNTRY_FO: {
        id: 'countriesL10nMessages.FO',
        defaultMessage: 'Faroe Islands',
        description: '[Type: label][Vis: high] - country label for Faroe Islands',
    },
    COUNTRY_FM: {
        id: 'countriesL10nMessages.FM',
        defaultMessage: 'Federated States of Micronesia',
        description: '[Type: label][Vis: high] - country label for Federated States of Micronesia',
    },
    COUNTRY_FJ: {
        id: 'countriesL10nMessages.FJ',
        defaultMessage: 'Fiji',
        description: '[Type: label][Vis: high] - country label for Fiji',
    },
    COUNTRY_FI: {
        id: 'countriesL10nMessages.FI',
        defaultMessage: 'Finland',
        description: '[Type: label][Vis: high] - country label for Finland',
    },
    COUNTRY_FR: {
        id: 'countriesL10nMessages.FR',
        defaultMessage: 'France',
        description: '[Type: label][Vis: high] - country label for France',
    },
    COUNTRY_GF: {
        id: 'countriesL10nMessages.GF',
        defaultMessage: 'French Guiana',
        description: '[Type: label][Vis: high] - country label for French Guiana',
    },
    COUNTRY_PF: {
        id: 'countriesL10nMessages.PF',
        defaultMessage: 'French Polynesia',
        description: '[Type: label][Vis: high] - country label for French Polynesia',
    },
    COUNTRY_GA: {
        id: 'countriesL10nMessages.GA',
        defaultMessage: 'Gabon',
        description: '[Type: label][Vis: high] - country label for Gabon',
    },
    COUNTRY_GM: {
        id: 'countriesL10nMessages.GM',
        defaultMessage: 'Gambia',
        description: '[Type: label][Vis: high] - country label for Gambia',
    },
    COUNTRY_GE: {
        id: 'countriesL10nMessages.GE',
        defaultMessage: 'Georgia',
        description: '[Type: label][Vis: high] - country label for Georgia',
    },
    COUNTRY_DE: {
        id: 'countriesL10nMessages.DE',
        defaultMessage: 'Germany',
        description: '[Type: label][Vis: high] - country label for Germany',
    },
    COUNTRY_GH: {
        id: 'countriesL10nMessages.GH',
        defaultMessage: 'Ghana',
        description: '[Type: label][Vis: high] - country label for Ghana',
    },
    COUNTRY_GI: {
        id: 'countriesL10nMessages.GI',
        defaultMessage: 'Gibraltar',
        description: '[Type: label][Vis: high] - country label for Gibraltar',
    },
    COUNTRY_GR: {
        id: 'countriesL10nMessages.GR',
        defaultMessage: 'Greece',
        description: '[Type: label][Vis: high] - country label for Greece',
    },
    COUNTRY_GL: {
        id: 'countriesL10nMessages.GL',
        defaultMessage: 'Greenland',
        description: '[Type: label][Vis: high] - country label for Greenland',
    },
    COUNTRY_GD: {
        id: 'countriesL10nMessages.GD',
        defaultMessage: 'Grenada',
        description: '[Type: label][Vis: high] - country label for Grenada',
    },
    COUNTRY_GP: {
        id: 'countriesL10nMessages.GP',
        defaultMessage: 'Guadeloupe',
        description: '[Type: label][Vis: high] - country label for Guadeloupe',
    },
    COUNTRY_GU: {
        id: 'countriesL10nMessages.GU',
        defaultMessage: 'Guam',
        description: '[Type: label][Vis: high] - country label for Guam',
    },
    COUNTRY_GT: {
        id: 'countriesL10nMessages.GT',
        defaultMessage: 'Guatemala',
        description: '[Type: label][Vis: high] - country label for Guatemala',
    },
    COUNTRY_GG: {
        id: 'countriesL10nMessages.GG',
        defaultMessage: 'Guernsey',
        description: '[Type: label][Vis: high] - country label for Guernsey',
    },
    COUNTRY_GN: {
        id: 'countriesL10nMessages.GN',
        defaultMessage: 'Guinea',
        description: '[Type: label][Vis: high] - country label for Guinea',
    },
    COUNTRY_GW: {
        id: 'countriesL10nMessages.GW',
        defaultMessage: 'Guinea-Bissau',
        description: '[Type: label][Vis: high] - country label for Guinea-Bissau',
    },
    COUNTRY_GY: {
        id: 'countriesL10nMessages.GY',
        defaultMessage: 'Guyana',
        description: '[Type: label][Vis: high] - country label for Guyana',
    },
    COUNTRY_HT: {
        id: 'countriesL10nMessages.HT',
        defaultMessage: 'Haiti',
        description: '[Type: label][Vis: high] - country label for Haiti',
    },
    COUNTRY_VA: {
        id: 'countriesL10nMessages.VA',
        defaultMessage: 'Holy See (Vatican City State)',
        description: '[Type: label][Vis: high] - country label for Holy See (Vatican City State)',
    },
    COUNTRY_HN: {
        id: 'countriesL10nMessages.HN',
        defaultMessage: 'Honduras',
        description: '[Type: label][Vis: high] - country label for Honduras',
    },
    COUNTRY_HK: {
        id: 'countriesL10nMessages.HK',
        defaultMessage: 'Hong Kong',
        description: '[Type: label][Vis: high] - country label for Hong Kong',
    },
    COUNTRY_HU: {
        id: 'countriesL10nMessages.HU',
        defaultMessage: 'Hungary',
        description: '[Type: label][Vis: high] - country label for Hungary',
    },
    COUNTRY_IS: {
        id: 'countriesL10nMessages.IS',
        defaultMessage: 'Iceland',
        description: '[Type: label][Vis: high] - country label for Iceland',
    },
    COUNTRY_IN: {
        id: 'countriesL10nMessages.IN',
        defaultMessage: 'India',
        description: '[Type: label][Vis: high] - country label for India',
    },
    COUNTRY_ID: {
        id: 'countriesL10nMessages.ID',
        defaultMessage: 'Indonesia',
        description: '[Type: label][Vis: high] - country label for Indonesia',
    },
    COUNTRY_IQ: {
        id: 'countriesL10nMessages.IQ',
        defaultMessage: 'Iraq',
        description: '[Type: label][Vis: high] - country label for Iraq',
    },
    COUNTRY_IE: {
        id: 'countriesL10nMessages.IE',
        defaultMessage: 'Ireland',
        description: '[Type: label][Vis: high] - country label for Ireland',
    },
    COUNTRY_IM: {
        id: 'countriesL10nMessages.IM',
        defaultMessage: 'Isle of Man',
        description: '[Type: label][Vis: high] - country label for Isle of Man',
    },
    COUNTRY_IL: {
        id: 'countriesL10nMessages.IL',
        defaultMessage: 'Israel',
        description: '[Type: label][Vis: high] - country label for Israel',
    },
    COUNTRY_IT: {
        id: 'countriesL10nMessages.IT',
        defaultMessage: 'Italy',
        description: '[Type: label][Vis: high] - country label for Italy',
    },
    COUNTRY_JM: {
        id: 'countriesL10nMessages.JM',
        defaultMessage: 'Jamaica',
        description: '[Type: label][Vis: high] - country label for Jamaica',
    },
    COUNTRY_JP: {
        id: 'countriesL10nMessages.JP',
        defaultMessage: 'Japan',
        description: '[Type: label][Vis: high] - country label for Japan',
    },
    COUNTRY_JE: {
        id: 'countriesL10nMessages.JE',
        defaultMessage: 'Jersey',
        description: '[Type: label][Vis: high] - country label for Jersey',
    },
    COUNTRY_JO: {
        id: 'countriesL10nMessages.JO',
        defaultMessage: 'Jordan',
        description: '[Type: label][Vis: high] - country label for Jordan',
    },
    COUNTRY_KZ: {
        id: 'countriesL10nMessages.KZ',
        defaultMessage: 'Kazakhstan',
        description: '[Type: label][Vis: high] - country label for Kazakhstan',
    },
    COUNTRY_KE: {
        id: 'countriesL10nMessages.KE',
        defaultMessage: 'Kenya',
        description: '[Type: label][Vis: high] - country label for Kenya',
    },
    COUNTRY_KI: {
        id: 'countriesL10nMessages.KI',
        defaultMessage: 'Kiribati',
        description: '[Type: label][Vis: high] - country label for Kiribati',
    },
    COUNTRY_XK: {
        id: 'countriesL10nMessages.XK',
        defaultMessage: 'Kosovo',
        description: '[Type: label][Vis: high] - country label for Kosovo',
    },
    COUNTRY_KW: {
        id: 'countriesL10nMessages.KW',
        defaultMessage: 'Kuwait',
        description: '[Type: label][Vis: high] - country label for Kuwait',
    },
    COUNTRY_KG: {
        id: 'countriesL10nMessages.KG',
        defaultMessage: 'Kyrgyzstan',
        description: '[Type: label][Vis: high] - country label for Kyrgyzstan',
    },
    COUNTRY_LA: {
        id: 'countriesL10nMessages.LA',
        defaultMessage: 'Laos',
        description: '[Type: label][Vis: high] - country label for Laos',
    },
    COUNTRY_LV: {
        id: 'countriesL10nMessages.LV',
        defaultMessage: 'Latvia',
        description: '[Type: label][Vis: high] - country label for Latvia',
    },
    COUNTRY_LB: {
        id: 'countriesL10nMessages.LB',
        defaultMessage: 'Lebanon',
        description: '[Type: label][Vis: high] - country label for Lebanon',
    },
    COUNTRY_LS: {
        id: 'countriesL10nMessages.LS',
        defaultMessage: 'Lesotho',
        description: '[Type: label][Vis: high] - country label for Lesotho',
    },
    COUNTRY_LR: {
        id: 'countriesL10nMessages.LR',
        defaultMessage: 'Liberia',
        description: '[Type: label][Vis: high] - country label for Liberia',
    },
    COUNTRY_LY: {
        id: 'countriesL10nMessages.LY',
        defaultMessage: 'Libya',
        description: '[Type: label][Vis: high] - country label for Libya',
    },
    COUNTRY_LI: {
        id: 'countriesL10nMessages.LI',
        defaultMessage: 'Liechtenstein',
        description: '[Type: label][Vis: high] - country label for Liechtenstein',
    },
    COUNTRY_LT: {
        id: 'countriesL10nMessages.LT',
        defaultMessage: 'Lithuania',
        description: '[Type: label][Vis: high] - country label for Lithuania',
    },
    COUNTRY_LU: {
        id: 'countriesL10nMessages.LU',
        defaultMessage: 'Luxembourg',
        description: '[Type: label][Vis: high] - country label for Luxembourg',
    },
    COUNTRY_MO: {
        id: 'countriesL10nMessages.MO',
        defaultMessage: 'Macao',
        description: '[Type: label][Vis: high] - country label for Macao',
    },
    COUNTRY_MK: {
        id: 'countriesL10nMessages.MK',
        defaultMessage: 'Macedonia',
        description: '[Type: label][Vis: high] - country label for Macedonia',
    },
    COUNTRY_MG: {
        id: 'countriesL10nMessages.MG',
        defaultMessage: 'Madagascar',
        description: '[Type: label][Vis: high] - country label for Madagascar',
    },
    COUNTRY_MW: {
        id: 'countriesL10nMessages.MW',
        defaultMessage: 'Malawi',
        description: '[Type: label][Vis: high] - country label for Malawi',
    },
    COUNTRY_MY: {
        id: 'countriesL10nMessages.MY',
        defaultMessage: 'Malaysia',
        description: '[Type: label][Vis: high] - country label for Malaysia',
    },
    COUNTRY_MV: {
        id: 'countriesL10nMessages.MV',
        defaultMessage: 'Maldives',
        description: '[Type: label][Vis: high] - country label for Maldives',
    },
    COUNTRY_ML: {
        id: 'countriesL10nMessages.ML',
        defaultMessage: 'Mali',
        description: '[Type: label][Vis: high] - country label for Mali',
    },
    COUNTRY_MT: {
        id: 'countriesL10nMessages.MT',
        defaultMessage: 'Malta',
        description: '[Type: label][Vis: high] - country label for Malta',
    },
    COUNTRY_MH: {
        id: 'countriesL10nMessages.MH',
        defaultMessage: 'Marshall Islands',
        description: '[Type: label][Vis: high] - country label for Marshall Islands',
    },
    COUNTRY_MQ: {
        id: 'countriesL10nMessages.MQ',
        defaultMessage: 'Martinique',
        description: '[Type: label][Vis: high] - country label for Martinique',
    },
    COUNTRY_MR: {
        id: 'countriesL10nMessages.MR',
        defaultMessage: 'Mauritania',
        description: '[Type: label][Vis: high] - country label for Mauritania',
    },
    COUNTRY_MU: {
        id: 'countriesL10nMessages.MU',
        defaultMessage: 'Mauritius',
        description: '[Type: label][Vis: high] - country label for Mauritius',
    },
    COUNTRY_YT: {
        id: 'countriesL10nMessages.YT',
        defaultMessage: 'Mayotte',
        description: '[Type: label][Vis: high] - country label for Mayotte',
    },
    COUNTRY_MX: {
        id: 'countriesL10nMessages.MX',
        defaultMessage: 'Mexico',
        description: '[Type: label][Vis: high] - country label for Mexico',
    },
    COUNTRY_MD: {
        id: 'countriesL10nMessages.MD',
        defaultMessage: 'Moldova',
        description: '[Type: label][Vis: high] - country label for Moldova',
    },
    COUNTRY_MC: {
        id: 'countriesL10nMessages.MC',
        defaultMessage: 'Monaco',
        description: '[Type: label][Vis: high] - country label for Monaco',
    },
    COUNTRY_MN: {
        id: 'countriesL10nMessages.MN',
        defaultMessage: 'Mongolia',
        description: '[Type: label][Vis: high] - country label for Mongolia',
    },
    COUNTRY_ME: {
        id: 'countriesL10nMessages.ME',
        defaultMessage: 'Montenegro',
        description: '[Type: label][Vis: high] - country label for Montenegro',
    },
    COUNTRY_MS: {
        id: 'countriesL10nMessages.MS',
        defaultMessage: 'Montserrat',
        description: '[Type: label][Vis: high] - country label for Montserrat',
    },
    COUNTRY_MA: {
        id: 'countriesL10nMessages.MA',
        defaultMessage: 'Morocco',
        description: '[Type: label][Vis: high] - country label for Morocco',
    },
    COUNTRY_MZ: {
        id: 'countriesL10nMessages.MZ',
        defaultMessage: 'Mozambique',
        description: '[Type: label][Vis: high] - country label for Mozambique',
    },
    COUNTRY_MM: {
        id: 'countriesL10nMessages.MM',
        defaultMessage: 'Myanmar',
        description: '[Type: label][Vis: high] - country label for Myanmar',
    },
    COUNTRY_NA: {
        id: 'countriesL10nMessages.NA',
        defaultMessage: 'Namibia',
        description: '[Type: label][Vis: high] - country label for Namibia',
    },
    COUNTRY_NR: {
        id: 'countriesL10nMessages.NR',
        defaultMessage: 'Nauru',
        description: '[Type: label][Vis: high] - country label for Nauru',
    },
    COUNTRY_NP: {
        id: 'countriesL10nMessages.NP',
        defaultMessage: 'Nepal',
        description: '[Type: label][Vis: high] - country label for Nepal',
    },
    COUNTRY_NL: {
        id: 'countriesL10nMessages.NL',
        defaultMessage: 'Netherlands',
        description: '[Type: label][Vis: high] - country label for Netherlands',
    },
    COUNTRY_NC: {
        id: 'countriesL10nMessages.NC',
        defaultMessage: 'New Caledonia',
        description: '[Type: label][Vis: high] - country label for New Caledonia',
    },
    COUNTRY_NZ: {
        id: 'countriesL10nMessages.NZ',
        defaultMessage: 'New Zealand',
        description: '[Type: label][Vis: high] - country label for New Zealand',
    },
    COUNTRY_NI: {
        id: 'countriesL10nMessages.NI',
        defaultMessage: 'Nicaragua',
        description: '[Type: label][Vis: high] - country label for Nicaragua',
    },
    COUNTRY_NE: {
        id: 'countriesL10nMessages.NE',
        defaultMessage: 'Niger',
        description: '[Type: label][Vis: high] - country label for Niger',
    },
    COUNTRY_NG: {
        id: 'countriesL10nMessages.NG',
        defaultMessage: 'Nigeria',
        description: '[Type: label][Vis: high] - country label for Nigeria',
    },
    COUNTRY_NU: {
        id: 'countriesL10nMessages.NU',
        defaultMessage: 'Niue',
        description: '[Type: label][Vis: high] - country label for Niue',
    },
    COUNTRY_NF: {
        id: 'countriesL10nMessages.NF',
        defaultMessage: 'Norfolk Island',
        description: '[Type: label][Vis: high] - country label for Norfolk Island',
    },
    COUNTRY_MP: {
        id: 'countriesL10nMessages.MP',
        defaultMessage: 'Northern Mariana Island',
        description: '[Type: label][Vis: high] - country label for Northern Mariana Island',
    },
    COUNTRY_NO: {
        id: 'countriesL10nMessages.NO',
        defaultMessage: 'Norway',
        description: '[Type: label][Vis: high] - country label for Norway',
    },
    COUNTRY_OM: {
        id: 'countriesL10nMessages.OM',
        defaultMessage: 'Oman',
        description: '[Type: label][Vis: high] - country label for Oman',
    },
    COUNTRY_PK: {
        id: 'countriesL10nMessages.PK',
        defaultMessage: 'Pakistan',
        description: '[Type: label][Vis: high] - country label for Pakistan',
    },
    COUNTRY_PW: {
        id: 'countriesL10nMessages.PW',
        defaultMessage: 'Palau',
        description: '[Type: label][Vis: high] - country label for Palau',
    },
    COUNTRY_PS: {
        id: 'countriesL10nMessages.PS',
        defaultMessage: 'Palestine',
        description: '[Type: label][Vis: high] - country label for Palestine, State of',
    },
    COUNTRY_PA: {
        id: 'countriesL10nMessages.PA',
        defaultMessage: 'Panama',
        description: '[Type: label][Vis: high] - country label for Panama',
    },
    COUNTRY_PG: {
        id: 'countriesL10nMessages.PG',
        defaultMessage: 'Papua New Guinea',
        description: '[Type: label][Vis: high] - country label for Papua New Guinea',
    },
    COUNTRY_PY: {
        id: 'countriesL10nMessages.PY',
        defaultMessage: 'Paraguay',
        description: '[Type: label][Vis: high] - country label for Paraguay',
    },
    COUNTRY_PE: {
        id: 'countriesL10nMessages.PE',
        defaultMessage: 'Peru',
        description: '[Type: label][Vis: high] - country label for Peru',
    },
    COUNTRY_PH: {
        id: 'countriesL10nMessages.PH',
        defaultMessage: 'Philippines',
        description: '[Type: label][Vis: high] - country label for Philippines',
    },
    COUNTRY_PL: {
        id: 'countriesL10nMessages.PL',
        defaultMessage: 'Poland',
        description: '[Type: label][Vis: high] - country label for Poland',
    },
    COUNTRY_PT: {
        id: 'countriesL10nMessages.PT',
        defaultMessage: 'Portugal',
        description: '[Type: label][Vis: high] - country label for Portugal',
    },
    COUNTRY_PR: {
        id: 'countriesL10nMessages.PR',
        defaultMessage: 'Puerto Rico',
        description: '[Type: label][Vis: high] - country label for Puerto Rico',
    },
    COUNTRY_QA: {
        id: 'countriesL10nMessages.QA',
        defaultMessage: 'Qatar',
        description: '[Type: label][Vis: high] - country label for Qatar',
    },
    COUNTRY_RE: {
        id: 'countriesL10nMessages.RE',
        defaultMessage: 'Réunion',
        description: '[Type: label][Vis: high] - country label for Réunion',
    },
    COUNTRY_RO: {
        id: 'countriesL10nMessages.RO',
        defaultMessage: 'Romania',
        description: '[Type: label][Vis: high] - country label for Romania',
    },
    COUNTRY_RW: {
        id: 'countriesL10nMessages.RW',
        defaultMessage: 'Rwanda',
        description: '[Type: label][Vis: high] - country label for Rwanda',
    },
    COUNTRY_BL: {
        id: 'countriesL10nMessages.BL',
        defaultMessage: 'Saint Barthélemy',
        description: '[Type: label][Vis: high] - country label for Saint Barthélemy',
    },
    COUNTRY_SH: {
        id: 'countriesL10nMessages.SH',
        defaultMessage: 'Saint Helena',
        description: '[Type: label][Vis: high] - country label for Saint Helena',
    },
    COUNTRY_KN: {
        id: 'countriesL10nMessages.KN',
        defaultMessage: 'Saint Kitts and Nevis',
        description: '[Type: label][Vis: high] - country label for Saint Kitts and Nevis',
    },
    COUNTRY_LC: {
        id: 'countriesL10nMessages.LC',
        defaultMessage: 'Saint Lucia',
        description: '[Type: label][Vis: high] - country label for Saint Lucia',
    },
    COUNTRY_MF: {
        id: 'countriesL10nMessages.MF',
        defaultMessage: 'Saint Martin (French part)',
        description: '[Type: label][Vis: high] - country label for Saint Martin (French part)',
    },
    COUNTRY_PM: {
        id: 'countriesL10nMessages.PM',
        defaultMessage: 'Saint Pierre and Miquelon',
        description: '[Type: label][Vis: high] - country label for Saint Pierre and Miquelon',
    },
    COUNTRY_VC: {
        id: 'countriesL10nMessages.VC',
        defaultMessage: 'Saint Vincent and the Grenadines',
        description: '[Type: label][Vis: high] - country label for Saint Vincent and the Grenadines',
    },
    COUNTRY_WS: {
        id: 'countriesL10nMessages.WS',
        defaultMessage: 'Samoa',
        description: '[Type: label][Vis: high] - country label for Samoa',
    },
    COUNTRY_SM: {
        id: 'countriesL10nMessages.SM',
        defaultMessage: 'San Marino',
        description: '[Type: label][Vis: high] - country label for San Marino',
    },
    COUNTRY_ST: {
        id: 'countriesL10nMessages.ST',
        defaultMessage: 'Sao Tome and Principe',
        description: '[Type: label][Vis: high] - country label for Sao Tome and Principe',
    },
    COUNTRY_SA: {
        id: 'countriesL10nMessages.SA',
        defaultMessage: 'Saudi Arabia',
        description: '[Type: label][Vis: high] - country label for Saudi Arabia',
    },
    COUNTRY_SN: {
        id: 'countriesL10nMessages.SN',
        defaultMessage: 'Senegal',
        description: '[Type: label][Vis: high] - country label for Senegal',
    },
    COUNTRY_RS: {
        id: 'countriesL10nMessages.RS',
        defaultMessage: 'Serbia',
        description: '[Type: label][Vis: high] - country label for Serbia',
    },
    COUNTRY_SC: {
        id: 'countriesL10nMessages.SC',
        defaultMessage: 'Seychelles',
        description: '[Type: label][Vis: high] - country label for Seychelles',
    },
    COUNTRY_SL: {
        id: 'countriesL10nMessages.SL',
        defaultMessage: 'Sierra Leone',
        description: '[Type: label][Vis: high] - country label for Sierra Leone',
    },
    COUNTRY_SG: {
        id: 'countriesL10nMessages.SG',
        defaultMessage: 'Singapore',
        description: '[Type: label][Vis: high] - country label for Singapore',
    },
    COUNTRY_SX: {
        id: 'countriesL10nMessages.SX',
        defaultMessage: 'Sint Maarten',
        description: '[Type: label][Vis: high] - country label for Sint Maarten (Dutch part)',
    },
    COUNTRY_SK: {
        id: 'countriesL10nMessages.SK',
        defaultMessage: 'Slovakia',
        description: '[Type: label][Vis: high] - country label for Slovakia',
    },
    COUNTRY_SI: {
        id: 'countriesL10nMessages.SI',
        defaultMessage: 'Slovenia',
        description: '[Type: label][Vis: high] - country label for Slovenia',
    },
    COUNTRY_SB: {
        id: 'countriesL10nMessages.SB',
        defaultMessage: 'Solomon Islands',
        description: '[Type: label][Vis: high] - country label for Solomon Islands',
    },
    COUNTRY_SO: {
        id: 'countriesL10nMessages.SO',
        defaultMessage: 'Somalia',
        description: '[Type: label][Vis: high] - country label for Somalia',
    },
    COUNTRY_ZA: {
        id: 'countriesL10nMessages.ZA',
        defaultMessage: 'South Africa',
        description: '[Type: label][Vis: high] - country label for South Africa',
    },
    COUNTRY_KR: {
        id: 'countriesL10nMessages.KR',
        defaultMessage: 'South Korea',
        description: '[Type: label][Vis: high] - country label for South Korea',
    },
    COUNTRY_SS: {
        id: 'countriesL10nMessages.SS',
        defaultMessage: 'South Sudan',
        description: '[Type: label][Vis: high] - country label for South Sudan',
    },
    COUNTRY_ES: {
        id: 'countriesL10nMessages.ES',
        defaultMessage: 'Spain',
        description: '[Type: label][Vis: high] - country label for Spain',
    },
    COUNTRY_LK: {
        id: 'countriesL10nMessages.LK',
        defaultMessage: 'Sri Lanka',
        description: '[Type: label][Vis: high] - country label for Sri Lanka',
    },
    COUNTRY_SR: {
        id: 'countriesL10nMessages.SR',
        defaultMessage: 'Suriname',
        description: '[Type: label][Vis: high] - country label for Suriname',
    },
    COUNTRY_SJ: {
        id: 'countriesL10nMessages.SJ',
        defaultMessage: 'Svalbard and Jan Mayen',
        description: '[Type: label][Vis: high] - country label for Svalbard and Jan Mayen',
    },
    COUNTRY_SZ: {
        id: 'countriesL10nMessages.SZ',
        defaultMessage: 'Swaziland',
        description: '[Type: label][Vis: high] - country label for Swaziland',
    },
    COUNTRY_SE: {
        id: 'countriesL10nMessages.SE',
        defaultMessage: 'Sweden',
        description: '[Type: label][Vis: high] - country label for Sweden',
    },
    COUNTRY_CH: {
        id: 'countriesL10nMessages.CH',
        defaultMessage: 'Switzerland',
        description: '[Type: label][Vis: high] - country label for Switzerland',
    },
    COUNTRY_TW: {
        id: 'countriesL10nMessages.TW',
        defaultMessage: 'Taiwan',
        description: '[Type: label][Vis: high] - country label for Taiwan',
    },
    COUNTRY_TJ: {
        id: 'countriesL10nMessages.TJ',
        defaultMessage: 'Tajikistan',
        description: '[Type: label][Vis: high] - country label for Tajikistan',
    },
    COUNTRY_TZ: {
        id: 'countriesL10nMessages.TZ',
        defaultMessage: 'Tanzania',
        description: '[Type: label][Vis: high] - country label for Tanzania',
    },
    COUNTRY_TH: {
        id: 'countriesL10nMessages.TH',
        defaultMessage: 'Thailand',
        description: '[Type: label][Vis: high] - country label for Thailand',
    },
    COUNTRY_TL: {
        id: 'countriesL10nMessages.TL',
        defaultMessage: 'Timor-Leste',
        description: '[Type: label][Vis: high] - country label for Timor-Leste',
    },
    COUNTRY_TG: {
        id: 'countriesL10nMessages.TG',
        defaultMessage: 'Togo',
        description: '[Type: label][Vis: high] - country label for Togo',
    },
    COUNTRY_TK: {
        id: 'countriesL10nMessages.TK',
        defaultMessage: 'Tokelau',
        description: '[Type: label][Vis: high] - country label for Tokelau',
    },
    COUNTRY_TO: {
        id: 'countriesL10nMessages.TO',
        defaultMessage: 'Tonga',
        description: '[Type: label][Vis: high] - country label for Tonga',
    },
    COUNTRY_TT: {
        id: 'countriesL10nMessages.TT',
        defaultMessage: 'Trinidad and Tobago',
        description: '[Type: label][Vis: high] - country label for Trinidad and Tobago',
    },
    COUNTRY_TA: {
        id: 'countriesL10nMessages.TA',
        defaultMessage: 'Tristan da Cunha',
        description: '[Type: label][Vis: high] - country label for Tristan da Cunha',
    },
    COUNTRY_TN: {
        id: 'countriesL10nMessages.TN',
        defaultMessage: 'Tunisia',
        description: '[Type: label][Vis: high] - country label for Tunisia',
    },
    COUNTRY_TR: {
        id: 'countriesL10nMessages.TR',
        defaultMessage: 'Turkey',
        description: '[Type: label][Vis: high] - country label for Turkey',
    },
    COUNTRY_TM: {
        id: 'countriesL10nMessages.TM',
        defaultMessage: 'Turkmenistan',
        description: '[Type: label][Vis: high] - country label for Turkmenistan',
    },
    COUNTRY_TC: {
        id: 'countriesL10nMessages.TC',
        defaultMessage: 'Turks and Caicos Islands',
        description: '[Type: label][Vis: high] - country label for Turks and Caicos Islands',
    },
    COUNTRY_TV: {
        id: 'countriesL10nMessages.TV',
        defaultMessage: 'Tuvalu',
        description: '[Type: label][Vis: high] - country label for Tuvalu',
    },
    COUNTRY_UG: {
        id: 'countriesL10nMessages.UG',
        defaultMessage: 'Uganda',
        description: '[Type: label][Vis: high] - country label for Uganda',
    },
    COUNTRY_UA: {
        id: 'countriesL10nMessages.UA',
        defaultMessage: 'Ukraine',
        description: '[Type: label][Vis: high] - country label for Ukraine',
    },
    COUNTRY_AE: {
        id: 'countriesL10nMessages.AE',
        defaultMessage: 'United Arab Emirates',
        description: '[Type: label][Vis: high] - country label for United Arab Emirates',
    },
    COUNTRY_GB: {
        id: 'countriesL10nMessages.GB',
        defaultMessage: 'United Kingdom',
        description: '[Type: label][Vis: high] - country label for United Kingdom',
    },
    COUNTRY_US: {
        id: 'countriesL10nMessages.US',
        defaultMessage: 'United States',
        description: '[Type: label][Vis: high] - country label for United States',
    },
    COUNTRY_UY: {
        id: 'countriesL10nMessages.UY',
        defaultMessage: 'Uruguay',
        description: '[Type: label][Vis: high] - country label for Uruguay',
    },
    COUNTRY_UZ: {
        id: 'countriesL10nMessages.UZ',
        defaultMessage: 'Uzbekistan',
        description: '[Type: label][Vis: high] - country label for Uzbekistan',
    },
    COUNTRY_VU: {
        id: 'countriesL10nMessages.VU',
        defaultMessage: 'Vanuatu',
        description: '[Type: label][Vis: high] - country label for Vanuatu',
    },
    COUNTRY_VE: {
        id: 'countriesL10nMessages.VE',
        defaultMessage: 'Venezuela',
        description: '[Type: label][Vis: high] - country label for Venezuela',
    },
    COUNTRY_VN: {
        id: 'countriesL10nMessages.VN',
        defaultMessage: 'Vietnam',
        description: '[Type: label][Vis: high] - country label for Vietnam',
    },
    COUNTRY_VI: {
        id: 'countriesL10nMessages.VI',
        defaultMessage: 'Virgin Islands, U.S.',
        description: '[Type: label][Vis: high] - country label for Virgin Islands, U.S.',
    },
    COUNTRY_VG: {
        id: 'countriesL10nMessages.VG',
        defaultMessage: 'Virgin Islands, British',
        description: '[Type: label][Vis: high] - country label for Virgin Islands, British',
    },
    COUNTRY_WF: {
        id: 'countriesL10nMessages.WF',
        defaultMessage: 'Wallis and Futuna',
        description: '[Type: label][Vis: high] - country label for Wallis and Futuna',
    },
    COUNTRY_EH: {
        id: 'countriesL10nMessages.EH',
        defaultMessage: 'Western Sahara',
        description: '[Type: label][Vis: high] - country label for Western Sahara',
    },
    COUNTRY_YE: {
        id: 'countriesL10nMessages.YE',
        defaultMessage: 'Yemen',
        description: '[Type: label][Vis: high] - country label for Yemen',
    },
    COUNTRY_ZM: {
        id: 'countriesL10nMessages.ZM',
        defaultMessage: 'Zambia',
        description: '[Type: label][Vis: high] - country label for Zambia',
    },
    COUNTRY_ZW: {
        id: 'countriesL10nMessages.ZW',
        defaultMessage: 'Zimbabwe',
        description: '[Type: label][Vis: high] - country label for Zimbabwe',
    },
});

const useStyles$7 = createUseStyles((theme) => {
    const { fontFamily = 'inherit', color = 'inherit', fontSize = 'inherit' } = theme.questionBody ?? {};
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
    // for partial theme layout the breakpoint to switch the layout is larger than full theme
    const layoutBreakpoint = theme.layout.includes('PARTIAL')
        ? theme.breakpoints.sm.max
        : theme.breakpoints.md.max;
    return {
        rowBlock: {
            [`@media (min-width: ${layoutBreakpoint})`]: {
                width: '33.3% !important',
            },
            flexDirection: 'row',
            float: 'left',
        },
        addressBlock: {
            display: 'flex',
            flexDirection: 'column',
            padding: '0px 14px 8px 0px',
            width: '100%',
        },
        addressLabel: {
            paddingBottom: '8px',
            fontFamily: theme.questionBody.fontFamily ?? {},
            fontSize: theme.questionBody.fontSize,
            color,
        },
        addressContainer: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
        },
        selectInput: {
            border: `1px solid ${theme.answerColor} !important`,
            height: '40px',
            width: '100%',
            marginBottom: '8px',
            padding: '6px',
            backgroundColor: '#fff',
            fontSize,
            [breakpointMedium]: {
                fontSize: '16px',
            },
            fontFamily,
            borderRadius: '0px !important',
            '&:hover, &:focus-within': {
                outline: `2px solid ${theme.primaryAccentColor}`,
                border: `1px solid ${theme.answerColor}`,
            },
            // the select tag in the NativeSelect does not take in classNames
            '& > select': {
                width: '100%',
                border: 'none',
                outline: 'none',
                fontSize,
                [breakpointMedium]: {
                    fontSize: '16px',
                },
                '&:focus-within, &:hover': {
                    border: 'none',
                    outline: 'none',
                },
            },
        },
        addressInput: {
            border: `1px solid ${theme.answerColor}`,
            height: '40px',
            width: '100%',
            marginBottom: '8px',
            fontSize,
            [breakpointMedium]: {
                fontSize: '16px',
            },
            fontFamily,
            padding: '6px',
            '&::placeholder': {
                fontWeight: fontWeightOptions.regular,
                fontFamily,
                fontSize,
                color: '#999',
            },
        },
        requiredAsterisk: ({ isRTL }) => ({
            display: 'inline-block',
            margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0',
        }),
    };
});

const COPY$4 = defineMessages({
    SELECT_COUNTRY: {
        id: 'AddressField.selectCountry',
        defaultMessage: 'Select country',
        description: '[Type: label][Vis: high] - label for default value for country dropdown',
    },
});
function AddressField({ id: questionId, required, disabled, readOnly, responses: defaultResponses = [], onChange, size = 20, answerOptions = [], showStatesDropdown, defaultCountry = 'us', ...fieldProps }) {
    const { error, errorId = createErrorId(questionId), title: { id: titleId }, } = fieldProps;
    const { isRTL } = useContext(L10NContext);
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value;
    };
    const handleChange = (e) => {
        const responses = e.target.value
            ? [{ id: e.target.id, value: e.target.value }]
            : [];
        onChange?.(responses);
    };
    const { addressLabel, addressInput, addressBlock, rowBlock, addressContainer, requiredAsterisk, selectInput, } = useStyles$7({ isRTL });
    // the following fields are styled different from the street addresses
    const rowSubtypes = ['state', 'zip', 'city', 'country'];
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, ...fieldProps },
        React__default.createElement("div", { className: addressContainer }, answerOptions.map((option) => {
            const isStateLabel = option.subtype === 'state_label';
            const isCountryLabel = option.subtype === 'country_label';
            const showDropdown = (isStateLabel && showStatesDropdown) || isCountryLabel;
            const optionsList = isCountryLabel ? COUNTRY_CODES : US_STATES;
            let defaultValue = getDefaultValueById(option.id);
            // if there is no value for country set, use the defaultCountry value
            defaultValue =
                isCountryLabel && defaultValue === undefined
                    ? defaultCountry
                    : defaultValue;
            return (option?.visible && (React__default.createElement("div", { key: option.id, className: classNames(rowSubtypes.some((el) => option.subtype.includes(el))
                    ? rowBlock
                    : {}, addressBlock) }, showDropdown ? (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("label", { className: addressLabel, htmlFor: `${questionId}_${option.id}` },
                    option.required && (React__default.createElement("span", { "aria-hidden": true, className: requiredAsterisk }, "*")),
                    React__default.createElement(RichText, { element: "span", text: option.label })),
                React__default.createElement(NativeSelect, { id: `${questionId}_${option.id}`, name: `${questionId}_${option.id}`, disabled: option.disabled && disabled, onChange: handleChange, className: classNames(selectInput, 'address-dropdown') },
                    React__default.createElement(SelectAnswerOption, { selected: true, label: isCountryLabel
                            ? t(COPY$4.SELECT_COUNTRY)
                            : 'Select state', value: "" }),
                    ' ',
                    optionsList?.map((choice) => {
                        const countryCode = choice.toLowerCase();
                        const value = isCountryLabel
                            ? t(COPY$5[`COUNTRY_${choice}`])
                            : choice;
                        return (React__default.createElement(SelectAnswerOption, { key: `${option.id}_${countryCode}`, value: countryCode, selected: countryCode === defaultValue, label: value }));
                    })))) : (React__default.createElement(React__default.Fragment, null,
                React__default.createElement("label", { className: addressLabel, htmlFor: `${questionId}_${option.id}` },
                    option.required && React__default.createElement("span", { "aria-hidden": true }, "* "),
                    React__default.createElement(RichText, { element: "span", text: option.label })),
                React__default.createElement(TextInput$1, { id: `${questionId}_${option.id}`, name: `${questionId}_${option.id}`, className: addressInput, defaultValue: defaultValue, required: option?.required && required, disabled: option?.disabled && disabled, readOnly: option?.readOnly && readOnly, placeholder: option.placeholder ?? '', size: size, onChange: handleChange, "aria-invalid": !!error, "aria-describedby": error && errorId, "aria-labelledby": titleId, maxLength: TEXT_INPUT_MAX_CHARS }))))));
        }))));
}
var AddressField$1 = withErrorBoundary(AddressField);

const useStyles$6 = createUseStyles((theme) => {
    return {
        clearableTextInputContainer: {
            display: 'flex',
            position: 'relative',
            '&:hover $textInput': {
                outline: `2px solid ${theme.primaryAccentColor}`,
            },
        },
        textInput: ({ isRTL, maxLength }) => ({
            width: `calc(${maxLength + 1}ch + 25px)`,
            height: 30,
            fontSize: 16,
            textAlign: isRTL ? 'right' : 'left',
            borderRadius: 3,
            padding: isRTL ? [0, 10, 0, 25] : [0, 25, 0, 10],
            margin: [10, 0, 0, 5],
        }),
        clearButton: ({ isRTL }) => ({
            position: 'absolute',
            right: isRTL ? 'auto' : 5,
            left: isRTL ? 10 : 'auto',
            top: 15,
            backgroundColor: theme.secondaryAccentColor,
            borderRadius: 20,
            border: 'none',
            color: theme.primaryAccentColor,
            cursor: 'pointer',
            height: 20,
            width: 20,
            '&:hover': {
                color: theme.secondaryAccentColor,
                backgroundColor: theme.primaryAccentColor,
            },
        }),
        clearButtonIcon: {
            width: 'inherit',
            height: 'inherit',
        },
        visuallyHidden,
    };
});

function ClearableTextInput({ id, className, value = '', onClear, clearVisible, clearButtonLabelText, maxLength = 8, ...props }) {
    const { isRTL } = useContext(L10NContext);
    const { clearableTextInputContainer, textInput, clearButton, clearButtonIcon, visuallyHidden, } = useStyles$6({ isRTL, maxLength });
    const textBoxRef = useRef(null);
    const clearButtonClick = () => {
        onClear?.();
        textBoxRef.current?.focus();
    };
    return (React__default.createElement("div", { className: classNames(clearableTextInputContainer, className) },
        React__default.createElement(TextInput$1, { id: id, className: classNames(textInput), maxLength: maxLength, size: maxLength, ref: textBoxRef, value: value, ...props }),
        clearVisible && (React__default.createElement("button", { type: "button", tabIndex: -1, onClick: clearButtonClick, className: clearButton },
            React__default.createElement(CloseOutlineIcon$1, { className: clearButtonIcon }),
            React__default.createElement("span", { className: visuallyHidden }, clearButtonLabelText)))));
}

const useStyles$5 = createUseStyles((theme) => {
    const fontWeightOptions = getFontWeights(theme.questionBody.fontFamily);
    return {
        slider: {
            '-webkit-appearance': 'none',
            appearance: 'none',
            background: 'transparent',
            cursor: 'pointer',
            width: '100%',
            '&:focus': {
                outline: 'none',
                // Chrome, Safari, Opera, Edge focus style
                '&::-webkit-slider-thumb': {
                    border: ['1px', 'solid', theme.answerColor],
                    outline: [3, 'solid', theme.answerColor],
                    outlineOffset: '0.2rem',
                },
                // Firefox focus style
                '&::-moz-range-thumb': {
                    border: ['1px', 'solid', theme.answerColor],
                    outline: [3, 'solid', theme.answerColor],
                    outlineOffset: '0.2rem',
                },
            },
            // Chrome, Safari, Opera and Edge Chromium styles
            // slider track
            '&::-webkit-slider-runnable-track': {
                backgroundColor: '#ddd',
                border: [1, 'solid', theme.answerColor],
                borderRadius: '0.5rem',
                height: '0.75rem',
            },
            // slider thumb
            '&::-webkit-slider-thumb': {
                '-webkit-appearance': 'none',
                appearance: 'none',
                marginTop: '-6px',
                borderRadius: '3rem',
                border: ['1px', 'solid', theme.answerColor],
                backgroundColor: theme.primaryAccentColor,
                height: '1.5rem',
                width: '1.5rem',
            },
            // Firefox styles
            // slider track
            '&::-moz-range-track': {
                backgroundColor: '#ddd',
                border: [1, 'solid', theme.answerColor],
                borderRadius: '0.5rem',
                height: '0.75rem',
            },
            // slider thumb
            '&::-moz-range-thumb': {
                borderRadius: '3rem',
                border: [1, 'solid', theme.answerColor],
                backgroundColor: theme.primaryAccentColor,
                height: '1.25rem',
                width: '1.25rem',
            },
        },
        labelContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: 10,
        },
        labelItem: {
            color: theme.questionColor,
            fontFamily: theme.questionBody.fontFamily,
            fontSize: '14px !important',
            fontWeight: theme.questionBody.fontWeight,
        },
        container: {
            display: 'flex',
            gap: '0 25px',
            flexFlow: 'row wrap',
            justifyContent: 'space-between',
        },
        textInputContainer: {
            margin: 0,
        },
        sliderContainer: {
            flexGrow: 4,
        },
        alertMessageContainer: {
            width: '100%',
        },
        alertMessage: {
            display: 'flex',
            width: '100%',
            alignSelf: 'flex-end',
            alignItems: 'center',
            color: theme.questionColor,
            marginTop: 20,
            fontFamily: theme.questionBody.fontFamily,
            fontSize: 16,
            fontWeight: fontWeightOptions.regular,
        },
        alertMessageIcon: {
            margin: [1, 3, 0, 0],
        },
    };
});

function RangeSlider({ className, minValue, maxValue, stepSize, labels, hideTextBox, startPosition, defaultValue, clearButtonLabelText, stepMessage, errorMessage, onChange, }) {
    const { slider, labelContainer, labelItem, container, sliderContainer, textInputContainer, alertMessage, alertMessageIcon, alertMessageContainer, } = useStyles$5();
    const startValue = defaultValue || startPosition;
    const [responseValue, setResponseValue] = useState('');
    const [rangeValue, setRangeValue] = useState(startValue.toString());
    const [textValue, setTextValue] = useState(defaultValue || '');
    const [showStepMessage, setShowStepMessage] = useState(false);
    const [showInvalidEntryMessage, setShowInvalidEntryMessage] = useState(false);
    const { left: minLabelText, middle: middleLabelText, right: maxLabelText, } = labels;
    const changeValue = (value) => {
        const intVal = parseInt(value, 10);
        let responseVal = '';
        let textBoxVal = '';
        let displayStepMessage = false;
        let displayInvalidMessage = false;
        if (intVal < minValue) {
            textBoxVal = value;
            displayInvalidMessage = true;
        }
        else if (intVal > maxValue) {
            textBoxVal = value;
            displayInvalidMessage = true;
        }
        else if (value === '' || (intVal - minValue) % stepSize === 0) {
            responseVal = value;
            textBoxVal = value;
        }
        else if (Number.isNaN(intVal)) {
            displayInvalidMessage = true;
            textBoxVal = value;
        }
        else {
            // The value lies between two step points.
            // Determine the closest step point and make that the current value.
            responseVal = (Math.round((intVal - minValue) / stepSize) * stepSize +
                minValue).toString();
            textBoxVal = responseVal;
            displayStepMessage = true;
        }
        setRangeValue(responseVal || startPosition.toString());
        setResponseValue(responseVal);
        setTextValue(textBoxVal);
        onChange?.(responseVal);
        setShowStepMessage(displayStepMessage);
        setShowInvalidEntryMessage(displayInvalidMessage);
    };
    const valueChangeHandler = (answerValue) => {
        if (answerValue !== responseValue) {
            changeValue(answerValue);
        }
    };
    const clearText = () => {
        changeValue('');
    };
    const getAccessibleValueText = (rValue) => {
        const value = parseInt(rValue, 10);
        if (value === minValue) {
            return `${value}, ${minLabelText}`;
        }
        if (value === Math.round(maxValue / 2)) {
            return `${value}, ${middleLabelText}`;
        }
        if (value === maxValue) {
            return `${value}, ${maxLabelText}`;
        }
        return rValue;
    };
    const invalidEntryMessage = errorMessage
        .replace(/\{0}/, minValue.toString())
        .replace(/\{1}/, maxValue.toString());
    return (React__default.createElement("div", { className: classNames(className, container) },
        React__default.createElement("div", { className: sliderContainer },
            React__default.createElement("div", { className: labelContainer },
                React__default.createElement("div", { className: labelItem }, minLabelText),
                React__default.createElement("div", { className: labelItem }, middleLabelText),
                React__default.createElement("div", { className: labelItem }, maxLabelText)),
            React__default.createElement("input", { type: "range", "aria-valuetext": getAccessibleValueText(rangeValue), value: rangeValue, className: slider, min: minValue, max: maxValue, step: stepSize, onChange: (e) => {
                    valueChangeHandler(e.target.value);
                } })),
        !hideTextBox && (React__default.createElement(ClearableTextInput, { className: textInputContainer, onChange: (e) => {
                setTextValue(e.target.value);
            }, onBlur: (e) => {
                valueChangeHandler(e.target.value);
            }, onClear: clearText, value: textValue, maxLength: 8, clearVisible: !!textValue, inputMode: "numeric", pattern: "[0-9]*", clearButtonLabelText: clearButtonLabelText })),
        React__default.createElement("div", { "aria-live": "assertive", className: alertMessageContainer }, (showStepMessage || showInvalidEntryMessage) && (React__default.createElement("div", { className: alertMessage },
            React__default.createElement(InfoIcon$1, { className: alertMessageIcon }),
            showStepMessage && React__default.createElement("span", null, stepMessage),
            showInvalidEntryMessage && React__default.createElement("span", null, invalidEntryMessage))))));
}

const COPY$3 = defineMessages({
    SLIDER_CLEAR_BUTTON_LABEL: {
        id: 'Slider.CLEAR_BUTTON_LABEL',
        defaultMessage: 'Clear',
        description: '[Type: label][Vis: low] - screen reader only label for clear button',
    },
    SLIDER_STEP_MESSAGE: {
        id: 'Slider.STEP_MESSAGE',
        defaultMessage: "We adjusted the number you entered based on the slider's scale.",
        description: '[Type: message][Vis: medium] - Message displayed when the entered value needs to be moved to the next closest step',
    },
});
function Slider({ id: questionId, responses: defaultResponses = [], onChange, scaleOptions, hideNumericalInput, disabled: _dis, required: _req, readOnly: _ro, ...fieldProps }) {
    const defaultValue = useMemo(() => defaultResponses.find((r) => !!r.value)?.value ?? '', 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const [, setResponseValue] = useState(defaultResponses);
    const clearButtonLabelText = t(COPY$3.SLIDER_CLEAR_BUTTON_LABEL);
    const stepMessage = t(COPY$3.SLIDER_STEP_MESSAGE);
    const valueChanged = (selectedValue) => {
        const responses = selectedValue
            ? [{ id: questionId, value: selectedValue }]
            : [];
        setResponseValue(responses);
        onChange?.(responses);
    };
    const errorId = createErrorId(questionId);
    return (React__default.createElement(QuestionField, { id: questionId, errorId: errorId, "data-testid": "SliderQuestionType", ...fieldProps },
        React__default.createElement(RangeSlider, { defaultValue: parseInt(defaultValue, 10), hideTextBox: hideNumericalInput, stepMessage: stepMessage, clearButtonLabelText: clearButtonLabelText, onChange: valueChanged, ...scaleOptions })));
}

const useStyles$4 = createUseStyles({
    radioIconInput: {
        /* needed to override styles in BaseInput which effects opacity of icons */
        '& svg': {
            opacity: 1,
        },
    },
});

function RadioIconInput({ className, icon, ...props }, forwardedRef) {
    const { inputProps } = useInputStyles(props);
    const { radioIconInput } = useStyles$4();
    return (React__default.createElement(BaseInput$1, { type: "radio", className: classNames(radioIconInput, className), icon: icon, ref: forwardedRef, ...inputProps }));
}
var RadioIconInput$1 = forwardRef(RadioIconInput);

const useStyles$3 = createUseStyles((theme) => ({
    visuallyHidden,
    inputStyles: {
        textAlign: 'center',
        margin: 'auto',
        marginTop: '4px',
        [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
            margin: '5px',
            marginTop: '4px',
        },
        '&:hover': {
            outline: `1px dashed ${theme.questionColor}`,
            marginTop: '5px',
        },
        '&:focus-within': {
            outline: `1px solid ${theme.questionColor}`,
            marginTop: '5px',
        },
        color: ({ selected = false, symbolColor = '#F5A623' }) => {
            if (selected) {
                return symbolColor;
            }
            return '#fff';
        },
    },
}));

function StarRatingItem({ onChange, selected, checked: checkedProp, name: nameProp, symbolColor, ...props }, ref) {
    let checked = checkedProp;
    let name = nameProp;
    const radioGroup = useRadioGroup();
    if (radioGroup) {
        if (typeof checked === 'undefined') {
            checked = radioGroup.value === props.value;
        }
        if (typeof name === 'undefined') {
            name = radioGroup.name;
        }
    }
    const { visuallyHidden, inputStyles } = useStyles$3({
        checked,
        selected,
        symbolColor: symbolColor ?? '',
    });
    const handleChange = (e) => {
        const { id, checked: isChecked, value } = e.target;
        radioGroup?.onChange({ id, checked: isChecked, value });
        onChange?.({ id, checked: isChecked, value });
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(RadioIconInput$1, { name: name, className: inputStyles, onChange: handleChange, checked: checked, ref: ref, ...props }),
        React__default.createElement("label", { className: visuallyHidden, htmlFor: props.id }, props.label)));
}
var StarRatingItem$1 = forwardRef(StarRatingItem);

const useStyles$2 = createUseStyles((theme) => {
    const { bgColor: listItemBackground } = theme.input;
    const layouts = [
        'BACKGROUND_FULL_TEXT_RIGHT',
        'BACKGROUND_FULL_TEXT_LEFT',
        'BACKGROUND_PARTIAL_LEFT_HALF',
        'BACKGROUND_PARTIAL_RIGHT_HALF',
    ];
    const isHalf = layouts.some((str) => theme.layout.includes(str));
    return {
        visuallyHidden,
        ratingGroupStyle: ({ hasLabels }) => ({
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            overflowX: 'auto',
            overflowY: 'hidden',
            // half layout styling
            flexDirection: isHalf && hasLabels ? 'column' : 'row',
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                flexDirection: hasLabels ? 'column' : 'row',
            },
        }),
        starContainer: ({ hasLabels }) => ({
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            // half layout styling
            flexDirection: isHalf && hasLabels ? 'row-reverse' : 'column',
            marginBottom: isHalf ? '5px' : '0px',
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                ...(hasLabels && {
                    backgroundColor: isDark(theme.questionBody.color || '#fff')
                        ? listItemBackground
                        : 'rgba(255, 255, 255, 0.15)',
                }),
                display: 'flex',
                flexDirection: hasLabels ? 'row-reverse' : 'column',
                marginBottom: '5px',
            },
            [`@media (min-width: ${theme.breakpoints?.sm.min})`]: isHalf && hasLabels
                ? {
                    background: isDark(theme.questionBody.color || '#fff')
                        ? listItemBackground
                        : 'rgba(255, 255, 255, 0.15)',
                }
                : {},
        }),
        starLabel: ({ hasLabels }) => ({
            display: hasLabels ? 'block' : 'none',
            color: theme.questionColor,
            fontFamily: theme.questionBody.fontFamily,
            fontSize: 14,
            fontWeight: theme.questionBody.fontWeight,
            lineHeight: 1.25,
            // half layout styling
            width: isHalf ? '100%' : 'auto',
            marginTop: isHalf ? '8px' : '',
            padding: isHalf ? '0px 4px' : '0 3px',
            overflowWrap: isHalf ? 'anywhere' : 'normal',
            textAlign: isHalf ? 'start' : 'center',
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                width: '100%',
                // overwrite TextInput styles
                fontSize: theme.questionBody.fontSize,
                marginTop: '8px',
                padding: '0px 4px',
                overflowWrap: 'anywhere',
                textAlign: 'start',
            },
        }),
        naLabel: {
            color: theme.questionColor,
            fontFamily: theme.questionBody.fontFamily,
            fontSize: 14,
            fontWeight: theme.questionBody.fontWeight,
            lineHeight: 1.25,
            display: 'inline-block',
            textAlign: 'center',
            marginTop: isHalf ? 8 : 0,
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                marginTop: 8,
                fontSize: theme.questionBody.fontSize,
                padding: '0px 4px',
            },
        },
        starRow: ({ hasLabels }) => ({
            height: '42px',
            width: '100%',
            fontSize: '2em',
            textAlign: 'center',
            backgroundColor: isDark(theme.questionBody.color || '#fff')
                ? listItemBackground
                : 'rgba(255, 255, 255, 0.15)',
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: hasLabels
                ? {
                    backgroundColor: 'transparent',
                    width: 'auto',
                }
                : {},
            [`@media (min-width: ${theme.breakpoints?.sm.min})`]: isHalf && hasLabels
                ? {
                    width: 'auto',
                    backgroundColor: 'transparent',
                }
                : {},
        }),
        naRow: ({ hasLabels }) => ({
            height: '42px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& label': {
                gap: 0,
            },
            backgroundColor: isDark(theme.questionBody.color || '#fff')
                ? listItemBackground
                : 'rgba(255, 255, 255, 0.15)',
            [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
                display: 'flex',
                alignItems: !hasLabels ? 'left' : 'center',
                flexDirection: 'column',
                ...(hasLabels && { backgroundColor: 'transparent' }),
            },
            [`@media (min-width: ${theme.breakpoints?.sm.min})`]: isHalf && hasLabels ? { backgroundColor: 'transparent' } : {},
        }),
        otherOption: {
            marginTop: '10px',
        },
    };
});

const COPY$2 = defineMessages({
    STAR: {
        id: 'StarRating.STAR',
        defaultMessage: '{count} {count, plural, one {star} other {stars}}',
        description: 'Star Symbol Rating Label',
    },
    SMILEY: {
        id: 'StarRating.Smiley',
        defaultMessage: '{count} {count, plural, one {smiley} other {smileys}}',
        description: 'Smiley Symbol Rating Label',
    },
    HEART: {
        id: 'StarRating.Heart',
        defaultMessage: '{count} {count, plural, one {heart} other {hearts}}',
        description: 'Heart Symbol Rating Label',
    },
    THUMB: {
        id: 'StarRating.Thumb',
        defaultMessage: '{count} {count, plural, one {thumb} other {thumbs}}',
        description: 'Thumb Symbol Rating Label',
    },
});
function StarRating({ choices = [], choiceNoneOfTheAbove, choiceComment, disabled, required, id: questionId, rowId, readOnly: _ro, onChange, responses: defaultResponses = [], symbolOptions, ...fieldProps }) {
    const errorId = createErrorId(questionId);
    const [response, setResponse] = useState(defaultResponses);
    /** Filter choices that exist and are visible  */
    const { options, sortableOptions: ratingOptions, notaOption, commentOption, } = useQuestionChoices([...choices, choiceNoneOfTheAbove, choiceComment]);
    const hasLabels = ratingOptions.filter((option) => option.label).length > 0;
    const isSelected = (index) => {
        const selectedResponse = response.find((c) => c.type !== 'COMMENT');
        const selectedIndex = ratingOptions.findIndex((option) => option.id === selectedResponse?.value);
        return selectedIndex >= index;
    };
    const isChecked = (ratingId) => {
        const selectedResponse = response.find((c) => c.type !== 'COMMENT');
        return ratingId === selectedResponse?.value;
    };
    /**
     * Default value for the group
     */
    const defaultValue = useMemo(() => defaultResponses.find((c) => c.type !== 'COMMENT')?.value, 
    // defaultValues should not be changed post render (breaks rules of controlled components)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    /**
     * deselect choice if the question is not required
     */
    const unCheckOption = () => {
        if (required) {
            return;
        }
        const responses = response.filter((c) => c.type === 'COMMENT');
        setResponse(responses);
        onChange?.(responses);
    };
    /**
     * Default value by id
     * used for text-input based choices
     */
    const getDefaultValueById = (id) => {
        const defaultChoice = defaultResponses.find((c) => c.id === id);
        return defaultChoice?.value;
    };
    const handleClick = (e) => {
        const input = e.target;
        input.focus();
        const selectedResponse = response.find((c) => c.type !== 'COMMENT');
        const isCheckedID = selectedResponse?.value === input.id;
        if (isCheckedID && input.checked) {
            e.preventDefault();
            unCheckOption();
        }
    };
    const handleKeyboard = (e) => {
        const selectedResponse = response.find((c) => c.type !== 'COMMENT');
        const isCheckedID = selectedResponse?.value === e.target.id;
        if ((isCheckedID && e.code === 'Space') ||
            /* unCheck when ArrowLeft is pressed while on the first option */
            (selectedResponse?.value === options[0].id && e.code === 'ArrowLeft')) {
            e.preventDefault();
            unCheckOption();
        }
    };
    const handleRadioChange = (e) => {
        const choiceData = ratingOptions.find((c) => e.id === c.id);
        const responses = response.filter((c) => c.type === 'COMMENT');
        if (choiceData) {
            const { type } = choiceData;
            responses.push({ id: rowId, type, value: e.value });
            setResponse(responses);
            onChange?.(responses);
        }
        else if (e.id === notaOption?.id) {
            responses.push({
                id: rowId,
                type: 'NOTA',
                value: e.value,
            });
            setResponse(responses);
            onChange?.(responses);
        }
    };
    const handleCommentChange = (e) => {
        const choiceData = options.find((c) => c.id === e.target.id);
        const responses = response.filter((c) => c.type !== 'COMMENT');
        if (choiceData && !!e.target.value) {
            const { id, type } = choiceData;
            responses.push({ id, type, value: e.target.value });
        }
        setResponse(responses);
        onChange?.(responses);
    };
    const radioGroupProps = {
        disabled,
        name: questionId,
        defaultValue,
        onChange: handleRadioChange,
    };
    const { starLabel, naLabel, ratingGroupStyle, starContainer, naRow, starRow, otherOption, } = useStyles$2({ hasLabels });
    const radioEvents = { onClick: handleClick, onKeyDown: handleKeyboard };
    const getHiddenLabel = (index) => {
        const count = index + 1;
        switch (symbolOptions?.symbol) {
            case 'HEART':
                return t(COPY$2.HEART, {
                    count,
                });
            case 'SMILEY':
                return t(COPY$2.SMILEY, {
                    count,
                });
            case 'THUMB':
                return t(COPY$2.THUMB, {
                    count,
                });
            default:
                return t(COPY$2.STAR, {
                    count,
                });
        }
    };
    const getIcon = () => {
        switch (symbolOptions?.symbol) {
            case 'HEART':
                return React__default.createElement(HeartIcon$1, null);
            case 'SMILEY':
                return React__default.createElement(SmileyIcon$1, null);
            case 'THUMB':
                return React__default.createElement(ThumbsUpIcon$1, null);
            default:
                return React__default.createElement(StarIcon$1, null);
        }
    };
    const itemsEls = useRef([]);
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "StarRatingQuestionType", errorId: errorId, ...fieldProps },
        React__default.createElement(RadioGroup, { ...radioGroupProps },
            React__default.createElement("div", { className: ratingGroupStyle },
                ratingOptions.map((ratingOption, index) => {
                    return (React__default.createElement("div", { className: starContainer, key: ratingOption.id },
                        React__default.createElement(RichText, { element: "div", text: ratingOption.label, className: starLabel, id: `${ratingOption.id}-desc`, onClick: () => {
                                itemsEls.current[index].click();
                            }, onKeyDown: () => {
                                itemsEls.current[index].click();
                            } }),
                        React__default.createElement("div", { className: starRow },
                            React__default.createElement(StarRatingItem$1, { ref: (element) => {
                                    itemsEls.current[index] = element;
                                }, icon: getIcon(), id: ratingOption.id, label: getHiddenLabel(index), checked: isChecked(ratingOption.id), selected: isSelected(index), value: ratingOption.id, ...radioEvents, "aria-describedby": ratingOption.label ? `${ratingOption.id}-desc` : undefined, symbolColor: symbolOptions?.color }))));
                }),
                notaOption && (React__default.createElement("div", { className: starContainer, key: notaOption.id },
                    React__default.createElement(RichText, { element: "div", text: notaOption.label, className: naLabel, id: `${notaOption.id}-desc` }),
                    React__default.createElement("div", { className: naRow },
                        React__default.createElement(Radio$1, { id: notaOption.id, key: notaOption.id, value: notaOption.id, checked: isChecked(notaOption.id), "aria-describedby": fieldProps.error && errorId, inputSize: 20, ...radioEvents }))))),
            commentOption && (React__default.createElement(CommentChoice, { id: commentOption.id, label: commentOption.label, onChange: handleCommentChange, defaultValue: getDefaultValueById(commentOption.id), lineCount: commentOption.linesCount, charCount: commentOption.charsCount, maxLength: TEXT_INPUT_MAX_CHARS, className: otherOption })))));
}

const useStyles$1 = createUseStyles((theme) => {
    const { fontSize, fontFamily, fontWeight, color } = theme.questionBody;
    const iconSize = '40px';
    return {
        srOnly: {
            ...visuallyHidden,
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '75%',
            alignItems: 'center',
        },
        label: {
            fontSize,
            fontFamily,
            fontWeight,
            color: theme.questionColor,
            marginBottom: '14px',
        },
        instructionsContainer: {
            paddingBottom: '15px',
            fontFamily,
            fontSize,
            color: theme.questionColor,
        },
        fileDragArea: {
            display: 'flex',
            padding: '40px 30px',
            flexDirection: 'column',
            alignItems: 'center',
            color: '#404040',
            transition: 'all .2s linear',
            opacity: ({ disabled }) => (disabled ? 0.5 : 1),
            position: 'relative',
            borderRadius: '8px',
            backgroundColor: ({ disabled, dragActive }) => !disabled && dragActive ? '#ABABAB' : '#FFFFFF',
            border: ({ disabled, dragActive }) => !disabled && dragActive ? '2px dashed #ABABAB' : '2px dashed #D0D0D0',
            outline: ({ disabled, dragActive }) => !disabled && dragActive ? '3px solid #404040' : 'none',
            [`@media (min-width: ${theme.breakpoints.sm.min})`]: {
                minWidth: '600px',
                padding: '75px 95px',
            },
        },
        draggingActive: {
            borderColor: '#ABABAB',
            backgroundColor: '#ABABAB',
            outline: '3px solid #404040',
        },
        fileUploadDragDesktop: {
            display: 'inline-block',
            fontWeight: 'bold',
            color: '#404040',
        },
        stateArea: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px',
            [`@media (min-width: ${theme.breakpoints.sm.min})`]: {
                minHeight: '90px',
            },
        },
        stateIcon: {
            fontSize: iconSize,
        },
        removeFileButton: {
            height: '20px',
            borderRadius: '50%',
            padding: '0',
            border: 'none',
            background: 'transparent',
            fontSize: '20px',
            transition: 'all .2s linear',
            color: '#404040',
            cursor: 'pointer',
        },
        removeModalButton: {
            position: 'absolute',
            top: '10px',
            right: '10px',
            height: '32px',
            width: '32px',
            borderRadius: '50%',
            padding: '0',
            border: 'none',
            background: 'transparent',
            fontSize: '20px',
            transition: 'all .2s linear',
            color: '#404040',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
        },
        fileNameState: {
            fontSize: '16px',
            fontFamily,
            color,
            textAlign: 'center',
        },
        fileUploadLabel: {
            position: 'relative',
            marginRight: '5px',
            transition: 'all .2s linear',
            '&:has(input:focus)': {
                outline: '1px solid #0A7CC1',
                boxShadow: '0 0 4px transparent',
                outlineOffset: '5px',
            },
        },
        fileUploadInput: {
            cursor: 'pointer',
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            opacity: 0,
            fontSize: '.1px',
            display: 'none',
        },
        chooseFileContainer: {
            display: 'flex',
            flexDirection: ({ isMobile }) => (isMobile ? 'column' : 'row'),
            alignItems: ({ isMobile }) => (isMobile ? 'stretch' : 'center'),
            gap: '12px',
            fontFamily,
            fontSize,
            color,
        },
        modalInstructions: {
            color: '#333',
            fontWeight: 'bold',
            fontSize: '20px',
            textAlign: 'center',
        },
        fileTypeListParagraph: {
            margin: '0',
            textAlign: 'center',
        },
        loading: {
            width: '25px',
            height: '25px',
            border: '5px solid rgba(0,0,0,0.25)',
            borderBottomColor: '#008323',
            borderRadius: '50%',
            display: 'inline-block',
            boxSizing: 'border-box',
            animation: '$rotation 1s linear infinite',
            margin: ({ isMobile }) => (isMobile ? '0 auto' : '0'),
        },
        '@keyframes rotation': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            width: '100%',
            height: '100%',
            maxWidth: 'none',
            maxHeight: 'none',
            zIndex: 1000,
        },
        trashIcon: {
            color,
            '&:hover, &:focus': {
                color: '#AC145A',
            },
        },
    };
});

const COPY$1 = defineMessages({
    CHOOSE_FILE: {
        id: 'FileUpload.chooseFile',
        defaultMessage: 'Choose File',
        description: '[Type: label][Vis: high] - label for file input when a file has not been uploaded',
    },
    DRAG_DROP: {
        id: 'FileUpload.dragDrop',
        defaultMessage: 'Drag and drop a file here',
        description: '[Type: paragraph][Vis: high] - instruction for file input when a file has not been uploaded',
    },
    NO_FILE_CHOSEN: {
        id: 'FileUpload.noFileChosen',
        defaultMessage: 'No file chosen',
        description: '[Type: label][Vis: high] - label for file text when a file has not been uploaded',
    },
    CHOOSE_OR_DRAG: {
        id: 'FileUpload.chooseDragFile',
        defaultMessage: '<div class={dragClass}>Upload file</div>',
        description: '[Type: label][Vis: high] - label for file input when a file has not been uploaded',
    },
    REPLACE_OR_DRAG: {
        id: 'FileUpload.replaceDragFile',
        defaultMessage: '<div class={dragClass}>Replace file</div>',
        description: '[Type: label][Vis: high] - label for file input when a file has been uploaded',
    },
    FILE_UPLOADING: {
        id: 'FileUpload.fileUploading',
        defaultMessage: 'File uploading',
        description: '[Type: label][Vis: high] - Label for when a file is uploading',
    },
    REMOVE_FILE: {
        id: 'FileUpload.removeFile',
        defaultMessage: 'Remove file',
        description: '[Type: label][Vis: high] - Label for the remove uploaded file button',
    },
    REPLACE_FILE: {
        id: 'FileUpload.replaceFile',
        defaultMessage: 'Replace file',
        description: '[Type: label][Vis: high] - Label for the replace currently selected file button',
    },
    CLOSE_MODAL: {
        id: 'FileUpload.closeModal',
        defaultMessage: 'Close modal',
        description: '[Type: label][Vis: high] - Label for the close modal button',
    },
    FILE_UPLOADED: {
        id: 'FileUpload.fileUploaded',
        defaultMessage: 'File uploaded',
        description: '[Type: label][Vis: high] - Label for when a file has been uploaded',
    },
});
const UPLOAD_STATE = {
    default: 'default',
    uploading: 'uploading',
    error: 'error',
    success: 'success',
};
function FileUpload({ id: questionId, readOnly: _ro, disabled, responses: defaultResponses = [], instructions = 'Optional Instructions', validation, onChange, uploadFile, ...questionFieldProps }) {
    /** possible states are default, uploading, error, success */
    const [state, setState] = useState(defaultResponses[0] ? UPLOAD_STATE.success : UPLOAD_STATE.default);
    const [showModal, setShowModal] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    /** response is the main state data object from tier 1. Example: {id, value: https://s3.aws.com/filename.jpg} */
    const [response, setResponse] = useState(defaultResponses);
    const errorId = createErrorId(questionId);
    const [announcement, setAnnouncement] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const fileInputRef = useRef(null);
    const isMobile = getClientEnvironmentDetails()?.isMobile ?? false;
    const { formats: fileTypes = [], errorMessage: fileTypesError = '' } = validation || {};
    const handleOpenModal = () => {
        setShowModal(true);
    };
    const handleCloseModal = () => {
        setShowModal(false);
    };
    const handleChooseFile = () => {
        if (isMobile) {
            fileInputRef.current?.click();
        }
        else {
            handleOpenModal();
        }
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        if (!disabled) {
            setDragActive(true);
        }
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        if (!disabled) {
            setDragActive(false);
        }
    };
    const setAnnouncementString = (str) => {
        setTimeout(() => {
            setAnnouncement(str);
        }, 5000);
        setAnnouncement('');
    };
    const handleSetError = (errorString) => {
        setState(UPLOAD_STATE.error);
        setResponse([]);
        setAnnouncementString(errorString);
        setErrorMessage(errorString);
    };
    const handleFileUpload = async (f) => {
        if (f && fileTypes.includes(`${f.type.split('/')[1]}`)) {
            setState(UPLOAD_STATE.uploading);
            setShowModal(false);
            setAnnouncementString(t(COPY$1.FILE_UPLOADING));
            const fileResponse = await uploadFile?.(f);
            if (fileResponse) {
                if (fileResponse.error) {
                    handleSetError(fileResponse.error);
                }
                else {
                    setState(UPLOAD_STATE.success);
                    const newResponse = [{ id: questionId, value: fileResponse.url }];
                    setResponse(newResponse);
                    /** value will be an s3.awe.com url */
                    onChange?.(newResponse);
                    setAnnouncementString(t(COPY$1.FILE_UPLOADED));
                }
            }
            /** not sure what to do if there is no file response */
        }
        else {
            handleSetError(fileTypesError);
        }
    };
    const handleDrop = (e) => {
        e.preventDefault();
        if (!disabled) {
            setDragActive(false);
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                handleFileUpload(e.dataTransfer.files[0]);
            }
        }
    };
    const handleFileInput = (e) => {
        if (!disabled && e.target.files && e.target.files.length > 0) {
            handleFileUpload(e.target.files[0]);
            handleCloseModal();
        }
    };
    const handleClearFile = () => {
        setState(UPLOAD_STATE.default);
        setAnnouncementString('');
        fileInputRef.current?.focus();
    };
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    };
    const handleKeyDownFileInput = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
        }
    };
    const { srOnly, container, instructionsContainer, fileDragArea, stateArea, stateIcon, loading, fileNameState, fileUploadDragDesktop, chooseFileContainer, modalInstructions, removeFileButton, removeModalButton, fileUploadInput, fileTypeListParagraph, modalOverlay, trashIcon, } = useStyles$1({ dragActive, disabled, isMobile });
    /** using <div> because sm-intl doesn't allow the use of <span> */
    const chooseFileLabel = t(COPY$1.CHOOSE_OR_DRAG, {
        dragClass: fileUploadDragDesktop,
    }, { html: true });
    const replaceFileLabel = t(COPY$1.REPLACE_OR_DRAG, {
        dragClass: fileUploadDragDesktop,
    }, { html: true });
    return (React__default.createElement(QuestionField, { id: questionId, "data-testid": "FileUpload", errorId: errorId, ...questionFieldProps },
        React__default.createElement("input", { type: "file", id: `${questionId}-fileInput`, ref: fileInputRef, accept: fileTypes.join(), onChange: handleFileInput, "aria-describedby": "fileTypeList", className: fileUploadInput, "data-testid": "FileUpload__FileUploadInput" }),
        React__default.createElement("div", { role: "status", "aria-live": "assertive", "aria-atomic": "true", className: srOnly }, announcement),
        !!instructions && (React__default.createElement("p", { className: instructionsContainer }, instructions)),
        React__default.createElement("div", { className: chooseFileContainer },
            state === 'default' && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Button, { text: t(COPY$1.CHOOSE_FILE), onClick: handleChooseFile, "aria-label": t(COPY$1.CHOOSE_FILE) }),
                React__default.createElement("p", { className: fileNameState },
                    React__default.createElement(T, { desc: COPY$1.NO_FILE_CHOSEN })))),
            state === 'uploading' && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Button, { text: t(COPY$1.CHOOSE_FILE), onClick: handleChooseFile }),
                React__default.createElement("span", { className: loading }))),
            state === 'error' && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Button, { text: t(COPY$1.CHOOSE_FILE), onClick: handleChooseFile, "aria-label": t(COPY$1.CHOOSE_FILE) }),
                React__default.createElement(WarningIcon$1, { color: "#CC0000 " }),
                React__default.createElement("p", { className: fileNameState }, errorMessage))),
            state === 'success' && response[0] && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(Button, { text: t(COPY$1.REPLACE_FILE), onClick: handleChooseFile, "aria-label": t(COPY$1.REPLACE_FILE) }),
                React__default.createElement("p", { className: fileNameState }, response[0].value.split('/').pop()),
                React__default.createElement("button", { type: "button", "aria-label": t(COPY$1.REMOVE_FILE), className: removeFileButton, onClick: handleClearFile, "data-testid": "FileUpload__RemoveFileButton" },
                    React__default.createElement(TrashIcon$1, { title: t(COPY$1.REMOVE_FILE), className: trashIcon }))))),
        showModal && !isMobile && (
        // ignoring eslint rule for a11y because a close button is provided to dismiss the modal overlay
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        React__default.createElement("div", { className: modalOverlay, onClick: handleOverlayClick, role: "dialog", "aria-modal": "true", "aria-label": t(COPY$1.CHOOSE_FILE) },
            React__default.createElement("div", { className: container },
                React__default.createElement("div", { className: classNames(fileDragArea), onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, "data-testid": "FileUpload__DropArea" },
                    React__default.createElement("button", { type: "button", "aria-label": t(COPY$1.CLOSE_MODAL), onClick: handleCloseModal, className: removeModalButton },
                        React__default.createElement(CloseOutlineIcon$1, null)),
                    React__default.createElement("div", { className: stateArea },
                        React__default.createElement("div", { className: stateIcon }, state === 'success' ? (React__default.createElement(CheckOutlineIcon$1, null)) : (React__default.createElement(CloudArrowUpIcon$1, { color: "#404040" }))),
                        state === 'success' ? (React__default.createElement("p", { className: fileNameState },
                            ' ',
                            response[0].value.split('/').pop())) : (React__default.createElement("p", { className: modalInstructions }, t(COPY$1.DRAG_DROP))),
                        React__default.createElement("p", { id: `${questionId}-fileTypeList`, className: fileTypeListParagraph },
                            "Format: ",
                            fileTypes.join(', ').toUpperCase()),
                        React__default.createElement(ThemeProvider, { theme: WrenchTheme },
                            React__default.createElement(Button$1, { buttonType: "button", color: "secondary", variant: "ghost", frameBorder: 1, onKeyDown: handleKeyDownFileInput, onClick: () => {
                                    fileInputRef.current?.click();
                                }, "aria-label": state === 'success'
                                    ? t(COPY$1.CHOOSE_FILE)
                                    : t(COPY$1.REPLACE_FILE), "data-testid": "FileUpload__ChooseFileButton" }, state === 'success' || state === 'uploading'
                                ? replaceFileLabel
                                : chooseFileLabel)))))))));
}

const useStyles = createUseStyles((theme) => {
    // FIXME: every property using inherit should be replaced with the actual theme property
    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            marginTop: '8px',
            alignItems: 'flex-start',
        },
        emailInputContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        emailLabel: {
            paddingBottom: '8px',
            fontFamily: theme.questionBody?.fontFamily ?? 'inherit',
            fontSize: theme.questionBody?.fontSize ?? 'inherit',
            color: theme.questionBody?.color ?? 'inherit',
            maxWidth: 'calc(100% - 32px)',
            marginLeft: 32,
            marginTop: 10,
        },
        emailInput: {
            maxWidth: 'calc(100% - 32px)',
            marginLeft: 32,
        },
    };
});

const COPY = defineMessages({
    CHECKBOX_LABEL: {
        id: 'ToggleableEmailField.checkboxLabel',
        defaultMessage: 'Send me a copy of my responses via email',
        description: '[Type: label][Vis: high] - label for checkbox input',
    },
    EMAIL_LABEL: {
        id: 'ToggleableEmailField.emailLabel',
        defaultMessage: 'Enter your email address',
        description: '[Type: label][Vis: high] - label for text input',
    },
});
function ToggleableEmailField({ responses = [], forcedchecked = false, onBlur, error, onChange, onToggle, id, labels = {}, ...props }) {
    const [checked, setChecked] = useState((responses[0]?.value.length > 0 ?? false) || forcedchecked);
    const [emailValue, setEmailValue] = useState({
        value: responses[0]?.value ?? '',
        isDirty: false,
    });
    const handleChange = (e) => {
        const response = {
            value: e.target.value,
            isDirty: emailValue.value !== e.target.value,
        };
        setEmailValue(response);
        onChange?.(response);
    };
    const handleBlur = (_e) => {
        onBlur?.(emailValue);
    };
    const handleChecked = () => {
        const isChecked = !checked;
        setChecked(isChecked);
        if (!isChecked) {
            const response = {
                value: '',
                isDirty: false,
            };
            setEmailValue(response);
            onChange?.(response);
        }
        onToggle?.(isChecked);
    };
    const { container, emailLabel, emailInput, emailInputContainer, } = useStyles();
    return (React__default.createElement("div", { className: container },
        error && (React__default.createElement(ValidationMessage, { id: `${id}-validation-message` }, error)),
        React__default.createElement(Checkbox$3, { id: `${id}-checkbox`, checked: checked, onChange: handleChecked, name: "toggleable-email-field-checkbox", ...props }, labels?.checkboxLabelValue ?? t(COPY.CHECKBOX_LABEL)),
        checked && (React__default.createElement("div", { id: `view-${id}`, className: emailInputContainer },
            React__default.createElement("label", { htmlFor: `${id}-input`, className: emailLabel }, labels?.emailLabelValue ?? t(COPY.EMAIL_LABEL)),
            React__default.createElement(TextInput$1, { id: `${id}-input`, className: emailInput, onBlur: handleBlur, onChange: handleChange, defaultValue: emailValue.value, required: true, maxLength: TEXT_INPUT_MAX_CHARS, "aria-invalid": !!error, "aria-describedby": error && `${id}-validation-message`, name: "toggleable-email-field-text-input" })))));
}
var ToggleableEmailField$1 = withErrorBoundary(ToggleableEmailField);

const getMessages = (localeCode) => {
    let messages = {};
    if (localeCode === 'en-US' || localeCode === 'en') {
        return messages;
    }
    if (localeCode in CountryLabels) {
        messages = CountryLabels[localeCode];
    }
    return messages;
};
function L10nProviderWrapper({ localeCode = 'en_US', languageCode = 'en', children, }) {
    return (React__default.createElement(L10nProvider, { localeCode: localeCode, languageCode: languageCode, localeMessages: getMessages(localeCode) }, children));
}

export { AddressField$1 as AddressField, BestWorst$1 as BestWorst, COPY$5 as COPY, CaretDownIcon, CaretDownOutlineIcon, Checkbox$1 as Checkbox, CommentBox$1 as CommentBox, DateTime$1 as DateTime, Dropdown$1 as Dropdown, EmailField$1 as EmailField, FileUpload, ImageChoice$1 as ImageChoice, ImagePresentational$1 as ImagePresentational, L10nProviderWrapper, MatrixDropdown, MatrixRating, MultipleChoice, MultipleTextbox, NameField$1 as NameField, NativeSelect, SelectAnswerOption as NativeSelectOption, Nps, PasswordField, PhoneField$1 as PhoneField, Ranking, SingleTextbox$1 as SingleTextbox, Slider, StarRating, TextPresentational$1 as TextPresentational, ToggleableEmailField$1 as ToggleableEmailField, WarningIcon$1 as WarningIcon };
//# sourceMappingURL=index.js.map
